import {
  Fragment,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  mdiContentCopy,
  mdiCursorDefault,
  mdiHelpCircleOutline,
  mdiPipe,
  mdiRoad,
  mdiStar,
} from '@mdi/js'
import { useAbp } from '../platform/abp/react'
import { EventTypes, trackEvent } from '../platform/analytics/analytics'
import { NavMdiIcon } from '../NavTabIcons'
import { defaultTemplateId, nextCopyTitle, newId, templateById, visibleTemplates } from './persist'
import {
  BLANK_BOARD_ID,
  BLANK_EXERCISE_ID,
  BLANK_WALKTHROUGH_ID,
  blankLibraryIdForType,
  createBlankBoardSession,
} from './canned/blankBoard'
import {
  applyBoardCameraToSession,
  BOARD_LOCATION_PREVIEW_IDLE,
  boardCamerasEqual,
  isBlankBoardTemplateId,
  isBoardLocationLocked,
  resolveTemplateKeyForLocation,
  type BoardCamera,
} from './boardLocation'
import { BoardLocationPicker } from './BoardLocationPicker'
import { ensurePosterPlateMapRegistered, MAP_POSTER_STYLE_ID } from './posters/shotManifest'

/** Calm Streets plate behind the setup wizard — not persisted on the board. */
const WIZARD_MAP_BACKDROP_CAMERA = { lng: -115.478, lat: 33.852, zoom: 16.05 }

ensurePosterPlateMapRegistered()
import {
  isHistoricalTemplate,
  isYuccaElmTemplate,
  yuccaElmCardLead,
  YUCCA_GAS_LEAK_ID,
} from './canned'
import {
  clearStudioArrival,
  LAST_SERVER_INSTANCE_KEY,
  LAST_STUDIO_VIEW_KEY,
  isStudioTemplatesHubPath,
  parseStudioSessionId,
  parseStudioTemplateId,
  readStudioArrival,
  SCENARIOS_PATH,
  SCENARIOS_TEMPLATES_PATH,
  studioSessionPath,
  studioTemplatePath,
} from './serverMode'
import {
  defaultUntitledTitle,
  defaultWizardTitle,
  focusModeClassNames,
  isDefaultUntitledTitle,
  lifecycleStatusLabel,
  offeredSessionTypes,
  passiveFocusChrome,
  sessionCardSubtitle,
  sessionIsPublished,
  sessionPublishBlockers,
  sessionPublishHints,
  sessionTypeById,
  sessionTypeOffersGhosts,
  sessionWindowLabels,
  showStoryTransport,
  statusFromLifecycle,
  storyTransportLabels,
  studioMinimalInspect,
  packageOffers,
  typeOffersSharedRun,
  typeOffersStoryClock,
  type SettingsChrome,
  type SessionTypeId,
} from './sessionTypes'
import { StudioFeature, studioFeatureOn } from './studioFeatures'
import { HOME_PATH } from '../routing/pathPolicy'
import {
  cloneScenarioInstance,
  createScenarioInstance,
  fetchScenarioInstance,
  fetchScenarioInstances,
  fetchScenarioMaps,
  fetchScenarioTemplates,
  readServerInstanceIds,
  writeServerInstanceIds,
  fetchUserTextScale,
  fetchUserStudioTheme,
  instanceFromSnapshot,
  readUiScale,
  readStudioTheme,
  saveUserTextScale,
  saveUserStudioTheme,
  snapshotFromWire,
  fetchScenarioDocPdf,
  fetchScenarioInstancePdf,
  fetchScenarioSnapshotPdf,
  uploadInviteMaterial,
  writeUiScale,
  writeStudioTheme,
  type InviteMaterialUpload,
  type ServerSnapshot,
  type StudioTheme,
  type UiScale,
} from './api/scenarioStudioApi'
import { ScenarioUserSwitcher } from './ScenarioUserSwitcher'
import {
  ensureScenarioHub,
  hubPause,
  hubPreviewPlay,
  hubResume,
  hubResumeAt,
  hubStartRun,
  hubStopRun,
  hubSeek,
  hubRemoveObject,
  hubSetAudienceType,
  hubSetAllowLinkGuests,
  hubDeleteInstance,
  hubRespondToInvite,
  hubSendInvite,
  hubSetPeople,
  hubSetComments,
  hubSetRate,
  hubSetSessionFields,
  hubSendSessionFieldsNow,
  hubRebaseStory,
  hubApplyBoardSeed,
  hubUpsertObject,
  type ApplyBoardSeedPatch,
  type SessionFieldPatch,
  type StartRunPatch,
  type StopRunPatch,
  hubGoLive,
  hubSetWatchFrame,
  resendWatchPlumes,
  setOutgoingWatchPlumes,
  hubFlushWatchFrames,
  hubSetPresenter,
  hubStop,
  joinScenarioInstance,
  leaveScenarioInstance,
  clientSentClockTrace,
  noteClockTrace,
  newClientTrace,
  noteScenarioSeq,
  stopScenarioHub,
  STUDIO_HUB_RECONNECT_DELAYS_MS,
  type ScenarioHubHandlers,
} from './api/scenarioStudioHub'
import {
  applyBoardSeed,
  cloneBoardVersion,
  ensureBoardVersions,
  findBoardVersion,
  healRunsForHubTake,
  latestExerciseRun,
  latestExerciseRunForVersion,
  mergeExerciseRunsFromPeer,
  selectedBoardVersion,
  selectedVersionSeedMatches,
  startExerciseRun,
  stopExerciseRun,
  updateSelectedVersionSeed,
  versionLocalRunNumber as versionLocalRunNumberFor,
} from './exerciseBoardModel'
import {
  chatSectionsForPane,
  clearChatWatermark,
  coerceRoomChat,
  countChatUnread,
  healRoomChatFromRuns,
  inferRoomChatPhase,
  liveRoomChat,
  markChatRead,
  mergeRoomChat,
  patchRoomChat,
  resolveActiveChatRun,
} from './runChat'
import {
  actionsInRunSlice,
  resolveTapeAvailableUntilMs,
  runHasTapeSeed,
  sessionForTapeReview,
} from './tapePlayback'
import { deriveRoomTransportStripLabel, exercisePhaseChip } from './exerciseChrome'
import {
  fetchImpersonationTargets,
  hasSavedImpersonationSession,
} from './api/impersonation'
import { fetchStudioPeopleCeiling, type StudioPeopleHit } from './api/studioPeopleSearch'
import { boardPlayheadIso, playheadIsoFromClock, type StudioClock } from './api/scenarioClock'
import {
  isStudioUnreachable,
  isStudioUnreachableNotice,
  STUDIO_LINK_DOWN_HINT,
  STUDIO_LINK_DOWN_TITLE,
  STUDIO_UNREACHABLE_RAIL,
  studioActionMessage,
  studioLoadMessage,
} from './api/studioErrors'
import {
  displayToMeters,
  formatAuthorTime,
  formatDistanceM,
  formatDistanceCompact,
  formatSpeedMph,
  formatDuration,
  formatElapsed,
  formatWall,
  defaultPlaybackRuntimeMs,
  minPlaybackRuntimeMs,
  PLAYBACK_RATE_MIN,
  playbackRateBounds,
  playbackSpeedMenuRates,
  snapPlaybackRate,
  formatRunningClock,
  formatTimelineStamp,
  metersToDisplay,
  widthInputNumber,
  widthInputSuffix,
  widthInputToMeters,
  boardDeltaToAuthorVisibleMs,
  formatHoldCountdown,
  posePreviewAtElapsed,
  posesSameSpot,
  visibilityPreviewIndexAtElapsed,
  visibilityPreviewSpanMs,
  numberPreviewIndexAtElapsed,
  windCardinal,
  cameraBeatAtElapsed,
  cameraBeatNear,
  CAMERA_BEAT_MERGE_MS,
  DEFAULT_CAMERA_EASE_MS,
  removeCameraBeatNear,
  upsertCameraBeat,
  distanceUnitShort,
  type ScenarioClockConfig,
} from './format'
import {
  addStoryMs,
  placeSessionOnNextClock,
  rebaseStorySession,
  storyDaysDiffer,
  storyDeltaMs,
  storyMs,
  STUDIO_TIME_ZONES,
  formatTimeZoneLabel,
  timeZoneAbbrev,
} from './storyTime'
import { iconSvg } from './icons'
import {
  paletteColor,
  paletteHint,
  paletteItemVisible,
  paletteLabel,
  PALETTE,
  DRAW_TOOLS,
  objectShowsOnTimeline,
  supportsKeyframe,
} from './palette'
import { cloakEggSession, isBrwnPlumeType, isGeneratedBrwnPlumeDescription } from './eggs/brwnPlume'
import {
  downloadBlob,
  previewVideoFilename,
  requestTabCapture,
  startPreviewRecorder,
  type PreviewRecorder,
} from './exportPreviewVideo'
import {
  buildNarrationTimelineEvent,
  displayDurationMs,
  eventObjectIds,
  findWind,
  healSessionCopy,
  healSyncedTimeline,
  isLocalStorm,
  isNarrationTimelineEvent,
  isPristineNarrationBeat,
  isSyncedTimelineEvent,
  mergeTimeline,
  NARRATION_INSERT_STEP_MS,
  narrationBeatAtBetween,
  resolveSession,
  syncedTimelineEventId,
  visibilityFramesFromShowHide,
  withSessionSpan,
} from './resolveSession'
import { SCENARIO_TOOL_SHORT_NAME } from './docs'
import { ScenarioMapHost, type MapFeatureTap, type ScenarioMapApi } from './ScenarioMapHost'
import { MapAssetPanel } from './MapAssetPanel'
import {
  coerceSessionMaps,
  mapRefById,
  mapRefFromDownloadedOperator,
  mapRefFromServerMap,
  mapsForPicker,
  mapsOffered,
  rememberExtraMaps,
  rememberMapboxTokenOverrides,
  sessionMapStyle,
  truncateMapPickerLabel,
  type ScenarioMapCatalogRow,
} from './maps'
import {
  presenceInitials,
  presenceToneForKey,
  type PresenceAvatarTone,
} from './presenceAvatar'
import { useOperatorSession } from '../platform/buxus/OperatorSessionContext'
import {
  SIZE_RADIUS_M,
  TYPE_COLORS,
  TYPE_SIZE_LABEL,
  TYPE_SIZE_PX,
  presetLabel,
  windSpeedBand,
  plumeSpreadBand,
  zoneCfgFor,
  zoneLabel,
} from './toolUi'
import {
  hasIconSize,
  hasVisibilityWindow,
  objectTypeMeta,
  poseFoldLabel,
  defaultObjectDescription,
} from './objectMeta'
import { objectAnchorLngLat, tornadoImpactRadiusM } from './geo'
import {
  clearPersonRolePlaySeats,
  isRolePlayEnabled,
  personForRolePlayAssignee,
  rolePlayLabelUi,
  rolePlaySlotsFromSession,
  slotAssigneeId,
  slotForObject,
  withRolePlayAssignee,
} from './rolePlay'
import { BOARD_REVEAL_OPTIONS, type BoardRevealMode } from './boardReveal'
import { STUDIO_REVISION, studioEnvLabel, studioVersionTitle } from './pocStamp'
import { WindCompass, WindAnalogClock, StripWindBadge } from './WindCompass'
import { StoryWhenInput } from './StoryWhenInput'
import {
  loggedInPerson,
  platformProfileDebugForEmail,
  profileFieldsFromBlob,
  readRegistrationProfileJson,
  sameLoginEmail,
} from './user'
import buxusLogo from '../assets/logo.png'
import './scenarios.css'
import type {
  AttachedAsset,
  CameraBeat,
  ExerciseRun,
  NumberKeyframe,
  ObjectSize,
  PersonRole,
  PoseKeyframe,
  ResolvedObject,
  RoadStatus,
  ScenarioAction,
  ScenarioComment,
  ScenarioInstance,
  ScenarioMapRef,
  ScenarioObject,
  ScenarioPerson,
  PersonStatus,
  AccountBoardMode,
  ScenarioRun,
  ScenarioSession,
  ScenarioTemplate,
  StatusKeyframe,
  TimelineAction,
  VisibilityKeyframe,
  WindKeyframe,
} from './types'

import {
  PencilIcon,
  CloseIcon,
  CopyIcon,
  PdfIcon,
  EmailLine,
  ToolIcon,
  SwatchRow,
  StrokeFields,
  PaneEyebrow,
  BeatEffectsEditor,
  NarrationBeatCopyFields,
  ChatThreadScroll,
  ReviewRunChipRow,
  RoomChatCompose,
  scrollChatThreadToBottom,
} from './pageChrome'
import {
  DockAccountButton,
  DockPane,
  DockViewingCountButton,
  StudioDockEffects,
  StudioDockProvider,
  type DockSetter,
  type DockTab,
} from './studioDock'
import { ShareSessionPanel } from './ShareSessionPanel'
import {
  GET_STARTED_SEEN_KEY,
  GettingStartedWizard,
  hasSeenGetStarted,
  markGetStartedSeen,
  type WizardDoneMeta,
  type WizardStartResult,
} from './GettingStartedWizard'
import { FastHoverTip, PersonPresencePip } from './pagePeople'
import { RosterAddField } from './RosterAddField'
import {
  CARD_EDIT_ROLES,
  ROLE_LABEL,
  TIME_FORMAT_EXAMPLE_HM,
  TIME_FORMAT_EXAMPLE_HMS,
  DISPLAY_UNITS_EXAMPLE_M,
  personIsActive,
  personHasRole,
  personUserId,
  findPersonByUserId,
  findPersonByKey,
  studioAccountId,
  personPrivilegeLabel,
  presenceRoleSuffix,
  viewerSeesPresenceRoleSuffix,
  joinOfferHostTag,
  inviteAction,
  inviteNotice,
  personHasStudioAccount,
  rosterRoleCanBeOffered,
  activeOwners,
  isDesignatedOwner,
  resolveDesignatedOwnerUserId,
} from './pagePeopleModel'
import { commentWhoParts, commentIsAction, commentThreadBody, liveComment } from './pageComments'
import { isAdditiveToggleMod, selectIdRange, type ListSelectMods } from './pageSelection'
import {
  cloneSession,
  sortIsoDesc,
  sortOversightRail,
  sortPlannedBySchedule,
  defaultLessons,
  doneSessionCardSubtitle,
  joinedSessionCardSubtitle,
  liveSessionCardSubtitle,
  plannedSessionCardSubtitle,
  scheduleDurationLabel,
  sessionCardByline,
  instanceRun,
  personOnRoster,
} from './pageRuns'
import {
  mapAssetKindColor,
  defaultParams,
  MAX_BOARD_OBJECTS,
  MAX_OWNED_OBJECTS,
  assetKey,
  assetDistanceM,
  assetToHighlightTap,
  readAttachedAssets,
  writeAttachedAssets,
  withRoadGeometryFromAssets,
  attachKindsForObject,
  type NearbySortCol,
  type LinkedSortCol,
} from './pageAssets'
import {
  ElapsedInput,
  NumberSeriesEditor,
  StatusSeriesEditor,
  CoordInput,
  PoseSeriesEditor,
  VisibilitySeriesEditor,
} from './keyframeEditors'
import {
  sortNumberFrames,
  sortPoseFrames,
  sortStatusFrames,
  sortVisFrames,
  MAP_LABEL_MAX_CHARS,
} from './keyframeSort'
import {
  nearestWindFrameIndex,
  WIND_MARK_MATCH_MS,
  WindDegControl,
  WindKeyframeScrubber,
  WindStoryMirror,
} from './windAuthoring'
import {
  adjacentBeatMs,
  PlaybackSpeedMenu,
  scrubMarksFromTimeline,
  StoryScrubber,
} from './storyTransportChrome'
import { sortWindFrames } from './windSort'
import { sampleWindAt } from './windInterp'
import {
  WIND_PRESETS,
  WIND_PRESET_SPAN_OPTIONS,
  WIND_SPEED_SLIDER_MAX_MPH,
  WIND_PLUME_DISTANCE_SLIDER_MAX_M,
  buildWindPresetFrames,
  clampWindPresetWindow,
  formatWindPresetBlurb,
  suggestedWindPresetOnsetMs,
  windPresetById,
  type WindPresetId,
} from './windPresets'
import {
  TORNADO_PRESETS,
  TORNADO_PRESET_SPAN_OPTIONS,
  buildClampedTornadoPreset,
  tornadoPresetById,
  tornadoPresetIdFromParams,
  tornadoPresetSpanFromParams,
  type TornadoPresetId,
} from './tornadoPresets'
import {
  HURRICANE_PRESETS,
  HURRICANE_PRESET_SPAN_OPTIONS,
  HURRICANE_TRACK_HEADINGS,
  HURRICANE_WIND_SPEED_MAX_MPH,
  buildClampedHurricanePreset,
  hurricanePresetById,
  hurricanePresetIdFromParams,
  hurricanePresetSpanFromParams,
  hurricaneTrackHeadingFromParams,
  type HurricanePresetId,
} from './hurricanePresets'

/** After Get started: Timeline only when the new board already has cards. Blanks stay on Sessions so placement is first. */
function dockAfterWizardCreate(typeId: string | null | undefined, timelineLen: number): DockTab {
  return sessionTypeById(typeId)?.storyClock === true && timelineLen > 0 ? 'timeline' : 'scenarios'
}

type NotesCtx = 'scenario' | string

/**
 * Boards you're on the roster for (owner, invited, or joined).
 * Public discovery and admin-only oversight never drive auto-resume.
 */
function ownedBoardsFor(instances: readonly ScenarioInstance[], email: string): ScenarioInstance[] {
  return instances.filter((i) => personOnRoster(i.people, email))
}

const EMPTY_MAP_IDS: readonly string[] = []
const EMPTY_MAP_OBJECTS: readonly ResolvedObject[] = []

/** Sessions rail card tint — Live / Draft / Published / Done (selection uses the same family). */
function sessionRailToneClass(inst: ScenarioInstance): string {
  if (inst.lifecycle === 'running') return ' running'
  if (inst.lifecycle === 'completed') return ' is-done'
  const st = statusFromLifecycle(inst.lifecycle, inst.session)
  if (st === 'published') return ' is-published'
  if (st === 'draft') return ' is-draft'
  return ''
}

/**
 * Resume target for Sessions landing:
 * - 0 owned → null (caller opens Templates)
 * - 1 owned → that board
 * - 2+ → last remembered if still owned, else running, else newest planned, else newest
 */
function pickResumeOwnedBoard(
  owned: readonly ScenarioInstance[],
  lastId: string
): ScenarioInstance | null {
  if (!owned.length) return null
  if (owned.length === 1) return owned[0]!
  const last = lastId.trim() ? owned.find((i) => i.id === lastId.trim()) : undefined
  if (last) return last
  const running = owned.find((i) => i.lifecycle === 'running')
  if (running) return running
  const planned = owned
    .filter((i) => i.lifecycle === 'planned')
    .slice()
    .sort((a, b) => sortIsoDesc(a.createdAtIso, b.createdAtIso))
  if (planned[0]) return planned[0]
  return owned.slice().sort((a, b) => sortIsoDesc(a.createdAtIso, b.createdAtIso))[0] ?? null
}

function readLastServerInstanceId(): string {
  try {
    return localStorage.getItem(LAST_SERVER_INSTANCE_KEY)?.trim() ?? ''
  } catch {
    return ''
  }
}

/**
 * Spine docks. Catalog root (`scenarios`) is the Sessions list.
 * Re-clicking a content pane also returns here.
 * List tab temporarily hidden while Sessions← back affordance is under test.
 */
const TABS: { id: DockTab; label: string; iconPath?: string }[] = [
  // { id: 'scenarios', label: 'List' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'inspector', label: 'Inspect' },
  { id: 'notes', label: 'Chat' },
  { id: 'people', label: 'People' },
  { id: 'settings', label: 'Settings' },
]

function StudioOfflineNotice({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="sc-boot sc-offline" role="alert">
      <p className="sc-offline-title">{message}</p>
      <button type="button" className="sc-btn sc-offline-retry" onClick={onRetry}>
        Try again
      </button>
    </div>
  )
}

/** Local draft while typing — avoids re-rendering the whole page on every keystroke. */
function VersionDupNameForm({
  initial,
  onDuplicate,
  onCancel,
}: {
  initial: string
  onDuplicate: (name: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  return (
    <>
      <label className="sc-run-options-name">
        <span className="sc-run-options-name-label">New Version Name</span>
        <input
          className="sc-run-options-name-input"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onDuplicate(draft)
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
        />
      </label>
      <div className="sc-join-offer-actions">
        <button
          type="button"
          className="sc-btn rec armed"
          onClick={() => onDuplicate(draft)}
        >
          Create version
        </button>
        <button type="button" className="sc-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  )
}

export function ScenariosPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { bootstrap, isAuthenticated, can, featureEnabled, logout, roleNames } = useAbp()
  const [loggingOut, setLoggingOut] = useState(false)
  const studioFeature = (name: (typeof StudioFeature)[keyof typeof StudioFeature]) =>
    studioFeatureOn(featureEnabled, name)
  const user = useMemo(() => loggedInPerson(bootstrap), [bootstrap])
  const myPresenceInitials = useMemo(
    () => presenceInitials(user.name || user.email),
    [user.name, user.email]
  )
  /** Color from email/user id so two people with similar initials don’t share a purple. */
  const myPresenceTone = useMemo(
    () => presenceToneForKey(user.email || user.userId || myPresenceInitials),
    [user.email, user.userId, myPresenceInitials]
  )
  const { downloadedOperators, getCachedOperatorPayload, ensureOperatorPayload } =
    useOperatorSession()
  const [downloadedOperatorMaps, setDownloadedOperatorMaps] = useState<ScenarioMapRef[]>([])
  /** This tenant's catalog, including per-style tokens. Ready after the fetch settles. */
  const [mapCatalog, setMapCatalog] = useState<ScenarioMapCatalogRow[]>([])

  useEffect(() => {
    let cancelled = false
    void fetchScenarioMaps()
      .then((rows) => {
        if (cancelled) return
        setMapCatalog(rows)
        rememberMapboxTokenOverrides(rows)
        rememberExtraMaps(
          rows.map(mapRefFromServerMap).filter((m): m is ScenarioMapRef => m != null),
        )
      })
      .catch(() => {
        /* keep default token / client catalog URLs */
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const refs: ScenarioMapRef[] = []
      for (const op of downloadedOperators) {
        let payload = getCachedOperatorPayload(op.operatorId)
        if (!payload) {
          try {
            payload = await ensureOperatorPayload(op.operatorId)
          } catch {
            continue
          }
        }
        if (cancelled) return
        const ref = mapRefFromDownloadedOperator(op.operatorId, op.name, payload)
        if (ref) refs.push(ref)
      }
      if (cancelled) return
      rememberExtraMaps(refs)
      setDownloadedOperatorMaps(refs)
    })()
    return () => {
      cancelled = true
    }
  }, [downloadedOperators, getCachedOperatorPayload, ensureOperatorPayload])

  const [uiScale, setUiScale] = useState<UiScale>(() => readUiScale())
  const [studioTheme, setStudioTheme] = useState<StudioTheme>(() => readStudioTheme())
  const [instances, setInstances] = useState<ScenarioInstance[]>([])
  const [templates, setTemplates] = useState<ScenarioTemplate[]>([])
  const [instanceId, setInstanceId] = useState('')
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null)
  /** Local Version pick while browsing a multi-version template (no hub instance yet). */
  const [templatePreviewVersionId, setTemplatePreviewVersionId] = useState<string | null>(null)
  /** Clone-from-template in flight — lights the copy icon + in-card loader. */
  const [cloningTemplateId, setCloningTemplateId] = useState<string | null>(null)
  /** Session-card copy in flight — same loader pattern as template clone. */
  const [cloningSessionId, setCloningSessionId] = useState<string | null>(null)
  /** Friendly confirm after clone-from-template (or similar create/copy). */
  const [cloneReadyOffer, setCloneReadyOffer] = useState(false)
  /** Sessions list shows every template. Only the menu's Templates section turns this on. */
  const [libraryMode, setLibraryMode] = useState(false)
  /** Server boot reads the saved view before this turns true, so the first render cannot wipe it. */
  const studioViewReadyRef = useRef(false)
  /** When set with edit_templates, session holds a mutable template draft (POC). */
  const [templateEditLive, setTemplateEditLive] = useState(false)
  const [session, setSession] = useState<ScenarioSession>(() =>
    healSyncedTimeline(withSessionSpan(createBlankBoardSession()))
  )
  // Operator plates stay in the picker after downloads clear. The board does not paint from this.
  useEffect(() => {
    const embedded = session.maps.filter((m) => m.id.startsWith('op:') && m.styleUrl)
    if (embedded.length) rememberExtraMaps(embedded)
  }, [session.maps])
  const [people, setPeople] = useState<ScenarioPerson[]>([])
  const [remembered, setRemembered] = useState<{ name: string; email: string }[]>([])
  const [eggsOn, setEggsOn] = useState(false)
  /** Developer-only panels; off by default (demos / screenshots). Toggle with D. */
  const [debugPanelsOn, setDebugPanelsOn] = useState(false)
  const accessPerms = useMemo(() => {
    const granted = new Set<string>()
    const leaves: [string, string][] = [
      ['scenarios.create', 'Create'],
      ['scenarios.edit_templates', 'EditTemplates'],
      ['scenarios.edit_board', 'EditBoard'],
      ['scenarios.place_objects', 'PlaceObjects'],
      ['scenarios.run', 'Run'],
      ['scenarios.manage_people', 'ManagePeople'],
      ['scenarios.send_invites', 'SendInvites'],
      ['scenarios.capture_screen', 'CaptureScreen'],
      ['scenarios.comment', 'Comment'],
      ['scenarios.view_debug', 'Developer'],
      ['scenarios.delete', 'Delete'],
      ['scenarios.view_all', 'ViewAllSessions'],
    ]
    for (const [bag, leaf] of leaves) {
      if (can(`Buxus.Scenarios.${leaf}`)) granted.add(bag)
    }
    return granted
  }, [can])
  /** Account chrome — not ABP Participant and not the roster seat. */
  const accountBoardMode: AccountBoardMode =
    accessPerms.has('scenarios.edit_board') ||
    accessPerms.has('scenarios.edit_templates') ||
    accessPerms.has('scenarios.place_objects') ||
    accessPerms.has('scenarios.run')
      ? 'authoring'
      : 'following'
  const canDemoRoleOverride = accessPerms.has('scenarios.view_debug')
  const canViewEggs = eggsOn && canDemoRoleOverride
  const [elapsedMs, setElapsedMs] = useState(0)
  /** Load idle — a stored playhead is an offset, not a running ticker. */
  const [running, setRunning] = useState(false)
  /** Real-time length Preview/Play should take (slider); maps to an internal advance rate. */
  const [playbackRuntimeMs, setPlaybackRuntimeMs] = useState(() =>
    defaultPlaybackRuntimeMs(28 * 60_000)
  )
  const [videoExportStatus, setVideoExportStatus] = useState<'idle' | 'recording' | 'saving'>(
    'idle'
  )
  const [cameraFollowOn, setCameraFollowOn] = useState(false)
  const cameraFollowOnRef = useRef(false)
  cameraFollowOnRef.current = cameraFollowOn
  /** Bumped when Auto-track turns on or a timeline card is hovered so the map re-eases even if beatKey is unchanged. */
  const [cameraFollowApplyEpoch, setCameraFollowApplyEpoch] = useState(0)
  /** Leader is sharing pan/zoom. Off until they press W. Not saved. */
  const [watchOn, setWatchOn] = useState(false)
  const watchOnRef = useRef(false)
  watchOnRef.current = watchOn
  const shareEditPointerRef = useRef(false)
  /** True while pointer-up is flushing final edit → Upsert; blocks mid-clear Watch frames. */
  const dragCommitInFlightRef = useRef(false)
  /** Bump to re-paint map when sticky remote drag expires without another frame. */
  const [stickyDragEpoch, setStickyDragEpoch] = useState(0)
  const stickyDragTimerRef = useRef<number | null>(null)
  const placeTypeRef = useRef<string | null>(null)
  const editWhoRef = useRef('')
  const editWhoNameRef = useRef('')
  const myEmailRef = useRef('')
  const editShareLiveRef = useRef(false)
  /** Last peer drag pose — held briefly after the stream drops so Upsert can land without a snap-back. */
  const stickyRemoteDragRef = useRef<{
    dragId: string
    dragLng: number
    dragLat: number
    until: number
  } | null>(null)
  type LiveWatchFrame = {
    lng: number
    lat: number
    zoom: number
    pointerLng?: number
    pointerLat?: number
    pointerDown?: boolean
    clickSeq?: number
    hoverId?: string
    hoverTip?: string
    grab?: boolean
    cursor?: 'pointer' | 'grab' | 'grabbing'
    focusId?: string
    dragId?: string
    dragLng?: number
    dragLat?: number
    who?: string
    whoName?: string
    whoEmail?: string
    /** Done-review Presenter story playhead (ms from story start). */
    storyElapsedMs?: number
    storyRunning?: boolean
    /** Presenter's personal Show plumes. */
    surfacePlumes?: boolean
    /** True while in leave/rejoin grace — lineup stays, live cursor does not. */
    grace?: boolean
  }
  /** Active Watch-Me frames keyed by sharer email (multi-share safe). */
  const [watchByEmail, setWatchByEmail] = useState(() => new Map<string, LiveWatchFrame>())
  /**
   * Brief keep-alive when someone leaves the board (template/hub) and may return.
   * Decisive Stop share (S) while still in the room is immediate — no grace.
   */
  const SHARE_GRACE_MS = 20_000
  const shareGraceTimersRef = useRef(new Map<string, number>())
  function clearShareGrace(email: string): void {
    const t = shareGraceTimersRef.current.get(email)
    if (t != null) {
      window.clearTimeout(t)
      shareGraceTimersRef.current.delete(email)
    }
  }
  function endShareImmediate(email: string): void {
    const key = email.trim().toLowerCase()
    if (!key || key === '__anon__') return
    clearShareGrace(key)
    setWatchByEmail((prev) => {
      if (!prev.has(key)) return prev
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }
  function beginShareGrace(email: string): void {
    const key = email.trim().toLowerCase()
    if (!key || key === '__anon__') return
    clearShareGrace(key)
    setWatchByEmail((prev) => {
      const cur = prev.get(key)
      if (!cur || cur.grace) return prev
      const next = new Map(prev)
      next.set(key, { ...cur, grace: true })
      return next
    })
    const timer = window.setTimeout(() => {
      shareGraceTimersRef.current.delete(key)
      setWatchByEmail((prev) => {
        if (!prev.has(key)) return prev
        const next = new Map(prev)
        next.delete(key)
        return next
      })
    }, SHARE_GRACE_MS)
    shareGraceTimersRef.current.set(key, timer)
  }
  /** Room Presenter (hub) — silent camera override while they share. Null = respect Auto-track. */
  const [presenterEmail, setPresenterEmail] = useState<string | null>(null)
  const presenterEmailRef = useRef<string | null>(null)
  presenterEmailRef.current = presenterEmail
  const watchByEmailRef = useRef(watchByEmail)
  watchByEmailRef.current = watchByEmail
  const presentNameByEmailRef = useRef<ReadonlyMap<string, string>>(new Map())
  const myPresenceInitialsRef = useRef('')
  const watchFrame = useMemo((): LiveWatchFrame | null => {
    if (!presenterEmail) return null
    const frame = watchByEmail.get(presenterEmail)
    if (!frame || frame.grace) return null
    return frame
  }, [watchByEmail, presenterEmail])
  const [watchZoomCue, setWatchZoomCue] = useState<{
    dir: 'in' | 'out'
    n: number
  } | null>(null)
  const watchZoomPrevRef = useRef<number | null>(null)
  const [editFrame, setEditFrame] = useState<{
    pointerLng: number
    pointerLat: number
    pointerDown?: boolean
    hoverId?: string
    grab?: boolean
    cursor?: 'pointer' | 'grab' | 'grabbing'
    dragId?: string
    dragLng?: number
    dragLat?: number
    who?: string
  } | null>(null)
  const [keyToast, setKeyToast] = useState<{
    token: string
    label: string
  } | null>(null)
  const [keysOpen, setKeysOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const helpOpenRef = useRef(false)
  const [focusMode, setFocusMode] = useState(false)
  const focusModeRef = useRef(false)
  focusModeRef.current = focusMode
  const keysOpenRef = useRef(false)
  const ghostsOfferedRef = useRef(true)
  keysOpenRef.current = keysOpen
  helpOpenRef.current = helpOpen
  const [joinToasts, setJoinToasts] = useState<
    {
      id: number
      text: string
      debug?: boolean
      rank: number
      sticky?: boolean
    }[]
  >([])
  const presentCountsRef = useRef(new Map<string, number>())
  const [presentEmails, setPresentEmails] = useState<ReadonlySet<string>>(() => new Set())
  /** Best-known display names from hub presence (email → name). */
  const [presentNameByEmail, setPresentNameByEmail] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  )
  presentNameByEmailRef.current = presentNameByEmail
  /**
   * Emails we've already toasted as "is here" while *this viewer* stayed on the board.
   * Cleared when we leave the instance (not when they leave). Suppresses template→back rejoin noise.
   */
  const presenceAnnouncedRef = useRef(new Set<string>())
  /** True after hub Presence sync for the open board (Join). Watching waits on this; Publish does not. */
  const [presenceSynced, setPresenceSynced] = useState(false)
  /**
   * Dwell (~2.5s) before showing “N viewing” after Join sync — quick card flips through
   * silent rooms should not flash “1 viewing”, and presence can finish settling first.
   * Count updates after reveal stay immediate.
   */
  const [viewingCountSettled, setViewingCountSettled] = useState(false)
  /** Left rail open/closed for this board visit (P). Seeded from place rights. */
  const [paletteOpen, setPaletteOpen] = useState(true)
  const paletteOpenRef = useRef(true)
  paletteOpenRef.current = paletteOpen
  /** True after the user presses P on this board — stop auto-seeding from rights changes. */
  const paletteUserTouchedRef = useRef(false)
  /** Width transition only after first settled open/closed — avoids boot slide-in. */
  const [paletteMotionOn, setPaletteMotionOn] = useState(false)
  /** Seed open/closed once per board after presence/rights settle — never on later grants. */
  const paletteSeededRef = useRef(false)
  const canPlaceObjectsRef = useRef(false)
  const showBoardChromeRef = useRef(false)
  /** Deferred stop-share when leaving a board (grace). Resume if back before it fires. */
  const deferredShareStopRef = useRef<{
    instanceId: string
    timer: number
  } | null>(null)
  const peopleRef = useRef<ScenarioPerson[]>([])
  const rosterAccountHealRef = useRef<Set<string>>(new Set())
  const sessionTitleRef = useRef('')
  const joinToastSeqRef = useRef(0)
  const toastBurstRef = useRef<{
    text: string
    debug: boolean
    rank: number
    holdMs?: number
    ephemeral?: boolean
  } | null>(null)
  const toastBurstTimerRef = useRef<number | null>(null)
  /** Mirrors showDebugPanels for toast flush (D-mode sticky dismiss). */
  const debugPanelsOnRef = useRef(false)
  const rolePlayRemoteRef = useRef<{
    before: ScenarioObject[]
    after: ScenarioObject[]
  }>({
    before: [],
    after: [],
  })
  const rolePlayRemoteTimerRef = useRef<number | null>(null)
  const keyToastTimerRef = useRef<number | null>(null)
  /** Personal display pref. On shows plumes; off hides them. */
  const [surfacePlumes, setSurfacePlumes] = useState(() => {
    try {
      const on = localStorage.getItem('buxus.sc.surfacePlumes') !== '0'
      setOutgoingWatchPlumes(on)
      return on
    } catch {
      return true
    }
  })
  useEffect(() => {
    setOutgoingWatchPlumes(surfacePlumes)
    const me = myEmailRef.current
    const driving = watchOnRef.current || presenterEmailRef.current === me
    if (!driving) return
    resendWatchPlumes()
  }, [surfacePlumes])
  /**
   * Palette section open map (global - follows the user across boards).
   * Unset keys: Drawing Tools open in place mode; Hazard open in legend mode
   * (no Drawing Tools row there). Everything else starts closed.
   */
  const [paletteSectionOpen, setPaletteSectionOpen] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('buxus.sc.paletteSections')
      if (!raw) return {}
      const parsed = JSON.parse(raw) as unknown
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
      const out: Record<string, boolean> = {}
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'boolean') out[k] = v
      }
      return out
    } catch {
      return {}
    }
  })
  function paletteSectionDefaultOpen(id: string, placeMode: boolean): boolean {
    return placeMode ? id === 'drawing' : id === 'hazard'
  }
  function paletteSectionIsOpen(id: string): boolean {
    return paletteSectionOpen[id] ?? paletteSectionDefaultOpen(id, showPlacePaletteUi)
  }
  function togglePaletteSection(id: string): void {
    setPaletteSectionOpen((prev) => {
      const cur = prev[id] ?? paletteSectionDefaultOpen(id, showPlacePaletteUi)
      const next = { ...prev, [id]: !cur }
      try {
        localStorage.setItem('buxus.sc.paletteSections', JSON.stringify(next))
      } catch {
        /* private mode */
      }
      return next
    })
  }
  /** Hold G: light objects the current Show on board view is hiding. */
  const [ghostFlash, setGhostFlash] = useState(false)
  /** Hold Z: frame and light every object on the board at this clock. */
  const [zoomFlash, setZoomFlash] = useState(false)
  /** Soft-hold Auto-pan for this card clock after a user pan (Save-camera flow). */
  const [camEditHoldAtMs, setCamEditHoldAtMs] = useState<number | null>(null)
  const [camHoverId, setCamHoverId] = useState<string | null>(null)
  const [camMenuSuppressId, setCamMenuSuppressId] = useState<string | null>(null)
  const [mapCamFlash, setMapCamFlash] = useState(false)
  const [recToast, setRecToast] = useState(false)
  const cameraFlashTimerRef = useRef<number | null>(null)
  const recToastTimerRef = useRef<number | null>(null)
  const camLeaveTimerRef = useRef<number | null>(null)
  const videoRecorderRef = useRef<PreviewRecorder | null>(null)
  const videoStreamArmRef = useRef<MediaStream | null>(null)
  const videoExportActiveRef = useRef(false)
  const videoCaptureStartedAtRef = useRef(0)
  const [started, setStarted] = useState(false)
  const [actualStartTime, setActualStartTime] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [placeType, setPlaceType] = useState<string | null>(null)
  const [descDraft, setDescDraft] = useState('')
  /** While typing a radius, hold the raw field so min-clamp does not fight partial input. */
  const [radiusInputDraft, setRadiusInputDraft] = useState<string | null>(null)
  /** Inspect field id whose input border should flash after a real commit. */
  const [fieldFlashKey, setFieldFlashKey] = useState<string | null>(null)
  const fieldFlashTimerRef = useRef<number | null>(null)
  const labelFocusValueRef = useRef<string>('')
  const mapLabelFocusValueRef = useRef<string>('')
  const [titleDraft, setTitleDraft] = useState('Scenario Studio')
  const [serverClock, setServerClock] = useState<StudioClock | null>(null)
  const serverClockRef = useRef<StudioClock | null>(null)
  const [serverNotice, setServerNotice] = useState<string | null>(null)
  /** SignalR wire — reconnecting/disconnected blanks strip transport until up again. */
  const [hubLinkState, setHubLinkState] = useState<'connected' | 'reconnecting' | 'disconnected'>(
    'connected'
  )
  const studioLinkRetryingRef = useRef(false)
  /** Hold the placeholder board until templates and the last instance have arrived. */
  const [serverBooting, setServerBooting] = useState(true)
  /** Set when the host cannot be reached. The placeholder board is not a real session. */
  const [bootError, setBootError] = useState<string | null>(null)
  const [bootAttempt, setBootAttempt] = useState(0)
  const [summaryDraft, setSummaryDraft] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [completedOpen, setCompletedOpen] = useState(true)
  /** LIVE bin fold — default open each load; toggle is session-only (not persisted). */
  const [liveOpen, setLiveOpen] = useState(true)
  /** Flat “more” visibility for older Done / Oversight cards (not a nested accordion). */
  const [doneShowMore, setDoneShowMore] = useState(false)
  const [oversightShowMore, setOversightShowMore] = useState(false)
  /**
   * Older Done/Oversight cards: selected ⇒ full by default. Ids here stay squat (spine)
   * even while selected — click toggles expand ↔ spine.
   */
  const [railForcedSpineIds, setRailForcedSpineIds] = useState<Set<string>>(() => new Set())
  /** Admin Oversight bin fold — persisted like palette section opens (`buxus.sc.*`). */
  const [oversightOpen, setOversightOpen] = useState(() => {
    try {
      const raw = localStorage.getItem('buxus.sc.oversightFoldOpen')
      if (raw === '0') return false
      if (raw === '1') return true
    } catch {
      /* private mode */
    }
    return true
  })
  /** User closed Oversight — do not auto-reopen until the open board changes. */
  const oversightManualCloseRef = useRef(false)
  const liveFoldBodyRef = useRef<HTMLDivElement | null>(null)
  const liveFoldChevronRef = useRef<HTMLSpanElement | null>(null)
  const completedFoldBodyRef = useRef<HTMLDivElement | null>(null)
  const completedFoldChevronRef = useRef<HTMLSpanElement | null>(null)
  const oversightFoldBodyRef = useRef<HTMLDivElement | null>(null)
  const oversightFoldChevronRef = useRef<HTMLSpanElement | null>(null)

  /** Paint fold open/close immediately; defer React state (full page re-render is slow). */
  function paintRailFold(
    body: HTMLDivElement | null,
    chevron: HTMLSpanElement | null,
    btn: HTMLElement | null,
    open: boolean
  ): void {
    if (body) body.hidden = !open
    chevron?.classList.toggle('open', open)
    btn?.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  function toggleLiveFold(btn: HTMLButtonElement): void {
    const next = !liveOpen
    paintRailFold(liveFoldBodyRef.current, liveFoldChevronRef.current, btn, next)
    startTransition(() => setLiveOpen(next))
  }

  function toggleCompletedFold(btn: HTMLButtonElement): void {
    const next = !completedOpen
    paintRailFold(completedFoldBodyRef.current, completedFoldChevronRef.current, btn, next)
    startTransition(() => setCompletedOpen(next))
  }

  function toggleOversightFold(btn: HTMLButtonElement): void {
    const next = !oversightOpen
    oversightManualCloseRef.current = !next
    paintRailFold(oversightFoldBodyRef.current, oversightFoldChevronRef.current, btn, next)
    startTransition(() => {
      setOversightOpen(next)
      try {
        localStorage.setItem('buxus.sc.oversightFoldOpen', next ? '1' : '0')
      } catch {
        /* private mode */
      }
    })
  }
  function toggleRailOlderCard(id: string, isOlder: boolean): void {
    if (!isOlder) {
      selectInstance(id)
      return
    }
    const on = instanceIdRef.current === id && !previewTemplateIdRef.current
    if (on && !railForcedSpineIds.has(id)) {
      // Full → spine again (board stays open).
      setRailForcedSpineIds((prev) => new Set(prev).add(id))
      return
    }
    if (on && railForcedSpineIds.has(id)) {
      // Spine → full.
      setRailForcedSpineIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      return
    }
    // Closed spine → open board as full card.
    setRailForcedSpineIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    selectInstance(id)
  }
  const [stopped, setStopped] = useState(false)
  /** Exercise: Stop finished this timed run (still Live). Run again clears it; Pause does not set it. */
  const [exerciseRunStopped, setExerciseRunStopped] = useState(false)
  const [justCompletedId, setJustCompletedId] = useState<string | null>(null)
  /** Keeps a just-stopped card completed while an in-flight clock tick still says running. */
  const stopHoldIdRef = useRef<string | null>(null)
  /** Walkthrough Reset: ignore Seek echoes that briefly report running before Pause sticks. */
  const pauseHoldIdRef = useRef<string | null>(null)
  /** Mid-story Pause: ignore running clock echoes until hubPause settles. */
  const transportPauseHoldIdRef = useRef<string | null>(null)
  /** Play/Run: ignore paused SetRate echoes until hubResume settles. */
  const transportPlayHoldIdRef = useRef<string | null>(null)
  /**
   * After Stop / Start Run: ignore Clock/Fields/Snapshot that disagree with the take
   * we just committed until a matching echo confirms (same idea as IR startTape intent).
   * Cleared on confirm — not a wall-clock mute. Never clear while Start is `preparing`
   * (ApplyBoardSeed Snapshot + Seek Clocks often echo stale take#=0). See TODO “Atomic StartRun”.
   */
  const takePhaseHoldRef = useRef<{
    take: number
    stopped: boolean
  } | null>(null)
  /**
   * Start Run gate: story-end must not fire until hub StartRun has returned.
   * `preparing` = countdown / reset; `armed` = official take may finish at story end.
   */
  const runStartGateRef = useRef<'idle' | 'preparing' | 'armed'>('idle')
  /**
   * Story-ms where the finished take parked (Stop / story-end). Used to tell a
   * *stale* Running Clock still sitting on the stop beat from an intentional
   * ResumeAt elsewhere on the tape. Not a timer — see staleRunningAfterTakeStop.
   */
  const finishedRunStopMsRef = useRef<number | null>(null)
  /**
   * Peer (or operator mid StartRun): after StartRun patch / local reset, ignore
   * late countdown Pause Clocks until a Running Clock admits — otherwise strip
   * sticks on Paused · Run N at story start with RUN N PAUSED chrome.
   */
  const awaitingStartRunRunningRef = useRef(false)
  /**
   * Version switch in flight — Snapshot must not re-adopt the prior version’s tape
   * overlay (ApplyBoardSeed Snapshot used to restore stale tapeReviewRunId via ?? cur).
   */
  const versionSwitchHoldRef = useRef<{
    gen: number
    versionId: string
  } | null>(null)
  /** Strip / map chrome: true while hub ApplyBoardSeed + settle is in flight. */
  const [versionSwitching, setVersionSwitching] = useState(false)
  /** Peer-only veil timer — do not use versionSwitchHoldRef (that blocks Review sync). */
  const peerVersionSwitchClearRef = useRef<number | null>(null)
  /** Followers: brief Switching chrome while a peer ApplyBoardSeed / Snapshot lands. */
  function beginPeerVersionSwitchChrome(): void {
    if (versionSwitchHoldRef.current) return
    if (peerVersionSwitchClearRef.current != null) {
      window.clearTimeout(peerVersionSwitchClearRef.current)
    }
    setVersionSwitching(true)
    peerVersionSwitchClearRef.current = window.setTimeout(() => {
      peerVersionSwitchClearRef.current = null
      if (versionSwitchHoldRef.current) return
      setVersionSwitching(false)
    }, 750)
  }
  /**
   * Admit take-phase wire against the hold + Start gate.
   * Returns values to apply; may clear the hold when the room confirms after arming.
   */
  function admitTakePhaseWire(
    wireN: number | null,
    wireStopped: boolean | null
  ): { take: number | null; stopped: boolean | null } {
    const hold = takePhaseHoldRef.current
    if (!hold) return { take: wireN, stopped: wireStopped }
    let take = wireN
    let stopped = wireStopped
    if (wireStopped != null && wireStopped !== hold.stopped) stopped = hold.stopped
    if (wireN != null && wireN !== hold.take) take = hold.take
    // While preparing, keep the latch — stale Seek/Snapshot take#=0 must not win.
    if (runStartGateRef.current === 'preparing') {
      return {
        take: take ?? hold.take,
        stopped: stopped ?? hold.stopped,
      }
    }
    if (
      wireN === hold.take &&
      (wireStopped == null || wireStopped === hold.stopped)
    ) {
      takePhaseHoldRef.current = null
    }
    return { take: take ?? hold.take, stopped }
  }
  /** Go Live: ignore Pause→Seek Planned echoes (and Start’s brief running) until Idle sticks. */
  const goLiveHoldIdRef = useRef<string | null>(null)
  /**
   * Instant Replay: story ms when the Live take was paused. Resume returns here;
   * scrub while paused only moves the replay head.
   */
  const instantReplayPauseMsRef = useRef<number | null>(null)
  /** Trainer pinned the time bar open between Exercise takes. */
  const [timeBarPinned, setTimeBarPinned] = useState(false)
  /** Auto-hide after Stop (fade); cleared on Run / pin. */
  const [timeBarAutoHidden, setTimeBarAutoHidden] = useState(false)
  /** Bumps enter animation when a take starts. */
  const [timeBarEnterKey, setTimeBarEnterKey] = useState(0)
  /** Exercise take counter (0 = poised / not yet run). */
  const [takeNumber, setTakeNumber] = useState(0)
  /** Instant Replay mode — top clock frozen at pause; bottom bar is the tape. */
  const [instantReplayEngaged, setInstantReplayEngaged] = useState(false)
  const instantReplayEngagedRef = useRef(false)
  instantReplayEngagedRef.current = instantReplayEngaged
  /** Mirror of pause ref so mission-clock freeze re-renders for spectators. */
  const [instantReplayPauseMs, setInstantReplayPauseMs] = useState<number | null>(null)
  /** Tape playing inside Instant Replay (does not mean the official run is Running). */
  const [replayPlaying, setReplayPlaying] = useState(false)
  const replayPlayingRef = useRef(false)
  replayPlayingRef.current = replayPlaying
  /**
   * Between-takes tape review: read-only view over a finished run’s seed + ActionsJson
   * slice. Does not replace the working board in `session`.
   */
  const [tapeReview, setTapeReview] = useState<{
    runId: string
    actions: ScenarioAction[]
  } | null>(null)
  const tapeReviewRef = useRef(tapeReview)
  tapeReviewRef.current = tapeReview
  const tapeReviewActive = tapeReview != null
  const tapeAvailableUntilMsRef = useRef<number | null>(null)
  const enterTapeReviewRef = useRef<() => void>(() => {})
  /** Bump to restart the IR tape ticker after a seek so arrows work while playing. */
  const [replayPlayEpoch, setReplayPlayEpoch] = useState(0)
  /** Personal scratch pad — local strokes, independent of Freehand / Instant Replay. */
  const [scribbleMode, setScribbleMode] = useState(false)
  const scribbleModeRef = useRef(false)
  scribbleModeRef.current = scribbleMode
  const [scribbleStrokes, setScribbleStrokes] = useState<
    { id: string; path: [number, number][] }[]
  >([])
  /** Official run rate before IR speed changes — restored on exit/Resume. */
  const replaySavedRateRef = useRef<number | null>(null)
  /** Ignore Clock.rate echoes briefly after restoring the pre-IR rate. */
  const rateRestoreUntilRef = useRef(0)
  /** Ignore hub seek echoes while local scrub / tape owns the head (ms timestamp). */
  const localReplayDriveUntilRef = useRef(0)
  /** Spectator IR tape interpolation base — re-anchored on each inbound Clock. */
  const spectatorTapeAnchorRef = useRef({ wall: 0, elapsed: 0 })
  /** Last IR align seq applied on this client — beat skip / scrub bumps hub seq. */
  const irAlignSeqAppliedRef = useRef(0)
  /**
   * Ignore stale Pause/Seek echoes after Resume/Seek chains (~1.2s). Dicey under slow RTT;
   * see docs/SCENARIO_TRANSPORT_SCRUB_PLAN.md § Clock echo fundamentals.
   */
  const localPreviewDriveUntilRef = useRef(0)
  /** Path / vis / radius / wind Preview ticker owns the strip — ignore hub Clock playhead. */
  const seriesPreviewActiveRef = useRef(false)
  /**
   * Local preview ticker base — re-anchored on beat skip so Left/Right while playing
   * both stick (stale interval used to keep writing past a rewind).
   */
  const localPlayAnchorRef = useRef({ wall: 0, elapsed: 0, rate: 1 })
  /**
   * Coalesced studio clock commands (Pause / Resume / PreviewPlay / Seek).
   * Rapid Space + arrows share one pump so hub calls never interleave.
   * While the pump owns the head, Clock playhead does not override local elapsed.
   */
  type StudioClockDesire = {
    running: boolean
    seekMs: number | null
    playAs: 'resume' | 'previewPlay'
    rate: number
    setRate: boolean
    /** Scrub-begin: hubPause without freezing a seekMs (commit Seek owns the head). */
    pauseWithoutPin?: boolean
  }
  const studioClockDesireRef = useRef<StudioClockDesire | null>(null)
  /** Re-read desire without CFA treating `.current = null` as permanently null. */
  const peekStudioClockDesire = (): StudioClockDesire | null => studioClockDesireRef.current
  const studioClockPumpRef = useRef<Promise<void> | null>(null)
  /** Same flag name used by tickers — true while pause/play/seek pump owns the head. */
  const hubSeekOwnsHeadRef = useRef(false)
  /** After ←, head can still read ahead of the seek; next ← steps from this mark. */
  const beatNavAnchorMsRef = useRef<number | null>(null)
  /** Kermit hotkey — same pattern as Instant Replay (avoid stale closures). */
  const scribbleHotkeyRef = useRef<() => void>(() => {})
  /** Last hubSeek wall time during IR tape play (throttle peer updates). */
  const replaySeekWallRef = useRef(0)
  /**
   * Bumps on IR tape Play/Pause/scrub-pause/end so pending progress Seeks are
   * dropped (progress must not race Space pauseTape/startTape).
   */
  const irTapeSeekGenRef = useRef(0)
  /** Latest coalesced progress ms waiting for the serial IR Seek lane. */
  const irTapePendingProgressMsRef = useRef<number | null>(null)
  /**
   * Beat-skip/scrub head waiting while a play/pause control owns the lane.
   * Must not overwrite pending pause — that left hub playing=true while A looked paused
   * and P kept interpolating until the next A action.
   */
  const irTapePendingScrubMsRef = useRef<number | null>(null)
  /** Play/Pause/end/scrub Seek waiting for the serial IR Seek lane (wins over progress). */
  const irTapePendingControlRef = useRef<{
    ms: number
    pauseTape: boolean
    startTape: boolean
    /** null = plain scrub/engage Seek (no play/pause intent to confirm). */
    intentGen: number | null
    /** Beat skip / scrub — hub bumps align seq so P snaps (not play/pause). */
    pinHead: boolean
  } | null>(null)
  /** True while the IR Seek lane is draining — at most one hub Seek in flight. */
  const irTapeSeekBusyRef = useRef(false)
  /**
   * Last story-ms actually sent to hub Seek (not optimistic clocksRef).
   * enqueueStudioClock paints clocksRef before the pump runs — comparing to clocksRef
   * made curMs≈0 and skipped every IR tape Seek (P got banner only).
   */
  const lastSentHubSeekMsRef = useRef<number | null>(null)
  /**
   * Last *hub-admitted* running/paused (inbound Clock or successful pump RPC).
   * Must not read optimistic clocksRef — Space pause painted paused locally, then the
   * pump skipped hubPause and only Sought, so the server kept Running and P never stopped.
   */
  const hubAdmittedRunningRef = useRef(false)
  /**
   * Bumps when Pause supersedes an in-flight driveDeckPlayAt ResumeAt/PreviewPlay.
   * Late play success must not re-admit running or leave the hub Running after A paused.
   */
  const deckPlayGenRef = useRef(0)
  /**
   * Last playhead ms from an inbound hub Clock (not optimistic enqueue).
   * Chip Ready/Paused must use this — local elapsed can hit 0 while hub is still mid-story.
   */
  const hubWirePlayheadMsRef = useRef(0)
  const [hubWirePlayheadMs, setHubWirePlayheadMs] = useState(0)
  /** Debounce hub seeks while dragging so peers aren't flooded. */
  const scrubSeekTimerRef = useRef<number | null>(null)
  /**
   * Bumps on scrub begin/commit so an in-flight mid-drag Seek(end) cannot stamp
   * lastSent / hubWire after Seek(0) on release (end↔start thrash).
   */
  const deckScrubSeekGenRef = useRef(0)
  /** At most one Reviewing/Planned deck Seek in flight — coalesce arrows to the latest. */
  const deckScrubSeekBusyRef = useRef(false)
  /** Latest deck scrub/beat-skip ms waiting for the serial Seek lane. */
  const deckScrubPendingMsRef = useRef<number | null>(null)
  /**
   * Ignore Clock playheads off the local deck target until this wall time.
   * Intermediate Seek Clocks (or concurrency retries) used to tug the thumb before settle.
   */
  const deckSeekProtectUntilRef = useRef(0)
  /**
   * True from scrub pointer-down through commit. Suppresses tape-end / story-end
   * auto-Pause so drag end→start does not race Pause(end) against Seek(0).
   */
  const timeBarScrubbingRef = useRef(false)
  /** Hotkey-safe IR tape play/pause — persists `instantReplayPlaying` for peers. */
  const toggleReplayPlayingRef = useRef<() => void>(() => {})
  /**
   * Last operator IR tape Play/Pause/scrub-pause intent. Ignore Clock.playing that
   * disagrees until a matching Clock arrives (in-flight startTape must not beat a
   * later pauseTape). Not a wall-clock mute — cleared on confirm or newer intent.
   */
  const irTapePlayingIntentRef = useRef<{ playing: boolean; gen: number } | null>(null)
  const irTapePlayingIntentGenRef = useRef(0)
  /** Portaled tips for time-bar meta (eye / playhead clock) — same stack as scrubber tips. */
  const [timeBarMetaTip, setTimeBarMetaTip] = useState<{
    text: string
    left: number
    top: number
  } | null>(null)
  const playbackRateRef = useRef(1)
  const localSharePointerRef = useRef<{
    lng: number
    lat: number
    down?: boolean
    hoverId?: string
    cursor?: 'pointer' | 'grab' | 'grabbing'
  } | null>(null)
  /** Local Watch-Me hand — remount cue; coords live on the ref for map markers. */
  const [localSharePointerEpoch, setLocalSharePointerEpoch] = useState(0)
  const liveTransportToggleRef = useRef<() => void>(() => {})
  const canOperateSessionRef = useRef(false)
  const hubActionsLockedRef = useRef(false)
  /** Done: roster stewards may sync tapeReviewRunId via Fields (ApplyBoardSeed is blocked). */
  const canDriveDoneReviewRef = useRef(false)
  /**
   * Board as ended (objects/timeline/camera) for the version open at Done — restore when
   * local version-switch returns to that version (hub ApplyBoardSeed refuses Completed).
   */
  const doneEndBoardRef = useRef<{
    versionId: string
    objects: ScenarioSession['objects']
    timeline: ScenarioSession['timeline']
    camera: ScenarioSession['camera']
    cameraBeats: ScenarioSession['cameraBeats']
    activeMapId: ScenarioSession['activeMapId']
    comments: ScenarioSession['comments']
  } | null>(null)
  const selectedIdRef = useRef<string | null>(null)
  const canMutateSelectedRef = useRef(false)
  const selectedObjectPristineRef = useRef(false)
  const canDeleteSelectedSessionRef = useRef(false)
  const selectedSessionIdRef = useRef<string | null>(null)
  const requestDeleteSelectedRef = useRef<() => void>(() => {})
  /** Narration beat focused in Timeline edit mode — Delete/Backspace removes it. */
  const narrationDeleteFocusRef = useRef<string | null>(null)
  const removeNarrationBeatRef = useRef<(id: string) => void>(() => {})
  const canSeekTimeBarRef = useRef(false)
  const skipBeatRef = useRef<(direction: 1 | -1) => void>(() => {})
  /** Shared Preview drive (planned) — rate sync while Owner/Trainer/Editor advances the board clock. */
  const canDrivePreviewRef = useRef(false)
  /** Shared strip compression: Preview = room (not guests); Live = operators / ViewAll. */
  const canShareStripRateRef = useRef(false)
  /** Only one client should hubPause when the story hits the end (avoids Pause storms). */
  const canCommitStoryEndPauseRef = useRef(false)
  /** Preview path: wall-clock motion length (natural travel + authored holds). */
  const [pathPreviewTotalMs, setPathPreviewTotalMs] = useState<number | null>(null)
  const [pathPreviewStartMs, setPathPreviewStartMs] = useState<number | null>(null)
  const [pathPreviewEpoch, setPathPreviewEpoch] = useState(0)
  const [pathPreviewObjectId, setPathPreviewObjectId] = useState<string | null>(null)
  const [pathPreviewPose, setPathPreviewPose] = useState<{
    lng: number
    lat: number
  } | null>(null)
  const [pathPreviewWaypointIndex, setPathPreviewWaypointIndex] = useState<number | null>(null)
  const [pathPreviewHolding, setPathPreviewHolding] = useState(false)
  /** Visibility keyframe Preview — local map playhead only (strip stays on hub clock). */
  const [visPreviewTotalMs, setVisPreviewTotalMs] = useState<number | null>(null)
  const [visPreviewEpoch, setVisPreviewEpoch] = useState(0)
  const [visPreviewObjectId, setVisPreviewObjectId] = useState<string | null>(null)
  const [radiusPreviewTotalMs, setRadiusPreviewTotalMs] = useState<number | null>(null)
  const [radiusPreviewStartMs, setRadiusPreviewStartMs] = useState<number | null>(null)
  const [radiusPreviewEpoch, setRadiusPreviewEpoch] = useState(0)
  const [radiusPreviewObjectId, setRadiusPreviewObjectId] = useState<string | null>(null)
  const [radiusFocusIndex, setRadiusFocusIndex] = useState<number | null>(null)
  /**
   * Inspect series Preview playhead (wind / path / vis / radius).
   * Map + Inspect sample this; strip / mission clock stay on hub `elapsedMs`.
   */
  const [inspectPreviewMs, setInspectPreviewMs] = useState<number | null>(null)
  /** Wind timespan Preview — local keyframe walk; top mission clock stays on hub. */
  const [windPreviewTotalMs, setWindPreviewTotalMs] = useState<number | null>(null)
  const [windPreviewStartMs, setWindPreviewStartMs] = useState<number | null>(null)
  const [windPreviewEpoch, setWindPreviewEpoch] = useState(0)
  /** Story Preview started from the tornado panel. Lightbox only, same clock. */
  const [tornadoPreviewFocus, setTornadoPreviewFocus] = useState(false)
  const tornadoPreviewSawRunRef = useRef(false)
  const [visibilityFocusIndex, setVisibilityFocusIndex] = useState<number | null>(null)
  const [pathHoldLabel, setPathHoldLabel] = useState<string | null>(null)
  /** Always start on Sessions/Templates — do not restore last dock (Inspect on an empty hub felt broken). */
  const dockRef = useRef<DockTab>('scenarios')
  const setDockRef = useRef<DockSetter>(() => {})
  const setDock = useCallback<DockSetter>((next) => {
    setDockRef.current(next)
  }, [])
  /** True only while Inspect is open — parent re-renders on enter/leave, not on People↔Settings. */
  const [inspectDockOpen, setInspectDockOpen] = useState(false)
  const [mapAssetPick, setMapAssetPick] = useState<{
    hits: MapFeatureTap[]
    index: number
  } | null>(null)
  /** Details cover the right rail. The dock underneath stays until the cover is dismissed. */
  const [mapAssetDetailsOpen, setMapAssetDetailsOpen] = useState(false)
  /** Pending invites the operator already opened Sessions to see — badge only for unseen ones. */
  const [seenInviteIds, setSeenInviteIds] = useState<Set<string>>(() => new Set())
  /** Pane open before a “N viewing” jump to People — click again restores it. */
  const peopleViewReturnDockRef = useRef<DockTab | null>(null)
  /** Return dock after toggling Surface wind Inspect (same pattern as “N viewing”). */
  /** Dock to restore when clearing map selection / leaving Inspect (was Timeline, List, …). */
  const inspectReturnDockRef = useRef<DockTab | null>(null)
  const mapAssetGenRef = useRef(0)
  const mapAssetDismissRef = useRef<() => void>(() => {})
  const mapAssetOpenRef = useRef(false)
  const mapAssetDetailsOpenRef = useRef(false)
  const [notesCtx, setNotesCtx] = useState<NotesCtx>('scenario')
  /** Bumps when local chat read watermark changes so the spine badge re-renders. */
  const [chatReadEpoch, setChatReadEpoch] = useState(0)
  const [hoverIds, setHoverIds] = useState<string[]>([])
  const [hoverElapsedMs, setHoverElapsedMs] = useState<number | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false)
  /** Exercise Stop — end this timed run (session stays Live). */
  const [stopRunConfirmOpen, setStopRunConfirmOpen] = useState(false)
  /** Friendly run-options modal — opened only from the strip status widget. */
  const [postRunOfferOpen, setPostRunOfferOpen] = useState(false)
  /**
   * Inline duplicate-version UI inside the run-options modal (avoids prompt + stacked modals).
   * `name` = editing the copy title; `done` = confirmation after clone.
   */
  const [versionDupUi, setVersionDupUi] = useState<
    null | { mode: 'name'; draft: string } | { mode: 'done'; name: string }
  >(null)
  const versionDupUiRef = useRef(versionDupUi)
  versionDupUiRef.current = versionDupUi
  const postRunOfferOpenRef = useRef(false)
  postRunOfferOpenRef.current = postRunOfferOpen
  /** Shared 5…4…3…2…1 before Start Run (operator drives; peers mirror via session fields). */
  const [runCountdown, setRunCountdown] = useState<number | null>(null)
  const runCountdownDriverRef = useRef(false)
  /** Wall-ms deadline for shared 5…4…3…2…1 — peers tick locally (not Fields-per-second). */
  const runCountdownEndsAtRef = useRef<number | null>(null)
  const runCountdownOptsRef = useRef<{ continueDirty?: boolean; slug?: string | null } | undefined>(
    undefined
  )
  /** Display run # shown on the countdown overlay (per-version next). */
  /** Which finished run the room is reviewing (Watch) — latest after Stop / story switch. */
  const [watchingRunId, setWatchingRunId] = useState<string | null>(null)
  /**
   * What the trainer is doing with the board between / around runs.
   * Strip status answers this; Story dropdown answers which setup.
   */
  const [boardActivity, setBoardActivity] = useState<
    'watching' | 'preview' | 'editing' | 'running' | 'ready'
  >('ready')
  const boardActivityRef = useRef(boardActivity)
  boardActivityRef.current = boardActivity
  const [goLiveConfirmOpen, setGoLiveConfirmOpen] = useState(false)
  const [privateConfirmOpen, setPrivateConfirmOpen] = useState(false)
  /** Publish or Share panel (link / QR / Invite people). */
  const [sharePanelOpen, setSharePanelOpen] = useState(false)
  const [sharePanelMode, setSharePanelMode] = useState<'publish' | 'share'>('publish')
  const [pendingDeleteObjectId, setPendingDeleteObjectId] = useState<string | null>(null)
  /** Roster soft-remove confirm (same modal pattern as Inspect delete). */
  const [pendingRemovePeopleIds, setPendingRemovePeopleIds] = useState<string[] | null>(null)
  const [clockEpoch, setClockEpoch] = useState(0)
  const [nearbyCandidates, setNearbyCandidates] = useState<MapFeatureTap[]>([])
  const [nearbyPicks, setNearbyPicks] = useState<Set<string>>(() => new Set())
  const [nearbyHoverKey, setNearbyHoverKey] = useState<string | null>(null)
  const [linkedHoverKey, setLinkedHoverKey] = useState<string | null>(null)
  /** Toggle Mapbox props debug panel — set by clicking a row's type icon. */
  const [assetDebugKey, setAssetDebugKey] = useState<string | null>(null)
  const linkedGeomRefreshIdRef = useRef<string | null>(null)
  const mapIdleBumpAtRef = useRef(0)
  const [mapIdleEpoch, setMapIdleEpoch] = useState(0)
  const [nearbyHint, setNearbyHint] = useState<string | null>(null)
  const [nearbySort, setNearbySort] = useState<{
    col: NearbySortCol
    dir: 'asc' | 'desc'
  }>({
    col: 'dist',
    dir: 'asc',
  })
  const [linkedSort, setLinkedSort] = useState<{
    col: LinkedSortCol
    dir: 'asc' | 'desc'
  }>({
    col: 'dist',
    dir: 'asc',
  })
  const [jsonDebug, setJsonDebug] = useState<{
    kind: 'scenario' | 'store' | 'people' | 'profile' | 'actions'
    title: string
    body: string
  } | null>(null)
  const [jsonCopyNote, setJsonCopyNote] = useState<string | null>(null)
  const jsonCopyTimerRef = useRef<number | null>(null)
  const [titleEditing, setTitleEditing] = useState(false)
  const mapApiRef = useRef<ScenarioMapApi | null>(null)
  const holdCamRef = useRef<{ lng: number; lat: number; zoom: number } | null>(null)
  const toggleClockRef = useRef<() => void>(() => {})
  /** Strip Play/Pause/Resume — time-bar triangle clicks through to the same control. */
  const stripPlayBtnRef = useRef<HTMLButtonElement | null>(null)
  const previewSpaceRef = useRef(false)
  const liveRunSpaceRef = useRef(false)
  /** Live take in progress (running or paused mid-take) — not Live-idle story preview. */
  const liveTakeActiveRef = useRef(false)
  /** Hub-shared take number — IR helpers read this (0 = Ready / pre–Run 1 preview). */
  const takeNumberRef = useRef(0)
  /** Hub-shared take stopped — peers toast when this flips true on the wire. */
  const exerciseRunStoppedRef = useRef(false)
  /**
   * After StopRun / take-stopped edge: keep Reviewing · Paused at the stop stamp until
   * this wall time. Blocks late Running Clocks from advancing past the park.
   * Cleared on StartRun / scrub / intentional Seek / Space wrap to T0.
   */
  const reviewingStopPinUntilRef = useRef(0)
  const [windChipHint, setWindChipHint] = useState(false)
  const [viewResetId, setViewResetId] = useState(0)
  const [timelineFocusId, setTimelineFocusId] = useState<string | null>(null)
  /** Local Timeline pane edit mode — not persisted. */
  const [timelineEditMode, setTimelineEditMode] = useState(false)
  /** Newly inserted narration beat — keep form expanded / focused. */
  const [narrationDraftId, setNarrationDraftId] = useState<string | null>(null)
  const [waypointFocusIndex, setWaypointFocusIndex] = useState<number | null>(null)
  const [waypointsOpen, setWaypointsOpen] = useState(false)
  const [windAuthorMs, setWindAuthorMs] = useState(0)
  const [windDraft, setWindDraft] = useState<{
    fromDeg: number
    speedMph: number
    plumeDistanceM: number
    plumeSpreadDeg: number
  } | null>(null)
  const [windHoverIndex, setWindHoverIndex] = useState<number | null>(null)
  const [_windSelectIndex, setWindSelectIndex] = useState<number | null>(null)
  const windTimeFocusedRef = useRef(false)
  const [windPresetId, setWindPresetId] = useState<WindPresetId>('steady_northerly')
  const [windPresetOnsetMs, setWindPresetOnsetMs] = useState(0)
  const [windPresetSpanMs, setWindPresetSpanMs] = useState(
    () => windPresetById('steady_northerly').defaultSpanMs
  )
  const [windPresetsOpen, setWindPresetsOpen] = useState(false)
  const [tornadoPresetId, setTornadoPresetId] = useState('')
  const [tornadoPresetOnsetMs, setTornadoPresetOnsetMs] = useState(0)
  const [tornadoPresetSpanMs, setTornadoPresetSpanMs] = useState(
    () => tornadoPresetById('graze').defaultSpanMs
  )
  /** Direction the hurricane center travels. 0 is north. */
  const [hurricaneTrackHeadingDeg, setHurricaneTrackHeadingDeg] = useState(0)
  /** Last tornado whose preset onset was seeded from the story clock. Reselect keeps a manual onset. */
  const tornadoOnsetSeededForRef = useRef<string | null>(null)
  const [visibilityOpen, setVisibilityOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)
  const [radiusOpen, setRadiusOpen] = useState(false)
  const [statusFocusIndex, setStatusFocusIndex] = useState<number | null>(null)
  const [waypointFlashIndex, setWaypointFlashIndex] = useState<number | null>(null)
  const waypointFlashTimerRef = useRef<number | null>(null)
  const [labelStyle, setLabelStyle] = useState<{
    text: string
    color: string
    size: ObjectSize
  }>({
    text: 'Label',
    color: '#ffffff',
    size: 'md',
  })
  const [sketchStyle, setSketchStyle] = useState({
    color: '#E24B4A',
    width: 4,
    opacity: 0.9,
    label: '',
  })
  const [rulerStats, setRulerStats] = useState<{
    points: number
    totalM: number
    lastM: number
  } | null>(null)
  const applyRosterPickRef = useRef<(row: StudioPeopleHit) => void>(() => {})
  const onRosterPick = useCallback((row: StudioPeopleHit) => {
    applyRosterPickRef.current(row)
  }, [])
  const [peopleSelectMode, setPeopleSelectMode] = useState(false)
  const [selectedPeopleIds, setSelectedPeopleIds] = useState<string[]>([])
  const [studioPermsByEmail, setStudioPermsByEmail] = useState<
    ReadonlyMap<string, readonly string[]>
  >(() => new Map())
  /** Emails we already asked the directory about, so a missing ceiling is not refetched. */
  const rosterCeilingRequestedRef = useRef(new Set<string>())
  const [directoryNameByEmail, setDirectoryNameByEmail] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  )
  const [directoryUserIdByEmail, setDirectoryUserIdByEmail] = useState<
    ReadonlyMap<string, string>
  >(() => new Map())
  /** Author map projection — independent of Role override (participant forces audience). */
  const [boardRevealMode, setBoardRevealMode] = useState<BoardRevealMode>('author')
  const [peopleInviteHint, setPeopleInviteHint] = useState<string | null>(null)
  const [peopleInviteFlash, setPeopleInviteFlash] = useState<'Sent' | 'Nudged' | 'Reminded' | null>(
    null
  )
  const peopleInviteFlashTimer = useRef<number | null>(null)
  const registrationMailLock = useRef(new Set<string>())
  /** Ids added via + that still need a first invite — drives invite-first selection chrome. */
  const [rosterPendingInviteIds, setRosterPendingInviteIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  /** Include docs checkbox off; dropdown defaults to All generated docs when enabled. */
  const [includeInviteDocs, setIncludeInviteDocs] = useState(false)
  const [inviteDocChoice, setInviteDocChoice] = useState<'plan' | 'trainer' | 'cheatsheet' | 'all'>(
    'all'
  )
  const [inviteIntro, setInviteIntro] = useState('')
  const [inviteAttachFiles, setInviteAttachFiles] = useState<File[]>([])
  const inviteAttachInputRef = useRef<HTMLInputElement | null>(null)
  const [getStartedOpen, setGetStartedOpen] = useState(false)
  const [getStartedPhase, setGetStartedPhase] = useState<'pick' | 'done'>('pick')
  const [getStartedType, setGetStartedType] = useState<SessionTypeId | null>(null)
  const [getStartedDoneMeta, setGetStartedDoneMeta] = useState<WizardDoneMeta | null>(null)
  const [getStartedBusy, setGetStartedBusy] = useState(false)
  const [getStartedForceArrive, setGetStartedForceArrive] = useState(true)
  /** Auto cold-open → “Not now”; Help / manual reopen → “Cancel”. */
  const [getStartedDismissLabel, setGetStartedDismissLabel] = useState('Not now')
  /** Settings Set Location, or place-before-create for Templates → Use blank. */
  const [boardLocationEditor, setBoardLocationEditor] = useState<null | {
    mode: 'settings' | 'template-pre'
    camera: BoardCamera
    mapId: string
    instanceId?: string
    templateId?: string
  }>(null)
  const [boardLocationBusy, setBoardLocationBusy] = useState(false)
  /** Brief flash on a Sessions card after clone-from-template (or similar). */
  const getStartedAutoPromptedRef = useRef(false)
  /** Empty Sessions hub → wizard once per empty episode (avoids reopen loop after Cancel). */
  const emptyHubWizardPromptedRef = useRef(false)
  /** Created by wizard but not opened yet (returning users who may dismiss). */
  const getStartedPendingIdRef = useRef<string | null>(null)
  const getStartedResumeRef = useRef<{
    instanceId: string
    libraryMode: boolean
    previewTemplateId: string | null
  } | null>(null)
  /** Deep-link board missing / closed / no access — soft 404, not a silent jump. */
  const [sessionUnavailableOpen, setSessionUnavailableOpen] = useState(false)
  /** Upper toast after declining an invite (not the bottom join stack). */
  const [declineThanksOpen, setDeclineThanksOpen] = useState(false)
  /** Pending invitee dismissed the Join card — sneak peek + chip; reload clears this. */
  const [inviteJoinPeekId, setInviteJoinPeekId] = useState<string | null>(null)
  /** Anchor for shift-click range select in the people list. */
  const peopleSelectAnchorIdRef = useRef<string | null>(null)
  /** Anchor for shift-click range select in Find-nearby picks. */
  const nearbySelectAnchorKeyRef = useRef<string | null>(null)
  const elapsedRef = useRef(0)
  /** Set by the Running ticker — when true, elapsedRef is not overwritten from React state. */
  const roomClockRunningRef = useRef(false)
  // While a local ticker owns the head, do not clobber elapsedRef from React state
  // (re-renders — e.g. each IR progress Seek's setHubWirePlayheadMs — would stair-step
  // pose sampling). Running RAF and IR tape play both advance the ref on an interval.
  if (
    !running &&
    !roomClockRunningRef.current &&
    !replayPlayingRef.current
  ) {
    // Hold Reviewing at the stop stamp while the pin is armed — a re-render must
    // not yank the head (e.g. stale T0 from an old StopRun build).
    if (
      exerciseRunStoppedRef.current &&
      performance.now() < reviewingStopPinUntilRef.current
    ) {
      const stopMs =
        finishedRunStopMsRef.current ?? tapeAvailableUntilMsRef.current
      if (stopMs != null && stopMs > 250) elapsedRef.current = stopMs
      else elapsedRef.current = elapsedMs
    } else {
      elapsedRef.current = elapsedMs
    }
  }
  const currentRowRef = useRef<HTMLDivElement | null>(null)
  const timelineScrollRef = useRef<HTMLDivElement | null>(null)
  const scenarioRailRef = useRef<HTMLDivElement | null>(null)
  const instanceIdRef = useRef(instanceId)
  const persistedObjectsRef = useRef(new Map<string, string>())
  const pendingObjectUpsertsRef = useRef(new Map<string, ScenarioObject>())
  const pendingObjectRemovesRef = useRef(new Set<string>())
  const objectSaveTimerRef = useRef<number | null>(null)
  const objectFlushAtRef = useRef(0)
  /** One in-flight flush at a time — overlapping UpsertObject hits ConcurrencyStamp races. */
  const objectFlushChainRef = useRef<Promise<void>>(Promise.resolve())
  const objectWriteInstanceRef = useRef<string | null>(null)
  const objectMotionsRef = useRef(
    new Map<
      string,
      {
        target: ScenarioObject
        dLng: number
        dLat: number
        started: number
        ms: number
      }
    >()
  )
  const objectMotionRafRef = useRef<number | null>(null)
  const mayPersistObjectsRef = useRef(false)
  const canLeadWatchRef = useRef(false)
  const canCommentRef = useRef(false)
  const persistedCommentsRef = useRef('')
  const pendingCommentsRef = useRef<ScenarioComment[] | null>(null)
  const commentSaveTimerRef = useRef<number | null>(null)
  const pendingRoomChatRef = useRef<ScenarioComment[] | null>(null)
  const roomChatFlushInFlightRef = useRef(false)
  /** Coalesce huge boardVersions Fields writes — every object persist used to slam the row. */
  const versionFieldsFlushTimerRef = useRef<number | null>(null)
  const pendingVersionFieldsRef = useRef<ScenarioSession | null>(null)
  const pendingVersionSpineRef = useRef(false)
  /** True only while a wind dial / slider is pointer-down (sampled playhead must not persist). */
  const windUserEditRef = useRef(false)
  /** Coalesce compass / slider writes while sitting on a wind mark. */
  const windPersistTimerRef = useRef<number | null>(null)
  const runningRef = useRef(false)
  /** Instance the in-flight audience save belongs to, so a late echo cannot retarget the next board. */
  const audienceWriteIdRef = useRef<string | null>(null)
  instanceIdRef.current = instanceId
  const clocksRef = useRef(new Map<string, StudioClock>())
  const serverListReadyRef = useRef(false)
  const [livePulse, setLivePulse] = useState(0)
  const snapshotRef = useRef({
    session,
    people,
    elapsedMs,
    started,
    running,
    actualStartTime,
    stopped,
  })
  snapshotRef.current = {
    session,
    people,
    elapsedMs,
    started,
    running,
    actualStartTime,
    stopped,
  }
  /**
   * MapHost live pose sampling. Uses elapsedRef (kept hot by the Running ticker);
   * null when previews/IR own the head or the room clock is not running.
   */
  const mapPlayheadSampleBlockedRef = useRef(false)
  const sampleMapPlayheadIso = useCallback((): string | null => {
    if (mapPlayheadSampleBlockedRef.current) return null
    const clock = clocksRef.current.get(instanceIdRef.current)
    const start = snapshotRef.current.session.storyStartTime
    if (!start) return null
    // While Seek/Play owns the head, keep sampling elapsed even if a Seek echo
    // briefly paints clocksRef paused — MapHost otherwise falls back to
    // stack.anchor and fights poseFromKeyframes (±1px jitter at story start).
    if (hubSeekOwnsHeadRef.current && runningRef.current) {
      return addStoryMs(start, elapsedRef.current)
    }
    if (!clock || clock.clockStatus !== 'running') return null
    return addStoryMs(start, elapsedRef.current)
  }, [])

  const templatePreview = useMemo(() => {
    if (!previewTemplateId) return null
    const base = healSyncedTimeline(
      withSessionSpan(cloneSession(templateById(templates, previewTemplateId).session))
    )
    const ensured = ensureBoardVersions(base, {
      userId: user.userId ?? null,
      name: shortPersonName(user.name ?? '') ?? user.email ?? null,
    })
    const versions = ensured.boardVersions ?? []
    const want =
      (templatePreviewVersionId &&
        versions.some((v) => v.id === templatePreviewVersionId) &&
        templatePreviewVersionId) ||
      ensured.selectedVersionId ||
      versions[0]?.id
    if (!want) return ensured
    const ver = findBoardVersion(versions, want)
    if (!ver) return { ...ensured, selectedVersionId: want }
    return applyBoardSeed({ ...ensured, selectedVersionId: want }, ver.seed)
  }, [previewTemplateId, templates, templatePreviewVersionId, user.userId, user.name, user.email])
  const viewingTemplate = previewTemplateId != null
  // Template write (POC): session is a mutable draft seeded from the canned template.
  // Otherwise template preview is a fresh read-only seed each time.
  const tapePlayheadIso = addStoryMs(session.storyStartTime, elapsedMs)
  const tapeViewSession = useMemo(() => {
    if (!tapeReview) return null
    const run = (session.exerciseRuns ?? []).find((r) => r.id === tapeReview.runId)
    if (!runHasTapeSeed(run) || !run?.seed) return null
    return sessionForTapeReview(session, run.seed, tapeReview.actions, tapePlayheadIso)
  }, [tapeReview, session, tapePlayheadIso])
  /**
   * Full story bar; scrub/play clamp + blackout past this (run stop / story-end).
   * Prefer the reviewing run stamp; when take-stopped without a tape overlay yet (peer
   * missed seed), still black out from the finished run / StopRun patch stamp.
   */
  const tapeAvailableUntilMs = useMemo(() => {
    const runs = session.exerciseRuns ?? []
    if (tapeReview) {
      const run = runs.find((r) => r.id === tapeReview.runId)
      const fromTape = resolveTapeAvailableUntilMs(
        run,
        session.storyStartTime,
        tapeReview.actions
      )
      if (fromTape != null) return fromTape
    }
    if (!exerciseRunStopped) return null
    const run =
      (watchingRunId ? runs.find((r) => r.id === watchingRunId) : null) ??
      latestExerciseRunForVersion(session) ??
      latestExerciseRun(session)
    if (
      run &&
      typeof run.stoppedStoryElapsedMs === 'number' &&
      Number.isFinite(run.stoppedStoryElapsedMs) &&
      run.stoppedStoryElapsedMs > 250
    ) {
      return Math.max(0, Math.floor(run.stoppedStoryElapsedMs))
    }
    const fromRef = finishedRunStopMsRef.current
    if (fromRef != null && fromRef > 250) return fromRef
    return null
  }, [
    tapeReview,
    session,
    session.exerciseRuns,
    session.storyStartTime,
    exerciseRunStopped,
    watchingRunId,
  ])
  tapeAvailableUntilMsRef.current = tapeAvailableUntilMs
  const viewSessionRaw =
    tapeViewSession ??
    (viewingTemplate && !templateEditLive ? (templatePreview ?? session) : session)
  const viewSession = useMemo(
    () => cloakEggSession(viewSessionRaw, canViewEggs),
    [viewSessionRaw, canViewEggs]
  )
  void livePulse
  mapPlayheadSampleBlockedRef.current =
    instantReplayEngaged ||
    previewTemplateId != null ||
    pathPreviewTotalMs != null ||
    visPreviewTotalMs != null ||
    radiusPreviewTotalMs != null ||
    windPreviewTotalMs != null
  const viewElapsedMs = elapsedMs
  const playheadIso = addStoryMs(viewSession.storyStartTime, viewElapsedMs)
  const effectiveRevealMode: BoardRevealMode =
    accountBoardMode === 'following' ? 'audience' : boardRevealMode
  const resolved = useMemo(() => {
    const base = resolveSession(
      viewSession,
      playheadIso,
      accountBoardMode,
      effectiveRevealMode,
      ghostFlash
    )
    let objects = base.objects
    if (pathPreviewPose && pathPreviewObjectId) {
      objects = objects.map((o) =>
        o.id === pathPreviewObjectId ? { ...o, pose: pathPreviewPose } : o
      )
    }
    // Live dial edits while wind keyframes are active — params alone are overwritten by sampleWindAt.
    const draftTarget = selectedId ? viewSession.objects.find((o) => o.id === selectedId) : null
    if (
      windDraft &&
      draftTarget &&
      (draftTarget.type === 'wind' || isLocalStorm(draftTarget.type))
    ) {
      const id = draftTarget.id
      objects = objects.map((o) =>
        o.id === id
          ? {
              ...o,
              params: {
                ...o.params,
                fromDeg: windDraft.fromDeg,
                speedMph: windDraft.speedMph,
                plumeDistanceM: windDraft.plumeDistanceM,
                plumeSpreadDeg: windDraft.plumeSpreadDeg,
              },
            }
          : o
      )
    }
    return objects === base.objects ? base : { ...base, objects }
  }, [
    viewSession,
    viewElapsedMs,
    accountBoardMode,
    effectiveRevealMode,
    pathPreviewPose,
    pathPreviewObjectId,
    windDraft,
    selectedId,
    ghostFlash,
  ])
  const liveBoardOnScreen =
    !viewingTemplate &&
    !stopped &&
    instances.some((i) => i.id === instanceId && i.lifecycle === 'running')
  const mapElapsedMs =
    inspectPreviewMs != null
      ? inspectPreviewMs
      : running || liveBoardOnScreen
        ? viewElapsedMs
        : (hoverElapsedMs ?? viewElapsedMs)
  const mapPlayheadIso = addStoryMs(viewSession.storyStartTime, mapElapsedMs)
  // A dial draft is the unsaved wind at one time. Once the clock moves, the
  // arrow and compass follow the storm's keyframes instead of that one value.
  const windDraftPlayheadBucket = Math.round(mapElapsedMs / 250)
  useEffect(() => {
    setWindDraft((cur) => (cur == null ? cur : null))
  }, [windDraftPlayheadBucket])
  const off = (iso: string | null | undefined): number =>
    iso ? storyDeltaMs(viewSession.storyStartTime, iso) : 0
  const atOff = (ms: number): string => addStoryMs(viewSession.storyStartTime, ms)
  const mapResolved = useMemo(() => {
    const base = resolveSession(
      viewSession,
      mapPlayheadIso,
      accountBoardMode,
      effectiveRevealMode,
      ghostFlash
    )
    let objects = base.objects
    if (pathPreviewPose && pathPreviewObjectId) {
      objects = objects.map((o) =>
        o.id === pathPreviewObjectId ? { ...o, pose: pathPreviewPose } : o
      )
    }
    const draftTarget = selectedId ? viewSession.objects.find((o) => o.id === selectedId) : null
    if (
      windDraft &&
      draftTarget &&
      (draftTarget.type === 'wind' || isLocalStorm(draftTarget.type))
    ) {
      const id = draftTarget.id
      objects = objects.map((o) =>
        o.id === id
          ? {
              ...o,
              params: {
                ...o.params,
                fromDeg: windDraft.fromDeg,
                speedMph: windDraft.speedMph,
                plumeDistanceM: windDraft.plumeDistanceM,
                plumeSpreadDeg: windDraft.plumeSpreadDeg,
              },
            }
          : o
      )
    }
    const nextObjects = objects.length === 0 ? EMPTY_MAP_OBJECTS : objects
    return nextObjects === base.objects ? base : { ...base, objects: nextObjects }
  }, [
    viewSession,
    mapElapsedMs,
    accountBoardMode,
    effectiveRevealMode,
    pathPreviewPose,
    pathPreviewObjectId,
    windDraft,
    selectedId,
    ghostFlash,
  ])
  /**
   * Open session's type package includes a story clock (Walkthrough / Exercise).
   * Chrome only — Whiteboard JSON may still hold timeline / From–Until; we ignore it in UI.
   */
  const sessionOffersStoryClock = typeOffersStoryClock(viewSession.sessionType)
  /** Whiteboard uses session-window controls only; story strip needs a story-clock type. */
  const showStoryStrip = sessionOffersStoryClock
  const displayTimeline = useMemo(
    () => mergeTimeline(healSyncedTimeline(healSessionCopy(viewSession))),
    [viewSession]
  )
  const displayDur = displayDurationMs(viewSession)
  const storyBarCap =
    (tapeReviewActive || exerciseRunStopped) && tapeAvailableUntilMs != null
      ? tapeAvailableUntilMs
      : instantReplayEngaged && instantReplayPauseMs != null
        ? instantReplayPauseMs
        : displayDur
  const storyBarElapsedMs = Math.min(
    Math.max(0, mapElapsedMs),
    storyBarCap > 0 ? storyBarCap : 0,
  )
  const authorClock = useMemo<ScenarioClockConfig>(
    () => ({
      startsAt: viewSession.storyStartTime,
      timezone: viewSession.timezone,
      durationMs: displayDur,
      timeFormat: viewSession.timelineTimeFormat ?? 'hms',
      showTimeZone: viewSession.showTimeZone,
    }),
    [
      viewSession.storyStartTime,
      viewSession.timezone,
      displayDur,
      viewSession.timelineTimeFormat,
      viewSession.showTimeZone,
    ]
  )
  // No instances[0] fallback — hub with nothing selected parks a blank map and
  // must not inherit lifecycle/Publish chrome from an unrelated list row.
  const currentInstance = instanceId ? instances.find((i) => i.id === instanceId) : undefined
  const seeAllInstances = accessPerms.has('scenarios.view_all')
  const hasScenarioAdminRole = roleNames.some(
    (name) => name.toLowerCase() === 'scenarioadmin',
  )
  const onMyRoster = (inst: ScenarioInstance) =>
    personOnRoster(inst.people, user.email, user.userId)
  const hasOwnSession = instances.some((inst) => onMyRoster(inst))
  const myRosterRow = (inst: ScenarioInstance) => {
    if (!user.email.trim()) return undefined
    return inst.people.find(
      (p) => !p.isDeleted && p.status !== 'declined' && sameLoginEmail(p.email, user.email)
    )
  }
  const myOpenInvite = people.find((p) => !p.isDeleted && sameLoginEmail(p.email, user.email))
  const myInvitePending = Boolean(
    myOpenInvite && myOpenInvite.status === 'pending' && myOpenInvite.hasBeenInvited !== false
  )
  /** Owner-planned only — selecting/deselecting must not reshuffle Draft/Published bins. */
  const iPlanThis = (inst: ScenarioInstance) => {
    const row = myRosterRow(inst)
    return Boolean(row && personHasRole(row, 'owner'))
  }
  /**
   * May open without a roster seat: Public or Anyone-with-link — only after Publish
   * (Live/Done count as past Publish). Draft stores the toggles but does not honor them.
   */
  const instanceOpenViaAudience = (inst: ScenarioInstance) => {
    const published =
      inst.lifecycle !== 'planned' || sessionIsPublished(inst.session)
    if (!published) return false
    if (inst.allowLinkGuests) return true
    return inst.audienceType === 'public' && inst.lifecycle !== 'completed'
  }
  const instanceVisibleToMe = (inst: ScenarioInstance) =>
    seeAllInstances || onMyRoster(inst) || instanceOpenViaAudience(inst)
  /** Personal role bins (Live / Done / …): roster only. ViewAll leftovers stay in Oversight. */
  const inMyGroups = (inst: ScenarioInstance) => onMyRoster(inst)
  /** Pending invitee who still needs to Accept — Invited bin, not Live/Joined. */
  const isPendingInvitee = (inst: ScenarioInstance) => {
    if (iPlanThis(inst)) return false
    const row = myRosterRow(inst)
    return Boolean(row && row.status === 'pending' && row.hasBeenInvited !== false)
  }
  const runningInsts = instances
    .filter((i) => i.lifecycle === 'running' && inMyGroups(i) && !isPendingInvitee(i))
    .slice()
    .sort((a, b) => {
      const byStart = sortIsoDesc(
        a.actualStartTime ?? a.createdAtIso,
        b.actualStartTime ?? b.createdAtIso
      )
      if (byStart) return byStart
      const byCreated = sortIsoDesc(a.createdAtIso, b.createdAtIso)
      if (byCreated) return byCreated
      return a.id.localeCompare(b.id)
    })
  const plannedInsts = instances
    .filter((i) => i.lifecycle === 'planned' && iPlanThis(i))
    .slice()
    .sort(sortPlannedBySchedule)
  const draftInsts = plannedInsts.filter(
    (i) => statusFromLifecycle(i.lifecycle, i.session) === 'draft'
  )
  const readyInsts = plannedInsts.filter(
    (i) => statusFromLifecycle(i.lifecycle, i.session) === 'published'
  )
  // Planned or live — invitees need the Invited bin either way (Live used to claim first).
  // Live invites first so a silent draft does not sit above a happening-now card.
  const invitedInsts = instances
    .filter(
      (i) =>
        (i.lifecycle === 'planned' || i.lifecycle === 'running') &&
        onMyRoster(i) &&
        isPendingInvitee(i)
    )
    .slice()
    .sort((a, b) => {
      if (a.lifecycle !== b.lifecycle) return a.lifecycle === 'running' ? -1 : 1
      if (a.lifecycle === 'running') {
        return sortIsoDesc(a.actualStartTime ?? a.createdAtIso, b.actualStartTime ?? b.createdAtIso)
      }
      return sortPlannedBySchedule(a, b)
    })
  /** Sessions list already shows Invited; badge on Templates (any invite) or other docks (elsewhere). */
  const unseenInviteCount = invitedInsts.filter((i) => !seenInviteIds.has(i.id)).length
  const unseenElsewhereCount = invitedInsts.filter(
    (i) => i.id !== instanceId && !seenInviteIds.has(i.id)
  ).length
  const inviteBadgeAvailable = libraryMode ? unseenInviteCount > 0 : unseenElsewhereCount > 0
  const sessionsInviteBadgeCount = libraryMode ? unseenInviteCount : unseenElsewhereCount
  const sessionsInviteBadgeLive = invitedInsts.some(
    (i) => i.lifecycle === 'running' && !seenInviteIds.has(i.id)
  )
  const invitedIdsKey = invitedInsts.map((i) => i.id).join('|')
  /** Accepted invite / co-editor — invitation status, not “when”. */
  const joinedInsts = instances
    .filter(
      (i) =>
        i.lifecycle === 'planned' &&
        onMyRoster(i) &&
        !iPlanThis(i) &&
        myRosterRow(i)?.status !== 'pending'
    )
    .slice()
    .sort(sortPlannedBySchedule)
  const completedInsts = instances
    .filter((i) => i.lifecycle === 'completed' && inMyGroups(i))
    .slice()
    .sort((a, b) =>
      sortIsoDesc(a.actualEndTime ?? a.createdAtIso, b.actualEndTime ?? b.createdAtIso)
    )
  /** Public open-door discovery — same bin for everyone, including view_all. */
  const interestInsts = instances
    .filter((i) => {
      if (onMyRoster(i) || i.audienceType !== 'public') return false
      const status = statusFromLifecycle(i.lifecycle, i.session)
      return status === 'published' || status === 'live'
    })
    .slice()
    .sort((a, b) => {
      if (a.lifecycle !== b.lifecycle) return a.lifecycle === 'running' ? -1 : 1
      if (a.lifecycle === 'running') {
        return sortIsoDesc(a.actualStartTime ?? a.createdAtIso, b.actualStartTime ?? b.createdAtIso)
      }
      return sortPlannedBySchedule(a, b)
    })
  /**
   * Admin Oversight — sessions view_all can see that are not already in a personal or
   * public-interest bin. Normal bins stay roster-only; this is the only admin-only rail group.
   */
  const oversightInsts = seeAllInstances
    ? instances.filter((i) => !onMyRoster(i)).slice().sort(sortOversightRail)
    : []
  const selectedIsCompleted = !viewingTemplate && completedInsts.some((i) => i.id === instanceId)
  const selectedIsCompletedRef = useRef(selectedIsCompleted)
  selectedIsCompletedRef.current = selectedIsCompleted
  const selectedIsPlanned = !viewingTemplate && currentInstance?.lifecycle === 'planned'
  const selectedIsPlannedRef = useRef(selectedIsPlanned)
  selectedIsPlannedRef.current = selectedIsPlanned
  const canEditTemplates = accessPerms.has('scenarios.edit_templates')
  /** Library preview is always read-only; live template edit is a separate (off) mode. */
  const templatePreviewReadOnly = viewingTemplate && !templateEditLive
  const isReview =
    selectedIsCompleted || templatePreviewReadOnly || (viewingTemplate && !canEditTemplates)
  const isClockPreview = viewingTemplate || selectedIsPlanned
  /** Live run controls — also heal clockStarted/lifecycle desync so Pause never orphans the strip. */
  const inLiveRun =
    !viewingTemplate &&
    !isReview &&
    (started || currentInstance?.lifecycle === 'running') &&
    !stopped
  previewSpaceRef.current = sessionOffersStoryClock && (isClockPreview || selectedIsCompleted) && !inLiveRun
  liveRunSpaceRef.current = inLiveRun
  const showBoardChrome = !serverBooting && bootError == null
  /** Mid-session or boot: host unreachable — do not keep firing strip/hub requests. */
  const studioLinkDown =
    hubLinkState !== 'connected' ||
    isStudioUnreachableNotice(serverNotice) ||
    isStudioUnreachableNotice(bootError)
  /** Hub unreachable / reconnecting — block lifecycle + create/clone writes (UI stays visible where useful). */
  const hubActionsLocked = studioLinkDown
  const hubLockTitle =
    hubLinkState === 'reconnecting' ? 'Reconnecting…' : 'Offline — try again when connected'
  /**
   * Everyone currently on this board with presence recorded — room size for People / strip.
   * Developer-observer Join does not record presence, so those viewers are excluded (matches
   * pips / People live dots / no “is here” toast). Tenant admin helper Joins count.
   */
  const viewingCount = presentEmails.size
  /** Hubs with nothing selected still park a blank board on the map — don't title it. */
  const hubNothingSelected = !previewTemplateId && !instanceId
  /** Strip transport + Inspect spine wait for a selected board/template. */
  const showBoardTools = showBoardChrome && !hubNothingSelected
  const selectionTitle =
    hubNothingSelected || (!showBoardChrome && isBlankBoardTemplateId(viewSession.id))
      ? ''
      : viewSession.title
  const selectedStatus =
    currentInstance != null
      ? statusFromLifecycle(
          currentInstance.lifecycle,
          currentInstance.lifecycle === 'planned' ? viewSession : currentInstance.session
        )
      : null
  /**
   * Top-bar Status sign — Draft → Published → Live → Done, or TEMPLATE while
   * browsing the library (not a real lifecycle — clarifies preview vs a session).
   * Not strip transport (Preview/Play/Run).
   */
  const studioSign: {
    kind: 'draft' | 'published' | 'live' | 'done' | 'template'
    label: string
    title: string
  } | null = (() => {
    if (!showBoardTools || hubNothingSelected) return null
    if (viewingTemplate) {
      return {
        kind: 'template',
        label: 'TEMPLATE',
        title: 'Browsing a template. Create a session to run or edit your own copy.',
      }
    }
    if (selectedIsCompleted) {
      return {
        kind: 'done',
        label: 'DONE',
        title: 'Session complete. Board available for review.',
      }
    }
    if (inLiveRun) {
      return {
        kind: 'live',
        label: 'LIVE',
        title: 'Session is live',
      }
    }
    // Published beats story Preview — transport is separate from Status.
    if (selectedIsPlanned && selectedStatus === 'published') {
      return {
        kind: 'published',
        label: 'PUBLISHED',
        title: 'Published. Waiting to Go Live.',
      }
    }
    if (selectedIsPlanned) {
      return {
        kind: 'draft',
        label: 'DRAFT',
        title: 'Draft. Publish so others can find this session.',
      }
    }
    return null
  })()
  /**
   * Sessions catalog: all listable boards by status (no templateKey family filter).
   * claimRail enforces one card per id across bins — never light the same board twice.
   */
  const claimRailIds = new Set<string>()
  const claimRail = (list: ScenarioInstance[]) => {
    if (libraryMode) return [] as ScenarioInstance[]
    const out: ScenarioInstance[] = []
    for (const inst of list) {
      if (claimRailIds.has(inst.id)) continue
      claimRailIds.add(inst.id)
      out.push(inst)
    }
    return out
  }
  // Invited before Live so a pending RSVP on a running board is not swallowed by Live.
  const paneInvited = claimRail(invitedInsts)
  const paneRunning = claimRail(runningInsts)
  const paneJoined = claimRail(joinedInsts)
  const paneDraft = claimRail(draftInsts)
  const paneReady = claimRail(readyInsts)
  const panePinnedCompleted = claimRail(
    justCompletedId != null ? completedInsts.filter((inst) => inst.id === justCompletedId) : []
  )
  const paneInterest = claimRail(interestInsts)
  const paneListedCompleted = claimRail(
    justCompletedId != null
      ? completedInsts.filter((inst) => inst.id !== justCompletedId)
      : completedInsts
  )
  /** Claim last so Oversight only shows leftovers (not already in role / interest / Done). */
  const paneOversight = claimRail(oversightInsts)
  /**
   * Done folds on its own (chevron), independent of Oversight — Oversight is additive.
   * Selecting a Done board opens the fold. Other bins stay always open.
   */
  const completedFoldOpen = completedOpen
  /** Newest N stay full cards; older are squat spines (or hidden until “More”). */
  const RAIL_FULL_CARDS = 4
  const libraryTemplates = visibleTemplates(templates)
  const paneTemplates = libraryMode ? libraryTemplates : []
  const isBlankTemplate = (t: ScenarioTemplate) =>
    isBlankBoardTemplateId(t.id) || isBlankBoardTemplateId(t.session.id)
  const blankTypeOrder = (t: ScenarioTemplate): number => {
    const key = t.session.id || t.id
    if (key === BLANK_BOARD_ID || key.endsWith('/' + BLANK_BOARD_ID)) return 0
    if (key === BLANK_WALKTHROUGH_ID || key.endsWith('/' + BLANK_WALKTHROUGH_ID)) return 1
    if (key === BLANK_EXERCISE_ID || key.endsWith('/' + BLANK_EXERCISE_ID)) return 2
    return 9
  }
  const templateGroups = (
    libraryMode
      ? [
          {
            key: 'starter',
            label: 'Starter Boards',
            items: paneTemplates
              .filter(isBlankTemplate)
              .slice()
              .sort((a, b) => blankTypeOrder(a) - blankTypeOrder(b) || a.title.localeCompare(b.title)),
          },
          {
            key: 'yucca',
            label: 'Example Incidents',
            items: paneTemplates.filter(
              (t) => isYuccaElmTemplate(t.id) || isYuccaElmTemplate(t.session.id)
            ),
          },
          {
            key: 'historical',
            label: 'Historical cases',
            items: paneTemplates.filter(
              (t) => isHistoricalTemplate(t.id) || isHistoricalTemplate(t.session.id)
            ),
          },
          {
            key: 'rest',
            label: '',
            items: paneTemplates.filter(
              (t) =>
                !isBlankTemplate(t) &&
                !isYuccaElmTemplate(t.id) &&
                !isYuccaElmTemplate(t.session.id) &&
                !isHistoricalTemplate(t.id) &&
                !isHistoricalTemplate(t.session.id)
            ),
          },
        ]
      : []
  ).filter((group) => group.items.length)
  const paneIsEmpty =
    !paneRunning.length &&
    !paneInvited.length &&
    !paneJoined.length &&
    !paneDraft.length &&
    !paneReady.length &&
    !panePinnedCompleted.length &&
    !paneInterest.length &&
    !paneListedCompleted.length &&
    !paneOversight.length
  /**
   * Hub title slot when nothing is selected. The empty sessions list has its own note
   * under New session, so this slot stays blank there.
   */
  const hubTitlePlaceholder = hubNothingSelected && libraryMode
    ? 'Start from a template or a blank map'
    : null
  /** Blocking overlays — hide hub empty / avoid stacking under wizard & confirms. */
  const studioModalOpen =
    getStartedOpen ||
    sharePanelOpen ||
    goLiveConfirmOpen ||
    stopConfirmOpen ||
    stopRunConfirmOpen ||
    postRunOfferOpen ||
    privateConfirmOpen ||
    sessionUnavailableOpen ||
    pendingDeleteId != null ||
    pendingDeleteObjectId != null ||
    pendingRemovePeopleIds != null

  const selected = resolved.objects.find((o) => o.id === selectedId) ?? null
  const selectedSource = viewSession.objects.find((o) => o.id === selectedId) ?? null
  const canRun = !isReview && accessPerms.has('scenarios.run')
  const holdsManagePeople = accessPerms.has('scenarios.manage_people')
  const holdsSendInvites = accessPerms.has('scenarios.send_invites')
  useEffect(() => {
    if (!isAuthenticated) return
    let cancel = false
    void fetchImpersonationTargets()
      .then((rows) => {
        if (cancel) return
        const perms = new Map<string, readonly string[]>()
        const names = new Map<string, string>()
        const userIds = new Map<string, string>()
        for (const row of rows) {
          const email = row.email?.trim().toLowerCase()
          if (email && row.grantedPermissions.length) perms.set(email, row.grantedPermissions)
          if (email && row.userId?.trim()) userIds.set(email, row.userId.trim())
          const full = row.name?.trim()
          if (email && full && !full.includes('@')) names.set(email, full)
        }
        setStudioPermsByEmail(perms)
        setDirectoryNameByEmail(names)
        setDirectoryUserIdByEmail(userIds)
      })
      .catch(() => {
        /* same-tenant ceilings stay empty; search still works */
      })
    return () => {
      cancel = true
    }
  }, [isAuthenticated])
  const homeTenantId = bootstrap?.tenantId ?? null
  /**
   * Admin Delete may remove any lifecycle (including Done). `isReview` must not hide that —
   * Done cards used to look undeletable while Delete/Backspace did nothing.
   */
  const canDeleteSession = accessPerms.has('scenarios.delete')
  const sessionIsPublishedNow = selectedIsPlanned && sessionIsPublished(viewSession)
  const shareHints =
    selectedIsPlanned && !sessionIsPublishedNow ? sessionPublishHints(viewSession) : []
  const shareBlockers =
    selectedIsPlanned && !sessionIsPublishedNow ? sessionPublishBlockers(viewSession) : []
  const canCommentAccount = !isReview && accessPerms.has('scenarios.comment')
  const canCaptureScreen = accessPerms.has('scenarios.capture_screen')
  const canCaptureScreenRef = useRef(false)
  canCaptureScreenRef.current = canCaptureScreen
  const canViewDebug = accessPerms.has('scenarios.view_debug')
  const canViewDebugRef = useRef(false)
  canViewDebugRef.current = canViewDebug
  const showDebugPanels = canViewDebug && debugPanelsOn
  debugPanelsOnRef.current = showDebugPanels
  const rolePlaySlots = useMemo(() => rolePlaySlotsFromSession(viewSession), [viewSession])
  const activePeople = useMemo(() => people.filter(personIsActive), [people])
  const meOnRoster = useMemo(() => {
    const email = user.email.trim().toLowerCase()
    const byId = user.userId?.trim()
      ? findPersonByUserId(activePeople, user.userId)
      : undefined
    const byEmail = email
      ? activePeople.find((person) => person.email.trim().toLowerCase() === email)
      : undefined
    if (byId && byEmail && personUserId(byId) !== personUserId(byEmail)) {
      // Prefer the seat that can run the session window if id/email disagree.
      const idRun =
        personHasRole(byId, 'owner') || personHasRole(byId, 'trainer')
      const emailRun =
        personHasRole(byEmail, 'owner') || personHasRole(byEmail, 'trainer')
      if (emailRun && !idRun) return byEmail
      return byId
    }
    return byId ?? byEmail
  }, [activePeople, user.email, user.userId])
  /** Roster seat ownership for this board — not the ABP ScenarioAdmin product role. */
  const isRosterOwner = Boolean(meOnRoster && personHasRole(meOnRoster, 'owner'))
  const isRosterTrainer = Boolean(meOnRoster && personHasRole(meOnRoster, 'trainer'))
  /**
   * Developer without scenarioadmin, not on this roster — silent observe.
   * scenarioadmin (with or without Developer) is always-visible help.
   */
  const isDeveloperObserver =
    accessPerms.has('scenarios.view_debug') &&
    !hasScenarioAdminRole &&
    !meOnRoster &&
    Boolean(currentInstance) &&
    !viewingTemplate
  const isAdminHelper =
    seeAllInstances &&
    !isDeveloperObserver &&
    !meOnRoster &&
    Boolean(currentInstance) &&
    !viewingTemplate
  /** Owner checkbox + Owner grants: session owners or tenant admin helper. */
  const canAssignOwner = isRosterOwner || isAdminHelper
  /** Own unpublished draft — discard without scenarios.delete (server mirrors creator/owner gate). */
  const canDiscardOwnDraft =
    !isReview &&
    !viewingTemplate &&
    selectedIsPlanned &&
    !sessionIsPublishedNow &&
    isRosterOwner &&
    Boolean(instanceId)
  const showSessionDeleteAction =
    !viewingTemplate &&
    Boolean(instanceId) &&
    !isDeveloperObserver &&
    (canDeleteSession || canDiscardOwnDraft)
  const sessionDeleteIsDiscard = canDiscardOwnDraft && !canDeleteSession
  /**
   * After End, editors are done. Owner, Trainer, or tenant admin helper
   * may still invite / remove to fix mistakes or send report links.
   */
  const canStewardRosterAfterEnd =
    selectedIsCompleted && (isRosterOwner || isRosterTrainer || isAdminHelper)
  const canManagePeopleAccount = holdsManagePeople && (!isReview || canStewardRosterAfterEnd)
  const canSendInvitesAccount =
    holdsSendInvites &&
    ((!isReview && (selectedIsPlanned || inLiveRun)) || canStewardRosterAfterEnd)
  /**
   * On the board via Public or Anyone-with-the-link, without a roster seat (and not ViewAll).
   * Always read-only — high-ceiling ABP accounts do not unlock board edits as guests.
   * Draft does not admit link/public guests (doors open only after Publish).
   */
  const isLinkGuest =
    Boolean(currentInstance) &&
    !viewingTemplate &&
    !meOnRoster &&
    !seeAllInstances &&
    instanceOpenViaAudience(currentInstance!)
  const boardLocationLocked = isBoardLocationLocked(
    viewSession,
    resolveTemplateKeyForLocation(currentInstance?.templateId, templates),
  )
  /** Visibility (Public / Anyone with the link) — still editable after End for review access. */
  const canEditVisibility =
    !templatePreviewReadOnly &&
    !isLinkGuest &&
    !hubActionsLocked &&
    (accessPerms.has('scenarios.edit_board') ||
      accessPerms.has('scenarios.manage_people') ||
      accessPerms.has('scenarios.run'))
  /** Export omit — still editable after Done (impacts AAR / plan PDFs sent post-session). */
  const canEditExportOmit = canEditVisibility
  /**
   * Present Buxus users who opened via the link and were never rostered.
   * Soft-deleted seats stay excluded so Remove does not flash them as Guest.
   */
  const presenceGuests = useMemo(() => {
    const rosteredEver = new Set(
      people.map((person) => person.email.trim().toLowerCase()).filter(Boolean)
    )
    const rows: { id: string; email: string; name: string }[] = []
    for (const email of presentEmails) {
      if (!email || rosteredEver.has(email)) continue
      const name = presentNameByEmail.get(email)?.trim() || email
      rows.push({ id: `guest:${email}`, email, name })
    }
    rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    return rows
  }, [people, presentEmails, presentNameByEmail])
  // Write a learned account name onto the roster row so the next load does not fall back to the email.
  // Same seat as hub SetPeople (owner/trainer). Account ManagePeople alone is not enough —
  // a scenariouser invitee would otherwise rewrite the roster on Join.
  useEffect(() => {
    if (previewTemplateId || !instanceId || !canManagePeopleAccount) return
    if (!isRosterOwner && !isRosterTrainer) return
    let changed = false
    const next = people.map((person) => {
      if (!storedNameIsMailbox(person)) return person
      const known = accountNameFor(person)
      if (!known || known === person.name.trim()) return person
      changed = true
      return { ...person, name: known }
    })
    if (!changed) return
    peopleRef.current = next
    setPeople(next)
    const id = instanceId
    setInstances((prev) => prev.map((row) => (row.id === id ? { ...row, people: next } : row)))
    void hubSetPeople(id, next, actorPhrase()).catch(() => {})
  }, [
    people,
    directoryNameByEmail,
    instanceId,
    previewTemplateId,
    canManagePeopleAccount,
    isRosterOwner,
    isRosterTrainer,
    user.name,
    user.email,
  ])
  // Registration rewrites the email-keyed row on the server. Pull that row once
  // someone with no account id is actually in the room, so this session does not
  // keep the pre-account name, RSVP, and Nudge state.
  useEffect(() => {
    if (previewTemplateId || !instanceId) return
    const waiting = activePeople.filter((person) => {
      const email = person.email.trim().toLowerCase()
      return email && !personHasStudioAccount(person) && presentEmails.has(email)
    })
    if (!waiting.length) return
    const key = waiting
      .map((person) => person.email.trim().toLowerCase())
      .sort()
      .join('|')
    if (rosterAccountHealRef.current.has(key)) return
    rosterAccountHealRef.current.add(key)
    const id = instanceId
    void fetchScenarioInstance(id, user)
      .then((snap) => {
        if (instanceIdRef.current !== id) return
        const local = peopleRef.current
        let changed = false
        const next = local.map((person) => {
          if (person.isDeleted || personHasStudioAccount(person)) return person
          const email = person.email.trim().toLowerCase()
          const fresh = snap.people.find(
            (row) =>
              !row.isDeleted &&
              row.email.trim().toLowerCase() === email &&
              personHasStudioAccount(row),
          )
          if (!fresh) return person
          changed = true
          return fresh
        })
        if (!changed) return
        peopleRef.current = next
        setPeople(next)
        setInstances((prev) =>
          prev.map((row) => (row.id === id ? { ...row, people: next } : row)),
        )
        setSelectedPeopleIds((prev) =>
          prev.map((selectedId) => {
            const old = local.find((person) => person.id === selectedId)
            if (!old) return selectedId
            const fresh = next.find(
              (person) => person.email.trim().toLowerCase() === old.email.trim().toLowerCase(),
            )
            return fresh?.id ?? selectedId
          }),
        )
      })
      .catch(() => {
        rosterAccountHealRef.current.delete(key)
      })
  }, [activePeople, instanceId, previewTemplateId, presentEmails, user])
  /**
   * Map-corner lineup: pointer sharers + Presenter only (empty when nobody is sharing).
   * Self appears while I share (or as Presenter) — no extra share pulse on the avatar.
   */
  const presenceLineup = useMemo(() => {
    const me = user.email.trim().toLowerCase()
    const rows: {
      email: string
      label: string
      initials: string
      tone: PresenceAvatarTone
      self: boolean
    }[] = []
    const pushRow = (email: string, self: boolean): void => {
      if (!email || email === '__anon__') return
      if (rows.some((r) => r.email === email)) return
      const person = activePeople.find((p) => p.email.trim().toLowerCase() === email)
      const rosterName = person ? person.name?.trim() : ''
      const presenceName = presentNameByEmail.get(email)?.trim() ?? ''
      const frameName = watchByEmail.get(email)?.whoName?.trim() ?? ''
      const selfName = self ? user.name?.trim() || '' : ''
      const label = rosterName || presenceName || frameName || selfName || email
      const initials = self
        ? myPresenceInitials
        : watchByEmail.get(email)?.who?.trim() ||
          presenceInitials(rosterName || presenceName || frameName || email)
      rows.push({
        email,
        label: self ? `${label} (you)` : label,
        initials,
        tone: self ? myPresenceTone : presenceToneForKey(email),
        self,
      })
    }
    for (const email of watchByEmail.keys()) pushRow(email, email === me)
    if (watchOn && me) pushRow(me, true)
    // Presenter stays in the lineup even with pointer sharing off (camera-only drive).
    if (presenterEmail) pushRow(presenterEmail, presenterEmail === me)
    rows.sort((a, b) => {
      if (a.self !== b.self) return a.self ? 1 : -1
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    })
    return rows
  }, [
    watchByEmail,
    watchOn,
    presenterEmail,
    presentNameByEmail,
    activePeople,
    user.email,
    user.name,
    myPresenceInitials,
    myPresenceTone,
  ])
  const sessionTypeProfile = sessionTypeById(viewSession.sessionType)
  const windowLabels = sessionWindowLabels()
  const transportLabels = storyTransportLabels(viewSession.sessionType)
  const showTransport = showStoryTransport({
    typeId: viewSession.sessionType,
    accountBoardMode,
    focusMode,
  })
  const passiveFocusChromeForTabs = passiveFocusChrome(viewSession.sessionType)
  const settingsChrome: SettingsChrome = (() => {
    const rosterExpanded =
      Boolean(meOnRoster) &&
      (personHasRole(meOnRoster!, 'owner') || personHasRole(meOnRoster!, 'editor'))
    const rosterSimple =
      Boolean(meOnRoster) &&
      !rosterExpanded &&
      (personHasRole(meOnRoster!, 'trainer') || personHasRole(meOnRoster!, 'participant'))
    if (rosterExpanded) return 'expanded'
    if (rosterSimple) return 'simple'
    const accountAdmin =
      accessPerms.has('scenarios.view_all') ||
      accessPerms.has('scenarios.edit_templates') ||
      accessPerms.has('scenarios.view_debug')
    const accountEditor =
      accessPerms.has('scenarios.edit_board') && !accessPerms.has('scenarios.run')
    return accountAdmin || accountEditor ? 'expanded' : 'simple'
  })()
  const showRolePlay =
    sessionTypeProfile?.rolePlay !== false &&
    packageOffers(sessionTypeProfile, 'rolePlay') &&
    studioFeature(StudioFeature.rolePlay)
  const showComments =
    sessionTypeProfile?.comments !== false &&
    packageOffers(sessionTypeProfile, 'comments') &&
    studioFeature(StudioFeature.comments)
  const showWaypoints =
    packageOffers(sessionTypeProfile, 'waypoints') &&
    studioFeature(StudioFeature.timelineKeyframing)
  const showVisibility =
    packageOffers(sessionTypeProfile, 'visibilityKeyframes') &&
    studioFeature(StudioFeature.timelineKeyframing)
  const showFindAssets =
    packageOffers(sessionTypeProfile, 'findAssets') && studioFeature(StudioFeature.findAssets)
  const showTornado = packageOffers(sessionTypeProfile, 'tornado')
  const showTimelineAuthoring = studioFeature(StudioFeature.timeline)
  const showTimelineEdit = studioFeature(StudioFeature.timelineEdit)
  const showOnBoardFields = studioFeature(StudioFeature.showOnBoard)
  const showExerciseRunChrome = studioFeature(StudioFeature.exerciseRuns)
  const showVersionChrome = studioFeature(StudioFeature.versions)
  const showOperatorMapStyles = studioFeature(StudioFeature.operatorStyles)
  const showPublicAudience = studioFeature(StudioFeature.publicAudience)
  const showAnyoneWithLink = studioFeature(StudioFeature.anyoneWithLink)
  const showPdfExports = studioFeature(StudioFeature.pdfExports)
  const showInviteAttachDocs = studioFeature(StudioFeature.inviteAttachDocs)
  const showQr = studioFeature(StudioFeature.qrCode)
  ghostsOfferedRef.current =
    sessionTypeOffersGhosts(sessionTypeProfile) && showOnBoardFields
  const minimalInspect = studioMinimalInspect(sessionTypeProfile, settingsChrome)
  const rolePlayOn = isRolePlayEnabled(viewSession) && showRolePlay
  // Templates never own a take/tape — drop leftover IR chrome from the prior session.
  useEffect(() => {
    if (!viewingTemplate) return
    if (tapeReviewRef.current) setTapeReview(null)
    setInstantReplayEngaged(false)
    setInstantReplayPauseMs(null)
    setReplayPlaying(false)
  }, [viewingTemplate])
  useEffect(() => {
    const paint = window.requestAnimationFrame(() => mapApiRef.current?.resize())
    const later = window.setTimeout(() => mapApiRef.current?.resize(), 60)
    return () => {
      window.cancelAnimationFrame(paint)
      window.clearTimeout(later)
    }
  }, [focusMode, hubNothingSelected])
  /**
   * Soft “no roster row yet” only when the board’s people list is still empty (new draft /
   * creator before first People write). ViewAll leftover opens never get this — admin helper
   * and developer-observer have their own seats.
   */
  const rosterEmptySoftGrant =
    !meOnRoster && !seeAllInstances && activePeople.length === 0
  const rosterGrantsEdit =
    Boolean(
      meOnRoster &&
        (personHasRole(meOnRoster, 'owner') ||
          personHasRole(meOnRoster, 'editor') ||
          personHasRole(meOnRoster, 'trainer')),
    ) ||
    rosterEmptySoftGrant ||
    isAdminHelper
  /**
   * Settings writes (title, map, schedule, …). Account ceiling is not enough —
   * Participants with scenariouser would otherwise flip Background Map locally,
   * then SetSessionFields would 401 without an Owner/Editor/Trainer seat.
   */
  const canEditSettings =
    !isReview &&
    !isLinkGuest &&
    !hubActionsLocked &&
    rosterGrantsEdit &&
    (accessPerms.has('scenarios.edit_board') ||
      accessPerms.has('scenarios.edit_templates') ||
      accessPerms.has('scenarios.manage_people') ||
      accessPerms.has('scenarios.run'))
  /** Session window (Publish / Go Live / End / Share / roster invites) — Owner/Trainer, not Editor co-authors. */
  const rosterGrantsSession =
    Boolean(
      meOnRoster &&
        (personHasRole(meOnRoster, 'owner') || personHasRole(meOnRoster, 'trainer')),
    ) ||
    rosterEmptySoftGrant ||
    isAdminHelper
  /** Manage roster / send invites: owner/trainer, or tenant admin helper. */
  const canManagePeople =
    canManagePeopleAccount &&
    !isLinkGuest &&
    rosterGrantsSession
  const canSendInvites =
    canSendInvitesAccount &&
    !isLinkGuest &&
    rosterGrantsSession
  /** Multi-select / invite actions — not needed for read-only profile cards. */
  const canSelectPeople = canManagePeople || canSendInvites
  /** Trainers/editors manage the full roster; participants see who showed up (joined) + self. */
  const peopleTabAdmin = canManagePeople || canSendInvites
  /**
   * The viewing-as list is a short configured set, not the directory.
   * Roster accounts missing from it are asked one at a time.
   */
  const rosterCeilingTargets = useMemo(() => {
    if (!canManagePeople) return []
    const me = user.email.trim().toLowerCase()
    const seen = new Set<string>()
    const rows: { email: string; userId: string }[] = []
    for (const person of activePeople) {
      const email = person.email.trim().toLowerCase()
      const id = personUserId(person)
      if (!email || !id || !personHasStudioAccount(person) || email === me || studioPermsByEmail.has(email) || seen.has(email)) continue
      seen.add(email)
      rows.push({ email, userId: id })
    }
    return rows
  }, [canManagePeople, activePeople, studioPermsByEmail, user.email])
  useEffect(() => {
    if (!rosterCeilingTargets.length) return
    for (const row of rosterCeilingTargets) {
      if (rosterCeilingRequestedRef.current.has(row.email)) continue
      rosterCeilingRequestedRef.current.add(row.email)
      void fetchStudioPeopleCeiling(row.userId)
        .then((ceiling) => {
          if (!ceiling) return
          setStudioPermsByEmail((prev) => {
            if (prev.has(row.email)) return prev
            const next = new Map(prev)
            next.set(row.email, ceiling.grantedPermissions)
            return next
          })
        })
        .catch(() => {
          rosterCeilingRequestedRef.current.delete(row.email)
        })
    }
  }, [rosterCeilingTargets])
  const peopleTabList = useMemo(() => {
    if (peopleTabAdmin) return activePeople
    const me = user.email.trim().toLowerCase()
    return activePeople.filter(
      (person) =>
        person.status === 'joined' || (me.length > 0 && person.email.trim().toLowerCase() === me)
    )
  }, [activePeople, peopleTabAdmin, user.email])
  /** Chat is for everyone in the room who holds Comment. Developer-observer does not compose. */
  const canComment = canCommentAccount && !isDeveloperObserver
  // Account ABP alone is not enough — Participant/Editor roster seats must not Publish / Go Live.
  const mayPublish =
    selectedIsPlanned &&
    !sessionIsPublishedNow &&
    rosterGrantsSession &&
    !isLinkGuest &&
    (canEditSettings || accessPerms.has('scenarios.edit_board'))
  const canPublish = mayPublish && shareBlockers.length === 0 && !hubActionsLocked
  /** Operator seat — visibility for End Session / Go Live chrome while reconnecting. */
  const mayOperateSession = canRun && rosterGrantsSession && !isLinkGuest
  const canOperateSession = mayOperateSession && !hubActionsLocked
  /** Visibility for Go Live while reconnecting (button stays; disabled via canGoLive). */
  const mayGoLive = mayOperateSession && sessionIsPublishedNow
  const canGoLive = mayGoLive && !hubActionsLocked
  /**
   * Done review drive — Fields (tapeReviewRunId) for stewards. Version switch is local-only
   * on Completed (hub ApplyBoardSeed throws AlreadyCompleted).
   */
  const canDriveDoneReview =
    selectedIsCompleted &&
    !isLinkGuest &&
    rosterGrantsSession &&
    !hubActionsLocked &&
    (accessPerms.has('scenarios.edit_board') || accessPerms.has('scenarios.run'))
  canDriveDoneReviewRef.current = canDriveDoneReview
  /** Shared hub Preview while Draft/Published — Owner/Trainer/Editor (Run or EditBoard). */
  const canDrivePreview =
    selectedIsPlanned &&
    sessionOffersStoryClock &&
    Boolean(instanceId) &&
    !viewingTemplate &&
    rosterGrantsEdit &&
    !isLinkGuest &&
    !hubActionsLocked &&
    (accessPerms.has('scenarios.edit_board') || accessPerms.has('scenarios.run'))
  canDrivePreviewRef.current = canDrivePreview
  canCommitStoryEndPauseRef.current = canDrivePreview || canOperateSession
  // Speed follows who can drive the playhead (same as scrub), not every spectator.
  canShareStripRateRef.current =
    !isLinkGuest &&
    !hubActionsLocked &&
    ((selectedIsPlanned && canDrivePreview) ||
      (inLiveRun && canOperateSession) ||
      selectedIsCompleted)
  const canOpenShare =
    !viewingTemplate &&
    showBoardChrome &&
    rosterGrantsSession &&
    !isLinkGuest &&
    !hubActionsLocked &&
    (sessionIsPublishedNow || inLiveRun || selectedIsCompleted) &&
    (accessPerms.has('scenarios.edit_board') ||
      accessPerms.has('scenarios.send_invites') ||
      accessPerms.has('scenarios.manage_people') ||
      accessPerms.has('scenarios.run'))
  const showViewingCount =
    showBoardTools && presenceSynced && viewingCountSettled && viewingCount > 0
  // Arm “N viewing” after a short stay — or immediately when 2+ people are already here
  // (host used to wait the full dwell while a peer already saw the count).
  useEffect(() => {
    setViewingCountSettled(false)
    if (!instanceId || previewTemplateId || !presenceSynced) return
    if (presentEmails.size >= 2) {
      setViewingCountSettled(true)
      return
    }
    const t = window.setTimeout(() => setViewingCountSettled(true), 2500)
    return () => window.clearTimeout(t)
  }, [instanceId, previewTemplateId, presenceSynced, presentEmails.size])
  const selectedPeople = useMemo(
    () => activePeople.filter((p) => selectedPeopleIds.includes(p.id)),
    [activePeople, selectedPeopleIds]
  )
  const selectedPresenceGuests = useMemo(
    () => presenceGuests.filter((g) => selectedPeopleIds.includes(g.id)),
    [presenceGuests, selectedPeopleIds]
  )
  /**
   * Structural board edits (place/edit objects). Locked while an official Run is active;
   * story transport / IR stay on canOperateSession.
   */
  const structuralBoardLocked =
    inLiveRun &&
    typeOffersSharedRun(viewSession.sessionType) &&
    takeNumber > 0 &&
    !exerciseRunStopped
  /**
   * Any playing transport freezes board edits — live take clock, story/review preview,
   * Instant Replay tape play, or between-takes tape review while the tape is rolling.
   */
  const playbackBoardLocked =
    running || (instantReplayEngaged && replayPlaying) || (tapeReviewActive && running)
  /**
   * Finished-run Reviewing is historic — inspect OK, no board mutations (edit the working
   * board after “I’m done reviewing” / View final board, or on another version).
   */
  const historicRunLocked = tapeReviewActive || boardActivity === 'watching'
  const boardEditGrants = viewingTemplate
    ? canEditTemplates && templateEditLive
    : Boolean(
        currentInstance &&
        rosterGrantsEdit &&
        !isLinkGuest &&
        !selectedIsCompleted &&
        currentInstance.lifecycle !== 'completed' &&
        accessPerms.has('scenarios.edit_board')
      )
  /** Role may author the board — ignores temporary Play/Pause freeze (Inspect chrome). */
  const canEditBoardUnlocked =
    !structuralBoardLocked && !historicRunLocked && boardEditGrants
  /** Place/edit role on this board — ignores temporary playback freeze. */
  const boardPlaceGrants =
    boardEditGrants ||
    Boolean(
      currentInstance &&
      rosterGrantsEdit &&
      !isReview &&
      !isLinkGuest &&
      !selectedIsCompleted &&
      currentInstance.lifecycle !== 'completed' &&
      (!viewingTemplate || templateEditLive) &&
      accessPerms.has('scenarios.place_objects')
    )
  const canEditBoard = canEditBoardUnlocked && !playbackBoardLocked
  /**
   * Role may use the place palette (chrome + tool pick). Ignores temporary
   * playback freeze so Play/Pause does not dim every row (opacity 0.38 flash).
   */
  const paletteToolsEnabled =
    !hubActionsLocked &&
    !structuralBoardLocked &&
    !historicRunLocked &&
    (!viewingTemplate || templateEditLive) &&
    !isLinkGuest &&
    boardPlaceGrants
  /** May commit place/edit on the board — false while story/IR is advancing. */
  const canPlaceObjects = paletteToolsEnabled && !playbackBoardLocked
  /**
   * Left toolbox open/closed (P). Content is place palette or map-symbols legend.
   * Open state is local for this board visit — not a rights change.
   * Hub keeps the place palette open; map commits stay gated by canPlaceObjects.
   * Invitees / Participants keep the rail as map-symbols — do not collapse just
   * because they cannot place. Do not flip Palette ↔ Map symbols on Play/Pause.
   */
  const showPlacePaletteUi = hubNothingSelected || paletteToolsEnabled
  const showPalette = showBoardChrome && paletteOpen
  canPlaceObjectsRef.current = canPlaceObjects
  showBoardChromeRef.current = showBoardChrome
  useEffect(() => {
    paletteUserTouchedRef.current = false
    paletteSeededRef.current = false
  }, [instanceId, previewTemplateId])
  // Seed open once per board after presence settles — place palette or legend.
  // Never auto-collapse on open; P still toggles. Grants must not re-slide the rail.
  useEffect(() => {
    if (!showBoardChrome || paletteUserTouchedRef.current || paletteSeededRef.current) return
    if (instanceId && !presenceSynced) return
    paletteSeededRef.current = true
    setPaletteOpen(true)
  }, [instanceId, previewTemplateId, showBoardChrome, presenceSynced])
  // Place rights restored → ensure rail is open (legend→tools). Losing place rights only
  // switches content to map-symbols; do not collapse (hub → invited card used to slam shut).
  useEffect(() => {
    if (!showBoardChrome || (instanceId && !presenceSynced)) return
    if (!canPlaceObjects) return
    if (paletteUserTouchedRef.current && paletteOpenRef.current) return
    setPaletteOpen(true)
  }, [canPlaceObjects, showBoardChrome, presenceSynced, instanceId])
  // Enable width animation only after the first settled frame (P toggles / later rights still animate).
  useEffect(() => {
    if (!showBoardChrome) {
      setPaletteMotionOn(false)
      return
    }
    if (instanceId && !presenceSynced) return
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setPaletteMotionOn(true))
    })
    return () => window.cancelAnimationFrame(id)
  }, [showBoardChrome, presenceSynced, instanceId, previewTemplateId])

  // Boot / offline: drop Timeline Edit so open narration forms aren't left mid-edit on the error shell.
  useEffect(() => {
    if (showBoardChrome) return
    setTimelineEditMode(false)
    setNarrationDraftId(null)
    setTimelineFocusId(null)
    setPostRunOfferOpen(false)
    setSharePanelOpen(false)
    setGoLiveConfirmOpen(false)
    setKeysOpen(false)
    setHelpOpen(false)
  }, [showBoardChrome])
  mayPersistObjectsRef.current =
    canPlaceObjects && !previewTemplateId && currentInstance?.lifecycle !== 'completed'
  // Share pointer is view-sharing, not board edit — keep it after Done so review with watchers still works.
  canLeadWatchRef.current =
    !previewTemplateId &&
    Boolean(instanceId) &&
    !isDeveloperObserver &&
    (accessPerms.has('scenarios.run') || accessPerms.has('scenarios.edit_board'))
  // Live drag/place pose for co-editors. Independent of Share pointer — Sharing owns the hand/camera;
  // this stream owns object pose (gated off Sharing used to drop pose while the hand still moved).
  shareEditPointerRef.current =
    canPlaceObjects &&
    !previewTemplateId &&
    Boolean(instanceId) &&
    currentInstance?.lifecycle !== 'completed'
  placeTypeRef.current = placeType
  editWhoRef.current = myPresenceInitials
  myPresenceInitialsRef.current = myPresenceInitials
  editWhoNameRef.current = shortPersonName(user.name || '') ?? ''
  myEmailRef.current = user.email.trim().toLowerCase()
  runningRef.current = running
  canCommentRef.current =
    canComment && !previewTemplateId && currentInstance?.lifecycle !== 'completed'
  sessionTitleRef.current = session.title
  const myActorKeys = useMemo(() => {
    const keys = new Set<string>()
    const email = user.email.trim().toLowerCase()
    if (email) keys.add(email)
    const accountId = user.userId?.trim()
    if (accountId) keys.add(accountId)
    const me = people.find((p) => p.email.trim().toLowerCase() === email)
    if (me?.id) keys.add(me.id)
    return keys
  }, [user.email, people])

  function ownsObject(obj: ScenarioObject): boolean {
    const c = obj.createdById?.trim()
    if (!c) return false
    return myActorKeys.has(c) || myActorKeys.has(c.toLowerCase())
  }

  /** Board editors mutate anything; place_objects users mutate only objects they created. */
  function canMutateObject(
    obj: ScenarioObject | null | undefined,
    opts?: { ignorePlayback?: boolean }
  ): boolean {
    if (!obj) return false
    if (!opts?.ignorePlayback && playbackBoardLocked) return false
    if (canEditBoardUnlocked) return true
    if (
      isReview ||
      viewingTemplate ||
      selectedIsCompleted ||
      currentInstance?.lifecycle === 'completed'
    ) {
      return false
    }
    if (!rosterGrantsEdit || !accessPerms.has('scenarios.place_objects')) return false
    return ownsObject(obj)
  }

  const canMutateSelected = canMutateObject(selectedSource)
  /**
   * Inspect field chrome — same inputs while Play freezes writes.
   * `canMutateSelected` / `canEditBoard` still gate commits.
   */
  const editing = canMutateObject(selectedSource, { ignorePlayback: true })
  useEffect(() => {
    if (canEditBoard) return
    setTimelineEditMode(false)
    setNarrationDraftId(null)
  }, [canEditBoard])
  /** Map marker drag — per object. Path pins own the drag while Waypoints/Position is open. */
  const canDragObject = (id: string): boolean => {
    // Template preview is read-only (session writes don't bind to the preview snapshot → rubberband).
    if (viewingTemplate && !templateEditLive) return false
    if (playbackBoardLocked) return false
    const obj = viewSession.objects.find((o) => o.id === id)
    if (!obj || !canMutateObject(obj)) return false
    if (waypointObjectId === id) return false
    if (isReview) return false
    return true
  }
  /** Inspect pane subject — selection or armed place tool, not the scenario name. */
  const inspectTitle = (() => {
    if (selectedSource) {
      return selectedSource.label.trim() || paletteLabel(selectedSource.type)
    }
    if (canPlaceObjects && placeType === 'type') return 'Type / Label'
    if (canPlaceObjects && placeType === 'freehand') return 'Freehand Sketch'
    if (canPlaceObjects && placeType === 'pen') return 'Line'
    if (canPlaceObjects && placeType === 'ruler') return 'Rule'
    if (canPlaceObjects && placeType && placeType !== 'wind') return paletteLabel(placeType)
    return 'Inspect'
  })()
  /** Keyframe guides + cursor coords — board edit or template write. */
  const showAuthorGuides =
    accessPerms.has('scenarios.edit_board') || accessPerms.has('scenarios.edit_templates')
  /** Pose keyframes on map — accordion open with pins (read-only hover can preview too).
   * Cleared during wind Preview so path pins hide and the full scene undims. */
  const waypointCount = selectedSource?.keyframes?.length ?? 0
  const windPreviewActiveEarly = windPreviewTotalMs != null
  const pathPreviewActiveEarly = pathPreviewTotalMs != null
  const waypointObjectId =
    inspectDockOpen &&
    selectedId &&
    waypointsOpen &&
    waypointCount >= 1 &&
    !windPreviewActiveEarly
      ? selectedId
      : null
  /** Hide unit under pins while inspecting path; keep it visible during path / wind Preview. */
  const hideWaypointUnitIcon =
    Boolean(waypointObjectId) && !pathPreviewActiveEarly && !windPreviewActiveEarly
  /** While editing tornado Position pins, hide other plumes for clarity.
   * Wind Preview keeps all plumes so local-wind takeover on nearby leaks is visible. */
  const plumeFocusObjectId =
    isLocalStorm(selectedSource?.type) &&
    selectedId &&
    !windPreviewActiveEarly &&
    (waypointsOpen || (pathPreviewActiveEarly && pathPreviewObjectId === selectedId))
      ? selectedId
      : null

  useEffect(() => {
    if (canViewEggs) return
    setSelectedId((id) => {
      if (!id) return id
      const obj = session.objects.find((o) => o.id === id)
      return obj && isBrwnPlumeType(obj.type) ? null : id
    })
    setPlaceType((cur) => (cur && isBrwnPlumeType(cur) ? null : cur))
  }, [canViewEggs, session.objects])

  useEffect(() => {
    if (showTornado) return
    setPlaceType((cur) => (cur === 'tornado' || cur === 'hurricane' ? null : cur))
  }, [showTornado])

  useEffect(() => {
    setWaypointFocusIndex(null)
    setWaypointsOpen(false)
    setVisibilityOpen(false)
    setStatusOpen(false)
    setRadiusOpen(false)
    setStatusFocusIndex(null)
    setRadiusFocusIndex(null)
    setRadiusPreviewTotalMs(null)
    setRadiusPreviewStartMs(null)
    setRadiusPreviewObjectId(null)
    setLinkedSort({ col: 'dist', dir: 'asc' })
    setWindHoverIndex(null)
    setWindSelectIndex(null)
    setWindDraft(null)
    setWindAuthorMs(elapsedMs)
    setWindPreviewTotalMs(null)
    setWindPreviewStartMs(null)
    setTornadoPreviewFocus(false)
    setInspectPreviewMs(null)
    const preset = windPresetById(windPresetId)
    const suggested = suggestedWindPresetOnsetMs(preset, authorClock, elapsedMs)
    const clamped = clampWindPresetWindow(suggested, preset.defaultSpanMs, authorClock.durationMs)
    setWindPresetSpanMs(clamped.spanMs)
    setWindPresetOnsetMs(clamped.onsetMs)
    setWindPresetsOpen(false)
    const picked = selectedId
      ? session.objects.find((o) => o.id === selectedId)
      : null
    if (picked && isLocalStorm(picked.type)) {
      const hurricane = picked.type === 'hurricane'
      const storedPresetId = hurricane
        ? hurricanePresetIdFromParams(picked.params)
        : tornadoPresetIdFromParams(picked.params)
      setTornadoPresetId(storedPresetId)
      const storedSpan = hurricane
        ? hurricanePresetSpanFromParams(picked.params)
        : tornadoPresetSpanFromParams(picked.params)
      setTornadoPresetSpanMs(
        storedSpan ??
          (storedPresetId
            ? hurricane
              ? hurricanePresetById(storedPresetId as HurricanePresetId).defaultSpanMs
              : tornadoPresetById(storedPresetId as TornadoPresetId).defaultSpanMs
            : hurricane
              ? 6 * 60 * 60_000
              : 45 * 60_000),
      )
      setHurricaneTrackHeadingDeg(hurricane ? hurricaneTrackHeadingFromParams(picked.params) : 0)
      const stored = picked.params.presetOnsetAt
      if (typeof stored === 'string' && stored) {
        setTornadoPresetOnsetMs(Math.max(0, off(stored)))
      } else if (tornadoOnsetSeededForRef.current !== picked.id) {
        tornadoOnsetSeededForRef.current = picked.id
        const fromObject = picked.windKeyframes?.[0]?.at ?? picked.keyframes?.[0]?.at
        const end = Math.max(0, authorClock.durationMs)
        const onset = fromObject
          ? Math.max(0, off(fromObject))
          : Math.min(Math.max(0, Math.round(elapsedMs)), end)
        setTornadoPresetOnsetMs(onset)
        const iso = atOff(onset)
        patchSelectedObject((o) =>
          o.id === picked.id ? { ...o, params: { ...o.params, presetOnsetAt: iso } } : o,
        )
      }
    }
  }, [selectedId])

  // When the preset recipe changes, refresh suggested onset/span (keep user edits until then).
  useEffect(() => {
    if (selectedSource?.type !== 'wind') return
    const preset = windPresetById(windPresetId)
    const suggested = suggestedWindPresetOnsetMs(preset, authorClock, windAuthorMs)
    const clamped = clampWindPresetWindow(suggested, preset.defaultSpanMs, authorClock.durationMs)
    setWindPresetSpanMs(clamped.spanMs)
    setWindPresetOnsetMs(clamped.onsetMs)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to preset id / wind selection
  }, [windPresetId, selectedSource?.type])

  // Keep Wind author time aligned with the scenario clock (Preview / scrub / pause).
  // Skip while the time field is focused, hovering a keyframe, or Inspect series Preview
  // is walking a local playhead (strip / hub elapsed stays put).
  useEffect(() => {
    if (selectedSource?.type !== 'wind' && !isLocalStorm(selectedSource?.type)) return
    if (windTimeFocusedRef.current) return
    if (windHoverIndex != null) return
    if (inspectPreviewMs != null) return
    setWindAuthorMs(elapsedMs)
  }, [elapsedMs, selectedSource?.type, windHoverIndex, inspectPreviewMs])

  useEffect(() => {
    if (canSelectPeople) return
    setPeopleSelectMode(false)
  }, [canSelectPeople])

  useEffect(() => {
    if (activePeople.length > 1) return
    setPeopleSelectMode(false)
  }, [activePeople.length])

  useEffect(() => {
    return () => {
      if (jsonCopyTimerRef.current != null) window.clearTimeout(jsonCopyTimerRef.current)
    }
  }, [])

  /**
   * Timeline / time-bar seek. Official Live take while Running: orientation only.
   * Live-idle story preview may scrub even while the preview clock is advancing.
   * Live take Paused: Instant Replay. Preview / Done: drivers as before.
   * Done + following Presenter: follow their story playhead (no solo scrub).
   */
  const followingPresenter =
    Boolean(watchFrame) &&
    Boolean(presenterEmail) &&
    presenterEmail !== user.email.trim().toLowerCase()
  /** Following Presenter story on Done only — Planned Preview playhead is always hub Clock. */
  const followingStoryPresenter = followingPresenter && selectedIsCompleted
  const officialTakeRunning =
    inLiveRun && running && takeNumber > 0 && !exerciseRunStopped
  const canScrub =
    sessionOffersStoryClock &&
    !officialTakeRunning &&
    !followingStoryPresenter &&
    (viewingTemplate ||
      selectedIsCompleted ||
      (selectedIsPlanned && (canDrivePreview || Boolean(previewTemplateId) || !instanceId)) ||
      (inLiveRun && canOperateSession))
  /**
   * Done review freezes the plate. Live may still change Background Map — it is a
   * style swap only (camera / object lng-lat stay put).
   */
  const mapLocked = isReview
  /** Board home / Set Location — locked for the life of a Live run (and Done). */
  const boardHomeLocked =
    isReview || (currentInstance?.lifecycle === 'running' && started)
  /** Board wind row (may be parked) — strip/Inspect still need a handle when ignored. */
  const sessionWind = viewSession.objects.find((o) => o.type === 'wind') ?? null
  /** Active surface wind for plumes / map effects (skips ignored + ghosts). */
  const wind = findWind(resolved.objects)
  /** Same clock as the Map (hover scrub / Preview) so the strip tracks plume changes. */
  const mapWind = findWind(mapResolved.objects) ?? wind
  const windAuthoring =
    selectedSource?.type === 'wind' || isLocalStorm(selectedSource?.type) ? selectedSource : null
  const windFrames = windAuthoring ? sortWindFrames(windAuthoring.windKeyframes ?? []) : []
  const windShow = (() => {
    if (!windAuthoring) return null
    const hover = windHoverIndex != null ? windFrames[windHoverIndex] : null
    if (hover) {
      return {
        fromDeg: hover.fromDeg,
        speedMph: hover.speedMph,
        plumeDistanceM: hover.plumeDistanceM,
        plumeSpreadDeg: hover.plumeSpreadDeg,
      }
    }
    if (windDraft) return windDraft
    // Whiteboard has no story clock — show the static params, not a leftover series.
    const sampled =
      sessionOffersStoryClock && windAuthoring.windKeyframes?.length
        ? sampleWindAt(windAuthoring.windKeyframes, mapPlayheadIso)
        : null
    if (sampled) {
      return {
        fromDeg: sampled.fromDeg,
        speedMph: sampled.speedMph,
        plumeDistanceM: sampled.plumeDistanceM,
        plumeSpreadDeg: sampled.plumeSpreadDeg,
      }
    }
    const src =
      isLocalStorm(windAuthoring.type) ? (selected ?? windAuthoring) : (mapWind ?? windAuthoring)
    return {
      fromDeg: Number(src.params.fromDeg) || 0,
      speedMph: Number(src.params.speedMph) || 0,
      plumeDistanceM: Number(src.params.plumeDistanceM) || 400,
      plumeSpreadDeg: Number(src.params.plumeSpreadDeg) || 25,
    }
  })()
  /** Strip badge: show parked wind muted so authors can reopen Inspect. */
  const stripWind = mapWind ?? (sessionWind?.ignored ? sessionWind : null)
  const currentEvent = resolved.currentEvent
  // Playhead owns "current" highlight — edit focus must not pin it (scrubbing a new
  // end beat stayed stuck on the previously focused card until Preview cleared focus).
  const currentAtMs = useMemo(() => {
    return currentEvent ? off(currentEvent.at) : null
  }, [currentEvent, viewSession.storyStartTime])
  /** Prefer the last co-timed card so bottom injects stay in view as the clock advances. */
  const timelineScrollId = useMemo(() => {
    if (timelineFocusId && !running) return timelineFocusId
    if (currentAtMs == null) return null
    let lastId: string | null = null
    for (const ev of displayTimeline) {
      if (off(ev.at) < currentAtMs) continue
      if (off(ev.at) > currentAtMs) break
      lastId = ev.id
    }
    return lastId
  }, [timelineFocusId, running, currentAtMs, displayTimeline])
  const scenarioFinished = stopped || currentInstance?.lifecycle === 'completed'
  /** Live-run strip/timeline “just finished” — false once the instance is in completed review. */
  const complete = !isReview && scenarioFinished
  /**
   * Pending invite RSVP: centered Join card (or slim chip after “Not now”).
   * Hidden once Done (RSVP stays pending — no confirm/decline recorded).
   * Other upper banners stay mutually exclusive with an open Join card.
   */
  const showAcceptInvite = myInvitePending && !viewingTemplate && !scenarioFinished
  const showJoinOfferCard =
    showAcceptInvite && Boolean(instanceId) && inviteJoinPeekId !== instanceId
  const showJoinOfferChip =
    showAcceptInvite && Boolean(instanceId) && inviteJoinPeekId === instanceId
  /** Thanks can show after leave-on-decline lands in the library (template preview). */
  const showDeclineThanks = declineThanksOpen && !myInvitePending && !showAcceptInvite
  /** Join card owns focus — don't stack the catch-up toast underneath it. */
  const showJoinToastStack = joinToasts.length > 0 && !recToast && !showJoinOfferCard

  const joinOfferHost = (() => {
    if (!showAcceptInvite || !myOpenInvite) return null
    const byId = myOpenInvite.lastInviterId
      ? people.find((p) => p.id === myOpenInvite.lastInviterId && !p.isDeleted)
      : undefined
    const owner = people.find((p) => !p.isDeleted && personHasRole(p, 'owner'))
    const who = byId ?? owner
    if (!who) return null
    const initials = presenceInitials(who.name || who.email)
    return {
      name: personLabel(who),
      initials,
      tone: presenceToneForKey(who.email || who.id),
      hostTag: joinOfferHostTag(myOpenInvite),
    }
  })()
  const clockEnded = !running && displayDur > 0 && elapsedMs >= displayDur
  const fullRuntimeMs = Math.max(1, displayDur)
  const floorRuntimeMs = minPlaybackRuntimeMs(fullRuntimeMs)
  const { maxRate: playbackMaxRate } = playbackRateBounds(fullRuntimeMs)
  const speedMenuRates = playbackSpeedMenuRates(playbackMaxRate)
  // 1× → content length; fastest → 30s floor, snapped to round rates.
  const runtimeMs = Math.min(fullRuntimeMs, Math.max(floorRuntimeMs, playbackRuntimeMs))
  const previewSpeedRate = snapPlaybackRate(fullRuntimeMs / Math.max(1, runtimeMs), playbackMaxRate)
  /** Advance scenario elapsed this many× vs wall clock so the board finishes in `runtimeMs`. */
  const playbackRate =
    displayDur > 0 ? snapPlaybackRate(displayDur / Math.max(1, runtimeMs), playbackMaxRate) : 1
  playbackRateRef.current = playbackRate
  const storyScrubMarks = useMemo(
    () =>
      scrubMarksFromTimeline(displayTimeline, displayDur, (iso) =>
        iso ? storyDeltaMs(viewSession.storyStartTime, iso) : 0
      ),
    [displayTimeline, displayDur, viewSession.storyStartTime]
  )
  const storyScrubMarksRef = useRef(storyScrubMarks)
  storyScrubMarksRef.current = storyScrubMarks
  const displayDurRef = useRef(displayDur)
  displayDurRef.current = displayDur
  const isExerciseType = typeOffersSharedRun(viewSession.sessionType)
  /**
   * Between Exercise takes (after Stop, before Run again) — including while the
   * as-now story is preview-playing on the hub clock. Do not key off `running`:
   * preview play must not look like a mid-take.
   */
  const betweenExerciseTakes =
    inLiveRun && isExerciseType && exerciseRunStopped
  /**
   * Bottom story time bar — Preview, Live take, Done. Layout sibling under the map
   * (never overlays Mapbox). Hidden between Exercise takes unless trainer pins it.
   */
  const showTimeBar =
    showBoardTools &&
    showTransport &&
    sessionOffersStoryClock &&
    displayDur > 0 &&
    !complete &&
    (isClockPreview || selectedIsCompleted || inLiveRun)
  const timeBarHidden = Boolean(betweenExerciseTakes && timeBarAutoHidden && !timeBarPinned)
  const canSeekTimeBar =
    showTimeBar &&
    !timeBarHidden &&
    !studioLinkDown &&
    canScrub &&
    (viewingTemplate ||
      selectedIsCompleted ||
      (selectedIsPlanned && canDrivePreview) ||
      (inLiveRun && canOperateSession && !officialTakeRunning))
  /** Speed matches who can drive the playhead (not spectators / ViewAll-only). Locked while an official take is Running. */
  const canEditTimeBarSpeed =
    showTimeBar &&
    !timeBarHidden &&
    !studioLinkDown &&
    !officialTakeRunning &&
    !followingStoryPresenter &&
    (viewingTemplate ||
      selectedIsCompleted ||
      (selectedIsPlanned && canDrivePreview) ||
      (inLiveRun && canOperateSession))
  /** At / near story origin — Ready chip (small slack for Clock echo float). */
  const nearStoryStart = elapsedMs <= 250 && hubWirePlayheadMs <= 250
  /** Poised after Go Live at story start — Run options… (not mid-preview). */
  const poisedForFirstRun =
    inLiveRun &&
    !running &&
    takeNumber === 0 &&
    !exerciseRunStopped &&
    nearStoryStart
  /** Live take paused mid-take — Instant Replay (not Idle before first Run / between takes). */
  const liveTakePausedForReplay =
    inLiveRun &&
    !running &&
    takeNumber > 0 &&
    !betweenExerciseTakes &&
    (isExerciseType ? !exerciseRunStopped : true)
  /**
   * Official timed take is on (running or paused). Live-idle story scrub/play is *not* a take —
   * Space must not Start Run there. IR chrome before Run 1 is a bug — never count it as a take.
   */
  const liveTakeActive =
    inLiveRun &&
    takeNumber > 0 &&
    (instantReplayEngaged ||
      liveTakePausedForReplay ||
      (running && !exerciseRunStopped))
  /** Draft/Published preview drivers and Live operators may switch plan versions. */
  /** Live/Planned: hub switch. Done: any non-guest viewer can switch locally. */
  const canSwitchBoardVersion =
    ((canOperateSession || canDrivePreview) && !liveTakeActive) ||
    (selectedIsCompleted && !isLinkGuest && Boolean(instanceId))
  /**
   * Eye (not play): spectator / follower — no control of the shared story clock.
   * Draft/Published participants were incorrectly shown a disabled "Preview story" play control.
   */
  const showStoryFollowEye =
    Boolean(instanceId) &&
    !viewingTemplate &&
    (followingStoryPresenter ||
      (selectedIsPlanned && !canDrivePreview) ||
      (inLiveRun && !canOperateSession))
  /**
   * Official timed Exercise take in progress (running or paused mid-take).
   * Live-idle story preview/scrub must not count — End Session copy uses this.
   */
  const exerciseRunActive =
    typeOffersSharedRun(viewSession.sessionType) && liveTakeActive
  /**
   * Live session, no official take yet — story clock play/scrub before Run 1.
   * Take phase is hub-shared (`exerciseTakeNumber` / `exerciseTakeStopped`) so the
   * whole room sees the same chip. After Stop → between-takes ("Run n finished").
   */
  const storyPreviewing =
    inLiveRun &&
    !liveTakeActive &&
    !exerciseRunStopped &&
    !betweenExerciseTakes &&
    takeNumber === 0 &&
    (running || !nearStoryStart)
  /**
   * Shared take-phase copy for the strip chip (Live only).
   * Ready / Story open / Run N / between-takes Previewing — scorebug detail.
   */
  const roomTakePhaseLabel = exercisePhaseChip({
    completed: selectedIsCompleted,
    storyPreviewing,
    clockPreviewRunning: isClockPreview && running && !inLiveRun,
    poisedForFirstRun,
    instantReplayEngaged,
    liveTakeActive,
    running,
    betweenTakes: betweenExerciseTakes,
    liveTakePaused: liveTakePausedForReplay,
    takeNumber,
  })
  /**
   * Version + status chrome for Exercise — not gated on Live.
   * Run controls (Start/Pause/Stop) stay lifecycle-gated; previewing versions is not.
   */
  const showExerciseScoreboard =
    showStoryStrip &&
    showTransport &&
    typeOffersSharedRun(viewSession.sessionType) &&
    showExerciseRunChrome &&
    (inLiveRun || isClockPreview || viewingTemplate || selectedIsCompleted)
  const boardVersions = viewSession.boardVersions ?? []
  const selectedVersion = selectedBoardVersion(viewSession)
  /** Prior runs for the Story setup currently selected (not the whole session). */
  const runsForSelectedStory = useMemo(() => {
    const vid = selectedVersion?.id
    if (!vid) return [] as ExerciseRun[]
    return (viewSession.exerciseRuns ?? [])
      .filter((r) => r.versionId === vid)
      .slice()
      .sort((a, b) => b.number - a.number)
  }, [viewSession.exerciseRuns, selectedVersion?.id])
  const latestRunForSelectedStory = runsForSelectedStory[0] ?? null
  /** Only count a watch target that belongs to the selected Version (avoids stale #1 after switch). */
  const watchingRun =
    (watchingRunId
      ? (viewSession.exerciseRuns ?? []).find(
          (r) => r.id === watchingRunId && r.versionId === selectedVersion?.id
        )
      : null) ?? null
  const inProgressRun = (() => {
    const live = (viewSession.exerciseRuns ?? []).filter(
      (r) =>
        r.status === 'in_progress' &&
        (!selectedVersion?.id || r.versionId === selectedVersion.id)
    )
    if (!live.length) return null
    // Hub take# wins — never paint the oldest stuck in_progress over Run N.
    if (takeNumber > 0) {
      const match = live.find((r) => r.number === takeNumber)
      if (match) return match
    }
    return live.reduce((a, b) => (a.number >= b.number ? a : b))
  })()
  /** Per-version ordinal (1-based) for strip / modal — not the session-global run.number. */
  const versionLocalRunNumber = (run: ExerciseRun): number =>
    versionLocalRunNumberFor(viewSession, run)
  const nextVersionRunNumber = runsForSelectedStory.length + 1
  const completedLatestForStory =
    latestRunForSelectedStory &&
    (latestRunForSelectedStory.status === 'completed' ||
      latestRunForSelectedStory.status === 'abandoned')
      ? latestRunForSelectedStory
      : null
  /** True only when this Version actually has a finished/selected run to review. */
  const hasReviewableRun = Boolean(watchingRun || completedLatestForStory)
  /**
   * Strip/modal run focus — only an explicit watch target between takes (not “latest
   * finished exists”). Auto-picking completedLatest made Previewing modals claim
   * Run N was being reviewed.
   */
  const stripFocusRun = liveTakeActive ? inProgressRun : watchingRun
  /**
   * Per-version run ordinal for strip / banner / eye tip.
   * During a live take, hub `exerciseTakeNumber` is authoritative — a missed peer
   * `startRun` left P on in_progress Run 1 while take# was already 3.
   */
  const stripFocusRunLocal = (() => {
    if (liveTakeActive) {
      if (takeNumber > 0) {
        if (inProgressRun && inProgressRun.number === takeNumber) {
          return versionLocalRunNumber(inProgressRun)
        }
        if (!selectedVersion?.id) return takeNumber
        const onVersion = (viewSession.exerciseRuns ?? []).filter(
          (r) => r.versionId === selectedVersion.id
        )
        const finished = onVersion.filter(
          (r) => r.status === 'completed' || r.status === 'abandoned'
        ).length
        // Prefer hub take number when runs[] is still catching up / stale.
        return Math.max(takeNumber, finished + 1)
      }
      if (inProgressRun) return versionLocalRunNumber(inProgressRun)
      return null
    }
    if (stripFocusRun) return versionLocalRunNumber(stripFocusRun)
    if (!selectedVersion?.id) {
      if (betweenExerciseTakes && takeNumber > 0) return takeNumber
      return null
    }
    const onVersion = (viewSession.exerciseRuns ?? []).filter(
      (r) => r.versionId === selectedVersion.id
    )
    const finished = onVersion.filter(
      (r) => r.status === 'completed' || r.status === 'abandoned'
    ).length
    if (betweenExerciseTakes && finished > 0) return finished
    return null
  })()
  /** Version picker — template / draft / published / Live; locked only while a take is live. */
  const showStorySetupPick =
    showVersionChrome &&
    showStoryStrip &&
    showTransport &&
    typeOffersSharedRun(viewSession.sessionType) &&
    boardVersions.length > 0 &&
    (inLiveRun || isClockPreview || viewingTemplate || selectedIsCompleted)
  const clockPreviewingStory =
    !inLiveRun && isClockPreview && (running || elapsedMs > 0)
  /**
   * Transport verb for story preview (not the room *task*).
   * Matches the play/pause control: advancing → Previewing; stopped (mid or end) →
   * Paused. Modal title carries the task (“Previewing …”). Do not invent a third
   * chip like “Preview complete” — that belongs in toast land only.
   */
  const previewTransportLabel =
    running && !instantReplayEngaged
      ? 'Previewing'
      : hubWirePlayheadMs > 50 || elapsedMs > 50
        ? 'Paused'
        : 'Previewing'
  /**
   * Shared finished-run Reviewing (hub tapeReviewRunId). Banner follows this room
   * signal, not only local tapeReview — peers used to miss IR · Paused when the
   * overlay lagged. Never while an official take is active.
   */
  const sharedFinishedRunReview =
    !liveTakeActive &&
    typeof viewSession.tapeReviewRunId === 'string' &&
    Boolean(viewSession.tapeReviewRunId.trim())
  /**
   * “I’m done reviewing” — Previewing/Paused between takes with no shared tape.
   * Hub `tapeReviewRunId` is the durable SoT: cleared means Live Idle Previewing
   * (reload/HMR must not resurrect Reviewing · Run N).
   */
  const explicitlyDoneReviewing =
    !tapeReviewActive &&
    !watchingRun &&
    !sharedFinishedRunReview &&
    (boardActivity === 'preview' ||
      boardActivity === 'editing' ||
      boardActivity === 'ready')
  /**
   * Actively reviewing a finished run (shared tape id and/or local watch overlay).
   * Between-takes alone is not Reviewing — cleared tape = Previewing until a chip
   * pick or StopRun stamps tapeReviewRunId again.
   */
  const inRunReview =
    !liveTakeActive &&
    !explicitlyDoneReviewing &&
    (tapeReviewActive ||
      Boolean(watchingRun) ||
      sharedFinishedRunReview ||
      boardActivity === 'watching')
  const showFinishedRunReviewBanner =
    !liveTakeActive && (tapeReviewActive || sharedFinishedRunReview)
  /**
   * Strip status opens the run-options / status modal whenever the chip is shown
   * (except during countdown). Operators get controls; participants get read-only —
   * same openability so the chip is never a frustrating dead control.
   */
  const canOpenRunStatus = runCountdown == null && !versionSwitching && !studioLinkDown
  // Prefer hub lifecycle × take phase × clock (SoT) over local boardActivity alone.
  const hubStripLabel = deriveRoomTransportStripLabel({
    completed: selectedIsCompleted,
    takeNumber: stripFocusRunLocal ?? takeNumber,
    takeStopped: Boolean(viewSession.exerciseTakeStopped),
    tapeReviewRunId: viewSession.tapeReviewRunId,
    liveTakeActive,
    running,
    instantReplayEngaged,
    countdownActive: runCountdown != null,
    versionSwitching,
    editing: boardActivity === 'editing',
    reviewing: inRunReview,
    storyPreviewing:
      !liveTakeActive &&
      !inRunReview &&
      (storyPreviewing ||
        clockPreviewingStory ||
        betweenExerciseTakes ||
        boardActivity === 'preview' ||
        (viewingTemplate && !liveTakeActive)),
  })
  // Stable strip tasks: Previewing / Paused / Running · #N / Reviewing · #N / IR /
  // Editing / Switching / Starting · #N (countdown) / Offline.
  const stripStatusLabel = studioLinkDown
    ? hubLinkState === 'reconnecting'
      ? 'Reconnecting'
      : 'Offline'
    : hubStripLabel ??
      (inLiveRun ? roomTakePhaseLabel ?? previewTransportLabel : previewTransportLabel)
  /** Passive eye tip — always "You are …" (never host verbs like "Preview story"). */
  const followEyeLabel = (() => {
    if (followingStoryPresenter) return 'You are watching the Presenter’s story'
    if (boardActivity === 'editing') return 'You are watching board edits'
    if (inRunReview || stripStatusLabel.startsWith('Reviewing')) {
      const n = stripFocusRunLocal
      return n != null
        ? `You are watching a review of Run ${n}`
        : 'You are watching a run review'
    }
    if (instantReplayEngaged && stripFocusRunLocal != null) {
      return `You are watching Instant Replay of Run ${stripFocusRunLocal}`
    }
    const previewingFollow =
      !liveTakeActive &&
      (selectedIsPlanned ||
        stripStatusLabel === 'Previewing' ||
        stripStatusLabel === 'Paused' ||
        storyPreviewing ||
        clockPreviewingStory)
    if (previewingFollow) return 'You are watching a story preview'
    if (takeNumber > 0 && !exerciseRunStopped) return `You are watching Run ${takeNumber}`
    return 'You are watching an exercise'
  })()
  const startRunButtonLabel =
    runsForSelectedStory.length === 0 ? 'Start a run' : 'Start a new run'
  const previewingVersionLabel = (selectedVersion?.name ?? 'this version').trim() || 'this version'
  const versionQuoted = `"${previewingVersionLabel}"`
  /** Modal title — short form; Reviewing only when this version has a finished take. */
  const reviewModalRunN =
    stripFocusRunLocal ??
    (watchingRun ? versionLocalRunNumber(watchingRun) : null) ??
    (completedLatestForStory ? versionLocalRunNumber(completedLatestForStory) : null)
  const storyModalTitle = (() => {
    const titleShort =
      previewingVersionLabel.length > 36
        ? `${previewingVersionLabel.slice(0, 33).trimEnd()}…`
        : previewingVersionLabel
    // Only while actively reviewing — finished runs alone stay Previewing.
    if (inRunReview || boardActivity === 'watching' || tapeReviewActive) {
      return reviewModalRunN != null
        ? `You're reviewing Run ${reviewModalRunN}`
        : "You're reviewing a run"
    }
    return `Previewing "${titleShort}"`
  })()
  /** Duplicate is part of editing — available in Draft / Published / Live (not mid-take). */
  const showDuplicateVersionAction =
    showVersionChrome && Boolean(selectedVersion) && canOperateSession
  /**
   * Runs available to Instant Replay for the selected version (oldest → newest).
   * Between takes / reviewing, include stuck in_progress rows too — Stop/Start races
   * used to leave every take as in_progress, which hid the whole chip row.
   */
  const reviewRunChips = useMemo(() => {
    const reviewingBetweenTakes =
      !liveTakeActive &&
      (selectedIsCompleted ||
        exerciseRunStopped ||
        betweenExerciseTakes ||
        tapeReviewActive ||
        boardActivity === 'watching')
    const rows = runsForSelectedStory.filter((r) => {
      if (r.status === 'completed' || r.status === 'abandoned') return true
      return reviewingBetweenTakes && r.status === 'in_progress'
    })
    // Safety: if we are watching a run that somehow failed the filter, still show it.
    if (
      watchingRun &&
      !rows.some((r) => r.id === watchingRun.id) &&
      watchingRun.versionId === selectedVersion?.id
    ) {
      rows.push(watchingRun)
    }
    return rows.slice().sort((a, b) => a.number - b.number)
  }, [
    runsForSelectedStory,
    liveTakeActive,
    selectedIsCompleted,
    exerciseRunStopped,
    betweenExerciseTakes,
    tapeReviewActive,
    boardActivity,
    watchingRun,
    selectedVersion?.id,
  ])
  /** Stable chip props — keep hover snappy while the page ticks on the clock. */
  const reviewRunChipItems = useMemo(
    () =>
      reviewRunChips.map((run) => ({
        id: run.id,
        localNum: versionLocalRunNumber(run),
      })),
    [reviewRunChips],
  )
  const reviewChipActiveRunId =
    watchingRunId && reviewRunChips.some((r) => r.id === watchingRunId)
      ? watchingRunId
      : tapeReview?.runId && reviewRunChips.some((r) => r.id === tapeReview.runId)
        ? tapeReview.runId
        : null
  const canPickReviewRunChip = canOperateSession || selectedIsCompleted
  const pickReviewRunChipRef = useRef<(runId: string) => void>(() => {})
  const onPickReviewRunChip = useCallback((runId: string) => {
    pickReviewRunChipRef.current(runId)
  }, [])
  /**
   * Mission clock (top): official take / IR pause point, otherwise the shared playhead.
   * Preview must keep moving — pinning this to 0 made empty boards look frozen.
   */
  // Freeze top mission clock at the shared IR pause point (host + spectators).
  // Only mid-take — never freeze from a stale pauseMs during Ready / story preview.
  const missionClockMs =
    inLiveRun && !running && takeNumber > 0 && instantReplayPauseMs != null
      ? instantReplayPauseMs
      : viewElapsedMs
  const missionPlayheadIso = addStoryMs(viewSession.storyStartTime, missionClockMs)
  liveTakeActiveRef.current = liveTakeActive
  /** Trainer force. Read from the live session so Instant Replay tape doesn't drop it. */
  const roomPlumesForced = liveTakeActive && session.runRoomPlumes === true
  const mapSurfacePlumes =
    roomPlumesForced
      ? true
      : followingPresenter && typeof watchFrame?.surfacePlumes === 'boolean'
        ? watchFrame.surfacePlumes
        : surfacePlumes
  const liveTakeWasActiveRef = useRef(false)
  useEffect(() => {
    const becameLive = liveTakeActive && !liveTakeWasActiveRef.current
    liveTakeWasActiveRef.current = liveTakeActive
    if (!becameLive || session.runRoomPlumes !== true) return
    pushInfoToast('Wind plumes are on for this run', false, 3, 3200)
  }, [liveTakeActive, session.runRoomPlumes])
  takeNumberRef.current = takeNumber
  exerciseRunStoppedRef.current = exerciseRunStopped
  canOperateSessionRef.current = canOperateSession
  hubActionsLockedRef.current = hubActionsLocked
  selectedIdRef.current = selectedId
  canMutateSelectedRef.current = canMutateSelected
  selectedObjectPristineRef.current = Boolean(selectedSource?.pristine)
  selectedSessionIdRef.current = viewingTemplate ? null : instanceId
  // Done: no Delete/Backspace from the list (Settings still allows admin delete).
  canDeleteSelectedSessionRef.current =
    showSessionDeleteAction &&
    currentInstance?.lifecycle !== 'completed' &&
    !selectedIsCompleted
  requestDeleteSelectedRef.current = () => {
    const objectId = selectedIdRef.current
    if (objectId && canMutateSelectedRef.current) {
      if (selectedObjectPristineRef.current) {
        deleteObject(objectId)
        return
      }
      setPendingDeleteObjectId(objectId)
      return
    }
    const sessionId = selectedSessionIdRef.current
    if (sessionId && canDeleteSelectedSessionRef.current) {
      setPendingDeleteId(sessionId)
    }
  }
  canSeekTimeBarRef.current = canSeekTimeBar
  skipBeatRef.current = skipStoryBeat
  liveTransportToggleRef.current = () => {
    const btn = document.querySelector(
      '.sc-strip .sc-btn.rec.sc-strip-transport:not(:disabled)'
    ) as HTMLButtonElement | null
    btn?.click()
  }
  const pathPreviewActive = pathPreviewTotalMs != null
  const visPreviewActive = visPreviewTotalMs != null
  const radiusPreviewActive = radiusPreviewTotalMs != null
  const windPreviewActive = windPreviewTotalMs != null
  const seriesPreviewActive =
    pathPreviewActive || visPreviewActive || radiusPreviewActive || windPreviewActive
  seriesPreviewActiveRef.current = seriesPreviewActive
  /**
   * Exact mark under the authoring playhead — ignored while Preview plays so Set/Update/Remove
   * don’t flash when the clock lands on (then leaves) the first keyframe.
   */
  const windClockMs = isLocalStorm(selectedSource?.type) ? storyBarElapsedMs : windAuthorMs
  const windMarkIndexLive = nearestWindFrameIndex(windFrames, windClockMs, (iso) => off(iso))
  const windMarkIndex = windPreviewActive ? -1 : windMarkIndexLive
  const windAtExactMark = windMarkIndex >= 0
  const directedCamera = useMemo(() => {
    // Room Presenter sharing: silent override of Auto-track (switch unchanged).
    // Still follow while you share your own pointer — only the Presenter skips follow.
    const me = user.email.trim().toLowerCase()
    const followingPresenter =
      Boolean(watchFrame) && Boolean(presenterEmail) && presenterEmail !== me
    if (followingPresenter && watchFrame) {
      return {
        lng: watchFrame.lng,
        lat: watchFrame.lat,
        zoom: watchFrame.zoom,
        beatKey: `watch:${watchFrame.lng.toFixed(4)}:${watchFrame.lat.toFixed(4)}:${watchFrame.zoom.toFixed(2)}`,
        easeMs: 180,
        instant: false,
        softHold: false,
      }
    }
    if (!cameraFollowOn) return null
    const beats = viewSession.cameraBeats ?? []
    // Version flip settle: hold the destination opening framing once. Playhead/Clock can
    // still race late→start under the Switching veil; don't ease between the prior
    // last-beat framing and the new opening shot while ApplyBoardSeed + Seek run.
    // Prefer the first camera beat (Bird strike wide shot) over session.camera when both
    // exist so lifting the veil does not fire a second ease to the same framing.
    if (versionSwitching) {
      const sorted = [...beats].sort((a, b) => off(a.at) - off(b.at))
      const opening = sorted[0]
      const cam = opening
        ? { lng: opening.lng, lat: opening.lat, zoom: opening.zoom }
        : viewSession.camera
      return {
        lng: cam.lng,
        lat: cam.lat,
        zoom: cam.zoom,
        // Stable key into the post-switch first-beat key so veil-off does not re-ease.
        beatKey: opening
          ? `${opening.at}:${opening.lng.toFixed(5)}:${opening.lat.toFixed(5)}:${opening.zoom.toFixed(2)}:a${cameraFollowApplyEpoch}`
          : `version-switch:${cam.lng.toFixed(5)}:${cam.lat.toFixed(5)}:${cam.zoom.toFixed(2)}`,
        easeMs: 0,
        instant: true,
        softHold: false,
      }
    }
    if (!beats.length) return null
    // While paused, hover scrub must win over a stale selected card (timelineFocusId),
    // otherwise Auto-pan only updates after click. Preview/running uses the clock.
    const beatLookupMs =
      !running && hoverElapsedMs != null ? hoverElapsedMs : (currentAtMs ?? mapElapsedMs)
    const beat =
      cameraBeatNear(beats, atOff(beatLookupMs), 2_000) ??
      cameraBeatAtElapsed(beats, mapPlayheadIso)
    const baseEase = DEFAULT_CAMERA_EASE_MS
    const scrubEaseMs = running
      ? Math.min(1400, Math.max(480, baseEase))
      : Math.min(900, Math.max(500, baseEase))
    if (!beat) {
      // Before the first stamp — ease back to the board default (avoid foundation jumpTo).
      const cam = viewSession.camera
      return {
        lng: cam.lng,
        lat: cam.lat,
        zoom: cam.zoom,
        beatKey: `default:${cam.lng.toFixed(5)}:${cam.lat.toFixed(5)}:${cam.zoom.toFixed(2)}:a${cameraFollowApplyEpoch}`,
        easeMs: scrubEaseMs,
        instant: false,
        softHold: false,
      }
    }
    const sorted = [...beats].sort((a, b) => off(a.at) - off(b.at))
    const idx = sorted.findIndex(
      (b) =>
        off(b.at) === off(beat.at) &&
        b.lng === beat.lng &&
        b.lat === beat.lat &&
        b.zoom === beat.zoom
    )
    const next = idx >= 0 ? sorted[idx + 1] : undefined
    const beatEase = beat.easeMs ?? baseEase
    // Wall-clock ease during Preview; same ease when scrubbing/paused so zoom doesn't jump.
    const gapScenarioMs = next
      ? Math.max(0, off(next.at) - off(beat.at))
      : beatEase * Math.max(1, playbackRate)
    const gapWallMs = running ? gapScenarioMs / Math.max(1, playbackRate) : beatEase
    const easeMs = running
      ? Math.min(1400, Math.max(480, Math.round(Math.min(beatEase, gapWallMs * 0.7))))
      : Math.min(900, Math.max(500, beatEase))
    // Soft-hold only while still on the card that was reframed (not other cards / Preview).
    const softHold =
      !running &&
      camEditHoldAtMs != null &&
      Math.abs(off(beat.at) - camEditHoldAtMs) <= CAMERA_BEAT_MERGE_MS &&
      (hoverElapsedMs ?? currentAtMs) != null &&
      Math.abs((hoverElapsedMs ?? currentAtMs)! - camEditHoldAtMs) <= CAMERA_BEAT_MERGE_MS
    return {
      lng: beat.lng,
      lat: beat.lat,
      zoom: beat.zoom,
      // Include apply epoch so OFF→ON / card hover re-eases after the user panned away.
      beatKey: `${beat.at}:${beat.lng.toFixed(5)}:${beat.lat.toFixed(5)}:${beat.zoom.toFixed(2)}:a${cameraFollowApplyEpoch}`,
      easeMs,
      instant: false,
      softHold,
    }
  }, [
    cameraFollowOn,
    versionSwitching,
    watchOn,
    watchFrame,
    presenterEmail,
    user.email,
    viewSession.cameraBeats,
    viewSession.camera,
    mapElapsedMs,
    hoverElapsedMs,
    currentAtMs,
    running,
    playbackRate,
    camEditHoldAtMs,
    cameraFollowApplyEpoch,
  ])
  /** Selected or hovered card clock — used when arming a Save-camera soft-hold. */
  const followEditAnchorMs = !running ? (hoverElapsedMs ?? currentAtMs) : null
  const followEditAnchorMsRef = useRef(followEditAnchorMs)
  followEditAnchorMsRef.current = followEditAnchorMs

  // Leave the reframed card or start Preview → drop soft-hold so Auto-pan runs again.
  useEffect(() => {
    if (camEditHoldAtMs == null) return
    if (running || followEditAnchorMs == null) {
      setCamEditHoldAtMs(null)
      return
    }
    if (Math.abs(followEditAnchorMs - camEditHoldAtMs) > CAMERA_BEAT_MERGE_MS) {
      setCamEditHoldAtMs(null)
    }
  }, [camEditHoldAtMs, followEditAnchorMs, running])
  const highlightIds = useMemo(() => {
    const withoutLabels = (ids: string[]) =>
      ids.filter((id) => viewSession.objects.find((o) => o.id === id)?.type !== 'label')
    // Light the playhead beat while playing *or* paused — otherwise map captions
    // (mapLabel) vanish on pause when zoom is below the caption threshold.
    if (currentAtMs != null) {
      const ids = new Set<string>()
      for (const ev of displayTimeline) {
        if (off(ev.at) !== currentAtMs) continue
        for (const id of eventObjectIds(ev.actions)) ids.add(id)
      }
      if (ids.size) return withoutLabels([...ids])
    }
    if (hoverIds.length) return withoutLabels(hoverIds)
    return []
  }, [hoverIds, currentAtMs, displayTimeline, viewSession.objects])

  // Link-edit highlight only: checked picks while Find/Edit links is open.
  // Selection alone must not light the cluster or dim the map (blocks normal Inspect use).
  const attachedHighlightObjects = useMemo(() => {
    if (!selectedSource || !nearbyCandidates.length) return []
    const picked = nearbyCandidates.filter((c) => nearbyPicks.has(assetKey(c)))
    if (!picked.length) return []
    return [
      {
        params: writeAttachedAssets(
          {},
          picked.map((tap) => ({
            kind: tap.kind,
            name: tap.name,
            properties: tap.properties,
            path: tap.path,
            segments: tap.segments,
          }))
        ),
      },
    ]
  }, [selectedSource, nearbyCandidates, nearbyPicks])

  const sortedNearbyCandidates = useMemo(() => {
    const rows = [...nearbyCandidates]
    const dir = nearbySort.dir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      if (nearbySort.col === 'checked') {
        const ca = nearbyPicks.has(assetKey(a)) ? 0 : 1
        const cb = nearbyPicks.has(assetKey(b)) ? 0 : 1
        return (ca - cb) * dir || (a.distanceM ?? 0) - (b.distanceM ?? 0)
      }
      if (nearbySort.col === 'type') {
        const ta = a.kind === 'pipeline' ? 'Pipe' : 'Road'
        const tb = b.kind === 'pipeline' ? 'Pipe' : 'Road'
        return ta.localeCompare(tb) * dir || (a.distanceM ?? 0) - (b.distanceM ?? 0)
      }
      if (nearbySort.col === 'name') {
        return a.name.localeCompare(b.name) * dir || (a.distanceM ?? 0) - (b.distanceM ?? 0)
      }
      const da = a.distanceM ?? Number.POSITIVE_INFINITY
      const db = b.distanceM ?? Number.POSITIVE_INFINITY
      return (da - db) * dir || a.name.localeCompare(b.name)
    })
    return rows
  }, [nearbyCandidates, nearbySort, nearbyPicks])

  const linkedAssetsSorted = useMemo(() => {
    if (!selectedSource) return [] as AttachedAsset[]
    const pose = selectedSource.pose
    const assets = readAttachedAssets(selectedSource.params).map((a) => {
      const fresh = mapApiRef.current?.resolveAttachedGeometry(a.kind, a.properties)
      return fresh?.segments.length ? { ...a, path: fresh.path, segments: fresh.segments } : a
    })
    const dir = linkedSort.dir === 'asc' ? 1 : -1
    return [...assets].sort((a, b) => {
      if (linkedSort.col === 'type') {
        const ta = a.kind === 'pipeline' ? 'Pipe' : 'Road'
        const tb = b.kind === 'pipeline' ? 'Pipe' : 'Road'
        const da = pose ? (assetDistanceM(pose, a) ?? 0) : 0
        const db = pose ? (assetDistanceM(pose, b) ?? 0) : 0
        return ta.localeCompare(tb) * dir || da - db
      }
      if (linkedSort.col === 'name') {
        const da = pose ? (assetDistanceM(pose, a) ?? 0) : 0
        const db = pose ? (assetDistanceM(pose, b) ?? 0) : 0
        return a.name.localeCompare(b.name) * dir || da - db
      }
      const da = pose
        ? (assetDistanceM(pose, a) ?? Number.POSITIVE_INFINITY)
        : Number.POSITIVE_INFINITY
      const db = pose
        ? (assetDistanceM(pose, b) ?? Number.POSITIVE_INFINITY)
        : Number.POSITIVE_INFINITY
      return (da - db) * dir || a.name.localeCompare(b.name)
    })
  }, [selectedSource, mapIdleEpoch, linkedSort])

  function closeMapAssetPanel(): void {
    setMapAssetDetailsOpen(false)
  }

  function dismissMapAsset(): void {
    mapAssetGenRef.current += 1
    setMapAssetPick(null)
    setMapAssetDetailsOpen(false)
  }

  function openMapAssetDetails(): void {
    if (!mapAssetPick?.hits.length) return
    setHelpOpen(false)
    setMapAssetDetailsOpen(true)
  }

  function applyBasemapPipelines(
    hits: MapFeatureTap[],
    _point: { x: number; y: number },
  ): void {
    mapAssetGenRef.current += 1
    if (!hits.length) {
      setMapAssetPick(null)
      setMapAssetDetailsOpen(false)
      return
    }
    // Jurisdiction stays off this path while Studio is used for emergency work.
    // The Buxus map still applies its own gate. A line the operator style drew is shown.
    setMapAssetPick({ hits, index: 0 })
  }

  mapAssetDismissRef.current = dismissMapAsset
  mapAssetOpenRef.current = mapAssetPick != null
  mapAssetDetailsOpenRef.current = mapAssetDetailsOpen

  const mapAssetHighlight = mapAssetPick?.hits[mapAssetPick.index] ?? null
  const mapAssetChip = mapAssetPick
    ? {
        lng: mapAssetPick.hits[mapAssetPick.index]?.lng ?? 0,
        lat: mapAssetPick.hits[mapAssetPick.index]?.lat ?? 0,
        name: mapAssetPick.hits[mapAssetPick.index]?.name || 'Pipeline',
        operator: (() => {
          const raw = mapAssetPick.hits[mapAssetPick.index]?.properties.CO_NAME
          return typeof raw === 'string' ? raw.trim() : ''
        })(),
        count: mapAssetPick.hits.length,
      }
    : null
  const mapAssetOperators = (() => {
    const rows = downloadedOperators.map((op) => ({
      operatorId: op.operatorId,
      name: op.name,
    }))
    const mapId = viewSession.activeMapId
    if (mapId.startsWith('op:')) {
      const operatorId = mapId.slice(3).trim()
      if (operatorId && !rows.some((row) => row.operatorId === operatorId)) {
        rows.push({ operatorId, name: mapRefById(mapId).label })
      }
    }
    return rows
  })()

  const boardMapId = getStartedOpen ? MAP_POSTER_STYLE_ID : viewSession.activeMapId
  const boardPlate = sessionMapStyle(
    getStartedOpen ? undefined : viewSession.maps,
    boardMapId,
    mapCatalog,
  )

  const featureHoverHighlight = useMemo(() => {
    const live = (asset: AttachedAsset, pose: { lng: number; lat: number } | null) => {
      const fresh = mapApiRef.current?.resolveAttachedGeometry(asset.kind, asset.properties)
      if (!fresh?.segments.length) return null
      const resolved = { ...asset, path: fresh.path, segments: fresh.segments }
      return assetToHighlightTap(resolved, pose)
    }
    // Find-nearby: hot layer follows the hovered row (checked or not).
    if (nearbyCandidates.length && nearbyHoverKey) {
      return nearbyCandidates.find((x) => assetKey(x) === nearbyHoverKey) ?? null
    }
    if (nearbyCandidates.length) return null
    // Non-edit Inspect: hot layer only on linked-row hover (no cluster / lightbox).
    if (inspectDockOpen && linkedHoverKey && selectedSource) {
      const asset = readAttachedAssets(selectedSource.params).find(
        (a) => assetKey(a) === linkedHoverKey
      )
      return asset ? live(asset, selectedSource.pose) : null
    }
    return null
  }, [inspectDockOpen, linkedHoverKey, nearbyCandidates, nearbyHoverKey, selectedSource, mapIdleEpoch])

  /** Cyan cluster + lightbox only while editing links in Inspect. */
  const linksEditing = nearbyCandidates.length > 0 && inspectDockOpen
  const cyanFeatureHlActive = linksEditing && Boolean(selectedId)
  /** Dim basemap while the Position/Waypoints accordion is open (path isolation). Undim for Preview. */
  const mapFocusDim =
    ghostFlash ||
    zoomFlash ||
    cyanFeatureHlActive ||
    (Boolean(waypointObjectId) && !pathPreviewActiveEarly && !windPreviewActiveEarly) ||
    (isLocalStorm(selectedSource?.type) && tornadoPreviewFocus && running)
  const ghostFlashIds = useMemo(() => {
    if (!ghostFlash && !zoomFlash) return []
    const ids: string[] = []
    for (const obj of mapResolved.objects) {
      if (ghostFlash && obj.ghost) ids.push(obj.id)
      else if (zoomFlash && !obj.ghost && objectAnchorLngLat(obj)) ids.push(obj.id)
    }
    return ids
  }, [ghostFlash, zoomFlash, mapResolved])

  const featureDebugProps = useMemo(() => {
    if (!assetDebugKey) return null
    const fromNearby = nearbyCandidates.find((c) => assetKey(c) === assetDebugKey)
    if (fromNearby) return fromNearby.properties
    if (selectedSource) {
      const asset = readAttachedAssets(selectedSource.params).find(
        (a) => assetKey(a) === assetDebugKey
      )
      if (asset) return asset.properties
    }
    return null
  }, [assetDebugKey, nearbyCandidates, selectedSource])

  function toggleAssetDebug(key: string, ev?: { stopPropagation: () => void }): void {
    ev?.stopPropagation()
    if (!canViewDebug) return
    setAssetDebugKey((cur) => (cur === key ? null : key))
  }

  useEffect(() => {
    // Prefer hub Clock.rate when present so peers keep the same strip thumb after join / board-length change.
    // Do not push hubSetRate here — that overwrote a peer's speed and raced Pause/PreviewPlay.
    const clock = !previewTemplateIdRef.current
      ? clocksRef.current.get(instanceIdRef.current)
      : undefined
    if (clock && Number.isFinite(clock.rate) && clock.rate > 0) {
      setPlaybackRuntimeMs(Math.round(fullRuntimeMs / Math.max(PLAYBACK_RATE_MIN, clock.rate)))
      return
    }
    setPlaybackRuntimeMs(defaultPlaybackRuntimeMs(fullRuntimeMs))
  }, [instanceId, fullRuntimeMs])

  useEffect(() => {
    if (!running) return
    setTimelineFocusId(null)
  }, [currentEvent?.id, running])

  useEffect(() => {
    const raw = selectedSource?.description ?? ''
    // Auto story lines stay live (units/radius); don't park a stale bake in the draft.
    setDescDraft(
      selectedSource && isBrwnPlumeType(selectedSource.type) && isGeneratedBrwnPlumeDescription(raw)
        ? ''
        : raw
    )
    setRadiusInputDraft(null)
    setTitleEditing(false)
    setLinkedHoverKey(null)
    setAssetDebugKey(null)
    linkedGeomRefreshIdRef.current = null
  }, [selectedSource?.id, selectedSource?.description])

  useEffect(() => {
    if (selectedIsCompleted) setCompletedOpen(true)
  }, [selectedIsCompleted])

  // Keep fold [hidden] / chevron in sync when React opens bins (select card, prefs).
  useEffect(() => {
    paintRailFold(liveFoldBodyRef.current, liveFoldChevronRef.current, null, liveOpen)
  }, [liveOpen])
  useEffect(() => {
    paintRailFold(
      completedFoldBodyRef.current,
      completedFoldChevronRef.current,
      null,
      completedOpen
    )
  }, [completedOpen])
  useEffect(() => {
    paintRailFold(
      oversightFoldBodyRef.current,
      oversightFoldChevronRef.current,
      null,
      oversightOpen
    )
  }, [oversightOpen])

  /** Only when the open board *changes* into Oversight — never re-fight a manual collapse. */
  const railAutoOpenInstanceRef = useRef<string | null>(null)
  // Selecting a card past the always-visible window reveals the flat “more” list.
  useEffect(() => {
    if (!instanceId || viewingTemplate) {
      railAutoOpenInstanceRef.current = instanceId
      return
    }
    const instanceChanged = railAutoOpenInstanceRef.current !== instanceId
    railAutoOpenInstanceRef.current = instanceId
    const doneRows = [...panePinnedCompleted, ...paneListedCompleted]
    if (doneRows.findIndex((i) => i.id === instanceId) >= RAIL_FULL_CARDS) {
      setDoneShowMore(true)
    }
    const oversightIdx = paneOversight.findIndex((i) => i.id === instanceId)
    if (oversightIdx < 0 || !instanceChanged) return
    // New board in Oversight — clear manual-close and open once.
    oversightManualCloseRef.current = false
    setOversightOpen(true)
    try {
      localStorage.setItem('buxus.sc.oversightFoldOpen', '1')
    } catch {
      /* private mode */
    }
    if (oversightIdx >= RAIL_FULL_CARDS) setOversightShowMore(true)
  }, [instanceId, viewingTemplate, panePinnedCompleted, paneListedCompleted, paneOversight])

  // Exercise Stop → keep the time bar for review (natural next step after Stop).
  useEffect(() => {
    if (betweenExerciseTakes) {
      setTimeBarAutoHidden(false)
      setTimeBarPinned(true)
      return
    }
    if (liveTakeActive) return
    // Ignore take-phase hold while Stop is unconfirmed so Reviewing sticks.
    const hold = takePhaseHoldRef.current
    if (hold?.stopped) return
    // Never auto-dismiss while the run-options / duplicate flow is open.
    if (postRunOfferOpenRef.current || versionDupUiRef.current) return
    // Only clear watch target when we are truly back to pre–Run 1 idle — not on
    // every flicker out of between-takes (that wiped Reviewing → Previewing).
    if (poisedForFirstRun) {
      setPostRunOfferOpen(false)
      setWatchingRunId(null)
      setBoardActivity('ready')
    }
  }, [betweenExerciseTakes, liveTakeActive, poisedForFirstRun])

  // Friendly run-options modal: Escape dismisses (same as click outside / Not now).
  useEffect(() => {
    if (!postRunOfferOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setPostRunOfferOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [postRunOfferOpen])

  useEffect(() => {
    if (focusMode) setHelpOpen(false)
  }, [focusMode])

  // Version copy modal (from the version dropdown): Escape dismisses.
  useEffect(() => {
    if (!versionDupUi) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setVersionDupUi(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [versionDupUi])

  // Post-copy success modal: Escape dismisses (same as click outside / Got it).
  useEffect(() => {
    if (!cloneReadyOffer) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setCloneReadyOffer(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cloneReadyOffer])

  // Join card owns focus — clear any catch-up toast that raced in underneath.
  useEffect(() => {
    if (!showJoinOfferCard) return
    setJoinToasts([])
    toastBurstRef.current = null
  }, [showJoinOfferCard])

  /**
   * Shared countdown digit from a wall-clock deadline.
   * ceil so 5000→5 … 1→1; ≤0 means “fire Start / clear overlay”.
   */
  function runCountdownDigitFromEndsAt(endsAtMs: number, now = Date.now()): number | null {
    const rem = endsAtMs - now
    if (rem <= 0) return null
    return Math.min(5, Math.max(1, Math.ceil(rem / 1000)))
  }

  // Wall-clock 5…4…3…2…1 for operator + peers. Sending Fields on every second used to
  // leave P ~1s + RTT behind A; both sides now derive the digit from endsAt locally.
  useEffect(() => {
    if (runCountdownEndsAtRef.current == null) return
    const tick = (): void => {
      const ends = runCountdownEndsAtRef.current
      if (ends == null) return
      const digit = runCountdownDigitFromEndsAt(ends)
      if (digit == null) {
        runCountdownEndsAtRef.current = null
        setRunCountdown(null)
        if (runCountdownDriverRef.current) {
          runCountdownDriverRef.current = false
          // Do NOT clear runStartCountdown here — a separate Fields null raced the
          // take write and could wipe the new run. Hub StartRun clears it on the same row.
          const opts = runCountdownOptsRef.current
          runCountdownOptsRef.current = undefined
          resetLiveStory(opts)
        }
        // Peer: clear at the wall deadline with A — do not hold "1" until StartRun
        // RTT lands (that left P on 1 for an extra second and missed run open).
        return
      }
      setRunCountdown((c) => (c === digit ? c : digit))
    }
    tick()
    const id = window.setInterval(tick, 100)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- kick when endsAt is armed (runCountdown set)
  }, [runCountdown != null])

  useEffect(() => {
    if (!justCompletedId) return
    scenarioRailRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [justCompletedId])

  // Green “just finished” pin lasts only while that Done board stays open. Any leave
  // (other session, Templates, Sessions hub) returns it to the normal Done fold.
  useEffect(() => {
    if (!justCompletedId) return
    const stillOnIt = !libraryMode && !previewTemplateId && instanceId === justCompletedId
    if (!stillOnIt) setJustCompletedId(null)
  }, [justCompletedId, instanceId, libraryMode, previewTemplateId])

  const libraryModeRef = useRef(libraryMode)
  // Lost access or vanished board → neutral Sessions hub (never jump to another titled board).
  useEffect(() => {
    const leftLibrary = libraryModeRef.current && !libraryMode
    libraryModeRef.current = libraryMode
    if (seeAllInstances || previewTemplateId || libraryMode || leftLibrary) return
    if (!instanceId) return
    const open = instances.find((i) => i.id === instanceId)
    if (!open) {
      clearStudioArrival()
      parkSessionsHub()
      setSessionUnavailableOpen(true)
      return
    }
    // Entitlement only — do not treat “board currently open” as access (that trapped
    // removed invitees until reload). Roster, ViewAll, or post-Publish Public / link guests.
    const stillAllowed =
      seeAllInstances || onMyRoster(open) || instanceOpenViaAudience(open)
    if (stillAllowed) return
    clearStudioArrival()
    parkSessionsHub()
    setSessionUnavailableOpen(true)
  }, [seeAllInstances, previewTemplateId, libraryMode, instances, instanceId, user.email])

  useEffect(() => {
    if (pathPreviewTotalMs == null || !pathPreviewObjectId) return
    const frames = sortPoseFrames(
      viewSession.objects.find((o) => o.id === pathPreviewObjectId)?.keyframes ?? []
    )
    if (frames.length < 2) {
      setPathPreviewTotalMs(null)
      setPathPreviewStartMs(null)
      setPathPreviewPose(null)
      setPathPreviewWaypointIndex(null)
      setPathPreviewHolding(false)
      setPathHoldLabel(null)
      setInspectPreviewMs(null)
      return
    }
    const total = pathPreviewTotalMs
    const startMs = pathPreviewStartMs ?? off(frames[0]?.at) ?? 0
    const rate = Math.max(0.05, playbackRate)
    const startWall = performance.now()
    const id = window.setInterval(() => {
      const advanceMs = (performance.now() - startWall) * rate
      if (advanceMs >= total) {
        const endElapsed = startMs + total
        const end = posePreviewAtElapsed(frames, atOff(endElapsed))
        if (end) {
          setPathPreviewPose(end.pose)
          setPathPreviewWaypointIndex(end.waypointIndex)
          setWaypointFocusIndex(end.waypointIndex)
          setPathPreviewHolding(false)
        }
        setPathPreviewTotalMs(null)
        setPathPreviewStartMs(null)
        setPathPreviewPose(null)
        setPathPreviewWaypointIndex(null)
        setPathPreviewHolding(false)
        setPathHoldLabel(null)
        setInspectPreviewMs(null)
        return
      }
      const sample = posePreviewAtElapsed(frames, atOff(startMs + advanceMs))
      if (!sample) return
      setPathPreviewPose(sample.pose)
      setInspectPreviewMs(off(sample.at))
      setPathPreviewWaypointIndex(sample.waypointIndex)
      setWaypointFocusIndex(sample.waypointIndex)
      setPathPreviewHolding(sample.holding)
      setPathHoldLabel(
        sample.holding
          ? `Hold ${formatHoldCountdown(
              boardDeltaToAuthorVisibleMs(sample.holdRemainingMs, authorClock)
            )}`
          : null
      )
    }, 50)
    return () => window.clearInterval(id)
    // viewSession.objects identity is fine — preview pins object id + epoch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathPreviewTotalMs, pathPreviewStartMs, pathPreviewEpoch, pathPreviewObjectId, playbackRate])

  useEffect(() => {
    if (visPreviewTotalMs == null || !visPreviewObjectId) return
    const frames = sortVisFrames(
      viewSession.objects.find((o) => o.id === visPreviewObjectId)?.visibilityKeyframes ?? []
    )
    if (frames.length < 2) {
      setVisPreviewTotalMs(null)
      setVisPreviewObjectId(null)
      setInspectPreviewMs(null)
      return
    }
    const total = visPreviewTotalMs
    const startMs = off(frames[0]?.at) ?? 0
    const rate = Math.max(0.05, playbackRate)
    const startWall = performance.now()
    const id = window.setInterval(() => {
      const advanceMs = (performance.now() - startWall) * rate
      if (advanceMs >= total) {
        const endElapsed = startMs + total
        setVisibilityFocusIndex(visibilityPreviewIndexAtElapsed(frames, atOff(endElapsed)))
        setVisPreviewTotalMs(null)
        setVisPreviewObjectId(null)
        setInspectPreviewMs(null)
        return
      }
      setInspectPreviewMs(startMs + advanceMs)
    }, 50)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visPreviewTotalMs, visPreviewEpoch, visPreviewObjectId, playbackRate])

  useEffect(() => {
    if (radiusPreviewTotalMs == null || !radiusPreviewObjectId) return
    const frames = sortNumberFrames(
      viewSession.objects.find((o) => o.id === radiusPreviewObjectId)?.radiusKeyframes ?? []
    )
    if (frames.length < 2) {
      setRadiusPreviewTotalMs(null)
      setRadiusPreviewStartMs(null)
      setRadiusPreviewObjectId(null)
      setInspectPreviewMs(null)
      return
    }
    const total = radiusPreviewTotalMs
    const startMs = radiusPreviewStartMs ?? off(frames[0]?.at) ?? 0
    const rate = Math.max(0.05, playbackRate)
    const startWall = performance.now()
    const id = window.setInterval(() => {
      const advanceMs = (performance.now() - startWall) * rate
      if (advanceMs >= total) {
        const endElapsed = startMs + total
        setRadiusFocusIndex(numberPreviewIndexAtElapsed(frames, atOff(endElapsed)))
        setRadiusPreviewTotalMs(null)
        setRadiusPreviewStartMs(null)
        setRadiusPreviewObjectId(null)
        setInspectPreviewMs(null)
        return
      }
      setInspectPreviewMs(startMs + advanceMs)
    }, 50)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    radiusPreviewTotalMs,
    radiusPreviewStartMs,
    radiusPreviewEpoch,
    radiusPreviewObjectId,
    playbackRate,
  ])

  useEffect(() => {
    if (windPreviewTotalMs == null || windPreviewStartMs == null) return
    const total = windPreviewTotalMs
    const startMs = windPreviewStartMs
    const rate = Math.max(0.05, playbackRate)
    const startWall = performance.now()
    let cancelled = false
    const id = window.setInterval(() => {
      if (cancelled) return
      const advanceMs = (performance.now() - startWall) * rate
      if (advanceMs >= total) {
        cancelled = true
        const endMs = startMs + total
        setWindPreviewTotalMs(null)
        setWindPreviewStartMs(null)
        setInspectPreviewMs(null)
        elapsedRef.current = endMs
        setElapsedMs(endMs)
        setWindAuthorMs(endMs)
        jumpTo(endMs)
        return
      }
      const ms = startMs + advanceMs
      setInspectPreviewMs(ms)
      setWindAuthorMs(ms)
    }, 50)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windPreviewTotalMs, windPreviewStartMs, windPreviewEpoch, playbackRate])

  useEffect(() => {
    if (!tornadoPreviewFocus) {
      tornadoPreviewSawRunRef.current = false
      return
    }
    if (running) {
      tornadoPreviewSawRunRef.current = true
      return
    }
    if (!tornadoPreviewSawRunRef.current) return
    tornadoPreviewSawRunRef.current = false
    setTornadoPreviewFocus(false)
  }, [running, tornadoPreviewFocus])

  useEffect(() => {
    if (!running || seriesPreviewActive) return
    // Template Preview is this local ticker. A live run on another board must
    // not cancel it; server clock updates also leave `running` alone while a
    // template is open.
    // A live server run follows the server anchor only. The local ticker races
    // ahead on Resume until that anchor arrives, then snaps back.
    if (!previewTemplateId) {
      const clock = clocksRef.current.get(instanceIdRef.current)
      // Done tape review plays locally (Clock blocked on Completed). Live Reviewing
      // never takes a local ticker — hub Clock only (same machine as Preview), even
      // if clocksRef is briefly empty (do not invent a peer/host free-run).
      const doneLocalTape =
        Boolean(tapeReviewRef.current) && selectedIsCompletedRef.current
      const liveReviewingTape =
        Boolean(tapeReviewRef.current) && !selectedIsCompletedRef.current
      if (liveReviewingTape) return
      if (
        !doneLocalTape &&
        clock &&
        (clock.lifecycle === 'running' ||
          clock.clockStatus === 'paused' ||
          clock.clockStatus === 'running')
      ) {
        return
      }
    }
    if (
      serverClock?.clockStatus === 'running' &&
      !previewTemplateId &&
      !(tapeReviewRef.current && selectedIsCompletedRef.current)
    )
      return
    localPlayAnchorRef.current = {
      wall: performance.now(),
      elapsed: elapsedRef.current,
      rate: Math.max(PLAYBACK_RATE_MIN, playbackRate),
    }
    const id = window.setInterval(() => {
      const a = localPlayAnchorRef.current
      const next = a.elapsed + (performance.now() - a.wall) * a.rate
      const tapeCap =
        tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
          ? tapeAvailableUntilMsRef.current
          : null
      const spanCap =
        tapeCap != null ? tapeCap : displayDur > 0 ? displayDur : null
      const capped = spanCap == null ? next : Math.min(next, spanCap)
      elapsedRef.current = capped
      const rounded = Math.round(capped)
      setElapsedMs((prev) => (Math.abs(prev - rounded) < 40 ? prev : rounded))
    }, 250)
    return () => window.clearInterval(id)
  }, [
    running,
    clockEpoch,
    playbackRate,
    displayDur,
    seriesPreviewActive,
    started,
    isReview,
    serverClock,
    previewTemplateId,
  ])

  // Planned Preview / Ready pre–Run 1 / finished-run Reviewing: never keep Instant
  // Replay freeze. A leftover pauseMs made hub Seek clamp past the freeze → no-op
  // Clock (looked like TX death). Stale engaged during Reviewing stole Space.
  // Do not persist the clear on Planned / pre–Run 1 — that SetSessionFields races
  // PreviewPlay and room chat, then broadcasts a stale Paused Clock to the room.
  useEffect(() => {
    const readyPreRun1 = inLiveRun && takeNumber === 0
    const plannedPreview = !inLiveRun && selectedIsPlanned
    const finishedRunReview = tapeReviewActive || exerciseRunStopped
    if (!readyPreRun1 && !plannedPreview && !finishedRunReview) return
    if (
      instantReplayEngaged ||
      instantReplayPauseMs != null ||
      viewSession.instantReplayEngaged === true ||
      viewSession.instantReplayPauseMs != null ||
      viewSession.instantReplayPlaying === true
    ) {
      clearInstantReplayPause(
        finishedRunReview && canOperateSession ? { persist: true } : undefined
      )
    }
  }, [
    inLiveRun,
    takeNumber,
    selectedIsPlanned,
    tapeReviewActive,
    exerciseRunStopped,
    instantReplayEngaged,
    instantReplayPauseMs,
    viewSession.instantReplayEngaged,
    viewSession.instantReplayPauseMs,
    viewSession.instantReplayPlaying,
    canOperateSession,
  ])

  // Peer lag: tapeReviewRunId can land before exerciseRuns[] has the finished run, so
  // adoptTapeReviewRunId no-ops and strip says Reviewing without a local tape overlay
  // (banner used to require tapeReview). Retry when runs catch up.
  // Do NOT adopt during Start countdown / preparing — that re-armed Run N−1 tape over
  // the new Run (IR banner + Reviewing · Run N−1 while strip said Running · Run N).
  useEffect(() => {
    if (liveTakeActive) return
    if (runCountdown != null || runCountdownDriverRef.current) return
    if (runStartGateRef.current === 'preparing') return
    if (boardActivity === 'ready' || boardActivity === 'running') return
    const rid =
      typeof viewSession.tapeReviewRunId === 'string' && viewSession.tapeReviewRunId.trim()
        ? viewSession.tapeReviewRunId.trim()
        : null
    if (!rid) return
    if (tapeReview?.runId === rid) return
    adoptTapeReviewRunId(rid)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt is stable enough; re-run on runs/id
  }, [
    liveTakeActive,
    runCountdown,
    boardActivity,
    viewSession.tapeReviewRunId,
    viewSession.exerciseRuns,
    tapeReview?.runId,
  ])

  // Keep elapsedRef hot every frame while Running; strip/React state stays 4 Hz
  // and skips when the head moved less than 40 ms. MapHost samples elapsedRef.
  useEffect(() => {
    let raf = 0
    let lastStateSync = 0
    const tick = (now: number): void => {
      let anyClockRunning = false
      clocksRef.current.forEach((clock) => {
        if (clock.clockStatus === 'running') anyClockRunning = true
      })
      roomClockRunningRef.current = anyClockRunning
      if (!anyClockRunning && !runningRef.current) {
        raf = window.requestAnimationFrame(tick)
        return
      }
      // Instant Replay owns the local head — don't let the server-anchor ticker yank it.
      if (instantReplayEngagedRef.current) {
        raf = window.requestAnimationFrame(tick)
        return
      }
      // Reviewing · Paused after StopRun — hold the stop park; ignore stale Running RAF.
      if (
        exerciseRunStoppedRef.current &&
        !runningRef.current &&
        !awaitingStartRunRunningRef.current
      ) {
        if (now < reviewingStopPinUntilRef.current) {
          const stopMs =
            finishedRunStopMsRef.current ?? tapeAvailableUntilMsRef.current
          if (stopMs != null && stopMs > 250) elapsedRef.current = stopMs
        }
        raf = window.requestAnimationFrame(tick)
        return
      }
      if (!previewTemplateIdRef.current) {
        // Local series Preview owns the strip — don't also write hub Clock elapsed.
        if (seriesPreviewActiveRef.current) {
          raf = window.requestAnimationFrame(tick)
          return
        }
        const clock = clocksRef.current.get(instanceIdRef.current)
        const start = snapshotRef.current.session.storyStartTime
        if (clock?.clockStatus === 'running' && start) {
          const dur = displayDurationMs(snapshotRef.current.session)
          const tapeCap =
            tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
              ? tapeAvailableUntilMsRef.current
              : null
          const spanCap =
            tapeCap != null ? (dur > 0 ? Math.min(dur, tapeCap) : tapeCap) : dur
          let capped: number
          if (hubSeekOwnsHeadRef.current) {
            if (!runningRef.current) {
              raf = window.requestAnimationFrame(tick)
              return
            }
            // Seek+play owns the head until the running Clock handoff. Do not advance
            // on hubAdmittedRunning alone — pump await flips that before Clock apply,
            // and (now - click) × 20× would false-lead then snap back to the hub head.
            const a = localPlayAnchorRef.current
            capped = spanCap > 0 ? Math.min(a.elapsed, spanCap) : a.elapsed
            elapsedRef.current = capped
            // Frozen await PreviewPlay — skip livePulse. Pulsing re-rendered the tree
            // ~10Hz while MapHost also wrote the same pose every RAF → faint ±1px jitter.
            raf = window.requestAnimationFrame(tick)
            return
          } else if (
            performance.now() < localPreviewDriveUntilRef.current &&
            Math.abs(
              Math.max(0, storyDeltaMs(start, boardPlayheadIso(clock, start))) -
                elapsedRef.current,
            ) > 250
          ) {
            raf = window.requestAnimationFrame(tick)
            return
          } else {
            const nextElapsed = Math.max(0, storyDeltaMs(start, boardPlayheadIso(clock, start)))
            capped = spanCap > 0 ? Math.min(nextElapsed, spanCap) : nextElapsed
          }
          elapsedRef.current = capped
          // Do not write React elapsedMs every frame (or even 4 Hz). A commit of
          // this page can take hundreds of ms; stacking setElapsedMs before the
          // last commit finished is the max-update-depth error on a running clock.
          // Strip faces update once a second. Story-end still keys off elapsedMs,
          // so cross the end in this ticker once.
          const atEnd = spanCap > 0 && capped >= spanCap - 80
          if (atEnd) {
            if (now - lastStateSync >= 250) {
              lastStateSync = now
              const endMs = Math.floor(spanCap)
              setElapsedMs((prev) => (Math.abs(prev - endMs) < 1 ? prev : endMs))
            }
          } else if (now - lastStateSync >= 1000) {
            lastStateSync = now
            const rounded = Math.round(capped)
            startTransition(() => {
              setElapsedMs((prev) => (Math.abs(prev - rounded) < 40 ? prev : rounded))
            })
          }
        }
      }
      raf = window.requestAnimationFrame(tick)
    }
    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [])

  // Live wall-time (since Go Live) — personal Live bin, Invited-live, or Oversight-live.
  const liveRailTickCount =
    paneRunning.length +
    paneInvited.filter((i) => i.lifecycle === 'running').length +
    paneOversight.filter((i) => i.lifecycle === 'running').length
  useEffect(() => {
    if (liveRailTickCount === 0) return
    const id = window.setInterval(() => setLivePulse((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [liveRailTickCount])

  /**
   * Instant Replay tape play — operator advances local head + coalesced hub Seeks.
   * Spectators interpolate from the last Clock head (no Seek) so the bar keeps
   * moving between sparse mid-tape Seeks.
   */
  useEffect(() => {
    if (!instantReplayEngaged || !replayPlaying) return
    // Tape ends at the pause point (look-back only), not the full story duration.
    const tapeEnd =
      instantReplayPauseMsRef.current != null
        ? instantReplayPauseMsRef.current
        : displayDur > 0
          ? displayDur
          : 0
    if (tapeEnd <= 0) return
    const isOperator = canOperateSessionRef.current
    // Already at tape end — stop once (Seek stamps playing=false).
    const flushTapeEnd = (): void => {
      setReplayPlaying(false)
      if (!isOperator) return
      const id = instanceIdRef.current
      if (!id || previewTemplateIdRef.current) return
      // Drop in-flight mid-tape Seeks so a late plain Seek cannot yank the head
      // back from the freeze (clock backtrack + banner flicker).
      elapsedRef.current = tapeEnd
      setElapsedMs(tapeEnd)
      lastSentHubSeekMsRef.current = tapeEnd
      hubSeekOwnsHeadRef.current = true
      localReplayDriveUntilRef.current = performance.now() + 900
      const gen = ++irTapePlayingIntentGenRef.current
      irTapePlayingIntentRef.current = { playing: false, gen }
      // Serial lane pauseTape — must not race mid-tape progress Seeks.
      sendIrTapeControlSeek(tapeEnd, true, false, gen)
    }
    if (elapsedRef.current >= tapeEnd - 10) {
      flushTapeEnd()
      return
    }
    if (!isOperator) {
      spectatorTapeAnchorRef.current = {
        wall: performance.now(),
        elapsed: elapsedRef.current,
      }
    }
    const startWall = performance.now()
    const startElapsed = elapsedRef.current
    let stopped = false
    const id = window.setInterval(() => {
      if (stopped) return
      // Space sets the ref before React re-renders — stop ticks immediately.
      if (!replayPlayingRef.current || !instantReplayEngagedRef.current) {
        stopped = true
        window.clearInterval(id)
        return
      }
      const rate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
      let next: number
      if (isOperator) {
        next = Math.min(tapeEnd, startElapsed + (performance.now() - startWall) * rate)
        localReplayDriveUntilRef.current = performance.now() + 400
      } else {
        const a = spectatorTapeAnchorRef.current
        next = Math.min(tapeEnd, a.elapsed + (performance.now() - a.wall) * rate)
      }
      elapsedRef.current = next
      setElapsedMs(next)
      const now = performance.now()
      if (next >= tapeEnd - 10) {
        stopped = true
        window.clearInterval(id)
        clearScrubSeekTimer()
        flushTapeEnd()
        return
      }
      if (isOperator && now - replaySeekWallRef.current > 280) {
        replaySeekWallRef.current = now
        seekInstantReplay(next, { engageReplay: true, skipLocal: true })
      }
    }, 50)
    return () => {
      stopped = true
      window.clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- IR tape loop; seeks via refs
  }, [instantReplayEngaged, replayPlaying, displayDur, replayPlayEpoch])

  /**
   * Live Reviewing: one authoritative Pause at the stop stamp. Cancels in-flight
   * ResumeAt/Seek desires and bumps deckPlayGen so a late Play cannot restart the hub.
   * (Play + →→ onto the tape-cap mark used to ResumeAt(stop) then race this Pause —
   * P stuck at stop while A kept seeking, often back to T0.)
   */
  function stopLiveTapeReviewAtEnd(reason: string): void {
    const tapeCap = tapeAvailableUntilMsRef.current
    if (tapeCap == null || tapeCap <= 250) return
    if (!tapeReviewRef.current || selectedIsCompletedRef.current) {
      elapsedRef.current = tapeCap
      setElapsedMs(tapeCap)
      beatNavAnchorMsRef.current = tapeCap
      runningRef.current = false
      setRunning(false)
      return
    }
    if (
      !runningRef.current &&
      Math.abs(elapsedRef.current - tapeCap) <= 40 &&
      !hubAdmittedRunningRef.current
    ) {
      return
    }
    elapsedRef.current = tapeCap
    setElapsedMs(tapeCap)
    beatNavAnchorMsRef.current = tapeCap
    runningRef.current = false
    setRunning(false)
    deckPlayGenRef.current += 1
    cancelStudioClockDesire(reason)
    hubAdmittedRunningRef.current = false
    if (transportPlayHoldIdRef.current) transportPlayHoldIdRef.current = null
    const id = instanceIdRef.current
    if (!canOperateSessionRef.current || !id) return
    const start = snapshotRef.current.session.storyStartTime
    const iso = addStoryMs(start, tapeCap)
    hubSeekOwnsHeadRef.current = true
    localPreviewDriveUntilRef.current = performance.now() + 900
    lastSentHubSeekMsRef.current = tapeCap
    hubWirePlayheadMsRef.current = tapeCap
    setHubWirePlayheadMs(tapeCap)
    void hubPause(id, iso, newClientTrace('tapeEnd'))
      .catch(() => undefined)
      .finally(() => {
        window.setTimeout(() => {
          releaseHubSeekOwnsHeadUnlessPlayPending()
        }, 800)
      })
  }

  /** Tape review: pause at the run’s stop playhead (bar stays full with blackout past it). */
  useEffect(() => {
    if (!tapeReviewActive || tapeAvailableUntilMs == null) return
    if (!running) return
    // Dragging to the stop point must not enqueue Pause(end) — that fights Seek(0).
    if (timeBarScrubbingRef.current) return
    // Prefer ref — React elapsedMs can still be a prior stop while a new Play/→ paints.
    const head = elapsedRef.current
    if (head < tapeAvailableUntilMs - 40) return
    // In-flight beat ResumeAt to an earlier head — do not Pause@end on stale state.
    const desire = studioClockDesireRef.current
    if (
      desire?.running &&
      desire.seekMs != null &&
      desire.seekMs < tapeAvailableUntilMs - 40
    ) {
      return
    }
    stopLiveTapeReviewAtEnd('tape-end-effect')
  }, [
    tapeReviewActive,
    tapeAvailableUntilMs,
    running,
    elapsedMs,
    canOperateSession,
  ])

  /** One-shot guard so story-end Pause + toast cannot re-enter while Clock echoes thrash. */
  const storyEndLatchRef = useRef<string | null>(null)

  /**
   * Board edit → strip "Editing" (Live idle, Planned preview, between takes).
   * Cleared when playback/scrub resumes Previewing / Reviewing.
   * Mid-take never flips (strip stays Running / Instant Replay / Paused).
   */
  function noteBoardEditActivity(): void {
    if (liveTakeActiveRef.current) return
    if (previewTemplateIdRef.current) return
    if (!typeOffersSharedRun(snapshotRef.current.session.sessionType)) return
    // Editing the live board leaves tape review (working board is not the tape view).
    if (tapeReviewRef.current) exitTapeReview({ persist: true })
    setWatchingRunId(null)
    setBoardActivity('editing')
  }

  function persistTapeReviewRunId(runId: string | null): void {
    const id = instanceIdRef.current
    if (!id) return
    if (!canOperateSessionRef.current && !canDriveDoneReviewRef.current) return
    // Update local SessionJson immediately. Fields used to be hub-only — the
    // adopt-tape effect then saw stale tapeReviewRunId and re-armed the previous
    // run (chip pick looked like a no-op; modal just closed).
    const nextId = runId == null || runId === '' ? null : runId.trim() || null
    commitSessionFields((prev) => {
      const cur =
        typeof prev.tapeReviewRunId === 'string' && prev.tapeReviewRunId.trim()
          ? prev.tapeReviewRunId.trim()
          : null
      if (cur === nextId) return prev
      const next: ScenarioSession = { ...prev }
      if (nextId == null) delete next.tapeReviewRunId
      else next.tapeReviewRunId = nextId
      return next
    })
    void hubSetSessionFields(id, { tapeReviewRunId: nextId }).catch(() => {})
  }

  function exitTapeReview(opts?: { persist?: boolean }): void {
    const hadOverlay = Boolean(tapeReviewRef.current)
    const hadSharedId = Boolean(snapshotRef.current.session.tapeReviewRunId)
    if (!hadOverlay && !hadSharedId) return
    setTapeReview(null)
    // Drop local SessionJson id immediately so the heal-adopt effect cannot re-arm
    // Reviewing while persist Fields is still in flight (Start Run countdown).
    if (hadSharedId) {
      commitSessionFields((prev) => {
        if (!prev.tapeReviewRunId) return prev
        const next: ScenarioSession = { ...prev }
        delete next.tapeReviewRunId
        return next
      })
    }
    if (opts?.persist !== false) persistTapeReviewRunId(null)
  }

  /**
   * Load tape overlay for a run (operator enter or peer Fields/Snapshot).
   * Content only until startPlay: Live → hub ResumeAt; Done → local ticker.
   */
  function loadTapeReviewOverlay(
    run: ExerciseRun,
    opts?: { startPlay?: boolean }
  ): void {
    const boardId = instanceIdRef.current
    if (!boardId || !runHasTapeSeed(run)) return
    // Arm Reviewing chrome immediately — waiting on ActionsJson fetch left
    // tapeReview null so Space took live-idle Resume (strip said Reviewing, transport Idle).
    setTapeReview((prev) =>
      prev?.runId === run.id ? prev : { runId: run.id, actions: prev?.actions ?? [] }
    )
    setWatchingRunId(run.id)
    noteBoardReviewActivity()
    setTimeBarAutoHidden(false)
    setTimeBarPinned(true)
    const finishEnter = (actions: ScenarioAction[]): void => {
      if (instanceIdRef.current !== boardId) return
      const slice = actionsInRunSlice(actions, run.actionsSeqStart, run.actionsSeqEnd)
      setTapeReview({ runId: run.id, actions: slice })
      setWatchingRunId(run.id)
      noteBoardReviewActivity()
      setTimeBarAutoHidden(false)
      setTimeBarPinned(true)
      setHoverIds([])
      setHoverElapsedMs(null)
      // Legacy / raced Stop: derive stop stamp from the slice so blackout works even on
      // Done (hub Clock / operate may be blocked). Persist when we can still write Fields.
      if (run.stoppedStoryElapsedMs == null || !Number.isFinite(run.stoppedStoryElapsedMs)) {
        const derived = resolveTapeAvailableUntilMs(
          null,
          snapshotRef.current.session.storyStartTime,
          slice
        )
        if (derived != null) {
          let healedSession: ScenarioSession | null = null
          commitSessionFields((prev) => {
            const runs = (prev.exerciseRuns ?? []).map((r) =>
              r.id === run.id ? { ...r, stoppedStoryElapsedMs: derived } : r
            )
            healedSession = { ...prev, exerciseRuns: runs }
            return healedSession
          })
          if (
            healedSession &&
            (canOperateSessionRef.current || canDriveDoneReviewRef.current)
          ) {
            void persistExerciseBoardFields(healedSession, { omitBoardVersions: true }).catch(
              () => {}
            )
          }
        }
      }
      if (opts?.startPlay) {
        if (canOperateSessionRef.current && !selectedIsCompletedRef.current) {
          const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
          if (pauseHoldIdRef.current === boardId) pauseHoldIdRef.current = null
          // Always ResumeAt(0) — bare Resume after Stop left chrome Playing with a frozen bar.
          enqueueStudioClock({
            running: true,
            seekMs: 0,
            playAs: 'resume',
            rate: stripRate,
          })
        } else if (selectedIsCompletedRef.current) {
          // Done: hub Clock is blocked — local tape play.
          const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
          localPlayAnchorRef.current = {
            wall: performance.now(),
            elapsed: 0,
            rate: stripRate,
          }
          elapsedRef.current = 0
          setElapsedMs(0)
          setRunning(true)
          setClockEpoch((n) => n + 1)
        }
      }
    }
    const cached = instancesRef.current.find((i) => i.id === boardId)?.actions
    if (cached?.length) {
      finishEnter(cached)
      return
    }
    void fetchScenarioInstance(boardId, user)
      .then((snap) => {
        if (instanceIdRef.current !== boardId) return
        const actions = snap.actions ?? []
        setInstances((prev) =>
          prev.map((row) => (row.id === boardId ? { ...row, actions } : row))
        )
        finishEnter(actions)
      })
      .catch(() => {
        finishEnter([])
      })
  }

  function adoptTapeReviewRunId(runId: string | null | undefined): void {
    if (runId == null || runId === '') {
      // Shared “I’m done reviewing” (tapeReviewRunId cleared) — leave Reviewing for
      // the whole room. watchingRunId / boardActivity are local and used to stick.
      if (tapeReviewRef.current) setTapeReview(null)
      setWatchingRunId(null)
      if (boardActivityRef.current === 'watching') setBoardActivity('preview')
      if (postRunOfferOpenRef.current) setPostRunOfferOpen(false)
      return
    }
    if (tapeReviewRef.current?.runId === runId) return
    const run = (snapshotRef.current.session.exerciseRuns ?? []).find((r) => r.id === runId)
    if (!run || !runHasTapeSeed(run)) return
    loadTapeReviewOverlay(run)
  }

  /**
   * Between-takes: enter tape review (IR-style banner) over the last finished run’s
   * start seed + ActionsJson slice, then start story-time transport. Legacy runs
   * without a seed fall back to ordinary story preview.
   */
  function enterTapeReview(): void {
    if (liveTakeActiveRef.current) return
    const sess = snapshotRef.current.session
    const vid = sess.selectedVersionId
    const run =
      (watchingRunId
        ? (sess.exerciseRuns ?? []).find(
            (r) => r.id === watchingRunId && (!vid || r.versionId === vid)
          )
        : null) ??
      (sess.exerciseRuns ?? [])
        .filter(
          (r) =>
            (!vid || r.versionId === vid) &&
            (r.status === 'completed' || r.status === 'abandoned')
        )
        .reduce<ExerciseRun | null>((best, r) => (!best || r.number > best.number ? r : best), null)
    if (!run || !runHasTapeSeed(run)) {
      toggleClock()
      return
    }
    const boardId = instanceIdRef.current
    if (!boardId) {
      toggleClock()
      return
    }
    // Live between-takes: operators start shared tape. Done: anyone can play locally.
    if (!canOperateSessionRef.current && !selectedIsCompletedRef.current) {
      toggleClock()
      return
    }
    if (canOperateSessionRef.current || canDriveDoneReviewRef.current) {
      persistTapeReviewRunId(run.id)
    }
    loadTapeReviewOverlay(run, { startPlay: true })
  }
  enterTapeReviewRef.current = enterTapeReview

  /** Playback / scrub of a finished take — strip back to Reviewing · #N. */
  function noteBoardReviewActivity(): void {
    if (liveTakeActiveRef.current) return
    if (
      !selectedIsCompletedRef.current &&
      !exerciseRunStoppedRef.current &&
      (takeNumberRef.current ?? 0) <= 0
    ) {
      return
    }
    setBoardActivity('watching')
  }

  /**
   * Finished-run Reviewing: hub may still deliver a *Running* Clock parked on the
   * stop beat (in-flight take ticker / Pause race). That must not paint Instant Replay
   * (playing) with nothing advancing.
   *
   * Invariant (phase + playhead — not a wall-clock mute):
   * - While exerciseTakeStopped, a Running Clock whose head is still on the stop
   *   park is stale → keep local paused / do not admit hub running.
   * - Running at ~story start is never “stale stop” (that is StartRun / new take).
   * - Unknown stop stamp → do not reject (rejecting all Running caused Paused · Run N
   *   at Start when takeStopped Fields lagged the Running Clock).
   * - Cleared when the next take starts (exerciseTakeStopped → false).
   */
  function staleRunningAfterTakeStop(
    clockElapsedMs: number,
    instanceKey: string
  ): boolean {
    if (!exerciseRunStoppedRef.current) return false
    if (awaitingStartRunRunningRef.current) return false
    if (transportPlayHoldIdRef.current === instanceKey) return false
    if (studioClockDesireRef.current?.running === true) return false
    // StartRun / countdown head — not the finished-run stop park.
    if (clockElapsedMs <= 400) return false
    const stopMs =
      finishedRunStopMsRef.current ??
      (tapeAvailableUntilMsRef.current != null && tapeAvailableUntilMsRef.current > 250
        ? tapeAvailableUntilMsRef.current
        : null)
    if (stopMs == null) return false
    return Math.abs(clockElapsedMs - stopMs) <= 400
  }

  /**
   * Was: block Seek(~0) while local sat on the finished-run stop park.
   * Retired — Reviewing cues at T0 and ← from the stop must rewind; that guard
   * made left-arrow dead after play/right-arrow to the end until Space unwedged.
   * Countdown / StartRun still use awaitingStartRunRunningRef + runStartGate.
   */
  function staleStoryStartYankAfterTakeStop(_clockElapsedMs: number): boolean {
    return false
  }

  /** Remember stop stamp for tape blackout + Reviewing park (Space wraps to T0). */
  function rememberFinishedRunStopMs(run: ExerciseRun): void {
    const dur = displayDurRef.current
    if (
      typeof run.stoppedStoryElapsedMs === 'number' &&
      Number.isFinite(run.stoppedStoryElapsedMs) &&
      run.stoppedStoryElapsedMs > 250
    ) {
      finishedRunStopMsRef.current = Math.max(0, Math.floor(run.stoppedStoryElapsedMs))
      return
    }
    // Peer StopRun may have already stamped the patch ms — do not replace with full dur
    // (that hid the recorded-window blackout on P).
    if (finishedRunStopMsRef.current != null && finishedRunStopMsRef.current > 250) return
    finishedRunStopMsRef.current = Math.max(
      0,
      dur > 0 ? dur : Math.floor(elapsedRef.current)
    )
  }

  /**
   * Reviewing · Paused at the stop stamp after StopRun / run-chip pick. Stamps clocksRef
   * paused there so Running RAF cannot advance past the park before Pause Clock lands.
   * Arms a short peer pin against late Running Clocks. Space wraps to T0 to replay.
   * Pass share:true on operator chip pick so peers Seek to the same park (Fields alone
   * only switched tape id / blackout — playhead lagged until Play).
   */
  function pinReviewingPausedAtStop(opts?: {
    pinMs?: number
    stopMs?: number
    share?: boolean
  }): void {
    const stopMs = Math.max(
      0,
      Math.floor(
        opts?.stopMs ??
          finishedRunStopMsRef.current ??
          tapeAvailableUntilMsRef.current ??
          elapsedRef.current
      )
    )
    if (stopMs > 250) finishedRunStopMsRef.current = stopMs
    // Peer-only latch — operators scrub/Seek Reviewing freely.
    if (!canOperateSessionRef.current) {
      const pinMs = opts?.pinMs ?? 2500
      reviewingStopPinUntilRef.current = performance.now() + pinMs
    }
    elapsedRef.current = stopMs
    setElapsedMs(stopMs)
    beatNavAnchorMsRef.current = stopMs
    hubWirePlayheadMsRef.current = stopMs
    setHubWirePlayheadMs(stopMs)
    runningRef.current = false
    setRunning(false)
    hubAdmittedRunningRef.current = false
    const openId = instanceIdRef.current
    const start = snapshotRef.current.session.storyStartTime
    if (openId && start) {
      const prev = clocksRef.current.get(openId)
      if (prev) {
        const parkIso = addStoryMs(start, stopMs)
        const pinned: StudioClock = {
          ...prev,
          clockStatus: 'paused',
          anchorStoryTimeIso: parkIso,
          anchorActualTime: null,
        }
        clocksRef.current.set(openId, pinned)
        serverClockRef.current = pinned
        setServerClock(pinned)
        setInstances((rows) =>
          rows.map((row) =>
            row.id === openId
              ? {
                  ...row,
                  clockStatus: 'paused',
                  studioClock: pinned,
                  playheadStoryIso: parkIso,
                }
              : row
          )
        )
      }
    }
    if (
      opts?.share &&
      canOperateSessionRef.current &&
      !selectedIsCompletedRef.current &&
      openId
    ) {
      // Sync the tape cap *before* Seek — forceAdmitDeckScrubSeek clamps to
      // tapeAvailableUntilMsRef, which still holds the previous run until React
      // re-renders (2→14 Seek was clamped to Run 2's stop → hub no-op; A local
      // at Run 14 end, P stuck at the old head).
      if (stopMs > 250) tapeAvailableUntilMsRef.current = stopMs
      forceAdmitDeckScrubSeek(stopMs)
    }
  }

  /**
   * Cue finished-run Reviewing at story start (T0) — Space wrap / explicit replay from
   * start. Stop/story-end parks at the stop stamp instead (pinReviewingPausedAtStop).
   */
  function cueFinishedRunTapeAtStart(): void {
    reviewingStopPinUntilRef.current = 0
    setHoverElapsedMs(null)
    runningRef.current = false
    setRunning(false)
    setTimeBarAutoHidden(false)
    setTimeBarPinned(true)
    if (selectedIsCompletedRef.current) {
      elapsedRef.current = 0
      setElapsedMs(0)
      return
    }
    if (canOperateSessionRef.current || canDrivePreviewRef.current) {
      void seekStoryStartBetweenRuns()
      return
    }
    elapsedRef.current = 0
    setElapsedMs(0)
  }

  /**
   * Between-takes Reviewing overlay for a finished run (paused unless startPlay).
   * Overlay = seed + ActionsJson content only — does not invent a local playhead.
   * Live startPlay goes through hub ResumeAt; Done startPlay is local (Clock blocked).
   * Stop/story-end parks at the stop stamp; pass cueAtStart to wrap to T0 for replay.
   */
  function beginFinishedRunTapeReview(
    run: ExerciseRun | null | undefined,
    opts?: { startPlay?: boolean; persistTapeId?: boolean; cueAtStart?: boolean }
  ): void {
    if (!run || !runHasTapeSeed(run)) return
    // Drop mid-take IR chrome — Reviewing uses hub Resume/Pause, not IR tape Seeks.
    if (
      instantReplayEngagedRef.current ||
      replayPlayingRef.current ||
      instantReplayPauseMsRef.current != null
    ) {
      clearInstantReplayPause(
        canOperateSessionRef.current ? { persist: true } : undefined
      )
    }
    setWatchingRunId(run.id)
    setBoardActivity('watching')
    if (opts?.persistTapeId !== false) persistTapeReviewRunId(run.id)
    rememberFinishedRunStopMs(run)
    loadTapeReviewOverlay(run, { startPlay: Boolean(opts?.startPlay) })
    // Explicit replay-from-start only (Space wrap). Stop parks at stop via pin/hub.
    if (!opts?.startPlay && opts?.cueAtStart === true) {
      cueFinishedRunTapeAtStart()
    }
  }

  /**
   * Peer (or late Fields): take just stopped → Reviewing chrome + watch the finished run.
   */
  function adoptPeerTakeFinished(_takeN: number): void {
    // Official take is over — Reviewing · Paused at the stop stamp.
    runningRef.current = false
    setRunning(false)
    setBoardActivity('watching')
    const session = snapshotRef.current.session
    const vid = session.selectedVersionId
    const finished =
      latestExerciseRun(session) ??
      (session.exerciseRuns ?? [])
        .filter(
          (r) =>
            (!vid || r.versionId === vid) &&
            (r.status === 'completed' || r.status === 'abandoned')
        )
        .reduce<ExerciseRun | null>((best, r) => (!best || r.number > best.number ? r : best), null)
    if (
      finished &&
      typeof finished.stoppedStoryElapsedMs === 'number' &&
      Number.isFinite(finished.stoppedStoryElapsedMs)
    ) {
      finishedRunStopMsRef.current = Math.max(0, Math.floor(finished.stoppedStoryElapsedMs))
    } else if (finishedRunStopMsRef.current == null && elapsedRef.current > 250) {
      finishedRunStopMsRef.current = Math.floor(elapsedRef.current)
    }
    // Runs[] may land a tick later — strip still says Reviewing via take-stopped.
    if (finished && runHasTapeSeed(finished)) {
      beginFinishedRunTapeReview(finished)
    } else if (finished) {
      setWatchingRunId(finished.id)
    }
    pinReviewingPausedAtStop()
  }

  /**
   * Operator: story end of an official take → Reviewing (not mid-run Paused).
   * Shared by the story-end effect and Resume-at-end recovery.
   *
   * Hub StopRun Pause @ stop playhead is the room writer; local paint is optimistic.
   * Space wraps to T0 when starting Reviewing playback.
   */
  function finishOfficialTakeAtStoryEnd(displayEndMs: number): void {
    const id = instanceIdRef.current
    if (!id || !canOperateSessionRef.current) return
    clearInstantReplayPause({ persist: true })
    setTimeBarPinned(true)
    setTimeBarAutoHidden(false)
    runStartGateRef.current = 'idle'
    cancelStudioClockDesire('stop-run-story-end')
    const stopMs = Math.max(0, Math.floor(displayEndMs))
    finishedRunStopMsRef.current = stopMs
    // Optimistic Reviewing · Paused at stop (hub StopRun confirms).
    pinReviewingPausedAtStop({ stopMs })
    const start = snapshotRef.current.session.storyStartTime
    const freezeIso = addStoryMs(start, stopMs)
    const stopHolder: { session: ScenarioSession | null } = { session: null }
    commitSession(
      (prevSession) => {
        let next = stopExerciseRun(prevSession, {
          actor: exerciseActor(),
          actionsSeqEnd: clocksRef.current.get(id)?.seq ?? null,
          stoppedStoryElapsedMs: stopMs,
          resetToSeed: false,
        })
        const finished =
          latestExerciseRunForVersion(next) ?? latestExerciseRun(next)
        if (finished && runHasTapeSeed(finished)) {
          next = { ...next, tapeReviewRunId: finished.id }
        }
        stopHolder.session = next
        return next
      },
      { persistObjects: false, adoptAsPersisted: true }
    )
    const stoppedSession = stopHolder.session
    const takeN = Math.max(1, stoppedSession?.exerciseTakeNumber ?? takeNumberRef.current)
    applyExerciseTakePhase(takeN, true)
    const stopped = stoppedSession ?? snapshotRef.current.session
    const finishedRun =
      latestExerciseRunForVersion(stopped) ?? latestExerciseRun(stopped)
    if (finishedRun && runHasTapeSeed(finishedRun)) {
      beginFinishedRunTapeReview(finishedRun, { persistTapeId: false })
    } else {
      if (finishedRun) setWatchingRunId(finishedRun.id)
      setBoardActivity('watching')
    }
    const localN = versionLocalRunNumberFor(stopped, finishedRun) || takeN
    pushInfoToast(`Run ${localN} finished.`, false, 3, 3600)
    void hubStopRun(id, { storyPlayheadIso: freezeIso })
      .then(() => {
        pinReviewingPausedAtStop({ stopMs })
        runningRef.current = false
        setRunning(false)
      })
      .catch((err) => {
        setServerNotice(studioActionMessage(err, 'Could not save the finished run.'))
      })
  }

  useEffect(() => {
    if (displayDur <= 0 || seriesPreviewActive) return
    // Clear latch only when the playhead leaves the end (restart / scrub back).
    // Keep the latch while takeStopped — cueing Reviewing @ T0 must not re-arm finish.
    if (elapsedMs < displayDur - 80) {
      if (!exerciseRunStoppedRef.current) storyEndLatchRef.current = null
      return
    }
    if (!running) return
    // Scrubbing to 100% then back to 0 must not fire story-end Pause(end).
    if (timeBarScrubbingRef.current) return
    // Start Run gate: `preparing` = countdown / Seek-before-Resume — never finish.
    // Do NOT require `armed`: reload / missed arm left gate `idle` and treated a real
    // Run as Preview (toast "Preview complete", Paused at end, Resume restarted from 0).
    // See TODO “Atomic StartRun” — this is client sequencing, not a wall-clock mute.
    if (runStartGateRef.current === 'preparing') return
    // Template preview: local clock only — pause + toast (not a session operator).
    const templatePreviewEnd = Boolean(previewTemplateIdRef.current)
    // Spectators: cap the head at story end only while the take is still open.
    // Do NOT finish the take locally — StopRun is SoT. Once takeStopped, never
    // re-clamp to displayDur (that left P parked at end while A/StopRun were @ T0).
    if (!canOperateSessionRef.current && !templatePreviewEnd) {
      if (exerciseRunStoppedRef.current || tapeReviewRef.current) return
      if (elapsedRef.current < displayDur) {
        elapsedRef.current = displayDur
        setElapsedMs(displayDur)
      }
      return
    }
    const id = instanceIdRef.current
    const latchKey = `${id ?? 'local'}:${displayDur}:${takeNumber}:${exerciseRunStopped ? 1 : 0}`
    if (storyEndLatchRef.current === latchKey) return
    storyEndLatchRef.current = latchKey

    // Official take reached the story end — finish the take (Reviewing), not mid-run Paused.
    // Between-takes / pre–Run 1 story preview only pauses the clock.
    const officialTake =
      takeNumber > 0 &&
      !exerciseRunStopped &&
      inLiveRun &&
      typeOffersSharedRun(viewSession.sessionType) &&
      !previewTemplateIdRef.current
    if (templatePreviewEnd) {
      setElapsedMs(displayDur)
      elapsedRef.current = displayDur
      setRunning(false)
      pushInfoToast('Preview complete.', false, 2, 2800)
      return
    }
    if (!id) return
    if (officialTake) {
      finishOfficialTakeAtStoryEnd(displayDur)
      return
    }
    // Already Reviewing / between-takes: tape-end effect owns pause @ stop.
    // Do not pump Seek(displayDur) — that fought T0 cue and re-drove a playthrough.
    if (exerciseRunStopped || tapeReviewRef.current) {
      runningRef.current = false
      setRunning(false)
      return
    }
    // Use the transport pause path (hold + enqueue) so Running Clock echoes cannot
    // resurrect Preview and bounce the camera between first/last beats.
    setElapsedMs(displayDur)
    elapsedRef.current = displayDur
    setRunning(false)
    enqueueStudioClock({ running: false, seekMs: displayDur })
    // Pre–Run 1 / Planned keep the preview toast.
    if (takeNumber > 0) return
    pushInfoToast('Preview complete.', false, 2, 2800)
  }, [
    running,
    displayDur,
    elapsedMs,
    seriesPreviewActive,
    takeNumber,
    exerciseRunStopped,
    inLiveRun,
    viewSession.sessionType,
  ])

  function showKeyToast(token: string, label: string, hold = false): void {
    if (keysOpenRef.current) return
    if (keyToastTimerRef.current != null) {
      window.clearTimeout(keyToastTimerRef.current)
      keyToastTimerRef.current = null
    }
    setKeyToast({ token, label })
    if (hold) return
    keyToastTimerRef.current = window.setTimeout(() => {
      keyToastTimerRef.current = null
      setKeyToast(null)
    }, 1600)
  }

  /** Latest hotkey actions — keydown effect deps are sparse; avoid stale P→Plumes routing. */
  const hotkeyActionsRef = useRef({
    togglePlumes: (): void => {},
    toggleRunRoomPlumes: (): void => {},
    togglePalette: (): void => {},
    toggleCapture: (): void => {},
    toggleScribble: (): void => {},
    eraseScribble: (): void => {},
    toggleShare: (): void => {},
  })
  hotkeyActionsRef.current.togglePlumes = () => {
    setSurfacePlumes((prev) => {
      const next = !prev
      try {
        localStorage.setItem('buxus.sc.surfacePlumes', next ? '1' : '0')
      } catch {
        /* private mode */
      }
      showKeyToast('W', next ? 'Wind plumes on' : 'Wind plumes off')
      return next
    })
  }
  hotkeyActionsRef.current.toggleRunRoomPlumes = () => {
    if (!liveTakeActiveRef.current || !canOperateSessionRef.current) return
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    const next = snapshotRef.current.session.runRoomPlumes !== true
    const by = editWhoNameRef.current.trim() || myEmailRef.current || 'Someone'
    // Socket write in this turn, before the toast and the plume map render.
    void hubSendSessionFieldsNow(id, { runRoomPlumes: next, runRoomPlumesBy: by }).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not update wind plumes for this run.'))
    })
    snapshotRef.current = {
      ...snapshotRef.current,
      session: { ...snapshotRef.current.session, runRoomPlumes: next, runRoomPlumesBy: by },
    }
    commitSessionFields((prev) => ({ ...prev, runRoomPlumes: next, runRoomPlumesBy: by }))
    showKeyToast('R', next ? 'Show wind plumes on' : 'Show wind plumes off')
  }
  hotkeyActionsRef.current.togglePalette = () => {
    if (!showBoardChromeRef.current) return
    paletteUserTouchedRef.current = true
    setPaletteOpen((prev) => {
      const next = !prev
      showKeyToast('P', next ? 'Palette shown' : 'Palette hidden')
      return next
    })
  }
  /** Kermit mode — silent show/hide (no toast; broadcast-safe). */
  const toggleScribbleMode = (): void => {
    setScribbleMode((prev) => {
      const next = !prev
      scribbleModeRef.current = next
      if (next) {
        setPlaceType(null)
        setRulerStats(null)
      }
      return next
    })
  }
  hotkeyActionsRef.current.toggleScribble = toggleScribbleMode
  scribbleHotkeyRef.current = toggleScribbleMode
  hotkeyActionsRef.current.eraseScribble = () => {
    if (!scribbleModeRef.current) return
    setScribbleStrokes([])
  }
  hotkeyActionsRef.current.toggleShare = () => {
    if (!canLeadWatchRef.current && !watchOnRef.current) return
    setWatchOn((prev) => {
      const next = !prev
      watchOnRef.current = next
      const id = instanceIdRef.current
      const cam = mapApiRef.current?.getCamera()
      if (!next && id) {
        if (presenterEmailRef.current === myEmailRef.current && cam) {
          void hubSetWatchFrame(id, {
            active: true,
            lng: cam.lng,
            lat: cam.lat,
            zoom: cam.zoom,
            who: editWhoRef.current || undefined,
            whoName: editWhoNameRef.current || undefined,
          }).catch(() => {})
        } else {
          void hubSetWatchFrame(id, { active: false }).catch(() => {})
        }
      }
      if (next && id && cam) {
        void hubSetWatchFrame(id, {
          active: true,
          ...cam,
          pointerLng: cam.lng,
          pointerLat: cam.lat,
          cursor: 'grab',
          who: editWhoRef.current || undefined,
          whoName: editWhoNameRef.current || undefined,
        }).catch(() => {})
      }
      showKeyToast('S', next ? 'Sharing pointer' : 'Pointer sharing off')
      return next
    })
  }

  function clearKeyToast(): void {
    if (keyToastTimerRef.current != null) {
      window.clearTimeout(keyToastTimerRef.current)
      keyToastTimerRef.current = null
    }
    setKeyToast(null)
  }

  useEffect(() => {
    const typing = (): boolean => {
      const el = document.activeElement
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
    }
    /** Space still types in text fields. Sliders, cards, and buttons do not keep it. */
    const textEntry = (): boolean => {
      const el = document.activeElement
      if (!(el instanceof HTMLElement)) return false
      if (el.isContentEditable) return true
      if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
      if (!(el instanceof HTMLInputElement)) return false
      const kind = el.type
      return (
        kind !== 'range' &&
        kind !== 'checkbox' &&
        kind !== 'radio' &&
        kind !== 'button' &&
        kind !== 'submit' &&
        kind !== 'reset' &&
        kind !== 'file' &&
        kind !== 'color'
      )
    }
    const down = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && helpOpenRef.current) {
        setHelpOpen(false)
        return
      }
      if (e.key === 'Escape' && keysOpenRef.current) {
        setKeysOpen(false)
        return
      }
      if (
        e.key === 'Escape' &&
        (mapAssetDetailsOpenRef.current || mapAssetOpenRef.current)
      ) {
        e.preventDefault()
        e.stopPropagation()
        mapAssetDismissRef.current()
        return
      }
      if (e.key === 'Escape' && scribbleModeRef.current && !typing()) {
        setScribbleMode(false)
        return
      }
      if (e.key === 'Escape' && focusModeRef.current && !typing()) {
        setFocusMode(false)
        return
      }
      if (hubActionsLockedRef.current) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return
      if (e.key === ' ' || e.code === 'Space') {
        if (textEntry() || accountBoardMode === 'following') return
        e.preventDefault()
        e.stopPropagation()
        const el = document.activeElement
        if (el instanceof HTMLElement && el !== document.body) el.blur()
        if (liveRunSpaceRef.current) {
          // Finished-run tape review (Reviewing · #N): hub Resume/Pause via toggleClock.
          // Do not route to IR tape play — stale engaged flags freeze the bar.
          if (tapeReviewRef.current) {
            if (canOperateSessionRef.current || selectedIsCompletedRef.current) {
              toggleClockRef.current()
            }
            return
          }
          if (instantReplayEngagedRef.current) {
            if (canOperateSessionRef.current) toggleReplayPlayingRef.current()
            return
          }
          // Mid-take: Pause/Resume the run. Live-idle: story preview only (never Start Run).
          if (liveTakeActiveRef.current) {
            if (canOperateSessionRef.current) liveTransportToggleRef.current()
            return
          }
          if (canOperateSessionRef.current || previewSpaceRef.current) {
            // Between takes: Space previews the live board. Tape / Instant Replay
            // is entered only via a run chip (or explicit Review), not by Play.
            toggleClockRef.current()
          }
          return
        }
        if (!previewSpaceRef.current) return
        toggleClockRef.current()
        return
      }
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !textEntry()) {
        // IR tape play keeps canSeek false in some edge states — still allow beat skips.
        // Use textEntry (not typing): Public/Published checkboxes are <input> and used to
        // swallow ←/→ after Settings toggles even though arrows do nothing useful there.
        if (
          !canSeekTimeBarRef.current &&
          !(instantReplayEngagedRef.current && canOperateSessionRef.current)
        )
          return
        e.preventDefault()
        e.stopPropagation()
        const el = document.activeElement
        if (el instanceof HTMLElement && el !== document.body) el.blur()
        skipBeatRef.current(e.key === 'ArrowRight' ? 1 : -1)
        return
      }
      if (e.key === '?' || (e.code === 'Slash' && e.shiftKey)) {
        if (typing()) return
        e.preventDefault()
        e.stopPropagation()
        setHelpOpen(false)
        setKeysOpen((open) => {
          if (!open) setKeyToast(null)
          return !open
        })
        return
      }
      if ((e.key === 'c' || e.key === 'C') && canCaptureScreenRef.current) {
        if (typing()) return
        e.preventDefault()
        hotkeyActionsRef.current.toggleCapture()
        return
      }
      if (!typing() && (e.key === 'd' || e.key === 'D') && canViewDebugRef.current) {
        e.preventDefault()
        setDebugPanelsOn((prev) => {
          const next = !prev
          showKeyToast('D', next ? 'Developer panels on' : 'Developer panels off')
          return next
        })
        return
      }
      if (!typing() && (e.code === 'KeyW' || e.key === 'w' || e.key === 'W')) {
        e.preventDefault()
        hotkeyActionsRef.current.togglePlumes()
        return
      }
      if (
        !typing() &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        (e.code === 'KeyR' || e.key === 'r' || e.key === 'R')
      ) {
        if (!liveTakeActiveRef.current || !canOperateSessionRef.current) return
        e.preventDefault()
        hotkeyActionsRef.current.toggleRunRoomPlumes()
        return
      }
      if (!typing() && (e.key === 't' || e.key === 'T')) {
        e.preventDefault()
        setCameraFollowOn((prev) => {
          const next = !prev
          if (next) {
            setCamEditHoldAtMs(null)
            setCameraFollowApplyEpoch((n) => n + 1)
          }
          showKeyToast('T', next ? 'Auto-track on' : 'Auto-track off')
          return next
        })
        return
      }
      if (!typing() && accountBoardMode !== 'following' && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault()
        setBoardRevealMode((prev) => {
          const ids = BOARD_REVEAL_OPTIONS.map((o) => o.id)
          const next = ids[(ids.indexOf(prev) + 1) % ids.length] ?? ids[0]!
          const label = BOARD_REVEAL_OPTIONS.find((o) => o.id === next)?.label ?? 'board'
          showKeyToast('V', `Viewing ${label}`)
          return next
        })
        return
      }
      if (!typing() && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault()
        setFocusMode((prev) => {
          const next = !prev
          showKeyToast('F', next ? 'Focus on' : 'Focus off')
          return next
        })
        return
      }
      // K = Kermit mode — show/hide personal scribble layer. Silent (no toast).
      if (!typing() && (e.code === 'KeyK' || e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        e.stopPropagation()
        scribbleHotkeyRef.current()
        return
      }
      // E = erase all scribbles while layer is shown. Silent.
      if (
        !typing() &&
        (e.code === 'KeyE' || e.key === 'e' || e.key === 'E') &&
        scribbleModeRef.current
      ) {
        e.preventDefault()
        e.stopPropagation()
        hotkeyActionsRef.current.eraseScribble()
        return
      }
      // P = Palette (W = Plumes / wind). Routed via ref so sparse effect deps can't stale-bind P→Plumes.
      if (!typing() && (e.code === 'KeyP' || e.key === 'p' || e.key === 'P')) {
        e.preventDefault()
        hotkeyActionsRef.current.togglePalette()
        return
      }
      // Delete / Backspace = narration beat (Timeline edit), else object, else session card.
      // Always swallow when a session/object is selected so the browser doesn't navigate Back
      // (that flashed a green focus ring around Done card tools).
      if (!typing() && (e.key === 'Delete' || e.key === 'Backspace')) {
        const narrationId = narrationDeleteFocusRef.current
        if (narrationId) {
          e.preventDefault()
          e.stopPropagation()
          removeNarrationBeatRef.current(narrationId)
          return
        }
        const canObj = Boolean(selectedIdRef.current && canMutateSelectedRef.current)
        // Session delete only while a session card is the thing on screen.
        // An open board is not a selected session — Backspace must not hit Settings' Delete.
        const sessionCardsOpen =
          dockRef.current === 'scenarios' && !libraryModeRef.current
        const canSess = Boolean(
          sessionCardsOpen &&
            selectedSessionIdRef.current &&
            canDeleteSelectedSessionRef.current
        )
        if (canObj || canSess) {
          e.preventDefault()
          e.stopPropagation()
          requestDeleteSelectedRef.current()
          return
        }
        if (selectedSessionIdRef.current || selectedIdRef.current) {
          e.preventDefault()
          e.stopPropagation()
          const ae = document.activeElement
          if (ae instanceof HTMLElement && ae.closest('.sc-inst-tools')) ae.blur()
        }
        return
      }
      // S = Share pointer — before the participant gate so editors/trainers with run/edit
      // can always toggle; does not require Presenter (shelf badge is the self reminder).
      if (!typing() && (e.key === 's' || e.key === 'S')) {
        if (!canLeadWatchRef.current && !watchOnRef.current) return
        e.preventDefault()
        hotkeyActionsRef.current.toggleShare()
        return
      }
      if (typing() || accountBoardMode === 'following') return
      const key = e.key.toLowerCase()
      if (key !== 'g' && key !== 'z') return
      if (key === 'g' && !ghostsOfferedRef.current) return
      e.preventDefault()
      if (key === 'g') {
        setGhostFlash(true)
        showKeyToast('G', 'Show ghosts', true)
      } else {
        setZoomFlash(true)
        showKeyToast('Z', 'Zoom out', true)
      }
    }
    const up = (e: KeyboardEvent): void => {
      const key = e.key.toLowerCase()
      if (key === 'g') {
        setGhostFlash(false)
        clearKeyToast()
      } else if (key === 'z') {
        setZoomFlash(false)
        clearKeyToast()
      }
    }
    const blur = (): void => {
      setGhostFlash(false)
      setZoomFlash(false)
      setKeysOpen(false)
      clearKeyToast()
    }
    window.addEventListener('keydown', down, true)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down, true)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
    }
  }, [accountBoardMode])

  useEffect(() => {
    const zoom = watchFrame?.zoom
    // Zoom cue while following a Presenter (silent override) or Auto-track story.
    const me = user.email.trim().toLowerCase()
    const followingPresenter = Boolean(watchFrame && presenterEmail && presenterEmail !== me)
    if (zoom == null || (!followingPresenter && !cameraFollowOn)) {
      watchZoomPrevRef.current = zoom ?? null
      setWatchZoomCue(null)
      return
    }
    const prev = watchZoomPrevRef.current
    watchZoomPrevRef.current = zoom
    if (prev == null || Math.abs(zoom - prev) < 0.02) return
    const dir = zoom > prev ? 'in' : 'out'
    setWatchZoomCue((cur) => ({ dir, n: (cur?.n ?? 0) + 1 }))
    const timer = window.setTimeout(() => setWatchZoomCue(null), 700)
    return () => window.clearTimeout(timer)
  }, [watchFrame?.zoom, watchOn, cameraFollowOn, watchFrame, presenterEmail, user.email])

  /**
   * Done review: Presenter / Share pointer publishes story playhead so followers’
   * thumbs and scene follow (hub clock is blocked on completed boards).
   */
  useEffect(() => {
    if (!selectedIsCompleted || !instanceId) return
    const me = user.email.trim().toLowerCase()
    const driving = watchOn || presenterEmail === me
    if (!driving) return
    const cam = mapApiRef.current?.getCamera()
    if (!cam) return
    const pointer = watchOn
      ? {
          pointerLng: localSharePointerRef.current?.lng ?? cam.lng,
          pointerLat: localSharePointerRef.current?.lat ?? cam.lat,
          pointerDown: localSharePointerRef.current?.down,
          cursor: (localSharePointerRef.current?.cursor ?? 'grab') as
            | 'pointer'
            | 'grab'
            | 'grabbing',
        }
      : {}
    void hubSetWatchFrame(instanceId, {
      active: true,
      lng: cam.lng,
      lat: cam.lat,
      zoom: cam.zoom,
      ...pointer,
      who: editWhoRef.current || undefined,
      whoName: editWhoNameRef.current || undefined,
      storyElapsedMs: elapsedRef.current,
      storyRunning: runningRef.current,
    }).catch(() => {})
  }, [
    selectedIsCompleted,
    instanceId,
    watchOn,
    presenterEmail,
    user.email,
    elapsedMs,
    running,
  ])

  /**
   * Follow Presenter story playhead on Done only (hub clock is blocked there).
   * Planned Preview must never take playhead from Watch — Share (S) used to attach
   * storyElapsedMs/storyRunning and yank P off Clock the instant Sharing started
   * (seeks/play worked until S; hand still moved).
   */
  useEffect(() => {
    if (!followingPresenter || !selectedIsCompleted) return
    const ms = watchFrame?.storyElapsedMs
    if (ms == null || !Number.isFinite(ms)) return
    const cap = displayDur > 0 ? Math.min(Math.max(0, ms), displayDur) : Math.max(0, ms)
    const wantRun = Boolean(watchFrame?.storyRunning)
    if (Math.abs(elapsedRef.current - cap) < 40 && runningRef.current === wantRun) return
    const rate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
    localPlayAnchorRef.current = { wall: performance.now(), elapsed: cap, rate }
    beatNavAnchorMsRef.current = cap
    elapsedRef.current = cap
    setElapsedMs(cap)
    setHoverElapsedMs(null)
    setRunning(wantRun)
    if (wantRun) setClockEpoch((n) => n + 1)
  }, [
    followingPresenter,
    selectedIsCompleted,
    watchFrame?.storyElapsedMs,
    watchFrame?.storyRunning,
    displayDur,
  ])

  // Auto-track / timeline hover drives the map via programmatic ease (skipped by onCameraFrame).
  // Publish those frames while sharing so followers see the same view without a click.
  // Skip when we are following someone else's Presenter (don't echo their camera as ours).
  useEffect(() => {
    if (!watchOn || !directedCamera || directedCamera.softHold) return
    if (previewTemplateIdRef.current) return
    const me = myEmailRef.current
    const presenter = presenterEmailRef.current
    if (presenter && presenter !== me) return
    const id = instanceIdRef.current
    if (!id) return
    const { lng, lat, zoom } = directedCamera
    const timer = window.setTimeout(() => {
      void hubSetWatchFrame(id, {
        active: true,
        lng,
        lat,
        zoom,
        pointerLng: lng,
        pointerLat: lat,
        cursor: 'grab',
        who: editWhoRef.current || undefined,
        whoName: editWhoNameRef.current || undefined,
      }).catch(() => {})
    }, 80)
    return () => window.clearTimeout(timer)
  }, [watchOn, directedCamera?.beatKey, directedCamera?.softHold])

  useEffect(() => {
    const holding = ghostFlash || zoomFlash
    if (!holding) {
      const cam = holdCamRef.current
      holdCamRef.current = null
      if (cam) mapApiRef.current?.easeToCamera(cam, 280)
      return
    }
    if (holdCamRef.current) return
    const cam = mapApiRef.current?.getCamera() ?? null
    const pts = mapResolved.objects.flatMap((obj) => {
      if (zoomFlash && !obj.ghost) {
        const anchor = objectAnchorLngLat(obj)
        return anchor ? [anchor] : []
      }
      if (ghostFlash && obj.ghost && obj.pose) return [obj.pose]
      return []
    })
    const moved = zoomFlash
      ? (mapApiRef.current?.fitPoints(pts) ?? false)
      : (mapApiRef.current?.expandToInclude(pts) ?? false)
    if (moved && cam) holdCamRef.current = cam
  }, [ghostFlash, zoomFlash, mapResolved])

  useEffect(() => {
    if (videoExportStatus !== 'recording') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      void stopVideoCapture()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [videoExportStatus])

  // Clear dismiss-peek when the invite is answered or the board changes (reload shows the card again).
  useEffect(() => {
    if (!myInvitePending || !instanceId) {
      setInviteJoinPeekId(null)
      return
    }
    if (inviteJoinPeekId && inviteJoinPeekId !== instanceId) {
      setInviteJoinPeekId(null)
    }
  }, [myInvitePending, instanceId, inviteJoinPeekId])

  // Pending invitee: Auto-track on once so they see live editing / Share pointer (FOMO; they can turn it off).
  const inviteAutoTrackAppliedRef = useRef(false)
  useEffect(() => {
    if (!myInvitePending || !instanceId) {
      inviteAutoTrackAppliedRef.current = false
      return
    }
    if (inviteAutoTrackAppliedRef.current) return
    inviteAutoTrackAppliedRef.current = true
    setCamEditHoldAtMs(null)
    setCameraFollowApplyEpoch((n) => n + 1)
    setCameraFollowOn(true)
  }, [myInvitePending, instanceId])

  useEffect(() => {
    if (!serverBooting) return
    dismissAllStudioModals()
  }, [serverBooting])

  useEffect(() => {
    if (serverBooting || bootError) return
    if (!accessPerms.has('scenarios.create')) return
    if (hasSeenGetStarted() || getStartedAutoPromptedRef.current) return
    // Deep-linked board or pending invite join — don't cover Join / the session they landed on.
    const pendingInvite = instances.some((inst) => {
      const row = inst.people.find((p) => !p.isDeleted && sameLoginEmail(p.email, user.email))
      return Boolean(row && row.status === 'pending' && row.hasBeenInvited !== false)
    })
    if (instanceId || previewTemplateId || pendingInvite) {
      getStartedAutoPromptedRef.current = true
      return
    }
    openGetStartedWizard('auto')
  }, [serverBooting, bootError, accessPerms, instanceId, previewTemplateId, instances, user.email])

  // Empty Sessions hub: open the wizard instead of the map empty card. Once per empty episode
  // so Cancel/Not now does not immediately re-open; resets when any session returns.
  useEffect(() => {
    if (serverBooting || bootError) return
    if (!accessPerms.has('scenarios.create')) return
    if (!hubNothingSelected || libraryMode || !paneIsEmpty) {
      if (!paneIsEmpty) emptyHubWizardPromptedRef.current = false
      return
    }
    if (getStartedOpen || studioModalOpen) return
    const pendingInvite = instances.some((inst) => {
      const row = inst.people.find((p) => !p.isDeleted && sameLoginEmail(p.email, user.email))
      return Boolean(row && row.status === 'pending' && row.hasBeenInvited !== false)
    })
    if (pendingInvite) return
    if (emptyHubWizardPromptedRef.current) return
    emptyHubWizardPromptedRef.current = true
    openGetStartedWizard('auto')
  }, [
    serverBooting,
    bootError,
    accessPerms,
    hubNothingSelected,
    libraryMode,
    paneIsEmpty,
    getStartedOpen,
    studioModalOpen,
    instances,
    user.email,
  ])

  const selectedPeopleKey = selectedPeopleIds.slice().sort().join(',')
  useEffect(() => {
    setInviteIntro('')
    setInviteAttachFiles([])
    setIncludeInviteDocs(false)
    setInviteDocChoice('all')
  }, [selectedPeopleKey])

  /** Templates are browse/settings only — no Inspect spine tab (even when the seed has objects). */
  const templateInspectOffered = !viewingTemplate

  useEffect(() => {
    if (!serverListReadyRef.current) return
    writeServerInstanceIds(instances.map((i) => i.id))
  }, [instances])

  function flushInstance(list: ScenarioInstance[]): ScenarioInstance[] {
    const snap = snapshotRef.current
    const id = instanceIdRef.current
    return list.map((i) => {
      if (i.id !== id) return i
      let lifecycle = i.lifecycle
      if (snap.stopped) lifecycle = 'completed'
      // Never promote Planned → Live from strip UI alone (leaked clocks used to stick via this).
      else if (i.lifecycle === 'planned') lifecycle = 'planned'
      else if (snap.started && i.lifecycle !== 'completed') lifecycle = 'running'
      return {
        ...i,
        session: snap.session,
        people: snap.people,
        playheadStoryIso: addStoryMs(snap.session.storyStartTime, snap.elapsedMs),
        actualStartTime:
          i.lifecycle === 'planned'
            ? i.actualStartTime
            : (snap.actualStartTime ?? i.actualStartTime),
        actualEndTime: snap.stopped
          ? (i.actualEndTime ?? new Date().toISOString())
          : i.actualEndTime,
        lessons: snap.stopped ? (i.lessons ?? defaultLessons(snap.session)) : i.lessons,
        lifecycle,
      }
    })
  }

  /** Write the live session back into the previewed template row (template write mode). */
  function flushTemplates(list: readonly ScenarioTemplate[]): ScenarioTemplate[] {
    const id = previewTemplateIdRef.current
    if (!id) return [...list]
    const snap = snapshotRef.current
    return list.map((t) =>
      t.id === id
        ? {
            ...t,
            session: snap.session,
            title: snap.session.title,
            summary: snap.session.summary,
            updatedAtIso: new Date().toISOString(),
          }
        : t
    )
  }

  /** Remember the open session id so a reload lands back on it. */
  function persistInstancesNow(): void {
    const id = instanceIdRef.current
    if (!id) return
    try {
      localStorage.setItem(LAST_SERVER_INSTANCE_KEY, id)
    } catch {
      /* private mode */
    }
  }

  useEffect(() => {
    // Debounce: drag fires moveObject every frame; coalesce writes until input settles.
    const t = window.setTimeout(() => persistInstancesNow(), 250)
    return () => window.clearTimeout(t)
  }, [
    session,
    people,
    started,
    running,
    actualStartTime,
    stopped,
    remembered,
    instanceId,
    previewTemplateId,
    templateEditLive,
  ])

  // Hard reload / tab close cancels the debounce above — flush synchronously so Planned edits stick.
  const instancesRef = useRef(instances)
  instancesRef.current = instances
  const templatesRef = useRef(templates)
  templatesRef.current = templates
  const rememberedRef = useRef(remembered)
  rememberedRef.current = remembered
  const previewTemplateIdRef = useRef(previewTemplateId)
  previewTemplateIdRef.current = previewTemplateId
  const showJoinOfferCardRef = useRef(false)
  showJoinOfferCardRef.current = showJoinOfferCard
  const templateEditLiveRef = useRef(templateEditLive)
  templateEditLiveRef.current = templateEditLive
  useEffect(() => {
    const flushBoardObjects = (): void => {
      flushVersionFieldsNow()
      void flushObjectWrites()
    }
    window.addEventListener('pagehide', flushBoardObjects)
    return () => {
      window.removeEventListener('pagehide', flushBoardObjects)
    }
  }, [])

  function rememberPersistedObjects(objects: readonly ScenarioObject[]): void {
    const next = new Map<string, string>()
    for (const object of objects) next.set(object.id, JSON.stringify(object))
    persistedObjectsRef.current = next
  }

  function rememberOnePersistedObject(object: ScenarioObject): void {
    persistedObjectsRef.current.set(object.id, JSON.stringify(object))
    pendingObjectUpsertsRef.current.delete(object.id)
    pendingObjectRemovesRef.current.delete(object.id)
  }

  function mirrorObjectsOnInstance(objects: readonly ScenarioObject[]): void {
    const id = instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((inst) =>
        inst.id === id ? { ...inst, session: { ...inst.session, objects: [...objects] } } : inst
      )
    )
  }

  type PendingObjectFlush = {
    instanceId: string
    removes: string[]
    upserts: [string, ScenarioObject][]
  }

  /** Snapshot + clear pending for a specific board. Cancels the debounce timer. */
  function takePendingObjectFlush(instanceId: string | null | undefined): PendingObjectFlush | null {
    if (objectSaveTimerRef.current != null) {
      window.clearTimeout(objectSaveTimerRef.current)
      objectSaveTimerRef.current = null
    }
    if (!instanceId) {
      pendingObjectRemovesRef.current.clear()
      pendingObjectUpsertsRef.current.clear()
      return null
    }
    const removes = [...pendingObjectRemovesRef.current]
    const upserts = [...pendingObjectUpsertsRef.current.entries()]
    pendingObjectRemovesRef.current.clear()
    pendingObjectUpsertsRef.current.clear()
    if (!removes.length && !upserts.length) return null
    return { instanceId, removes, upserts }
  }

  function enqueueObjectFlush(batch: PendingObjectFlush): Promise<void> {
    const run = objectFlushChainRef.current.then(() => flushCapturedObjectWrites(batch))
    objectFlushChainRef.current = run.then(
      () => undefined,
      () => undefined
    )
    return run
  }

  async function flushObjectWrites(): Promise<void> {
    const batch = takePendingObjectFlush(objectWriteInstanceRef.current)
    if (!batch) return
    await enqueueObjectFlush(batch)
  }

  /**
   * Drain pending writes for the board we are leaving *before* retargeting
   * objectWriteInstanceRef. The old path scheduled an async flush then switched the
   * write pointer, so Upserts from board A landed on board B (and polluted ActionsJson).
   */
  function flushObjectWritesForLeavingBoard(leavingId: string | null | undefined): void {
    if (!leavingId) {
      pendingObjectRemovesRef.current.clear()
      pendingObjectUpsertsRef.current.clear()
      if (objectSaveTimerRef.current != null) {
        window.clearTimeout(objectSaveTimerRef.current)
        objectSaveTimerRef.current = null
      }
      return
    }
    const batch = takePendingObjectFlush(leavingId)
    if (batch) void enqueueObjectFlush(batch)
  }

  async function flushCapturedObjectWrites(batch: PendingObjectFlush): Promise<void> {
    const { instanceId, removes, upserts } = batch
    const inst = instancesRef.current.find((i) => i.id === instanceId)
    // Done boards are read-only — drop any in-flight upserts from End Session races.
    if (inst?.lifecycle === 'completed') return
    const stillThisBoard = (): boolean => objectWriteInstanceRef.current === instanceId
    try {
      for (const objectId of removes) {
        await hubRemoveObject(instanceId, objectId)
        if (stillThisBoard()) persistedObjectsRef.current.delete(objectId)
      }
      for (const [objectId, object] of upserts) {
        await hubUpsertObject(instanceId, object)
        if (stillThisBoard()) persistedObjectsRef.current.set(objectId, JSON.stringify(object))
      }
    } catch (err) {
      if (stillThisBoard()) {
        for (const objectId of removes) {
          if (!pendingObjectUpsertsRef.current.has(objectId))
            pendingObjectRemovesRef.current.add(objectId)
        }
        for (const [objectId, object] of upserts) {
          if (
            !pendingObjectUpsertsRef.current.has(objectId) &&
            !pendingObjectRemovesRef.current.has(objectId)
          ) {
            pendingObjectUpsertsRef.current.set(objectId, object)
          }
        }
      }
      setServerNotice(studioActionMessage(err, 'Could not save board objects.'))
      return
    }
    if (
      stillThisBoard() &&
      (pendingObjectRemovesRef.current.size || pendingObjectUpsertsRef.current.size)
    ) {
      scheduleObjectFlush()
    }
  }

  function scheduleObjectFlush(): void {
    if (objectSaveTimerRef.current != null) return
    const since = performance.now() - objectFlushAtRef.current
    const wait = since >= 120 ? 40 : Math.max(40, 120 - since)
    objectSaveTimerRef.current = window.setTimeout(() => {
      objectSaveTimerRef.current = null
      objectFlushAtRef.current = performance.now()
      void flushObjectWrites()
    }, wait)
  }

  function queueObjectWrites(nextObjects: readonly ScenarioObject[]): void {
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    objectWriteInstanceRef.current = instanceIdRef.current
    mirrorObjectsOnInstance(nextObjects)
    if (!mayPersistObjectsRef.current) return
    const persisted = persistedObjectsRef.current
    const nextIds = new Set(nextObjects.map((object) => object.id))
    for (const id of persisted.keys()) {
      if (nextIds.has(id) || objectMotionsRef.current.has(id)) continue
      pendingObjectRemovesRef.current.add(id)
      pendingObjectUpsertsRef.current.delete(id)
    }
    for (const object of nextObjects) {
      if (objectMotionsRef.current.has(object.id)) continue
      const json = JSON.stringify(object)
      if (persisted.get(object.id) === json) {
        pendingObjectUpsertsRef.current.delete(object.id)
        continue
      }
      pendingObjectRemovesRef.current.delete(object.id)
      pendingObjectUpsertsRef.current.set(object.id, object)
    }
    if (!pendingObjectRemovesRef.current.size && !pendingObjectUpsertsRef.current.size) return
    // Place / move / delete between takes → strip "Editing" (not Reviewing).
    noteBoardEditActivity()
    scheduleObjectFlush()
  }

  function queueCommentWrite(comments: readonly ScenarioComment[]): void {
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    if (!canCommentRef.current) return
    const json = JSON.stringify(comments)
    if (json === persistedCommentsRef.current) return
    pendingCommentsRef.current = [...comments]
    if (commentSaveTimerRef.current != null) window.clearTimeout(commentSaveTimerRef.current)
    commentSaveTimerRef.current = window.setTimeout(() => {
      commentSaveTimerRef.current = null
      const payload = pendingCommentsRef.current
      const id = instanceIdRef.current
      pendingCommentsRef.current = null
      if (!payload || !id) return
      persistedCommentsRef.current = JSON.stringify(payload)
      setInstances((prev) =>
        prev.map((inst) =>
          inst.id === id ? { ...inst, session: { ...inst.session, comments: payload } } : inst
        )
      )
      void hubSetComments(id, payload).catch((err) => {
        persistedCommentsRef.current = ''
        setServerNotice(studioActionMessage(err, 'Could not save comments.'))
      })
    }, 200)
  }

  function flushVersionFieldsNow(): void {
    if (versionFieldsFlushTimerRef.current != null) {
      window.clearTimeout(versionFieldsFlushTimerRef.current)
      versionFieldsFlushTimerRef.current = null
    }
    const board = pendingVersionFieldsRef.current
    const spine = pendingVersionSpineRef.current
    pendingVersionFieldsRef.current = null
    pendingVersionSpineRef.current = false
    if (!board) return
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current || !canOperateSessionRef.current) return
    if (liveTakeActiveRef.current) return
    const hasVersions = (board.boardVersions?.length ?? 0) > 0
    // Seed content only — do not send selectedVersionId (peers applyBoardSeed
    // when that key is present) or take-phase scalars (they race UpsertObject).
    const patch: SessionFieldPatch = {
      ...(hasVersions ? { boardVersions: board.boardVersions } : {}),
      ...(spine
        ? {
            timeline: board.timeline,
            cameraBeats: board.cameraBeats ?? null,
          }
        : {}),
    }
    if (!Object.keys(patch).length) return
    const run = objectFlushChainRef.current.then(() =>
      hubSetSessionFields(id, patch).catch(() => {
        /* local board already updated */
      })
    )
    objectFlushChainRef.current = run.then(
      () => undefined,
      () => undefined
    )
  }

  function scheduleVersionFieldsFlush(session: ScenarioSession, spineChanged: boolean): void {
    pendingVersionFieldsRef.current = session
    if (spineChanged) pendingVersionSpineRef.current = true
    if (versionFieldsFlushTimerRef.current != null) return
    versionFieldsFlushTimerRef.current = window.setTimeout(() => {
      versionFieldsFlushTimerRef.current = null
      flushVersionFieldsNow()
    }, 800)
  }

  function commitSession(
    updater: (prev: ScenarioSession) => ScenarioSession,
    opts?: {
      persistObjects?: boolean
      /**
       * Treat the next session objects/comments as already durable (no Upsert / SetComments).
       * Used after local story rebase — server RebaseStory + Snapshot own persistence.
       * Without this, persistedObjectsRef stays on the pre-rebase JSON and the next
       * queueObjectWrites (e.g. linked-geom heal) storms an Upsert per shifted object.
       */
      adoptAsPersisted?: boolean
    }
  ): void {
    const persistObjects = opts?.persistObjects !== false
    const adoptAsPersisted = opts?.adoptAsPersisted === true
    setSession((prev) => {
      const timelineBefore = prev.timeline
      const camsBefore = prev.cameraBeats
      let next = withSessionSpan(
        healSessionCopy(healSyncedTimeline(updater(prev))),
        elapsedRef.current
      )
      const spineChanged =
        next.timeline !== timelineBefore || next.cameraBeats !== camsBefore
      // Keep the selected version seed aligned with the live board (deletes/edits).
      // Version switch / Start Run reloads from seed — without this, those restore ghosts.
      // Do not SetSessionFields the full boardVersions blob on every object twitch —
      // that raced UpsertObject on the same ConcurrencyStamp and slammed the hub.
      // Skip mid-take so a live run does not rewrite the plan under itself.
      if (persistObjects && !liveTakeActiveRef.current && !seriesPreviewActiveRef.current) {
        const hasVersions = (next.boardVersions?.length ?? 0) > 0
        if (hasVersions && !selectedVersionSeedMatches(next)) {
          next = updateSelectedVersionSeed(next, exerciseActor())
          scheduleVersionFieldsFlush(next, spineChanged)
        } else if (spineChanged) {
          scheduleVersionFieldsFlush(next, true)
        }
      }
      queueMicrotask(() => {
        narrateRolePlay(prev.objects, next.objects, actorPhrase())
        if (persistObjects && !seriesPreviewActiveRef.current) {
          queueObjectWrites(next.objects)
          queueCommentWrite(next.comments)
        } else {
          // Map drag: keep the open-board catalog warm without Upsert storms mid-gesture.
          mirrorObjectsOnInstance(next.objects)
          if (adoptAsPersisted) {
            if (objectSaveTimerRef.current != null) {
              window.clearTimeout(objectSaveTimerRef.current)
              objectSaveTimerRef.current = null
            }
            pendingObjectUpsertsRef.current.clear()
            pendingObjectRemovesRef.current.clear()
            rememberPersistedObjects(next.objects)
            pendingCommentsRef.current = null
            persistedCommentsRef.current = JSON.stringify(next.comments ?? [])
          }
        }
      })
      return next
    })
  }

  /**
   * Scalar Settings / Board echo — never flush UpsertObject (hub SetSessionFields owns save).
   * Skips healSessionCopy when objects/timeline refs are unchanged so Settings toggles
   * stay snappy on large boards (heal walks every object).
   */
  function commitSessionFields(
    updater: (prev: ScenarioSession) => ScenarioSession,
    opts?: { adoptAsPersisted?: boolean }
  ): void {
    const adoptAsPersisted = opts?.adoptAsPersisted === true
    setSession((prev) => {
      const updated = updater(prev)
      if (updated === prev) return prev
      const needsHeal =
        updated.objects !== prev.objects ||
        updated.timeline !== prev.timeline ||
        updated.boardVersions !== prev.boardVersions
      const next = needsHeal
        ? withSessionSpan(healSessionCopy(healSyncedTimeline(updated)), elapsedRef.current)
        : withSessionSpan(updated, elapsedRef.current)
      queueMicrotask(() => {
        mirrorObjectsOnInstance(next.objects)
        if (adoptAsPersisted) {
          if (objectSaveTimerRef.current != null) {
            window.clearTimeout(objectSaveTimerRef.current)
            objectSaveTimerRef.current = null
          }
          pendingObjectUpsertsRef.current.clear()
          pendingObjectRemovesRef.current.clear()
          rememberPersistedObjects(next.objects)
          pendingCommentsRef.current = null
          persistedCommentsRef.current = JSON.stringify(next.comments ?? [])
        }
      })
      return next
    })
  }

  // Heal linked geometries shifted by older drag logic — re-resolve from loaded Mapbox tiles.
  // Retries on map idle until tiles yield geometry (first paint often races tile load).
  // Do not re-run on selectedSource identity (every persist used to retrigger this).
  useEffect(() => {
    if (runningRef.current || seriesPreviewActiveRef.current) return
    const oid = selectedId
    if (!oid) return
    const source = snapshotRef.current.session.objects.find((o) => o.id === oid)
    if (!source || source.type === 'road' || source.type === 'sketch') return
    const api = mapApiRef.current
    if (!api) return
    const assets = readAttachedAssets(source.params)
    const assetSig = assets.map((a) => assetKey(a)).sort().join('|')
    const doneKey = `${oid}:${assetSig}`
    if (!assets.length) {
      linkedGeomRefreshIdRef.current = doneKey
      return
    }
    if (linkedGeomRefreshIdRef.current === doneKey) return
    let changed = false
    let resolvedAny = false
    const next = assets.map((a) => {
      const fresh = api.resolveAttachedGeometry(a.kind, a.properties)
      if (!fresh?.segments.length) return a
      resolvedAny = true
      const before = JSON.stringify(a.segments ?? a.path ?? null)
      const after = JSON.stringify(fresh.segments)
      if (before === after) return a
      changed = true
      return { ...a, path: fresh.path, segments: fresh.segments }
    })
    if (!resolvedAny) return
    // Also strip legacy top-level path/segments copied onto point icons.
    const hadOwnGeom =
      (source.path != null && source.path.length >= 2) ||
      (Array.isArray(source.params.segments) && source.params.segments.length > 0)
    if (!changed && !hadOwnGeom) {
      linkedGeomRefreshIdRef.current = doneKey
      return
    }
    linkedGeomRefreshIdRef.current = doneKey
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== oid) return o
        let patched = withRoadGeometryFromAssets(o, next)
        // Point icons must not keep board path/segments — those paint amber strokes and drift.
        if (patched.type !== 'road' && patched.type !== 'sketch') {
          const params = { ...patched.params }
          delete params.segments
          patched = { ...patched, params }
          delete patched.path
        }
        return patched
      }),
    }))
  }, [selectedId, mapIdleEpoch])

  /** Apply hub/session take phase onto React state + open instance row. */
  function applyExerciseTakePhase(nextTake: number, stopped: boolean): void {
    const n = Math.max(0, Math.floor(nextTake))
    takePhaseHoldRef.current = { take: n, stopped }
    // Sync refs before any same-tick Clock playhead apply (setState alone is too late).
    takeNumberRef.current = n
    exerciseRunStoppedRef.current = stopped
    setTakeNumber(n)
    setExerciseRunStopped(stopped)
    if (!stopped) finishedRunStopMsRef.current = null
    const id = instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
              ...i,
              session: {
                ...i.session,
                exerciseTakeNumber: n,
                exerciseTakeStopped: stopped,
              },
            }
          : i
      )
    )
  }

  /**
   * Broadcast Exercise take phase so spectators / peers share strip chrome.
   * Returns the hub write promise so Stop can Pause only after SessionJson is updated
   * (Clock piggybacks take phase from SessionJson).
   */
  function persistExerciseTakePhase(nextTake: number, stopped: boolean): Promise<void> {
    const n = Math.max(0, Math.floor(nextTake))
    takePhaseHoldRef.current = { take: n, stopped }
    setTakeNumber(n)
    setExerciseRunStopped(stopped)
    const id = instanceIdRef.current
    if (!id || !canOperateSessionRef.current) return Promise.resolve()
    commitSessionFields((prev) => ({
      ...prev,
      exerciseTakeNumber: n,
      exerciseTakeStopped: stopped,
    }))
    return hubSetSessionFields(id, {
      exerciseTakeNumber: n,
      exerciseTakeStopped: stopped,
    })
      .then(() => undefined)
      .catch(() => {
        /* local chrome already updated */
      })
  }

  function exerciseActor() {
    return {
      userId: user.userId ?? null,
      name: shortPersonName(user.name ?? '') ?? user.email ?? null,
    }
  }

  /**
   * Persist versions / runs / take phase (not gated on sessionType === exercise).
   * `omitBoardVersions`: Start Run / take-phase writes — full boardVersions JSON is huge and
   * was delaying Seek/Resume by seconds (A ran optimistically; P lagged ~5s on the hub clock).
   */
  function persistExerciseBoardFields(
    session: ScenarioSession,
    opts?: {
      clearRunStartCountdown?: boolean
      omitBoardVersions?: boolean
      /** Include in the same Fields write (avoids racing a solo tapeReviewRunId patch). */
      tapeReviewRunId?: string | null
      /** Clear mid-take IR flags in the same write as the finished runs. */
      clearInstantReplay?: boolean
    }
  ): Promise<void> {
    const id = instanceIdRef.current
    if (!id || !canOperateSessionRef.current) return Promise.resolve()
    const patch: SessionFieldPatch = {
      ...(opts?.omitBoardVersions ? {} : { boardVersions: session.boardVersions }),
      selectedVersionId: session.selectedVersionId ?? null,
      exerciseRuns: session.exerciseRuns ?? [],
      exerciseTakeNumber: session.exerciseTakeNumber ?? 0,
      exerciseTakeStopped: Boolean(session.exerciseTakeStopped),
      // Same write as Start take phase — avoids a racing countdown-null Fields wipe.
      ...(opts?.clearRunStartCountdown
        ? { runStartCountdown: null, runStartCountdownEndsAt: null }
        : {}),
      ...(opts && Object.prototype.hasOwnProperty.call(opts, 'tapeReviewRunId')
        ? { tapeReviewRunId: opts.tapeReviewRunId ?? null }
        : {}),
      ...(opts?.clearInstantReplay
        ? {
            instantReplayPauseMs: null,
            instantReplayPlaying: false,
            instantReplayEngaged: false,
          }
        : {}),
    }
    return hubSetSessionFields(id, patch).then(() => undefined)
  }

  function ensureVersionsOnSession(prev: ScenarioSession): ScenarioSession {
    return ensureBoardVersions(prev, exerciseActor())
  }

  /**
   * Seek story clock to the beginning while staying between runs (preview / Watch setup).
   * Owns the head until Seek/Pause settle so a late Clock echo cannot yank the playhead
   * (and Auto-track camera) back to the prior version's last beat mid version-switch.
   */
  function seekStoryStartBetweenRuns(): Promise<void> {
    const id = instanceIdRef.current
    const startIso = snapshotRef.current.session.storyStartTime
    setElapsedMs(0)
    elapsedRef.current = 0
    lastSentHubSeekMsRef.current = 0
    hubWirePlayheadMsRef.current = 0
    setHubWirePlayheadMs(0)
    setHoverElapsedMs(null)
    setTimeBarAutoHidden(false)
    setTimeBarPinned(true)
    setTimeBarEnterKey((n) => n + 1)
    runningRef.current = false
    setRunning(false)
    if (!id) return Promise.resolve()
    const prev = clocksRef.current.get(id)
    if (prev) {
      const stamped: StudioClock = {
        ...prev,
        clockStatus: 'paused',
        anchorStoryTimeIso: startIso,
        anchorActualTime: null,
        storyOriginIso: prev.storyOriginIso || startIso,
      }
      clocksRef.current.set(id, stamped)
      serverClockRef.current = stamped
      setServerClock(stamped)
    }
    // Local park only for spectators — hub Seek would 401 and toast “Could not update the clock.”
    if (!canOperateSessionRef.current && !canDrivePreviewRef.current) {
      hubSeekOwnsHeadRef.current = false
      return Promise.resolve()
    }
    hubSeekOwnsHeadRef.current = true
    // Pause first when still Running — Seek while Running re-anchors play @ T0
    // (story-end Reviewing autoplay disaster).
    const mustPauseFirst = prev?.clockStatus === 'running'
    const afterPause = mustPauseFirst
      ? hubPause(id, startIso, newClientTrace('seekStart.pause')).catch(() => undefined)
      : Promise.resolve()
    return afterPause
      .then(() => hubSeek(id, startIso, false, false, true))
      .then((admitted) => {
        if (admitted !== false) {
          lastSentHubSeekMsRef.current = 0
          hubWirePlayheadMsRef.current = 0
          setHubWirePlayheadMs(0)
        }
        return hubPause(id, startIso)
      })
      .then(() => {
        /* Seek Clock near 0 releases owns-head; keep pin until Pause lands too. */
      })
      .catch(() => {
        /* local head already at start */
      })
      .finally(() => {
        hubSeekOwnsHeadRef.current = false
        runningRef.current = false
        setRunning(false)
      })
  }

  /**
   * After a version flip (or Fields while already reviewing), retarget Review.
   * Does NOT auto-enter Reviewing merely because finished runs exist — that fought
   * “I’m done reviewing” and hard-reload Previewing.
   */
  function syncReviewFocusForVersion(session: ScenarioSession): void {
    if (liveTakeActiveRef.current) return
    // Mid version-switch: operator already painted optimistic Review — do not retarget
    // to Preview from a lean Board/Fields/Snapshot that has not yet settled tape.
    const hold = versionSwitchHoldRef.current
    if (hold && session.selectedVersionId === hold.versionId) return
    const vid = session.selectedVersionId
    if (!vid) {
      if (tapeReviewRef.current) setTapeReview(null)
      setWatchingRunId(null)
      if (boardActivityRef.current === 'watching') setBoardActivity('preview')
      return
    }
    // Stale tape overlay from another version — drop it so the working board can show.
    if (tapeReviewRef.current) {
      const overlayRun = (session.exerciseRuns ?? []).find(
        (r) => r.id === tapeReviewRef.current!.runId
      )
      if (!overlayRun || overlayRun.versionId !== vid) {
        setTapeReview(null)
      }
    }
    const tapeId =
      typeof session.tapeReviewRunId === 'string' && session.tapeReviewRunId.trim()
        ? session.tapeReviewRunId.trim()
        : null
    if (tapeId) {
      const taped = (session.exerciseRuns ?? []).find(
        (r) => r.id === tapeId && r.versionId === vid
      )
      if (taped) {
        setWatchingRunId(taped.id)
        setBoardActivity('watching')
        if (!tapeReviewRef.current || tapeReviewRef.current.runId !== taped.id) {
          if (runHasTapeSeed(taped)) loadTapeReviewOverlay(taped)
        }
        return
      }
    }
    // Already reviewing / on tape — keep a valid watch target for this version.
    const stillReviewing =
      boardActivityRef.current === 'watching' || Boolean(tapeReviewRef.current)
    if (!stillReviewing) {
      // Previewing / Editing / ready — leave watch alone (usually null after I’m done).
      return
    }
    const latestForStory = (session.exerciseRuns ?? [])
      .filter(
        (r) =>
          r.versionId === vid &&
          (r.status === 'completed' || r.status === 'abandoned')
      )
      .reduce<ExerciseRun | null>((best, r) => (!best || r.number > best.number ? r : best), null)
    if (latestForStory) {
      setWatchingRunId(latestForStory.id)
      setBoardActivity('watching')
      if (
        runHasTapeSeed(latestForStory) &&
        tapeReviewRef.current?.runId !== latestForStory.id
      ) {
        loadTapeReviewOverlay(latestForStory)
      }
    } else {
      if (tapeReviewRef.current) setTapeReview(null)
      setWatchingRunId(null)
      setBoardActivity('preview')
    }
  }

  /**
   * Done: hub ApplyBoardSeed is blocked on Completed — switch versions locally.
   * Surface 1 (final board): seed / end-board only. Surface 2 (reviewing): retarget Run 1
   * on the destination version (stable ordinal — not “latest”, which jumped around).
   */
  function switchDoneBoardVersionLocal(want: string): void {
    const reviewing =
      boardActivityRef.current === 'watching' || Boolean(tapeReviewRef.current)
    // Local flip is fast — still show the same Switching veil / strip lock as Live.
    if (peerVersionSwitchClearRef.current != null) {
      window.clearTimeout(peerVersionSwitchClearRef.current)
      peerVersionSwitchClearRef.current = null
    }
    setVersionSwitching(true)
    exitTapeReview({ persist: false })
    const ensured = ensureVersionsOnSession(snapshotRef.current.session)
    const ver = findBoardVersion(ensured.boardVersions, want)
    if (!ver) {
      setVersionSwitching(false)
      setServerNotice('That version was not found.')
      return
    }
    const baseline = doneEndBoardRef.current
    let next: ScenarioSession = { ...ensured, selectedVersionId: want }
    if (baseline && baseline.versionId === want) {
      next = {
        ...next,
        objects: structuredClone(baseline.objects),
        timeline: structuredClone(baseline.timeline),
        camera: { ...baseline.camera },
        cameraBeats: baseline.cameraBeats
          ? structuredClone(baseline.cameraBeats)
          : undefined,
        ...(baseline.activeMapId ? { activeMapId: baseline.activeMapId } : {}),
        comments: structuredClone(baseline.comments ?? []),
      }
    } else {
      next = applyBoardSeed(next, ver.seed)
    }
    delete next.tapeReviewRunId
    snapshotRef.current = { ...snapshotRef.current, session: next }
    commitSessionFields(() => next, { adoptAsPersisted: true })
    setElapsedMs(0)
    elapsedRef.current = 0
    setHoverElapsedMs(null)
    setRunning(false)
    setTimeBarAutoHidden(false)
    setTimeBarPinned(true)
    const firstOnTarget =
      (next.exerciseRuns ?? [])
        .filter(
          (r) =>
            r.versionId === want &&
            (r.status === 'completed' || r.status === 'abandoned')
        )
        .reduce<ExerciseRun | null>(
          (best, r) => (!best || r.number < best.number ? r : best),
          null
        ) ?? null
    if (reviewing && firstOnTarget && runHasTapeSeed(firstOnTarget)) {
      beginFinishedRunTapeReview(firstOnTarget, {
        persistTapeId: canDriveDoneReviewRef.current,
        startPlay: false,
      })
      if (!selectedIsCompletedRef.current) {
        rememberFinishedRunStopMs(firstOnTarget)
        pinReviewingPausedAtStop()
      }
    } else {
      setWatchingRunId(null)
      setBoardActivity('preview')
      if (canDriveDoneReviewRef.current) persistTapeReviewRunId(null)
    }
    peerVersionSwitchClearRef.current = window.setTimeout(() => {
      peerVersionSwitchClearRef.current = null
      setVersionSwitching(false)
    }, 550)
  }

  /** Switch story (between runs). Load that setup; Review latest run if any, else Preview. */
  function switchBoardVersion(versionId: string): void {
    const want = versionId.trim()
    if (!want) return
    if (liveTakeActiveRef.current) return
    // Template browse — local Version pick only (no hub instance).
    if (viewingTemplate || previewTemplateIdRef.current) {
      setTemplatePreviewVersionId(want)
      setWatchingRunId(null)
      setBoardActivity('preview')
      setElapsedMs(0)
      elapsedRef.current = 0
      setHoverElapsedMs(null)
      setRunning(false)
      return
    }
    // Done: local only (server refuses ApplyBoardSeed on Completed).
    if (selectedIsCompletedRef.current) {
      if (isLinkGuest) return
      switchDoneBoardVersionLocal(want)
      return
    }
    // Draft/Published: canDrivePreview; Live: canOperateSession. Both must hit the hub.
    if (!canOperateSessionRef.current && !canDrivePreviewRef.current) return
    const id = instanceIdRef.current
    if (!id) return
    // Mask strip/map as Switching; paint the destination Review (or Preview) immediately
    // so we never flash Previewing of the bare version seed for 1–2s (shot 2).
    if (peerVersionSwitchClearRef.current != null) {
      window.clearTimeout(peerVersionSwitchClearRef.current)
      peerVersionSwitchClearRef.current = null
    }
    const switchGen = (versionSwitchHoldRef.current?.gen ?? 0) + 1
    versionSwitchHoldRef.current = { gen: switchGen, versionId: want }
    setVersionSwitching(true)
    exitTapeReview({ persist: false })
    cancelStudioClockDesire('version-switch')
    // Reset playhead *before* painting the destination seed. Otherwise Auto-track
    // applies the new version's cameraBeats at the prior late elapsed (last shore/zoom
    // shot), then seekStoryStartBetweenRuns eases to the opening — and a late Clock
    // echo can yank back again (2–3 thrash cycles). Done/template paths already reset.
    setElapsedMs(0)
    elapsedRef.current = 0
    lastSentHubSeekMsRef.current = 0
    hubWirePlayheadMsRef.current = 0
    setHubWirePlayheadMs(0)
    setHoverElapsedMs(null)
    setRunning(false)
    hubAdmittedRunningRef.current = false
    // Build the next session synchronously — do NOT stash it inside a setState updater.
    // React may defer that updater until after this handler returns; the old
    // `holder.session` pattern then hit `if (!holder.session) return` and skipped every
    // hub invoke (A still painted locally; P and server logs saw nothing).
    const ensured = ensureVersionsOnSession(snapshotRef.current.session)
    const saved =
      ensured.selectedVersionId && ensured.selectedVersionId !== want
        ? updateSelectedVersionSeed(ensured, exerciseActor())
        : ensured
    const ver = findBoardVersion(saved.boardVersions, want)
    let next: ScenarioSession = { ...saved, selectedVersionId: want }
    if (ver) next = applyBoardSeed(next, ver.seed)
    delete next.tapeReviewRunId
    // Stable ordinal on version flip — always Run 1 (not “latest”, which jumped around).
    const firstOnTarget =
      (next.exerciseRuns ?? [])
        .filter(
          (r) =>
            r.versionId === want &&
            (r.status === 'completed' || r.status === 'abandoned')
        )
        .reduce<ExerciseRun | null>(
          (best, r) => (!best || r.number < best.number ? r : best),
          null
        ) ?? null
    snapshotRef.current = { ...snapshotRef.current, session: next }
    // adoptAsPersisted: seed replace must not leave persistedObjectsRef on the prior
    // board — the next pointer-up would Upsert-storm every object and starve Watch/Seek.
    commitSessionFields(() => next, { adoptAsPersisted: true })
    if (firstOnTarget && runHasTapeSeed(firstOnTarget)) {
      // Optimistic Review of the destination run — hub settle only confirms/persists.
      // Version flip eases to story start (seek below); do not park at the prior stop.
      beginFinishedRunTapeReview(firstOnTarget, {
        persistTapeId: false,
      })
    } else {
      setWatchingRunId(null)
      setBoardActivity('preview')
    }
    // ApplyBoardSeed already stamps selectedVersionId — do not race a solo Fields write.
    void hubApplyBoardSeed(id, want)
      .then(() => {
        if (versionSwitchHoldRef.current?.gen !== switchGen) return
        rememberPersistedObjects(snapshotRef.current.session.objects)
        return seekStoryStartBetweenRuns()
      })
      .then(() => {
        if (versionSwitchHoldRef.current?.gen !== switchGen) return
        return persistExerciseBoardFields(next, {
          tapeReviewRunId: firstOnTarget?.id ?? null,
          clearInstantReplay: true,
        }).catch(() => {})
      })
      .then(() => {
        if (versionSwitchHoldRef.current?.gen !== switchGen) return
        if (instanceIdRef.current !== id) return
        if (snapshotRef.current.session.selectedVersionId !== want) return
        versionSwitchHoldRef.current = null
        setVersionSwitching(false)
        if (firstOnTarget && runHasTapeSeed(firstOnTarget)) {
          if (tapeReviewRef.current?.runId !== firstOnTarget.id) {
            beginFinishedRunTapeReview(firstOnTarget)
          } else {
            persistTapeReviewRunId(firstOnTarget.id)
          }
        } else {
          syncReviewFocusForVersion(snapshotRef.current.session)
        }
      })
      .catch((err) => {
        if (versionSwitchHoldRef.current?.gen === switchGen) {
          versionSwitchHoldRef.current = null
          setVersionSwitching(false)
        }
        setServerNotice(studioActionMessage(err, 'Could not switch story.'))
      })
  }

  /** Peer ApplyBoardSeed Board patch — working board from server (not local seed copies). */
  function applyPeerBoardSeed(patch: ApplyBoardSeedPatch, seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    if (liveTakeActiveRef.current) return
    const cur = snapshotRef.current.session
    const versionFlip =
      (cur.selectedVersionId ?? null) !== (patch.selectedVersionId ?? null)
    const hold = versionSwitchHoldRef.current
    const switchingHere =
      hold != null &&
      patch.selectedVersionId != null &&
      hold.versionId === patch.selectedVersionId
    // Followers: same Switching veil the operator sees (brief settle, not a hub hold).
    if (versionFlip && !switchingHere) beginPeerVersionSwitchChrome()
    // Version flip: pin local head at story start with the new seed so Auto-track does
    // not ease to the prior late beat on the new cameraBeats before the operator Seek lands.
    if (versionFlip) {
      cancelStudioClockDesire('peer-version-seed')
      setElapsedMs(0)
      elapsedRef.current = 0
      setHoverElapsedMs(null)
      setRunning(false)
      hubAdmittedRunningRef.current = false
    }
    const adopted: ScenarioSession = {
      ...cur,
      selectedVersionId: patch.selectedVersionId,
      objects: patch.objects,
      timeline: patch.timeline,
      camera: patch.camera ?? cur.camera,
      ...(patch.activeMapId ? { activeMapId: patch.activeMapId } : {}),
    }
    if (patch.cameraBeats) adopted.cameraBeats = patch.cameraBeats
    else delete adopted.cameraBeats
    // Lean Board does not carry tapeReviewRunId — drop a cross-version overlay so peers
    // do not keep Instant Replay of the story they just left. Skip while we hold an
    // optimistic Review for this same version (would flash Previewing).
    if (versionFlip && !switchingHere) {
      delete adopted.tapeReviewRunId
      setTapeReview(null)
    }
    snapshotRef.current = { ...snapshotRef.current, session: adopted }
    commitSession(() => adopted, { persistObjects: false, adoptAsPersisted: true })
    if (!switchingHere) syncReviewFocusForVersion(adopted)
    const openId = instanceIdRef.current
    if (openId) {
      setInstances((prev) =>
        prev.map((row) =>
          row.id === openId
            ? {
                ...row,
                session: {
                  ...row.session,
                  selectedVersionId: adopted.selectedVersionId,
                  objects: adopted.objects,
                  timeline: adopted.timeline,
                  camera: adopted.camera,
                  cameraBeats: adopted.cameraBeats,
                  activeMapId: adopted.activeMapId,
                },
              }
            : row
        )
      )
    }
  }

  /**
   * Peer hub StopRun. GroupExcept skips the host (they already painted locally).
   * Applies take-stopped + tape id + runs so strip shows Reviewing without Fields races.
   */
  function applyPeerStopRun(patch: StopRunPatch, seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    cancelStudioClockDesire('peer-stop-run')
    const cur = snapshotRef.current.session
    const stopMs = Math.max(0, Math.floor(patch.stoppedStoryElapsedMs))
    // Lean stopRun always carries top-level stoppedStoryElapsedMs — stamp onto the
    // tape run so P's scrubber blackout matches A (wire rows can omit the field).
    const mergedRuns = mergeExerciseRunsFromPeer(cur.exerciseRuns, patch.exerciseRuns).map(
      (r) =>
        r.id === patch.tapeReviewRunId
          ? {
              ...r,
              stoppedStoryElapsedMs:
                stopMs > 0 ? stopMs : r.stoppedStoryElapsedMs,
            }
          : r
    )
    const adopted: ScenarioSession = {
      ...cur,
      ...(patch.selectedVersionId
        ? { selectedVersionId: patch.selectedVersionId }
        : {}),
      exerciseRuns: mergedRuns,
      exerciseTakeNumber: patch.exerciseTakeNumber,
      exerciseTakeStopped: true,
      tapeReviewRunId: patch.tapeReviewRunId,
    }
    delete adopted.runStartCountdown
    delete adopted.runStartCountdownEndsAt
    snapshotRef.current = { ...snapshotRef.current, session: adopted }
    commitSession(() => adopted, { persistObjects: false, adoptAsPersisted: true })
    applyExerciseTakePhase(patch.exerciseTakeNumber, true)
    finishedRunStopMsRef.current = stopMs > 0 ? stopMs : finishedRunStopMsRef.current
    const finished =
      mergedRuns.find((r) => r.id === patch.tapeReviewRunId) ??
      latestExerciseRunForVersion(adopted) ??
      latestExerciseRun(adopted)
    if (finished && runHasTapeSeed(finished)) {
      // Local chrome only — hub StopRun Pause@stop is SoT (no peer Seek).
      beginFinishedRunTapeReview(finished, { persistTapeId: false })
    } else if (finished) {
      setWatchingRunId(finished.id)
      setBoardActivity('watching')
      rememberFinishedRunStopMs(finished)
    }
    // Atomic StopRun lands Reviewing · Paused at the stop stamp.
    pinReviewingPausedAtStop({ stopMs: stopMs > 0 ? stopMs : undefined })
    const openId = instanceIdRef.current
    if (openId) {
      setInstances((prev) =>
        prev.map((row) =>
          row.id === openId
            ? {
                ...row,
                session: {
                  ...row.session,
                  exerciseRuns: adopted.exerciseRuns,
                  exerciseTakeNumber: adopted.exerciseTakeNumber,
                  exerciseTakeStopped: true,
                  tapeReviewRunId: adopted.tapeReviewRunId,
                },
              }
            : row
        )
      )
    }
  }

  /**
   * Peer hub StartRun. GroupExcept skips the host (they already painted locally).
   * Applies the run row and seed even if a Clock already marked the take live —
   * applyPeerBoardSeed bails in that case, which would leave peers on the previous scene.
   * A late duplicate after the take is underway does not snap the board back to the seed.
   */
  function applyPeerStartRun(patch: StartRunPatch, seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    const cur = snapshotRef.current.session
    const alreadyUnderway =
      (cur.exerciseTakeNumber ?? 0) === patch.exerciseTakeNumber &&
      runningRef.current &&
      elapsedRef.current > 2000
    // Wire may omit per-run seeds (lean startRun); keep local seeds for IR of prior takes.
    const mergedRuns = mergeExerciseRunsFromPeer(cur.exerciseRuns, patch.exerciseRuns)
    let adopted: ScenarioSession = {
      ...cur,
      selectedVersionId: patch.selectedVersionId,
      exerciseRuns: mergedRuns,
      exerciseTakeNumber: patch.exerciseTakeNumber,
      exerciseTakeStopped: false,
    }
    delete adopted.runStartCountdown
    delete adopted.runStartCountdownEndsAt
    delete adopted.tapeReviewRunId
    if (!alreadyUnderway && patch.seedApplied && patch.objects && patch.timeline) {
      adopted = {
        ...adopted,
        objects: patch.objects,
        timeline: patch.timeline,
        camera: patch.camera ?? adopted.camera,
        ...(patch.activeMapId ? { activeMapId: patch.activeMapId } : {}),
      }
      if (patch.cameraBeats) adopted.cameraBeats = patch.cameraBeats
      else delete adopted.cameraBeats
    }
    snapshotRef.current = { ...snapshotRef.current, session: adopted }
    commitSession(() => adopted, { persistObjects: false, adoptAsPersisted: true })
    applyExerciseTakePhase(patch.exerciseTakeNumber, false)
    // Leave between-takes Reviewing / tape so the strip can show Running · Run N.
    setTapeReview(null)
    finishedRunStopMsRef.current = null
    setWatchingRunId(null)
    setBoardActivity('running')
    // Operator clears the overlay locally when the countdown hits 0. Peers only
    // hear StartRun (GroupExcept), which drops runStartCountdown on the row.
    if (!runCountdownDriverRef.current) {
      runCountdownEndsAtRef.current = null
      setRunCountdown(null)
    }
    const openId = instanceIdRef.current
    if (openId) {
      setInstances((prev) =>
        prev.map((row) =>
          row.id === openId
            ? {
                ...row,
                session: {
                  ...row.session,
                  selectedVersionId: adopted.selectedVersionId,
                  exerciseRuns: adopted.exerciseRuns,
                  exerciseTakeNumber: adopted.exerciseTakeNumber,
                  exerciseTakeStopped: false,
                  objects: adopted.objects,
                  timeline: adopted.timeline,
                  camera: adopted.camera,
                  cameraBeats: adopted.cameraBeats,
                  activeMapId: adopted.activeMapId,
                },
              }
            : row
        )
      )
    }
    if (alreadyUnderway) return
    // Ignore late countdown Pause until StartRun Running lands (Paused · Run N bug).
    awaitingStartRunRunningRef.current = true
    hubAdmittedRunningRef.current = true
    reviewingStopPinUntilRef.current = 0
    // Atomic StartRun is always Running @ T0 — pin immediately (same as operator countdown).
    elapsedRef.current = 0
    setElapsedMs(0)
    beatNavAnchorMsRef.current = 0
    hubWirePlayheadMsRef.current = 0
    setHubWirePlayheadMs(0)
    lastSentHubSeekMsRef.current = 0
    setRunning(true)
    setStarted(true)
  }

  /**
   * Open-board Snapshot (ApplyBoardSeed catch-up, reconnect). Must stay on studioRef —
   * boot-time hub closures used to freeze the first-render commitSession.
   */
  function applyOpenBoardSnapshot(raw: unknown): void {
    try {
      const snap = snapshotFromWire(raw, user)
      const inst = instanceFromSnapshot(snap)
      if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
      // Only the open board may drive selection. A late Snapshot from a board we just
      // left used to call applyServerSnapshot and steal the rail selection mid-click.
      if (instanceIdRef.current !== snap.id) {
        setInstances((prev) => {
          if (!prev.some((row) => row.id === inst.id)) return prev
          return prev.map((row) => (row.id === inst.id ? inst : row))
        })
        return
      }
      // Open board: roster + clock/title. When ApplyBoardSeed (or catch-up) brings a
      // different version/board still, adopt objects without full applyInstance chrome reset.
      peopleRef.current = inst.people
      setPeople(inst.people)
      const cur = snapshotRef.current.session
      const mergedChat = mergeRoomChat(cur.roomChat, snap.session.roomChat)
      const snapVid = snap.session.selectedVersionId ?? null
      const curVid = cur.selectedVersionId ?? null
      const versionFlip = snapVid !== curVid
      // Seeds share object ids across Hudson versions — fingerprint path / keyframes
      // so ApplyBoardSeed still replaces the board after a Fields-only id patch.
      const boardFingerprint = (s: typeof cur) => {
        const path = s.objects.find((o) => o.id === 'flight-path-1')
        const plane = s.objects.find((o) => o.id === 'airplane-1')
        return [
          s.selectedVersionId ?? '',
          String(s.objects.length),
          String(s.timeline?.length ?? 0),
          String(path?.path?.length ?? 0),
          String(path?.params?.birdLaterSeedRev ?? path?.params?.sullySeedRev ?? ''),
          String(plane?.keyframes?.length ?? 0),
          String(plane?.params?.color ?? ''),
        ].join('|')
      }
      const boardStillFlip =
        versionFlip || boardFingerprint(snap.session) !== boardFingerprint(cur)
      setInstances((prev) =>
        prev.map((row) =>
          row.id === inst.id
            ? {
                ...row,
                people: inst.people,
                studioClock: inst.studioClock ?? row.studioClock,
                audienceType: inst.audienceType,
                ...(inst.allowLinkGuests ? { allowLinkGuests: true } : {}),
                session: {
                  ...row.session,
                  roomChat: mergedChat,
                  ...(boardStillFlip
                    ? {
                        selectedVersionId:
                          snap.session.selectedVersionId ?? row.session.selectedVersionId,
                        boardVersions: snap.session.boardVersions ?? row.session.boardVersions,
                        objects: snap.session.objects,
                        timeline: snap.session.timeline,
                        camera: snap.session.camera ?? row.session.camera,
                      }
                    : {}),
                },
              }
            : row
        )
      )
      if (boardStillFlip) {
        const nextVid = snap.session.selectedVersionId ?? cur.selectedVersionId
        const wireTape =
          typeof snap.session.tapeReviewRunId === 'string' &&
          snap.session.tapeReviewRunId.trim()
            ? snap.session.tapeReviewRunId.trim()
            : null
        // Never keep the prior version’s tape id: `?? cur.tapeReviewRunId` re-armed Bird
        // Instant Replay after ApplyBoardSeed Snapshot omitted the cleared key.
        const runs = snap.session.exerciseRuns ?? cur.exerciseRuns ?? []
        const tapeOnThisVersion =
          wireTape &&
          runs.some((r) => r.id === wireTape && (!nextVid || r.versionId === nextVid))
            ? wireTape
            : null
        const switchHold = versionSwitchHoldRef.current
        const switchingTo =
          switchHold && nextVid && switchHold.versionId === nextVid ? switchHold : null
        const adopted = {
          ...cur,
          title: snap.session.title || cur.title,
          selectedVersionId: nextVid,
          boardVersions: snap.session.boardVersions ?? cur.boardVersions,
          objects: snap.session.objects,
          timeline: snap.session.timeline,
          camera: snap.session.camera ?? cur.camera,
          activeMapId: snap.session.activeMapId || cur.activeMapId,
          comments: snap.session.comments ?? cur.comments,
          roomChat: mergedChat,
          exerciseRuns: runs,
          exerciseTakeNumber: snap.session.exerciseTakeNumber ?? cur.exerciseTakeNumber,
          exerciseTakeStopped: snap.session.exerciseTakeStopped ?? cur.exerciseTakeStopped,
          ...(tapeOnThisVersion && !switchingTo
            ? { tapeReviewRunId: tapeOnThisVersion }
            : {}),
        }
        if (!tapeOnThisVersion || switchingTo) delete adopted.tapeReviewRunId
        // Warm ref before setState so a racing Fields patch sees the new board.
        snapshotRef.current = { ...snapshotRef.current, session: adopted }
        commitSession(() => adopted, { persistObjects: false, adoptAsPersisted: true })
        if (switchingTo) {
          // Operator owns chrome until settle — leave optimistic Review/tape alone.
          // adoptTapeReviewRunId(null) would flash Previewing (shot 2) for 1–2s.
        } else if (versionFlip) {
          beginPeerVersionSwitchChrome()
          syncReviewFocusForVersion(adopted)
          adoptTapeReviewRunId(tapeOnThisVersion)
        } else {
          adoptTapeReviewRunId(tapeOnThisVersion)
        }
      } else {
        adoptTapeReviewRunId(snap.session.tapeReviewRunId)
        commitSessionFields((prev) => {
          const next = mergeRoomChat(prev.roomChat, snap.session.roomChat)
          const prevChat = prev.roomChat ?? []
          if (
            next.length === prevChat.length &&
            next.every((line, i) => line.id === prevChat[i]?.id)
          ) {
            return prev
          }
          return patchRoomChat(prev, next)
        })
        snapshotRef.current = {
          ...snapshotRef.current,
          session: patchRoomChat(snapshotRef.current.session, mergedChat),
        }
      }
      studioRef.current.onClock(snap.clock, snap.id)
      if (snap.title) studioRef.current.onSessionFields({ title: snap.title }, snap.clock.seq)
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not read this session.'))
    }
  }

  function openPostRunOffer(): void {
    const typeId =
      snapshotRef.current.session.sessionType ?? viewSession.sessionType
    if (!typeOffersSharedRun(typeId)) return
    if (runCountdownDriverRef.current || runCountdown != null) return
    // Operators: full controls in the modal. Participants: same openability, read-only.
    setVersionDupUi(null)
    setPostRunOfferOpen(true)
  }

  function closePostRunOffer(): void {
    setPostRunOfferOpen(false)
    setVersionDupUi(null)
  }

  function beginDuplicateVersionInline(): void {
    if (!canOperateSessionRef.current || liveTakeActiveRef.current) return
    const source = selectedBoardVersion(snapshotRef.current.session)
    if (!source) return
    const stem = source.name.replace(/\s*\((edited|copy)\)\s*$/i, '').trim() || source.name
    const versions = snapshotRef.current.session.boardVersions ?? []
    const draft = nextCopyTitle(
      stem,
      versions.map((v) => ({ session: { title: v.name } }))
    )
    // Dedicated modal (not nested on Run options).
    setPostRunOfferOpen(false)
    versionDupUiRef.current = { mode: 'name', draft }
    setVersionDupUi({ mode: 'name', draft })
  }

  /** Leave finished-run Instant Replay and preview the live working board (Done: final board). */
  function exitRunReviewToBoard(): void {
    exitTapeReview({ persist: true })
    setWatchingRunId(null)
    setBoardActivity('preview')
    closePostRunOffer()
    if (selectedIsCompletedRef.current) {
      // Restore end-board if we are on the version that was open at End.
      const baseline = doneEndBoardRef.current
      const vid = snapshotRef.current.session.selectedVersionId
      if (baseline && baseline.versionId && baseline.versionId === vid) {
        const restored: ScenarioSession = {
          ...snapshotRef.current.session,
          objects: structuredClone(baseline.objects),
          timeline: structuredClone(baseline.timeline),
          camera: { ...baseline.camera },
          cameraBeats: baseline.cameraBeats
            ? structuredClone(baseline.cameraBeats)
            : undefined,
          ...(baseline.activeMapId ? { activeMapId: baseline.activeMapId } : {}),
          comments: structuredClone(baseline.comments ?? []),
        }
        delete restored.tapeReviewRunId
        snapshotRef.current = { ...snapshotRef.current, session: restored }
        commitSessionFields(() => restored, { adoptAsPersisted: true })
      }
      setElapsedMs(0)
      elapsedRef.current = 0
      setHoverElapsedMs(null)
      setRunning(false)
      setTimeBarAutoHidden(false)
      setTimeBarPinned(true)
      return
    }
    seekStoryStartBetweenRuns()
  }

  /** After an edit burst, strip returns to Previewing (not Reviewing). */
  function noteBoardPreviewActivity(): void {
    if (liveTakeActiveRef.current) return
    if (tapeReviewRef.current || boardActivityRef.current === 'watching') return
    if (boardActivityRef.current === 'editing' || boardActivityRef.current === 'ready') {
      setBoardActivity('preview')
    }
  }

  function confirmDuplicateVersionInline(draftName?: string): void {
    if (!canOperateSessionRef.current || liveTakeActiveRef.current) return
    const ui = versionDupUiRef.current
    if (!ui || ui.mode !== 'name') return
    const prev = snapshotRef.current.session
    const source = selectedBoardVersion(prev)
    if (!source) {
      setServerNotice('Could not duplicate this version.')
      return
    }
    const stem = source.name.replace(/\s*\((edited|copy)\)\s*$/i, '').trim() || source.name
    const versions = prev.boardVersions ?? []
    const titleRows = versions.map((v) => ({ session: { title: v.name } }))
    const fallback = nextCopyTitle(stem, titleRows)
    let name = (draftName ?? ui.draft).trim() || fallback
    // Don't allow a second identical version name — bump like session titles.
    if (versions.some((v) => v.name.trim() === name)) {
      name = nextCopyTitle(name, titleRows)
    }
    const cloned = cloneBoardVersion(prev, source.id, name, exerciseActor())
    const vid = cloned.selectedVersionId
    if (!vid || vid === prev.selectedVersionId || cloned === prev) {
      setServerNotice('Could not duplicate this version.')
      return
    }
    // Flip the modal first so a slow hub write cannot leave the name form stuck.
    versionDupUiRef.current = { mode: 'done', name }
    setVersionDupUi({ mode: 'done', name })
    setWatchingRunId(null)
    setBoardActivity('preview')
    snapshotRef.current = { ...snapshotRef.current, session: cloned }
    commitSessionFields(() => cloned)
    const id = instanceIdRef.current
    void (async () => {
      try {
        if (id) {
          await hubSetSessionFields(id, {
            boardVersions: cloned.boardVersions,
            selectedVersionId: vid,
            exerciseRuns: cloned.exerciseRuns ?? [],
            exerciseTakeNumber: cloned.exerciseTakeNumber ?? 0,
            exerciseTakeStopped: Boolean(cloned.exerciseTakeStopped),
          })
          await hubApplyBoardSeed(id, vid)
        }
        await seekStoryStartBetweenRuns()
      } catch (err) {
        setServerNotice(studioActionMessage(err, 'Could not save the new version.'))
      }
    })()
  }

  /** Confirm Start in the friendly modal → shared countdown → resetLiveStory. */
  function requestStartRun(opts?: { continueDirty?: boolean; slug?: string | null }): void {
    if (!showExerciseRunChrome) return
    if (!canOperateSessionRef.current) return
    if (runCountdown != null || runCountdownDriverRef.current) return
    if (liveTakeActive) return
    exitTapeReview({ persist: true })
    setWatchingRunId(null)
    setBoardActivity('ready')
    closePostRunOffer()
    runCountdownOptsRef.current = opts
    runCountdownDriverRef.current = true
    // Gate story-end for the countdown and until StartRun returns (not a wall-clock mute).
    runStartGateRef.current = 'preparing'
    storyEndLatchRef.current = null
    awaitingStartRunRunningRef.current = false
    // Pin the local head at story start during countdown so we are never Running-at-end.
    setElapsedMs(0)
    elapsedRef.current = 0
    setRunning(false)
    // One wall deadline for the room — peers tick from endsAt (not each Fields second).
    const endsAtMs = Date.now() + 5000
    runCountdownEndsAtRef.current = endsAtMs
    setRunCountdown(5)
    const id = instanceIdRef.current
    if (id) {
      // Fields-only countdown — no hub Pause/Seek (late Pause@0 after StartRun caused
      // Paused · Run N / missed Start). Room clock moves on StartRun only.
      void hubSetSessionFields(id, {
        runStartCountdown: 5,
        runStartCountdownEndsAt: new Date(endsAtMs).toISOString(),
      }).catch(() => {
        /* local countdown still runs */
      })
      hubSeekOwnsHeadRef.current = true
      lastSentHubSeekMsRef.current = 0
      hubWirePlayheadMsRef.current = 0
      setHubWirePlayheadMs(0)
      cancelStudioClockDesire('countdown-arm')
    }
  }

  /** Pick a completed run to Instant Replay (leaves the working board untouched). */
  function selectReviewRun(run: ExerciseRun): void {
    if (liveTakeActive) return
    const doneLocal = selectedIsCompletedRef.current
    if (!canOperateSession && !doneLocal) return
    if (run.versionId !== selectedVersion?.id) {
      if (doneLocal) {
        // Local version flip then pin this run (hub ApplyBoardSeed blocked on Done).
        setBoardActivity('watching')
        switchDoneBoardVersionLocal(run.versionId)
        if (runHasTapeSeed(run)) {
          beginFinishedRunTapeReview(run, {
            persistTapeId: canDriveDoneReview,
            startPlay: false,
          })
        } else {
          setWatchingRunId(run.id)
          setBoardActivity('watching')
        }
        setElapsedMs(0)
        elapsedRef.current = 0
        setHoverElapsedMs(null)
        setRunning(false)
        return
      }
      switchBoardVersion(run.versionId)
      return
    }
    if (runHasTapeSeed(run)) {
      beginFinishedRunTapeReview(run, {
        persistTapeId: canOperateSession || canDriveDoneReview,
        startPlay: false,
      })
      if (doneLocal) {
        setElapsedMs(0)
        elapsedRef.current = 0
        setHoverElapsedMs(null)
        setRunning(false)
        setTimeBarAutoHidden(false)
        setTimeBarPinned(true)
        return
      }
      // Live: park at that run's stop stamp and Seek so P's thumb matches (Space wraps to T0).
      rememberFinishedRunStopMs(run)
      pinReviewingPausedAtStop({ share: true })
      return
    }
    // Legacy run without seed — as-now preview focused on that run id.
    exitTapeReview({ persist: true })
    setWatchingRunId(run.id)
    setBoardActivity('watching')
    if (doneLocal) {
      setElapsedMs(0)
      elapsedRef.current = 0
      setHoverElapsedMs(null)
      setRunning(false)
      return
    }
    seekStoryStartBetweenRuns()
  }

  // Stable chip onPick target — refreshed each render; ReviewRunChipRow is memoized.
  pickReviewRunChipRef.current = (runId: string) => {
    const run = reviewRunChips.find((r) => r.id === runId)
    if (!run) return
    selectReviewRun(run)
    closePostRunOffer()
  }

  /**
   * Walkthrough Reset / Exercise Start Run N+1 — story back to start, stay Live.
   * Exercise path records a new run against the selected version (default reset to seed).
   *
   * Exercise Start Run is one hub `StartRun` (run row + optional seed + clock at story
   * start). Story-end stays gated until that invoke returns — not a Seek/Resume chain
   * and not a wall-clock mute. Local session is still built from snapshotRef first
   * (countdown setTimeout must not read a setState holder).
   */
  function resetLiveStory(opts?: { continueDirty?: boolean; slug?: string | null }): void {
    // Use the ref — countdown effect can call this across renders; state lags.
    if (!canOperateSessionRef.current) {
      runStartGateRef.current = 'idle'
      return
    }
    const shared = typeOffersSharedRun(snapshotRef.current.session.sessionType)
    // StartRun clears IR on the same row. A separate Fields write here raced the
    // take (countdown-null class) and could drop the new run.
    clearInstantReplayPause({ persist: !shared })
    // Drop finished-run tape overlay / shared id — StartRun clears hub row; local must
    // not keep Run N−1 Reviewing chrome under Running · Run N.
    setTapeReview(null)
    finishedRunStopMsRef.current = null
    // Drop any stale Pause desire from Review scrub so Seek echoes cannot paint Paused
    // over the new Run (UI paused while hub kept running → Resume jumped forward).
    studioClockDesireRef.current = null
    // Late countdown Pause must not win over StartRun Running (Paused · Run N at 0).
    awaitingStartRunRunningRef.current = true
    setTimeBarAutoHidden(false)
    setTimeBarPinned(false)
    setTimeBarEnterKey((n) => n + 1)
    setPostRunOfferOpen(false)
    setWatchingRunId(null)
    setBoardActivity('running')
    // Pin head at story start before claiming Running. Reviewing at the last beat
    // used to finish the new run in the same frame ("Preview complete" / jump to end).
    storyEndLatchRef.current = null
    setElapsedMs(0)
    elapsedRef.current = 0
    hubAdmittedRunningRef.current = Boolean(shared)
    let startedTakeN = 0
    let startedRunId: string | null = null
    let startedVersionId: string | null = null
    if (shared) {
      // Keep story-end blocked until StartRun's Clock is the room (armed below).
      runStartGateRef.current = 'preparing'
      // Build the run *synchronously* from snapshotRef. Countdown finishes via
      // setTimeout → commitSession's setState updater is not guaranteed to run
      // before we read a holder (React 18). That skipped applyExerciseTakePhase
      // and left take#=0 → strip stuck on Previewing after Start.
      const startOpts = {
        actor: exerciseActor(),
        slug: opts?.slug,
        continueDirty: opts?.continueDirty === true,
        actionsSeqStart:
          clocksRef.current.get(instanceIdRef.current ?? '')?.seq ?? null,
      }
      const { session: startedSession, run } = startExerciseRun(
        snapshotRef.current.session,
        startOpts
      )
      const runSession: ScenarioSession = { ...startedSession }
      delete runSession.tapeReviewRunId
      startedRunId = run.id
      startedVersionId = runSession.selectedVersionId ?? null
      startedTakeN = Math.max(
        1,
        run.number,
        runSession.exerciseTakeNumber ?? takeNumberRef.current + 1
      )
      applyExerciseTakePhase(startedTakeN, false)
      snapshotRef.current = { ...snapshotRef.current, session: runSession }
      commitSession(() => runSession, { persistObjects: false, adoptAsPersisted: true })
      setWatchingRunId(run.id)
    } else {
      setExerciseRunStopped(false)
    }
    const startIso = snapshotRef.current.session.storyStartTime
    setPathPreviewTotalMs(null)
    setPathPreviewStartMs(null)
    setPathPreviewPose(null)
    setPathPreviewObjectId(null)
    setPathPreviewWaypointIndex(null)
    setPathPreviewHolding(false)
    setPathHoldLabel(null)
    setVisPreviewTotalMs(null)
    setVisPreviewObjectId(null)
    setRadiusPreviewTotalMs(null)
    setRadiusPreviewStartMs(null)
    setRadiusPreviewObjectId(null)
    setWindPreviewTotalMs(null)
    setWindPreviewStartMs(null)
    setTimelineFocusId(null)
    setHoverElapsedMs(null)
    const id = instanceIdRef.current
    if (!id) {
      if (shared) runStartGateRef.current = 'idle'
      return
    }
    const prev = clocksRef.current.get(id)
    if (!prev) {
      if (shared) runStartGateRef.current = 'idle'
      return
    }
    // Walkthrough Reset → Idle at start (Play lit). Exercise Run again → start running.
    if (!shared) {
      pauseHoldIdRef.current = id
      if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
    } else {
      if (pauseHoldIdRef.current === id) pauseHoldIdRef.current = null
      if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
      // Hold Running until the StartRun Clock admits (one echo, not Seek/Resume).
      transportPlayHoldIdRef.current = id
      hubSeekOwnsHeadRef.current = true
    }
    const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRate)
    const takeN = shared ? Math.max(1, startedTakeN || takeNumber + 1) : 0
    const stamped: StudioClock = {
      ...prev,
      lifecycle: 'running',
      clockStatus: shared ? 'running' : 'paused',
      rate: stripRate,
      anchorStoryTimeIso: startIso,
      anchorActualTime: shared ? new Date().toISOString() : null,
      storyOriginIso: startIso,
      ...(shared
        ? { exerciseTakeNumber: takeN, exerciseTakeStopped: false }
        : {}),
    }
    clocksRef.current.set(id, stamped)
    serverClockRef.current = stamped
    setServerClock(stamped)
    // Optimistic Running for strip UX; story-end stays gated until StartRun returns.
    setRunning(shared)
    setStarted(true)
    localPlayAnchorRef.current = {
      wall: performance.now(),
      elapsed: 0,
      rate: stripRate,
    }
    setInstances((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? {
              ...row,
              clockStatus: stamped.clockStatus,
              studioClock: stamped,
              playheadStoryIso: startIso,
            }
          : row
      )
    )
    // Pin storm/skip-dup memory at story start with the local head. Nulling lastSent
    // after a direct hubSeek left skip-dup unable to match, so reset→play always
    // re-Sought (no-op) and stretched the pump — local 20× raced then snapped back.
    lastSentHubSeekMsRef.current = 0
    hubWirePlayheadMsRef.current = 0
    setHubWirePlayheadMs(0)
    // owns-head (not a wall mute) keeps a late end-cued Clock from yanking the head
    // until the StartRun playhead is near story start.
    const finish = shared
      ? hubStartRun(id, {
          versionId: startedVersionId ?? '',
          continueDirty: opts?.continueDirty === true,
          rate: stripRate,
          runId: startedRunId ?? '',
          runNumber: startedTakeN,
          slug: opts?.slug ?? null,
        }).then((result) => {
          if (result.takeNumber !== startedTakeN && startedRunId) {
            const fixed: ScenarioSession = {
              ...snapshotRef.current.session,
              exerciseTakeNumber: result.takeNumber,
              exerciseRuns: (snapshotRef.current.session.exerciseRuns ?? []).map((r) =>
                r.id === startedRunId ? { ...r, number: result.takeNumber } : r
              ),
            }
            snapshotRef.current = { ...snapshotRef.current, session: fixed }
            commitSession(() => fixed, { persistObjects: false, adoptAsPersisted: true })
            applyExerciseTakePhase(result.takeNumber, false)
          }
          runStartGateRef.current = 'armed'
          // Running Clock may still be in flight — awaitingStartRunRunningRef clears
          // when it admits (or stays until then so late countdown Pause cannot win).
          // Keep hubSeekOwnsHeadRef until applyServerClock sees a playhead near
          // story start — clearing here let a late end-cued Clock yank the head.
        })
      : // Pause first so Seek cannot leave the clock advancing; Pause again after Seek.
        hubPause(id)
          .then(() => hubSeek(id, startIso, false, false, true))
          .then((admitted) => {
            if (admitted !== false) {
              lastSentHubSeekMsRef.current = 0
              hubWirePlayheadMsRef.current = 0
              setHubWirePlayheadMs(0)
            }
            return hubPause(id)
          })
          .finally(() => {
            if (pauseHoldIdRef.current === id) pauseHoldIdRef.current = null
          })
    void finish.catch((err) => {
      if (pauseHoldIdRef.current === id) pauseHoldIdRef.current = null
      if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
      hubSeekOwnsHeadRef.current = false
      awaitingStartRunRunningRef.current = false
      if (shared) runStartGateRef.current = 'idle'
      setServerNotice(
        studioActionMessage(
          err,
          shared ? 'Could not restart the run.' : 'Could not reset the story clock.'
        )
      )
    })
  }

  /** Exercise STOP — freeze the timed run; session stays Live (not End Session). */
  function stopLiveRun(): void {
    if (!canOperateSession || !typeOffersSharedRun(viewSession.sessionType)) return
    if (!running && elapsedMs <= 0) return
    setHoverElapsedMs(null)
    clearInstantReplayPause()
    setTimeBarPinned(true)
    setTimeBarAutoHidden(false)
    const id = instanceIdRef.current
    if (!id) return
    const start = snapshotRef.current.session.storyStartTime
    const stopMs = Math.max(0, Math.floor(elapsedRef.current))
    const freezeIso = addStoryMs(start, stopMs)
    cancelStudioClockDesire('stop-run')
    // Optimistic Reviewing · Paused at the stop playhead (hub StopRun confirms).
    finishedRunStopMsRef.current = stopMs
    const stopHolder: { session: ScenarioSession | null } = { session: null }
    commitSession(
      (prevSession) => {
        let next = stopExerciseRun(prevSession, {
          actor: exerciseActor(),
          actionsSeqEnd: currentInstance?.studioClock?.seq ?? null,
          stoppedStoryElapsedMs: stopMs,
          resetToSeed: false,
        })
        const finished =
          latestExerciseRunForVersion(next) ?? latestExerciseRun(next)
        if (finished && runHasTapeSeed(finished)) {
          next = { ...next, tapeReviewRunId: finished.id }
        }
        stopHolder.session = next
        return next
      },
      { persistObjects: false, adoptAsPersisted: true }
    )
    const stoppedSession = stopHolder.session
    const takeN = Math.max(1, stoppedSession?.exerciseTakeNumber ?? takeNumber)
    applyExerciseTakePhase(takeN, true)
    finishedRunStopMsRef.current = stopMs
    if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
    transportPauseHoldIdRef.current = id
    pinReviewingPausedAtStop({ stopMs })
    const stopped = stoppedSession ?? snapshotRef.current.session
    const finishedRun =
      latestExerciseRunForVersion(stopped) ?? latestExerciseRun(stopped)
    if (finishedRun && runHasTapeSeed(finishedRun)) {
      beginFinishedRunTapeReview(finishedRun, { persistTapeId: false })
    } else {
      if (finishedRun) setWatchingRunId(finishedRun.id)
      setBoardActivity('watching')
    }
    const localN = versionLocalRunNumberFor(stopped, finishedRun) || takeN
    pushInfoToast(`Run ${localN} finished.`, false, 3, 3600)
    void hubStopRun(id, { storyPlayheadIso: freezeIso })
      .then((res) => {
        const healedStop =
          res.stoppedStoryElapsedMs > 0 ? res.stoppedStoryElapsedMs : stopMs
        finishedRunStopMsRef.current = healedStop
        if (res.runId) {
          const run =
            (stoppedSession?.exerciseRuns ?? snapshotRef.current.session.exerciseRuns ?? []).find(
              (r) => r.id === res.runId
            ) ?? finishedRun
          if (run && runHasTapeSeed(run)) {
            beginFinishedRunTapeReview(
              { ...run, id: res.runId, stoppedStoryElapsedMs: healedStop },
              { persistTapeId: false },
            )
          }
        }
        pinReviewingPausedAtStop({ stopMs: healedStop })
        runningRef.current = false
        setRunning(false)
        if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
      })
      .catch((err) => {
        if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
        setServerNotice(studioActionMessage(err, 'Could not stop the run.'))
      })
  }

  /** Clock for a board that is not the open strip — update list/map only. */
  function applyBackgroundClock(clock: StudioClock, instanceKey: string): void {
    const prevClock = clocksRef.current.get(instanceKey)
    if (prevClock && clock.seq < prevClock.seq) return
    const emptyPlanned =
      clock.lifecycle === 'planned' &&
      clock.clockStatus === 'idle' &&
      !clock.anchorStoryTimeIso &&
      !clock.actualStartTime
    const livePrev =
      prevClock != null &&
      (prevClock.lifecycle === 'running' ||
        prevClock.clockStatus === 'running' ||
        prevClock.clockStatus === 'paused')
    if (emptyPlanned && livePrev) return
    const row = instancesRef.current.find((i) => i.id === instanceKey)
    const start = row?.session.storyStartTime || clock.anchorStoryTimeIso || ''
    const stamped: StudioClock = {
      ...clock,
      storyOriginIso:
        prevClock?.storyOriginIso ||
        clock.anchorStoryTimeIso ||
        prevClock?.anchorStoryTimeIso ||
        null,
    }
    const playhead = start ? boardPlayheadIso(stamped, start) : stamped.anchorStoryTimeIso
    clocksRef.current.set(instanceKey, stamped)
    setInstances((prev) =>
      prev.map((i) =>
        i.id === instanceKey
          ? {
              ...i,
              lifecycle: clock.lifecycle,
              clockStatus: clock.clockStatus,
              studioClock: stamped,
              playheadStoryIso: playhead ?? i.playheadStoryIso,
              actualStartTime: clock.actualStartTime ?? i.actualStartTime,
              actualEndTime: clock.actualEndTime ?? i.actualEndTime,
            }
          : i
      )
    )
  }

  function applyServerClock(
    clock: StudioClock,
    startsAt?: string,
    id?: string,
    clientTrace?: string | null,
  ): void {
    const trace = clientTrace?.trim() || ''
    const instanceKey = id ?? instanceIdRef.current
    if (!instanceKey) return
    const applyNote = (decision: string, extra?: Record<string, unknown>) => {
      noteClockTrace(
        'apply',
        decision,
        trace,
        {
          seq: clock.seq,
          lifecycle: clock.lifecycle,
          status: clock.clockStatus,
          story: clock.anchorStoryTimeIso,
          localElapsed: elapsedRef.current,
          localRunning: runningRef.current,
          hubSeekOwns: hubSeekOwnsHeadRef.current,
          pauseHold: transportPauseHoldIdRef.current === instanceKey,
          playHold: transportPlayHoldIdRef.current === instanceKey,
          hubAdmittedRunning: hubAdmittedRunningRef.current,
          ...extra,
        },
        instanceKey,
      )
    }
    // Never paint another board's clock onto the open strip (join/leave race).
    if (instanceKey !== instanceIdRef.current || previewTemplateIdRef.current) {
      if (id) applyBackgroundClock(clock, instanceKey)
      applyNote('bg-or-template')
      return
    }
    // Spectators must never keep owns-head — a stale true (prior board / soft-grant race)
    // ignored PreviewPlay Running until Pause jumped the bar.
    if (!canOperateSessionRef.current && !canDrivePreviewRef.current) {
      hubSeekOwnsHeadRef.current = false
    }
    const prevClock = clocksRef.current.get(instanceKey)
    // An older anchor, or a clock that arrived without a lifecycle, must not
    // paint a live run back to Planned at the start time.
    if (prevClock && clock.seq < prevClock.seq) {
      applyNote('drop-stale-seq', { prevSeq: prevClock.seq })
      return
    }
    const emptyPlanned =
      clock.lifecycle === 'planned' &&
      clock.clockStatus === 'idle' &&
      !clock.anchorStoryTimeIso &&
      !clock.actualStartTime
    const livePrev =
      prevClock != null &&
      (prevClock.lifecycle === 'running' ||
        prevClock.clockStatus === 'running' ||
        prevClock.clockStatus === 'paused')
    if (emptyPlanned && livePrev) {
      applyNote('drop-empty-planned-over-live')
      return
    }
    // Operator already painted Pause (or Pause RPC in flight) — ignore Running
    // echoes that would restart the local ticker while the hub stays paused.
    // Do not apply during StartRun await — countdown Pause hold must not block Running.
    if (
      canOperateSessionRef.current &&
      clock.clockStatus === 'running' &&
      !awaitingStartRunRunningRef.current &&
      (transportPauseHoldIdRef.current === instanceKey ||
        studioClockDesireRef.current?.running === false)
    ) {
      noteScenarioSeq(clock.seq)
      applyNote('hold-ignore-running-echo')
      return
    }
    noteScenarioSeq(clock.seq)
    // IR playing before this Clock — cadence Seeks must not snap spectators who were
    // already interpolating (see playhead apply below).
    const irPlayingBeforeClock = replayPlayingRef.current
    {
      // Wire playhead for strip Ready/Paused — ignore optimistic local zeros.
      const wireStart = startsAt ?? snapshotRef.current.session.storyStartTime
      if (wireStart) {
        const wireMs = Math.max(0, storyDeltaMs(wireStart, boardPlayheadIso(clock, wireStart)))
        if (Math.abs(hubWirePlayheadMsRef.current - wireMs) >= 40) {
          hubWirePlayheadMsRef.current = wireMs
          setHubWirePlayheadMs(wireMs)
        }
      }
    }
    // Take phase + IR pause ride Clock (from SessionJson) so peers match the host.
    // When takeStopped flips true this Clock, force peer Reviewing at the stop stamp
    // even if Board stopRun was missed/reordered.
    let forcePeerReviewingAtStop = false
    if (instanceKey === instanceIdRef.current && !previewTemplateIdRef.current) {
      if (
        typeof clock.exerciseTakeNumber === 'number' ||
        typeof clock.exerciseTakeStopped === 'boolean'
      ) {
        const hasNum =
          typeof clock.exerciseTakeNumber === 'number' && Number.isFinite(clock.exerciseTakeNumber)
        const hasStopped = typeof clock.exerciseTakeStopped === 'boolean'
        if (hasNum || hasStopped) {
          const n = hasNum ? Math.max(0, Math.floor(clock.exerciseTakeNumber as number)) : null
          let stopped = hasStopped ? (clock.exerciseTakeStopped as boolean) : null
          const admitted = admitTakePhaseWire(n, stopped)
          const applyN = admitted.take
          stopped = admitted.stopped
          const wasStopped = exerciseRunStoppedRef.current
          if (applyN != null) {
            takeNumberRef.current = applyN
            setTakeNumber(applyN)
          }
          if (stopped != null) {
            // Same-tick playhead apply must see take-stopped before React re-renders.
            exerciseRunStoppedRef.current = stopped
            setExerciseRunStopped(stopped)
            if (stopped === false) finishedRunStopMsRef.current = null
          }
          commitSessionFields((prev) => {
            const takeSame = applyN == null || prev.exerciseTakeNumber === applyN
            const stoppedSame =
              stopped == null || Boolean(prev.exerciseTakeStopped) === stopped
            if (takeSame && stoppedSame) return prev
            let next: ScenarioSession = {
              ...prev,
              ...(applyN != null ? { exerciseTakeNumber: applyN } : {}),
              ...(stopped != null ? { exerciseTakeStopped: stopped } : {}),
            }
            // Missed startRun left peers with in_progress Run 1 while take#=3.
            if (applyN != null && applyN > 0) {
              next = healRunsForHubTake(next, applyN, stopped === true)
            }
            return next
          })
          setInstances((prev) => {
            let changed = false
            const next = prev.map((i) => {
              if (i.id !== instanceKey) return i
              const takeSame = applyN == null || i.session.exerciseTakeNumber === applyN
              const stoppedSame =
                stopped == null || Boolean(i.session.exerciseTakeStopped) === stopped
              if (takeSame && stoppedSame) return i
              changed = true
              return {
                ...i,
                session: {
                  ...i.session,
                  ...(applyN != null ? { exerciseTakeNumber: applyN } : {}),
                  ...(stopped != null ? { exerciseTakeStopped: stopped } : {}),
                },
              }
            })
            return changed ? next : prev
          })
          // Peers: operator commits finish; announce the same toast they missed when
          // story-end was misclassified as Preview (no take-stopped wire).
          if (stopped === true && !wasStopped && (applyN ?? takeNumberRef.current) > 0) {
            const n = Math.max(1, applyN ?? takeNumberRef.current)
            adoptPeerTakeFinished(n)
            // StopRun Pause@stop — pin peer head even if Board stopRun was missed/reordered.
            // adoptPeerTakeFinished already pinReviewingPausedAtStop; toast only for peers.
            if (!canOperateSessionRef.current) {
              forcePeerReviewingAtStop = true
              const sess = snapshotRef.current.session
              const finished =
                latestExerciseRunForVersion(sess) ?? latestExerciseRun(sess)
              const localN = versionLocalRunNumberFor(sess, finished) || n
              pushInfoToast(`Run ${localN} finished.`, false, 3, 3600)
            }
          }
        }
      }
      if (Object.prototype.hasOwnProperty.call(clock, 'instantReplayPauseMs')) {
        const takeFinished =
          clock.exerciseTakeStopped === true ||
          (typeof clock.exerciseTakeStopped !== 'boolean' &&
            snapshotRef.current.session.exerciseTakeStopped === true)
        const clockTakeN =
          typeof clock.exerciseTakeNumber === 'number' && Number.isFinite(clock.exerciseTakeNumber)
            ? Math.max(0, Math.floor(clock.exerciseTakeNumber))
            : takeNumberRef.current
        // Between takes / before Run 1: ignore/clear freeze — story preview is not IR.
        if (takeFinished || clockTakeN <= 0) {
          applyInstantReplayPauseFromWire(null, true)
          if (instantReplayEngagedRef.current) setInstantReplayEngaged(false)
          if (replayPlayingRef.current) setReplayPlaying(false)
          commitSessionFields((prev) => {
            if (
              prev.instantReplayPauseMs == null &&
              prev.instantReplayPlaying == null &&
              prev.instantReplayEngaged == null
            ) {
              return prev
            }
            const next: ScenarioSession = { ...prev }
            delete next.instantReplayPauseMs
            delete next.instantReplayPlaying
            delete next.instantReplayEngaged
            return next
          })
        } else {
          applyInstantReplayPauseFromWire(clock.instantReplayPauseMs ?? null, true)
          const pauseMs = clock.instantReplayPauseMs
          commitSessionFields((prev) => {
            if (pauseMs == null) {
              if (
                prev.instantReplayPauseMs == null &&
                prev.instantReplayPlaying == null &&
                prev.instantReplayEngaged == null
              ) {
                return prev
              }
              const next: ScenarioSession = { ...prev }
              delete next.instantReplayPauseMs
              delete next.instantReplayPlaying
              delete next.instantReplayEngaged
              return next
            }
            const n = Math.max(0, Math.floor(pauseMs))
            if (prev.instantReplayPauseMs === n) return prev
            return { ...prev, instantReplayPauseMs: n }
          })
        }
      }
      const takeFinishedForIr =
        clock.exerciseTakeStopped === true ||
        (typeof clock.exerciseTakeStopped !== 'boolean' &&
          snapshotRef.current.session.exerciseTakeStopped === true) ||
        Boolean(tapeReviewRef.current)
      const clockTakeN =
        typeof clock.exerciseTakeNumber === 'number' && Number.isFinite(clock.exerciseTakeNumber)
          ? Math.max(0, Math.floor(clock.exerciseTakeNumber))
          : takeNumberRef.current
      // Between-takes / finished-run tape review is hub Resume/Pause — never mid-take IR.
      // Stale Clock.instantReplayPlaying=true used to re-engage IR, block the RAF ticker,
      // and steal Space onto toggleReplayPlaying (bar frozen; Space pause then no resume).
      if (takeFinishedForIr || clockTakeN <= 0) {
        if (
          instantReplayEngagedRef.current ||
          replayPlayingRef.current ||
          instantReplayPauseMsRef.current != null
        ) {
          instantReplayPauseMsRef.current = null
          setInstantReplayPauseMs(null)
          setInstantReplayEngaged(false)
          setReplayPlaying(false)
        }
      } else if (typeof clock.instantReplayEngaged === 'boolean') {
        setInstantReplayEngaged(clock.instantReplayEngaged)
        if (!clock.instantReplayEngaged) setReplayPlaying(false)
      }
      if (
        !takeFinishedForIr &&
        clockTakeN > 0 &&
        typeof clock.instantReplayPlaying === 'boolean'
      ) {
        const wantPlay = clock.instantReplayPlaying
        const intent = irTapePlayingIntentRef.current
        // Operator: ignore opposite playing until our Seek confirms (concurrency can
        // deliver an older startTape Clock after pauseTape). Spectators always apply.
        if (
          canOperateSessionRef.current &&
          intent != null &&
          wantPlay !== intent.playing
        ) {
          /* keep local intent; playhead still applied below */
        } else {
          if (intent != null && wantPlay === intent.playing) {
            irTapePlayingIntentRef.current = null
            // Intent confirmed — allow owns-head release (held while pause/play in flight).
            releaseHubSeekOwnsHeadUnlessPlayPending()
          }
          const dur = displayDurRef.current
          const headMs = clock.anchorStoryTimeIso
            ? Math.max(
                0,
                storyDeltaMs(
                  startsAt ?? snapshotRef.current.session.storyStartTime,
                  clock.anchorStoryTimeIso
                )
              )
            : elapsedRef.current
          const freeze = instantReplayPauseMsRef.current
          const tapeEnd = freeze != null ? freeze : dur
          const clockAtEnd = tapeEnd > 0 && headMs >= tapeEnd - 10
          if (wantPlay && clockAtEnd) {
            // Local stop only — pauseTape Seek owns hub playing=false.
            replayPlayingRef.current = false
            setReplayPlaying(false)
          } else {
            replayPlayingRef.current = wantPlay
            setReplayPlaying(wantPlay)
            if (wantPlay) setInstantReplayEngaged(true)
          }
        }
      }
    }
    const start = startsAt ?? snapshotRef.current.session.storyStartTime
    const anchorOnBoard =
      Boolean(clock.anchorStoryTimeIso) &&
      storyDeltaMs(start, clock.anchorStoryTimeIso as string) >= -1000
    let stamped: StudioClock = {
      ...clock,
      storyOriginIso: anchorOnBoard
        ? start
        : prevClock?.storyOriginIso ||
          clock.anchorStoryTimeIso ||
          prevClock?.anchorStoryTimeIso ||
          null,
    }
    const playhead = boardPlayheadIso(stamped, start)
    const holdingStop = stopHoldIdRef.current === instanceKey && clock.lifecycle !== 'completed'
    if (stopHoldIdRef.current === instanceKey && clock.lifecycle === 'completed') {
      stopHoldIdRef.current = null
    }
    // Reset hold: ignore every clock echo until the Pause→Seek→Pause chain finishes.
    const holdingReset = pauseHoldIdRef.current === instanceKey && clock.lifecycle !== 'completed'
    const holdingTransportPause =
      transportPauseHoldIdRef.current === instanceKey && clock.lifecycle !== 'completed'
    const holdingTransportPlay =
      transportPlayHoldIdRef.current === instanceKey && clock.lifecycle !== 'completed'
    const holdingGoLive =
      goLiveHoldIdRef.current === instanceKey && clock.lifecycle !== 'completed'
    // A template on screen keeps its own clock. The live instance still drives Running/Paused.
    // A stop we already painted stays completed until the server agrees.
    const viewingTemplateNow = Boolean(previewTemplateIdRef.current)
    if (holdingGoLive && !viewingTemplateNow) {
      // Final Pause after Start — Live Idle. Clear hold and apply normally.
      if (clock.lifecycle === 'running' && clock.clockStatus === 'paused') {
        goLiveHoldIdRef.current = null
      } else {
        // Planned Pause/Seek, or Start’s brief running — keep Live Idle painted.
        const pinned: StudioClock = {
          ...stamped,
          lifecycle: 'running',
          clockStatus: 'paused',
          anchorStoryTimeIso: start,
          anchorActualTime: null,
          storyOriginIso: start,
          actualStartTime: clock.actualStartTime ?? prevClock?.actualStartTime ?? null,
        }
        clocksRef.current.set(instanceKey, pinned)
        serverClockRef.current = pinned
        setServerClock(pinned)
        setElapsedMs(0)
        elapsedRef.current = 0
        setRunning(false)
        setStarted(true)
        setStopped(false)
        setInstances((prev) =>
          prev.map((i) =>
            i.id === instanceKey
              ? {
                  ...i,
                  lifecycle: 'running',
                  clockStatus: 'paused',
                  studioClock: pinned,
                  playheadStoryIso: start,
                  actualStartTime: pinned.actualStartTime ?? i.actualStartTime,
                }
              : i
          )
        )
        applyNote('hold-go-live-pin')
        return
      }
    }
    if (holdingReset && !viewingTemplateNow) {
      const keepPlanned = clock.lifecycle === 'planned'
      const pinned: StudioClock = {
        ...stamped,
        lifecycle: keepPlanned ? 'planned' : stamped.lifecycle,
        clockStatus: 'paused',
        anchorStoryTimeIso: start,
        anchorActualTime: null,
        storyOriginIso: start,
      }
      clocksRef.current.set(instanceKey, pinned)
      serverClockRef.current = pinned
      setServerClock(pinned)
      setElapsedMs(0)
      elapsedRef.current = 0
      setRunning(false)
      setStarted(!keepPlanned)
      setStopped(false)
      setInstances((prev) =>
        prev.map((i) =>
          i.id === instanceKey
            ? {
                ...i,
                lifecycle: keepPlanned ? 'planned' : 'running',
                clockStatus: 'paused',
                studioClock: pinned,
                playheadStoryIso: start,
                actualStartTime: clock.actualStartTime ?? i.actualStartTime,
              }
            : i
        )
      )
      applyNote('hold-reset-pin')
      return
    }
    if (holdingTransportPlay && !viewingTemplateNow) {
      if (clock.clockStatus === 'running') {
        // Resume / PreviewPlay landed — apply normally below after clearing the hold.
        transportPlayHoldIdRef.current = null
        hubAdmittedRunningRef.current = true
      } else if (
        // Late paused Seek/Clock-sync after play RPC returned but before/after this
        // running Clock — never park the board on paused @ start while we still
        // intend to play (no active pause hold). (Already in the non-running branch.)
        hubAdmittedRunningRef.current &&
        runningRef.current &&
        transportPauseHoldIdRef.current !== instanceKey
      ) {
        applyNote('ignore-paused-during-transport-play')
        return
      } else if (
        // Pause already won locally (Space / bar) — never resurrect Running over a
        // paused Clock. Only honor an *active* pause hold — not a stale desire from
        // an earlier Review scrub (that falsely painted Paused mid-Run).
        transportPauseHoldIdRef.current === instanceKey
      ) {
        transportPlayHoldIdRef.current = null
        hubAdmittedRunningRef.current = false
        // Fall through — apply paused Clock status + playhead.
      } else {
        // SetRate/Seek echoed while Resume still in flight — keep Play/Run painted.
        // Use prevClock (optimistic), not stamped — stamped was the echo and still has the
        // pre-Seek stop time (Run again would flash 10:54 after painting start).
        const keepPlanned = clock.lifecycle === 'planned' || stamped.lifecycle === 'planned'
        const holdFromOptimistic = prevClock?.clockStatus === 'running'
        // While owns-head (reset→play Seek chain), keep the local freeze head — do not
        // adopt boardPlayheadIso(optimistic), which advances from click time and fights
        // the RAF freeze (visible ±1px jitter at story start).
        const holdPlayhead = hubSeekOwnsHeadRef.current
          ? addStoryMs(start, elapsedRef.current)
          : holdFromOptimistic
            ? boardPlayheadIso(prevClock, start)
            : playhead
        const pinned: StudioClock = {
          ...stamped,
          lifecycle: keepPlanned ? 'planned' : 'running',
          clockStatus: 'running',
          // Pin anchors to the display head so boardPlayheadIso cannot race ahead of
          // owns-head and snap the plane backward when ownership is released.
          anchorStoryTimeIso: holdPlayhead,
          anchorActualTime: new Date().toISOString(),
          storyOriginIso: holdFromOptimistic
            ? prevClock.storyOriginIso || prevClock.anchorStoryTimeIso
            : stamped.storyOriginIso,
          rate: holdFromOptimistic ? prevClock.rate : stamped.rate,
        }
        clocksRef.current.set(instanceKey, pinned)
        serverClockRef.current = pinned
        setServerClock(pinned)
        if (!hubSeekOwnsHeadRef.current) {
          setElapsedMs(Math.max(0, storyDeltaMs(start, holdPlayhead)))
          elapsedRef.current = Math.max(0, storyDeltaMs(start, holdPlayhead))
        }
        setRunning(true)
        setStarted(!keepPlanned)
        setInstances((prev) =>
          prev.map((i) =>
            i.id === instanceKey
              ? {
                  ...i,
                  lifecycle: keepPlanned ? 'planned' : 'running',
                  clockStatus: 'running',
                  studioClock: pinned,
                  playheadStoryIso: holdPlayhead,
                }
              : i
          )
        )
        applyNote('hold-transport-play-pin')
        return
      }
    }
    // Our own late Seek/Clock-sync after Play: a paused echo we sent must not
    // rewind a live clock. A Pause or Seek from another Owner is the room head
    // and must paint — canOperate is not "I am the only driver."
    // Intentional Pause clears runningRef and sets transportPauseHold first.
    if (
      canOperateSessionRef.current &&
      !viewingTemplateNow &&
      !holdingTransportPause &&
      stamped.clockStatus !== 'running' &&
      hubAdmittedRunningRef.current &&
      runningRef.current &&
      transportPauseHoldIdRef.current !== instanceKey &&
      clientSentClockTrace(trace)
    ) {
      applyNote('ignore-paused-while-playing', {
        incomingStatus: stamped.clockStatus,
        ownEcho: true,
      })
      return
    }
    // Do not abort Clock apply during localPreviewDriveUntil — that used to
    // noteScenarioSeq + return and drop Pause/Resume status while leaving
    // clocksRef Running, so A's ticker kept advancing with no hub traffic.
    // Playhead yank is skipped below via previewEchoYank / operatorOwnsHead.
    // While coalesced Seek is in flight, operator keeps the *display* head local.
    // Do not rewrite clocksRef anchors — that poisoned boardPlayheadIso after release
    // (A became stair-steppy while P stayed smooth on hub interpolation).
    let appliedPlayhead = playhead
    if (
      canOperateSessionRef.current &&
      hubSeekOwnsHeadRef.current &&
      !viewingTemplateNow
    ) {
      const incomingElapsed = Math.max(0, storyDeltaMs(start, playhead))
      const localElapsed = elapsedRef.current
      // Paused Seek / Clock-sync while play still owns the head — keep the
      // optimistic running clock so boardPlayheadIso cannot snap to story start.
      if (
        stamped.clockStatus !== 'running' &&
        (runningRef.current || transportPlayHoldIdRef.current === instanceKey) &&
        transportPauseHoldIdRef.current !== instanceKey
      ) {
        const pinIso = addStoryMs(start, localElapsed)
        const pinned: StudioClock = {
          ...(prevClock ?? stamped),
          seq: Math.max(prevClock?.seq ?? 0, stamped.seq ?? 0),
          rate: stamped.rate > 0 ? stamped.rate : (prevClock?.rate ?? stamped.rate),
          lifecycle:
            stamped.lifecycle === 'planned' || prevClock?.lifecycle === 'planned'
              ? 'planned'
              : (prevClock?.lifecycle ?? stamped.lifecycle),
          clockStatus: 'running',
          anchorStoryTimeIso: pinIso,
          anchorActualTime: new Date().toISOString(),
          storyOriginIso:
            prevClock?.storyOriginIso || prevClock?.anchorStoryTimeIso || stamped.storyOriginIso,
        }
        clocksRef.current.set(instanceKey, pinned)
        serverClockRef.current = pinned
        setServerClock(pinned)
        setRunning(true)
        setInstances((prev) =>
          prev.map((i) =>
            i.id === instanceKey
              ? {
                  ...i,
                  clockStatus: 'running',
                  studioClock: pinned,
                  playheadStoryIso: pinIso,
                }
              : i
          )
        )
        applyNote('keep-running-clock-during-owns-head', {
          incomingElapsed,
          localElapsed,
        })
        return
      }
      // PreviewPlay/Resume Clock: adopt hub head and drop owns-head on the event
      // (not pump await). hubAdmittedRunning means the play RPC already returned.
      const deckSeekInFlightForRelease =
        deckScrubSeekBusyRef.current || deckScrubPendingMsRef.current != null
      if (
        stamped.clockStatus === 'running' &&
        transportPauseHoldIdRef.current !== instanceKey &&
        !deckSeekInFlightForRelease &&
        (hubAdmittedRunningRef.current ||
          Math.abs(incomingElapsed - localElapsed) <= 250 ||
          (beatNavAnchorMsRef.current != null &&
            Math.abs(incomingElapsed - beatNavAnchorMsRef.current) <= 250))
      ) {
        const rate = stamped.rate > 0 ? stamped.rate : localPlayAnchorRef.current.rate
        localPlayAnchorRef.current = {
          wall: performance.now(),
          elapsed: incomingElapsed,
          rate,
        }
        elapsedRef.current = incomingElapsed
        setElapsedMs((prev) =>
          Math.abs(prev - incomingElapsed) < 40 ? prev : incomingElapsed
        )
        appliedPlayhead = playhead
        hubSeekOwnsHeadRef.current = false
        hubAdmittedRunningRef.current = true
        applyNote('release-seek-owns-head', {
          incomingElapsed,
          localElapsed,
          via: 'running-clock',
        })
      } else {
        appliedPlayhead = addStoryMs(start, localElapsed)
        // Server caught up to (or past, while playing) our seek — release ownership.
        // Keep owns-head while the deck Seek lane still has work (rapid →→→).
        let releasedOwnsHead = false
        const deckSeekInFlight =
          deckScrubSeekBusyRef.current || deckScrubPendingMsRef.current != null
        if (
          !deckSeekInFlight &&
          !studioClockPumpRef.current &&
          !studioClockDesireRef.current &&
          (Math.abs(incomingElapsed - localElapsed) <= 250 ||
            (stamped.clockStatus === 'running' &&
              incomingElapsed >= localElapsed - 250 &&
              beatNavAnchorMsRef.current != null &&
              Math.abs(incomingElapsed - beatNavAnchorMsRef.current) <= 250))
        ) {
          hubSeekOwnsHeadRef.current = false
          releasedOwnsHead = true
          applyNote('release-seek-owns-head', {
            incomingElapsed,
            localElapsed,
          })
        }
        // Rapid end↔start scrub: an older Seek Clock can arrive after Seek(0). If we
        // kept owns-head but still stored the remote extreme in clocksRef, the 800ms
        // release followed boardPlayheadIso(end) and wedged the bar / Space.
        if (
          !releasedOwnsHead &&
          stamped.clockStatus !== 'running' &&
          Math.abs(incomingElapsed - localElapsed) > 250
        ) {
          stamped = {
            ...stamped,
            anchorStoryTimeIso: appliedPlayhead,
            anchorActualTime: null,
          }
          applyNote('pin-owns-head-over-stale-seek', {
            incomingElapsed,
            localElapsed,
          })
        }
      }
    }
    clocksRef.current.set(instanceKey, stamped)
    const prevSrv = serverClockRef.current
    serverClockRef.current = stamped
    // Playhead ticks must not setState — that re-rendered this page on every Clock
    // and stacked updates into max-update-depth. Paint React only on status/phase.
    if (
      prevSrv?.clockStatus !== stamped.clockStatus ||
      prevSrv?.lifecycle !== stamped.lifecycle
    ) {
      setServerClock(stamped)
    }
    // Official run resumed (us or peer) — leave Instant Replay and follow broadcast.
    if (
      (instantReplayEngagedRef.current || instantReplayPauseMsRef.current != null) &&
      clock.clockStatus === 'running' &&
      !holdingTransportPause
    ) {
      instantReplayPauseMsRef.current = null
      setInstantReplayPauseMs(null)
      setInstantReplayEngaged(false)
      setReplayPlaying(false)
      replaySavedRateRef.current = null
    }
    // Strip speed follows Clock.rate for everyone on the board (Preview + Live).
    // While in IR (or just after restoring), keep the local / restored rate.
    if (
      !instantReplayEngagedRef.current &&
      performance.now() >= rateRestoreUntilRef.current &&
      !previewTemplateIdRef.current &&
      instanceKey === instanceIdRef.current &&
      Number.isFinite(clock.rate) &&
      clock.rate > 0
    ) {
      const dur = Math.max(1, displayDurationMs(snapshotRef.current.session))
      const nextRuntime = Math.round(dur / Math.max(PLAYBACK_RATE_MIN, clock.rate))
      setPlaybackRuntimeMs((prev) => (Math.abs(prev - nextRuntime) < 2 ? prev : nextRuntime))
    }
    if (holdingTransportPause && !viewingTemplateNow) {
      // Optimistic freeze at click time (prevClock). Pause already sent freezeIso to the hub —
      // never corrective-Seek here: that raced StampTakePauseFreeze and false-engaged IR.
      const freezeIso =
        prevClock?.clockStatus === 'paused' && prevClock.anchorStoryTimeIso
          ? prevClock.anchorStoryTimeIso
          : null
      if (clock.clockStatus === 'paused' || clock.clockStatus === 'idle') {
        transportPauseHoldIdRef.current = null
        hubAdmittedRunningRef.current = false
        if (freezeIso) {
          const frozenMs = Math.max(0, storyDeltaMs(start, freezeIso))
          const serverMs = Math.max(0, storyDeltaMs(start, playhead))
          // Prefer click-time head only for small Pause RTT overshoot — not when an
          // older Seek Clock arrives during rapid ← (server far from local beat head).
          const pauseRttOvershoot =
            serverMs > frozenMs + 120 && serverMs - frozenMs < 2500
          if (pauseRttOvershoot && !hubSeekOwnsHeadRef.current) {
            const pinned: StudioClock = {
              ...stamped,
              clockStatus: 'paused',
              anchorStoryTimeIso: freezeIso,
              anchorActualTime: null,
              storyOriginIso: prevClock?.storyOriginIso || stamped.storyOriginIso,
            }
            clocksRef.current.set(instanceKey, pinned)
            serverClockRef.current = pinned
            setServerClock(pinned)
            setElapsedMs(frozenMs)
            elapsedRef.current = frozenMs
            setRunning(false)
            setInstances((prev) =>
              prev.map((i) =>
                i.id === instanceKey
                  ? {
                      ...i,
                      clockStatus: 'paused',
                      studioClock: pinned,
                      playheadStoryIso: freezeIso,
                    }
                  : i
              )
            )
            applyNote('hold-pause-prefer-local-freeze', { frozenMs, serverMs })
            return
          }
        }
      } else {
        // Running echoes during Pause settle — do not advance the head (stop jump-forward).
        const pinIso = freezeIso ?? addStoryMs(start, elapsedRef.current)
        const pinned: StudioClock = {
          ...stamped,
          clockStatus: 'paused',
          anchorStoryTimeIso: pinIso,
          anchorActualTime: null,
          storyOriginIso: prevClock?.storyOriginIso || stamped.storyOriginIso,
        }
        clocksRef.current.set(instanceKey, pinned)
        serverClockRef.current = pinned
        setServerClock(pinned)
        setElapsedMs(Math.max(0, storyDeltaMs(start, pinIso)))
        setRunning(false)
        setInstances((prev) =>
          prev.map((i) =>
            i.id === instanceKey
              ? {
                  ...i,
                  clockStatus: 'paused',
                  studioClock: pinned,
                  playheadStoryIso: pinIso,
                }
              : i
          )
        )
        applyNote('hold-pause-ignore-running-echo')
        return
      }
    }
    // Room clock core: only the *operator* may ignore Clock while owning the head.
    // Spectators always apply hub playhead + IR flags (no localReplayDrive / drift veto).
    // versionSwitchHoldRef always owns the head (Preview drivers switch versions too) so a
    // late Pause Clock cannot re-drive Auto-track to the prior last camera beat mid-settle.
    // IR tape play: always own the head — progress Seek Clocks are ~RTT behind the local
    // ticker; applying them stair-steps the bar forward/back (same class as Running RAF).
    const operatorOwnsHead =
      Boolean(versionSwitchHoldRef.current) ||
      seriesPreviewActiveRef.current ||
      (canOperateSessionRef.current &&
        (hubSeekOwnsHeadRef.current ||
          (instantReplayEngagedRef.current && replayPlayingRef.current) ||
          (instantReplayEngagedRef.current &&
            clock.clockStatus !== 'running' &&
            performance.now() < localReplayDriveUntilRef.current)))
    const localPreviewDrive =
      canOperateSessionRef.current &&
      !instantReplayEngagedRef.current &&
      performance.now() < localPreviewDriveUntilRef.current &&
      (runningRef.current || clock.clockStatus === 'running')
    if (!holdingStop && !viewingTemplateNow) {
      if (forcePeerReviewingAtStop) {
        const stopMs =
          finishedRunStopMsRef.current ?? tapeAvailableUntilMsRef.current ?? 0
        applyNote('force-peer-reviewing-at-stop', {
          hubElapsed: Math.max(0, storyDeltaMs(start, playhead)),
          stopMs,
        })
        const park = stopMs > 250 ? stopMs : Math.max(0, storyDeltaMs(start, playhead))
        elapsedRef.current = park
        setElapsedMs(park)
        beatNavAnchorMsRef.current = park
        hubWirePlayheadMsRef.current = park
        setHubWirePlayheadMs(park)
      } else if (!operatorOwnsHead) {
        const nextElapsed = Math.max(0, storyDeltaMs(start, playhead))
        // Only ignore *running* echoes while scrubbing. Pausing on scrub-begin used to
        // pin the old head (~90s); skipping the follow-up Seek(0) Clock left the bar
        // at 0 for ~1s then snapped forward when that Pause Clock won.
        const previewEchoYank =
          localPreviewDrive &&
          clock.clockStatus === 'running' &&
          Math.abs(nextElapsed - elapsedRef.current) > 250
        const stopYankToStart = staleStoryStartYankAfterTakeStop(nextElapsed)
        // Late Running after StopRun — keep Reviewing parked at the stop stamp.
        const staleStopPlayhead =
          clock.clockStatus === 'running' &&
          staleRunningAfterTakeStop(nextElapsed, instanceKey)
        const stopParkMs =
          finishedRunStopMsRef.current ?? tapeAvailableUntilMsRef.current
        const reviewingStopPin =
          exerciseRunStoppedRef.current &&
          !awaitingStartRunRunningRef.current &&
          performance.now() < reviewingStopPinUntilRef.current &&
          stopParkMs != null &&
          stopParkMs > 250 &&
          Math.abs(nextElapsed - stopParkMs) > 400
        // Rapid Reviewing ←→ / scrub: ignore Clock off the local deck target until
        // settle (intermediate or rewound Seek Clocks used to tug the thumb).
        const deckTargetMs =
          deckScrubPendingMsRef.current ??
          beatNavAnchorMsRef.current ??
          lastSentHubSeekMsRef.current
        const deckSeekOffTarget =
          canOperateSessionRef.current &&
          !instantReplayEngagedRef.current &&
          !awaitingStartRunRunningRef.current &&
          performance.now() < deckSeekProtectUntilRef.current &&
          deckTargetMs != null &&
          Math.abs(nextElapsed - deckTargetMs) > 250
        if (staleStopPlayhead || reviewingStopPin) {
          const park =
            stopParkMs != null && stopParkMs > 250
              ? stopParkMs
              : elapsedRef.current
          applyNote(
            staleStopPlayhead
              ? 'skip-stale-running-playhead-after-take-stop'
              : 'hold-reviewing-stop-pin',
            { nextElapsed, localElapsed: elapsedRef.current, park }
          )
          elapsedRef.current = park
          setElapsedMs(park)
        } else if (deckSeekOffTarget) {
          applyNote('skip-off-target-deck-seek-clock', {
            nextElapsed,
            deckTargetMs,
            localElapsed: elapsedRef.current,
          })
        } else if (!previewEchoYank && !stopYankToStart) {
          // Spectator IR tape: cadence Seeks are SoT posts ("A is here"), not jump
          // cuts — ignore their playhead while interpolating. Intentional jumps
          // (beat skip / scrub) bump instantReplayAlignSeq on the hub; snap then.
          // Also snap on play-start and pause (playing flag edge).
          const alignSeq =
            typeof clock.instantReplayAlignSeq === 'number' &&
            Number.isFinite(clock.instantReplayAlignSeq)
              ? Math.max(0, Math.floor(clock.instantReplayAlignSeq))
              : null
          // Monotonic align: never rewind applied seq on a late older Clock (that
          // re-armed a stale head after rapid ←→). Only reset on clear (null / 0).
          if (alignSeq == null || alignSeq === 0) {
            irAlignSeqAppliedRef.current = 0
          }
          const alignBump =
            alignSeq != null && alignSeq > irAlignSeqAppliedRef.current
          const staleAlign =
            instantReplayEngagedRef.current &&
            alignSeq != null &&
            alignSeq > 0 &&
            alignSeq < irAlignSeqAppliedRef.current
          const spectatorIrCadence =
            !canOperateSessionRef.current &&
            instantReplayEngagedRef.current &&
            clock.clockStatus !== 'running' &&
            irPlayingBeforeClock &&
            clock.instantReplayPlaying !== false &&
            !alignBump
          if (staleAlign) {
            applyNote('skip-stale-ir-align-playhead', {
              nextElapsed,
              localElapsed: elapsedRef.current,
              alignSeq,
              applied: irAlignSeqAppliedRef.current,
            })
          } else if (spectatorIrCadence) {
            applyNote('skip-ir-cadence-seek-playhead', {
              nextElapsed,
              localElapsed: elapsedRef.current,
            })
          } else {
            // Pause@stop from StopRun — clear the pin once hub confirms the park.
            if (
              exerciseRunStoppedRef.current &&
              clock.clockStatus !== 'running' &&
              stopParkMs != null &&
              Math.abs(nextElapsed - stopParkMs) <= 400
            ) {
              reviewingStopPinUntilRef.current = 0
            }
            elapsedRef.current = nextElapsed
            setElapsedMs((prev) => (Math.abs(prev - nextElapsed) < 40 ? prev : nextElapsed))
            if (alignSeq != null && alignSeq >= irAlignSeqAppliedRef.current) {
              irAlignSeqAppliedRef.current = alignSeq
            }
            if (
              !canOperateSessionRef.current &&
              instantReplayEngagedRef.current &&
              (alignBump ||
                (!irPlayingBeforeClock && clock.instantReplayPlaying === true))
            ) {
              spectatorTapeAnchorRef.current = {
                wall: performance.now(),
                elapsed: nextElapsed,
              }
            }
          }
        } else {
          applyNote(
            stopYankToStart ? 'stale-story-start-yank-after-take-stop' : 'skip-preview-echo-yank',
            { nextElapsed, localElapsed: elapsedRef.current }
          )
        }
      }
      setStarted(clock.lifecycle === 'running')
      setStopped(clock.lifecycle === 'completed')
      if (clock.actualStartTime) setActualStartTime(clock.actualStartTime)
    }
    if (!holdingStop && !viewingTemplateNow) {
      const clockElapsedMs = Math.max(0, storyDeltaMs(start, playhead))
      if (clock.clockStatus === 'running') {
        awaitingStartRunRunningRef.current = false
      } else if (awaitingStartRunRunningRef.current && clockElapsedMs > 400) {
        // Mid-take Pause (not countdown Pause@0) — real operator pause; stop awaiting.
        awaitingStartRunRunningRef.current = false
      }
      const staleTakeRunning =
        clock.clockStatus === 'running' &&
        staleRunningAfterTakeStop(clockElapsedMs, instanceKey)
      const keepStartRunRunning =
        awaitingStartRunRunningRef.current &&
        clock.clockStatus !== 'running' &&
        clockElapsedMs <= 400
      // Never paint Running over an active Instant Replay session.
      const wasLocallyRunning = runningRef.current
      if (instantReplayEngagedRef.current && clock.clockStatus !== 'running') {
        runningRef.current = false
        setRunning(false)
      } else if (keepStartRunRunning) {
        // Countdown Pause after StartRun patch — stay Running · Run N (not Paused at 0).
        runningRef.current = true
        setRunning(true)
        hubAdmittedRunningRef.current = true
        applyNote('keep-running-awaiting-startrun', { clockElapsedMs })
      } else if (staleTakeRunning) {
        // Take stopped + Running still on the stop beat → Instant Replay · Paused.
        runningRef.current = false
        setRunning(false)
        applyNote('stale-running-after-take-stop', {
          clockElapsedMs,
          stopMs: finishedRunStopMsRef.current,
        })
      } else {
        // Hub clockStatus is SoT for running — including Live Reviewing. Peers must
        // never keep local running=true against a paused Clock (side-clock free-run).
        const nextRunning = clock.clockStatus === 'running'
        runningRef.current = nextRunning
        setRunning(nextRunning)
        // Peers skip the story-end effect (operator commits Pause). Mirror host toast
        // when that Pause lands at story end during Planned / Live Idle preview.
        if (
          !canOperateSessionRef.current &&
          wasLocallyRunning &&
          !nextRunning &&
          !instantReplayEngagedRef.current &&
          !previewTemplateIdRef.current &&
          takeNumberRef.current <= 0 &&
          !exerciseRunStoppedRef.current
        ) {
          const dur = displayDurRef.current
          if (dur > 0 && clockElapsedMs >= dur - 80) {
            pushInfoToast('Preview complete.', false, 2, 2800)
          }
        }
      }
      // Peers stick on Editing from applyRemoteObject; host Play only cleared A locally.
      // PreviewPlay / Resume Clock → leave Editing for Previewing (no-op mid-take).
      if (clock.clockStatus === 'running' && !staleTakeRunning) noteBoardPreviewActivity()
      // Admit hub transport status even while operator owns the playhead.
      if (!holdingTransportPause && !holdingTransportPlay && !holdingGoLive && !holdingReset) {
        if (keepStartRunRunning) {
          hubAdmittedRunningRef.current = true
        } else if (staleTakeRunning) {
          hubAdmittedRunningRef.current = false
        } else {
          hubAdmittedRunningRef.current = clock.clockStatus === 'running'
        }
      }
    }
    if (!holdingStop && prevClock?.lifecycle !== 'completed' && clock.lifecycle === 'completed') {
      setJustCompletedId(instanceKey)
      setCompletedOpen(true)
      // Prefer SessionJson take phase so End/join peers land on the same leftover number.
      const row = instancesRef.current.find((i) => i.id === instanceKey)
      if (typeof row?.session.exerciseTakeNumber === 'number' && Number.isFinite(row.session.exerciseTakeNumber)) {
        setTakeNumber(Math.max(0, Math.floor(row.session.exerciseTakeNumber)))
      }
      if (typeof row?.session.exerciseTakeStopped === 'boolean') {
        setExerciseRunStopped(row.session.exerciseTakeStopped)
      }
      // End → final board (terminal). Don't leave the room stuck on Reviewing · Run N.
      if (tapeReviewRef.current) exitTapeReview({ persist: false })
      setWatchingRunId(null)
      setBoardActivity('preview')
      // canDriveDoneReview is still false this tick (lifecycle not in completedInsts yet).
      if (canOperateSessionRef.current || canDriveDoneReviewRef.current) {
        const id = instanceIdRef.current
        if (id) void hubSetSessionFields(id, { tapeReviewRunId: null }).catch(() => {})
      }
    }
    setInstances((prev) => {
      let changed = false
      const next = prev.map((i) => {
        if (i.id !== instanceKey) return i
        const nextLife = holdingStop ? 'completed' : clock.lifecycle
        const nextStatus = holdingStop ? 'completed' : clock.clockStatus
        if (
          i.lifecycle === nextLife &&
          i.clockStatus === nextStatus &&
          !holdingStop
        ) {
          return i
        }
        changed = true
        const nextClock: StudioClock = holdingStop
          ? {
              ...stamped,
              lifecycle: 'completed',
              clockStatus: 'completed',
              anchorStoryTimeIso: appliedPlayhead,
              anchorActualTime: null,
            }
          : stamped
        return {
          ...i,
          lifecycle: nextLife,
          clockStatus: nextStatus,
          studioClock: nextClock,
          playheadStoryIso: appliedPlayhead,
          actualStartTime: clock.actualStartTime ?? i.actualStartTime,
          actualEndTime: holdingStop
            ? (i.actualEndTime ?? new Date().toISOString())
            : (clock.actualEndTime ?? i.actualEndTime),
        }
      })
      return changed ? next : prev
    })
    applyNote('paint', {
      appliedPlayhead,
      elapsed: elapsedRef.current,
      running: runningRef.current,
      operatorOwnsHead:
        canOperateSessionRef.current &&
        (hubSeekOwnsHeadRef.current ||
          (instantReplayEngagedRef.current && clock.clockStatus !== 'running')),
      hubAdmittedRunning: hubAdmittedRunningRef.current,
    })
  }

  function applyServerSnapshot(snap: ServerSnapshot): void {
    const inst = instanceFromSnapshot(snap)
    if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
    setInstances((prev) => [inst, ...prev.filter((i) => i.id !== inst.id)])
    applyInstance(inst)
    applyServerClock(snap.clock, inst.session.storyStartTime, inst.id)
    try {
      localStorage.setItem(LAST_SERVER_INSTANCE_KEY, inst.id)
    } catch {
      /* private mode */
    }
  }

  function applyServerFields(fields: SessionFieldPatch, seq: number): void {
    // Board seq only — catch-up watermark advances on Clock/Snapshot (see noteScenarioSeq).
    void seq
    if (typeof fields.runRoomPlumes === 'boolean') {
      const prevOn = snapshotRef.current.session.runRoomPlumes === true
      if (prevOn !== fields.runRoomPlumes && liveTakeActiveRef.current) {
        const who =
          typeof fields.runRoomPlumesBy === 'string' && fields.runRoomPlumesBy.trim()
            ? fields.runRoomPlumesBy.trim()
            : 'Someone'
        pushInfoToast(
          `${who} has turned ${fields.runRoomPlumes ? 'on' : 'off'} wind plumes for this run`,
          false,
          3,
          3200
        )
      }
    }
    const {
      intro,
      sessionType,
      wizardOrigin,
      scheduledStartTime,
      scheduledEndTime,
      storyEndTime,
      storyEndDateSet,
      primaryOwnerUserId,
      exerciseTakeNumber,
      exerciseTakeStopped,
      instantReplayPauseMs,
      instantReplayPlaying: instantReplayPlayingField,
      instantReplayEngaged: instantReplayEngagedField,
      tapeReviewRunId: fieldsTapeReviewRunId,
      boardVersions: fieldsBoardVersions,
      exerciseRuns: fieldsExerciseRuns,
      roomChat: fieldsRoomChat,
      selectedVersionId: fieldsSelectedVersionId,
      runStartCountdown: fieldsRunStartCountdown,
      runStartCountdownEndsAt: fieldsRunStartCountdownEndsAt,
      timeline: fieldsTimeline,
      cameraBeats: fieldsCameraBeats,
      maps: fieldsMaps,
      ...rest
    } = fields
    const wiredMaps = coerceSessionMaps(fieldsMaps)
    // Pre-release: ignore void legacy key if a peer still sends it.
    delete (rest as { markedReady?: unknown }).markedReady
    if (Object.prototype.hasOwnProperty.call(fields, 'runStartCountdownEndsAt')) {
      const endsRaw: unknown = fieldsRunStartCountdownEndsAt
      if (endsRaw == null || endsRaw === '') {
        if (!runCountdownDriverRef.current) {
          runCountdownEndsAtRef.current = null
          setRunCountdown(null)
        }
      } else if (typeof endsRaw === 'string' && endsRaw.trim()) {
        const endsMs = Date.parse(endsRaw.trim())
        if (Number.isFinite(endsMs) && !runCountdownDriverRef.current) {
          const digit = runCountdownDigitFromEndsAt(endsMs)
          // Expired endsAt must clear — `digit ?? 1` used to re-arm "1" until StartRun
          // RTT cleared the field, leaving P on 1 an extra second past A's overlay.
          if (digit == null) {
            runCountdownEndsAtRef.current = null
            setRunCountdown(null)
          } else {
            runCountdownEndsAtRef.current = endsMs
            setRunCountdown(digit)
            // Match operator countdown arm: pin @ T0 so P does not keep a Reviewing
            // end-head through 5…4…3 while A already reset (StartRun Clock is later).
            if (!canOperateSessionRef.current) {
              exitTapeReview({ persist: false })
              setWatchingRunId(null)
              finishedRunStopMsRef.current = null
              reviewingStopPinUntilRef.current = 0
              elapsedRef.current = 0
              setElapsedMs(0)
              beatNavAnchorMsRef.current = 0
              hubWirePlayheadMsRef.current = 0
              setHubWirePlayheadMs(0)
              runningRef.current = false
              setRunning(false)
              setBoardActivity('ready')
              const openId = instanceIdRef.current
              const startIso = snapshotRef.current.session.storyStartTime
              if (openId && startIso) {
                const prev = clocksRef.current.get(openId)
                if (prev && prev.clockStatus !== 'running') {
                  const pinned: StudioClock = {
                    ...prev,
                    clockStatus: 'paused',
                    anchorStoryTimeIso: startIso,
                    anchorActualTime: null,
                  }
                  clocksRef.current.set(openId, pinned)
                  serverClockRef.current = pinned
                  setServerClock(pinned)
                }
              }
            }
          }
        }
      }
    } else if (Object.prototype.hasOwnProperty.call(fields, 'runStartCountdown')) {
      // Legacy: digit-only Fields (pre–endsAt) — peers lag ~1s + RTT per tick.
      const cdRaw: unknown = fieldsRunStartCountdown
      if (cdRaw == null) {
        if (!runCountdownDriverRef.current) {
          runCountdownEndsAtRef.current = null
          setRunCountdown(null)
        }
      } else if (typeof cdRaw === 'number' && Number.isFinite(cdRaw) && cdRaw > 0) {
        if (!runCountdownDriverRef.current && runCountdownEndsAtRef.current == null) {
          setRunCountdown(Math.max(1, Math.floor(cdRaw)))
        }
      }
    }
    const takeNumRaw: unknown = exerciseTakeNumber
    const takeStoppedRaw: unknown = exerciseTakeStopped
    let takeNumWire =
      typeof takeNumRaw === 'number' && Number.isFinite(takeNumRaw)
        ? Math.max(0, Math.floor(takeNumRaw))
        : typeof takeNumRaw === 'string' && /^\d+$/.test(takeNumRaw.trim())
          ? Math.max(0, Number(takeNumRaw.trim()))
          : null
    let takeStoppedWire =
      typeof takeStoppedRaw === 'boolean'
        ? takeStoppedRaw
        : takeStoppedRaw === 'true' || takeStoppedRaw === 1
          ? true
          : takeStoppedRaw === 'false' || takeStoppedRaw === 0
            ? false
            : null
    {
      const admitted = admitTakePhaseWire(takeNumWire, takeStoppedWire)
      takeNumWire = admitted.take
      takeStoppedWire = admitted.stopped
    }
    const wasStoppedFields = exerciseRunStoppedRef.current
    if (takeNumWire != null) setTakeNumber(takeNumWire)
    if (takeStoppedWire != null) {
      setExerciseRunStopped(takeStoppedWire)
      if (takeStoppedWire === false) finishedRunStopMsRef.current = null
    }
    if (takeStoppedWire === true && !wasStoppedFields && (takeNumWire ?? takeNumberRef.current) > 0) {
      const n = Math.max(1, takeNumWire ?? takeNumberRef.current)
      adoptPeerTakeFinished(n)
      if (!canOperateSessionRef.current) {
        const sess = snapshotRef.current.session
        const finished =
          latestExerciseRunForVersion(sess) ?? latestExerciseRun(sess)
        const localN = versionLocalRunNumberFor(sess, finished) || n
        pushInfoToast(`Run ${localN} finished.`, false, 3, 3600)
      }
    }
    if (Object.prototype.hasOwnProperty.call(fields, 'instantReplayPauseMs')) {
      const pauseRaw: unknown = instantReplayPauseMs
      const pauseWire =
        pauseRaw === null
          ? null
          : typeof pauseRaw === 'number' && Number.isFinite(pauseRaw)
            ? Math.max(0, Math.floor(pauseRaw))
            : undefined
      if (pauseWire !== undefined) applyInstantReplayPauseFromWire(pauseWire, true)
    }
    {
      const takeFinishedForIr =
        snapshotRef.current.session.exerciseTakeStopped === true ||
        Boolean(tapeReviewRef.current)
      if (takeFinishedForIr) {
        if (
          instantReplayEngagedRef.current ||
          replayPlayingRef.current ||
          instantReplayPauseMsRef.current != null ||
          instantReplayEngagedField === true ||
          instantReplayPlayingField === true
        ) {
          instantReplayPauseMsRef.current = null
          setInstantReplayPauseMs(null)
          setInstantReplayEngaged(false)
          setReplayPlaying(false)
        }
      } else {
        if (typeof instantReplayEngagedField === 'boolean') {
          setInstantReplayEngaged(instantReplayEngagedField)
          if (!instantReplayEngagedField) setReplayPlaying(false)
        }
        if (typeof instantReplayPlayingField === 'boolean') {
          setReplayPlaying(instantReplayPlayingField)
          if (instantReplayPlayingField) setInstantReplayEngaged(true)
        }
      }
    }
    const applyNullableSessionFields = (base: ScenarioSession): ScenarioSession => {
      let next: ScenarioSession = { ...base, ...rest }
      if (wiredMaps) next.maps = wiredMaps
      if (typeof intro === 'string') next.intro = intro
      else if (intro === null) delete next.intro
      if (sessionType) next.sessionType = sessionType
      else if (sessionType === null || sessionType === '') delete next.sessionType
      if (wizardOrigin) next.wizardOrigin = wizardOrigin
      else if (wizardOrigin === null) delete next.wizardOrigin
      if (rest.published === false) delete next.published
      if (typeof scheduledStartTime === 'string' && scheduledStartTime)
        next.scheduledStartTime = scheduledStartTime
      else if (scheduledStartTime === null || scheduledStartTime === '')
        delete next.scheduledStartTime
      if (typeof scheduledEndTime === 'string' && scheduledEndTime)
        next.scheduledEndTime = scheduledEndTime
      else if (scheduledEndTime === null || scheduledEndTime === '') delete next.scheduledEndTime
      if (Object.prototype.hasOwnProperty.call(fields, 'storyEndTime')) {
        if (typeof storyEndTime === 'string' && storyEndTime.trim()) next.storyEndTime = storyEndTime.trim()
        else delete next.storyEndTime
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'storyEndDateSet')) {
        if (storyEndDateSet === false) next.storyEndDateSet = false
        else if (storyEndDateSet === true) next.storyEndDateSet = true
        else delete next.storyEndDateSet
      }
      if (typeof primaryOwnerUserId === 'string' && primaryOwnerUserId.trim()) {
        next.primaryOwnerUserId = primaryOwnerUserId.trim()
      } else if (primaryOwnerUserId === null || primaryOwnerUserId === '') {
        delete next.primaryOwnerUserId
      }
      if (takeNumWire != null) next.exerciseTakeNumber = takeNumWire
      if (takeStoppedWire != null) next.exerciseTakeStopped = takeStoppedWire
      if (Object.prototype.hasOwnProperty.call(fields, 'instantReplayPauseMs')) {
        if (instantReplayPauseMs == null) {
          delete next.instantReplayPauseMs
          delete next.instantReplayPlaying
          delete next.instantReplayEngaged
        } else if (typeof instantReplayPauseMs === 'number' && Number.isFinite(instantReplayPauseMs)) {
          next.instantReplayPauseMs = Math.max(0, Math.floor(instantReplayPauseMs))
        }
      }
      if (typeof instantReplayPlayingField === 'boolean') {
        next.instantReplayPlaying = instantReplayPlayingField
      }
      if (typeof instantReplayEngagedField === 'boolean') {
        next.instantReplayEngaged = instantReplayEngagedField
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'tapeReviewRunId')) {
        if (typeof fieldsTapeReviewRunId === 'string' && fieldsTapeReviewRunId.trim()) {
          next.tapeReviewRunId = fieldsTapeReviewRunId.trim()
        } else {
          delete next.tapeReviewRunId
        }
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'runStartCountdown')) {
        if (
          typeof fieldsRunStartCountdown === 'number' &&
          Number.isFinite(fieldsRunStartCountdown) &&
          fieldsRunStartCountdown > 0
        ) {
          next.runStartCountdown = Math.max(1, Math.floor(fieldsRunStartCountdown))
        } else {
          delete next.runStartCountdown
        }
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'runStartCountdownEndsAt')) {
        if (
          typeof fieldsRunStartCountdownEndsAt === 'string' &&
          fieldsRunStartCountdownEndsAt.trim()
        ) {
          next.runStartCountdownEndsAt = fieldsRunStartCountdownEndsAt.trim()
        } else {
          delete next.runStartCountdownEndsAt
        }
      }
      if (Array.isArray(fieldsBoardVersions)) {
        next.boardVersions = fieldsBoardVersions
      }
      if (Array.isArray(fieldsExerciseRuns)) {
        next.exerciseRuns = mergeExerciseRunsFromPeer(base.exerciseRuns, fieldsExerciseRuns)
      }
      if (
        Object.prototype.hasOwnProperty.call(fields, 'roomChat') ||
        Object.prototype.hasOwnProperty.call(fields, 'RoomChat')
      ) {
        const coerced = coerceRoomChat(
          fieldsRoomChat ?? (fields as { RoomChat?: unknown }).RoomChat,
        )
        if (coerced) {
          next.roomChat =
            coerced.length === 0
              ? []
              : mergeRoomChat(base.roomChat, coerced)
        }
      }
      if (Array.isArray(fieldsTimeline)) {
        next.timeline = fieldsTimeline
      }
      // Wire may send null to clear beats; ScenarioSession omits the key instead.
      if (Object.prototype.hasOwnProperty.call(fields, 'cameraBeats')) {
        if (Array.isArray(fieldsCameraBeats) && fieldsCameraBeats.length > 0) {
          next.cameraBeats = fieldsCameraBeats
        } else {
          delete next.cameraBeats
        }
      }
      if (Object.prototype.hasOwnProperty.call(fields, 'selectedVersionId')) {
        if (typeof fieldsSelectedVersionId === 'string' && fieldsSelectedVersionId.trim()) {
          const want = fieldsSelectedVersionId.trim()
          next.selectedVersionId = want
          // Always re-apply seed when the patch names a version — not only on id change.
          // Peers can already have the new id from a partial update while still on the
          // old objects (wrong scene at the shared clock).
          const ver = findBoardVersion(next.boardVersions, want)
          if (ver?.seed) next = applyBoardSeed(next, ver.seed)
        } else {
          delete next.selectedVersionId
        }
      }
      delete (next as { markedReady?: unknown }).markedReady
      // Hub take# without a matching in_progress (missed startRun) — close stale Run 1.
      if (takeNumWire != null && takeNumWire > 0) {
        next = healRunsForHubTake(next, takeNumWire, takeStoppedWire === true)
      }
      return next
    }
    if (!previewTemplateIdRef.current) {
      // Peers only (author uses GroupExcept) — never queue object Upserts on a title/settings patch.
      // adoptAsPersisted when a version seed may replace objects (selectedVersionId flip).
      const versionNamed =
        Object.prototype.hasOwnProperty.call(fields, 'selectedVersionId') &&
        typeof fieldsSelectedVersionId === 'string' &&
        Boolean(fieldsSelectedVersionId.trim())
      const roomChatNamed =
        Object.prototype.hasOwnProperty.call(fields, 'roomChat') ||
        Object.prototype.hasOwnProperty.call(fields, 'RoomChat')
      const fieldsNext =
        versionNamed || roomChatNamed
          ? applyNullableSessionFields(snapshotRef.current.session)
          : null
      commitSessionFields((prev) => applyNullableSessionFields(prev), {
        adoptAsPersisted: Boolean(versionNamed),
      })
      if (fieldsNext) {
        // Keep ref warm before the next Snapshot/Fields so races see the new story.
        snapshotRef.current = { ...snapshotRef.current, session: fieldsNext }
        if (versionNamed) syncReviewFocusForVersion(fieldsNext)
      } else if (
        Array.isArray(fieldsExerciseRuns) &&
        (takeStoppedWire === true || exerciseRunStoppedRef.current)
      ) {
        // Take-stopped often arrives before exerciseRuns[]; pick Reviewing · #N when runs land.
        const withRuns = applyNullableSessionFields(snapshotRef.current.session)
        snapshotRef.current = { ...snapshotRef.current, session: withRuns }
        syncReviewFocusForVersion(withRuns)
      }
      if (typeof fields.title === 'string') setTitleDraft(fields.title)
      if (typeof fields.summary === 'string') setSummaryDraft(fields.summary)
    }
    setInstances((prev) =>
      prev.map((i) => {
        if (i.id !== instanceIdRef.current) return i
        return { ...i, session: applyNullableSessionFields(i.session) }
      })
    )
    // After exerciseRuns land on the session — peers need the run seed to enter tape review.
    // Mid version-switch: ignore a cleared/stale tape id until settle finishes.
    if (
      Object.prototype.hasOwnProperty.call(fields, 'tapeReviewRunId') &&
      !versionSwitchHoldRef.current
    ) {
      const rid =
        typeof fieldsTapeReviewRunId === 'string' && fieldsTapeReviewRunId.trim()
          ? fieldsTapeReviewRunId.trim()
          : null
      adoptTapeReviewRunId(rid)
    }
  }

  function applyAudienceType(
    audienceType: 'private' | 'public',
    seq: number,
    instanceId?: string
  ): void {
    void seq
    const id = instanceId || audienceWriteIdRef.current || instanceIdRef.current
    if (!instanceId || audienceWriteIdRef.current === instanceId) audienceWriteIdRef.current = null
    if (!id) return
    setInstances((prev) => prev.map((i) => (i.id === id ? { ...i, audienceType } : i)))
  }

  function applyAllowLinkGuests(allow: boolean, seq: number, instanceId?: string): void {
    void seq
    const id = instanceId || instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i
        if (allow) return { ...i, allowLinkGuests: true }
        const { allowLinkGuests: _drop, ...rest } = i
        return rest
      })
    )
  }

  function applyComments(comments: ScenarioComment[], seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    persistedCommentsRef.current = JSON.stringify(comments)
    setSession((prev) => ({ ...prev, comments }))
    const id = instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((inst) =>
        inst.id === id ? { ...inst, session: { ...inst.session, comments } } : inst
      )
    )
  }

  function pushInfoToast(
    text: string,
    debug = false,
    rank = debug ? 1 : 2,
    holdMs?: number,
    opts?: { ephemeral?: boolean }
  ): void {
    const pending = toastBurstRef.current
    if (!pending || rank > pending.rank) {
      toastBurstRef.current = { text, debug, rank, holdMs, ephemeral: opts?.ephemeral }
    } else if (pending.rank === rank && holdMs != null) {
      toastBurstRef.current = { ...pending, holdMs, ephemeral: opts?.ephemeral ?? pending.ephemeral }
    }
    if (toastBurstTimerRef.current != null) return
    toastBurstTimerRef.current = window.setTimeout(() => {
      toastBurstTimerRef.current = null
      const chosen = toastBurstRef.current
      toastBurstRef.current = null
      if (!chosen) return
      const id = ++joinToastSeqRef.current
      // D-mode: hold until × so feel/debug lines are readable. Normal / ephemeral: auto-clear.
      const sticky = chosen.ephemeral ? false : debugPanelsOnRef.current
      setJoinToasts([
        {
          id,
          text: chosen.text,
          debug: chosen.debug,
          rank: chosen.rank,
          sticky,
        },
      ])
      if (sticky) return
      window.setTimeout(
        () => {
          setJoinToasts((list) => list.filter((item) => item.id !== id))
        },
        chosen.holdMs ?? (chosen.debug ? 5500 : 4000)
      )
    }, 40)
  }

  function meOnOpenRoster(): ScenarioPerson | undefined {
    if (user.userId?.trim()) {
      const byId = findPersonByUserId(
        peopleRef.current.filter((p) => !p.isDeleted),
        user.userId,
      )
      if (byId) return byId
    }
    const me = (user.email ?? '').trim().toLowerCase()
    if (!me) return undefined
    return peopleRef.current.find(
      (person) => !person.isDeleted && person.email.trim().toLowerCase() === me
    )
  }

  function openBoardIsCompleted(): boolean {
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return false
    return instancesRef.current.find((row) => row.id === id)?.lifecycle === 'completed'
  }

  function notePersonHere(name: string, email: string | null): void {
    const key = email?.trim().toLowerCase()
    if (!key || key === (user.email ?? '').trim().toLowerCase()) return
    // Once per viewer while they stay on this board — not once globally.
    if (presenceAnnouncedRef.current.has(key)) return
    // Done / review: keep pips and viewing count; no arrival toasts.
    if (openBoardIsCompleted()) return
    const row = peopleRef.current.find(
      (person) => !person.isDeleted && person.email.trim().toLowerCase() === key
    )
    // Pending sneak-peek stays silent until Accept. Link guests (no row) and joined seats announce.
    if (row && row.status !== 'joined') return
    const who = (row ? personLabel(row) : null) || shortPersonName(name) || name.trim()
    if (!who) return
    presenceAnnouncedRef.current.add(key)
    if (!row) {
      // Link/Public join — same “is here” line; “as a guest” is admin jargon.
      pushInfoToast(`${who} is here.`, false, 3)
      return
    }
    const suffix = presenceRoleSuffix(row, viewerSeesPresenceRoleSuffix(meOnOpenRoster()))
    pushInfoToast(`${who} is here${suffix}.`, false, 3)
  }

  function invitePresenceGuest(email: string, name: string): void {
    if (!canManagePeople) return
    commitRosterAdds([{ email, name: name.trim() || email }])
  }

  /** Others Accept while already on the board — presence already joined as pending (silent). */
  function notePeopleAccepted(
    prev: readonly ScenarioPerson[],
    next: readonly ScenarioPerson[]
  ): void {
    if (openBoardIsCompleted()) return
    const me = (user.email ?? '').trim().toLowerCase()
    const prevById = new Map(prev.map((person) => [person.id, person]))
    const seeRole = viewerSeesPresenceRoleSuffix(
      next.find((person) => !person.isDeleted && person.email.trim().toLowerCase() === me) ??
        meOnOpenRoster()
    )
    for (const person of next) {
      if (person.isDeleted) continue
      const email = person.email.trim().toLowerCase()
      if (!email || email === me) continue
      const before = prevById.get(person.id)
      if (!before || before.isDeleted) continue
      if (before.status === 'joined' || person.status !== 'joined') continue
      const who = personLabel(person)
      if (!who) continue
      const suffix = presenceRoleSuffix(person, seeRole)
      pushInfoToast(`${who} just joined${suffix}.`, false, 3)
    }
  }

  function publishPresentEmails(): void {
    const next = new Set<string>()
    for (const [email, count] of presentCountsRef.current) {
      if (count > 0) next.add(email)
    }
    setPresentEmails(next)
  }

  function replacePresentEmails(
    emails: readonly string[],
    names?: ReadonlyMap<string, string> | null
  ): void {
    const counts = new Map<string, number>()
    for (const raw of emails) {
      const key = raw.trim().toLowerCase()
      if (!key) continue
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    presentCountsRef.current = counts
    publishPresentEmails()
    if (names) setPresentNameByEmail(names)
  }

  function bumpPresentEmail(email: string, delta: number, name?: string | null): void {
    const key = email.trim().toLowerCase()
    if (!key) return
    const counts = presentCountsRef.current
    const next = (counts.get(key) ?? 0) + delta
    if (next <= 0) counts.delete(key)
    else counts.set(key, next)
    publishPresentEmails()
    const label = name?.trim()
    if (label && next > 0) {
      setPresentNameByEmail((prev) => {
        if (prev.get(key) === label) return prev
        const copy = new Map(prev)
        copy.set(key, label)
        return copy
      })
    }
  }

  function shortPersonName(full: string | null | undefined): string | null {
    if (!full) return null
    const parts = full
      .trim()
      .split(/\s+/)
      .filter((part) => part && !part.startsWith('(') && !part.includes('@'))
    if (!parts.length) return null
    if (parts.length === 1) return parts[0]!
    const last = parts[parts.length - 1]!.replace(/\./g, '')
    if (/^\d+$/.test(last)) return `${parts[0]} ${last}`
    const initial = last[0]
    if (!initial) return parts[0]!
    return `${parts[0]} ${initial.toUpperCase()}.`
  }

  /** The roster stored an address because no account name was known yet. */
  function storedNameIsMailbox(person: ScenarioPerson): boolean {
    const name = person.name.trim()
    if (!name || name.includes('@')) return true
    return name.toLowerCase() === person.email.trim().toLowerCase()
  }

  /** Account first + last for this exact address. */
  function accountNameFor(person: ScenarioPerson): string {
    if (sameLoginEmail(person.email, user.email)) {
      const self = user.name.trim()
      if (self && !self.includes('@')) return self
    }
    const direct = directoryNameByEmail.get(person.email.trim().toLowerCase())
    if (direct && !direct.includes('@')) return direct
    return ''
  }

  function rosterDisplayName(person: ScenarioPerson): string {
    const raw = (person.name ?? '').trim()
    // The typeahead label is not a name. Show the address until registration stores one.
    const placeholder = raw.toLowerCase() === 'invite as a new account'
    if (raw && !raw.includes('@') && !placeholder) return raw
    return accountNameFor(person) || (person.email ?? '').trim()
  }

  function actorPhrase(): string {
    return shortPersonName(user.name ?? '') ?? user.email
  }

  function personLabel(person: ScenarioPerson): string {
    return shortPersonName(rosterDisplayName(person)) ?? person.email
  }

  function roleTitle(role: string): string {
    return role ? role[0]!.toUpperCase() + role.slice(1) : role
  }

  function rosterSummary(
    prev: readonly ScenarioPerson[],
    next: readonly ScenarioPerson[],
    roster: string,
    actor: string | null
  ): string | null {
    const where = roster.trim() || 'this session'
    const lead = actor ? `${actor} ` : ''
    const onThisBoard = Boolean(instanceIdRef.current) && !previewTemplateIdRef.current
    const place = (prep: 'on' | 'to' | 'from') => (onThisBoard ? '' : ` ${prep} ${where}`)
    const prevById = new Map(prev.map((p) => [p.id, p]))
    const nextIds = new Set(next.map((p) => p.id))
    const added: ScenarioPerson[] = []
    const removed: ScenarioPerson[] = []
    const grants = new Map<string, ScenarioPerson[]>()
    const revokes = new Map<string, ScenarioPerson[]>()
    const statusChanges: ScenarioPerson[] = []
    for (const person of next) {
      const before = prevById.get(person.id)
      if (!before) {
        if (!person.isDeleted) added.push(person)
        continue
      }
      if (!before.isDeleted && person.isDeleted) {
        removed.push(person)
        continue
      }
      if (before.isDeleted && !person.isDeleted) {
        added.push(person)
        continue
      }
      if (person.isDeleted) continue
      for (const role of person.roles) {
        if (before.roles.includes(role)) continue
        grants.set(role, [...(grants.get(role) ?? []), person])
      }
      for (const role of before.roles) {
        if (person.roles.includes(role)) continue
        revokes.set(role, [...(revokes.get(role) ?? []), person])
      }
      if (before.status !== person.status) statusChanges.push(person)
    }
    for (const person of prev) {
      if (!nextIds.has(person.id) && !person.isDeleted) removed.push(person)
    }
    const countPhrase = (people: readonly ScenarioPerson[], verb: string) =>
      people.length === 1 ? `${verb} ${personLabel(people[0]!)}` : `${verb} ${people.length} people`
    if (removed.length) return `${lead}${countPhrase(removed, 'removed')}${place('from')}.`
    if (grants.size === 1 && revokes.size === 0) {
      const [role, people] = [...grants.entries()][0]!
      return `${lead}granted ${roleTitle(role)} to ${people.length === 1 ? personLabel(people[0]!) : `${people.length} people`}${place('on')}.`
    }
    if (revokes.size === 1 && grants.size === 0) {
      const [role, people] = [...revokes.entries()][0]!
      return `${lead}revoked ${roleTitle(role)} from ${people.length === 1 ? personLabel(people[0]!) : `${people.length} people`}${place('on')}.`
    }
    if (grants.size || revokes.size) {
      const touched = new Set<string>()
      for (const people of grants.values()) for (const person of people) touched.add(person.id)
      for (const people of revokes.values()) for (const person of people) touched.add(person.id)
      return `${lead}changed roles for ${touched.size === 1 ? '1 person' : `${touched.size} people`}${place('on')}.`
    }
    if (added.length) return `${lead}${countPhrase(added, 'added')}${place('to')}.`
    if (statusChanges.length === 1) {
      const person = statusChanges[0]!
      const verb =
        person.status === 'joined'
          ? 'accepted'
          : person.status === 'declined'
            ? 'declined'
            : person.status
      const short = personLabel(person)
      const actorName = actor?.trim() ?? ''
      const self = actorName === short || actorName.endsWith(` ${short}`)
      if (self) return `${short} ${verb}.`
      return `${lead}marked ${short} ${verb}${place('on')}.`
    }
    if (statusChanges.length > 1) {
      const status = statusChanges.every((p) => p.status === statusChanges[0]!.status)
        ? statusChanges[0]!.status
        : 'updated'
      return status === 'updated'
        ? `${lead}updated ${statusChanges.length} people${place('on')}.`
        : `${lead}marked ${statusChanges.length} people ${status}${place('on')}.`
    }
    return null
  }

  function rosterChangeIsOnlyMe(
    prev: readonly ScenarioPerson[],
    next: readonly ScenarioPerson[]
  ): boolean {
    const me = user.email.trim().toLowerCase()
    if (!me) return false
    const emails = new Set<string>()
    const note = (person: ScenarioPerson) => {
      const key = person.email.trim().toLowerCase()
      if (key) emails.add(key)
    }
    const prevById = new Map(prev.map((person) => [person.id, person]))
    const nextIds = new Set(next.map((person) => person.id))
    for (const person of next) {
      const before = prevById.get(person.id)
      if (!before) {
        if (!person.isDeleted) note(person)
        continue
      }
      const rolesSame = before.roles.join() === person.roles.join()
      if (
        before.isDeleted !== person.isDeleted ||
        before.status !== person.status ||
        before.hasBeenInvited !== person.hasBeenInvited ||
        before.lastInvitedAtIso !== person.lastInvitedAtIso ||
        !rolesSame
      ) {
        note(person)
      }
    }
    for (const person of prev) {
      if (!nextIds.has(person.id) && !person.isDeleted) note(person)
    }
    return emails.size === 1 && emails.has(me)
  }

  function myAccessLine(
    prev: readonly ScenarioPerson[],
    next: readonly ScenarioPerson[],
    actor: string | null
  ): string | null {
    const me = user.email.trim().toLowerCase()
    if (!me) return null
    const before = prev.find(
      (person) => person.email.trim().toLowerCase() === me && !person.isDeleted
    )
    const after = next.find(
      (person) => person.email.trim().toLowerCase() === me && !person.isDeleted
    )
    if (!before || !after) return null
    const granted = after.roles.filter(
      (role) => role !== 'owner' && role !== 'participant' && !before.roles.includes(role)
    )
    const revoked = before.roles.filter(
      (role) => role !== 'owner' && role !== 'participant' && !after.roles.includes(role)
    )
    if (!granted.length && !revoked.length) return null
    const who = actor?.trim() || 'Someone'
    const article = (role: string) => (/^[aeiou]/i.test(role) ? 'an' : 'a')
    if (granted.length === 1 && !revoked.length) {
      const role = granted[0]!
      return `${who} made you ${article(role)} ${role}.`
    }
    if (revoked.length === 1 && !granted.length) {
      const role = revoked[0]!
      return `You are no longer ${article(role)} ${role}.`
    }
    if (!revoked.length) return `${who} has updated your access.`
    return 'Your access has changed.'
  }

  function narrateRoster(next: ScenarioPerson[], actor: string | null): void {
    // Accept while present: toast for others (pending sneak-peek stays silent until this).
    notePeopleAccepted(peopleRef.current, next)
    // Temporarily quiet — invite toasts only. Flip on to restore add/grant chatter.
    const rosterChatterToasts = false
    if (rosterChatterToasts) {
      const mine = myAccessLine(peopleRef.current, next, actor)
      if (mine) pushInfoToast(mine, true)
      else if (!rosterChangeIsOnlyMe(peopleRef.current, next)) {
        const line = rosterSummary(peopleRef.current, next, sessionTitleRef.current, actor)
        if (line) pushInfoToast(line, true)
      }
    }
    peopleRef.current = next
  }

  function rolePlayLine(
    before: readonly ScenarioObject[],
    after: readonly ScenarioObject[],
    actor: string | null
  ): string | null {
    const meId = peopleRef.current.find(
      (row) => !row.isDeleted && sameLoginEmail(row.email, user.email)
    )?.id
    const beforeById = new Map(
      before.map((object) => [object.id, object.rolePlayAssigneeId?.trim() || ''])
    )
    const assigned: { who: string; label: string; isMe: boolean }[] = []
    const cleared: { who: string; label: string; isMe: boolean }[] = []
    for (const object of after) {
      const nextId = object.rolePlayAssigneeId?.trim() || ''
      const prevId = beforeById.get(object.id) ?? ''
      if (nextId === prevId) continue
      const prevPerson = prevId ? personForRolePlayAssignee(peopleRef.current, prevId) : undefined
      const nextPerson = nextId ? personForRolePlayAssignee(peopleRef.current, nextId) : undefined
      if (prevPerson && nextPerson && prevPerson.id === nextPerson.id) continue
      const slot = slotForObject(object)
      if (!slot) continue
      const personId = nextId || prevId
      const person = peopleRef.current.find((row) => row.id === personId)
      const who = person ? personLabel(person) : 'someone'
      const isMe = Boolean(meId && personId === meId)
      const label = rolePlayLabelUi(slot.label)
      if (nextId) assigned.push({ who, label, isMe })
      else cleared.push({ who, label, isMe })
    }
    if (assigned.length === 1) {
      const row = assigned[0]!
      if (row.isMe) {
        return actor
          ? `${actor} assigned you as ${row.label}.`
          : `You've been assigned as ${row.label}.`
      }
      return actor
        ? `${actor} assigned ${row.who} to ${row.label}.`
        : `${row.who} was assigned to ${row.label}.`
    }
    if (assigned.length > 1) {
      return actor
        ? `${actor} assigned ${assigned.length} roleplay positions.`
        : `${assigned.length} roleplay positions were assigned.`
    }
    if (cleared.length === 1) {
      const row = cleared[0]!
      if (row.isMe) {
        return actor
          ? `${actor} cleared you from ${row.label}.`
          : `You've been cleared from ${row.label}.`
      }
      return actor
        ? `${actor} cleared ${row.who} from ${row.label}.`
        : `${row.who} was cleared from ${row.label}.`
    }
    if (cleared.length > 1) {
      return actor
        ? `${actor} cleared ${cleared.length} roleplay positions.`
        : `${cleared.length} roleplay positions were cleared.`
    }
    return null
  }

  function noteRemoteRolePlay(before: ScenarioObject | undefined, after: ScenarioObject): void {
    const bucket = rolePlayRemoteRef.current
    if (before) bucket.before.push(before)
    bucket.after.push(after)
    if (rolePlayRemoteTimerRef.current != null) return
    rolePlayRemoteTimerRef.current = window.setTimeout(() => {
      rolePlayRemoteTimerRef.current = null
      const pending = rolePlayRemoteRef.current
      rolePlayRemoteRef.current = { before: [], after: [] }
      narrateRolePlay(pending.before, pending.after, null)
    }, 200)
  }

  function narrateRolePlay(
    before: readonly ScenarioObject[],
    after: readonly ScenarioObject[],
    actor: string | null
  ): void {
    const line = rolePlayLine(before, after, actor)
    if (line) pushInfoToast(line, true)
  }

  function applyPeople(
    next: ScenarioPerson[],
    seq: number,
    actor: string | null,
    instanceId?: string | null
  ): void {
    const openId = instanceIdRef.current
    const targetId = (instanceId?.trim() || openId || '').trim()
    if (!targetId) return
    // Guid compare must ignore dashes — hub/instanceId formats otherwise diverge.
    const sameId = (a: string, b: string) =>
      a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase()
    if (openId && sameId(openId, targetId)) {
      void seq
      narrateRoster(next, actor)
      setPeople(next)
      peopleRef.current = next
    }
    // Always patch the catalog row — Decline leaves the room before setPeople arrives.
    setInstances((prev) => {
      const copy = prev.map((i) => (sameId(i.id, targetId) ? { ...i, people: next } : i))
      instancesRef.current = copy
      return copy
    })
  }

  function offsetObject(object: ScenarioObject, dLng: number, dLat: number): ScenarioObject {
    if (Math.abs(dLng) < 1e-12 && Math.abs(dLat) < 1e-12) return object
    const shiftPt = (p: [number, number]): [number, number] => [p[0] + dLng, p[1] + dLat]
    const next: ScenarioObject = {
      ...object,
      pose: object.pose
        ? { lng: object.pose.lng + dLng, lat: object.pose.lat + dLat }
        : object.pose,
    }
    if (object.keyframes?.length) {
      next.keyframes = object.keyframes.map((frame) => ({
        ...frame,
        lng: frame.lng + dLng,
        lat: frame.lat + dLat,
      }))
    }
    if (object.path?.length) next.path = object.path.map(shiftPt)
    const segments = object.params.segments
    if (Array.isArray(segments)) {
      next.params = {
        ...object.params,
        segments: segments.map((seg) =>
          Array.isArray(seg) ? (seg as [number, number][]).map(shiftPt) : seg
        ),
      }
    }
    return next
  }

  function kickObjectMotion(): void {
    if (objectMotionRafRef.current != null) return
    const step = (now: number): void => {
      const motions = objectMotionsRef.current
      if (!motions.size) {
        objectMotionRafRef.current = null
        return
      }
      setSession((prev) => {
        let objects = prev.objects
        for (const [id, motion] of motions) {
          const t = Math.min(1, (now - motion.started) / motion.ms)
          const remain = 1 - t
          const shown =
            remain <= 0.001
              ? motion.target
              : offsetObject(motion.target, -motion.dLng * remain, -motion.dLat * remain)
          objects = objects.some((item) => item.id === id)
            ? objects.map((item) => (item.id === id ? shown : item))
            : [...objects, shown]
          if (t >= 1) motions.delete(id)
        }
        return healSyncedTimeline(withSessionSpan({ ...prev, objects }, elapsedRef.current))
      })
      objectMotionRafRef.current = window.requestAnimationFrame(step)
    }
    objectMotionRafRef.current = window.requestAnimationFrame(step)
  }

  function applyRemoteObject(object: ScenarioObject, seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    if (
      pendingObjectUpsertsRef.current.has(object.id) ||
      pendingObjectRemovesRef.current.has(object.id)
    )
      return
    noteBoardEditActivity()
    // Sticky remote drag paints the live pose over session objects. Never clear sticky or
    // ease-tween here — that reintroduced chase when Upsert raced the last edit frame
    // (coalesce made the race worse: Upsert often landed before the drained Watch pose).
    const stickyDrag = stickyRemoteDragRef.current?.dragId === object.id
    rememberOnePersistedObject(object)
    let previous: ScenarioObject | undefined
    setSession((prev) => {
      const current = prev.objects.find((item) => item.id === object.id)
      previous = current
      const from = current?.pose
      const to = object.pose
      // Under an active sticky drag, commit durable pose quietly — overlay still wins.
      if (stickyDrag) {
        objectMotionsRef.current.delete(object.id)
        const objects = current
          ? prev.objects.map((item) => (item.id === object.id ? object : item))
          : [...prev.objects, object]
        return healSyncedTimeline(withSessionSpan({ ...prev, objects }, elapsedRef.current))
      }
      // Smooth catch-up under the live edit overlay (overlay hides the tween while dragging).
      const easeRemote = watchOnRef.current || !watchFrame
      if (
        easeRemote &&
        from &&
        to &&
        (Math.abs(to.lng - from.lng) > 1e-7 || Math.abs(to.lat - from.lat) > 1e-7)
      ) {
        objectMotionsRef.current.set(object.id, {
          target: object,
          dLng: to.lng - from.lng,
          dLat: to.lat - from.lat,
          started: performance.now(),
          ms: 200,
        })
        kickObjectMotion()
        return prev
      }
      objectMotionsRef.current.delete(object.id)
      const objects = current
        ? prev.objects.map((item) => (item.id === object.id ? object : item))
        : [...prev.objects, object]
      return healSyncedTimeline(withSessionSpan({ ...prev, objects }, elapsedRef.current))
    })
    if (
      (previous?.rolePlayAssigneeId?.trim() || '') !== (object.rolePlayAssigneeId?.trim() || '')
    ) {
      noteRemoteRolePlay(previous, object)
    }
    const id = instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((inst) => {
        if (inst.id !== id) return inst
        const objects = inst.session.objects.some((item) => item.id === object.id)
          ? inst.session.objects.map((item) => (item.id === object.id ? object : item))
          : [...inst.session.objects, object]
        return { ...inst, session: { ...inst.session, objects } }
      })
    )
  }

  function applyRemoteObjectRemoved(objectId: string, seq: number): void {
    void seq
    if (previewTemplateIdRef.current) return
    if (pendingObjectUpsertsRef.current.has(objectId)) return
    noteBoardEditActivity()
    persistedObjectsRef.current.delete(objectId)
    pendingObjectRemovesRef.current.delete(objectId)
    setSession((prev) =>
      healSyncedTimeline(
        withSessionSpan(
          {
            ...prev,
            objects: prev.objects.filter((item) => item.id !== objectId),
          },
          elapsedRef.current
        )
      )
    )
    const id = instanceIdRef.current
    if (!id) return
    setInstances((prev) =>
      prev.map((inst) =>
        inst.id === id
          ? {
              ...inst,
              session: {
                ...inst.session,
                objects: inst.session.objects.filter((item) => item.id !== objectId),
              },
            }
          : inst
      )
    )
  }

  const studioRef = useRef({
    onSnapshot: (_snap: ServerSnapshot) => {},
    onOpenBoardSnapshot: (_raw: unknown) => {},
    onClock: (_clock: StudioClock, _instanceId?: string, _clientTrace?: string | null) => {},
    onSessionFields: (_fields: SessionFieldPatch, _seq: number) => {},
    onApplyBoardSeed: (_patch: ApplyBoardSeedPatch, _seq: number) => {},
    onStartRun: (_patch: StartRunPatch, _seq: number) => {},
    onStopRun: (_patch: StopRunPatch, _seq: number) => {},
    onAudienceType: (_audienceType: 'private' | 'public', _seq: number, _instanceId?: string) => {},
    onAllowLinkGuests: (_allow: boolean, _seq: number, _instanceId?: string) => {},
    onPeople: (
      _people: ScenarioPerson[],
      _seq: number,
      _actor: string | null,
      _instanceId?: string | null
    ) => {},
    onObject: (_object: ScenarioObject, _seq: number) => {},
    onObjectRemoved: (_objectId: string, _seq: number) => {},
    onWatchFrame: (_frame: {
      active: boolean
      lng?: number
      lat?: number
      zoom?: number
      pointerLng?: number
      pointerLat?: number
      pointerDown?: boolean
      clickSeq?: number
      hoverId?: string
      hoverTip?: string
      grab?: boolean
      cursor?: 'pointer' | 'grab' | 'grabbing'
      focusId?: string
      dragId?: string
      dragLng?: number
      dragLat?: number
      edit?: boolean
      who?: string
      whoName?: string
      whoEmail?: string
      storyElapsedMs?: number
      storyRunning?: boolean
      surfacePlumes?: boolean
    }) => {},
    onPresenter: (_email: string | null) => {},
    onComments: (_comments: ScenarioComment[], _seq: number) => {},
    onPresence: (
      _action: 'join' | 'leave' | 'sync',
      _name: string,
      _email: string | null,
      _online: { email: string; name: string }[] | null
    ) => {},
    onDeleted: (_instanceId: string) => {},
    onInvited: (_raw: unknown, _actor: string | null) => {},
    onConnectionState: (_state: 'connected' | 'reconnecting' | 'disconnected') => {},
  })
  studioRef.current = {
    onSnapshot: applyServerSnapshot,
    onOpenBoardSnapshot: applyOpenBoardSnapshot,
    onClock: (clock, instanceId, clientTrace) =>
      applyServerClock(clock, undefined, instanceId, clientTrace),
    onSessionFields: applyServerFields,
    onApplyBoardSeed: applyPeerBoardSeed,
    onStartRun: applyPeerStartRun,
    onStopRun: applyPeerStopRun,
    onAudienceType: applyAudienceType,
    onAllowLinkGuests: applyAllowLinkGuests,
    onPeople: applyPeople,
    onObject: applyRemoteObject,
    onObjectRemoved: applyRemoteObjectRemoved,
    onConnectionState: (state) => {
      setHubLinkState(state)
      // Link-down chrome is the map-centered overlay — do not steal the upper toast/rail.
      if (state === 'connected') {
        setServerNotice((prev) => (isStudioUnreachableNotice(prev) ? null : prev))
      }
    },
    onPresenter: (email) => {
      const next = email?.trim().toLowerCase() || null
      setPresenterEmail((prev) => {
        const me = myEmailRef.current
        if (next && next !== prev) {
          const person = peopleRef.current.find((p) => p.email.trim().toLowerCase() === next)
          const frame = watchByEmailRef.current.get(next)
          const name =
            (next === me ? editWhoNameRef.current : '') ||
            person?.name?.trim() ||
            presentNameByEmailRef.current.get(next)?.trim() ||
            frame?.whoName?.trim() ||
            next
          queueMicrotask(() => {
            pushInfoToast(`${name} is now Presenter.`, false, 3)
          })
          // Newly appointed Presenter without pointer share still drives the room (camera only).
          if (next === me && !watchOnRef.current) {
            const id = instanceIdRef.current
            const cam = mapApiRef.current?.getCamera()
            if (id && cam) {
              void hubSetWatchFrame(id, {
                active: true,
                lng: cam.lng,
                lat: cam.lat,
                zoom: cam.zoom,
                who: editWhoRef.current || undefined,
                whoName: editWhoNameRef.current || undefined,
              }).catch(() => {})
            }
          }
        }
        // Cleared or replaced — stop camera-only drive if we aren't sharing a pointer.
        if (prev === me && prev !== next && !watchOnRef.current) {
          const id = instanceIdRef.current
          if (id) void hubSetWatchFrame(id, { active: false }).catch(() => {})
        }
        return next
      })
    },
    onWatchFrame: (frame) => {
      // Hub / templates: not on a board — ignore leftover group traffic.
      if (!instanceIdRef.current || previewTemplateIdRef.current) {
        setWatchByEmail(new Map())
        setPresenterEmail(null)
        setEditFrame(null)
        return
      }
      if (frame.edit) {
        const hasPointer = frame.pointerLng != null && frame.pointerLat != null
        const hasDrag = Boolean(frame.dragId) && frame.dragLng != null && frame.dragLat != null
        // Pose can arrive with drag only (pointer optional); clear when stream ends.
        if (!frame.active || (!hasPointer && !hasDrag)) {
          setEditFrame(null)
          // Drop sticky overlay — durable Upsert (or last drag pose already committed) owns paint.
          // Force a map re-render; sticky.until alone did not re-render, so P could sit on a
          // mid-drag pose until the next interaction ("misses until next move").
          if (stickyRemoteDragRef.current) {
            stickyRemoteDragRef.current = null
            if (stickyDragTimerRef.current != null) {
              window.clearTimeout(stickyDragTimerRef.current)
              stickyDragTimerRef.current = null
            }
            setStickyDragEpoch((n) => n + 1)
          }
          return
        }
        setEditFrame({
          pointerLng: hasPointer ? (frame.pointerLng as number) : (frame.dragLng as number),
          pointerLat: hasPointer ? (frame.pointerLat as number) : (frame.dragLat as number),
          pointerDown: hasPointer ? frame.pointerDown : true,
          hoverId: hasPointer ? frame.hoverId : frame.dragId,
          grab: hasPointer ? frame.grab : true,
          cursor: hasPointer ? frame.cursor : 'grabbing',
          dragId: frame.dragId,
          dragLng: frame.dragLng,
          dragLat: frame.dragLat,
          who: frame.who,
        })
        return
      }
      const email = frame.whoEmail?.trim().toLowerCase() || ''
      if (!frame.active || frame.lng == null || frame.lat == null || frame.zoom == null) {
        // Presenter may stop pointer share while still driving — keep camera, drop trails.
        if (email && email === presenterEmailRef.current) {
          clearShareGrace(email)
          setWatchByEmail((prev) => {
            const cur = prev.get(email)
            if (!cur) return prev
            const next = new Map(prev)
            next.set(email, {
              ...cur,
              pointerLng: undefined,
              pointerLat: undefined,
              pointerDown: false,
              hoverId: undefined,
              hoverTip: undefined,
              grab: false,
              cursor: undefined,
              dragId: undefined,
              dragLng: undefined,
              dragLat: undefined,
              // Omit = clear (do not keep a stale Done playhead across Share-on frames).
              storyElapsedMs: frame.storyElapsedMs,
              storyRunning: frame.storyRunning,
              surfacePlumes:
                typeof frame.surfacePlumes === 'boolean'
                  ? frame.surfacePlumes
                  : cur.surfacePlumes,
              grace: false,
            })
            return next
          })
          return
        }
        if (email) {
          const stillHere = (presentCountsRef.current.get(email) ?? 0) > 0
          if (stillHere) {
            // Decisive Stop share while still in the room — lineup drops immediately.
            endShareImmediate(email)
          } else {
            // Left the room: presence leave owns the 20s rejoin grace — don't restart it.
            const cur = watchByEmailRef.current.get(email)
            if (cur && !cur.grace) beginShareGrace(email)
          }
        } else {
          // Legacy deactivate without whoEmail — drop anyone still present; grace the rest.
          setWatchByEmail((prev) => {
            if (!prev.size) return prev
            const next = new Map(prev)
            for (const [key, cur] of prev) {
              const here = (presentCountsRef.current.get(key) ?? 0) > 0
              if (here) {
                clearShareGrace(key)
                next.delete(key)
                continue
              }
              if (cur.grace) continue
              next.set(key, { ...cur, grace: true })
              clearShareGrace(key)
              const timer = window.setTimeout(() => {
                shareGraceTimersRef.current.delete(key)
                setWatchByEmail((map) => {
                  if (!map.has(key)) return map
                  const copy = new Map(map)
                  copy.delete(key)
                  return copy
                })
              }, SHARE_GRACE_MS)
              shareGraceTimersRef.current.set(key, timer)
            }
            return next
          })
        }
        return
      }
      const hasPointer = frame.pointerLng != null && frame.pointerLat != null
      const key = email || '__anon__'
      clearShareGrace(key)
      setWatchByEmail((prev) => {
        const prior = prev.get(key)
        const next = new Map(prev)
        // Keep prior drag while pointer still down if this frame omitted pose (select/focus
        // frames). Clearing drag here parked the icon while the hand kept moving.
        const keepDrag =
          Boolean(prior?.dragId) &&
          prior?.dragLng != null &&
          prior?.dragLat != null &&
          (frame.pointerDown || prior?.pointerDown) &&
          (frame.dragId == null || frame.dragLng == null || frame.dragLat == null)
        next.set(key, {
          lng: frame.lng as number,
          lat: frame.lat as number,
          zoom: frame.zoom as number,
          pointerLng: hasPointer ? frame.pointerLng : undefined,
          pointerLat: hasPointer ? frame.pointerLat : undefined,
          pointerDown: hasPointer ? frame.pointerDown : false,
          clickSeq: hasPointer ? frame.clickSeq : prior?.clickSeq,
          hoverId: hasPointer ? frame.hoverId : undefined,
          hoverTip: hasPointer ? frame.hoverTip : undefined,
          grab: hasPointer ? frame.grab : false,
          cursor: hasPointer ? frame.cursor : undefined,
          focusId: hasPointer ? frame.focusId || undefined : prior?.focusId,
          dragId: keepDrag ? prior!.dragId : frame.dragId,
          dragLng: keepDrag ? prior!.dragLng : frame.dragLng,
          dragLat: keepDrag ? prior!.dragLat : frame.dragLat,
          who: frame.who || prior?.who,
          whoName: frame.whoName || prior?.whoName,
          whoEmail: email || prior?.whoEmail,
          // Omit = clear. Preserving prior made Share-on (no story fields) keep a stale
          // storyRunning:false / old ms and freeze P the instant S was pressed.
          storyElapsedMs: frame.storyElapsedMs,
          storyRunning: frame.storyRunning,
          surfacePlumes:
            typeof frame.surfacePlumes === 'boolean'
              ? frame.surfacePlumes
              : prior?.surfacePlumes,
          grace: false,
        })
        return next
      })
    },
    onComments: applyComments,
    onDeleted: applyDeleted,
    onInvited: applyInvited,
    onPresence: (action, name, email, online) => {
      if (action === 'sync') {
        const names = new Map<string, string>()
        for (const row of online ?? []) {
          const key = row.email?.trim().toLowerCase() ?? ''
          const label = row.name?.trim()
          if (key && label) names.set(key, label)
        }
        replacePresentEmails(
          (online ?? []).map((row) => row.email),
          names
        )
        setPresenceSynced(true)
        return
      }
      if (email) {
        bumpPresentEmail(email, action === 'join' ? 1 : -1, name)
        if (action === 'leave') beginShareGrace(email.trim().toLowerCase())
      }
      if (action === 'join') notePersonHere(name, email)
    },
  }

  useEffect(() => {
    if (!isAuthenticated) return
    let cancel = false
    void fetchUserTextScale().then((scale) => {
      if (cancel || !scale) return
      setUiScale(scale)
      writeUiScale(scale)
    })
    void fetchUserStudioTheme().then((theme) => {
      if (cancel || !theme) return
      setStudioTheme(theme)
      writeStudioTheme(theme)
    })
    return () => {
      cancel = true
    }
  }, [isAuthenticated])

  function logOutOfBuxus(): void {
    if (loggingOut) return
    setLoggingOut(true)
    trackEvent(EventTypes.USER_LOGGED_OUT, {
      screen: 'Scenarios',
      screenConstant: 'SCENARIOS_POC',
    })
    void logout()
      .then(() => {
        navigate('/', { replace: true })
      })
      .catch(() => {
        setLoggingOut(false)
      })
  }

  function chooseUiScale(next: UiScale): void {
    setUiScale(next)
    writeUiScale(next)
    if (isAuthenticated) {
      void saveUserTextScale(next).catch(() => {
        /* local choice still applies; the account write needs a signed-in host */
      })
    }
  }

  function chooseStudioTheme(next: StudioTheme): void {
    setStudioTheme(next)
    writeStudioTheme(next)
    if (isAuthenticated) {
      void saveUserStudioTheme(next).catch(() => {
        /* local choice still applies; the account write needs a signed-in host */
      })
    }
  }

  useEffect(() => {
    if (!studioViewReadyRef.current) return
    try {
      localStorage.setItem(
        LAST_STUDIO_VIEW_KEY,
        JSON.stringify({
          library: libraryMode,
          templateId: previewTemplateId ?? '',
          instanceId,
        })
      )
    } catch {
      /* private mode */
    }
  }, [libraryMode, previewTemplateId, instanceId])

  function studioHubHandlers(): ScenarioHubHandlers {
    return {
      onSnapshot: (raw) => studioRef.current.onOpenBoardSnapshot(raw),
      onClock: (clock, instanceId, clientTrace) =>
        studioRef.current.onClock(clock, instanceId, clientTrace),
      onSessionFields: (fields, seq) => studioRef.current.onSessionFields(fields, seq),
      onApplyBoardSeed: (patch, seq) => studioRef.current.onApplyBoardSeed(patch, seq),
      onStartRun: (patch, seq) => studioRef.current.onStartRun(patch, seq),
      onStopRun: (patch, seq) => studioRef.current.onStopRun(patch, seq),
      onAudienceType: (audienceType, seq, instanceId) =>
        studioRef.current.onAudienceType(audienceType, seq, instanceId),
      onAllowLinkGuests: (allow, seq, instanceId) =>
        studioRef.current.onAllowLinkGuests(allow, seq, instanceId),
      onPeople: (people, seq, actor, instanceId) =>
        studioRef.current.onPeople(people, seq, actor, instanceId),
      onObject: (object, seq) => studioRef.current.onObject(object, seq),
      onObjectRemoved: (objectId, seq) => studioRef.current.onObjectRemoved(objectId, seq),
      onWatchFrame: (frame) => studioRef.current.onWatchFrame(frame),
      onPresenter: (email) => studioRef.current.onPresenter(email),
      onComments: (comments, seq) => studioRef.current.onComments(comments, seq),
      onPresence: (action, name, email, online) =>
        studioRef.current.onPresence(action, name, email, online),
      onDeleted: (instanceId) => studioRef.current.onDeleted(instanceId),
      onInvited: (raw, actor) => studioRef.current.onInvited(raw, actor),
      onConnectionState: (state) => studioRef.current.onConnectionState(state),
    }
  }

  useEffect(() => {
    let cancel = false
    setServerBooting(true)
    setBootError(null)
    void (async () => {
      const persist = bootAttempt > 0
      const delays = persist ? STUDIO_HUB_RECONNECT_DELAYS_MS : ([0] as const)
      let lastErr: unknown
      let list: Awaited<ReturnType<typeof fetchScenarioTemplates>> = []
      for (let i = 0; i < delays.length; i++) {
        const wait = delays[i] ?? 0
        if (wait) await new Promise((resolve) => setTimeout(resolve, wait))
        if (cancel) return
        try {
          list = await fetchScenarioTemplates()
          if (cancel) return
          templatesRef.current = list
          setTemplates(list)
          await ensureScenarioHub(studioHubHandlers())
          if (cancel) return
          lastErr = null
          break
        } catch (err) {
          lastErr = err
          if (!persist) break
        }
      }
      if (cancel) return
      if (lastErr) {
        setBootError(studioLoadMessage(lastErr))
        setServerBooting(false)
        return
      }
      try {
        let last = ''
        try {
          last = localStorage.getItem(LAST_SERVER_INSTANCE_KEY)?.trim() ?? ''
        } catch {
          last = ''
        }
        const remembered = new Set(readServerInstanceIds())
        if (last) remembered.add(last)
        let snaps: ServerSnapshot[] = []
        try {
          snaps = await fetchScenarioInstances(user)
        } catch (err) {
          if (isStudioUnreachable(err)) throw err
          for (const id of remembered) {
            try {
              snaps.push(await fetchScenarioInstance(id, user))
            } catch {
              /* that copy is gone; keep looking */
            }
          }
          if (!snaps.length) throw err
        }
        if (cancel) return
        serverListReadyRef.current = true
        const arrival = readStudioArrival()
        const markSessionUnavailable = (): void => {
          clearStudioArrival()
          try {
            localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
          } catch {
            /* private mode */
          }
          setSessionUnavailableOpen(true)
        }
        writeServerInstanceIds(snaps.map((s) => s.id))
        const insts = snaps.map(instanceFromSnapshot)
        for (const inst of insts) {
          if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
        }
        setInstances(insts)
        const visible = (inst: ScenarioInstance) => instanceVisibleToMe(inst)

        // Drop remembered boards that are gone or no longer visible to this account.
        if (last && !insts.some((i) => i.id === last && visible(i))) {
          try {
            localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
            const rawView = localStorage.getItem(LAST_STUDIO_VIEW_KEY)
            if (rawView) {
              const parsed = JSON.parse(rawView) as { instanceId?: string }
              if (parsed.instanceId === last) {
                localStorage.setItem(
                  LAST_STUDIO_VIEW_KEY,
                  JSON.stringify({
                    library: false,
                    templateId: '',
                    instanceId: '',
                  })
                )
              }
            }
          } catch {
            /* private mode */
          }
          last = ''
        }

        // URL is source of truth. Last-viewed / arrival stay in storage for invite return /
        // soft deep links — they must not override hub URLs (`/scenarios`, `/scenarios/templates`).
        const pathNow = window.location.pathname
        const pathSession = parseStudioSessionId(pathNow)
        const pathTemplate = parseStudioTemplateId(pathNow)
        const pathTemplatesHub = isStudioTemplatesHubPath(pathNow)
        const pathSessionsHub =
          pathNow.replace(/\/+$/, '') === SCENARIOS_PATH || pathNow === SCENARIOS_PATH

        const openTemplatesHub = (): void => {
          // Prefer blank whiteboard so Templates never parks on the empty chooser / hub-idle.
          selectTemplate(defaultTemplateId(list), insts, true)
          clearStudioArrival()
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
        }

        /**
         * Open the board named by the URL / invite. GET by id is the real load;
         * the sessions list is only a same-boot cache hit (sidebar inventory, not the deep-link source of truth).
         */
        const resolveBoardById = async (id: string): Promise<ScenarioInstance | null> => {
          const cached = insts.find((i) => i.id === id && visible(i))
          if (cached) return cached
          try {
            const snap = await fetchScenarioInstance(id, user)
            const inst = instanceFromSnapshot(snap)
            if (!visible(inst)) return null
            if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
            if (!insts.some((i) => i.id === inst.id)) {
              insts.push(inst)
              setInstances([...insts])
              writeServerInstanceIds(insts.map((i) => i.id))
            }
            return inst
          } catch {
            return null
          }
        }

        // Truly empty account (no instances at all) — same as 0 owned: Templates + blank.
        // Stale sessionStorage arrival from a purged deep-link must not soft-404 the hub.
        if (!snaps.length && !pathSession && !pathTemplate && !pathTemplatesHub) {
          clearStudioArrival()
          try {
            localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
          } catch {
            /* private mode */
          }
          openTemplatesHub()
          return
        }

        /** Soft / Sessions landing: 0 owned → Templates (blank); 1+ owned → resume. */
        const landSessionsOwnedOrChooser = async (): Promise<void> => {
          const owned = ownedBoardsFor(insts, user.email)
          const resume = pickResumeOwnedBoard(owned, last)
          if (!resume) {
            // First login / no boards yet — land on Templates with blank selected, not empty Sessions.
            openTemplatesHub()
            return
          }
          applyInstance(resume)
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
          try {
            await joinScenarioInstance(resume.id, resume.studioClock?.seq ?? 0)
          } catch (err) {
            if (!cancel) setServerNotice(studioActionMessage(err, 'Could not join this session.'))
          }
        }

        // Deep links in the address bar win first.
        if (pathSession) {
          const wanted = await resolveBoardById(pathSession)
          if (!wanted) {
            markSessionUnavailable()
            // Same as invite soft-404: resume an owned board (or Templates), clear the dead URL.
            await landSessionsOwnedOrChooser()
            return
          }
          applyInstance(wanted)
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
          try {
            await joinScenarioInstance(wanted.id, wanted.studioClock?.seq ?? 0)
          } catch (err) {
            if (!cancel) setServerNotice(studioActionMessage(err, 'Could not join this session.'))
          }
          return
        }

        if (pathTemplate) {
          if (list.some((t) => t.id === pathTemplate)) {
            selectTemplate(pathTemplate, insts, true)
          } else {
            openTemplatesHub()
          }
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
          return
        }

        // Hub URLs: Templates opens blank (or first) template; Sessions resumes owned boards.
        if (pathTemplatesHub) {
          openTemplatesHub()
          return
        }

        if (pathSessionsHub) {
          await landSessionsOwnedOrChooser()
          return
        }

        // Soft entry (no path target): honor this-tab arrival once, else Sessions landing.
        if (arrival?.source === 'board') {
          const wanted = await resolveBoardById(arrival.targetId)
          if (!wanted) {
            markSessionUnavailable()
            await landSessionsOwnedOrChooser()
            return
          }
          applyInstance(wanted)
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
          try {
            await joinScenarioInstance(wanted.id, wanted.studioClock?.seq ?? 0)
          } catch (err) {
            if (!cancel) setServerNotice(studioActionMessage(err, 'Could not join this session.'))
          }
          return
        }
        if (arrival?.source === 'template') {
          if (list.some((t) => t.id === arrival.targetId)) {
            selectTemplate(arrival.targetId, insts, true)
          } else {
            openTemplatesHub()
          }
          studioViewReadyRef.current = true
          setServerNotice(null)
          setServerBooting(false)
          return
        }

        await landSessionsOwnedOrChooser()
      } catch (err) {
        if (!cancel) setBootError(studioLoadMessage(err))
      } finally {
        if (!cancel) setServerBooting(false)
      }
    })()
    return () => {
      cancel = true
      void stopScenarioHub()
    }
  }, [user, bootAttempt])

  function retryStudioBoot(): void {
    setBootError(null)
    setServerBooting(true)
    setBootAttempt((n) => n + 1)
  }

  /** Mid-session: same start + backoff window as a live drop (`withAutomaticReconnect`). */
  async function retryStudioLink(): Promise<void> {
    if (studioLinkRetryingRef.current) return
    studioLinkRetryingRef.current = true
    setHubLinkState('reconnecting')
    setServerNotice((prev) => (isStudioUnreachableNotice(prev) ? null : prev))
    try {
      await stopScenarioHub()
      await ensureScenarioHub(studioHubHandlers(), { persist: true })
      const id = instanceIdRef.current
      if (id && !previewTemplateIdRef.current) {
        try {
          await joinScenarioInstance(id, 0)
        } catch (err) {
          setServerNotice(studioActionMessage(err, 'Could not join this session.'))
        }
      }
    } catch {
      setHubLinkState('disconnected')
    } finally {
      studioLinkRetryingRef.current = false
    }
  }

  function applyInstance(inst: ScenarioInstance): void {
    setLibraryMode(false)
    const deferred = deferredShareStopRef.current
    const resumingShare = Boolean(deferred && deferred.instanceId === inst.id && watchOnRef.current)
    if (deferred) {
      window.clearTimeout(deferred.timer)
      deferredShareStopRef.current = null
      if (!resumingShare) {
        void hubSetWatchFrame(deferred.instanceId, { active: false }).catch(() => {})
        setWatchOn(false)
        watchOnRef.current = false
      }
    } else if (watchOnRef.current && instanceIdRef.current && instanceIdRef.current !== inst.id) {
      void hubSetWatchFrame(instanceIdRef.current, { active: false }).catch(() => {})
      setWatchOn(false)
      watchOnRef.current = false
    }
    const snapN =
      typeof inst.session.exerciseTakeNumber === 'number'
        ? Math.max(0, Math.floor(inst.session.exerciseTakeNumber))
        : null
    const snapStopped =
      typeof inst.session.exerciseTakeStopped === 'boolean'
        ? inst.session.exerciseTakeStopped
        : null
    const admittedTakePhase = admitTakePhaseWire(snapN, snapStopped)
    // Snapshot must not wipe an in-flight Start/Stop take phase (ApplyBoardSeed).
    if (admittedTakePhase.take != null) setTakeNumber(admittedTakePhase.take)
    else if (!takePhaseHoldRef.current) setTakeNumber(0)
    if (admittedTakePhase.stopped != null) setExerciseRunStopped(admittedTakePhase.stopped)
    else if (!takePhaseHoldRef.current) setExerciseRunStopped(false)
    const switchingBoard = inst.id !== instanceIdRef.current
    if (switchingBoard) {
      presentCountsRef.current = new Map()
      presenceAnnouncedRef.current = new Set()
      setPresentEmails(new Set())
      setPresentNameByEmail(new Map())
      setPresenceSynced(false)
      setPresenterEmail(null)
      setWatchByEmail(new Map())
      // Local-only clear when leaving — do not hub-clear the board we are leaving mid-switch.
      instantReplayPauseMsRef.current = null
      setInstantReplayPauseMs(null)
      setInstantReplayEngaged(false)
      setReplayPlaying(false)
      setTapeReview(null)
      setTimeBarAutoHidden(false)
      setTimeBarPinned(false)
      setScribbleMode(false)
      setScribbleStrokes([])
      localSharePointerRef.current = null
      setLocalSharePointerEpoch((n) => n + 1)
    }
    for (const key of shareGraceTimersRef.current.keys()) clearShareGrace(key)
    setWatchByEmail(new Map())
    if (!resumingShare) {
      setPresenterEmail(null)
      if (!watchOnRef.current) setWatchOn(false)
    }
    setEditFrame(null)
    const leavingId = objectWriteInstanceRef.current
    if (leavingId && leavingId !== inst.id) {
      flushObjectWritesForLeavingBoard(leavingId)
    } else {
      void flushObjectWrites()
    }
    instanceIdRef.current = inst.id
    objectWriteInstanceRef.current = inst.id
    const playback = inst.lifecycle === 'completed'
    setPreviewTemplateId(null)
    setTemplateEditLive(false)
    setInstanceId(inst.id)
    const healed = healSessionCopy(healSyncedTimeline(withSessionSpan(inst.session)))
    rememberPersistedObjects(healed.objects)
    persistedCommentsRef.current = JSON.stringify(healed.comments ?? [])
    pendingCommentsRef.current = null
    // Older blank-board copies may lack sessionType — stamp Whiteboard once.
    const tmpl = templatesRef.current?.find((t) => t.id === inst.templateId)
    const isBlankTmpl =
      isBlankBoardTemplateId(inst.templateId) ||
      isBlankBoardTemplateId(tmpl?.id) ||
      isBlankBoardTemplateId(tmpl?.session.id)
    const needsBlankType = isBlankTmpl && !sessionTypeById(healed.sessionType)
    const withType = needsBlankType
      ? {
          ...healed,
          sessionType: 'whiteboard' as const,
          boardComplexity: healed.boardComplexity ?? 'everything',
        }
      : healed
    // Every session gets at least Main version — wrap current board as seed when missing.
    let withVersions = ensureBoardVersions(withType, {
      userId: user.userId ?? null,
      name: shortPersonName(user.name ?? '') ?? user.email ?? null,
    })
    // Older boards: live lines on exerciseRuns[].comments → session.roomChat.
    const beforeRoomHeal = withVersions
    withVersions = healRoomChatFromRuns(withVersions)
    const roomChatHealed =
      (beforeRoomHeal.roomChat?.length ?? 0) !== (withVersions.roomChat?.length ?? 0)
    if (playback && withVersions.tapeReviewRunId) {
      // Terminal Done homepage: final board — drop sticky Run N before adopt.
      const cleared = { ...withVersions }
      delete cleared.tapeReviewRunId
      withVersions = cleared
      if (canDriveDoneReviewRef.current) {
        // Persist clear so peers/reload stay on the final board.
        void hubSetSessionFields(inst.id, { tapeReviewRunId: null }).catch(() => {})
      }
    }
    setSession(withVersions)
    snapshotRef.current = { ...snapshotRef.current, session: withVersions }
    // Live between-takes: room may already be in tape review for a finished run.
    // Done: tapeReviewRunId cleared above → final board.
    adoptTapeReviewRunId(withVersions.tapeReviewRunId)
    if (playback) {
      // Capture end-board for Done version restore (local switch; hub seed apply blocked).
      const endVid = withVersions.selectedVersionId ?? ''
      doneEndBoardRef.current = endVid
        ? {
            versionId: endVid,
            objects: structuredClone(withVersions.objects ?? []),
            timeline: structuredClone(withVersions.timeline ?? []),
            camera: { ...withVersions.camera },
            cameraBeats: withVersions.cameraBeats
              ? structuredClone(withVersions.cameraBeats)
              : undefined,
            activeMapId: withVersions.activeMapId,
            comments: structuredClone(withVersions.comments ?? []),
          }
        : null
      if (tapeReviewRef.current) exitTapeReview({ persist: false })
      setWatchingRunId(null)
      setBoardActivity('preview')
      finishedRunStopMsRef.current = null
    } else {
      doneEndBoardRef.current = null
    }
    setServerNotice(null)
    // One coalesce write for local heals — parallel SetSessionFields races the concurrency stamp
    // and was surfacing as “Could not save scenario settings.” markedReady is stripped locally;
    // the next real settings save (or backend normalize) drops it from storage.
    const healPatch: SessionFieldPatch = {}
    if (healed.summary !== inst.session.summary) healPatch.summary = healed.summary
    if (needsBlankType) {
      healPatch.sessionType = 'whiteboard'
      healPatch.boardComplexity = withVersions.boardComplexity ?? 'everything'
    }
    const versionsMissing = !(inst.session.boardVersions?.length)
    if (versionsMissing) {
      healPatch.boardVersions = withVersions.boardVersions
      healPatch.selectedVersionId = withVersions.selectedVersionId ?? null
      healPatch.exerciseRuns = withVersions.exerciseRuns ?? []
    }
    if (roomChatHealed) {
      healPatch.roomChat = withVersions.roomChat ?? []
    }
    const nextPeople = inst.people
    peopleRef.current = nextPeople
    setPeople(nextPeople)
    // Sync drive refs from this board’s roster before any hub Seek/Pause heal.
    // canOperateSessionRef still reflected the *previous* board until the next React paint
    // (Participants briefly looked like hosts → Seek 401 + owned-head ignored PreviewPlay).
    {
      const email = user.email.trim().toLowerCase()
      const uid = user.userId?.trim()
      const me =
        (uid ? findPersonByUserId(nextPeople.filter(personIsActive), uid) : undefined) ??
        (email
          ? nextPeople
              .filter(personIsActive)
              .find((p) => p.email.trim().toLowerCase() === email)
          : undefined)
      const active = nextPeople.filter(personIsActive)
      const emptySoft = !me && active.length === 0 && !seeAllInstances
      const observerOffRoster =
        !me &&
        accessPerms.has('scenarios.view_debug') &&
        !hasScenarioAdminRole
      const adminHelper = Boolean(seeAllInstances && !observerOffRoster && !me)
      const grantsSession =
        Boolean(
          me && (personHasRole(me, 'owner') || personHasRole(me, 'trainer')),
        ) ||
        emptySoft ||
        adminHelper
      const grantsEdit =
        Boolean(
          me &&
            (personHasRole(me, 'owner') ||
              personHasRole(me, 'editor') ||
              personHasRole(me, 'trainer')),
        ) ||
        emptySoft ||
        adminHelper
      // Completed / non-editors: keep the local heal; do not SetSessionFields.
      if (
        Object.keys(healPatch).length > 0 &&
        inst.lifecycle !== 'completed' &&
        grantsEdit
      ) {
        void hubSetSessionFields(inst.id, healPatch).catch(() => {
          /* local copy already healed */
        })
      }
      const linkGuest = isLinkGuest
      canOperateSessionRef.current = Boolean(
        canRun && grantsSession && !linkGuest && !hubActionsLocked,
      )
      canDrivePreviewRef.current = Boolean(
        inst.lifecycle === 'planned' &&
          grantsEdit &&
          !linkGuest &&
          !hubActionsLocked &&
          (accessPerms.has('scenarios.edit_board') || accessPerms.has('scenarios.run')),
      )
      // Drop a stale owns-head from a prior board so peers follow PreviewPlay/Resume.
      if (!canOperateSessionRef.current && !canDrivePreviewRef.current) {
        hubSeekOwnsHeadRef.current = false
        hubAdmittedRunningRef.current = false
        studioClockDesireRef.current = null
      }
    }
    const knownClock = inst.studioClock ?? clocksRef.current.get(inst.id)
    const playhead = knownClock
      ? boardPlayheadIso(knownClock, inst.session.storyStartTime)
      : (inst.playheadStoryIso ?? inst.session.storyStartTime)
    if (knownClock) {
      clocksRef.current.set(inst.id, knownClock)
      serverClockRef.current = knownClock
      setServerClock(knownClock)
    }
    snapshotRef.current = { ...snapshotRef.current, session: withVersions }
    if (needsBlankType || versionsMissing) {
      setInstances((prev) =>
        prev.map((row) =>
          row.id === inst.id ? { ...row, session: { ...row.session, ...withVersions } } : row
        )
      )
    }
    const nextElapsed = playback
      ? 0
      : Math.max(0, storyDeltaMs(inst.session.storyStartTime, playhead))
    hubWirePlayheadMsRef.current = nextElapsed
    setHubWirePlayheadMs(nextElapsed)
    setElapsedMs(nextElapsed)
    elapsedRef.current = nextElapsed
    const clockStatus = knownClock?.clockStatus ?? inst.clockStatus
    setStarted(clockStatus === 'running' || inst.lifecycle === 'running')
    setRunning(clockStatus === 'running')
    hubAdmittedRunningRef.current = clockStatus === 'running'
    setStopped(playback)
    setActualStartTime(inst.actualStartTime ?? null)
    // Reload / rejoin while take stopped: restore Reviewing only when hub still
    // has tapeReviewRunId. Cleared tape = “I’m done reviewing” (Live Idle
    // Previewing) — do not resurrect latest Run N or re-persist the id (HMR/reload
    // used to fight that clear).
    if (!playback) {
      const takeStopped =
        typeof withVersions.exerciseTakeStopped === 'boolean'
          ? withVersions.exerciseTakeStopped
          : admittedTakePhase.stopped === true
      const takeN =
        typeof withVersions.exerciseTakeNumber === 'number'
          ? Math.max(0, Math.floor(withVersions.exerciseTakeNumber))
          : admittedTakePhase.take ?? 0
      if (takeStopped && takeN > 0) {
        const finished =
          latestExerciseRunForVersion(withVersions) ?? latestExerciseRun(withVersions)
        if (
          finished &&
          typeof finished.stoppedStoryElapsedMs === 'number' &&
          Number.isFinite(finished.stoppedStoryElapsedMs)
        ) {
          finishedRunStopMsRef.current = Math.max(
            0,
            Math.floor(finished.stoppedStoryElapsedMs)
          )
        } else if (nextElapsed > 250) {
          finishedRunStopMsRef.current = Math.floor(nextElapsed)
        }
        const wireTape =
          typeof withVersions.tapeReviewRunId === 'string' &&
          withVersions.tapeReviewRunId.trim()
            ? withVersions.tapeReviewRunId.trim()
            : null
        if (wireTape) {
          // adoptTapeReviewRunId above already armed the overlay when runs were present.
          setBoardActivity('watching')
          if (finished && runHasTapeSeed(finished) && !tapeReviewRef.current) {
            beginFinishedRunTapeReview(finished, {
              persistTapeId: false,
            })
          } else if (finished) {
            // Reload while Reviewing: park at stop stamp (hub Pause@stop).
            rememberFinishedRunStopMs(finished)
            pinReviewingPausedAtStop()
          }
        } else {
          setWatchingRunId(null)
          setBoardActivity('preview')
        }
      }
    }
    // Shared take-pause freeze after playhead is known — freezes mission clock on join/reload.
    // IR only from hub engaged/playing — never infer from playhead drift (spectator Seek
    // storms left the clock off the freeze and looked like Instant Replay on reload).
    // Between takes: no freeze — leftover pauseMs from story-end Pause made scrub Seek
    // engage IR (between-takes is story preview, not Instant Replay).
    if (inst.session.exerciseTakeStopped === true) {
      if (
        instantReplayPauseMsRef.current != null ||
        instantReplayEngagedRef.current ||
        inst.session.instantReplayPauseMs != null ||
        inst.session.instantReplayEngaged === true ||
        inst.session.instantReplayPlaying === true
      ) {
        clearInstantReplayPause(
          canOperateSessionRef.current ? { persist: true } : undefined
        )
      }
    } else if (
      !playback &&
      clockStatus !== 'running' &&
      typeof inst.session.instantReplayPauseMs === 'number' &&
      Number.isFinite(inst.session.instantReplayPauseMs)
    ) {
      const freeze = Math.max(0, Math.floor(inst.session.instantReplayPauseMs))
      applyInstantReplayPauseFromWire(freeze, true)
      const wasPlaying = inst.session.instantReplayPlaying === true
      const wasEngaged = inst.session.instantReplayEngaged === true || wasPlaying
      if (wasEngaged) {
        setInstantReplayEngaged(true)
        // Always land IR *paused* on join/reload. Auto-resuming tape from SessionJson
        // left late joiners watching a ghost playback if the operator had disconnected
        // minutes/hours earlier. Live Space/Fields still start tape for the room.
        setReplayPlaying(false)
        if (wasPlaying && canOperateSessionRef.current) {
          persistInstantReplayPlaying(false)
        }
      } else {
        setInstantReplayEngaged(false)
        setReplayPlaying(false)
        // Local heal only — never hubSeek on join (that yanked the room to a stale freeze).
        if (Math.abs(nextElapsed - freeze) > 80) {
          elapsedRef.current = freeze
          setElapsedMs(freeze)
        }
      }
    }
    setSelectedId(null)
    setPlaceType(null)
    setNotesCtx('scenario')
    setTitleDraft(inst.session.title)
    setSummaryDraft(withType.summary)
    setTimelineFocusId(null)
    setHoverElapsedMs(null)
    setViewResetId((n) => n + 1)
    if (switchingBoard && inst.lifecycle === 'running') {
      scheduleRoomCatchup(inst)
    }
  }

  /**
   * Catch-up toast when switching onto a live board.
   * Session type is on the card now — no “You joined a …” cue.
   * Only surface when someone else is presenting (strip does not say who).
   */
  function scheduleRoomCatchup(inst: ScenarioInstance): void {
    const boardId = inst.id
    const tryShow = (attempt: number): void => {
      window.setTimeout(
        () => {
          if (instanceIdRef.current !== boardId || previewTemplateIdRef.current) return
          if (openBoardIsCompleted()) return
          if (showJoinOfferCardRef.current) return
          const lowerThirdUp =
            instantReplayEngagedRef.current ||
            (instantReplayPauseMsRef.current != null && !runningRef.current)
          if ((serverBooting || lowerThirdUp) && attempt < 14) {
            tryShow(attempt + 1)
            return
          }
          if (serverBooting || lowerThirdUp) return
          if (showJoinOfferCardRef.current) return
          const roster = peopleRef.current
          const me = user.email.trim().toLowerCase()
          const presenter = presenterEmailRef.current
          if (!presenter || presenter === me) return
          const row = roster.find(
            (p) => !p.isDeleted && p.email.trim().toLowerCase() === presenter
          )
          const who =
            (row ? personLabel(row) : null) ||
            shortPersonName(presentNameByEmailRef.current.get(presenter) ?? '') ||
            'Someone'
          pushInfoToast(`${who} is presenting.`, false, 4, 5200, { ephemeral: true })
        },
        attempt === 0 ? 450 : 550
      )
    }
    tryShow(0)
  }

  /** Scroll the new card into view — selection (.on) is enough; no wink flash. */
  function revealInstanceCard(id: string): void {
    requestAnimationFrame(() => {
      const el = scenarioRailRef.current?.querySelector(`[data-inst-id="${id}"]`)
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  function selectInstance(id: string): void {
    // No deselect-by-reclick. Switch boards by picking another; chooser only when you own none.
    if (id === instanceId && !previewTemplateId && !libraryMode) return
    setLibraryMode(false)
    const flushed = previewTemplateId ? instances : flushInstance(instances)
    setInstances(flushed)
    instancesRef.current = flushed
    const inst = flushed.find((i) => i.id === id)
    if (inst) {
      applyInstance(inst)
      const seq = inst.studioClock?.seq ?? clocksRef.current.get(inst.id)?.seq ?? 0
      void joinScenarioInstance(inst.id, seq).catch((err) => {
        setServerNotice(studioActionMessage(err, 'Could not join this session.'))
        // Stale pending after decline (or removal): clear Invited/badge instead of looping Join.
        const text = err instanceof Error ? err.message : String(err ?? '')
        if (!/do not have access/i.test(text)) return
        const me = user.email.trim().toLowerCase()
        if (!me) return
        const cleared = instancesRef.current.map((row) => {
          if (row.id !== id) return row
          return {
            ...row,
            people: row.people.map((person) =>
              !person.isDeleted && person.email.trim().toLowerCase() === me
                ? { ...person, status: 'declined' as const, hasBeenInvited: true }
                : person
            ),
          }
        })
        instancesRef.current = cleared
        setInstances(cleared)
        if (instanceIdRef.current === id) {
          setPeople(cleared.find((row) => row.id === id)?.people ?? [])
          peopleRef.current = cleared.find((row) => row.id === id)?.people ?? []
        }
      })
    }
  }

  /** Skip the next URL→selection pull after we pushed the URL ourselves. */
  const urlSyncFromUsRef = useRef(false)
  const urlLibraryModeRef = useRef(libraryMode)
  urlLibraryModeRef.current = libraryMode

  /**
   * Selection → address bar. Deps are selection only (not pathname) so navigate
   * does not re-enter this effect and loop with the pull effect.
   */
  useEffect(() => {
    if (!studioViewReadyRef.current || serverBooting) return
    const path = window.location.pathname
    const routeSession = parseStudioSessionId(path)
    const routeTemplate = parseStudioTemplateId(path)
    const want = previewTemplateId
      ? studioTemplatePath(previewTemplateId)
      : libraryMode
        ? SCENARIOS_TEMPLATES_PATH
        : instanceId
          ? studioSessionPath(instanceId)
          : SCENARIOS_PATH
    if (path === want) return
    // During boot only: keep a deep-link id in the bar until selection catches up.
    // After ready, hub leave (instanceId '') must clear `/scenarios/<uuid>` or URL and
    // selection fight — orphan status pills / empty title / wrong board.
    if (
      !studioViewReadyRef.current &&
      !previewTemplateId &&
      !instanceId &&
      (routeSession || routeTemplate)
    ) {
      return
    }
    if (!previewTemplateId && libraryMode && routeTemplate) return
    // Sessions hub already matches want above. Do not block leaving `/templates`
    // when libraryMode flips false (Sessions switch / exitLibraryToSessions).
    urlSyncFromUsRef.current = true
    navigate(want, { replace: true })
  }, [instanceId, libraryMode, previewTemplateId, serverBooting, navigate])

  /** Address bar → selection (back/forward / paste). Ignores our own pushes. */
  useEffect(() => {
    if (!studioViewReadyRef.current || serverBooting) return
    if (urlSyncFromUsRef.current) {
      urlSyncFromUsRef.current = false
      return
    }
    const routeTemplate = parseStudioTemplateId(location.pathname)
    if (routeTemplate) {
      if (routeTemplate === previewTemplateIdRef.current && urlLibraryModeRef.current) return
      if (!templatesRef.current.some((t) => t.id === routeTemplate)) return
      selectTemplate(routeTemplate, undefined, true)
      return
    }
    if (isStudioTemplatesHubPath(location.pathname)) {
      if (urlLibraryModeRef.current && !previewTemplateIdRef.current && !instanceIdRef.current) {
        return
      }
      enterTemplatesHub()
      return
    }
    const routeId = parseStudioSessionId(location.pathname)
    if (!routeId) {
      const hubPath = location.pathname.replace(/\/+$/, '') || '/'
      if (hubPath !== SCENARIOS_PATH) return
      // `/scenarios` — resume an owned board when you have one; chooser only when none.
      const owned = ownedBoardsFor(instancesRef.current, user.email)
      const resume = pickResumeOwnedBoard(owned, readLastServerInstanceId())
      if (resume) {
        if (
          resume.id === instanceIdRef.current &&
          !previewTemplateIdRef.current &&
          !urlLibraryModeRef.current
        ) {
          return
        }
        selectInstance(resume.id)
        return
      }
      if (!urlLibraryModeRef.current && !instanceIdRef.current && !previewTemplateIdRef.current) {
        return
      }
      parkSessionsHub()
      return
    }
    if (
      routeId === instanceIdRef.current &&
      !previewTemplateIdRef.current &&
      !urlLibraryModeRef.current
    ) {
      return
    }
    if (!instancesRef.current.some((i) => i.id === routeId)) return
    selectInstance(routeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname-driven; use refs for selection
  }, [location.pathname, serverBooting])

  /** Leave the board SignalR group (hub / templates). Share stays warm for SHARE_GRACE_MS. */
  function leaveJoinedBoard(): void {
    const id = instanceIdRef.current
    const wasSharing = watchOnRef.current
    if (deferredShareStopRef.current) {
      window.clearTimeout(deferredShareStopRef.current.timer)
      const prior = deferredShareStopRef.current.instanceId
      deferredShareStopRef.current = null
      if (prior && prior !== id) {
        void hubSetWatchFrame(prior, { active: false }).catch(() => {})
      }
    }
    if (wasSharing && id) {
      // Keep watchOn + delay active:false so a quick return resumes the same share.
      deferredShareStopRef.current = {
        instanceId: id,
        timer: window.setTimeout(() => {
          deferredShareStopRef.current = null
          void hubSetWatchFrame(id, { active: false }).catch(() => {})
          setWatchOn(false)
          watchOnRef.current = false
        }, SHARE_GRACE_MS),
      }
    } else {
      setWatchOn(false)
      watchOnRef.current = false
    }
    for (const key of shareGraceTimersRef.current.keys()) clearShareGrace(key)
    setWatchByEmail(new Map())
    setPresenterEmail(null)
    setEditFrame(null)
    presentCountsRef.current = new Map()
    presenceAnnouncedRef.current = new Set()
    setPresentEmails(new Set())
    setPresentNameByEmail(new Map())
    setPresenceSynced(false)
    void leaveScenarioInstance().catch(() => {})
  }

  /** Sessions hub with nothing selected — blank map, clear selection (same as `/scenarios`). */
  function parkSessionsHub(): void {
    flushObjectWritesForLeavingBoard(objectWriteInstanceRef.current)
    objectWriteInstanceRef.current = null
    leaveJoinedBoard()
    setLibraryMode(false)
    setPreviewTemplateId(null)
    setTemplateEditLive(false)
    instanceIdRef.current = ''
    setInstanceId('')
    setPeople([])
    setSession(healSyncedTimeline(withSessionSpan(createBlankBoardSession())))
    setStopped(false)
    setStarted(false)
    setRunning(false)
    setDock('scenarios')
    rememberServerInstance('')
  }

  /** Leave Template Library → resume an owned session, or Sessions chooser if none. */
  function exitLibraryToSessions(): void {
    const owned = ownedBoardsFor(instancesRef.current, user.email)
    const resume = pickResumeOwnedBoard(owned, readLastServerInstanceId())
    if (resume) {
      selectInstance(resume.id)
      return
    }
    parkSessionsHub()
  }

  /** Templates landing — select blank whiteboard (or first visible) so chrome/spine stay stable. */
  function enterTemplatesHub(): void {
    leaveJoinedBoard()
    setStopped(false)
    setStarted(false)
    setRunning(false)
    setDock('scenarios')
    selectTemplate(defaultTemplateId(templatesRef.current), undefined, true)
  }

  function selectTemplate(id: string, list?: ScenarioInstance[], library?: boolean): void {
    // No deselect-by-reclick — re-selecting the open template must not churn session/URL.
    const stayInLibrary = library === true || (library === undefined && libraryMode)
    if (id === previewTemplateId && stayInLibrary && !instanceId) return
    flushObjectWritesForLeavingBoard(objectWriteInstanceRef.current)
    objectWriteInstanceRef.current = null
    leaveJoinedBoard()
    setJustCompletedId(null)
    setServerNotice(null)
    // When `list` is passed (e.g. after delete), do not flushInstance(instances) —
    // React state is still stale and would revive the just-removed row.
    if (list) setInstances(list)
    else {
      const flushed = previewTemplateId ? instances : flushInstance(instances)
      setInstances(flushed)
    }
    setPreviewTemplateId(id)
    setTemplatePreviewVersionId(null)
    if (library === true) setLibraryMode(true)
    else if (library === false) setLibraryMode(false)
    // Library templates are browse-only. Create a session (+) to place/edit.
    // (edit_templates + templateEditLive remains for a future explicit template-edit mode.)
    setTemplateEditLive(false)
    setBoardActivity('preview')
    const seed = healSyncedTimeline(
      withSessionSpan(cloneSession(templateById(templatesRef.current, id).session))
    )
    setSession(seed)
    setTitleDraft(seed.title)
    setSummaryDraft(seed.summary)
    setElapsedMs(0)
    setStarted(false)
    setStopped(false)
    setRunning(false)
    setSelectedId(null)
    setPlaceType(null)
    setHoverElapsedMs(null)
    setTimelineFocusId(null)
    setViewResetId((n) => n + 1)
  }

  async function cloneSessionBoard(sourceId: string): Promise<void> {
    if (!accessPerms.has('scenarios.create')) return
    if (hubActionsLocked || cloningSessionId) return
    setCloningSessionId(sourceId)
    try {
      const snap = await cloneScenarioInstance(sourceId, user)
      // Only collide with titles on my roster — Oversight sessions must not force "(2)".
      const myTitleRows = instances.filter((i) => onMyRoster(i))
      const copiedTitle = nextCopyTitle(snap.session.title || snap.title, myTitleRows)
      if (copiedTitle !== snap.session.title) {
        snap.session.title = copiedTitle
        snap.title = copiedTitle
      }
      snap.session = placeSessionOnNextClock(snap.session, { blank: false })
      setLibraryMode(false)
      setPreviewTemplateId(null)
      setTemplateEditLive(false)
      applyServerSnapshot(snap)
      await joinScenarioInstance(snap.id, snap.clock.seq)
      void hubSetSessionFields(snap.id, { title: copiedTitle }).catch((err) => {
        setServerNotice(studioActionMessage(err, 'Could not save this copy.'))
      })
      setServerNotice(null)
      setDock('scenarios')
      setCloneReadyOffer(true)
      revealInstanceCard(snap.id)
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not copy this session.'))
    } finally {
      setCloningSessionId(null)
    }
  }

  function sessionCloneTool(instId: string, title: string) {
    if (!accessPerms.has('scenarios.create')) return null
    const cloning = cloningSessionId === instId
    return (
      <button
        type="button"
        className="sc-icon-btn"
        aria-label={`Copy ${title}`}
        title={hubActionsLocked ? hubLockTitle : 'Copy this session'}
        disabled={hubActionsLocked || Boolean(cloningSessionId)}
        onClick={() => void cloneSessionBoard(instId)}
      >
        {cloning ? <span className="sc-boot-spin" aria-hidden="true" /> : (
          <NavMdiIcon path={mdiContentCopy} sizePx={14} />
        )}
      </button>
    )
  }

  async function finishTemplateUseLanding(snap: ServerSnapshot): Promise<void> {
    await joinScenarioInstance(snap.id, snap.clock.seq)
    setCloningTemplateId(null)
    setLibraryMode(false)
    setPreviewTemplateId(null)
    setTemplatePreviewVersionId(null)
    setTemplateEditLive(false)
    setDock('scenarios')
    applyServerSnapshot(snap)
    setServerNotice(null)
    setCloneReadyOffer(true)
    revealInstanceCard(snap.id)
  }

  async function createFromBlankTemplate(
    camera: BoardCamera | null,
    templateId?: string | null,
  ): Promise<void> {
    if (hubActionsLocked) {
      setServerNotice(hubLockTitle)
      return
    }
    const wanted = (templateId ?? '').trim()
    const id =
      (wanted
        ? templates.find((t) => t.id === wanted || t.session.id === wanted)?.id
        : null) ??
      templates.find((t) => t.session.id === BLANK_BOARD_ID || t.id === BLANK_BOARD_ID)?.id ??
      BLANK_BOARD_ID
    setCloningTemplateId(id)
    try {
      const snap = await createScenarioInstance(id, user)
      const tmpl = templates.find((t) => t.id === id)
      snap.session = placeSessionOnNextClock(snap.session, { blank: true })
      const previewType =
        sessionTypeById(tmpl?.session.sessionType) ?? sessionTypeById('whiteboard')
      let baseTitle = snap.session.title
      if (previewType && isDefaultUntitledTitle(baseTitle)) {
        baseTitle = defaultUntitledTitle(previewType.label)
      }
      const myTitleRows = instances.filter((i) => onMyRoster(i))
      const copiedTitle = nextCopyTitle(baseTitle, myTitleRows)
      const titleChanged = copiedTitle !== snap.session.title
      if (titleChanged) {
        snap.session.title = copiedTitle
        snap.title = copiedTitle
      }
      const stampType = Boolean(previewType && !snap.session.sessionType)
      if (previewType && stampType) {
        snap.session.sessionType = previewType.id
        snap.session.boardComplexity = previewType.palette
      }
      snap.session.boardLocationLocked = false
      if (camera) {
        snap.session = applyBoardCameraToSession(snap.session, camera)
      }
      snap.session.activeMapId = snap.session.activeMapId || MAP_POSTER_STYLE_ID
      const metaFields: SessionFieldPatch = {
        ...(titleChanged ? { title: copiedTitle } : {}),
        ...(previewType && stampType
          ? {
              sessionType: previewType.id,
              boardComplexity: previewType.palette,
            }
          : {}),
      }
      if (Object.keys(metaFields).length) {
        await hubSetSessionFields(snap.id, metaFields)
      }
      if (camera) {
        await hubSetSessionFields(snap.id, {
          camera,
          ...(snap.session.boardVersions?.length
            ? { boardVersions: snap.session.boardVersions }
            : {}),
        })
      }
      await finishTemplateUseLanding(snap)
      if (camera) mapApiRef.current?.jumpToCamera(camera)
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not create this session.'))
      setCloningTemplateId(null)
    }
  }

  async function useTemplate(id: string): Promise<void> {
    if (!accessPerms.has('scenarios.create')) return
    if (hubActionsLocked || cloningTemplateId) return
    const tmpl = templates.find((t) => t.id === id)
    const isBlankTmpl =
      isBlankBoardTemplateId(id) ||
      isBlankBoardTemplateId(tmpl?.id) ||
      isBlankBoardTemplateId(tmpl?.session.id)
    if (isBlankTmpl) {
      setBoardLocationEditor({
        mode: 'template-pre',
        templateId: id,
        camera: BOARD_LOCATION_PREVIEW_IDLE,
        mapId: MAP_POSTER_STYLE_ID,
      })
      return
    }
    // Stay on Templates with an in-card loader (no rail banner / list jump) until ready.
    setCloningTemplateId(id)
    try {
      const snap = await createScenarioInstance(id, user)
      snap.session = placeSessionOnNextClock(snap.session, { blank: false })
      const previewType = sessionTypeById(tmpl?.session.sessionType)
      let baseTitle = snap.session.title
      const myTitleRows = instances.filter((i) => onMyRoster(i))
      const copiedTitle = nextCopyTitle(baseTitle, myTitleRows)
      const titleChanged = copiedTitle !== snap.session.title
      if (titleChanged) {
        snap.session.title = copiedTitle
        snap.title = copiedTitle
      }
      const stampType = Boolean(previewType && !snap.session.sessionType)
      if (previewType && stampType) {
        snap.session.sessionType = previewType.id
        snap.session.boardComplexity = previewType.palette
      }
      if (typeof snap.session.boardLocationLocked !== 'boolean') {
        snap.session.boardLocationLocked = true
      }
      if (titleChanged || stampType) {
        void hubSetSessionFields(snap.id, {
          ...(titleChanged ? { title: copiedTitle } : {}),
          ...(previewType && stampType
            ? {
                sessionType: previewType.id,
                boardComplexity: previewType.palette,
              }
            : {}),
        }).catch((err) => {
          setServerNotice(studioActionMessage(err, 'Could not save this copy.'))
        })
      }
      await finishTemplateUseLanding(snap)
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not create this session.'))
      setCloningTemplateId(null)
    }
  }

  async function confirmBoardLocationEditor(camera: BoardCamera): Promise<void> {
    const editor = boardLocationEditor
    if (!editor) return
    if (hubActionsLocked) {
      setServerNotice(hubLockTitle)
      return
    }
    setBoardLocationBusy(true)
    try {
      if (editor.mode === 'template-pre') {
        const blankId = editor.templateId
        setBoardLocationEditor(null)
        await createFromBlankTemplate(camera, blankId)
        return
      }
      if (!editor.instanceId) return
      // No-op confirm — keep the existing framing (map may have float noise; use tolerance).
      if (boardCamerasEqual(editor.camera, camera)) {
        setBoardLocationEditor(null)
        return
      }
      await persistBoardCamera(editor.instanceId, camera, { jumpLive: true })
      flashField('boardLocation')
      setBoardLocationEditor(null)
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not save the board location.'))
    } finally {
      setBoardLocationBusy(false)
    }
  }

  function cancelBoardLocationEditor(): void {
    setBoardLocationEditor(null)
  }

  /** Close confirm/share/delete overlays so a new modal (wizard, etc.) is the only layer. */
  function dismissStudioOverlays(): void {
    setSharePanelOpen(false)
    setGoLiveConfirmOpen(false)
    setStopConfirmOpen(false)
    setStopRunConfirmOpen(false)
    setPrivateConfirmOpen(false)
    setSessionUnavailableOpen(false)
    setDeclineThanksOpen(false)
    setPendingDeleteId(null)
    setPendingDeleteObjectId(null)
    setPendingRemovePeopleIds(null)
  }

  /** Hard boot / reload — drop every modal so loaders are not covered by stale dialogs. */
  function dismissAllStudioModals(): void {
    dismissStudioOverlays()
    setGetStartedOpen(false)
  }

  async function downloadSessionPlanPdf(
    sessionDoc: ScenarioSession,
    roster: readonly ScenarioPerson[],
    opts?: { fromTemplate?: boolean; instanceId?: string | null }
  ): Promise<void> {
    if (hubActionsLocked) return
    try {
      if (opts?.fromTemplate) {
        await fetchScenarioSnapshotPdf({
          kind: 'template-plan',
          session: sessionDoc,
          people: roster,
          title: sessionDoc.title,
        })
        return
      }
      if (opts?.instanceId) {
        await fetchScenarioInstancePdf(opts.instanceId, 'plan')
        return
      }
      // Unsaved local board — still QuestPDF via snapshot POST (no client HTML).
      await fetchScenarioSnapshotPdf({
        kind: 'plan',
        session: sessionDoc,
        people: roster,
        title: sessionDoc.title,
      })
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not download the plan PDF.'))
    }
  }

  async function downloadServerDocPdf(kind: 'cheatsheet' | 'trainer'): Promise<void> {
    if (hubActionsLocked) return
    try {
      console.info('[ScenarioStudio] PDF request', kind)
      await fetchScenarioDocPdf(kind)
      console.info('[ScenarioStudio] PDF ok', kind)
    } catch (err) {
      setServerNotice(
        studioActionMessage(
          err,
          kind === 'trainer'
            ? 'Could not download the Trainer Guide.'
            : 'Could not download the Cheatsheet.'
        )
      )
    }
  }

  async function downloadSessionSummaryPdf(
    _run: ScenarioRun,
    _roster: readonly ScenarioPerson[],
    instanceId: string,
    opts?: { aar?: boolean }
  ): Promise<void> {
    if (hubActionsLocked) return
    const id = instanceId.trim()
    if (!id) return
    try {
      await fetchScenarioInstancePdf(id, opts?.aar ? 'aar' : 'summary')
    } catch (err) {
      setServerNotice(
        studioActionMessage(
          err,
          opts?.aar
            ? 'Could not download the After Action Report.'
            : 'Could not download the Run Summary.'
        )
      )
    }
  }

  function openGetStartedWizard(entry: 'auto' | 'menu' = 'menu'): void {
    if (hubActionsLocked) return
    dismissStudioOverlays()
    const firstVisit = !hasSeenGetStarted()
    const hasOtherContent = instances.some((inst) => onMyRoster(inst) || inst.id === instanceId)
    setGetStartedForceArrive(entry === 'auto' || firstVisit || !hasOtherContent)
    setGetStartedDismissLabel(entry === 'auto' ? 'Not now' : 'Cancel')
    getStartedPendingIdRef.current = null
    getStartedResumeRef.current = null
    getStartedAutoPromptedRef.current = true
    setGetStartedPhase('pick')
    setGetStartedType(null)
    setGetStartedDoneMeta(null)
    setGetStartedOpen(true)
  }

  function wizardFirstName(): string {
    return (
      profileFieldsFromBlob(readRegistrationProfileJson()).first ||
      user.name.trim().split(/\s+/)[0] ||
      ''
    )
  }

  async function startFromWizard(result: WizardStartResult): Promise<void> {
    if (hubActionsLocked) {
      setServerNotice(hubLockTitle)
      return
    }
    if (!accessPerms.has('scenarios.create')) return
    const profile = sessionTypeById(result.typeId)
    if (!profile) return

    const boardComplexity = result.boardComplexity
    const wizardAnswers = result.answers
    const createdAtIso = new Date().toISOString()
    const openNow = getStartedForceArrive

    setGetStartedBusy(true)
    setGetStartedType(result.typeId)
    if (!openNow) {
      getStartedResumeRef.current = {
        instanceId,
        libraryMode,
        previewTemplateId,
      }
    }

    const blankTitle = nextCopyTitle(
      defaultWizardTitle(wizardFirstName(), profile.label),
      instances
    )

    const placeCamera = result.kind === 'create' ? result.camera : null
    const wantSample = result.kind === 'sample'
    const sampleTemplateId = wantSample ? result.templateId || YUCCA_GAS_LEAK_ID : null

    async function finishBlank(): Promise<boolean> {
      const blankKey = blankLibraryIdForType(profile!.id)
      const blankTmpl =
        templates.find((t) => t.session.id === blankKey || t.id === blankKey) ??
        templates.find((t) => t.session.id === BLANK_BOARD_ID || t.id === BLANK_BOARD_ID) ??
        null
      if (!blankTmpl) {
        setServerNotice('Could not create this session.')
        return false
      }
      const wizardOrigin = {
        createdAtIso,
        templateId: blankTmpl.id,
        typeId: profile!.id,
        boardComplexity,
        answers: wizardAnswers,
      }
      const snap = await createScenarioInstance(blankTmpl.id, user)
      snap.session = placeSessionOnNextClock(snap.session, { blank: true })
      snap.session.title = blankTitle
      snap.title = blankTitle
      snap.session.sessionType = profile!.id
      snap.session.boardComplexity = boardComplexity
      snap.session.rolePlayEnabled = profile!.rolePlay === true
      snap.session.wizardOrigin = wizardOrigin
      snap.session.boardLocationLocked = false
      // Match hub park / wizard backdrop — same Streets plate, no basemap flash on reselect.
      snap.session.activeMapId = MAP_POSTER_STYLE_ID
      if (placeCamera) {
        snap.session = applyBoardCameraToSession(snap.session, placeCamera)
      }
      // Persist before join so Snapshot/heal cannot re-seed the blank Yucca/idle camera.
      // Camera is its own write so a Fields failure cannot look like a successful create.
      await hubSetSessionFields(snap.id, {
        title: blankTitle,
        sessionType: profile!.id,
        boardComplexity,
        rolePlayEnabled: profile!.rolePlay === true,
        wizardOrigin,
        activeMapId: MAP_POSTER_STYLE_ID,
      })
      if (placeCamera) {
        await hubSetSessionFields(snap.id, {
          camera: placeCamera,
          ...(snap.session.boardVersions?.length
            ? { boardVersions: snap.session.boardVersions }
            : {}),
        })
      }
      const inst = instanceFromSnapshot(snap)
      setInstances((prev) => [inst, ...prev.filter((row) => row.id !== inst.id)])
      getStartedPendingIdRef.current = inst.id
      setGetStartedDoneMeta({})
      if (openNow) {
        applyServerSnapshot(snap)
        await joinScenarioInstance(snap.id, snap.clock.seq)
        setDock(dockAfterWizardCreate(profile!.id, snap.session.timeline?.length ?? 0))
        if (placeCamera) mapApiRef.current?.jumpToCamera(placeCamera)
      }
      setServerNotice(null)
      return true
    }

    async function finishSample(templateId: string): Promise<boolean> {
      const tmpl = templates.find((t) => t.id === templateId || t.session.id === templateId) ?? null
      if (!tmpl) return false
      const wizardOrigin = {
        createdAtIso,
        templateId: tmpl.id,
        typeId: profile!.id,
        boardComplexity,
        answers: wizardAnswers,
      }
      const snap = await createScenarioInstance(tmpl.id, user)
      snap.session = placeSessionOnNextClock(snap.session, { blank: false })
      const sampleTitle = nextCopyTitle(snap.session.title || tmpl.title, instances)
      snap.session.title = sampleTitle
      snap.title = sampleTitle
      snap.session.sessionType = profile!.id
      snap.session.boardComplexity = boardComplexity
      snap.session.rolePlayEnabled = profile!.rolePlay === true
      snap.session.wizardOrigin = wizardOrigin
      if (typeof snap.session.boardLocationLocked !== 'boolean') {
        snap.session.boardLocationLocked = !isBlankBoardTemplateId(tmpl.id)
      }
      try {
        await hubSetSessionFields(snap.id, {
          title: sampleTitle,
          sessionType: profile!.id,
          boardComplexity,
          rolePlayEnabled: profile!.rolePlay === true,
          wizardOrigin,
        })
      } catch (err) {
        setServerNotice(studioActionMessage(err, 'Could not save this session.'))
      }
      const inst = instanceFromSnapshot(snap)
      setInstances((prev) => [inst, ...prev.filter((row) => row.id !== inst.id)])
      getStartedPendingIdRef.current = inst.id
      setGetStartedDoneMeta({ title: sampleTitle })
      if (openNow) {
        applyServerSnapshot(snap)
        await joinScenarioInstance(snap.id, snap.clock.seq)
        setDock(dockAfterWizardCreate(profile!.id, snap.session.timeline?.length ?? 0))
      }
      setServerNotice(null)
      return true
    }

    try {
      let ok = false
      if (wantSample && sampleTemplateId) {
        try {
          ok = await finishSample(sampleTemplateId)
        } catch {
          ok = false
        }
        if (!ok && placeCamera) {
          ok = await finishBlank()
        }
      } else {
        ok = await finishBlank()
      }
      if (!ok) {
        setGetStartedOpen(false)
        return
      }
      markGetStartedSeen()
      setGetStartedPhase('done')
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not create this session.'))
      setGetStartedOpen(false)
    } finally {
      setGetStartedBusy(false)
    }
  }

  async function persistBoardCamera(
    targetInstanceId: string,
    camera: BoardCamera,
    opts?: { jumpLive?: boolean },
  ): Promise<void> {
    const base =
      instancesRef.current.find((i) => i.id === targetInstanceId)?.session ??
      (instanceIdRef.current === targetInstanceId ? snapshotRef.current.session : null)
    const withCam = base ? applyBoardCameraToSession(base, camera) : null
    setInstances((prev) =>
      prev.map((row) => {
        if (row.id !== targetInstanceId) return row
        return { ...row, session: applyBoardCameraToSession(row.session, camera) }
      }),
    )
    if (instanceIdRef.current === targetInstanceId) {
      commitSessionFields((prev) => applyBoardCameraToSession(prev, camera))
      if (opts?.jumpLive !== false) {
        mapApiRef.current?.jumpToCamera(camera)
      }
    }
    const fields: SessionFieldPatch = { camera }
    if (withCam?.boardVersions?.length) {
      fields.boardVersions = withCam.boardVersions
    }
    await hubSetSessionFields(targetInstanceId, fields)
  }

  function finishWizardGoToSession(): void {
    const pendingId = getStartedPendingIdRef.current
    getStartedPendingIdRef.current = null
    getStartedResumeRef.current = null
    setGetStartedOpen(false)
    setGetStartedPhase('pick')
    setLibraryMode(false)
    const pendingRow = pendingId ? instances.find((row) => row.id === pendingId) : null
    setDock(dockAfterWizardCreate(getStartedType, pendingRow?.session.timeline.length ?? 0))
    if (pendingId) {
      if (instanceIdRef.current !== pendingId) selectInstance(pendingId)
      revealInstanceCard(pendingId)
    }
  }

  function finishWizardDismiss(): void {
    const resume = getStartedResumeRef.current
    getStartedPendingIdRef.current = null
    getStartedResumeRef.current = null
    setGetStartedOpen(false)
    setGetStartedPhase('pick')
    if (!resume) return
    if (resume.libraryMode || resume.previewTemplateId) {
      selectTemplate(
        resume.previewTemplateId ?? defaultTemplateId(templatesRef.current),
        undefined,
        resume.libraryMode
      )
      return
    }
    if (resume.instanceId) selectInstance(resume.instanceId)
  }

  function rememberServerInstance(nextId: string): void {
    try {
      if (nextId) localStorage.setItem(LAST_SERVER_INSTANCE_KEY, nextId)
      else localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
    } catch {
      /* private mode */
    }
  }

  /** Drop a session that the server deleted. Viewers of that board land on the Scenarios pane. */
  function applyDeleted(id: string): void {
    const next = instancesRef.current.filter((i) => i.id !== id)
    if (next.length === instancesRef.current.length) return
    instancesRef.current = next
    setInstances(next)
    setPendingDeleteId((cur) => (cur === id ? null : cur))
    try {
      writeServerInstanceIds(readServerInstanceIds().filter((row) => row !== id))
    } catch {
      /* private mode */
    }
    const wasCurrent = instanceIdRef.current === id && !previewTemplateIdRef.current
    if (!wasCurrent) {
      try {
        if (localStorage.getItem(LAST_SERVER_INSTANCE_KEY) === id) {
          localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
        }
      } catch {
        /* private mode */
      }
      return
    }
    // Discard/delete from Settings must not leave Settings open on the next board.
    setDock('scenarios')
    const owned = ownedBoardsFor(next, user.email)
    const resume = pickResumeOwnedBoard(owned, readLastServerInstanceId())
    if (resume) {
      applyInstance(resume)
      void joinScenarioInstance(resume.id, resume.studioClock?.seq ?? 0).catch(() => {})
      rememberServerInstance(resume.id)
      return
    }
    parkSessionsHub()
  }

  function inviteNoticeLine(
    notice: string,
    actor: string | null,
    _title: string,
    grantRole: string | null,
    onBoard: boolean,
    sessionType?: string | null
  ): string {
    const shortName = (() => {
      const parts = actor?.trim().split(/\s+/) ?? []
      // Strip a leading role word only when a real name remains ("Trainer Jimmy" → "Jimmy").
      // Keep demo names like "Participant 1" / "Admin 1" intact.
      if (parts.length > 1 && ['Owner', 'Trainer', 'Editor', 'Participant'].includes(parts[0]!)) {
        const rest = parts.slice(1).join(' ')
        if (rest && !/^\d+$/.test(rest)) return rest
      }
      return actor?.trim() || 'Someone'
    })()
    // Prefer kind over long board titles so toasts stay scannable.
    const kindLabel = sessionTypeById(sessionType)?.label?.toLowerCase() || 'session'
    const kind = `${/^[aeiou]/i.test(kindLabel) ? 'an' : 'a'} ${kindLabel}`
    if (notice === 'nudge') return `${shortName} reminded you about ${kind}.`
    if (notice === 'invite' || notice === 'resend' || notice === 'reinvite') {
      return `${shortName} invited you to ${kind}.`
    }
    if (notice === 'joined') {
      return onBoard
        ? `${shortName} marked you accepted.`
        : `${shortName} marked you accepted for ${kind}.`
    }
    if (notice === 'removed') return `${shortName} removed you from ${kind}.`
    if (notice === 'grant') {
      const role = (grantRole ?? 'role').toLowerCase()
      const article = /^[aeiou]/i.test(role) ? 'an' : 'a'
      return `${shortName} made you ${article} ${role}.`
    }
    return `${shortName} invited you to ${kind}.`
  }

  function applyInvited(raw: unknown, actor: string | null): void {
    const body =
      raw && typeof raw === 'object'
        ? (raw as {
            notice?: unknown
            grantRole?: unknown
            targetEmail?: unknown
            id?: unknown
          })
        : {}
    const notice = typeof body.notice === 'string' ? body.notice : 'invite'
    const grantRole = typeof body.grantRole === 'string' ? body.grantRole : null
    const targetEmail =
      typeof body.targetEmail === 'string' ? body.targetEmail.trim().toLowerCase() : ''
    const mine = user.email.trim().toLowerCase()
    if (targetEmail && mine && targetEmail !== mine) return
    const rawId = typeof body.id === 'string' ? body.id : ''
    let snap: ServerSnapshot
    try {
      snap = snapshotFromWire(raw, user)
    } catch {
      if (rawId) {
        void fetchScenarioInstance(rawId, user)
          .then((loaded) => {
            const inst = instanceFromSnapshot(loaded)
            if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
            setInstances((prev) => {
              const idx = prev.findIndex((row) => row.id === inst.id)
              if (idx < 0) return [inst, ...prev]
              const copy = prev.slice()
              copy[idx] = { ...copy[idx]!, ...inst, people: inst.people }
              return copy
            })
          })
          .catch(() => {})
      }
      return
    }
    const inst = instanceFromSnapshot(snap)
    if (inst.studioClock) clocksRef.current.set(inst.id, inst.studioClock)
    const sameBoardId = (a: string | null | undefined, b: string | null | undefined) =>
      Boolean(a && b && a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase())
    const onThisBoard = sameBoardId(instanceIdRef.current, inst.id) && !previewTemplateIdRef.current
    // Toast uses grantRole; Join card reads people[].roles — merge so they cannot diverge.
    const grantRoleNorm = (grantRole ?? '').trim().toLowerCase()
    let roster = inst.people
    if (
      notice === 'grant' &&
      grantRoleNorm &&
      grantRoleNorm !== 'participant' &&
      (grantRoleNorm === 'editor' || grantRoleNorm === 'trainer' || grantRoleNorm === 'owner')
    ) {
      roster = roster.map((person) => {
        if (person.isDeleted || !sameLoginEmail(person.email, user.email)) return person
        if (person.roles.some((r) => r.toLowerCase() === grantRoleNorm)) return person
        return {
          ...person,
          roles: [...person.roles, grantRoleNorm as PersonRole],
        }
      })
    }
    // SendInvite can race SetPeople — force the invite flag so Join / Invited show without reload.
    if (
      notice === 'invite' ||
      notice === 'resend' ||
      notice === 'reinvite' ||
      notice === 'nudge'
    ) {
      roster = roster.map((person) => {
        if (person.isDeleted || !sameLoginEmail(person.email, user.email)) return person
        const next: ScenarioPerson = { ...person, hasBeenInvited: true }
        if (notice === 'reinvite' && person.status === 'declined') next.status = 'pending'
        return next
      })
    }
    const instWithRoster = roster === inst.people ? inst : { ...inst, people: roster }
    if (notice === 'published') {
      setInstances((prev) => {
        const idx = prev.findIndex((row) => sameBoardId(row.id, inst.id))
        if (idx < 0) return [instWithRoster, ...prev]
        const copy = prev.slice()
        const current = copy[idx]!
        copy[idx] = onThisBoard
          ? { ...current, audienceType: inst.audienceType }
          : { ...current, ...instWithRoster }
        return copy
      })
      return
    }
    // Removal: drop from the catalog when not on that board so Invited clears live.
    // On-board: keep the row so the open session does not vanish mid-view.
    if (notice === 'removed') {
      if (onThisBoard) {
        setPeople(roster)
        peopleRef.current = roster
        setInstances((prev) =>
          prev.map((row) => (sameBoardId(row.id, inst.id) ? { ...row, people: roster } : row))
        )
      } else if (!personOnRoster(roster, user.email)) {
        setInstances((prev) => prev.filter((row) => !sameBoardId(row.id, inst.id)))
      } else {
        setInstances((prev) => {
          const idx = prev.findIndex((row) => sameBoardId(row.id, inst.id))
          if (idx < 0) return prev
          const copy = prev.slice()
          copy[idx] = { ...copy[idx]!, ...instWithRoster }
          return copy
        })
      }
      return
    }
    if (!onThisBoard) {
      setInstances((prev) => {
        const idx = prev.findIndex((row) => sameBoardId(row.id, inst.id))
        if (idx < 0) return [instWithRoster, ...prev]
        const copy = prev.slice()
        copy[idx] = { ...copy[idx], ...instWithRoster }
        return copy
      })
    } else if (
      notice === 'joined' ||
      notice === 'grant' ||
      notice === 'nudge' ||
      notice === 'invite' ||
      notice === 'resend' ||
      notice === 'reinvite'
    ) {
      setPeople(roster)
      peopleRef.current = roster
      setInstances((prev) =>
        prev.map((row) => (sameBoardId(row.id, inst.id) ? { ...row, people: roster } : row))
      )
    }
    // Declined: no invite pings (except Invite again, which already flipped them back to pending).
    // Joined/RSVP: no “accepted” toast — presence “is here” covers arrival.
    if (notice === 'invite' || notice === 'resend' || notice === 'nudge') {
      const row = inst.people.find((p) => !p.isDeleted && sameLoginEmail(p.email, user.email))
      if (row?.status === 'declined') return
    }
    // Never announce join/removal — presence / list updates cover it.
    if (notice === 'joined') return
    // Participant is the default seat — required, not something to advertise.
    if (notice === 'grant' && (grantRole ?? '').toLowerCase() === 'participant') return
    // Role grants on another board: update the catalog only — don't toast as if this board changed.
    if (notice === 'grant' && !onThisBoard) return
    pushInfoToast(
      inviteNoticeLine(
        notice,
        actor,
        inst.session.title,
        grantRole,
        onThisBoard,
        inst.session.sessionType
      ),
      false,
      3
    )
  }

  function respondToInvite(targetId: string, status: 'joined' | 'declined'): void {
    const me = user.email.trim().toLowerCase()
    if (!me) {
      setServerNotice('Sign in with the invited account to join this session.')
      return
    }
    const seatOf = (rows: readonly ScenarioPerson[]) =>
      rows.find((person) => !person.isDeleted && person.email.trim().toLowerCase() === me)
    const openRows =
      targetId === instanceIdRef.current && !previewTemplateIdRef.current
        ? peopleRef.current
        : (instancesRef.current.find((row) => row.id === targetId)?.people ?? [])
    if (!seatOf(openRows)) {
      setServerNotice(
        'This invite is for a different account. Sign in with the invited email to join.'
      )
      return
    }
    const priorPeople =
      targetId === instanceIdRef.current && !previewTemplateIdRef.current
        ? peopleRef.current.slice()
        : (instancesRef.current.find((row) => row.id === targetId)?.people ?? []).slice()
    const priorInstances = instancesRef.current.slice()
    const mine = (rows: readonly ScenarioPerson[]) =>
      rows.map((person) =>
        person.email.trim().toLowerCase() === me && !person.isDeleted
          ? { ...person, status, hasBeenInvited: true }
          : person
      )
    setInviteJoinPeekId(null)
    const onThisBoard =
      targetId === instanceIdRef.current && !previewTemplateIdRef.current
    const nextPeople = mine(onThisBoard ? peopleRef.current : openRows)
    const nextInstances = instancesRef.current.map((row) =>
      row.id === targetId ? { ...row, people: nextPeople } : row
    )
    // Sync ref before leave — selectTemplate must not flush stale React state and revive pending.
    instancesRef.current = nextInstances
    setInstances(nextInstances)
    if (onThisBoard) {
      setPeople(nextPeople)
      peopleRef.current = nextPeople
    }
    if (status === 'declined') {
      setDeclineThanksOpen(true)
      // Answered the invite — drop the deep-link arrival so reload is not a soft 404.
      clearStudioArrival()
      leaveAfterDecline(targetId, nextInstances)
    } else {
      setDeclineThanksOpen(false)
    }
    void hubRespondToInvite(targetId, status).catch((err) => {
      instancesRef.current = priorInstances
      setInstances(priorInstances)
      if (onThisBoard) {
        setPeople(priorPeople)
        peopleRef.current = priorPeople
      }
      setServerNotice(studioActionMessage(err, 'Could not respond to the invite.'))
    })
  }

  /** After No: leave the declined board quietly (no Session-not-available modal). */
  function leaveAfterDecline(targetId: string, rows: ScenarioInstance[] = instancesRef.current): void {
    if (targetId !== instanceIdRef.current || previewTemplateIdRef.current) return
    try {
      if (localStorage.getItem(LAST_SERVER_INSTANCE_KEY) === targetId) {
        localStorage.removeItem(LAST_SERVER_INSTANCE_KEY)
      }
    } catch {
      /* private mode */
    }
    const stillMine = (inst: ScenarioInstance) =>
      seeAllInstances || personOnRoster(inst.people, user.email) || instanceOpenViaAudience(inst)
    const next =
      rows.find((i) => i.id !== targetId && i.lifecycle === 'running' && stillMine(i)) ??
      rows.find((i) => i.id !== targetId && stillMine(i) && i.lifecycle !== 'completed') ??
      rows.find((i) => i.id !== targetId && stillMine(i))
    if (next) {
      selectInstance(next.id)
      return
    }
    instanceIdRef.current = ''
    setInstanceId('')
    rememberServerInstance('')
    // Pass rows so selectTemplate does not flushInstance(stale pending) over the decline.
    selectTemplate(defaultTemplateId(templatesRef.current), rows, true)
  }

  function deleteInstance(id: string): void {
    if (hubActionsLocked) {
      setServerNotice(hubLockTitle)
      return
    }
    setPendingDeleteId(null)
    void hubDeleteInstance(id).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not delete this session.'))
    })
  }

  function deleteObject(id: string): void {
    const target = session.objects.find((o) => o.id === id)
    if (!canMutateObject(target)) return
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.filter((o) => o.id !== id),
      // Drop sync row for this object; strip it from authored beats but keep the beat.
      timeline: prev.timeline
        .filter((ev) => !(isSyncedTimelineEvent(ev) && ev.id === syncedTimelineEventId(id)))
        .map((ev) => {
          if (isSyncedTimelineEvent(ev)) return ev
          const actions = ev.actions.filter((a) => a.objectId !== id)
          return actions.length === ev.actions.length ? ev : { ...ev, actions }
        }),
    }))
    setPendingDeleteObjectId(null)
    setSelectedId(null)
    setNearbyCandidates([])
    setNearbyPicks(new Set())
    setNearbyHoverKey(null)
    setNearbyHint(null)
    if (notesCtx === id) setNotesCtx('scenario')
  }

  function openScenarioSettings(id: string): void {
    if (id !== instanceId || previewTemplateId) selectInstance(id)
    setTitleError(null)
    setDock('settings')
  }

  function titleTaken(title: string, exceptId: string | undefined): boolean {
    const key = title.trim().toLowerCase()
    if (!key) return false
    // Sessions only — template titles (e.g. stock "Untitled whiteboard") live in another list.
    return instances.some((i) => i.id !== exceptId && i.session.title.trim().toLowerCase() === key)
  }

  function persistRoomChat(lines: readonly ScenarioComment[]): void {
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    pendingRoomChatRef.current = [...lines]
    void flushRoomChatPersist()
  }

  async function flushRoomChatPersist(): Promise<void> {
    if (roomChatFlushInFlightRef.current) return
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    const payload = pendingRoomChatRef.current
    if (!payload) return
    pendingRoomChatRef.current = null
    roomChatFlushInFlightRef.current = true
    try {
      await hubSetSessionFields(id, { roomChat: payload })
    } catch (err) {
      setServerNotice(studioActionMessage(err, 'Could not save chat.'))
    } finally {
      roomChatFlushInFlightRef.current = false
      if (pendingRoomChatRef.current) void flushRoomChatPersist()
    }
  }

  function persistSessionField(fields: SessionFieldPatch): void {
    if (previewTemplateId || !instanceId) return
    // After End, Settings stay read-only — except primary-owner designation (People stewardship).
    const keys = Object.keys(fields)
    const onlyPrimaryOwner =
      keys.length === 1 && keys[0] === 'primaryOwnerUserId'
    if (currentInstance?.lifecycle === 'completed' && !onlyPrimaryOwner) return
    if (keys.length === 1 && keys[0] === 'roomChat') {
      persistRoomChat(fields.roomChat ?? [])
      return
    }
    void hubSetSessionFields(instanceId, fields).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not save scenario settings.'))
    })
  }

  /** Keep session.primaryOwnerUserId aligned with who still holds owner (star target). */
  function syncPrimaryOwnerUserId(nextPeople: ScenarioPerson[]): void {
    const current = viewSession.primaryOwnerUserId?.trim() || null
    const nextOwners = activeOwners(nextPeople)
    let resolved: string | null
    if (!nextOwners.length) {
      resolved = null
    } else if (current && nextOwners.some((p) => personUserId(p) === current)) {
      resolved = current
    } else if (current) {
      // Designated lost Owner — move star to another remaining owner.
      resolved = resolveDesignatedOwnerUserId(null, nextPeople)
    } else {
      // Legacy boards with no primaryOwnerUserId: keep the prior sole owner when still owner.
      const prevOwners = activeOwners(people)
      const sole =
        prevOwners.length === 1 ? personUserId(prevOwners[0]!) : ''
      resolved =
        sole && nextOwners.some((p) => personUserId(p) === sole)
          ? sole
          : resolveDesignatedOwnerUserId(null, nextPeople)
    }
    if (resolved === current) return
    commitSessionFields((prev) => {
      const copy = { ...prev }
      if (resolved) copy.primaryOwnerUserId = resolved
      else delete copy.primaryOwnerUserId
      return copy
    })
    persistSessionField({ primaryOwnerUserId: resolved })
  }

  function setSessionPublished(next: boolean): void {
    if (hubActionsLocked) return
    if (!selectedIsPlanned || !rosterGrantsSession) return
    if (next && !canEditSettings && !accessPerms.has('scenarios.edit_board')) return
    commitSessionFields((prev) => {
      const copy = { ...prev }
      delete (copy as { markedReady?: unknown }).markedReady
      if (next) copy.published = true
      else delete copy.published
      return copy
    })
    setInstances((prev) =>
      prev.map((i) => {
        if (i.id !== instanceId) return i
        const session = { ...i.session }
        delete (session as { markedReady?: unknown }).markedReady
        if (next) session.published = true
        else delete session.published
        return { ...i, session }
      })
    )
    persistSessionField({ published: next })
  }

  function persistPeople(next: ScenarioPerson[], prior?: ScenarioPerson[]): Promise<void> {
    if (previewTemplateId || !instanceId) return Promise.resolve()
    const actor = actorPhrase()
    narrateRoster(next, actor)
    peopleRef.current = next
    setInstances((prev) => prev.map((i) => (i.id === instanceId ? { ...i, people: next } : i)))
    const id = instanceId
    return hubSetPeople(id, next, actor).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not save the roster.'))
      // Roll back optimistic roster so checkboxes / Remove match the server.
      if (prior && instanceIdRef.current === id) {
        peopleRef.current = prior
        setPeople(prior)
        setInstances((prev) =>
          prev.map((i) => (i.id === id ? { ...i, people: prior } : i)),
        )
      }
    })
  }

  function commitPeople(updater: (prev: ScenarioPerson[]) => ScenarioPerson[]): void {
    const prior = people
    const next = updater(people)
    setPeople(next)
    void persistPeople(next, prior)
  }

  /** Private drops a board from this login only when it is not on the roster and this account cannot see every session. */
  function audienceHidesFromMe(next: 'private' | 'public'): boolean {
    if (seeAllInstances || !currentInstance || onMyRoster(currentInstance)) return false
    return next !== 'public' || currentInstance.lifecycle === 'completed'
  }

  function persistAudienceType(next: 'private' | 'public'): void {
    if (previewTemplateId || !instanceId) return
    const id = instanceId
    audienceWriteIdRef.current = id
    setInstances((prev) => prev.map((i) => (i.id === id ? { ...i, audienceType: next } : i)))
    void hubSetAudienceType(id, next).catch((err) => {
      if (audienceWriteIdRef.current === id) audienceWriteIdRef.current = null
      setServerNotice(studioActionMessage(err, 'Could not save who can find this session.'))
    })
  }

  function persistAllowLinkGuests(next: boolean): void {
    if (previewTemplateId || !instanceId) return
    const id = instanceId
    setInstances((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i
        if (next) return { ...i, allowLinkGuests: true }
        const { allowLinkGuests: _drop, ...rest } = i
        return rest
      })
    )
    void hubSetAllowLinkGuests(id, next).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not save link access.'))
    })
  }

  function saveScenarioMeta(which: 'title' | 'summary'): void {
    if (which === 'title') {
      const title = titleDraft.trim() || session.title
      if (
        title.toLowerCase() !== session.title.trim().toLowerCase() &&
        titleTaken(title, currentInstance?.id)
      ) {
        setTitleError(
          'Choose a unique title — add a distinguishing character if it matches another scenario.'
        )
        return
      }
      setTitleError(null)
      if (title === session.title) return
      commitSessionFields((prev) => ({ ...prev, title }))
      persistSessionField({ title })
      flashField('title')
      return
    }
    const summary = summaryDraft.trim()
    if (summary === session.summary) return
    commitSessionFields((prev) => ({ ...prev, summary }))
    persistSessionField({ summary })
    flashField('summary')
  }

  function changeFoundationMap(mapId: string): void {
    if (mapLocked || (!viewingTemplate && !canEditSettings)) return
    const fromSession = session.maps.find((m) => m.id === mapId)
    const chosen = fromSession ?? mapRefById(mapId)
    // Keep styleUrl on the board so operator maps survive reload even if downloads clear.
    rememberExtraMaps([chosen])
    const offered = mapsOffered(session.maps)
    const alreadyOffered = offered.some((m) => m.id === chosen.id)
    const nextMaps = alreadyOffered
      ? offered.map((m) => (m.id === chosen.id ? { ...chosen } : m))
      : [...offered, { ...chosen }]
    const mapsPromoted = !alreadyOffered
    // Plate swap only — keep live lat/lng/zoom (do not home-reset or bump viewResetId).
    setSession((s) => ({ ...s, maps: nextMaps, activeMapId: mapId }))
    if (mapId === session.activeMapId && !mapsPromoted) return
    persistSessionField({
      activeMapId: mapId,
      maps: nextMaps,
    })
    flashField('activeMapId')
  }

  function openBoardLocationSettings(): void {
    if (hubActionsLocked) return
    if (viewingTemplate || boardHomeLocked || !canEditSettings) return
    if (boardLocationLocked) return
    if (!instanceId) return
    setBoardLocationEditor({
      mode: 'settings',
      instanceId,
      camera: { ...viewSession.camera },
      mapId: viewSession.activeMapId || MAP_POSTER_STYLE_ID,
    })
  }

  function startScenario(): void {
    if (!canOperateSession) return
    if (!instanceId) return
    setPathPreviewTotalMs(null)
    setPathPreviewStartMs(null)
    setPathPreviewPose(null)
    setPathPreviewObjectId(null)
    setPathPreviewWaypointIndex(null)
    setPathPreviewHolding(false)
    setPathHoldLabel(null)
    setVisPreviewTotalMs(null)
    setVisPreviewObjectId(null)
    setRadiusPreviewTotalMs(null)
    setRadiusPreviewStartMs(null)
    setRadiusPreviewObjectId(null)
    setWindPreviewTotalMs(null)
    setWindPreviewStartMs(null)
    setVisibilityFocusIndex(null)
    setTimelineFocusId(null)
    setElapsedMs(0)
    elapsedRef.current = 0
    persistExerciseTakePhase(0, false)
    clearInstantReplayPause()
    setTimeBarAutoHidden(false)
    setTimeBarPinned(false)
    const startIso = snapshotRef.current.session.storyStartTime
    const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRate)
    // Hold strip through late Planned Preview Clock echoes until Live Idle sticks.
    goLiveHoldIdRef.current = instanceId
    // One hub GoLive: Lifecycle=Live, playhead=story start, clock paused (not playing).
    const fresh: StudioClock = {
      seq: clocksRef.current.get(instanceId)?.seq ?? 0,
      lifecycle: 'running',
      clockStatus: 'paused',
      rate: stripRate,
      anchorStoryTimeIso: startIso,
      anchorActualTime: null,
      actualStartTime: new Date().toISOString(),
      actualEndTime: null,
      storyOriginIso: startIso,
    }
    clocksRef.current.set(instanceId, fresh)
    serverClockRef.current = fresh
    setServerClock(fresh)
    setStarted(true)
    setRunning(false)
    hubAdmittedRunningRef.current = false
    setStopped(false)
    setExerciseRunStopped(false)
    elapsedRef.current = 0
    setElapsedMs(0)
    hubWirePlayheadMsRef.current = 0
    setHubWirePlayheadMs(0)
    setInstances((prev) =>
      prev.map((i) =>
        i.id === instanceId
          ? {
              ...i,
              lifecycle: 'running',
              clockStatus: 'paused',
              studioClock: fresh,
              playheadStoryIso: startIso,
              actualStartTime: fresh.actualStartTime ?? i.actualStartTime,
            }
          : i
      )
    )
    setDock(sessionOffersStoryClock ? 'timeline' : 'scenarios')
    void hubGoLive(instanceId, stripRate)
      .then(() => {
        elapsedRef.current = 0
        setElapsedMs(0)
        hubWirePlayheadMsRef.current = 0
        setHubWirePlayheadMs(0)
        hubAdmittedRunningRef.current = false
      })
      .catch((err) => {
        if (goLiveHoldIdRef.current === instanceId) goLiveHoldIdRef.current = null
        setServerNotice(studioActionMessage(err, 'Could not start this session.'))
      })
  }

  function stopScenario(): void {
    if (!canOperateSession) return
    const cur = currentInstance
    if (!cur) return
    const wasRunning = running
    const now = new Date().toISOString()
    stopHoldIdRef.current = cur.id
    const frozen = clocksRef.current.get(cur.id)
    if (frozen) {
      const playhead = playheadIsoFromClock(frozen, cur.session.storyStartTime)
      const done: StudioClock = {
        ...frozen,
        lifecycle: 'completed',
        clockStatus: 'completed',
        anchorStoryTimeIso: playhead,
        anchorActualTime: null,
      }
      clocksRef.current.set(cur.id, done)
      if (serverClockRef.current) serverClockRef.current = done
    }
    setRunning(false)
    setStarted(false)
    setStopped(true)
    setCompletedOpen(true)
    setJustCompletedId(cur.id)
    // Leave Editing / Reviewing chrome — Done lands Previewing or run-review separately.
    exitTapeReview({ persist: false })
    setWatchingRunId(null)
    setBoardActivity('preview')
    // Stamp last take phase onto SessionJson before End so joiners see the same number.
    if (typeOffersSharedRun(cur.session.sessionType) && takeNumber > 0) {
      persistExerciseTakePhase(takeNumber, true)
    }
    pendingObjectRemovesRef.current.clear()
    pendingObjectUpsertsRef.current.clear()
    if (objectSaveTimerRef.current != null) {
      window.clearTimeout(objectSaveTimerRef.current)
      objectSaveTimerRef.current = null
    }
    setInstances((prev) =>
      prev.map((i) =>
        i.id === cur.id
          ? {
              ...i,
              lifecycle: 'completed' as const,
              clockStatus: 'completed' as const,
              studioClock: clocksRef.current.get(cur.id) ?? i.studioClock,
              actualEndTime: i.actualEndTime ?? now,
              session: {
                ...i.session,
                ...(takeNumber > 0
                  ? {
                      exerciseTakeNumber: Math.max(0, Math.floor(takeNumber)),
                      exerciseTakeStopped: true,
                    }
                  : {}),
              },
            }
          : i
      )
    )
    void hubStop(cur.id).catch((err) => {
      if (stopHoldIdRef.current === cur.id) stopHoldIdRef.current = null
      setJustCompletedId(null)
      setStopped(false)
      setStarted(true)
      setRunning(wasRunning)
      if (frozen) {
        clocksRef.current.set(cur.id, frozen)
        serverClockRef.current = frozen
      }
      setInstances((prev) =>
        prev.map((i) => {
          if (i.id !== cur.id) return i
          const next = {
            ...i,
            lifecycle: 'running' as const,
            clockStatus: frozen?.clockStatus ?? i.clockStatus,
            studioClock: frozen ?? i.studioClock,
          }
          delete next.actualEndTime
          return next
        })
      )
      setServerNotice(studioActionMessage(err, 'Could not stop this session.'))
    })
    setDock('scenarios')
  }

  function stopPathPreview(): void {
    setPathPreviewTotalMs(null)
    setPathPreviewStartMs(null)
    setPathPreviewPose(null)
    setPathPreviewObjectId(null)
    setPathPreviewWaypointIndex(null)
    setPathPreviewHolding(false)
    setPathHoldLabel(null)
    setInspectPreviewMs(null)
  }

  function stopVisPreview(): void {
    setVisPreviewTotalMs(null)
    setVisPreviewObjectId(null)
    setInspectPreviewMs(null)
  }

  function stopRadiusPreview(): void {
    setRadiusPreviewTotalMs(null)
    setRadiusPreviewStartMs(null)
    setRadiusPreviewObjectId(null)
    setInspectPreviewMs(null)
  }

  function stopWindPreview(): void {
    setWindPreviewTotalMs(null)
    setWindPreviewStartMs(null)
    setInspectPreviewMs(null)
    // Wind Preview may have been driving path pose (tornado) without a path Preview span.
    setPathPreviewPose(null)
    setPathPreviewObjectId(null)
    setPathPreviewWaypointIndex(null)
    setPathPreviewHolding(false)
    setPathHoldLabel(null)
  }

  function startTornadoStoryPreview(): void {
    stopPathPreview()
    stopVisPreview()
    stopWindPreview()
    stopRadiusPreview()
    setInspectPreviewMs(null)
    setHoverElapsedMs(null)
    elapsedRef.current = 0
    setElapsedMs(0)
    setWindAuthorMs(0)
    setTornadoPreviewFocus(true)
    const id = instanceIdRef.current
    const clock = id ? clocksRef.current.get(id) : undefined
    const life = clock?.lifecycle ?? 'planned'
    if (
      id &&
      clock &&
      !previewTemplateIdRef.current &&
      (canDrivePreviewRef.current || canOperateSessionRef.current)
    ) {
      driveDeckPlayAt(0, {
        playAs: life === 'planned' || canDrivePreviewRef.current ? 'previewPlay' : 'resume',
        rate: playbackRateRef.current,
      })
      return
    }
    runningRef.current = true
    setRunning(true)
    setClockEpoch((n) => n + 1)
  }

  function stopTornadoStoryPreview(): void {
    setTornadoPreviewFocus(false)
    const id = instanceIdRef.current
    const clock = id ? clocksRef.current.get(id) : undefined
    if (
      runningRef.current &&
      id &&
      clock &&
      !previewTemplateIdRef.current &&
      (canDrivePreviewRef.current || canOperateSessionRef.current)
    ) {
      enqueueStudioClock({ running: false, seekMs: elapsedRef.current })
      return
    }
    runningRef.current = false
    setRunning(false)
  }

  function seekStoryFromTornadoTick(ms: number): void {
    const dur = displayDurRef.current
    const next = Math.min(dur > 0 ? dur : 0, Math.max(0, Math.round(ms)))
    setTornadoPreviewFocus(false)
    setInspectPreviewMs(null)
    setWindAuthorMs(next)
    jumpTo(next)
  }

  /** Inspect timespan park — one story head, so the time bar and the map follow it. */
  function parkWindInspectPlayhead(ms: number): void {
    const next = Math.max(0, Math.round(ms))
    stopWindPreview()
    const id = instanceIdRef.current
    if (id && transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
    setWindAuthorMs(next)
    setHoverElapsedMs(null)
    runningRef.current = false
    setRunning(false)
    elapsedRef.current = next
    setElapsedMs(next)
    jumpTo(next)
  }

  function previewSelectedWind(): void {
    if (isLocalStorm(selectedSource?.type)) return
    const marks: number[] = []
    for (const f of windFrames) marks.push(off(f.at))
    if (marks.length < 2) return
    const first = Math.min(...marks)
    const last = Math.max(...marks)
    const fullSpan = Math.max(0, last - first)
    if (fullSpan <= 0) return
    // Resume from the Inspect park when it still leaves span ahead; otherwise restart at first.
    const head = inspectPreviewMs ?? windAuthorMs
    const fromPlayhead = head >= first && head < last - 1 ? Math.round(head) : first
    const span = Math.max(0, last - fromPlayhead)
    if (span <= 0) return
    setRunning(false)
    stopPathPreview()
    stopVisPreview()
    stopRadiusPreview()
    setHoverElapsedMs(null)
    setWindHoverIndex(null)
    setWindDraft(null)
    setWindSelectIndex(null)
    setInspectPreviewMs(fromPlayhead)
    setWindAuthorMs(fromPlayhead)
    setWindPreviewStartMs(fromPlayhead)
    setWindPreviewTotalMs(span)
    setWindPreviewEpoch((n) => n + 1)
  }

  function previewSelectedPath(): void {
    // Tornado: path Preview lives on the wind/timespan panel (combined storm preview).
    if (isLocalStorm(selectedSource?.type)) return
    const frames = selectedSource?.keyframes
    if (!frames?.length || !selectedId) return
    const sorted = sortPoseFrames(frames)
    if (sorted.length < 2) return
    const lastElapsed = off(sorted[sorted.length - 1]!.at)
    // Resume from the selected row when it still leaves path ahead to play.
    const fromFocus =
      waypointFocusIndex != null &&
      waypointFocusIndex >= 0 &&
      waypointFocusIndex < sorted.length - 1
        ? off(sorted[waypointFocusIndex]!.at)
        : null
    const startElapsed = fromFocus ?? off(sorted[0]!.at)
    const span = Math.max(0, lastElapsed - startElapsed)
    if (span <= 0) return
    setRunning(false)
    stopVisPreview()
    stopWindPreview()
    const start = posePreviewAtElapsed(sorted, atOff(startElapsed))
    setPathPreviewObjectId(selectedId)
    setPathPreviewStartMs(startElapsed)
    setPathPreviewTotalMs(span)
    setPathPreviewPose(start?.pose ?? { lng: sorted[0]!.lng, lat: sorted[0]!.lat })
    setPathPreviewWaypointIndex(start?.waypointIndex ?? waypointFocusIndex ?? 0)
    setPathPreviewHolding(Boolean(start?.holding))
    setInspectPreviewMs(startElapsed)
    setPathHoldLabel(
      start?.holding
        ? `Hold ${formatHoldCountdown(
            boardDeltaToAuthorVisibleMs(start.holdRemainingMs, authorClock)
          )}`
        : null
    )
    setPathPreviewEpoch((n) => n + 1)
  }

  function previewSelectedVisibility(): void {
    const frames = selectedSource?.visibilityKeyframes
    if (!frames?.length || !selectedId) return
    const sorted = sortVisFrames(frames)
    const span = visibilityPreviewSpanMs(sorted)
    if (sorted.length < 2 || span <= 0) return
    setRunning(false)
    stopPathPreview()
    stopWindPreview()
    stopRadiusPreview()
    const startElapsed = off(sorted[0]!.at)
    setVisPreviewObjectId(selectedId)
    setVisPreviewTotalMs(span)
    setVisibilityFocusIndex(0)
    setInspectPreviewMs(startElapsed)
    setVisPreviewEpoch((n) => n + 1)
  }

  function previewSelectedRadius(fromIndex?: number | null): void {
    const frames = selectedSource?.radiusKeyframes
    if (!frames?.length || !selectedId) return
    const sorted = sortNumberFrames(frames)
    if (sorted.length < 2) return
    // Resume from the selected row when it still leaves radius ahead to play.
    const focusAt =
      fromIndex != null && fromIndex >= 0 && fromIndex < sorted.length - 1 ? fromIndex : null
    const startElapsed = focusAt != null ? off(sorted[focusAt]!.at) : off(sorted[0]!.at)
    const lastElapsed = off(sorted[sorted.length - 1]!.at)
    const span = Math.max(0, lastElapsed - startElapsed)
    if (span <= 0) return
    setRunning(false)
    stopPathPreview()
    stopWindPreview()
    stopVisPreview()
    setRadiusPreviewObjectId(selectedId)
    setRadiusPreviewStartMs(startElapsed)
    setRadiusPreviewTotalMs(span)
    setRadiusFocusIndex(focusAt ?? 0)
    setInspectPreviewMs(startElapsed)
    setRadiusPreviewEpoch((n) => n + 1)
  }

  function toggleClock(): void {
    // Following Presenter story on Done: their playhead drives the room.
    if (followingStoryPresenter) return
    // Participants follow the shared playhead; only Owner/Trainer/Editor drive it.
    if (selectedIsPlanned && Boolean(instanceId) && !previewTemplateId && !canDrivePreview) {
      return
    }
    noteBoardPreviewActivity()
    const sharedPreview =
      selectedIsPlanned && Boolean(instanceId) && !previewTemplateId && canDrivePreview
    /**
     * Live story preview with no shared tape: pre–Run 1, or after “I’m done
     * reviewing” (between-takes on the working board). Still-Reviewing between
     * takes uses the tape overlay path below — not this Idle lane.
     */
    const sessionTapeId =
      typeof snapshotRef.current.session.tapeReviewRunId === 'string' &&
      snapshotRef.current.session.tapeReviewRunId.trim()
        ? snapshotRef.current.session.tapeReviewRunId.trim()
        : null
    const liveIdlePreview =
      inLiveRun &&
      canOperateSession &&
      Boolean(instanceId) &&
      !liveTakeActive &&
      !instantReplayEngaged &&
      !tapeReviewRef.current &&
      !sessionTapeId &&
      boardActivityRef.current !== 'watching' &&
      typeOffersSharedRun(viewSession.sessionType) &&
      (takeNumberRef.current === 0 || exerciseRunStoppedRef.current)
    if (running) {
      // Live tape review shares hub Pause (same as loadTapeReviewOverlay startPlay).
      // Local-only pause left the strip on || while the RAF ticker waited on a paused Clock.
      if (
        tapeReviewRef.current &&
        canOperateSession &&
        !selectedIsCompleted &&
        instanceId
      ) {
        const hubTape = clocksRef.current.get(instanceId)
        // Stuck chrome: local running=true after a no-op Play, hub still paused — Resume.
        if (hubTape?.clockStatus !== 'running') {
          setRunning(false)
          // fall through to the play path below
        } else {
          enqueueStudioClock({ running: false, seekMs: elapsedRef.current })
          return
        }
      } else if ((sharedPreview || liveIdlePreview) && instanceId) {
        enqueueStudioClock({ running: false, seekMs: elapsedRef.current })
        return
      } else {
        setRunning(false)
        return
      }
    }
    stopPathPreview()
    stopVisPreview()
    stopWindPreview()
    stopRadiusPreview()
    setHoverIds([])
    setHoverElapsedMs(null)
    // Between-takes Reviewing without overlay yet — arm tape then play (not live Idle).
    // Gate on shared tape id or local watching: after “I’m done reviewing” both are
    // cleared and Play must stay Live Idle (this used to resurrect latest Run N on A
    // only — persistTapeId:false — while P stayed Previewing).
    if (
      !tapeReviewRef.current &&
      exerciseRunStoppedRef.current &&
      takeNumberRef.current > 0 &&
      canOperateSession &&
      !selectedIsCompleted &&
      instanceId &&
      (sessionTapeId || boardActivityRef.current === 'watching')
    ) {
      const sess = snapshotRef.current.session
      const rid = sessionTapeId
      const run =
        (rid
          ? (sess.exerciseRuns ?? []).find((r) => r.id === rid)
          : null) ??
        latestExerciseRunForVersion(sess) ??
        latestExerciseRun(sess)
      if (run && runHasTapeSeed(run)) {
        beginFinishedRunTapeReview(run, {
          startPlay: true,
          persistTapeId: false,
          cueAtStart: false,
        })
        return
      }
    }
    // Done / tape review: Space at stop wraps to story start (same as IR look-back).
    if (tapeReviewRef.current) {
      const tapeCap = tapeAvailableUntilMsRef.current
      const atTapeEnd =
        tapeCap != null && Number.isFinite(tapeCap) && elapsedRef.current >= tapeCap - 40
      const headMs = atTapeEnd ? 0 : elapsedRef.current
      if (atTapeEnd) {
        elapsedRef.current = 0
        setElapsedMs(0)
      }
      const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
      localPlayAnchorRef.current = {
        wall: performance.now(),
        elapsed: headMs,
        rate: stripRate,
      }
      noteBoardReviewActivity()
      // Live between-takes: Resume the shared clock (local setRunning alone never advances —
      // the interval bails when a hub Clock exists, and RAF only ticks clockStatus=running).
      if (canOperateSession && !selectedIsCompleted && instanceId) {
        // Direct ResumeAt — pump stayed wedged after scrub-begin Pause (no hub TX).
        driveDeckPlayAt(headMs, { playAs: 'resume', rate: stripRate })
        return
      }
      // Done: hub Clock is blocked — local tape play.
      setRunning(true)
      setClockEpoch((n) => n + 1)
      return
    }
    if (sharedPreview && instanceId) {
      const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRate)
      const restartFromStart = clockEnded || (displayDur > 0 && elapsedRef.current >= displayDur)
      // Always pass seekMs so owns-head freezes the plane during PreviewPlay RTT.
      const headMs = restartFromStart ? 0 : elapsedRef.current
      driveDeckPlayAt(headMs, { playAs: 'previewPlay', rate: stripRate })
      return
    }
    if (liveIdlePreview && instanceId) {
      const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRate)
      const restartFromStart = clockEnded || (displayDur > 0 && elapsedRef.current >= displayDur)
      // Pre–Run 1 Live Idle only (between-takes uses tape Reviewing above).
      driveDeckPlayAt(restartFromStart ? 0 : elapsedRef.current, {
        playAs: 'resume',
        rate: stripRate,
      })
      return
    }
    setRunning(true)
  }
  toggleClockRef.current = toggleClock

  async function stopVideoCapture(): Promise<void> {
    if (!videoExportActiveRef.current) return
    videoExportActiveRef.current = false
    setVideoExportStatus('saving')
    const rec = videoRecorderRef.current
    const armed = videoStreamArmRef.current
    videoRecorderRef.current = null
    videoStreamArmRef.current = null
    const elapsedWall = Math.max(
      1000,
      Date.now() - (videoCaptureStartedAtRef.current || Date.now())
    )
    try {
      if (rec) {
        const blob = await rec.stop()
        if (blob.size > 0) {
          downloadBlob(blob, previewVideoFilename(viewSession.title, elapsedWall, rec.extension))
        }
      } else if (armed) {
        armed.getTracks().forEach((t) => t.stop())
      }
    } finally {
      setRecToast(false)
      setVideoExportStatus('idle')
    }
  }

  async function startVideoCapture(): Promise<void> {
    if (videoExportActiveRef.current) return
    if (!navigator.mediaDevices?.getDisplayMedia) {
      window.alert(
        "This browser can't capture the tab. Try another browser, or use a desktop capture tool."
      )
      return
    }
    const stream = await requestTabCapture()
    if (!stream) return

    // Arm Esc + hide demo chrome first; show the banner, then start the recorder
    // only after the banner is gone so it never lands in the file.
    videoStreamArmRef.current = stream
    videoExportActiveRef.current = true
    setVideoExportStatus('recording')
    setRecToast(true)
    if (recToastTimerRef.current != null) window.clearTimeout(recToastTimerRef.current)
    await new Promise<void>((r) => {
      recToastTimerRef.current = window.setTimeout(() => r(), 3200)
    })
    setRecToast(false)
    await new Promise<void>((r) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => r()))
    })
    await new Promise<void>((r) => window.setTimeout(r, 120))
    if (!videoExportActiveRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      videoStreamArmRef.current = null
      setVideoExportStatus('idle')
      return
    }

    videoCaptureStartedAtRef.current = Date.now()
    const recorder = startPreviewRecorder(stream)
    videoStreamArmRef.current = null
    videoRecorderRef.current = recorder

    stream.getVideoTracks()[0]?.addEventListener(
      'ended',
      () => {
        void stopVideoCapture()
      },
      { once: true }
    )
  }

  function applyCameraView(
    cam: { lng: number; lat: number; zoom: number },
    opts?: { ease?: boolean }
  ): void {
    if (opts?.ease) {
      mapApiRef.current?.easeToCamera(cam, DEFAULT_CAMERA_EASE_MS)
      return
    }
    mapApiRef.current?.jumpToCamera(cam)
  }

  function openCamMenu(eventId: string): void {
    if (camLeaveTimerRef.current != null) {
      window.clearTimeout(camLeaveTimerRef.current)
      camLeaveTimerRef.current = null
    }
    setCamMenuSuppressId((cur) => (cur === eventId ? null : cur))
    setCamHoverId(eventId)
  }

  function scheduleCloseCamMenu(eventId: string): void {
    if (camLeaveTimerRef.current != null) window.clearTimeout(camLeaveTimerRef.current)
    camLeaveTimerRef.current = window.setTimeout(() => {
      setCamHoverId((cur) => (cur === eventId ? null : cur))
      camLeaveTimerRef.current = null
    }, 320)
  }

  function closeCamMenu(eventId?: string): void {
    if (camLeaveTimerRef.current != null) {
      window.clearTimeout(camLeaveTimerRef.current)
      camLeaveTimerRef.current = null
    }
    if (eventId) setCamMenuSuppressId(eventId)
    setCamHoverId(null)
  }

  function flashMapCamera(): void {
    setMapCamFlash(false)
    // Restart animation if already flashing.
    window.requestAnimationFrame(() => {
      setMapCamFlash(true)
      if (cameraFlashTimerRef.current != null) window.clearTimeout(cameraFlashTimerRef.current)
      cameraFlashTimerRef.current = window.setTimeout(() => setMapCamFlash(false), 200)
    })
  }

  function stampCameraAt(atElapsedMs: number, eventId?: string): void {
    if (!canEditBoard) return
    const live = mapApiRef.current?.getCamera()
    if (!live) {
      window.alert('Map is not ready yet.')
      return
    }
    const beat: CameraBeat = {
      at: atOff(Math.round(atElapsedMs)),
      lng: live.lng,
      lat: live.lat,
      zoom: Math.round(live.zoom * 100) / 100,
      ...(eventId ? { eventId } : {}),
    }
    commitSession((prev) => ({
      ...prev,
      cameraBeats: upsertCameraBeat(prev.cameraBeats ?? [], beat),
    }))
    flashMapCamera()
    setCamEditHoldAtMs(null)
    closeCamMenu(eventId)
  }

  /** Insert a narration beat after `afterIndex` (−1 = before first card). */
  function insertNarrationBeat(afterIndex: number): void {
    if (!canEditBoard) return
    // Empty-protection without hiding Add links: focus the unfinished draft instead of stacking.
    const pristine = displayTimeline.find(isPristineNarrationBeat)
    if (pristine) {
      setTimelineEditMode(true)
      setNarrationDraftId(pristine.id)
      focusTimelineBeat(pristine.id, pristine.at)
      return
    }
    const list = displayTimeline
    const prevEv = afterIndex >= 0 ? list[afterIndex] : undefined
    const nextEv = list[afterIndex + 1]
    let at: string
    if (list.length === 0) {
      // First beat = story origin so the playhead starts on a card.
      at = viewSession.storyStartTime
    } else if (prevEv && !nextEv) {
      // Append: +10 minutes so cards do not stack on the same stamp.
      at = addStoryMs(prevEv.at, NARRATION_INSERT_STEP_MS)
    } else {
      at = narrationBeatAtBetween(
        prevEv?.at,
        nextEv?.at,
        viewSession.storyStartTime,
        viewSession.storyEndTime
      )
    }
    const beat = buildNarrationTimelineEvent({ at })
    commitSession((prev) => ({
      ...prev,
      timeline: [...prev.timeline, beat],
    }))
    setTimelineEditMode(true)
    setNarrationDraftId(beat.id)
    setTimelineFocusId(beat.id)
  }

  function updateNarrationBeat(
    id: string,
    patch: {
      title?: string
      summary?: string
      at?: string
      actions?: TimelineAction[]
    }
  ): void {
    if (!canEditBoard) return
    commitSession((prev) => ({
      ...prev,
      timeline: prev.timeline.map((ev) => {
        if (ev.id !== id || !isNarrationTimelineEvent(ev)) return ev
        return {
          ...ev,
          kind: 'narration',
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.summary !== undefined ? { summary: patch.summary } : {}),
          ...(patch.at !== undefined ? { at: patch.at } : {}),
          ...(patch.actions !== undefined ? { actions: patch.actions } : {}),
        }
      }),
    }))
  }

  /** Edit a sync card by mutating the underlying object (heal refreshes the row). */
  function updateSyncedBeatObject(
    objectId: string,
    patch: { title?: string; summary?: string; at?: string }
  ): void {
    if (!canEditBoard) return
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== objectId) return o
        return {
          ...o,
          ...(patch.title !== undefined ? { label: patch.title } : {}),
          ...(patch.summary !== undefined ? { description: patch.summary } : {}),
          ...(patch.at !== undefined ? { startsAt: patch.at } : {}),
        }
      }),
    }))
  }

  function removeNarrationBeat(id: string): void {
    if (!canEditBoard) return
    // Prefer the card after the deleted one (then previous) so a mid-stack delete
    // lands on the later beat, not the first.
    const idx = displayTimeline.findIndex((ev) => ev.id === id)
    const focusAfter =
      idx >= 0 ? displayTimeline[idx + 1] ?? displayTimeline[idx - 1] : undefined
    commitSession((prev) => {
      const target = prev.timeline.find((ev) => ev.id === id)
      if (!target || !isNarrationTimelineEvent(target)) return prev
      const nextTimeline = prev.timeline.filter((ev) => ev.id !== id)
      const cams = prev.cameraBeats
      if (!cams?.length) return { ...prev, timeline: nextTimeline }
      const nextCams = cams.filter((b) => b.eventId !== id)
      if (nextCams.length === cams.length) return { ...prev, timeline: nextTimeline }
      const copy = { ...prev, timeline: nextTimeline }
      if (nextCams.length) copy.cameraBeats = nextCams
      else delete copy.cameraBeats
      return copy
    })
    setNarrationDraftId((cur) => (cur === id ? (focusAfter?.id ?? null) : cur))
    if (focusAfter) {
      focusTimelineBeat(
        focusAfter.id,
        focusAfter.at,
        eventObjectIds(focusAfter.actions)
      )
    } else {
      setTimelineFocusId((cur) => (cur === id ? null : cur))
    }
  }
  removeNarrationBeatRef.current = removeNarrationBeat
  narrationDeleteFocusRef.current =
    timelineEditMode && timelineFocusId
      ? displayTimeline.find(
          (ev) => ev.id === timelineFocusId && isNarrationTimelineEvent(ev)
        )?.id ?? null
      : null

  function previewCameraBeat(beat: CameraBeat, eventId?: string): void {
    setHoverElapsedMs(null)
    setElapsedMs(off(beat.at))
    setCamEditHoldAtMs(null)
    applyCameraView({ lng: beat.lng, lat: beat.lat, zoom: beat.zoom }, { ease: true })
    closeCamMenu(eventId)
  }

  function removeCameraAt(atElapsedMs: number, eventId?: string): void {
    if (!canEditBoard) return
    const remaining = removeCameraBeatNear(viewSession.cameraBeats ?? [], atOff(atElapsedMs))
    commitSession((prev) => {
      const copy = { ...prev }
      if (remaining) copy.cameraBeats = remaining
      else delete copy.cameraBeats
      return copy
    })
    if (cameraFollowOn) {
      const fallback = cameraBeatAtElapsed(remaining ?? [], atOff(atElapsedMs))
      applyCameraView(fallback ?? viewSession.camera, { ease: true })
    }
    closeCamMenu(eventId)
  }

  function toggleVideoCapture(): void {
    if (videoExportStatus === 'saving') return
    if (videoExportActiveRef.current || videoExportStatus === 'recording') {
      void stopVideoCapture()
      return
    }
    void startVideoCapture()
  }
  hotkeyActionsRef.current.toggleCapture = () => {
    if (!canCaptureScreenRef.current) return
    const stopping = videoExportActiveRef.current || videoExportStatus === 'recording'
    toggleVideoCapture()
    showKeyToast('C', stopping ? 'Capture stopped' : 'Capture screen')
  }

  function applyPlaybackRate(rate: number): void {
    const next = snapPlaybackRate(rate, playbackMaxRate)
    setPlaybackRuntimeMs(Math.round(fullRuntimeMs / Math.max(PLAYBACK_RATE_MIN, next)))
    // Instant Replay tape speed is ephemeral (restore on exit). Outside IR — including
    // paused mid-take and between runs — a menu pick is the new official rate.
    if (!instantReplayEngagedRef.current) {
      if (replaySavedRateRef.current != null) replaySavedRateRef.current = next
      rateRestoreUntilRef.current = 0
    }
    if (!instanceIdRef.current || !canShareStripRateRef.current) return
    void hubSetRate(instanceIdRef.current, next).catch((err) => {
      setServerNotice(studioActionMessage(err, 'Could not change speed.'))
    })
  }

  /**
   * One hub patch for IR session flags. Never fire parallel SetSessionFields for these keys —
   * they race Resume/Seek/Pause on the same ConcurrencyStamp.
   */
  function persistInstantReplayFields(patch: {
    pauseMs?: number | null
    playing?: boolean
    engaged?: boolean
  }): void {
    const id = instanceIdRef.current
    if (!id || !canOperateSessionRef.current) return
    const hasPause = Object.prototype.hasOwnProperty.call(patch, 'pauseMs')
    const hasPlaying = typeof patch.playing === 'boolean'
    const hasEngaged = typeof patch.engaged === 'boolean'
    if (!hasPause && !hasPlaying && !hasEngaged) return
    commitSessionFields((prev) => {
      const next: ScenarioSession = { ...prev }
      if (hasPause) {
        if (patch.pauseMs == null) {
          delete next.instantReplayPauseMs
          delete next.instantReplayPlaying
          delete next.instantReplayEngaged
        } else {
          next.instantReplayPauseMs = Math.max(0, Math.floor(patch.pauseMs))
          if (!hasPlaying) next.instantReplayPlaying = false
          if (!hasEngaged) next.instantReplayEngaged = false
        }
      }
      if (hasPlaying) next.instantReplayPlaying = patch.playing!
      if (hasEngaged) next.instantReplayEngaged = patch.engaged!
      return next
    })
    const fields: SessionFieldPatch = {}
    if (hasPause) {
      fields.instantReplayPauseMs = patch.pauseMs ?? null
      if (patch.pauseMs != null) {
        if (!hasPlaying) fields.instantReplayPlaying = false
        if (!hasEngaged) fields.instantReplayEngaged = false
      } else {
        fields.instantReplayPlaying = false
        fields.instantReplayEngaged = false
      }
    }
    if (hasPlaying) fields.instantReplayPlaying = patch.playing!
    if (hasEngaged) fields.instantReplayEngaged = patch.engaged!
    void hubSetSessionFields(id, fields).catch(() => {
      /* local chrome already updated; hub retries concurrency */
    })
  }

  /** Broadcast take-pause freeze (or null to clear) so spectators share mission clock + lower-third. */
  function persistInstantReplayPause(ms: number | null): void {
    persistInstantReplayFields({ pauseMs: ms, playing: false, engaged: false })
  }

  function persistInstantReplayPlaying(playing: boolean): void {
    persistInstantReplayFields({ playing })
  }

  function persistInstantReplayEngaged(engaged: boolean): void {
    // Reserved for explicit engage writes; Seek currently stamps engaged on the hub.
    persistInstantReplayFields({ engaged })
  }
  void persistInstantReplayEngaged

  /**
   * Apply hub take-pause freeze. Does **not** enter Instant Replay — scrub does.
   * Playing flag is applied separately from Clock / session fields.
   */
  function applyInstantReplayPauseFromWire(pauseMs: number | null | undefined, present: boolean): void {
    if (!present) return
    if (pauseMs == null) {
      if (instantReplayPauseMsRef.current != null || instantReplayEngagedRef.current) {
        instantReplayPauseMsRef.current = null
        setInstantReplayPauseMs(null)
        setInstantReplayEngaged(false)
        setReplayPlaying(false)
      }
      return
    }
    const n = Math.max(0, Math.floor(pauseMs))
    instantReplayPauseMsRef.current = n
    setInstantReplayPauseMs(n)
  }

  function markInstantReplayPause(): void {
    if (!liveRunSpaceRef.current || takeNumberRef.current <= 0) {
      // Story preview / Ready — no take-pause freeze (that is Instant Replay chrome).
      instantReplayPauseMsRef.current = null
      setInstantReplayPauseMs(null)
      setInstantReplayEngaged(false)
      setReplayPlaying(false)
      return
    }
    const at = elapsedRef.current
    instantReplayPauseMsRef.current = at
    setInstantReplayPauseMs(at)
    setInstantReplayEngaged(false)
    setReplayPlaying(false)
    // Capture the official take rate at Pause — discuss / IR speed changes must not stick.
    if (replaySavedRateRef.current == null) {
      replaySavedRateRef.current = playbackRateRef.current
    }
    // Hub PauseAsync stamps SessionJson freeze — do not SetSessionFields in parallel (concurrency).
  }

  function restoreReplaySavedRate(): void {
    const saved = replaySavedRateRef.current
    replaySavedRateRef.current = null
    if (saved == null || !Number.isFinite(saved) || saved <= 0) return
    const id = instanceIdRef.current
    const dur = Math.max(1, displayDurationMs(snapshotRef.current.session))
    setPlaybackRuntimeMs(Math.round(dur / Math.max(PLAYBACK_RATE_MIN, saved)))
    // Hold long enough that a late Clock echo at the discuss rate cannot re-stick 15×.
    rateRestoreUntilRef.current = performance.now() + 1600
    if (id) {
      const prev = clocksRef.current.get(id)
      if (prev && Math.abs((prev.rate || 1) - saved) > 0.02) {
        const stamped = { ...prev, rate: saved }
        clocksRef.current.set(id, stamped)
        serverClockRef.current = stamped
        setServerClock(stamped)
      }
    }
    if (!id || !canShareStripRateRef.current) return
    void hubSetRate(id, saved).catch(() => {})
  }

  /** Clear local IR/take-pause chrome. Hub Resume clears SessionJson; pass persist when no Resume. */
  function clearInstantReplayPause(opts?: { persist?: boolean }): void {
    const had = instantReplayPauseMsRef.current != null || instantReplayEngagedRef.current
    instantReplayPauseMsRef.current = null
    setInstantReplayPauseMs(null)
    setInstantReplayEngaged(false)
    setReplayPlaying(false)
    restoreReplaySavedRate()
    // Default: no hub patch — ResumeAsync clears flags on the same row as the clock.
    if (had && opts?.persist) persistInstantReplayPause(null)
  }

  /**
   * Operator-only optimistic IR chrome. Does not touch the hub — Seek/Pause/Resume
   * (or a sole SetSessionFields for R-on-freeze) are the room writers.
   */
  function paintInstantReplayLocal(atMs?: number): void {
    if (!liveRunSpaceRef.current || runningRef.current) return
    // Between takes / before Run 1 = story preview — never paint Instant Replay chrome.
    if (snapshotRef.current.session.exerciseTakeStopped === true) return
    if (takeNumberRef.current <= 0) return
    if (instantReplayPauseMsRef.current == null) markInstantReplayPause()
    if (replaySavedRateRef.current == null) {
      replaySavedRateRef.current = playbackRateRef.current
    }
    setReplayPlaying(false)
    setInstantReplayEngaged(true)
    localReplayDriveUntilRef.current = performance.now() + 400
    if (atMs != null) {
      const cap = displayDur > 0 ? displayDur : atMs
      const next = Math.min(Math.max(0, atMs), cap)
      elapsedRef.current = next
      setElapsedMs(next)
    }
  }

  /**
   * Enter Instant Replay (sportscast tape) — only after scrub / explicit seek while paused.
   * Plain Pause keeps the lower-third as “Run N paused” until this runs.
   */
  function enterInstantReplay(atMs?: number): void {
    paintInstantReplayLocal(atMs)
    if (atMs != null) {
      const cap = displayDur > 0 ? displayDur : atMs
      const next = Math.min(Math.max(0, atMs), cap)
      // Hub Seek stamps engaged (+ clears playing) when off freeze — one transition.
      seekInstantReplay(next, { engageReplay: true })
    } else {
      // R / tape UI on the freeze — no Seek; one SetSessionFields is the room writer.
      persistInstantReplayFields({ engaged: true, playing: false })
    }
  }

  /** Exit IR (if any) and resume the official run from the pause point. */
  function resumeLiveRunFromPause(): void {
    const id = instanceIdRef.current
    const dur = displayDurRef.current
    const head = instantReplayPauseMsRef.current ?? elapsedRef.current
    // Paused at story end on an official take — finish, don't Resume from the end (or start).
    if (
      dur > 0 &&
      head >= dur - 80 &&
      takeNumberRef.current > 0 &&
      !exerciseRunStoppedRef.current &&
      liveTakeActiveRef.current
    ) {
      finishOfficialTakeAtStoryEnd(dur)
      return
    }
    const pauseMs = instantReplayPauseMsRef.current
    const prevClock = clocksRef.current.get(id)
    const stripRate = Math.max(
      PLAYBACK_RATE_MIN,
      replaySavedRateRef.current ?? playbackRateRef.current
    )
    studioClockDesireRef.current = null
    if (pauseMs != null) {
      elapsedRef.current = pauseMs
      setElapsedMs(pauseMs)
    }
    clearInstantReplayPause()
    if (!id || !prevClock) {
      stripPlayBtnRef.current?.click()
      return
    }
    const start = snapshotRef.current.session.storyStartTime
    const headMs = pauseMs ?? elapsedRef.current
    const playhead = addStoryMs(start, headMs)
    const nextClock: StudioClock = {
      ...prevClock,
      lifecycle: 'running',
      clockStatus: 'running',
      rate: stripRate,
      anchorStoryTimeIso: playhead,
      anchorActualTime: new Date().toISOString(),
      storyOriginIso: prevClock.storyOriginIso || prevClock.anchorStoryTimeIso,
    }
    clocksRef.current.set(id, nextClock)
    serverClockRef.current = nextClock
    setServerClock(nextClock)
    setRunning(true)
    setBoardActivity('running')
    transportPlayHoldIdRef.current = id
    localPreviewDriveUntilRef.current = performance.now() + 1200
    // One Clock at the pause point (rate included). SetRate-then-Seek re-anchored
    // the old head and twitched the plane before Resume.
    void hubResumeAt(id, playhead, stripRate).catch((err) => {
        if (transportPlayHoldIdRef.current === id) {
          transportPlayHoldIdRef.current = null
        }
        setRunning(false)
        setServerNotice(studioActionMessage(err, 'Could not resume the run.'))
      })
  }

  function toggleReplayPlaying(): void {
    if (!instantReplayEngagedRef.current || !canOperateSessionRef.current) return
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    const start = snapshotRef.current.session.storyStartTime

    // Pause: stop local ticker immediately, then hub pauseTape.
    if (replayPlayingRef.current) {
      replayPlayingRef.current = false
      setReplayPlaying(false)
      const headMs = elapsedRef.current
      const iso = addStoryMs(start, headMs)
      const prev = clocksRef.current.get(id)
      if (prev) {
        const stamped: StudioClock = {
          ...prev,
          clockStatus: 'paused',
          anchorStoryTimeIso: iso,
          anchorActualTime: null,
        }
        clocksRef.current.set(id, stamped)
        serverClockRef.current = stamped
        setServerClock(stamped)
      }
      hubSeekOwnsHeadRef.current = true
      localReplayDriveUntilRef.current = performance.now() + 900
      lastSentHubSeekMsRef.current = headMs
      const gen = ++irTapePlayingIntentGenRef.current
      irTapePlayingIntentRef.current = { playing: false, gen }
      sendIrTapeControlSeek(headMs, true, false, gen)
      return
    }

    // Play: do NOT start the local ticker until startTape admits. A used to race ahead
    // during the irPlay RTT while P started only on Clock — permanent ~RTT lag for the
    // whole tape (cadence snaps would hide it; ignoring cadence made the lag obvious).
    const pauseMs = instantReplayPauseMsRef.current
    let headMs = elapsedRef.current
    if (pauseMs != null && Number.isFinite(pauseMs) && headMs >= pauseMs - 40) {
      headMs = 0
      elapsedRef.current = 0
      setElapsedMs(0)
      setHoverElapsedMs(null)
    }
    const iso = addStoryMs(start, headMs)
    const prev = clocksRef.current.get(id)
    if (prev) {
      const stamped: StudioClock = {
        ...prev,
        clockStatus: 'paused',
        anchorStoryTimeIso: iso,
        anchorActualTime: null,
      }
      clocksRef.current.set(id, stamped)
      serverClockRef.current = stamped
      setServerClock(stamped)
    }
    hubSeekOwnsHeadRef.current = true
    localReplayDriveUntilRef.current = performance.now() + 900
    lastSentHubSeekMsRef.current = headMs
    const gen = ++irTapePlayingIntentGenRef.current
    irTapePlayingIntentRef.current = { playing: true, gen }
    sendIrTapeControlSeek(headMs, false, true, gen)
  }
  toggleReplayPlayingRef.current = toggleReplayPlaying

  /**
   * Stop IR tape for scrub — local + pauseTape Seek so SessionJson playing=false before
   * mid-drag Seeks. Local-only false let Clock(playing=true) re-arm the banner mid-scrub.
   */
  function pauseReplayTapeForScrub(): void {
    if (!replayPlayingRef.current) return
    replayPlayingRef.current = false
    setReplayPlaying(false)
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current || !canOperateSessionRef.current) return
    const headMs = elapsedRef.current
    hubSeekOwnsHeadRef.current = true
    localReplayDriveUntilRef.current = performance.now() + 900
    lastSentHubSeekMsRef.current = headMs
    const gen = ++irTapePlayingIntentGenRef.current
    irTapePlayingIntentRef.current = { playing: false, gen }
    sendIrTapeControlSeek(headMs, true, false, gen)
  }

  function clearScrubSeekTimer(): void {
    if (scrubSeekTimerRef.current != null) {
      window.clearTimeout(scrubSeekTimerRef.current)
      scrubSeekTimerRef.current = null
    }
  }

  /**
   * Planned / Reviewing / live-idle mid-drag Seek — direct forceAdmit, never jumpTo/pump.
   * Gen-guards so a late Seek(end) cannot stamp lastSent after commit Seek(0).
   */
  /** Cancel in-flight pump desire so direct Seek/Play/Stop cannot wedge behind it. */
  function cancelStudioClockDesire(reason: string): void {
    if (studioClockDesireRef.current) {
      noteClockTrace(
        'local',
        'pump.cancel',
        newClientTrace('cancel'),
        { reason, hadRunning: studioClockDesireRef.current.running },
        instanceIdRef.current,
      )
    }
    studioClockDesireRef.current = null
  }

  /**
   * Reviewing / Planned / live-idle Seek lane — one hub Seek at a time, coalesce to
   * the latest ms. Local paint stays immediate; only the in-flight RPC is serialized.
   */
  function forceAdmitDeckScrubSeek(ms: number): void {
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    if (!canDrivePreviewRef.current && !canOperateSessionRef.current) return
    const tapeCap =
      tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
        ? tapeAvailableUntilMsRef.current
        : null
    const dur = displayDurRef.current
    const cap = tapeCap != null ? tapeCap : dur > 0 ? dur : ms
    const next = Math.min(Math.max(0, ms), cap)
    hubSeekOwnsHeadRef.current = true
    localPreviewDriveUntilRef.current = performance.now() + 900
    deckSeekProtectUntilRef.current = performance.now() + 1500
    cancelStudioClockDesire('deck-scrub-seek')
    deckScrubPendingMsRef.current = next
    void drainDeckScrubSeekLane()
  }

  async function drainDeckScrubSeekLane(): Promise<void> {
    if (deckScrubSeekBusyRef.current) return
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    deckScrubSeekBusyRef.current = true
    try {
      while (deckScrubPendingMsRef.current != null) {
        const next = deckScrubPendingMsRef.current
        deckScrubPendingMsRef.current = null
        deckScrubSeekGenRef.current += 1
        const gen = deckScrubSeekGenRef.current
        const start = snapshotRef.current.session.storyStartTime
        const iso = addStoryMs(start, next)
        hubSeekOwnsHeadRef.current = true
        deckSeekProtectUntilRef.current = performance.now() + 1500
        try {
          const admitted = await hubSeek(id, iso, false, false, true)
          if (admitted && gen === deckScrubSeekGenRef.current) {
            lastSentHubSeekMsRef.current = next
            hubWirePlayheadMsRef.current = next
            setHubWirePlayheadMs(next)
          }
        } catch {
          /* keep owns-head / protect window; next arrow or scrub retries */
        }
      }
    } finally {
      deckScrubSeekBusyRef.current = false
      if (deckScrubPendingMsRef.current != null) void drainDeckScrubSeekLane()
    }
  }

  /** Debounced hub seek while dragging — short coalesce; click commit bypasses this. */
  function scheduleDebouncedSeek(ms: number): void {
    clearScrubSeekTimer()
    localReplayDriveUntilRef.current = performance.now() + 400
    scrubSeekTimerRef.current = window.setTimeout(() => {
      scrubSeekTimerRef.current = null
      // Instant Replay only mid-take (paused). Live-idle / between-takes = ordinary seek.
      // forceAdmit on mid-drag: storm gate (~24/s) was dropping 32ms IR scrubs so P
      // only moved on pointer-up (commit already used forceAdmit).
      if (liveRunSpaceRef.current && liveTakeActiveRef.current) {
        if (!instantReplayEngagedRef.current) enterInstantReplay(ms)
        else seekInstantReplay(ms, { engageReplay: true, forceAdmit: true })
      } else jumpTo(ms)
    }, instantReplayEngagedRef.current || liveTakeActiveRef.current ? 32 : 60)
  }

  /**
   * Reviewing / Planned / live-idle mid-drag — coalesce like IR, but forceAdmit direct
   * (jumpTo→pump raced commit and wedged Play after extreme thrash).
   */
  function scheduleDebouncedDeckScrubSeek(ms: number): void {
    clearScrubSeekTimer()
    hubSeekOwnsHeadRef.current = true
    localPreviewDriveUntilRef.current = performance.now() + 900
    deckSeekProtectUntilRef.current = performance.now() + 1500
    scrubSeekTimerRef.current = window.setTimeout(() => {
      scrubSeekTimerRef.current = null
      forceAdmitDeckScrubSeek(ms)
    }, 60)
  }

  /**
   * Scrub/IR used to force-clear owns-head on an 800ms timer. Reset→Play within that
   * window dropped owns-head mid-PreviewPlay so RAF followed boardPlayheadIso and the
   * plane snapped back to story start. Only release when no play desire/pump/hold.
   */
  function releaseHubSeekOwnsHeadUnlessPlayPending(): void {
    if (studioClockDesireRef.current?.running) return
    if (studioClockPumpRef.current) return
    // Keep owns-head until pauseTape/startTape Clock confirms — late progress Seeks
    // must not yank the bar while Space Play/Pause is in flight.
    if (irTapePlayingIntentRef.current) return
    const id = instanceIdRef.current
    if (id && transportPlayHoldIdRef.current === id) return
    hubSeekOwnsHeadRef.current = false
  }

  /** Invalidate IR tape progress Seeks (gen + pending + pump desire + scrub timer). */
  function invalidateIrTapeProgressSeeks(reason: string): void {
    irTapeSeekGenRef.current += 1
    irTapePendingProgressMsRef.current = null
    clearScrubSeekTimer()
    cancelStudioClockDesire(reason)
  }

  function queueIrTapeScrubControl(ms: number): void {
    // When A is locally paused, reinforce pauseTape so a pinHead Seek cannot leave
    // hub instantReplayPlaying=true (P would keep free-running).
    const pauseTape = !replayPlayingRef.current
    irTapePendingControlRef.current = {
      ms,
      pauseTape,
      startTape: false,
      intentGen: null,
      pinHead: true,
    }
  }

  /**
   * Serial IR Seek lane — at most one hub Seek in flight. Control (Space play/pause /
   * tape-end) always drains before progress; progress coalesces to the latest ms.
   * Parallel irTape Seeks were stomping the row (concurrency retry storms + jitter).
   */
  function kickIrTapeSeekLane(): void {
    if (irTapeSeekBusyRef.current) return
    irTapeSeekBusyRef.current = true
    void (async () => {
      // One idle-tail pinHead per lane run — avoid spin if Seek is dropped.
      let pausedTailFlushAttempted = false
      try {
        while (true) {
          const id = instanceIdRef.current
          if (!id || previewTemplateIdRef.current) {
            irTapePendingControlRef.current = null
            irTapePendingProgressMsRef.current = null
            irTapePendingScrubMsRef.current = null
            break
          }
          // Promote stashed scrub when the lane is free (after play/pause or fresh kick).
          if (!irTapePendingControlRef.current && irTapePendingScrubMsRef.current != null) {
            const scrubMs = irTapePendingScrubMsRef.current
            irTapePendingScrubMsRef.current = null
            queueIrTapeScrubControl(scrubMs)
          }
          const control = irTapePendingControlRef.current
          if (control) {
            irTapePendingControlRef.current = null
            irTapePendingProgressMsRef.current = null
            // pinHead scrub/beat-skip: send the latest local head. Another ←→ can
            // land between dequeue and hubSeek; pending.ms would otherwise be stale
            // while A already painted the newer beat (P settled on the old one).
            const sendMs =
              control.pinHead &&
              control.intentGen == null &&
              !control.startTape
                ? elapsedRef.current
                : control.ms
            const start = snapshotRef.current.session.storyStartTime
            const iso = addStoryMs(start, sendMs)
            const trace = newClientTrace(
              control.startTape ? 'irPlay' : control.pauseTape ? 'irPause' : 'irSeek',
            )
            hubSeekOwnsHeadRef.current = true
            localReplayDriveUntilRef.current = performance.now() + 900
            try {
              const admitted = await hubSeek(
                id,
                iso,
                control.pauseTape,
                control.startTape,
                true,
                trace,
                control.pinHead,
              )
              const intentOk =
                control.intentGen == null ||
                irTapePlayingIntentRef.current?.gen === control.intentGen
              if (admitted && intentOk) {
                lastSentHubSeekMsRef.current = sendMs
                hubWirePlayheadMsRef.current = sendMs
                if (!replayPlayingRef.current) {
                  setHubWirePlayheadMs(sendMs)
                }
                // Start local IR ticker only after startTape admits — same moment P
                // gets the Clock, so free-run stays in phase (no permanent RTT lag).
                if (control.startTape) {
                  elapsedRef.current = sendMs
                  setElapsedMs(sendMs)
                  replayPlayingRef.current = true
                  setReplayPlaying(true)
                  setReplayPlayEpoch((n) => n + 1)
                }
              }
            } catch (err) {
              if (
                control.intentGen != null &&
                irTapePlayingIntentRef.current?.gen === control.intentGen
              ) {
                irTapePlayingIntentRef.current = null
                setServerNotice(
                  studioActionMessage(
                    err,
                    control.startTape
                      ? 'Could not play Instant Replay.'
                      : 'Could not pause Instant Replay.',
                  ),
                )
              }
            }
            window.setTimeout(() => {
              releaseHubSeekOwnsHeadUnlessPlayPending()
            }, 800)
            // Play/pause finished — apply beat-skip/scrub that waited so it would not
            // clobber the pause on the lane.
            const scrubMs = irTapePendingScrubMsRef.current
            if (scrubMs != null && !irTapePendingControlRef.current) {
              irTapePendingScrubMsRef.current = null
              queueIrTapeScrubControl(scrubMs)
            }
            continue
          }
          if (!canOperateSessionRef.current) {
            irTapePendingProgressMsRef.current = null
            irTapePendingScrubMsRef.current = null
            break
          }
          if (!instantReplayEngagedRef.current || !replayPlayingRef.current) {
            irTapePendingProgressMsRef.current = null
            // Lane idle while paused: if local head drifted past last admitted Seek
            // (rapid ←→ coalesce), one more pinHead Seek so P lands on A's paint.
            if (
              !pausedTailFlushAttempted &&
              instantReplayEngagedRef.current &&
              !replayPlayingRef.current &&
              !irTapePendingControlRef.current &&
              irTapePendingScrubMsRef.current == null
            ) {
              const local = elapsedRef.current
              const sent = lastSentHubSeekMsRef.current
              if (sent == null || Math.abs(local - sent) > 40) {
                pausedTailFlushAttempted = true
                queueIrTapeScrubControl(local)
                continue
              }
            }
            break
          }
          const ms = irTapePendingProgressMsRef.current
          if (ms == null) break
          irTapePendingProgressMsRef.current = null
          const gen = irTapeSeekGenRef.current
          const start = snapshotRef.current.session.storyStartTime
          const iso = addStoryMs(start, ms)
          const trace = newClientTrace('irTape')
          hubSeekOwnsHeadRef.current = true
          localReplayDriveUntilRef.current = performance.now() + 400
          try {
            const admitted = await hubSeek(id, iso, false, false, false, trace)
            if (admitted && gen === irTapeSeekGenRef.current) {
              lastSentHubSeekMsRef.current = ms
              // Ref only while tape plays — setHubWirePlayheadMs re-rendered every
              // ~280ms and stair-stepped the operator bar/pose via elapsedRef sync.
              hubWirePlayheadMsRef.current = ms
            }
          } catch {
            /* lane continues; next progress or control replaces */
          }
          window.setTimeout(() => {
            if (gen !== irTapeSeekGenRef.current) return
            releaseHubSeekOwnsHeadUnlessPlayPending()
          }, 400)
        }
      } finally {
        irTapeSeekBusyRef.current = false
        if (
          irTapePendingControlRef.current ||
          irTapePendingScrubMsRef.current != null ||
          (irTapePendingProgressMsRef.current != null &&
            replayPlayingRef.current &&
            instantReplayEngagedRef.current)
        ) {
          kickIrTapeSeekLane()
        }
      }
    })()
  }

  /** Queue latest mid-tape progress Seek on the serial lane (coalesced). */
  function sendIrTapeProgressSeek(ms: number): void {
    if (!canOperateSessionRef.current) return
    if (!instantReplayEngagedRef.current || !replayPlayingRef.current) return
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    // Control already queued — don't enqueue progress behind Play/Pause.
    if (irTapePendingControlRef.current) return
    irTapePendingProgressMsRef.current = ms
    kickIrTapeSeekLane()
  }

  /** Queue IR play/pause/end Seek on the serial lane (drops pending progress). */
  function sendIrTapeControlSeek(
    ms: number,
    pauseTape: boolean,
    startTape: boolean,
    intentGen: number | null,
  ): void {
    if (!instanceIdRef.current || previewTemplateIdRef.current) return
    invalidateIrTapeProgressSeeks('ir-tape-control')
    // Space pause/play owns the lane — drop a stashed scrub; caller can scrub again.
    if (pauseTape || startTape) irTapePendingScrubMsRef.current = null
    irTapePendingControlRef.current = {
      ms,
      pauseTape,
      startTape,
      intentGen,
      pinHead: false,
    }
    kickIrTapeSeekLane()
  }

  /**
   * IR scrub/beat-skip on the serial lane (pinHead so P snaps).
   * Must not replace a pending Space pause/play — that dropped pauseTape and left P running.
   */
  function sendIrTapeScrubSeek(ms: number): void {
    if (!instanceIdRef.current || previewTemplateIdRef.current) return
    if (!canOperateSessionRef.current) return
    irTapePendingProgressMsRef.current = null
    const pending = irTapePendingControlRef.current
    if (
      pending &&
      pending.intentGen != null &&
      (pending.pauseTape || pending.startTape)
    ) {
      irTapePendingScrubMsRef.current = ms
      hubSeekOwnsHeadRef.current = true
      localReplayDriveUntilRef.current = performance.now() + 400
      kickIrTapeSeekLane()
      return
    }
    queueIrTapeScrubControl(ms)
    hubSeekOwnsHeadRef.current = true
    localReplayDriveUntilRef.current = performance.now() + 400
    kickIrTapeSeekLane()
  }

  /**
   * Merge Pause / Play / Seek into one desire and pump hub calls serially.
   * Local UI updates immediately; only the latest desire is sent after in-flight work.
   */
  function enqueueStudioClock(patch: {
    running?: boolean
    seekMs?: number | null
    playAs?: 'resume' | 'previewPlay'
    rate?: number
    setRate?: boolean
    pauseWithoutPin?: boolean
  }): void {
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    // Spectators never drive the room clock (local paint only via callers that skip this).
    if (!canOperateSessionRef.current && !canDrivePreviewRef.current) return
    const prev = clocksRef.current.get(id)
    if (!prev) return

    const dur = displayDurRef.current
    const rate = Math.max(
      PLAYBACK_RATE_MIN,
      patch.rate ?? studioClockDesireRef.current?.rate ?? playbackRateRef.current
    )
    const running =
      patch.running ?? studioClockDesireRef.current?.running ?? runningRef.current
    let seekMs =
      patch.seekMs !== undefined
        ? patch.seekMs
        : (studioClockDesireRef.current?.seekMs ?? null)
    // Scrub-begin: never inherit a prior seekMs freeze.
    const pauseWithoutPin =
      patch.pauseWithoutPin === true ||
      (patch.seekMs === undefined &&
        patch.running === false &&
        studioClockDesireRef.current?.pauseWithoutPin === true)
    if (pauseWithoutPin && patch.seekMs === undefined) {
      seekMs = null
    }
    if (seekMs != null) {
      const tapeCap =
        tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
          ? tapeAvailableUntilMsRef.current
          : null
      // tapeCap=0 (bad derived stop stamp) used to clamp Seek(end) → 0 after finish,
      // leaving Instant Replay · Paused with a bar that looked like “stopped at 0s”.
      const spanCap =
        tapeCap != null && tapeCap > 250
          ? tapeCap
          : dur > 0
            ? dur
            : seekMs
      seekMs = Math.min(Math.max(0, seekMs), spanCap)
    }
    // Pause usually pins the freeze point so hub Pause-at-now cannot jump forward.
    // Scrub-begin skips the pin — commit Seek owns the head (else begin-elapsed races to 0).
    if (patch.running === false && seekMs == null && !pauseWithoutPin) {
      seekMs = elapsedRef.current
    }

    const playAs =
      patch.playAs ?? studioClockDesireRef.current?.playAs ?? 'resume'
    const setRate = Boolean(patch.setRate ?? studioClockDesireRef.current?.setRate)

    const headMs = seekMs ?? elapsedRef.current
    localPlayAnchorRef.current = { wall: performance.now(), elapsed: headMs, rate }
    if (seekMs != null) beatNavAnchorMsRef.current = seekMs
    // Scrub-begin pause: do not paint a freeze head — thumb is already moving.
    if (!pauseWithoutPin || seekMs != null) {
      elapsedRef.current = headMs
      setElapsedMs(headMs)
    }
    setHoverIds([])
    setHoverElapsedMs(null)
    setRunning(running)

    const start = snapshotRef.current.session.storyStartTime
    const iso = addStoryMs(start, pauseWithoutPin && seekMs == null ? elapsedRef.current : headMs)
    const stamped: StudioClock = {
      ...prev,
      lifecycle: prev.lifecycle === 'planned' && running ? 'running' : prev.lifecycle,
      clockStatus: running ? 'running' : 'paused',
      anchorStoryTimeIso: iso,
      anchorActualTime: running ? new Date().toISOString() : null,
      storyOriginIso: prev.storyOriginIso || start,
      // Stamp the desired rate immediately — keeping prev.rate left 1× on clocksRef
      // until SetRate Clock landed, so owns-head release could jump rate as well as head.
      rate,
    }
    clocksRef.current.set(id, stamped)
    serverClockRef.current = stamped
    setServerClock(stamped)
    setInstances((rows) =>
      rows.map((row) =>
        row.id === id
          ? {
              ...row,
              clockStatus: stamped.clockStatus,
              studioClock: stamped,
              playheadStoryIso: iso,
            }
          : row
      )
    )

    if (running) {
      if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
      transportPlayHoldIdRef.current = id
    } else {
      if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
      transportPauseHoldIdRef.current = id
      // Supersede in-flight Reviewing/Preview driveDeckPlayAt so a late ResumeAt
      // cannot restart the hub after this Pause.
      deckPlayGenRef.current += 1
      // Do NOT clear hubAdmittedRunning here. The pump uses it for scrub-begin
      // pauseWithoutPin skip only — pin Pause always sends hubPause (see pump).
    }

    studioClockDesireRef.current = {
      running,
      seekMs,
      playAs,
      rate,
      setRate,
      ...(pauseWithoutPin ? { pauseWithoutPin: true } : {}),
    }
    // Own the head only for Seek / scrub / pause pin — not bare PreviewPlay/Resume.
    // Otherwise A stays on the local 100ms anchor while P interpolates from the hub
    // Clock (smooth path motion), and A looks discrete/choppy.
    hubSeekOwnsHeadRef.current =
      seekMs != null || pauseWithoutPin || running === false
    noteClockTrace(
      'local',
      'enqueue',
      newClientTrace('enq'),
      {
        running,
        seekMs,
        playAs,
        pauseWithoutPin,
        elapsed: elapsedRef.current,
        hubAdmittedRunning: hubAdmittedRunningRef.current,
        pumpAlive: Boolean(studioClockPumpRef.current),
        hubWireMs: hubWirePlayheadMsRef.current,
        lastSentSeekMs: lastSentHubSeekMsRef.current,
      },
      id,
    )
    ensureStudioClockPump()
  }

  /** Start the coalesced clock pump; re-kick if desire lands during pump teardown. */
  function ensureStudioClockPump(): void {
    if (!studioClockPumpRef.current) {
      studioClockPumpRef.current = pumpStudioClock()
    }
    // Race: enqueue sets desire while pump has left the while-loop but still holds
    // the ref until finally — those enqueues used to paint locally and never TX.
    window.setTimeout(() => {
      if (studioClockDesireRef.current && !studioClockPumpRef.current) {
        noteClockTrace(
          'local',
          'pump.kick',
          newClientTrace('kick'),
          {
            seekMs: studioClockDesireRef.current.seekMs,
            running: studioClockDesireRef.current.running,
          },
          instanceIdRef.current,
        )
        studioClockPumpRef.current = pumpStudioClock()
      }
    }, 0)
  }

  async function pumpStudioClock(): Promise<void> {
    try {
      while (studioClockDesireRef.current) {
        const job = studioClockDesireRef.current
        // Snapshot; newer Space/arrow enqueues replace desire during awaits.
        studioClockDesireRef.current = null
        const id = instanceIdRef.current
        if (!id || previewTemplateIdRef.current) continue
        const start = snapshotRef.current.session.storyStartTime
        const pumpTrace = newClientTrace(
          !job.running ? 'pumpPause' : job.playAs === 'previewPlay' ? 'pumpPrev' : 'pumpPlay',
        )
        noteClockTrace(
          'local',
          'pump.begin',
          pumpTrace,
          {
            running: job.running,
            seekMs: job.seekMs,
            playAs: job.playAs,
            pauseWithoutPin: job.pauseWithoutPin === true,
            hubAdmittedRunning: hubAdmittedRunningRef.current,
            localElapsed: elapsedRef.current,
          },
          id,
        )
        try {
          const plannedLifecycle =
            clocksRef.current.get(id)?.lifecycle === 'planned' ||
            job.playAs === 'previewPlay'
          // Live seek+play is one ResumeAt. SetRate first would re-anchor the old head.
          const resumeAt = job.running && job.seekMs != null && !plannedLifecycle
          if (job.setRate && !resumeAt) {
            await hubSetRate(id, job.rate, `${pumpTrace}.rate`).catch(() => undefined)
          }
          if (!job.running) {
            // Pin Pause (Space / Reviewing) always hits hubPause — never skip on
            // !hubAdmittedRunning. Reviewing Play uses driveDeckPlayAt, which leaves
            // admitted=false until ResumeAt returns; skipping Pause left the hub
            // Running so P free-ran while A looked paused.
            // Scrub-begin (pauseWithoutPin) may skip when hub is already paused.
            const hubAlreadyPaused =
              job.pauseWithoutPin === true && !hubAdmittedRunningRef.current
            const freezeIso =
              job.pauseWithoutPin
                ? null
                : job.seekMs != null
                  ? addStoryMs(start, job.seekMs)
                  : null
            if (!hubAlreadyPaused) {
              // Pause-at-playhead so peers share the driver’s freeze second (not RTT "now").
              // Scrub-begin: no freeze ISO — commit Seek sets the room head.
              await hubPause(id, freezeIso, `${pumpTrace}.pause`).catch(() => undefined)
              hubAdmittedRunningRef.current = false
              // Pause moves the room head — keep lastSent aligned so a later ← to an
              // older beat is not skipped as a "dup" of a pre-Pause Seek.
              if (job.seekMs != null) lastSentHubSeekMsRef.current = job.seekMs
            } else if (freezeIso && job.seekMs != null) {
              // Skip only when the *room* is already on this head. lastSent alone is
              // wrong after Pause: Seek(19:37) then Pause(19:37:15) left lastSent at
              // 19:37, so ← to 19:37 skipped TX while A painted locally and P froze.
              const lastSent = lastSentHubSeekMsRef.current
              const roomMs = hubWirePlayheadMsRef.current
              const sameAsSent =
                lastSent != null && Math.abs(lastSent - job.seekMs) <= 40
              const sameAsRoom = Math.abs(roomMs - job.seekMs) <= 40
              if (sameAsSent && sameAsRoom) {
                noteClockTrace(
                  'local',
                  'pump.skip-seek-dup',
                  pumpTrace,
                  { seekMs: job.seekMs, lastSent, roomMs },
                  id,
                )
              } else {
                // forceAdmit: rapid ←←← while paused must not storm-drop.
                let admitted = await hubSeek(
                  id,
                  freezeIso,
                  false,
                  false,
                  true,
                  `${pumpTrace}.seek`,
                )
                if (!admitted) {
                  admitted = await hubSeek(
                    id,
                    freezeIso,
                    false,
                    false,
                    true,
                    `${pumpTrace}.seek2`,
                  )
                }
                if (admitted) lastSentHubSeekMsRef.current = job.seekMs
              }
            } else {
              noteClockTrace(
                'local',
                'pump.skip-pause-already',
                pumpTrace,
                { hubAlreadyPaused, pauseWithoutPin: job.pauseWithoutPin === true },
                id,
              )
            }
          } else if (resumeAt && job.seekMs != null) {
            // Newer arrow during the gap used to Resume at a stale beat. Check before
            // the one ResumeAt so we do not start that head.
            const newer = peekStudioClockDesire()
            if (newer) {
              noteClockTrace(
                'local',
                'pump.superseded-before-play',
                pumpTrace,
                {
                  jobSeekMs: job.seekMs,
                  jobRunning: job.running,
                  nextSeekMs: newer.seekMs,
                  nextRunning: newer.running,
                },
                id,
              )
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, superseded: true }, id)
              continue
            }
            const tapeCap = tapeAvailableUntilMsRef.current
            const resumeOntoTapeEnd =
              Boolean(tapeReviewRef.current) &&
              !selectedIsCompletedRef.current &&
              tapeCap != null &&
              tapeCap > 250 &&
              job.seekMs >= tapeCap - 40
            // Never ResumeAt onto the stop stamp — that raced tapeEnd Pause (P @ end, A drift).
            if (resumeOntoTapeEnd) {
              noteClockTrace(
                'local',
                'pump.skip-resume-at-tape-end',
                pumpTrace,
                { seekMs: job.seekMs, tapeCap },
                id,
              )
              stopLiveTapeReviewAtEnd('pump-resume-at-tape-end')
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, tapeEnd: true }, id)
              continue
            }
            const iso = addStoryMs(start, job.seekMs)
            await hubResumeAt(id, iso, job.rate, `${pumpTrace}.resumeAt`)
            lastSentHubSeekMsRef.current = job.seekMs
            // tapeEnd / Pause may have won during the await — do not re-admit Running.
            if (
              !runningRef.current ||
              (tapeCap != null &&
                tapeCap > 250 &&
                elapsedRef.current >= tapeCap - 40 &&
                Boolean(tapeReviewRef.current))
            ) {
              hubAdmittedRunningRef.current = false
              noteClockTrace(
                'local',
                'pump.resume-at-after-tape-end',
                pumpTrace,
                { seekMs: job.seekMs, elapsed: elapsedRef.current },
                id,
              )
              const freezeIso = addStoryMs(
                start,
                tapeCap != null && tapeCap > 250 ? tapeCap : elapsedRef.current
              )
              await hubPause(id, freezeIso, `${pumpTrace}.healTapeEnd`).catch(() => undefined)
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, healedTapeEnd: true }, id)
              continue
            }
            // Admit before supersede check — ResumeAt already started the room clock.
            // Otherwise a quick Pause skips hubPause (hubAdmittedRunning still false) and P keeps playing.
            // Local head stays frozen until applyServerClock sees the running Clock (avoids
            // pump-await reanchor clearing owns-head onto a still-paused clocksRef).
            hubAdmittedRunningRef.current = true
            const afterAt = peekStudioClockDesire()
            if (afterAt) {
              noteClockTrace(
                'local',
                'pump.superseded-after-play',
                pumpTrace,
                {
                  nextSeekMs: afterAt.seekMs,
                  nextRunning: afterAt.running,
                },
                id,
              )
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, superseded: true }, id)
              continue
            }
          } else {
            if (job.seekMs != null) {
              const iso = addStoryMs(start, job.seekMs)
              // Skip Seek when room + lastSent already match — reset→play used to
              // no-op Seek (Clock sync) then PreviewPlay, stretching the pump so
              // local 20× raced ahead before admit.
              const lastSent = lastSentHubSeekMsRef.current
              const roomMs = hubWirePlayheadMsRef.current
              const sameAsSent =
                lastSent != null && Math.abs(lastSent - job.seekMs) <= 40
              const sameAsRoom = Math.abs(roomMs - job.seekMs) <= 40
              if (sameAsSent && sameAsRoom) {
                noteClockTrace(
                  'local',
                  'pump.skip-seek-dup',
                  pumpTrace,
                  { seekMs: job.seekMs, lastSent, roomMs },
                  id,
                )
              } else {
                // Planned beat-skip: forceAdmit so rapid ←←← is not storm-dropped.
                // ResumeAt would stamp Lifecycle=Running (Go Live) — keep Seek + PreviewPlay.
                let admitted = await hubSeek(
                  id,
                  iso,
                  false,
                  false,
                  true,
                  `${pumpTrace}.seek`,
                )
                if (!admitted) {
                  admitted = await hubSeek(
                    id,
                    iso,
                    false,
                    false,
                    true,
                    `${pumpTrace}.seek2`,
                  )
                }
                if (admitted) lastSentHubSeekMsRef.current = job.seekMs
                else {
                  studioClockDesireRef.current = job
                  throw new Error('Seek was not admitted; preview play aborted.')
                }
              }
            }
            // Rapid ← while playing: a newer enqueue may have replaced desire during
            // Seek. PreviewPlay/Resume at this (stale) head puts P on 19:35 while A
            // already painted 19:27 — skip play and let the loop send the latest job.
            const newer = peekStudioClockDesire()
            if (newer) {
              noteClockTrace(
                'local',
                'pump.superseded-before-play',
                pumpTrace,
                {
                  jobSeekMs: job.seekMs,
                  jobRunning: job.running,
                  nextSeekMs: newer.seekMs,
                  nextRunning: newer.running,
                },
                id,
              )
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, superseded: true }, id)
              continue
            }
            if (plannedLifecycle) {
              // Always PreviewPlay on Planned — Resume would stamp Lifecycle=Running (Go Live).
              // Also re-broadcasts Running after Paused beat Seeks so P does not stay frozen.
              await hubPreviewPlay(id, job.rate, `${pumpTrace}.prev`)
            } else {
              await hubResume(id, `${pumpTrace}.resume`)
            }
            // Same as ResumeAt: admit before supersede so a rapid Pause still sends hubPause.
            // Do not clear owns-head here — applyServerClock hands off on the running Clock
            // so a paused Seek/Clock-sync stamp cannot poison boardPlayheadIso.
            hubAdmittedRunningRef.current = true
            const afterPlay = peekStudioClockDesire()
            if (afterPlay) {
              noteClockTrace(
                'local',
                'pump.superseded-after-play',
                pumpTrace,
                {
                  nextSeekMs: afterPlay.seekMs,
                  nextRunning: afterPlay.running,
                },
                id,
              )
              noteClockTrace('local', 'pump.end', pumpTrace, { ok: true, superseded: true }, id)
              continue
            }
            // Bare play: follow hub interpolation (same smooth path as peers).
            if (job.seekMs == null) hubSeekOwnsHeadRef.current = false
          }
          noteClockTrace('local', 'pump.end', pumpTrace, { ok: true }, id)
        } catch (err) {
          noteClockTrace(
            'local',
            'pump.error',
            pumpTrace,
            {
              err: err instanceof Error ? err.message : String(err),
              running: job.running,
            },
            id,
          )
          // Optimistic Previewing on A with a failed PreviewPlay left P frozen — unwind.
          if (job.running) {
            hubAdmittedRunningRef.current = false
            setRunning(false)
            runningRef.current = false
            const prev = clocksRef.current.get(id)
            if (prev) {
              const pinned = {
                ...prev,
                clockStatus: 'paused' as const,
                anchorActualTime: null,
              }
              clocksRef.current.set(id, pinned)
              serverClockRef.current = pinned
              setServerClock(pinned)
            }
          }
          const errText = err instanceof Error ? err.message : String(err ?? '')
          // Spectator / soft-grant race Seek 401 — do not spam the rail.
          if (!/not authorized/i.test(errText)) {
            setServerNotice(studioActionMessage(err, 'Could not update the clock.'))
          }
        }
      }
    } finally {
      studioClockPumpRef.current = null
      if (studioClockDesireRef.current) {
        studioClockPumpRef.current = pumpStudioClock()
        return
      }
      // Only drop owns-head on a timer when clocksRef is already running near the
      // local head. Blind release onto a paused Seek/Clock-sync stamp snaps to t=0.
      const releaseAt = performance.now() + 800
      const tryRelease = (): void => {
        if (studioClockDesireRef.current || studioClockPumpRef.current) return
        if (!hubSeekOwnsHeadRef.current) return
        const id = instanceIdRef.current
        const clock = id ? clocksRef.current.get(id) : undefined
        const start = snapshotRef.current.session.storyStartTime
        const hubMs =
          clock && start
            ? Math.max(0, storyDeltaMs(start, boardPlayheadIso(clock, start)))
            : null
        const near =
          hubMs != null && Math.abs(hubMs - elapsedRef.current) <= 250
        const hubRunning = clock?.clockStatus === 'running'
        if (performance.now() >= releaseAt) {
          if (hubRunning && near) {
            hubSeekOwnsHeadRef.current = false
          }
          return
        }
        window.setTimeout(tryRelease, 50)
      }
      window.setTimeout(tryRelease, 50)
    }
  }

  /** Seek helper — keeps current play/pause desire, coalesces the head only. */
  function enqueueHubStorySeek(
    ms: number,
    opts: { keepRunning: boolean; pauseFirst?: boolean; playAs?: 'resume' | 'previewPlay' }
  ): void {
    enqueueStudioClock({
      running: opts.keepRunning,
      seekMs: ms,
      ...(opts.playAs ? { playAs: opts.playAs } : {}),
      // pauseFirst is implied: paused seek goes Pause then Seek in the pump.
    })
  }

  /** Preview / template play: move the head and keep playing (both arrow directions). */
  function seekWhilePreviewPlaying(ms: number): void {
    if (previewTemplateIdRef.current || !instanceIdRef.current) {
      const dur = displayDurRef.current
      const next = Math.min(Math.max(0, ms), dur > 0 ? dur : ms)
      const rate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
      localPlayAnchorRef.current = { wall: performance.now(), elapsed: next, rate }
      beatNavAnchorMsRef.current = next
      elapsedRef.current = next
      setElapsedMs(next)
      setHoverIds([])
      setHoverElapsedMs(null)
      return
    }
    const id = instanceIdRef.current
    const lifecycle = clocksRef.current.get(id)?.lifecycle
    // Planned must PreviewPlay — default playAs "resume" would Go Live (Lifecycle=Running)
    // or no-op oddly; rapid ← while "playing" then left P on Paused Seek clocks.
    const playAs = lifecycle === 'planned' ? 'previewPlay' : 'resume'
    enqueueHubStorySeek(ms, { keepRunning: true, playAs })
  }

  function skipStoryBeat(direction: 1 | -1): void {
    // IR tape play: allow beat skips (canSeek is gated on !running; IR keeps running false).
    if (!canSeekTimeBarRef.current && !(instantReplayEngagedRef.current && canOperateSessionRef.current))
      return
    const dur = displayDurRef.current
    // Tape review: navigate only inside the recorded window (not blackout past stop).
    const tapeCap =
      tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
        ? tapeAvailableUntilMsRef.current
        : instantReplayEngagedRef.current && instantReplayPauseMsRef.current != null
          ? instantReplayPauseMsRef.current
          : null
    const navSpan =
      tapeCap != null && tapeCap > 250
        ? Math.min(dur > 0 ? dur : tapeCap, tapeCap)
        : dur
    // Include the stop / IR freeze as a navigable mark when *paused* so → can land
    // on the bracket. While *playing*, omit it — → onto the cap ResumeAt(stop) raced
    // tapeEnd Pause (P @ stop, A kept seeking, often to T0). Natural play still ends
    // via stopLiveTapeReviewAtEnd.
    const marks = [...storyScrubMarksRef.current]
    const playingLiveTape =
      runningRef.current && Boolean(tapeReviewRef.current) && !selectedIsCompletedRef.current
    if (tapeCap != null && tapeCap > 250 && !playingLiveTape) {
      marks.push({
        id: 'tape-cap',
        atMs: Math.round(tapeCap),
        title: tapeReviewRef.current ? 'Stop point' : 'Pause point',
      })
    }
    // ← : only use beatNavAnchor when we are still on/near that beat (just landed).
    // A stale anchor at 0 while the head is mid-story made adjacentBeatMs a no-op.
    // Also ignore anchors past the tape cap (right-arrow used to stash a blackout mark).
    let from = elapsedRef.current
    if (direction < 0 && beatNavAnchorMsRef.current != null) {
      const anchor = beatNavAnchorMsRef.current
      const anchorInWindow = tapeCap == null || anchor <= tapeCap + 40
      if (anchorInWindow && Math.abs(from - anchor) <= 250) {
        from = Math.min(from, anchor)
      }
    }
    if (tapeCap != null) from = Math.min(from, tapeCap)
    const next = adjacentBeatMs(marks, from, direction, navSpan)
    // Playing + → past the last story beat before stop → pause at stop (no ResumeAt@end).
    if (
      next == null &&
      playingLiveTape &&
      direction > 0 &&
      tapeCap != null &&
      tapeCap > 250 &&
      from < tapeCap - 40
    ) {
      stopLiveTapeReviewAtEnd('beat-skip-to-tape-end')
      return
    }
    if (next == null) return
    const capped =
      tapeCap != null && tapeCap > 250 ? Math.min(next, tapeCap) : next
    beatNavAnchorMsRef.current = capped
    if (liveRunSpaceRef.current && liveTakeActiveRef.current) {
      if (!instantReplayEngagedRef.current) enterInstantReplay(capped)
      else {
        seekInstantReplay(capped, { engageReplay: true, forceAdmit: true })
        // Restart tape ticker from the new head so play continues (YouTube-style seek).
        if (replayPlayingRef.current) setReplayPlayEpoch((n) => n + 1)
      }
      return
    }
    // Keep preview playing through beat skips — jumpTo pause-on-scrub fought Left rewind.
    if (runningRef.current) {
      if (
        tapeCap != null &&
        tapeCap > 250 &&
        Boolean(tapeReviewRef.current) &&
        !selectedIsCompletedRef.current &&
        capped >= tapeCap - 40
      ) {
        stopLiveTapeReviewAtEnd('beat-skip-tape-cap')
        return
      }
      seekWhilePreviewPlaying(capped)
      return
    }
    // Paused Reviewing / Planned / live-idle: paint + forceAdmit Seek (same as scrub).
    // jumpTo→pump raced the tape-end Pause desire and left ← dead until Space Play/Pause.
    elapsedRef.current = capped
    setElapsedMs(capped)
    setHoverElapsedMs(null)
    if (
      !previewTemplateIdRef.current &&
      instanceIdRef.current &&
      !selectedIsCompletedRef.current &&
      (canOperateSessionRef.current || canDrivePreviewRef.current)
    ) {
      forceAdmitDeckScrubSeek(capped)
      return
    }
    jumpTo(capped)
  }

  /** Pause-on-drag for Preview / Done / Live Reviewing. Official Live take uses IR scrub. */
  function pauseForDeckScrub(): void {
    // Official take Running: do not seek — Pause first via transport, then Instant Replay.
    if (liveTakeActive) return
    const id = instanceIdRef.current
    const sharedPreview =
      selectedIsPlanned && Boolean(id) && !previewTemplateId && canDrivePreview
    const liveReviewScrub =
      Boolean(id) &&
      canOperateSession &&
      !selectedIsCompletedRef.current &&
      (tapeReviewRef.current || (inLiveRun && !liveTakeActive))
    if ((sharedPreview || liveReviewScrub) && id) {
      // Scrub commit owns the Seek — do not freeze begin-elapsed (raced jumpTo(0)).
      runningRef.current = false
      setRunning(false)
      // Already paused: skip hub Pause. Enqueueing pauseWithoutPin left the pump /
      // transportPauseHold wedged so later Play's ResumeAt never left the client.
      if (!hubAdmittedRunningRef.current) {
        if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
        hubSeekOwnsHeadRef.current = true
        return
      }
      enqueueStudioClock({ running: false, pauseWithoutPin: true })
      return
    }
    if (!running && !runningRef.current) return
    runningRef.current = false
    setRunning(false)
  }

  /**
   * Preview / Reviewing / live-idle Play after scrub. Goes straight to ResumeAt /
   * PreviewPlay — same as scrub commit Seek. The coalesced pump could stay wedged
   * behind scrub-begin Pause (or supersede Play with skip-pause-already) so Space
   * painted || locally with zero hub TX.
   */
  function driveDeckPlayAt(
    headMs: number,
    opts: { playAs: 'resume' | 'previewPlay'; rate: number }
  ): void {
    const id = instanceIdRef.current
    if (!id || previewTemplateIdRef.current) return
    const prev = clocksRef.current.get(id)
    if (!prev) return
    const start = snapshotRef.current.session.storyStartTime
    const next = Math.max(0, headMs)
    const iso = addStoryMs(start, next)
    const rate = Math.max(PLAYBACK_RATE_MIN, opts.rate)
    cancelStudioClockDesire('drive-deck-play')
    if (transportPauseHoldIdRef.current === id) transportPauseHoldIdRef.current = null
    if (pauseHoldIdRef.current === id) pauseHoldIdRef.current = null
    const playGen = ++deckPlayGenRef.current
    runningRef.current = true
    setRunning(true)
    elapsedRef.current = next
    setElapsedMs(next)
    localPlayAnchorRef.current = { wall: performance.now(), elapsed: next, rate }
    hubSeekOwnsHeadRef.current = true
    transportPlayHoldIdRef.current = id
    // Admit only after hub succeeds. Pin Pause always sends hubPause now; gen
    // still voids a late ResumeAt that would restart the room after Pause.
    hubAdmittedRunningRef.current = false
    const keepPlanned = opts.playAs === 'previewPlay' || prev.lifecycle === 'planned'
    const nextClock: StudioClock = {
      ...prev,
      lifecycle: keepPlanned ? 'planned' : 'running',
      clockStatus: 'running',
      rate,
      anchorStoryTimeIso: iso,
      anchorActualTime: new Date().toISOString(),
      storyOriginIso: prev.storyOriginIso || prev.anchorStoryTimeIso,
    }
    clocksRef.current.set(id, nextClock)
    serverClockRef.current = nextClock
    setServerClock(nextClock)
    setInstances((rows) =>
      rows.map((row) =>
        row.id === id
          ? {
              ...row,
              clockStatus: 'running',
              studioClock: nextClock,
              playheadStoryIso: iso,
            }
          : row
      )
    )
    const stillThisPlay = (): boolean =>
      deckPlayGenRef.current === playGen && instanceIdRef.current === id
    const fail = (err: unknown): void => {
      if (!stillThisPlay()) return
      if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
      hubAdmittedRunningRef.current = false
      runningRef.current = false
      setRunning(false)
      setServerNotice(studioActionMessage(err, 'Could not resume playback.'))
    }
    /** Pause superseded this play after ResumeAt already hit the hub — re-pause. */
    const healSupersededPlay = (): void => {
      if (transportPlayHoldIdRef.current === id) transportPlayHoldIdRef.current = null
      hubAdmittedRunningRef.current = false
      const freezeIso = addStoryMs(
        snapshotRef.current.session.storyStartTime,
        elapsedRef.current
      )
      void hubPause(id, freezeIso, newClientTrace('deckPlaySuperseded')).catch(() => undefined)
    }
    if (opts.playAs === 'previewPlay') {
      void hubSeek(id, iso, false, false, true)
        .then((admitted) => {
          if (!stillThisPlay()) {
            healSupersededPlay()
            return
          }
          if (admitted) {
            lastSentHubSeekMsRef.current = next
            hubWirePlayheadMsRef.current = next
            setHubWirePlayheadMs(next)
          }
          return hubPreviewPlay(id, rate)
        })
        .then(() => {
          if (!stillThisPlay()) {
            healSupersededPlay()
            return
          }
          hubAdmittedRunningRef.current = true
        })
        .catch(fail)
      return
    }
    void hubResumeAt(id, iso, rate)
      .then(() => {
        if (!stillThisPlay()) {
          healSupersededPlay()
          return
        }
        lastSentHubSeekMsRef.current = next
        hubWirePlayheadMsRef.current = next
        setHubWirePlayheadMs(next)
        hubAdmittedRunningRef.current = true
      })
      .catch(fail)
  }

  /**
   * Instant Replay scrub while Live+Paused: move the shared replay head (hub Seek)
   * without changing the stored pause point Resume will return to.
   * Cap at the pause point — IR is look-back only (no peek-ahead past the freeze).
   */
  function seekInstantReplay(
    ms: number,
    opts?: { engageReplay?: boolean; skipLocal?: boolean; forceAdmit?: boolean }
  ): void {
    // Never engage IR while the official run is advancing, or before Run 1 / between takes.
    if (runningRef.current && opts?.engageReplay) return
    if (takeNumberRef.current <= 0 || snapshotRef.current.session.exerciseTakeStopped === true) {
      return
    }
    if (!canSeekTimeBarRef.current && !opts?.skipLocal && !instantReplayEngagedRef.current) return
    const storyCap = displayDur > 0 ? displayDur : ms
    const freeze = instantReplayPauseMsRef.current
    const inIr = Boolean(opts?.engageReplay || instantReplayEngagedRef.current)
    const cap =
      inIr && freeze != null ? Math.min(storyCap, freeze) : storyCap
    const next = Math.min(Math.max(0, ms), cap)
    if (!opts?.skipLocal) {
      elapsedRef.current = next
      setElapsedMs(next)
      setHoverElapsedMs(null)
      // Scrub / beat skip while tape is playing — re-anchor the ticker.
      if (replayPlayingRef.current) setReplayPlayEpoch((n) => n + 1)
    }
    // IR owns the head briefly so peer/self Clock from this Seek doesn't fight the tape.
    localReplayDriveUntilRef.current = performance.now() + 400
    // Only engage IR when explicitly requested (scrub / beat skip) — never because a
    // leftover pauseMs differs from the seek head (that false-engaged mid-Run).
    if (!runningRef.current && opts?.engageReplay) {
      if (replaySavedRateRef.current == null) {
        replaySavedRateRef.current = playbackRateRef.current
      }
      if (!instantReplayEngagedRef.current) setInstantReplayEngaged(true)
    }
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    // Spectators never author Seek — follow the operator's Clock only.
    if (!canOperateSessionRef.current) return
    if (opts?.skipLocal) {
      // Tape ticker already moved the head — coalesced progress on the serial lane.
      sendIrTapeProgressSeek(next)
      return
    }
    // Scrub / engage / commit — serial lane forceAdmit (never parallel hubSeek or pump).
    lastSentHubSeekMsRef.current = next
    hubWirePlayheadMsRef.current = next
    sendIrTapeScrubSeek(next)
  }

  function jumpTo(ms: number, opts?: { keepTimelineFocus?: boolean }): void {
    if (!canScrub) return
    // Time-bar / transport seeks drop edit focus so "current" follows the playhead.
    // Timeline card picks pass keepTimelineFocus so the row stays hot.
    if (!opts?.keepTimelineFocus) setTimelineFocusId(null)
    const tapeCap =
      tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
        ? tapeAvailableUntilMsRef.current
        : null
    const cap =
      tapeCap != null
        ? tapeCap
        : displayDur > 0
          ? displayDur
          : ms
    const next = Math.min(Math.max(0, ms), cap)
    const id = instanceIdRef.current
    const prev = id && !previewTemplateIdRef.current ? clocksRef.current.get(id) : undefined
    // Hub seek for Preview (planned) and Live — local-only only when we cannot drive the room.
    const mayHub =
      Boolean(id) &&
      !previewTemplateIdRef.current &&
      (canDrivePreviewRef.current || canOperateSessionRef.current) &&
      Boolean(prev) &&
      (prev!.lifecycle === 'planned' ||
        prev!.lifecycle === 'running' ||
        prev!.clockStatus === 'running' ||
        prev!.clockStatus === 'paused' ||
        prev!.clockStatus === 'idle')
    if (mayHub && prev) {
      const wasRunning = prev.clockStatus === 'running' || runningRef.current
      enqueueHubStorySeek(next, { keepRunning: false, pauseFirst: wasRunning })
    } else {
      const rate = Math.max(PLAYBACK_RATE_MIN, playbackRateRef.current)
      localPlayAnchorRef.current = { wall: performance.now(), elapsed: next, rate }
      beatNavAnchorMsRef.current = next
      elapsedRef.current = next
      setElapsedMs(next)
      setHoverIds([])
      setHoverElapsedMs(null)
      if (runningRef.current) setClockEpoch((n) => n + 1)
    }
    if (!started) return
    setActualStartTime((cur) => cur ?? new Date().toISOString())
  }

  /** Prefer icons/hazards over free-text labels when a timeline beat selects a map object. */
  function pickTimelineObjectId(objectIds: string[]): string | undefined {
    const present = objectIds.filter((id) => viewSession.objects.some((o) => o.id === id))
    return (
      present.find((id) => viewSession.objects.find((o) => o.id === id)?.type !== 'label') ??
      present[0]
    )
  }

  function jumpFromTimeline(evId: string, objectIds: string[]): void {
    setTimelineFocusId(evId)
    const pick = pickTimelineObjectId(objectIds)
    if (pick) {
      setSelectedId(pick)
      setNotesCtx(pick)
    }
  }

  /** Edit-mode card focus: seek playhead so "current" follows the card (scrub still clears focus). */
  function focusTimelineBeat(evId: string, atIso: string, objectIds?: string[]): void {
    setTimelineFocusId(evId)
    if (objectIds?.length) {
      const pick = pickTimelineObjectId(objectIds)
      if (pick) {
        setSelectedId(pick)
        setNotesCtx(pick)
      }
    }
    if (canScrub) jumpTo(off(atIso), { keepTimelineFocus: true })
  }

  function inspectFromTimeline(evId: string, objectIds: string[]): void {
    setTimelineFocusId(evId)
    const pick = pickTimelineObjectId(objectIds)
    if (!pick) return
    setSelectedId(pick)
    setNotesCtx(pick)
    setDock('inspector')
  }

  function ensureSurfaceWind(): ScenarioObject | null {
    const existing = viewSession.objects.find((o) => o.type === 'wind') ?? null
    if (existing) return existing
    if (!canEditBoard) return null
    const obj: ScenarioObject = {
      id: newId('el'),
      type: 'wind',
      label: 'Surface wind',
      scope: 'global',
      pose: null,
      size: 'md',
      showOnTimeline: false,
      params: defaultParams('wind'),
      comments: [],
    }
    commitSession((prev) => {
      if (prev.objects.some((o) => o.type === 'wind')) return prev
      return { ...prev, objects: [...prev.objects, obj] }
    })
    return obj
  }

  /** Remember which rail Inspect replaced. Profile is finished once Inspect opens. */
  function noteInspectOpenedFrom(): void {
    const cur = dockRef.current
    if (cur === 'inspector') return
    inspectReturnDockRef.current = cur === 'account' ? null : cur
  }

  function inspectWind(opts?: { create?: boolean }): void {
    const target =
      sessionWind ?? wind ?? (opts?.create === false ? null : ensureSurfaceWind())
    if (!target) return
    // Toggle: second click returns to the rail we left (People / Timeline / etc.).
    if (
      dockRef.current === 'inspector' &&
      selectedId === target.id &&
      inspectReturnDockRef.current
    ) {
      const back = inspectReturnDockRef.current
      inspectReturnDockRef.current = null
      setSelectedId(null)
      setDock(back && back !== 'inspector' && back !== 'account' ? back : 'scenarios')
      return
    }
    noteInspectOpenedFrom()
    setSelectedId(target.id)
    setPlaceType(null)
    setDock('inspector')
    const liveId = `live-${target.id}`
    if (displayTimeline.some((ev) => ev.id === liveId)) setTimelineFocusId(liveId)
  }

  /** Open Inspect; auto-pick Surface wind when nothing else is selected. */
  function openInspectorDock(): void {
    if (
      selectedId ||
      (canPlaceObjects &&
        (placeType === 'type' ||
          placeType === 'freehand' ||
          placeType === 'pen' ||
          placeType === 'ruler'))
    ) {
      setDock('inspector')
      return
    }
    if (sessionWind ?? wind) {
      inspectWind({ create: false })
      return
    }
    setDock('inspector')
  }

  function patchSelectedObject(patch: (obj: ScenarioObject) => ScenarioObject): void {
    if (!selectedId || !canMutateSelected) return
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== selectedId) return o
        const next = patch(o)
        if (!next.pristine) return next
        const { pristine: _p, ...rest } = next
        return rest
      }),
    }))
  }

  function setPoseFrames(frames: PoseKeyframe[] | undefined): void {
    if (!frames || frames.length < 2) stopPathPreview()
    patchSelectedObject((o) => {
      const next = { ...o }
      if (!frames?.length) {
        delete next.keyframes
        delete next.loopKeyframes
        return next
      }
      next.keyframes = sortPoseFrames(frames)
      return next
    })
  }

  function setVisibilityFrames(frames: VisibilityKeyframe[] | undefined): void {
    if (!frames || frames.length < 2) stopVisPreview()
    if (!frames?.length) setVisibilityFocusIndex(null)
    patchSelectedObject((o) => {
      const next = { ...o }
      if (!frames?.length) {
        delete next.visibilityKeyframes
        return next
      }
      const promoting = !o.visibilityKeyframes?.length
      if (promoting && (o.startsAt != null || o.endsAt != null)) {
        next.visibilityKeyframes = visibilityFramesFromShowHide(o)
      } else {
        next.visibilityKeyframes = sortVisFrames(frames)
      }
      // Either/or: keyframes own visibility once present.
      delete next.startsAt
      delete next.endsAt
      return next
    })
  }

  function moveWaypoint(sortedIndex: number, lng: number, lat: number): void {
    patchSelectedObject((o) => {
      const sorted = sortPoseFrames(o.keyframes ?? [])
      if (!sorted.length || sortedIndex < 0 || sortedIndex >= sorted.length) return o
      let start = sortedIndex
      let end = sortedIndex
      while (start > 0 && posesSameSpot(sorted[start - 1]!, sorted[start]!)) start -= 1
      while (end < sorted.length - 1 && posesSameSpot(sorted[end]!, sorted[end + 1]!)) end += 1
      const next = sorted.map((frame, i) =>
        i >= start && i <= end ? { ...frame, lng, lat } : frame
      )
      return { ...o, keyframes: sortPoseFrames(next) }
    })
    setWaypointFocusIndex(sortedIndex)
    flashWaypointRow(sortedIndex)
  }

  function flashWaypointRow(index: number): void {
    setWaypointFlashIndex(index)
    if (waypointFlashTimerRef.current != null) window.clearTimeout(waypointFlashTimerRef.current)
    waypointFlashTimerRef.current = window.setTimeout(() => setWaypointFlashIndex(null), 500)
  }

  function setRadiusFrames(frames: NumberKeyframe[] | undefined): void {
    patchSelectedObject((o) => {
      const next = { ...o }
      if (!frames?.length) {
        delete next.radiusKeyframes
        return next
      }
      next.radiusKeyframes = sortNumberFrames(frames)
      return next
    })
  }

  function setStatusFrames(frames: StatusKeyframe[] | undefined): void {
    if (!frames?.length) setStatusFocusIndex(null)
    patchSelectedObject((o) => {
      const next = { ...o }
      if (!frames?.length) {
        delete next.statusKeyframes
        return next
      }
      next.statusKeyframes = sortStatusFrames(frames)
      const last = next.statusKeyframes[next.statusKeyframes.length - 1]
      if (last) next.params = { ...o.params, status: last.status }
      return next
    })
  }

  function applyRoadStatus(status: RoadStatus): void {
    if (!canEditBoard) return
    patchSelectedObject((o) => {
      if (o.statusKeyframes?.length) {
        const frames = [...o.statusKeyframes]
        const at = frames.findIndex((f) => off(f.at) === elapsedMs)
        if (at >= 0) frames[at] = { ...frames[at]!, status }
        else frames.push({ at: atOff(elapsedMs), status })
        return {
          ...o,
          statusKeyframes: sortStatusFrames(frames),
          params: { ...o.params, status },
        }
      }
      return { ...o, params: { ...o.params, status } }
    })
  }

  function findNearbyAssets(): void {
    if (!selectedSource?.pose) {
      setNearbyHint('Select a placed object with a map position first.')
      setNearbyCandidates([])
      setNearbyHoverKey(null)
      return
    }
    if (!mapApiRef.current) {
      setNearbyHint('Map is still loading. Try Find assets again in a moment.')
      setNearbyCandidates([])
      setNearbyHoverKey(null)
      return
    }
    const kinds = new Set(attachKindsForObject(selectedSource.type))
    const pose = selectedSource.pose
    // Wide radius for POC scroll/visibility testing. queryRenderedFeatures still only
    // sees loaded Mapbox tiles in view — pan/zoom then Find again to refresh.
    const radiusM = 5_000
    const found = mapApiRef.current
      .queryNearbyFeatures(pose.lng, pose.lat, radiusM)
      .filter((f) => kinds.has(f.kind))
    const existing = readAttachedAssets(selectedSource.params)
    const byKey = new Map<string, MapFeatureTap>()
    for (const a of existing) {
      const key = assetKey(a)
      byKey.set(key, assetToHighlightTap(a, pose))
    }
    for (const f of found) {
      const key = assetKey(f)
      // Prefer live tile geometry from the query when available.
      byKey.set(key, f)
    }
    const merged = [...byKey.values()]
    const linkedKeys = new Set(existing.map(assetKey))
    setNearbyCandidates(merged)
    setNearbyPicks(new Set(linkedKeys))
    nearbySelectAnchorKeyRef.current = null
    setNearbyHoverKey(null)
    setNearbySort({ col: 'dist', dir: 'asc' })
    if (!merged.length) {
      setNearbyHint(
        'No matching roads/pipes visible near this object. Zoom in or move closer to the asset.'
      )
    } else {
      setNearbyHint(null)
    }
  }

  function toggleNearbySort(col: NearbySortCol): void {
    setNearbySort((prev) =>
      prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }
    )
  }

  function toggleLinkedSort(col: LinkedSortCol): void {
    setLinkedSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }
    )
  }

  function openScenarioJsonDebug(): void {
    if (!canViewDebug) return
    if (viewingTemplate && previewTemplateId) {
      const stored = templateEditLive
        ? flushTemplates(templates).find((t) => t.id === previewTemplateId)
        : templateById(templates, previewTemplateId)
      setJsonDebug((cur) =>
        cur?.kind === 'scenario'
          ? null
          : {
              kind: 'scenario',
              title: `Template (${previewTemplateId})`,
              body: JSON.stringify(stored ?? null, null, 2),
            }
      )
      return
    }
    const flushed = flushInstance(instances)
    const inst = flushed.find((i) => i.id === instanceId) ?? null
    setJsonDebug((cur) =>
      cur?.kind === 'scenario'
        ? null
        : {
            kind: 'scenario',
            title: `Scenario instance (${inst?.id ?? '—'})`,
            body: JSON.stringify(inst, null, 2),
          }
    )
  }

  function openStoreJsonDebug(): void {
    if (!canViewDebug) return
    const store = {
      lastInstanceId: instanceId,
      instances: viewingTemplate ? instances : flushInstance(instances),
      templates: templateEditLive ? flushTemplates(templates) : templates,
      remembered,
    }
    setJsonDebug((cur) =>
      cur?.kind === 'store'
        ? null
        : {
            kind: 'store',
            title: 'Studio client snapshot (in memory)',
            body: JSON.stringify(store, null, 2),
          }
    )
  }

  function openPeopleJsonDebug(): void {
    if (!canViewDebug) return
    const flushed = viewingTemplate ? instances : flushInstance(instances)
    const inst = flushed.find((i) => i.id === instanceId)
    const roster = inst?.people ?? people
    setJsonDebug((cur) =>
      cur?.kind === 'people'
        ? null
        : {
            kind: 'people',
            title: 'Scenario people roster',
            body: JSON.stringify(roster, null, 2),
          }
    )
  }

  function openActionsJsonDebug(): void {
    if (!canViewDebug || !instanceId || viewingTemplate) return
    if (jsonDebug?.kind === 'actions') {
      setJsonDebug(null)
      return
    }
    const boardId = instanceId
    setJsonDebug({
      kind: 'actions',
      title: `Room actions for ${boardId} (loading…)`,
      body: '[]',
    })
    void fetchScenarioInstance(boardId, user)
      .then((snap) => {
        // Stale response if the user switched boards while loading.
        if (instanceIdRef.current !== boardId) return
        const actions = snap.actions ?? []
        setInstances((prev) =>
          prev.map((row) => (row.id === snap.id ? { ...row, actions } : row)),
        )
        setJsonDebug({
          kind: 'actions',
          title: `Room actions (${actions.length}) · ${snap.id}`,
          body: JSON.stringify(actions, null, 2),
        })
      })
      .catch((err) => {
        if (instanceIdRef.current !== boardId) return
        setJsonDebug({
          kind: 'actions',
          title: `Room actions (failed) · ${boardId}`,
          body: JSON.stringify(
            { error: err instanceof Error ? err.message : String(err) },
            null,
            2,
          ),
        })
      })
  }

  function openSelectedProfileJsonDebug(): void {
    if (!canViewDebug || selectedPeople.length !== 1) return
    const person = selectedPeople[0]!
    const platform = platformProfileDebugForEmail(person.email)
    const blob =
      platform.registrationProfile ??
      (person.email.trim().toLowerCase() === user.email.trim().toLowerCase()
        ? readRegistrationProfileJson()
        : null)
    if (!blob && !platform.matched) return
    const payload = {
      scenarioPerson: person,
      loggedInPerson: user,
      platformSlice: platform.loggedInSlice,
      registrationProfile: blob,
    }
    setJsonDebug((cur) =>
      cur?.kind === 'profile'
        ? null
        : {
            kind: 'profile',
            title: 'PLATFORM PROFILE',
            body: JSON.stringify(payload, null, 2),
          }
    )
  }

  function flashJsonCopyNote(message: string): void {
    setJsonCopyNote(message)
    if (jsonCopyTimerRef.current != null) window.clearTimeout(jsonCopyTimerRef.current)
    jsonCopyTimerRef.current = window.setTimeout(() => {
      setJsonCopyNote(null)
      jsonCopyTimerRef.current = null
    }, 1600)
  }

  function commitNearbyAttaches(): void {
    if (!selectedId || !canEditBoard) return
    // Replace the full linked set so unchecked rows unlink (Find panel is the editor).
    const picks = nearbyCandidates.filter((c) => nearbyPicks.has(assetKey(c)))
    patchSelectedObject((o) => {
      const next: AttachedAsset[] = picks.map((tap) => ({
        kind: tap.kind,
        name: tap.name,
        properties: tap.properties,
        path: tap.path,
        segments: tap.segments,
      }))
      return withRoadGeometryFromAssets(o, next)
    })
    setNearbyCandidates([])
    setNearbyPicks(new Set())
    setNearbyHoverKey(null)
    setNearbyHint(null)
    setAssetDebugKey(null)
  }

  function removeAttachedAsset(key: string): void {
    patchSelectedObject((o) =>
      withRoadGeometryFromAssets(
        o,
        readAttachedAssets(o.params).filter((a) => assetKey(a) !== key)
      )
    )
  }

  function placeObject(lng: number, lat: number, _feature?: MapFeatureTap | null): void {
    if (!placeType || placeType === 'freehand' || placeType === 'pen' || placeType === 'ruler')
      return
    if (viewingTemplate && !templateEditLive) return
    if ((placeType === 'tornado' || placeType === 'hurricane') && !showTornado) return
    if (placeType === 'type' || placeType === 'wind') {
      if (!canEditBoard) return
    } else if (!canPlaceObjects) return
    const placedCount = session.objects.filter((o) => o.type !== 'wind').length
    if (placedCount >= MAX_BOARD_OBJECTS) {
      setNearbyHint(
        `Board limit reached (${MAX_BOARD_OBJECTS} objects). Remove some before adding more.`
      )
      return
    }
    if (!canEditBoard) {
      const owned = session.objects.filter((o) => ownsObject(o)).length
      if (owned >= MAX_OWNED_OBJECTS) {
        setNearbyHint(`You can place up to ${MAX_OWNED_OBJECTS} objects on this board.`)
        return
      }
    }
    const typeId = placeType === 'type' ? 'label' : placeType
    // Placement does not auto-attach map features — attach later from Inspect.
    const placerId = user.userId?.trim() || ''
    const obj: ScenarioObject = {
      id: newId('el'),
      type: typeId,
      label: typeId === 'label' ? labelStyle.text.trim() || 'Label' : paletteLabel(typeId),
      scope: 'local',
      pose: { lng, lat },
      size: typeId === 'label' ? labelStyle.size : 'md',
      params: {
        ...defaultParams(typeId),
        ...(typeId === 'label'
          ? { text: labelStyle.text.trim() || 'Label', color: labelStyle.color }
          : {}),
        ...(typeId === 'road' ? { status: 'open' } : {}),
      },
      comments: [],
      ...(placerId ? { createdById: placerId } : {}),
      pristine: true,
    }
    if (isLocalStorm(typeId)) {
      obj.params = { ...obj.params, presetOnsetAt: atOff(elapsedMs) }
    }
    if (started) {
      obj.addedAt = atOff(elapsedMs)
      obj.startsAt = atOff(elapsedMs)
    }
    const boardNote =
      typeId === 'label'
        ? liveComment({
            id: newId('c'),
            createdAt: atOff(elapsedMs),
            authorId: placerId,
            authorName: commentAuthorName(placerId),
            kind: 'action',
            body: `Placed text “${obj.label}”`,
          })
        : null
    commitSession((prev) => ({ ...prev, objects: [...prev.objects, obj] }))
    if (boardNote) appendRoomChatLine(boardNote)
    setSelectedId(obj.id)
    setPlaceType(null)
    setNearbyCandidates([])
    setNearbyPicks(new Set())
    setNearbyHoverKey(null)
    setNearbyHint(null)
    setDock('inspector')
  }

  function placeStroke(path: [number, number][]): void {
    if (viewingTemplate && !templateEditLive) return
    if (!canEditBoard || path.length < 2) return
    const start = path[0]!
    const fromLine = placeType === 'pen'
    const label = sketchStyle.label.trim() || (fromLine ? 'Line' : 'Sketch')
    const placerId = user.userId?.trim() || ''
    const obj: ScenarioObject = {
      id: newId('el'),
      type: 'sketch',
      label,
      scope: 'local',
      pose: { lng: start[0], lat: start[1] },
      size: 'md',
      path,
      params: {
        color: sketchStyle.color,
        width: sketchStyle.width,
        opacity: sketchStyle.opacity,
        strokeKind: fromLine ? 'line' : 'freehand',
      },
      comments: [],
      showOnTimeline: false,
      ...(placerId ? { createdById: placerId } : {}),
      pristine: true,
    }
    if (started) {
      obj.addedAt = atOff(elapsedMs)
      obj.startsAt = atOff(elapsedMs)
    }
    const toolName = fromLine ? 'Line' : 'Freehand sketch'
    const boardNote = liveComment({
      id: newId('c'),
      createdAt: atOff(elapsedMs),
      authorId: placerId,
      authorName: commentAuthorName(placerId),
      kind: 'action',
      body:
        label !== 'Sketch' && label !== 'Line'
          ? `Placed ${toolName} “${label}”`
          : `Placed ${toolName}`,
    })
    commitSession((prev) => ({ ...prev, objects: [...prev.objects, obj] }))
    appendRoomChatLine(boardNote)
    setSelectedId(obj.id)
    setPlaceType(null)
    setDock('inspector')
  }

  function inspectRadiusM(obj: {
    type: string
    params: Record<string, unknown>
    windKeyframes?: { plumeDistanceM?: number }[]
  }): number {
    if (isLocalStorm(obj.type)) return tornadoImpactRadiusM(obj)
    return Number(obj.params.radiusM)
  }

  function applyRadiusMeters(radiusM: number): void {
    setRadiusInputDraft(null)
    const typeId = selectedSource?.type
    const cfg = typeId ? zoneCfgFor(typeId) : undefined
    const min = cfg?.min ?? 1
    const max = cfg?.max ?? Number.POSITIVE_INFINITY
    const clamped = Math.min(max, Math.max(min, radiusM))
    patchSelectedObject((o) => {
      // Always keep params.radiusM in sync so the comedy story / Inspect baseline track the slider.
      const withParams = { ...o, params: { ...o.params, radiusM: clamped } }
      if (o.radiusKeyframes?.length) {
        const frames = [...o.radiusKeyframes]
        const at = frames.findIndex((f) => off(f.at) === elapsedMs)
        if (at >= 0) frames[at] = { ...frames[at]!, value: clamped }
        else frames.push({ at: atOff(elapsedMs), value: clamped })
        return { ...withParams, radiusKeyframes: sortNumberFrames(frames) }
      }
      return withParams
    })
  }

  /** Clear the highlighted Position/Waypoints row (pin focus). Map dim stays while the accordion is open. */
  function clearPathEditFocus(): void {
    setWaypointFocusIndex(null)
  }

  function focusPoseKeyframe(index: number | null): void {
    setWaypointFocusIndex(index)
    if (index == null || !selectedSource?.keyframes?.length) return
    const sorted = sortPoseFrames(selectedSource.keyframes)
    const frame = sorted[index]
    if (!frame) return
    if (windPreviewActive) stopWindPreview()
    if (pathPreviewActive) stopPathPreview()
    setRunning(false)
    setElapsedMs(off(frame.at))
    if (selectedSource.type === 'wind' || isLocalStorm(selectedSource.type)) {
      setWindAuthorMs(off(frame.at))
    }
  }

  function applyObjectSize(size: ObjectSize): void {
    patchSelectedObject((o) => {
      const mapped = SIZE_RADIUS_M[o.type]?.[size === 'xl' ? 'lg' : size]
      if (mapped == null) return { ...o, size }
      if (o.radiusKeyframes?.length) {
        const frames = [...o.radiusKeyframes]
        const at = frames.findIndex((f) => off(f.at) === elapsedMs)
        if (at >= 0) frames[at] = { ...frames[at]!, value: mapped }
        else frames.push({ at: atOff(elapsedMs), value: mapped })
        return { ...o, size, radiusKeyframes: sortNumberFrames(frames) }
      }
      return { ...o, size, params: { ...o.params, radiusM: mapped } }
    })
  }

  /** Move an object; path sketches and road paths translate with the drag (or pose edit).
   * Linked map assets stay georeferenced — never shift with a point icon.
   * Map drags pass `persist: false` and flush once on pointer-up (live pose rides Watch/edit frames). */
  function moveObject(
    id: string,
    lng: number,
    lat: number,
    origin: 'anchor' | 'pose' | { lng: number; lat: number } = 'anchor',
    opts?: { persist?: boolean }
  ): void {
    const target = session.objects.find((o) => o.id === id)
    if (!canMutateObject(target)) return
    if (!canDragObject(id)) return
    commitSession(
      (prev) => ({
        ...prev,
        objects: prev.objects.map((o) => {
          if (o.id !== id) return o
          // Prefer the map's drag sample when provided. Authored pose can be the path
          // origin while the marker sits at the playhead sample (keyframed boats etc.) —
          // delta-from-pose then teleports the whole path on mouseup.
          const from =
            typeof origin === 'object'
              ? origin
              : o.pose ?? objectAnchorLngLat(o)
          if (!from) {
            const moved = { ...o, pose: { lng, lat } }
            delete moved.pristine
            return moved
          }
          const dLng = lng - from.lng
          const dLat = lat - from.lat
          if (Math.abs(dLng) < 1e-12 && Math.abs(dLat) < 1e-12) return o
          const shiftPt = (p: [number, number]): [number, number] => [p[0] + dLng, p[1] + dLat]
          const next: ScenarioObject = {
            ...o,
            // Absolute pose. A delta from a marker position that has drifted
            // leaves the icon off the footprint for the rest of the drag.
            pose:
              o.type === 'road' || o.type === 'sketch'
                ? o.pose
                  ? { lng: o.pose.lng + dLng, lat: o.pose.lat + dLat }
                  : { lng, lat }
                : { lng, lat },
          }
          delete next.pristine
          // Point icons (responder, compressor, tornado…): move pose; when a path exists and
          // pins aren't open, nudge the whole waypoint path with the icon.
          if (o.type !== 'road' && o.type !== 'sketch') {
            if (o.keyframes?.length) {
              next.keyframes = o.keyframes.map((f) => ({
                ...f,
                lng: f.lng + dLng,
                lat: f.lat + dLat,
              }))
            }
            // Drop any legacy board line geom that would paint/shift with the pin.
            if (next.path || Array.isArray(next.params.segments)) {
              const params = { ...next.params }
              delete params.segments
              const cleaned = { ...next, params }
              delete cleaned.path
              return cleaned
            }
            return next
          }
          if (o.path?.length) next.path = o.path.map(shiftPt)
          const params = { ...o.params }
          const segs = params.segments
          if (Array.isArray(segs)) {
            params.segments = segs.map((seg) =>
              Array.isArray(seg) ? (seg as [number, number][]).map(shiftPt) : seg
            )
          }
          const assets = readAttachedAssets(params)
          if (assets.length) {
            return {
              ...next,
              params: writeAttachedAssets(
                params,
                assets.map((a) => ({
                  ...a,
                  path: a.path?.map(shiftPt),
                  segments: a.segments?.map((seg) => seg.map(shiftPt)),
                }))
              ),
            }
          }
          next.params = params
          return next
        }),
      }),
      { persistObjects: opts?.persist !== false }
    )
  }

  function commentAuthorName(authorId: string): string | undefined {
    const person = findPersonByKey(people, authorId)
    const name =
      person?.name.trim() ||
      (authorId === user.userId || authorId === user.email ? user.name.trim() : '')
    return name || undefined
  }

  /** Append a live/action line to the master room chat (YouTube thread). */
  function appendRoomChatLine(line: ScenarioComment): void {
    if (previewTemplateIdRef.current) return
    const reviewing =
      Boolean(tapeReviewRef.current) ||
      boardActivityRef.current === 'watching' ||
      Boolean(watchingRunId)
    const phase = inferRoomChatPhase({
      lifecycle: currentInstance?.lifecycle,
      liveTakeActive: liveTakeActiveRef.current,
      inRunReview: reviewing && !liveTakeActiveRef.current,
      boardActivity: boardActivityRef.current,
    })
    const activeRun = liveTakeActiveRef.current
      ? resolveActiveChatRun(snapshotRef.current.session, { watchingRunId })
      : null
    const stamped: ScenarioComment = {
      ...line,
      roomPhase: line.roomPhase ?? phase,
      ...(activeRun && !line.runId ? { runId: activeRun.id } : {}),
    }
    const prevChat = snapshotRef.current.session.roomChat ?? []
    const nextChat = prevChat.some((c) => c.id === stamped.id)
      ? prevChat
      : [...prevChat, stamped]
    commitSessionFields((prev) => {
      const cur = prev.roomChat ?? []
      if (cur.some((c) => c.id === stamped.id)) return prev
      return patchRoomChat(prev, [...cur, stamped])
    })
    snapshotRef.current = {
      ...snapshotRef.current,
      session: patchRoomChat(snapshotRef.current.session, nextChat),
    }
    persistSessionField({ roomChat: nextChat })
    const readerId = user.userId?.trim() || user.email?.trim() || ''
    if (markChatRead(instanceIdRef.current, liveRoomChat(snapshotRef.current.session), readerId)) {
      setChatReadEpoch((n) => n + 1)
    }
  }

  function addComment(body: string): void {
    const text = body.trim()
    if (!text || !canComment) return
    if (previewTemplateIdRef.current || currentInstance?.lifecycle === 'completed') return
    const authorId = user.userId?.trim() || ''
    appendRoomChatLine(
      liveComment({
        id: newId('c'),
        createdAt: atOff(elapsedMs),
        authorId,
        authorName: commentAuthorName(authorId),
        body: text,
      })
    )
  }

  function clearRoomChatHistory(): void {
    if (previewTemplateIdRef.current || !instanceIdRef.current) return
    commitSessionFields((prev) => patchRoomChat(prev, []))
    snapshotRef.current = {
      ...snapshotRef.current,
      session: patchRoomChat(snapshotRef.current.session, []),
    }
    persistSessionField({ roomChat: [] })
    const readerId = user.userId?.trim() || user.email?.trim() || ''
    clearChatWatermark(instanceIdRef.current, readerId)
    setChatReadEpoch((n) => n + 1)
    showKeyToast('D', 'Chat history cleared')
  }

  function personFromEmail(
    email: string,
    roles: PersonRole[],
    knownUserId?: string,
    knownName?: string,
  ): ScenarioPerson | null {
    const addr = email.trim()
    if (!addr) return null
    const key = addr.toLowerCase()
    const self = user.email.trim().toLowerCase() === key
    const known = remembered.find((r) => r.email.toLowerCase() === key)
    const directory = directoryNameByEmail.get(key)
    const abpUserId = studioAccountId(
      knownUserId ||
        (self ? user.userId : '') ||
        directoryUserIdByEmail.get(key) ||
        '',
    )
    const offered = [knownName, directory, known?.name]
      .map((value) => value?.trim() ?? '')
      .find((value) => value && value.toLowerCase() !== key && (value.includes(' ') || !value.includes('@')))
    return {
      id: crypto.randomUUID(),
      ...(abpUserId ? { userId: abpUserId } : {}),
      name: (self ? user.name.trim() : '') || offered || addr,
      email: addr,
      status: 'pending',
      hasBeenInvited: false,
      roles: roles.length ? roles : ['participant'],
    }
  }

  function rememberPersonCeiling(userId: string, email: string): void {
    const key = email.trim().toLowerCase()
    if (!userId.trim() || !key) return
    void fetchStudioPeopleCeiling(userId)
      .then((row) => {
        if (!row?.grantedPermissions.length) return
        setStudioPermsByEmail((prev) => {
          const next = new Map(prev)
          next.set(key, row.grantedPermissions)
          return next
        })
        setDirectoryUserIdByEmail((prev) => {
          const next = new Map(prev)
          next.set(key, row.userId || userId)
          return next
        })
      })
      .catch(() => {
        /* ceiling is optional — Participant seat still works */
      })
  }

  function rosterAddFromHit(row: StudioPeopleHit): { name: string; email: string; userId?: string } {
    if (row.inviteAsNew) return { name: row.email, email: row.email }
    return row
  }

  function applyRosterPick(row: StudioPeopleHit): void {
    setPeopleInviteHint(null)
    if (row.userId && !row.inviteAsNew) rememberPersonCeiling(row.userId, row.email)
    commitRosterAdds([rosterAddFromHit(row)])
  }
  applyRosterPickRef.current = applyRosterPick

  function commitRosterAdds(rows: readonly { name: string; email: string; userId?: string }[]): void {
    if (!canManagePeople || !rows.length) return
    const addedIds: string[] = []
    const rememberNew: { name: string; email: string }[] = []
    const nextPeople = [...people]
    for (const row of rows) {
      const addr = row.email
      const existingIdx = nextPeople.findIndex((p) => p.email.toLowerCase() === addr.toLowerCase())
      if (existingIdx >= 0) {
        const existing = nextPeople[existingIdx]!
        if (existing.isDeleted) {
          // Fresh seat — do not revive Editor/Trainer from a prior remove.
          const restored: ScenarioPerson = {
            ...existing,
            isDeleted: false,
            status: 'pending',
            hasBeenInvited: false,
            roles: personHasRole(existing, 'owner') ? existing.roles : ['participant'],
          }
          delete restored.deletionTimeIso
          delete restored.lastInviterId
          delete restored.lastInvitedAtIso
          delete restored.accountInviteSent
          nextPeople[existingIdx] = restored
          addedIds.push(existing.id)
        }
        continue
      }
      const person = personFromEmail(addr, ['participant'], row.userId, row.name)
      if (!person) continue
      nextPeople.push(person)
      addedIds.push(person.id)
      rememberNew.push({ name: person.name, email: person.email })
    }
    if (!addedIds.length) return
    const priorRoster = people
    setPeople(nextPeople)
    void persistPeople(nextPeople, priorRoster)
    if (rememberNew.length) {
      setRemembered((rem) => {
        let out = rem
        for (const row of rememberNew) {
          if (out.some((r) => r.email.toLowerCase() === row.email.toLowerCase())) continue
          out = [...out, row]
        }
        return out
      })
    }
    // Keep prior pending-invite selections so batch add → one Send invites.
    const pendingNext = new Set(rosterPendingInviteIds)
    for (const id of addedIds) pendingNext.add(id)
    setRosterPendingInviteIds(pendingNext)
    setSelectedPeopleIds((prev) => {
      const keep = prev.filter((id) => {
        const row = nextPeople.find((p) => p.id === id)
        return Boolean(row && !row.isDeleted && pendingNext.has(id))
      })
      return [...new Set([...keep, ...addedIds])]
    })
    setPeopleSelectMode(pendingNext.size > 1)
  }

  function softDeletePeople(ids: readonly string[]): void {
    if (!canManagePeople || !ids.length) return
    const idSet = new Set(ids)
    const selfEmail = user.email.trim().toLowerCase()
    commitPeople((prev) => {
      const ownersLeft = activeOwners(prev).filter((p) => !idSet.has(p.id))
      return prev.map((p) => {
        if (!idSet.has(p.id) || p.isDeleted) return p
        // Never remove yourself; never remove the last owner.
        if (selfEmail && p.email.trim().toLowerCase() === selfEmail) return p
        if (personHasRole(p, 'owner') && ownersLeft.length === 0) return p
        return {
          ...p,
          isDeleted: true,
          deletionTimeIso: new Date().toISOString(),
        }
      })
    })
    commitSession((prev) => clearPersonRolePlaySeats(prev, idSet))
    setSelectedPeopleIds((prev) => prev.filter((id) => !idSet.has(id)))
    setPeopleInviteHint(null)
  }

  function requestRemovePeople(ids: readonly string[]): void {
    if (!canManagePeople || !ids.length) return
    const selfEmail = user.email.trim().toLowerCase()
    const ownersLeft = activeOwners(activePeople).filter((p) => !ids.includes(p.id))
    const removable = ids.filter((id) => {
      const p = activePeople.find((x) => x.id === id)
      if (!p) return false
      // Last owner stays — otherwise Owners may be removed like anyone else.
      if (personHasRole(p, 'owner') && ownersLeft.length === 0) return false
      if (selfEmail && p.email.trim().toLowerCase() === selfEmail) return false
      return true
    })
    if (!removable.length) return
    setPendingRemovePeopleIds(removable)
  }

  /** Set after the registration mail is accepted, so a later click does not send it again. */
  function markRegistrationMailSent(id: string): Promise<void> {
    const invitedAt = new Date().toISOString()
    const inviterId = user.userId?.trim() || ''
    const priorRoster = peopleRef.current.slice()
    let changed = false
    const next = priorRoster.map((p) => {
      if (p.id !== id || p.isDeleted || personHasStudioAccount(p)) return p
      changed = true
      return {
        ...p,
        accountInviteSent: true,
        lastInvitedAtIso: invitedAt,
        ...(inviterId ? { lastInviterId: inviterId } : {}),
      }
    })
    if (!changed) return Promise.resolve()
    setPeople(next)
    return persistPeople(next, priorRoster)
  }

  /** Persist invite flags before SendInvite so the invitee’s snapshot is not stale. */
  function markPeopleInvited(ids: readonly string[]): Promise<void> {
    if (!ids.length) return Promise.resolve()
    const idSet = new Set(ids)
    const invitedAt = new Date().toISOString()
    const priorRoster = peopleRef.current.slice()
    const next = priorRoster.map((p) => {
      if (!idSet.has(p.id) || p.isDeleted) return p
      // Registration mail sets its own bit after the server accepts it.
      if (!personHasStudioAccount(p)) return p
      const row: ScenarioPerson = {
        ...p,
        hasBeenInvited: true,
        lastInvitedAtIso: invitedAt,
        // Invite again — clear declined so Join prompt shows again.
        ...(p.status === 'declined' ? { status: 'pending' as const } : {}),
        ...(user.userId?.trim() ? { lastInviterId: user.userId.trim() } : {}),
      }
      return row
    })
    setPeople(next)
    return persistPeople(next, priorRoster)
  }

  function flashPeopleInvite(kind: 'Sent' | 'Nudged' | 'Reminded'): void {
    if (peopleInviteFlashTimer.current != null) window.clearTimeout(peopleInviteFlashTimer.current)
    setPeopleInviteHint(null)
    setPeopleInviteFlash(kind)
    peopleInviteFlashTimer.current = window.setTimeout(() => {
      setPeopleInviteFlash(null)
      peopleInviteFlashTimer.current = null
    }, 2200)
  }

  function inviteDocsForTargets(targets: readonly ScenarioPerson[]): {
    choice: 'plan' | 'trainer' | 'cheatsheet' | 'all'
    options: { id: 'plan' | 'trainer' | 'cheatsheet' | 'all'; label: string }[]
    /** Server-generated kinds to render + upload on send (empty when docs unchecked). */
    docKinds: ('plan' | 'trainer' | 'cheatsheet')[]
  } {
    const typeLabel = sessionTypeById(viewSession.sessionType)?.label ?? 'Session'
    const planLabel = `${typeLabel} Plan`
    const allTrainers =
      targets.length > 0 &&
      targets.every((p) => personHasRole(p, 'trainer') || personHasRole(p, 'owner'))
    const options: {
      id: 'plan' | 'trainer' | 'cheatsheet' | 'all'
      label: string
    }[] = [{ id: 'plan', label: planLabel }]
    if (allTrainers) options.push({ id: 'trainer', label: 'Trainer Guide' })
    options.push({ id: 'cheatsheet', label: 'Product Cheatsheet' })
    options.push({ id: 'all', label: 'All generated docs' })
    const choice = options.some((o) => o.id === inviteDocChoice)
      ? inviteDocChoice
      : options.some((o) => o.id === 'all')
        ? 'all'
        : 'plan'
    const picks: ('plan' | 'trainer' | 'cheatsheet')[] =
      choice === 'all'
        ? allTrainers
          ? ['plan', 'trainer', 'cheatsheet']
          : ['plan', 'cheatsheet']
        : [choice]
    return {
      choice,
      options,
      docKinds: includeInviteDocs ? picks : [],
    }
  }

  function clearInviteExtras(): void {
    setInviteIntro('')
    setInviteAttachFiles([])
    setIncludeInviteDocs(false)
    setInviteDocChoice('all')
  }

  function deliverInvites(targets: readonly ScenarioPerson[]): void {
    if (!instanceId) return
    // Declined only when notice is reinvite (Invite again); other notices skip them.
    const deliverable = targets.filter((p) => {
      if (p.isDeleted) return false
      if (p.status === 'declined') return inviteNotice(p) === 'reinvite'
      return true
    })
    if (!deliverable.length) return
    const actor = shortPersonName(user.name ?? '') ?? user.email
    const id = instanceId
    const intro = inviteIntro.trim() || null
    const filesToUpload = showInviteAttachDocs ? [...inviteAttachFiles] : []
    const includeGenerated = showInviteAttachDocs && includeInviteDocs
    // Resolve kinds before clearing extras — clearInviteExtras resets includeInviteDocs.
    const docKinds = includeGenerated ? inviteDocsForTargets(deliverable).docKinds : []
    clearInviteExtras()

    void (async () => {
      let uploaded: InviteMaterialUpload[] = []
      if (filesToUpload.length) {
        try {
          for (const file of filesToUpload) {
            uploaded.push(await uploadInviteMaterial(id, file))
          }
        } catch (err) {
          setServerNotice(studioActionMessage(err, 'Could not upload invite files.'))
          return
        }
      }
      const attaches = uploaded.map((f) => ({
        fileName: f.fileName,
        url: f.url,
      }))
      for (const person of deliverable) {
        const notice = personHasStudioAccount(person) ? inviteNotice(person) : 'register'
        const mailKey = person.email.trim().toLowerCase()
        if (notice === 'register') {
          if (registrationMailLock.current.has(mailKey)) continue
          registrationMailLock.current.add(mailKey)
        }
        try {
          await hubSendInvite(
            id,
            person.email,
            notice,
            actor,
            notice === 'register' ? null : intro,
            notice === 'register' ? [] : docKinds,
            notice === 'register' ? [] : attaches,
          )
          if (notice === 'register') {
            registrationMailLock.current.delete(mailKey)
            await markRegistrationMailSent(person.id)
          }
        } catch (err) {
          if (notice === 'register') registrationMailLock.current.delete(mailKey)
          const raw = err instanceof Error ? err.message : ''
          if (/already sent/i.test(raw)) {
            setPeopleInviteHint('Already sent.')
          } else {
            setServerNotice(studioActionMessage(err, 'Could not send the invite email.'))
          }
        }
      }
    })()
  }

  function inviteExtras(docsMeta: ReturnType<typeof inviteDocsForTargets> | null) {
    const docsOn = showInviteAttachDocs && (includeInviteDocs || inviteAttachFiles.length > 0)
    return (
      <div className="sc-invite-extras">
        {showInviteAttachDocs ? (
          <>
            <div className="sc-invite-docs">
              <label>
                <input
                  type="checkbox"
                  checked={docsOn}
                  onChange={(e) => {
                    setIncludeInviteDocs(e.target.checked)
                    if (!e.target.checked) setInviteAttachFiles([])
                  }}
                />
                Include docs
              </label>
              {/* Always mount the select so toggling does not reflow Personal note. */}
              {docsMeta ? (
                <select
                  className="sc-invite-docs-pick"
                  value={docsMeta.choice}
                  disabled={!docsOn}
                  aria-hidden={!docsOn}
                  tabIndex={docsOn ? 0 : -1}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === 'files') {
                      inviteAttachInputRef.current?.click()
                      return
                    }
                    setIncludeInviteDocs(true)
                    setInviteDocChoice(value as 'plan' | 'trainer' | 'cheatsheet' | 'all')
                  }}
                  aria-label="Which docs to include"
                >
                  {docsMeta.options.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                  <option disabled value="">
                    ────────
                  </option>
                  <option value="files">Attach files…</option>
                </select>
              ) : null}
              <input
                ref={inviteAttachInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const next = Array.from(e.target.files ?? [])
                  if (next.length) {
                    setIncludeInviteDocs(true)
                    setInviteAttachFiles((cur) => [...cur, ...next].slice(0, 8))
                  }
                  e.target.value = ''
                }}
              />
            </div>
            {inviteAttachFiles.length ? (
              <ul className="sc-invite-attach-list">
                {inviteAttachFiles.map((file, i) => (
                  <li key={`${file.name}-${file.size}-${i}`}>
                    <span title={file.name}>{file.name}</span>
                    <button
                      type="button"
                      className="sc-link-btn"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setInviteAttachFiles((cur) => cur.filter((_, j) => j !== i))}
                    >
                      <CloseIcon />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
        <div className="sc-field">
          <label className="sc-flbl" htmlFor="sc-invite-intro">
            Personal note
          </label>
          <textarea
            id="sc-invite-intro"
            className="sc-fin sc-invite-intro"
            rows={2}
            maxLength={500}
            placeholder="Optional - appears under the join button"
            value={inviteIntro}
            onChange={(e) => setInviteIntro(e.target.value)}
          />
        </div>
      </div>
    )
  }

  function sendInvites(): void {
    if (!canSendInvites) return
    const targets = selectedPeople.length ? selectedPeople : []
    if (!targets.length) {
      setPeopleInviteHint('Select roster members, then send invites.')
      return
    }
    const sentIds = targets.filter((p) => !p.isDeleted).map((p) => p.id)
    const kinds = new Set(targets.map((p) => inviteNotice(p)))
    setRosterPendingInviteIds((prev) => {
      if (!prev.size) return prev
      const next = new Set(prev)
      for (const id of sentIds) next.delete(id)
      return next
    })
    setSelectedPeopleIds([])
    setPeopleSelectMode(false)
    peopleSelectAnchorIdRef.current = null
    if (sentIds.length) {
      flashPeopleInvite(
        kinds.size === 1 && kinds.has('nudge')
          ? 'Reminded'
          : kinds.size === 1 && kinds.has('resend')
            ? 'Nudged'
            : 'Sent'
      )
    }
    // Flags on the server first — Invite notice must not clobber with a pre-flag snapshot.
    void markPeopleInvited(sentIds).then(() => deliverInvites(targets))
  }

  function invitePersonNow(id: string): void {
    if (!canSendInvites) return
    const p = activePeople.find((x) => x.id === id)
    if (!p || p.isDeleted) return
    const notice = inviteNotice(p)
    setRosterPendingInviteIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    setSelectedPeopleIds([])
    setPeopleSelectMode(false)
    peopleSelectAnchorIdRef.current = null
    flashPeopleInvite(notice === 'nudge' ? 'Reminded' : notice === 'resend' ? 'Nudged' : 'Sent')
    void markPeopleInvited([id]).then(() => deliverInvites([p]))
  }

  function setPersonRosterStatus(id: string, status: PersonStatus): void {
    if (!canManagePeople) return
    commitPeople((prev) =>
      prev.map((p) => {
        if (p.id !== id || p.isDeleted) return p
        const next: ScenarioPerson = { ...p, status }
        if (status === 'joined') next.hasBeenInvited = true
        return next
      })
    )
  }

  function ceilingForEmail(email: string): readonly string[] | null {
    const key = email.trim().toLowerCase()
    if (key && key === user.email.trim().toLowerCase()) return [...accessPerms]
    return studioPermsByEmail.get(key) ?? null
  }

  function togglePersonRole(id: string, nextRole: PersonRole): void {
    if (!canManagePeople) return
    // Participant is the base seat — always on; Editor/Trainer stack on top (ABP ceiling).
    if (nextRole === 'participant') return
    // Owner checkbox: session owners, ScenarioAdmin, or ViewAll may grant / revoke.
    if (nextRole === 'owner' && !canAssignOwner) return
    const person = people.find((row) => row.id === id)
    if (
      person &&
      !personHasRole(person, nextRole) &&
      !rosterRoleCanBeOffered(nextRole, ceilingForEmail(person.email), false)
    ) {
      return
    }
    if (
      nextRole === 'owner' &&
      person &&
      personHasRole(person, 'owner') &&
      activeOwners(people).length <= 1
    ) {
      setPeopleInviteHint('Keep at least one owner so the board is not orphaned.')
      return
    }
    let nextPeople: ScenarioPerson[] | null = null
    commitPeople((prev) => {
      nextPeople = prev.map((p) => {
        if (p.id !== id || p.isDeleted) return p
        const has = p.roles.includes(nextRole)
        let roles = has ? p.roles.filter((r) => r !== nextRole) : [...p.roles, nextRole]
        if (!roles.includes('participant')) roles = [...roles, 'participant']
        return { ...p, roles }
      })
      return nextPeople
    })
    if (nextRole === 'owner' && nextPeople) syncPrimaryOwnerUserId(nextPeople)
  }

  function setPeopleSelection(ids: string[]): void {
    setPeopleInviteHint(null)
    setSelectedPeopleIds(ids)
  }

  function selectAllPeople(): void {
    if (!canSelectPeople) return
    setPeopleInviteHint(null)
    setPeopleSelectMode(true)
    setSelectedPeopleIds(activePeople.map((p) => p.id))
    peopleSelectAnchorIdRef.current = activePeople[0]?.id ?? null
  }

  function togglePeopleSelectMode(): void {
    if (!canSelectPeople) return
    setPeopleSelectMode((on) => {
      // Done: leave multi-select UI but keep the selection for Invites / detail.
      // Select: keep any card already focused so you can add more.
      return !on
    })
  }

  function onPersonCardActivate(id: string, ev?: ListSelectMods): void {
    setPeopleInviteHint(null)
    // Multi-select modifiers only when invite/people management is allowed.
    if (canSelectPeople && ev && isAdditiveToggleMod(ev)) {
      setPeopleSelectMode(true)
      peopleSelectAnchorIdRef.current = id
      setSelectedPeopleIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      )
      return
    }
    if (canSelectPeople && ev?.shiftKey) {
      setPeopleSelectMode(true)
      const ids = activePeople.map((p) => p.id)
      const anchor = peopleSelectAnchorIdRef.current ?? id
      const range = selectIdRange(ids, anchor, id)
      setSelectedPeopleIds(range)
      return
    }
    peopleSelectAnchorIdRef.current = id
    if (canSelectPeople && peopleSelectMode) {
      setSelectedPeopleIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      )
      return
    }
    setSelectedPeopleIds((prev) => (prev.length === 1 && prev[0] === id ? [] : [id]))
  }

  function onNearbyRowToggle(key: string, ev: ListSelectMods): void {
    const keys = sortedNearbyCandidates.map((c) => assetKey(c))
    if (isAdditiveToggleMod(ev)) {
      nearbySelectAnchorKeyRef.current = key
      setNearbyPicks((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
      return
    }
    if (ev.shiftKey) {
      const anchor = nearbySelectAnchorKeyRef.current ?? key
      setNearbyPicks(new Set(selectIdRange(keys, anchor, key)))
      return
    }
    nearbySelectAnchorKeyRef.current = key
    setNearbyPicks((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  /** Assign one roleplay position to a person, clearing any previous position they held. */
  function assignPersonRolePlaySeat(personId: string, slotId: string | null): void {
    if (!canManagePeople) return
    commitSession((prev) => {
      if (!slotId) return clearPersonRolePlaySeats(prev, new Set([personId]))
      const slot = rolePlaySlotsFromSession(prev).find((s) => s.id === slotId)
      if (!slot?.objectId) return prev
      return withRolePlayAssignee(prev, slot.objectId, personId)
    })
  }

  /** Whiteboard: one static slice. Keep leftover frames until the author edits, then drop all but the first. */
  function applyStaticWindSlice(next: {
    fromDeg: number
    speedMph: number
    plumeDistanceM: number
    plumeSpreadDeg: number
  }): void {
    if (!windAuthoring || !canEditBoard || windPreviewActive || running) return
    const id = windAuthoring.id
    const rounded = {
      fromDeg: Math.round(next.fromDeg),
      speedMph: Math.round(next.speedMph),
      plumeDistanceM: Math.round(next.plumeDistanceM),
      plumeSpreadDeg: Math.round(next.plumeSpreadDeg),
    }
    const first = sortWindFrames(windAuthoring.windKeyframes ?? [])[0]
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== id) return o
        const row: ScenarioObject = { ...o, params: { ...o.params, ...rounded } }
        if (first) row.windKeyframes = [{ at: first.at, ...rounded }]
        else delete row.windKeyframes
        return row
      }),
    }))
  }

  function setWindFrames(frames: WindKeyframe[] | undefined): void {
    if (!windAuthoring || !canEditBoard) return
    const id = windAuthoring.id
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== id) return o
        const next = { ...o }
        if (!frames?.length) {
          delete next.windKeyframes
          return next
        }
        next.windKeyframes = sortWindFrames(frames)
        return next
      }),
    }))
  }

  function upsertWindKeyframe(frame: WindKeyframe): void {
    if (!windAuthoring || !canEditBoard) return
    if (windPersistTimerRef.current != null) {
      window.clearTimeout(windPersistTimerRef.current)
      windPersistTimerRef.current = null
    }
    const id = windAuthoring.id
    const rounded: WindKeyframe = {
      at: frame.at,
      fromDeg: Math.round(frame.fromDeg),
      speedMph: Math.round(frame.speedMph),
      plumeDistanceM: Math.round(frame.plumeDistanceM),
      plumeSpreadDeg: Math.round(frame.plumeSpreadDeg),
    }
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== id) return o
        const existing = o.windKeyframes ? [...o.windKeyframes] : []
        const idx = existing.findIndex((f) => off(f.at) === off(rounded.at))
        if (idx >= 0) existing[idx] = rounded
        else existing.push(rounded)
        return {
          ...o,
          params: {
            ...o.params,
            fromDeg: rounded.fromDeg,
            speedMph: rounded.speedMph,
            plumeDistanceM: rounded.plumeDistanceM,
            plumeSpreadDeg: rounded.plumeSpreadDeg,
          },
          windKeyframes: sortWindFrames(existing),
        }
      }),
    }))
  }

  /**
   * Dial / compass edits: live map via draft only.
   * Story clock: persist on Set / Update. Whiteboard: one static write after the gesture.
   */
  function stageWindParams(next: {
    fromDeg: number
    speedMph: number
    plumeDistanceM: number
    plumeSpreadDeg: number
  }): void {
    if (windPreviewActive || running) return
    if (!windUserEditRef.current) return
    setWindDraft(next)
    if (!canEditBoard || sessionOffersStoryClock) return
    if (windPersistTimerRef.current != null) window.clearTimeout(windPersistTimerRef.current)
    windPersistTimerRef.current = window.setTimeout(() => {
      windPersistTimerRef.current = null
      applyStaticWindSlice(next)
    }, 400)
  }

  function removeWindKeyframeAt(index: number): void {
    if (!windAuthoring || !canEditBoard) return
    const sorted = sortWindFrames(windAuthoring.windKeyframes ?? [])
    const next = sorted.filter((_, i) => i !== index)
    setWindFrames(next.length ? next : undefined)
    setWindSelectIndex(null)
    setWindHoverIndex(null)
  }

  function applyWindPreset(): void {
    if (!windAuthoring || windAuthoring.type !== 'wind' || !canEditBoard) return
    if (windFrames.length) {
      const ok = window.confirm(
        `Replace the ${windFrames.length} existing wind point${windFrames.length === 1 ? '' : 's'} with this weather preset?`
      )
      if (!ok) return
    }
    stopWindPreview()
    const clockEnd = authorClock.durationMs
    const clamped = clampWindPresetWindow(windPresetOnsetMs, windPresetSpanMs, clockEnd)
    setWindPresetOnsetMs(clamped.onsetMs)
    setWindPresetSpanMs(clamped.spanMs)
    const frames = buildWindPresetFrames(windPresetId, {
      onsetMs: clamped.onsetMs,
      spanMs: clamped.spanMs,
      originIso: viewSession.storyStartTime,
    })
    const first = frames[0]!
    const id = windAuthoring.id
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) =>
        o.id === id
          ? {
              ...o,
              windKeyframes: sortWindFrames(frames),
              params: {
                ...o.params,
                fromDeg: first.fromDeg,
                speedMph: first.speedMph,
                plumeDistanceM: first.plumeDistanceM,
                plumeSpreadDeg: first.plumeSpreadDeg,
              },
            }
          : o
      ),
    }))
    setWindAuthorMs(off(first.at))
    setElapsedMs(off(first.at))
    setRunning(false)
    setWindSelectIndex(0)
    setWindHoverIndex(null)
    setWindDraft({
      fromDeg: first.fromDeg,
      speedMph: first.speedMph,
      plumeDistanceM: first.plumeDistanceM,
      plumeSpreadDeg: first.plumeSpreadDeg,
    })
  }

  function applyTornadoPreset(): void {
    if (!selectedSource || !isLocalStorm(selectedSource.type) || !canEditBoard) return
    if (!tornadoPresetId) return
    const hurricane = selectedSource.type === 'hurricane'
    const noun = hurricane ? 'hurricane' : 'tornado'
    const origin = selectedSource.pose
    if (!origin) {
      window.alert(`Place the ${noun} on the map before applying a path preset.`)
      return
    }
    if (selectedSource.keyframes?.length || selectedSource.windKeyframes?.length) {
      const ok = window.confirm(
        `Replace this ${noun}’s path and local wind points with the selected preset?`
      )
      if (!ok) return
    }
    const built = hurricane
      ? buildClampedHurricanePreset(tornadoPresetId as HurricanePresetId, {
          origin,
          onsetMs: tornadoPresetOnsetMs,
          spanMs: tornadoPresetSpanMs,
          clockEndMs: authorClock.durationMs,
          originIso: viewSession.storyStartTime,
          headingDeg: hurricaneTrackHeadingDeg,
        })
      : buildClampedTornadoPreset(tornadoPresetId as TornadoPresetId, {
          origin,
          onsetMs: tornadoPresetOnsetMs,
          spanMs: tornadoPresetSpanMs,
          clockEndMs: authorClock.durationMs,
          originIso: viewSession.storyStartTime,
        })
    setTornadoPresetOnsetMs(built.onsetMs)
    const spanOptions = hurricane ? HURRICANE_PRESET_SPAN_OPTIONS : TORNADO_PRESET_SPAN_OPTIONS
    const appliedSpan = spanOptions.some((opt) => opt.ms === built.spanMs)
      ? built.spanMs
      : tornadoPresetSpanMs
    if (appliedSpan !== tornadoPresetSpanMs) setTornadoPresetSpanMs(appliedSpan)
    const firstPose = built.keyframes[0]!
    patchSelectedObject((o) => {
      // Category does not choose the field. A width already on the hurricane stays.
      const chosenRadius = hurricane ? Number(o.params.radiusM) : NaN
      const keepWidth = Number.isFinite(chosenRadius) && chosenRadius > 0
      const windKeyframes = keepWidth
        ? built.windKeyframes.map((frame) => ({
            ...frame,
            plumeDistanceM: Math.max(50, Math.round(chosenRadius)),
          }))
        : built.windKeyframes
      return {
        ...o,
        // Keep the drop as the icon pose; path #1 is built from this origin.
        pose: { lng: origin.lng, lat: origin.lat },
        keyframes: built.keyframes,
        windKeyframes,
        params: {
          ...o.params,
          ...built.params,
          ...(keepWidth ? { radiusM: chosenRadius, plumeDistanceM: chosenRadius } : {}),
          ...(hurricane
            ? { hurricanePresetId: tornadoPresetId, trackHeadingDeg: hurricaneTrackHeadingDeg }
            : { tornadoPresetId }),
          presetSpanMs: appliedSpan,
          presetOnsetAt: atOff(built.onsetMs),
        },
      }
    })
    setInspectPreviewMs(null)
    setWindAuthorMs(off(firstPose.at))
    setRunning(false)
    // The draft is painted over every sample. Leave it and the arrow stays on
    // the wind from before Apply, which for a hurricane is due east.
    windUserEditRef.current = false
    setWindDraft(null)
    jumpTo(off(firstPose.at))
  }

  function flashField(key: string): void {
    if (fieldFlashTimerRef.current != null) window.clearTimeout(fieldFlashTimerRef.current)
    setFieldFlashKey(key)
    fieldFlashTimerRef.current = window.setTimeout(() => {
      setFieldFlashKey(null)
      fieldFlashTimerRef.current = null
    }, 650)
  }

  function saveDescription(): boolean {
    if (!selectedId || !canMutateSelected) return false
    const prev = selectedSource?.description ?? ''
    if (descDraft === prev) return false
    commitSession((s) => ({
      ...s,
      objects: s.objects.map((o) => (o.id === selectedId ? { ...o, description: descDraft } : o)),
    }))
    flashField(`desc:${selectedId}`)
    return true
  }

  function updateSelectedLabel(label: string): void {
    if (!selectedId || !canMutateSelected) return
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) => {
        if (o.id !== selectedId) return o
        if (o.type === 'label') return { ...o, label, params: { ...o.params, text: label } }
        return { ...o, label }
      }),
    }))
  }

  function updateMapText(text: string): void {
    if (!selectedId || !canMutateSelected) return
    commitSession((prev) => ({
      ...prev,
      objects: prev.objects.map((o) =>
        o.id === selectedId ? { ...o, label: text, params: { ...o.params, text } } : o
      ),
    }))
  }

  const chatRoomLines = liveRoomChat(viewSession)
  const chatPlaybackRunId = liveTakeActive
    ? inProgressRun?.id ??
      resolveActiveChatRun(viewSession, { watchingRunId })?.id ??
      null
    : inRunReview
      ? watchingRunId ?? tapeReview?.runId ?? null
      : null
  const chatSections = chatSectionsForPane(viewSession, {
    mode: liveTakeActive ? 'liveRun' : inRunReview ? 'runReview' : 'room',
    playheadIso,
    runId: chatPlaybackRunId,
  })
  /** Story / During-this-run lane (scrubbed). Hidden only for script-less boards in idle room. */
  const chatShowStoryLane =
    chatSections.layout === 'liveRun' ||
    chatSections.layout === 'runReview' ||
    (chatSections.layout === 'room' && chatSections.hasScript)
  /** Session lane — hidden while an official take is live (composer stamps the take). */
  const chatShowSessionLane = chatSections.layout !== 'liveRun'
  const chatSplit = chatShowStoryLane && chatShowSessionLane
  const chatStoryScrollRef = useRef<HTMLDivElement | null>(null)
  const chatSessionScrollRef = useRef<HTMLDivElement | null>(null)
  const chatReaderId = user.userId?.trim() || user.email?.trim() || ''
  const chatSelfIds = [
    meOnRoster ? personUserId(meOnRoster) : '',
    user.userId?.trim() || '',
    user.email?.trim() || '',
  ]
  // Done: no live unread badge (review/playback only). Live room keeps the spine badge.
  const chatUnreadCount = selectedIsCompleted
    ? 0
    : countChatUnread(instanceId, chatRoomLines, {
        readerId: chatReaderId,
        ignoreAuthorIds: chatSelfIds,
      })
  const canComposeChat =
    canComment &&
    !studioLinkDown &&
    !previewTemplateId &&
    currentInstance?.lifecycle !== 'completed'
  void chatReadEpoch

  const onLeaveInspector = useCallback(() => {
    setNearbyCandidates((prev) => (prev.length === 0 ? prev : []))
    setNearbyPicks((prev) => (prev.size === 0 ? prev : new Set()))
    setNearbyHoverKey((prev) => (prev == null ? prev : null))
    setLinkedHoverKey((prev) => (prev == null ? prev : null))
    setAssetDebugKey((prev) => (prev == null ? prev : null))
    setNearbyHint((prev) => (prev == null ? prev : null))
  }, [])

  const onLeaveTimeline = useCallback(() => {
    setHoverIds((prev) => (prev.length === 0 ? prev : []))
    setHoverElapsedMs((prev) => (prev == null ? prev : null))
  }, [])

  const onEnterTimeline = useCallback(() => {
    const row = currentRowRef.current
    const scroller = timelineScrollRef.current
    if (!row || !scroller) return
    const rowRect = row.getBoundingClientRect()
    const box = scroller.getBoundingClientRect()
    const padTop = 10
    const padBottom = Math.max(48, Math.round(rowRect.height * 0.9))
    if (rowRect.bottom > box.bottom - padBottom) {
      scroller.scrollTop += rowRect.bottom - box.bottom + padBottom
    } else if (rowRect.top < box.top + padTop) {
      scroller.scrollTop -= box.top - rowRect.top + padTop
    }
  }, [])

  const onInspectOpenChange = useCallback((open: boolean) => {
    setInspectDockOpen((prev) => (prev === open ? prev : open))
  }, [])

  const onHubChooser = useCallback(() => {
    setMapAssetPick(null)
    setMapAssetDetailsOpen(false)
  }, [])

  const onSeenInvites = useCallback((ids: string[]) => {
    setSeenInviteIds((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  const onChatOpened = useCallback(() => {
    const id = instanceIdRef.current
    if (!id) return
    if (markChatRead(id, chatRoomLines, chatReaderId)) setChatReadEpoch((n) => n + 1)
  }, [chatRoomLines, chatReaderId])

  function chatLineStamp(c: ScenarioComment, stamp: 'story' | 'wall'): string {
    if (stamp === 'wall') {
      return c.createdAtWallIso
        ? formatWall(c.createdAtWallIso, { seconds: true })
        : formatTimelineStamp(
            c.createdAt,
            viewSession.timezone,
            (viewSession.timelineTimeFormat ?? 'hms') === 'hms',
          )
    }
    return formatTimelineStamp(
      c.createdAt,
      viewSession.timezone,
      (viewSession.timelineTimeFormat ?? 'hms') === 'hms',
    )
  }

  function chatLineAuthorKey(c: ScenarioComment): string {
    return (
      (c.authorId || '').trim() ||
      (c.authorObjectId || '').trim() ||
      (c.authorName || '').trim() ||
      c.id
    )
  }

  /** Messages-esque: hide repeated stamps in a burst; tighten same-author clusters. */
  function renderChatThread(
    lines: readonly ScenarioComment[],
    stamp: 'story' | 'wall',
  ): ReactNode {
    return lines.map((c, i) => {
      const action = commentIsAction(c)
      const whoOpts = {
        session: viewSession,
        omitContactDetails: viewSession.omitContactDetailsFromExports,
        voice: (stamp === 'story' ? 'exercise' : 'session') as 'exercise' | 'session',
      }
      const whoParts = commentWhoParts(c, people, whoOpts)
      const who = whoParts.hint
        ? `${whoParts.primary} (${whoParts.hint})`
        : whoParts.primary
      const when = chatLineStamp(c, stamp)
      const prev = i > 0 ? lines[i - 1]! : null
      const sameStamp = Boolean(prev && chatLineStamp(prev, stamp) === when)
      const sameAuthor = Boolean(prev && chatLineAuthorKey(prev) === chatLineAuthorKey(c))
      const continued = sameStamp && sameAuthor
      const showWhen = !sameStamp
      const showWho = !continued
      return (
        <div
          key={c.id}
          className={`sc-comment${action ? ' action' : ''}${
            c.source === 'script' ? ' is-script' : ''
          }${continued ? ' is-continued' : ''}`}
        >
          {showWho || showWhen ? (
            <div className="sc-comment-meta">
              {showWho ? (
                <span className="who" title={who}>
                  {whoParts.primary}
                  {whoParts.hint ? (
                    <span className="who-hint"> ({whoParts.hint})</span>
                  ) : null}
                </span>
              ) : (
                <span className="who" aria-hidden="true" />
              )}
              {showWhen ? <span className="when">{when}</span> : null}
            </div>
          ) : null}
          <div className="body">{commentThreadBody(c.body, who, action)}</div>
        </div>
      )
    })
  }

  function studioLinkDownBlocksAction(target: EventTarget | null): boolean {
    if (!showBoardChrome || !studioLinkDown) return false
    const el =
      target instanceof Element
        ? target
        : target instanceof Node
          ? target.parentElement
          : null
    if (!el) return true
    if (
      el.closest(
        '.sc-version-switch-veil-retry, .sc-offline-retry, .sc-offline, .sc-pane-mode, .sc-rtabs, .sc-back-buxus, .sc-top-logo-link, .sc-title-home'
      )
    ) {
      return false
    }
    return true
  }

  return (
    <StudioDockProvider dockRef={dockRef} setDockRef={setDockRef}>
    <StudioDockEffects
      showComments={showComments}
      viewingTemplate={viewingTemplate}
      instanceId={instanceId ?? null}
      libraryMode={libraryMode}
      hubNothingSelected={hubNothingSelected}
      sessionOffersStoryClock={sessionOffersStoryClock}
      showTimelineAuthoring={showTimelineAuthoring}
      templateInspectOffered={templateInspectOffered}
      invitedIdsKey={invitedIdsKey}
      selectedIsCompleted={selectedIsCompleted}
      chatRoomLinesLength={chatRoomLines.length}
      chatReaderId={chatReaderId}
      timelineScrollId={timelineScrollId ?? ''}
      onLeaveInspector={onLeaveInspector}
      onLeaveTimeline={onLeaveTimeline}
      onEnterTimeline={onEnterTimeline}
      onInspectOpenChange={onInspectOpenChange}
      onHubChooser={onHubChooser}
      onSeenInvites={onSeenInvites}
      onChatOpened={onChatOpened}
    />
    <div
      className={`sc-app${hubNothingSelected ? ' is-hub-idle' : ''}${
        showBoardChrome && studioLinkDown ? ' is-link-down' : ''
      } ${focusModeClassNames({
        focusMode,
        typeId: viewSession.sessionType,
        accountBoardMode,
      })}`}
      data-ui-scale={uiScale === 'md' ? undefined : uiScale}
      data-sc-theme={studioTheme}
      data-tl-time={viewSession.timelineTimeFormat ?? 'hms'}
      onPointerDownCapture={(e) => {
        if (!studioLinkDownBlocksAction(e.target)) return
        e.preventDefault()
        e.stopPropagation()
      }}
      onClickCapture={(e) => {
        if (!studioLinkDownBlocksAction(e.target)) return
        e.preventDefault()
        e.stopPropagation()
      }}
      onKeyDownCapture={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        if (!studioLinkDownBlocksAction(e.target)) return
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <header className="sc-topbar">
        <div className="sc-top-start">
          <Link
            className="sc-back-buxus"
            to={HOME_PATH}
            title="Back to training"
            aria-label="Back to training"
          >
            <span aria-hidden="true">←</span>
          </Link>
          <Link className="sc-top-logo-link" to={HOME_PATH} title="Buxus" aria-label="Buxus">
            <img className="sc-top-logo" src={buxusLogo} alt="" />
          </Link>
          <span className="sc-title">
            <Link
              className="sc-title-home"
              to={SCENARIOS_PATH}
              title="Sessions"
              aria-label="Scenario Studio — Sessions"
              onClick={(e) => {
                // Plain /scenarios resumes an owned board; park so the Sessions hub stays open.
                e.preventDefault()
                if (!instanceId && !libraryMode && !previewTemplateId) return
                parkSessionsHub()
              }}
            >
              {SCENARIO_TOOL_SHORT_NAME}
            </Link>
            <span className="sc-ver" title={studioVersionTitle()}>
              {`v${STUDIO_REVISION}`}
              {studioEnvLabel() ? (
                <span className="sc-ver-env">{` · ${studioEnvLabel()}`}</span>
              ) : null}
            </span>
          </span>
        </div>
        <div className="sc-top-map">
          <div className="sc-top-map-mid">
            {showBoardChrome && !hubTitlePlaceholder ? (
              <div className="sc-channel-title">
                <h1 title={selectionTitle}>{selectionTitle}</h1>
                {studioSign ? (
                  <span
                    className={`sc-studio-sign is-${studioSign.kind}`}
                    title={studioSign.title}
                    aria-label={studioSign.title}
                  >
                    <span className="sc-studio-sign-face">{studioSign.label}</span>
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="sc-top-map-end">
            {showViewingCount ? (
              <div className="sc-top-room-meta">
                <DockViewingCountButton
                  viewingCount={viewingCount}
                  peopleViewReturnDockRef={peopleViewReturnDockRef}
                />
              </div>
            ) : null}
            {videoExportStatus === 'saving' ? (
              <span className="sc-top-saving">Saving…</span>
            ) : videoExportStatus === 'recording' ? (
              <button
                type="button"
                className="sc-top-rec-stop"
                onClick={() => void stopVideoCapture()}
                title="Stop screen capture and save"
              >
                <span className="sc-top-rec-sq" aria-hidden="true" />
                Recording
              </button>
            ) : null}
          </div>
        </div>
        <div className="sc-top-rail">
          {showBoardTools && inLiveRun && mayOperateSession && !complete ? (
            <button
              type="button"
              className="sc-top-window-link is-end-session"
              title={hubActionsLocked ? hubLockTitle : windowLabels.endSessionTitle}
              disabled={hubActionsLocked}
              onClick={() => setStopConfirmOpen(true)}
            >
              {windowLabels.endSession}
            </button>
          ) : null}
          {showBoardTools && canOpenShare ? (
            <button
              type="button"
              className="sc-top-window-link"
              title={windowLabels.shareTitle}
              onClick={() => {
                setSharePanelMode('share')
                setSharePanelOpen(true)
              }}
            >
              {windowLabels.share}
            </button>
          ) : null}
          <span className="sc-top-rail-divider" aria-hidden="true" />
          <button
            type="button"
            className={`sc-top-help-btn${helpOpen ? ' on' : ''}`}
            title="Help"
            aria-label="Help"
            aria-expanded={helpOpen}
            aria-haspopup="dialog"
            onClick={() => {
              if (mapAssetDetailsOpenRef.current) {
                setMapAssetDetailsOpen(false)
                return
              }
              setHelpOpen((open) => {
                const next = !open
                if (next) {
                  setKeysOpen(false)
                  setDock((cur) => (cur === 'account' ? 'scenarios' : cur))
                }
                return next
              })
            }}
          >
            <NavMdiIcon path={mdiHelpCircleOutline} sizePx={18} />
          </button>
          <DockAccountButton
            initials={myPresenceInitials}
            tone={myPresenceTone}
            onBeforeToggle={() => {
              if (mapAssetDetailsOpenRef.current) {
                setMapAssetDetailsOpen(false)
                return false
              }
              setHelpOpen(false)
              return true
            }}
          />
        </div>
      </header>

      <div className="sc-body">
        {showBoardChrome ? (
          <aside
            className={`sc-toolbox${showPalette ? '' : ' is-collapsed'}${
              showPlacePaletteUi ? '' : ' is-legend'
            }${paletteMotionOn ? ' is-animated' : ''}`}
            aria-label={showPlacePaletteUi ? 'Object palette' : 'Map symbols'}
            aria-hidden={!showPalette}
            inert={studioLinkDown || undefined}
            onTransitionEnd={(e) => {
              if (e.target !== e.currentTarget) return
              if (e.propertyName !== 'width') return
              mapApiRef.current?.resize()
            }}
          >
            <div className="sc-toolbox-inner">
              <div className="sc-legend-hd">{showPlacePaletteUi ? 'Palette' : 'Map symbols'}</div>
              <div className="sc-tscroll">
                {showPlacePaletteUi ? (
                  <div
                    className={`sc-psec${paletteSectionIsOpen('drawing') ? ' is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="sc-cat"
                      style={{ '--sc-cat': '#A89BE8' } as CSSProperties}
                      aria-expanded={paletteSectionIsOpen('drawing')}
                      aria-controls="sc-psec-drawing"
                      id="sc-psec-drawing-hd"
                      onClick={() => togglePaletteSection('drawing')}
                    >
                      <span className="sc-cat-dot" style={{ background: '#A89BE8' }} />
                      Drawing Tools
                    </button>
                    <div
                      id="sc-psec-drawing"
                      className="sc-psec-body"
                      role="region"
                      aria-labelledby="sc-psec-drawing-hd"
                    >
                      <div className="sc-psec-body-inner">
                        {DRAW_TOOLS.filter((t) => !t.hidden).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className={`sc-dt${placeType === t.id ? ' act' : ''}`}
                            title={
                              hubNothingSelected
                                ? 'Open a session to place tools'
                                : paletteHint(t.id) || undefined
                            }
                            disabled={
                              !paletteToolsEnabled || (boardEditGrants && !t.enabled)
                            }
                            onClick={() => {
                              if (!paletteToolsEnabled) return
                              if (!boardEditGrants || !t.enabled) return
                              if (
                                t.id === 'type' ||
                                t.id === 'freehand' ||
                                t.id === 'pen' ||
                                t.id === 'ruler'
                              ) {
                                setPlaceType((cur) => {
                                  const next = cur === t.id ? null : t.id
                                  if (next !== 'ruler') setRulerStats(null)
                                  return next
                                })
                                setSelectedId(null)
                                setDock('inspector')
                              }
                            }}
                          >
                            <ToolIcon typeId={t.id === 'type' ? 'label' : t.id} color="#A89BE8" />
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
                {PALETTE.map((cat) => {
                  const items = cat.items.filter(
                    (item) =>
                      !item.hidden &&
                      ((item.id !== 'tornado' && item.id !== 'hurricane') || showTornado) &&
                      (item.easterEgg
                        ? canViewEggs
                        : paletteItemVisible(viewSession.boardComplexity, item.id))
                  )
                  if (!items.length) return null
                  const open = paletteSectionIsOpen(cat.id)
                  const bodyId = `sc-psec-${cat.id}`
                  const hdId = `${bodyId}-hd`
                  return (
                    <div key={cat.id} className={`sc-psec${open ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="sc-cat"
                        style={{ '--sc-cat': cat.color } as CSSProperties}
                        aria-expanded={open}
                        aria-controls={bodyId}
                        id={hdId}
                        onClick={() => togglePaletteSection(cat.id)}
                      >
                        <span className="sc-cat-dot" style={{ background: cat.color }} />
                        {cat.label}
                      </button>
                      <div
                        id={bodyId}
                        className="sc-psec-body"
                        role="region"
                        aria-labelledby={hdId}
                      >
                        <div className="sc-psec-body-inner">
                          {items.map((item) =>
                            showPlacePaletteUi ? (
                              <button
                                key={item.id}
                                type="button"
                                className={`sc-ti${placeType === item.id ? ' act' : ''}`}
                                title={
                                  hubNothingSelected
                                    ? 'Open a session to place tools'
                                    : paletteHint(item.id) || undefined
                                }
                                disabled={!paletteToolsEnabled || !item.enabled}
                                onClick={() => {
                                  if (!paletteToolsEnabled || !item.enabled) return
                                  setPlaceType((cur) => (cur === item.id ? null : item.id))
                                }}
                              >
                                <ToolIcon typeId={item.id} color={cat.color} />
                                {item.label}
                              </button>
                            ) : (
                              <div
                                key={item.id}
                                className={`sc-ti legend${item.enabled ? '' : ' dim'}`}
                              >
                                <ToolIcon typeId={item.id} color={cat.color} plain />
                                {item.label}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </aside>
        ) : null}

        <div className="sc-main">
          <div
            className={`sc-strip${
              !showBoardTools ? ' is-hub' : !showStoryStrip && showBoardChrome ? ' is-sparse' : ''
            }${studioLinkDown ? ' is-link-down' : ''}${
              versionSwitching ? ' is-switching' : ''
            }`}
          >
            <div className="sc-strip-start">
            {showStoryStrip && showBoardTools ? (
              <div
                className={`sc-strip-clock${
                  // Breath while the shared clock advances — Preview and Live take alike.
                  running && !instantReplayEngaged ? ' is-run' : ''
                }${clockEnded && !running ? ' is-ended' : ''}${
                  !instantReplayEngaged && !running && !clockEnded && elapsedMs > 0
                    ? ' is-paused'
                    : ''
                }${
                  !running && !instantReplayEngaged && !clockEnded && elapsedMs === 0
                    ? ' is-idle'
                    : ''
                }${instantReplayEngaged ? ' is-replay' : ''}`}
                title={
                  instantReplayEngaged
                    ? 'Mission clock frozen at pause'
                    : running && !liveTakeActive
                      ? 'Story time'
                      : 'Scenario time'
                }
              >
                <span className="sc-strip-clock-face">
                  {formatRunningClock(missionPlayheadIso, authorClock)}
                </span>
              </div>
            ) : null}
            {/*
              Planned / Done story transport: bottom time bar + Space drive Preview.
              Hide the strip Play button when the Version pick is up (Exercise) so it
              does not collide with the center chrome; label Preview when shown.
            */}
            {showStoryStrip &&
            (isClockPreview || selectedIsCompleted) &&
            !inLiveRun &&
            !viewingTemplate &&
            !showExerciseScoreboard &&
            showBoardTools &&
            showTransport ? (
              <button
                ref={stripPlayBtnRef}
                type="button"
                title={
                  selectedIsPlanned && !canDrivePreview
                    ? transportLabels.hostControlledTip
                    : running
                      ? 'Pause'
                      : 'Preview'
                }
                className={`sc-btn preview sc-strip-transport${running ? ' live is-playing' : ''}`}
                onClick={toggleClock}
                disabled={selectedIsPlanned && !canDrivePreview}
              >
                {running ? transportLabels.pause : transportLabels.preview}
              </button>
            ) : null}
            {inLiveRun && !complete && showBoardTools && (showStoryStrip ? showTransport : true) ? (
              <>
                {showStoryStrip && showTransport ? (
                  <>
                    {/*
                      Deck owns Pause/Resume/Stop while a take is live.
                      Start Run lives in the friendly status modal (+ countdown).
                      Before Run 1: preview play/pause is bottom bar / Space only.
                      Instant Replay tape Play lives on the bottom time bar (Space while in IR).
                    */}
                    {canOperateSession &&
                    !(typeOffersSharedRun(viewSession.sessionType) && exerciseRunStopped) &&
                    !(
                      typeOffersSharedRun(viewSession.sessionType) &&
                      takeNumber === 0 &&
                      !exerciseRunStopped
                    ) &&
                    !instantReplayEngaged ? (
                      <button
                        ref={stripPlayBtnRef}
                        type="button"
                        title={
                          running
                            ? typeOffersSharedRun(viewSession.sessionType)
                              ? 'Pause run'
                              : 'Pause'
                            : typeOffersSharedRun(viewSession.sessionType)
                              ? 'Resume run'
                              : 'Resume'
                        }
                        className={
                          running
                            ? 'sc-btn sc-strip-transport live is-playing'
                            : 'sc-btn sc-strip-transport is-resume-hint'
                        }
                        onClick={() => {
                          const id = instanceIdRef.current
                          const nextRunning = !running
                          // Official take stuck paused at story end (misclassified finish):
                          // finish the take — never Seek(start)+Resume (that restarted the run).
                          if (
                            nextRunning &&
                            clockEnded &&
                            takeNumber > 0 &&
                            !exerciseRunStopped &&
                            inLiveRun &&
                            typeOffersSharedRun(viewSession.sessionType)
                          ) {
                            finishOfficialTakeAtStoryEnd(
                              displayDur > 0 ? displayDur : elapsedRef.current
                            )
                            return
                          }
                          const prevClock = clocksRef.current.get(id)
                          const stripRate = Math.max(PLAYBACK_RATE_MIN, playbackRate)
                          const replayPauseMs = instantReplayPauseMsRef.current
                          // Resume run: jump back to the pause point when scrubbed away in IR earlier.
                          const resumeMs =
                            nextRunning &&
                            replayPauseMs != null &&
                            !clockEnded &&
                            Math.abs(elapsedRef.current - replayPauseMs) > 80
                              ? replayPauseMs
                              : null
                          const resumePhaseWrite =
                            nextRunning && typeOffersSharedRun(viewSession.sessionType)
                              ? persistExerciseTakePhase(Math.max(1, takeNumber), false)
                              : Promise.resolve()
                          if (nextRunning) {
                            if (pauseHoldIdRef.current === id) pauseHoldIdRef.current = null
                            if (transportPauseHoldIdRef.current === id)
                              transportPauseHoldIdRef.current = null
                            transportPlayHoldIdRef.current = id
                            if (typeOffersSharedRun(viewSession.sessionType)) {
                              setTimeBarAutoHidden(false)
                              setTimeBarPinned(false)
                              setTimeBarEnterKey((n) => n + 1)
                            }
                            clearInstantReplayPause()
                          } else {
                            if (transportPlayHoldIdRef.current === id)
                              transportPlayHoldIdRef.current = null
                            transportPauseHoldIdRef.current = id
                            markInstantReplayPause()
                          }
                          if (prevClock) {
                            const start = snapshotRef.current.session.storyStartTime
                            const playhead =
                              clockEnded && nextRunning
                                ? start
                                : resumeMs != null
                                  ? addStoryMs(start, resumeMs)
                                  : addStoryMs(start, elapsedRef.current)
                            if (clockEnded && nextRunning) {
                              setElapsedMs(0)
                              elapsedRef.current = 0
                            } else if (resumeMs != null) {
                              setElapsedMs(resumeMs)
                              elapsedRef.current = resumeMs
                            }
                            const nextClock: StudioClock = nextRunning
                              ? {
                                  ...prevClock,
                                  lifecycle: 'running',
                                  clockStatus: 'running',
                                  rate: stripRate,
                                  anchorStoryTimeIso: playhead,
                                  anchorActualTime: new Date().toISOString(),
                                  storyOriginIso:
                                    prevClock.storyOriginIso || prevClock.anchorStoryTimeIso,
                                }
                              : {
                                  ...prevClock,
                                  clockStatus: 'paused',
                                  rate: stripRate,
                                  anchorStoryTimeIso: playhead,
                                  anchorActualTime: null,
                                  storyOriginIso:
                                    prevClock.storyOriginIso || prevClock.anchorStoryTimeIso,
                                }
                            clocksRef.current.set(id, nextClock)
                            serverClockRef.current = nextClock
                            setServerClock(nextClock)
                          }
                          setRunning(nextRunning)
                          if (!nextRunning) setHoverElapsedMs(null)
                          setInstances((prev) =>
                            prev.map((i) =>
                              i.id === id
                                ? {
                                    ...i,
                                    clockStatus: nextRunning ? 'running' : 'paused',
                                    studioClock: clocksRef.current.get(id) ?? i.studioClock,
                                    playheadStoryIso: clocksRef.current.get(id)
                                      ? boardPlayheadIso(
                                          clocksRef.current.get(id) as StudioClock,
                                          i.session.storyStartTime
                                        )
                                      : i.playheadStoryIso,
                                  }
                                : i
                            )
                          )
                          const rateMatches =
                            prevClock != null && Math.abs((prevClock.rate || 1) - stripRate) < 0.02
                          const afterRate = async (): Promise<void> => {
                            if (!rateMatches) await hubSetRate(id, stripRate)
                          }
                          if (nextRunning) {
                            localPreviewDriveUntilRef.current = performance.now() + 1200
                          }
                          // Holds clear when the matching Clock lands — not when the RPC returns.
                          const call = nextRunning
                            ? resumePhaseWrite.then(async () => {
                                const startIso = snapshotRef.current.session.storyStartTime
                                if (clockEnded) {
                                  return hubResumeAt(
                                    id,
                                    startIso,
                                    rateMatches ? null : stripRate,
                                  )
                                }
                                if (resumeMs != null) {
                                  return hubResumeAt(
                                    id,
                                    addStoryMs(startIso, resumeMs),
                                    rateMatches ? null : stripRate,
                                  )
                                }
                                // Plain pause/resume at the same head — no Seek (avoids stutter).
                                await afterRate()
                                return hubResume(id)
                              })
                            : (() => {
                                const startIso = snapshotRef.current.session.storyStartTime
                                const freezeIso = addStoryMs(startIso, elapsedRef.current)
                                return hubPause(id, freezeIso)
                              })()
                          void call.catch((err) => {
                            if (transportPlayHoldIdRef.current === id) {
                              transportPlayHoldIdRef.current = null
                            }
                            if (transportPauseHoldIdRef.current === id) {
                              transportPauseHoldIdRef.current = null
                            }
                            setRunning(!nextRunning)
                            setInstances((prev) =>
                              prev.map((i) =>
                                i.id === id
                                  ? {
                                      ...i,
                                      clockStatus: nextRunning ? 'paused' : 'running',
                                    }
                                  : i
                              )
                            )
                            setServerNotice(studioActionMessage(err, 'Could not update the clock.'))
                          })
                        }}
                      >
                        {running ? transportLabels.pause : transportLabels.resume}
                      </button>
                    ) : null}
                    {instantReplayEngaged && canOperateSession ? (
                      <button
                        type="button"
                        title="Exit Instant Replay and resume the run from the pause point"
                        className="sc-btn sc-strip-transport is-resume-hint"
                        onClick={() => resumeLiveRunFromPause()}
                      >
                        {transportLabels.resume}
                      </button>
                    ) : null}
                    {typeOffersSharedRun(viewSession.sessionType) ? (
                      canOperateSession &&
                      !storyPreviewing &&
                      !exerciseRunStopped &&
                      takeNumber > 0 &&
                      (running ||
                        instantReplayEngaged ||
                        liveTakePausedForReplay ||
                        liveTakeActive) ? (
                        <button
                          type="button"
                          title="Stop this timed run (session stays Live)"
                          className="sc-btn sc-strip-transport"
                          onClick={() => setStopRunConfirmOpen(true)}
                        >
                          {transportLabels.stop}
                        </button>
                      ) : null
                    ) : (
                      <button
                        type="button"
                        title={
                          !canOperateSession
                            ? transportLabels.hostControlledTip
                            : 'Reset story time to the beginning (still Live)'
                        }
                        className="sc-btn sc-strip-transport"
                        disabled={!canOperateSession || running || elapsedMs <= 0}
                        onClick={() => resetLiveStory()}
                      >
                        {transportLabels.runAgain}
                      </button>
                    )}
                  </>
                ) : null}
              </>
            ) : null}
            </div>
            {showStoryStrip && showTransport && (showExerciseScoreboard || showStorySetupPick) ? (
              <div className="sc-strip-scorebug-wrap">
                {showStorySetupPick ? (
                  <label
                    className={`sc-strip-story-pick${liveTakeActive ? ' is-locked' : ''}`}
                    title={
                      liveTakeActive
                        ? 'Version locked while a run is in progress'
                        : !canSwitchBoardVersion &&
                            !viewingTemplate &&
                            !showDuplicateVersionAction
                          ? 'Host switches versions'
                          : 'Switch version'
                    }
                  >
                    <select
                      className="sc-strip-story-pick-select"
                      value={selectedVersion?.id ?? ''}
                      disabled={
                        studioLinkDown ||
                        versionSwitching ||
                        liveTakeActive ||
                        ((!canSwitchBoardVersion && !viewingTemplate) &&
                          !showDuplicateVersionAction) ||
                        (boardVersions.length < 2 && !showDuplicateVersionAction)
                      }
                      aria-label={
                        studioLinkDown
                          ? 'Offline — version locked'
                          : versionSwitching
                          ? 'Switching version…'
                          : liveTakeActive
                            ? 'Version locked while a run is in progress'
                            : !canSwitchBoardVersion &&
                                !viewingTemplate &&
                                !showDuplicateVersionAction
                              ? 'Host switches versions'
                              : 'Switch version'
                      }
                      onChange={(e) => {
                        const next = e.target.value
                        if (next === '__create_copy__') {
                          // Sentinel action — keep the current version selected.
                          e.target.value = selectedVersion?.id ?? ''
                          beginDuplicateVersionInline()
                          return
                        }
                        switchBoardVersion(next)
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option disabled value="__versions_hdr__">
                        Versions
                      </option>
                      {boardVersions.map((v) => (
                        <option
                          key={v.id}
                          value={v.id}
                          title={v.summary ?? v.name}
                          disabled={liveTakeActive && v.id !== selectedVersion?.id}
                        >
                          {v.name}
                        </option>
                      ))}
                      {showDuplicateVersionAction && !liveTakeActive ? (
                        <>
                          <option disabled value="__versions_sep__">
                            ────────
                          </option>
                          <option value="__create_copy__">Create a new version…</option>
                        </>
                      ) : null}
                    </select>
                  </label>
                ) : null}
                {/*
                  Run / version options — Live, Draft/Published (setup), and Done review.
                  Create new copy lives on the version dropdown (not on Done).
                */}
                {showExerciseScoreboard &&
                (inLiveRun || selectedIsPlanned || selectedIsCompleted) ? (
                  <button
                    type="button"
                    className={`sc-strip-run-chip sc-strip-scorebug sc-strip-status is-control${
                      liveTakeActive && running && !instantReplayEngaged ? ' is-rec' : ''
                    }${
                      instantReplayEngaged ||
                      liveTakePausedForReplay ||
                      (liveTakeActive && !running)
                        ? ' is-paused'
                        : ''
                    }${postRunOfferOpen ? ' is-open' : ''}${
                      !canOpenRunStatus ? ' is-readonly' : ''
                    }${versionSwitching ? ' is-switching' : ''}${
                      studioLinkDown ? ' is-link-down' : ''
                    }`}
                    title={
                      studioLinkDown
                        ? hubLinkState === 'reconnecting'
                          ? 'Reconnecting to the server…'
                          : STUDIO_LINK_DOWN_TITLE
                        : versionSwitching
                        ? 'Switching version…'
                        : !canOpenRunStatus
                          ? 'Run status'
                          : selectedIsCompleted
                            ? postRunOfferOpen
                              ? 'Close review options'
                              : 'Review options'
                            : !canOperateSession
                              ? postRunOfferOpen
                                ? 'Close status'
                                : 'Run status'
                              : postRunOfferOpen
                                ? 'Close run options'
                                : 'Run options'
                    }
                    aria-expanded={canOpenRunStatus ? postRunOfferOpen : undefined}
                    aria-disabled={!canOpenRunStatus ? true : undefined}
                    disabled={!canOpenRunStatus}
                    onClick={() => {
                      if (runCountdown != null) return
                      if (!canOpenRunStatus) return
                      if (postRunOfferOpen) closePostRunOffer()
                      else openPostRunOffer()
                    }}
                  >
                    <span className="sc-strip-status-main">
                      <span className="sc-strip-scorebug-phase">{stripStatusLabel}</span>
                    </span>
                    {canOpenRunStatus ? (
                      <span className="sc-strip-status-affordance" aria-hidden="true">
                        ▾
                      </span>
                    ) : null}
                  </button>
                ) : null}
              </div>
            ) : null}
            {selectedIsPlanned &&
            !inLiveRun &&
            showBoardTools &&
            rosterGrantsSession &&
            (showStoryStrip ? showTransport : true) &&
            (mayPublish || mayGoLive) ? (
              <span className="sc-strip-window">
                {sessionIsPublishedNow ? (
                  mayGoLive ? (
                    <button
                      type="button"
                      title={hubActionsLocked ? hubLockTitle : windowLabels.goLiveTitle}
                      className="sc-btn rec armed"
                      disabled={!canGoLive}
                      onClick={() => setGoLiveConfirmOpen(true)}
                    >
                      {windowLabels.goLive}
                    </button>
                  ) : null
                ) : (
                  <button
                    type="button"
                    title={
                      hubActionsLocked
                        ? hubLockTitle
                        : shareBlockers.length
                          ? shareBlockers.join(' · ')
                          : shareHints.length
                            ? shareHints.join(' · ')
                            : windowLabels.publishTitle
                    }
                    className="sc-btn go armed-green"
                    onClick={() => {
                      setSharePanelMode('publish')
                      setSharePanelOpen(true)
                    }}
                    disabled={!canPublish}
                  >
                    {windowLabels.publish}
                  </button>
                )}
              </span>
            ) : null}
          </div>

          <div className="sc-map-stage">
            {serverBooting ? (
              <div className="sc-boot" role="status" aria-live="polite">
                <span className="sc-boot-spin" aria-hidden="true" />
                Loading
              </div>
            ) : bootError ? (
              <StudioOfflineNotice message={bootError} onRetry={retryStudioBoot} />
            ) : (
              <>
                {showBoardTools && !getStartedOpen ? (
                  <div className="sc-map-wind">
                    <StripWindBadge
                      fromDeg={
                        Number(stripWind?.params.fromDeg) ||
                        Number(defaultParams('wind').fromDeg) ||
                        0
                      }
                      speedMph={
                        Number(stripWind?.params.speedMph) ||
                        Number(defaultParams('wind').speedMph) ||
                        0
                      }
                      plumeDistanceM={
                        Number(stripWind?.params.plumeDistanceM) ||
                        Number(defaultParams('wind').plumeDistanceM) ||
                        0
                      }
                      displayUnits={viewSession.displayUnits}
                      muted={Boolean(!stripWind || stripWind.ignored)}
                      hint={windChipHint}
                      title={
                        stripWind?.ignored
                          ? 'Surface wind ignored on board — click to Inspect and turn Ignore off'
                          : 'Surface wind on the board'
                      }
                      onClick={() => inspectWind()}
                    />
                  </div>
                ) : null}
                {showBoardTools && presenceLineup.length > 0 ? (
                  <div className="sc-map-share-dock" aria-label="People on the board">
                    <div className="sc-presence-lineup" aria-label="People on the board">
                      {presenceLineup.map((row) => {
                        const isPresenter = presenterEmail === row.email
                        const name = row.self
                          ? user.name?.trim() || row.label.replace(/\s*\(you\)\s*$/i, '')
                          : row.label
                        const selfSharing = row.self && watchOn
                        const peerSharing = !row.self && watchByEmail.has(row.email)
                        const isSharing = selfSharing || peerSharing
                        const tip = isSharing
                          ? `${name} · Sharing pointer${
                              row.self ? ' (S to stop)' : ''
                            }${isPresenter ? ' · Presenter' : ''}`
                          : `${name} · ${isPresenter ? 'Unset presenter' : 'Set presenter'}`
                        return (
                          <FastHoverTip
                            key={row.email}
                            tip={tip}
                            className={`sc-presence-lineup-tip${isPresenter ? ' is-presenter' : ''}${
                              isSharing ? ' is-sharing' : ''
                            }`}
                            onClick={() => {
                              const id = instanceIdRef.current
                              if (!id) return
                              const next = isPresenter ? null : row.email
                              void hubSetPresenter(id, next).catch(() => {})
                            }}
                          >
                            <span
                              className="sc-presence-lineup-av"
                              style={
                                {
                                  '--sc-presence-bg': row.tone.bg,
                                  '--sc-presence-fg': row.tone.fg,
                                  '--sc-presence-ring': row.tone.ring,
                                } as CSSProperties
                              }
                              aria-hidden="true"
                            >
                              <span className="sc-presence-lineup-initials">{row.initials}</span>
                            </span>
                          </FastHoverTip>
                        )
                      })}
                    </div>
                  </div>
                ) : null}
                <ScenarioMapHost
                  mapId={boardMapId}
                  styleUrl={boardPlate.styleUrl}
                  accessToken={boardPlate.accessToken}
                  camera={getStartedOpen ? WIZARD_MAP_BACKDROP_CAMERA : viewSession.camera}
                  directedCamera={getStartedOpen ? null : directedCamera}
                  samplePlayheadIso={sampleMapPlayheadIso}
                  cameraFollowOn={cameraFollowOn}
                  onCameraFollowChange={(on) => {
                    setCameraFollowOn(on)
                    // Soft-hold from a prior pan must not block the first beat on OFF→ON.
                    if (on) {
                      setCamEditHoldAtMs(null)
                      setCameraFollowApplyEpoch((n) => n + 1)
                    }
                  }}
                  showAutoTrack={!getStartedOpen && showBoardTools}
                  onUserCameraGesture={() => {
                    if (getStartedOpen) return
                    // Only arm soft-hold while Auto-track is fighting the user's pan (Save camera).
                    if (!cameraFollowOnRef.current) return
                    const anchor = followEditAnchorMsRef.current
                    if (anchor != null) setCamEditHoldAtMs(anchor)
                  }}
                  onCameraFrame={(cam) => {
                    if (getStartedOpen) return
                    if (previewTemplateIdRef.current) return
                    const id = instanceIdRef.current
                    if (!id) return
                    const placing = Boolean(placeTypeRef.current && placeTypeRef.current !== 'wind')
                    const gesturing = Boolean(
                      cam.dragId ||
                      (placing && cam.pointerLng != null) ||
                      (cam.pointerDown && cam.hoverId)
                    )
                    // Object/place pose for peers (editFrame). Runs even while Share pointer is on —
                    // that path used to return early with only a share frame, so remotes saw the hand
                    // but not the dragged icon.
                    if (shareEditPointerRef.current) {
                      // Keyframed path whole-moves are authoring (shift the path), not co-edit
                      // pose — don't live-echo mid-drag (peers saw ghost path/time weirdness).
                      const dragObj = cam.dragId
                        ? snapshotRef.current.session.objects.find((o) => o.id === cam.dragId)
                        : undefined
                      const keyedPathAuthoring = Boolean(dragObj?.keyframes?.length)
                      if (gesturing && !keyedPathAuthoring) {
                        editShareLiveRef.current = true
                        if (!watchOnRef.current) {
                          // Co-edit without Share: edit-only stream (hand not shown).
                          void hubSetWatchFrame(id, {
                            active: true,
                            edit: true,
                            who: editWhoRef.current || undefined,
                            ...cam,
                          }).catch(() => {})
                          return
                        }
                        // Sharing: one frame below carries hand + drag together. Do NOT send
                        // edit then share — coalesce kept the second frame, and a later
                        // pointer-only frame could wipe dragId (hand moved, icon froze).
                      } else if (editShareLiveRef.current && !dragCommitInFlightRef.current) {
                        // Pointer-up commit owns the clear after Upsert — don't race a no-drag
                        // frame over the final pose (that left P sticky on a mid-drag point).
                        editShareLiveRef.current = false
                        void hubSetWatchFrame(id, {
                          active: true,
                          edit: true,
                          lng: cam.lng,
                          lat: cam.lat,
                          zoom: cam.zoom,
                        }).catch(() => {})
                        // When not Sharing, fall through so Presenter camera-only drive can continue.
                      }
                    }
                    if (watchOnRef.current) {
                      if (cam.pointerLng != null && cam.pointerLat != null) {
                        const prev = localSharePointerRef.current
                        localSharePointerRef.current = {
                          lng: cam.pointerLng,
                          lat: cam.pointerLat,
                          down: cam.pointerDown,
                          hoverId: cam.hoverId,
                          cursor: cam.cursor,
                        }
                        const moved =
                          !prev ||
                          Math.abs(prev.lng - cam.pointerLng) > 1e-7 ||
                          Math.abs(prev.lat - cam.pointerLat) > 1e-7 ||
                          prev.down !== cam.pointerDown ||
                          prev.hoverId !== cam.hoverId ||
                          prev.cursor !== cam.cursor
                        if (moved) setLocalSharePointerEpoch((n) => n + 1)
                      }
                      void hubSetWatchFrame(id, {
                        active: true,
                        ...cam,
                        who: editWhoRef.current || undefined,
                        whoName: editWhoNameRef.current || undefined,
                        // Done only — Planned Preview playhead is hub Clock (Share must not carry story).
                        ...(selectedIsCompletedRef.current
                          ? {
                              storyElapsedMs: elapsedRef.current,
                              storyRunning: runningRef.current,
                            }
                          : {}),
                      }).catch(() => {})
                      return
                    }
                    if (localSharePointerRef.current) {
                      localSharePointerRef.current = null
                      setLocalSharePointerEpoch((n) => n + 1)
                    }
                    // Presenter with share off: camera drive only (no hand).
                    if (
                      presenterEmailRef.current &&
                      presenterEmailRef.current === myEmailRef.current
                    ) {
                      void hubSetWatchFrame(id, {
                        active: true,
                        lng: cam.lng,
                        lat: cam.lat,
                        zoom: cam.zoom,
                        focusId: cam.focusId,
                        who: editWhoRef.current || undefined,
                        whoName: editWhoNameRef.current || undefined,
                        ...(selectedIsCompletedRef.current
                          ? {
                              storyElapsedMs: elapsedRef.current,
                              storyRunning: runningRef.current,
                            }
                          : {}),
                      }).catch(() => {})
                    }
                  }}
                  watchCursors={(() => {
                    void localSharePointerEpoch
                    const me = user.email.trim().toLowerCase()
                    const cursors: {
                      id: string
                      lng: number
                      lat: number
                      down?: boolean
                      clickSeq?: number
                      hoverId?: string
                      hoverTip?: string
                      grab?: boolean
                      cursor?: 'pointer' | 'grab' | 'grabbing'
                      zoomCue?: 'in' | 'out'
                      zoomCueN?: number
                      who?: string
                    }[] = []
                    for (const [email, frame] of watchByEmail) {
                      if (frame.grace || frame.pointerLng == null || frame.pointerLat == null)
                        continue
                      // Never draw our own jumbo hand — presence avatar is the share reminder.
                      if (watchOn && email === me) continue
                      const isPresenter = presenterEmail === email
                      const followingThem =
                        isPresenter &&
                        Boolean(watchFrame) &&
                        presenterEmail !== me
                      // Hide pan-drag hand only when that camera is *not* driving this map
                      // (geo-fixed ghost wiggles). While following Presenter pan, keep it —
                      // closed-fingers (grabbing) to match the native pan cursor.
                      const objectDrag = Boolean(
                        frame.dragId || frame.hoverId || frame.cursor === 'pointer'
                      )
                      const panDragGhost = Boolean(frame.pointerDown && !objectDrag)
                      if (panDragGhost && !followingThem) continue
                      const followCursor: 'pointer' | 'grab' | 'grabbing' | undefined =
                        followingThem
                          ? frame.hoverId || frame.dragId
                            ? 'pointer'
                            : frame.pointerDown
                              ? 'grabbing'
                              : 'grab'
                          : undefined
                      cursors.push({
                        id: email,
                        lng: frame.pointerLng,
                        lat: frame.pointerLat,
                        down: frame.pointerDown,
                        clickSeq: frame.clickSeq,
                        hoverId: frame.hoverId,
                        hoverTip: frame.hoverTip,
                        grab: followingThem ? Boolean(frame.pointerDown) : frame.grab,
                        cursor: followCursor ?? frame.cursor,
                        zoomCue: followingThem ? watchZoomCue?.dir : undefined,
                        zoomCueN: watchZoomCue?.n,
                        who: frame.who,
                      })
                    }
                    // Do not echo our own jumbo hand locally — avatar lineup is the share reminder.
                    // Edit-stream carries pointer for smooth drag pose, but do not draw a remote hand
                    // unless Share pointer / Presenter share is on (those use watchByEmail above).
                    return cursors
                  })()}
                  watchFocusId={!watchOn ? (watchFrame?.focusId ?? null) : null}
                  objects={
                    getStartedOpen
                      ? EMPTY_MAP_OBJECTS
                      : (() => {
                          // Live remote pose: edit-stream (co-edit) or any Share pointer peer's drag —
                          // not Presenter-only (Sharing shows the hand from watchByEmail, but object
                          // drag used to require presenterEmail and stayed stuck for everyone else).
                          const editorDrag =
                            editFrame?.dragId &&
                            editFrame.dragLng != null &&
                            editFrame.dragLat != null
                              ? editFrame
                              : null
                          let peerDrag: {
                            dragId: string
                            dragLng: number
                            dragLat: number
                          } | null = null
                          if (!editorDrag && !watchOn) {
                            for (const frame of watchByEmail.values()) {
                              if (frame.grace) continue
                              if (frame.dragId && frame.dragLng != null && frame.dragLat != null) {
                                peerDrag = {
                                  dragId: frame.dragId,
                                  dragLng: frame.dragLng,
                                  dragLat: frame.dragLat,
                                }
                                break
                              }
                            }
                          }
                          void stickyDragEpoch
                          const live = watchOn ? editorDrag : (editorDrag ?? peerDrag)
                          if (
                            live?.dragId != null &&
                            live.dragLng != null &&
                            live.dragLat != null
                          ) {
                            stickyRemoteDragRef.current = {
                              dragId: live.dragId,
                              dragLng: live.dragLng,
                              dragLat: live.dragLat,
                              until: performance.now() + 1500,
                            }
                            if (stickyDragTimerRef.current != null) {
                              window.clearTimeout(stickyDragTimerRef.current)
                            }
                            stickyDragTimerRef.current = window.setTimeout(() => {
                              stickyDragTimerRef.current = null
                              if (
                                stickyRemoteDragRef.current &&
                                performance.now() >= stickyRemoteDragRef.current.until
                              ) {
                                stickyRemoteDragRef.current = null
                                setStickyDragEpoch((n) => n + 1)
                              }
                            }, 1520)
                          }
                          const sticky = stickyRemoteDragRef.current
                          const drag =
                            live ?? (sticky && performance.now() < sticky.until ? sticky : null)
                          if (sticky && performance.now() >= sticky.until)
                            stickyRemoteDragRef.current = null
                          const dragLng = drag?.dragLng
                          const dragLat = drag?.dragLat
                          if (!drag?.dragId || dragLng == null || dragLat == null)
                            return mapResolved.objects
                          return mapResolved.objects.map((object) =>
                            object.id === drag.dragId
                              ? {
                                  ...object,
                                  pose: { lng: dragLng, lat: dragLat },
                                }
                              : object
                          )
                        })()
                  }
                  selectedId={getStartedOpen ? null : selectedId}
                  highlightIds={getStartedOpen ? EMPTY_MAP_IDS : highlightIds}
                  iconHighlightIds={EMPTY_MAP_IDS}
                  ghostFlashIds={getStartedOpen ? EMPTY_MAP_IDS : ghostFlashIds}
                  iconsOnly={ghostFlash || zoomFlash}
                  surfacePlumes={mapSurfacePlumes}
                  viewResetId={viewResetId}
                  placeArmed={
                    !getStartedOpen &&
                    !scribbleMode &&
                    canPlaceObjects &&
                    placeType != null &&
                    placeType !== 'wind' &&
                    placeType !== 'freehand' &&
                    placeType !== 'pen' &&
                    placeType !== 'ruler' &&
                    (placeType !== 'type' || canEditBoard)
                  }
                  freehandArmed={
                    !getStartedOpen &&
                    !scribbleMode &&
                    canEditBoard &&
                    placeType === 'freehand'
                  }
                  penArmed={
                    !getStartedOpen && !scribbleMode && canEditBoard && placeType === 'pen'
                  }
                  rulerArmed={
                    !getStartedOpen && !scribbleMode && canEditBoard && placeType === 'ruler'
                  }
                  scribbleArmed={!getStartedOpen && scribbleMode}
                  scribbleVisible={!getStartedOpen && scribbleMode}
                  scribbleStrokes={scribbleStrokes}
                  sketchStyle={sketchStyle}
                  editing={editing}
                  canDragObject={canDragObject}
                  showAuthorGuides={showAuthorGuides}
                  waypointObjectId={waypointObjectId}
                  plumeFocusObjectId={plumeFocusObjectId}
                  hideWaypointUnitIcon={hideWaypointUnitIcon}
                  waypointFocusIndex={
                    pathPreviewActive && pathPreviewWaypointIndex != null
                      ? pathPreviewWaypointIndex
                      : waypointFocusIndex
                  }
                  waypointsEditable={editing}
                  pathHoldLabel={pathHoldLabel}
                  onWaypointFocus={(idx) => {
                    setWaypointFocusIndex(idx)
                    if (waypointObjectId) {
                      setSelectedId(waypointObjectId)
                      setDock('inspector')
                    }
                  }}
                  onWaypointMove={moveWaypoint}
                  attachedHighlightObjects={attachedHighlightObjects}
                  mapFeatureHighlight={mapAssetHighlight ?? featureHoverHighlight}
                  mapApiRef={mapApiRef}
                  onMapIdle={() => {
                    // Throttle — Mapbox idle fires often; we only need tile-settle refreshes.
                    const now = Date.now()
                    if (now - (mapIdleBumpAtRef.current || 0) < 400) return
                    mapIdleBumpAtRef.current = now
                    setMapIdleEpoch((n) => n + 1)
                  }}
                  onSelectObject={(id) => {
                    if (id) {
                      mapAssetGenRef.current += 1
                      setMapAssetPick(null)
                      setMapAssetDetailsOpen(false)
                    }
                    setNearbyCandidates([])
                    setNearbyPicks(new Set())
                    setNearbyHoverKey(null)
                    setLinkedHoverKey(null)
                    setAssetDebugKey(null)
                    setNearbyHint(null)
                    if (id) {
                      noteInspectOpenedFrom()
                      setSelectedId(id)
                      setPlaceType(null)
                      setRulerStats(null)
                      setDock('inspector')
                      setNotesCtx(id)
                      return
                    }
                    // Empty map / toggle-off: clear selection and restore prior rail tab.
                    // Profile does not come back — opening Inspect closed it for good.
                    setSelectedId(null)
                    inspectReturnDockRef.current = null
                    if (dockRef.current === 'inspector') setDock('settings')
                  }}
                  onPlace={placeObject}
                  onStroke={placeStroke}
                  onScribbleStroke={(path) => {
                    if (path.length < 2) return
                    setScribbleStrokes((prev) => [
                      ...prev,
                      { id: newId('scr'), path },
                    ])
                  }}
                  onRulerChange={setRulerStats}
                  onMoveObject={(id, lng, lat, from) =>
                    moveObject(id, lng, lat, from ?? 'anchor', {
                      persist: false,
                    })
                  }
                  onObjectMoveEnd={() => {
                    persistInstancesNow()
                    // Drain final edit pose *before* Upsert — coalesce + parallel Upsert used to
                    // land a stale durable pose on P while sticky cleared, so the icon missed.
                    const id = instanceIdRef.current
                    const cam = mapApiRef.current?.getCamera()
                    const dragObj =
                      cam?.dragId != null
                        ? snapshotRef.current.session.objects.find((o) => o.id === cam.dragId)
                        : undefined
                    const keyedPath = Boolean(dragObj?.keyframes?.length)
                    dragCommitInFlightRef.current = true
                    void (async () => {
                      try {
                        if (
                          id &&
                          !keyedPath &&
                          cam?.dragId &&
                          cam.dragLng != null &&
                          cam.dragLat != null
                        ) {
                          hubSetWatchFrame(id, {
                            active: true,
                            edit: true,
                            who: editWhoRef.current || undefined,
                            lng: cam.lng,
                            lat: cam.lat,
                            zoom: cam.zoom,
                            dragId: cam.dragId,
                            dragLng: cam.dragLng,
                            dragLat: cam.dragLat,
                          })
                          await hubFlushWatchFrames()
                        }
                        queueObjectWrites(snapshotRef.current.session.objects)
                        await flushObjectWrites()
                        // Drag used persist:false — align the selected version seed so the next
                        // ApplyBoardSeed / StartRun does not restore the pre-drag path.
                        if (!liveTakeActiveRef.current) {
                          setSession((prev) => {
                            if ((prev.boardVersions?.length ?? 0) === 0) return prev
                            const next = updateSelectedVersionSeed(prev, exerciseActor())
                            queueMicrotask(() => {
                              void persistExerciseBoardFields(next)
                            })
                            return next
                          })
                        }
                        // Release edit overlay only after durable write.
                        if (id && shareEditPointerRef.current) {
                          editShareLiveRef.current = false
                          hubSetWatchFrame(id, {
                            active: true,
                            edit: true,
                            lng: cam?.lng ?? 0,
                            lat: cam?.lat ?? 0,
                            zoom: cam?.zoom ?? 0,
                          })
                        }
                      } finally {
                        dragCommitInFlightRef.current = false
                      }
                    })()
                  }}
                  linkHighlightSelected={cyanFeatureHlActive}
                  focusDim={mapFocusDim}
                  focusDimKeep={
                    isLocalStorm(selectedSource?.type) && tornadoPreviewFocus && running
                      ? ['sc-zones-fill', 'sc-zones-line']
                      : undefined
                  }
                  onPlumeHover={(kind) => setWindChipHint(kind === 'surface' || kind === 'both')}
                  onPipelineTap={() => {
                    /* Linking a pipe to an object stays Inspector-only. */
                  }}
                  onBasemapPipelines={applyBasemapPipelines}
                  mapAssetChip={mapAssetChip}
                  onMapAssetDetails={openMapAssetDetails}
                  onMapAssetDismiss={dismissMapAsset}
                />
              </>
            )}
            <div className={`sc-map-flash${mapCamFlash ? ' on' : ''}`} aria-hidden="true" />
            {hubNothingSelected &&
            showBoardChrome &&
            !serverBooting &&
            !bootError &&
            !studioModalOpen ? (
              <div className="sc-hub-empty" role="status">
                <div className="sc-hub-empty-card">
                  <div className="sc-hub-empty-visual" aria-hidden="true">
                    <div className="sc-hub-empty-rail">
                      <span className="sc-hub-empty-rail-row is-accent" />
                      <span className="sc-hub-empty-rail-row" />
                      <span className="sc-hub-empty-rail-row" />
                    </div>
                    <div className="sc-hub-empty-map-plate" />
                  </div>
                  <p className="sc-hub-empty-title">
                    {libraryMode
                      ? 'Borrow a board, then make it yours'
                      : paneIsEmpty
                        ? 'No sessions yet'
                        : 'Open a session — or start something new'}
                  </p>
                  <p className="sc-hub-empty-hint">
                    {libraryMode
                      ? 'Pick a template below and copy it into a Draft, or switch to Sessions for work you already have.'
                      : paneIsEmpty
                        ? 'Use New session, or browse Templates for a starter board.'
                        : 'Choose one from the list, or switch to Templates when you want a blank map.'}
                  </p>
                </div>
              </div>
            ) : null}
            {showDebugPanels && featureDebugProps ? (
              <pre className="sc-asset-debug sc-debug" aria-label="Mapbox feature properties">
                {JSON.stringify(featureDebugProps, null, 2)}
              </pre>
            ) : null}
            {recToast ? (
              <div className="sc-rec-toast" role="status">
                <span className="sc-rec-toast-title">Starting screen capture…</span>
                <span className="sc-rec-toast-esc">Esc stops and saves</span>
              </div>
            ) : null}
            {showBoardChrome && studioLinkDown ? (
              <div
                className="sc-version-switch-veil is-offline"
                role="alert"
                aria-live="assertive"
                aria-busy={hubLinkState === 'reconnecting'}
              >
                <span className="sc-version-switch-veil-label">
                  <span className="sc-version-switch-veil-row">
                    {hubLinkState === 'reconnecting' ? (
                      <span className="sc-boot-spin" aria-hidden="true" />
                    ) : null}
                    {hubLinkState === 'reconnecting'
                      ? 'Reconnecting to the server…'
                      : STUDIO_LINK_DOWN_TITLE}
                  </span>
                  {hubLinkState !== 'reconnecting' ? (
                    <>
                      <span className="sc-version-switch-veil-hint">{STUDIO_LINK_DOWN_HINT}</span>
                      <button
                        type="button"
                        className="sc-link-btn sc-version-switch-veil-retry"
                        onClick={() => void retryStudioLink()}
                      >
                        Try again
                      </button>
                    </>
                  ) : null}
                </span>
              </div>
            ) : null}
            {showBoardChrome && !studioLinkDown && versionSwitching ? (
              <div className="sc-version-switch-veil" role="status" aria-live="polite">
                <span className="sc-version-switch-veil-label">Switching story version…</span>
              </div>
            ) : null}
            {showBoardChrome &&
            !viewingTemplate &&
            !versionSwitching &&
            !studioLinkDown &&
            ((liveTakeActive &&
              !running &&
              !exerciseRunStopped &&
              (stripFocusRunLocal != null || takeNumber > 0)) ||
              showFinishedRunReviewBanner) ? (
              <div
                className={`sc-instant-replay-banner${
                  (showFinishedRunReviewBanner
                    ? running
                    : instantReplayEngaged && replayPlaying)
                    ? ' is-playing'
                    : ' is-paused'
                }`}
                role="status"
                aria-live="polite"
              >
                {(() => {
                  const paused = showFinishedRunReviewBanner
                    ? !running
                    : !(instantReplayEngaged && replayPlaying)
                  // Finished-run tape = Reviewing (Live or Done). Instant Replay is mid-take only.
                  if (showFinishedRunReviewBanner) {
                    return paused ? 'Reviewing · Paused' : 'Reviewing'
                  }
                  if (instantReplayEngaged) {
                    return paused ? 'Instant Replay · Paused' : 'Instant Replay'
                  }
                  return `Run ${stripFocusRunLocal ?? takeNumber} paused`
                })()}
              </div>
            ) : null}
            {cloneReadyOffer ? (
              <div
                className="sc-join-offer"
                role="dialog"
                aria-labelledby="sc-clone-ready-title"
                onClick={() => setCloneReadyOffer(false)}
              >
                <div
                  className="sc-join-offer-card sc-clone-ready-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="sc-join-offer-x"
                    aria-label="Close"
                    title="Close"
                    onClick={() => setCloneReadyOffer(false)}
                  >
                    ×
                  </button>
                  <div className="sc-clone-ready-check" aria-hidden="true">
                    ✓
                  </div>
                  <h2 id="sc-clone-ready-title" className="sc-join-offer-title">
                    You're all set
                  </h2>
                  <p className="sc-clone-ready-body">
                    Saved as a Draft. Edit anytime. Publish when you&apos;re ready for others to
                    find it.
                  </p>
                  <div className="sc-join-offer-actions">
                    <button
                      type="button"
                      className="sc-btn go armed-green"
                      autoFocus
                      onClick={() => setCloneReadyOffer(false)}
                    >
                      Got it
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {runCountdown != null && runCountdown > 0 ? (
              <div className="sc-run-countdown" role="status" aria-live="assertive">
                <span className="sc-run-countdown-label">
                  {`Starting Run ${nextVersionRunNumber}…`}
                </span>
                <div className="sc-run-countdown-ring" aria-hidden="true">
                  <svg className="sc-run-countdown-ring-svg" viewBox="0 0 120 120">
                    <circle
                      className="sc-run-countdown-ring-track"
                      cx="60"
                      cy="60"
                      r="52"
                    />
                    <circle
                      className="sc-run-countdown-ring-arc"
                      cx="60"
                      cy="60"
                      r="52"
                    />
                  </svg>
                  <span className="sc-run-countdown-digit" key={runCountdown}>
                    {runCountdown}
                  </span>
                </div>
                {selectedVersion?.name?.trim() ? (
                  <span className="sc-run-countdown-version">
                    {selectedVersion.name.trim()}
                  </span>
                ) : null}
              </div>
            ) : null}
            {postRunOfferOpen ? (
              <div
                className="sc-join-offer"
                role="dialog"
                aria-labelledby="sc-run-options-title"
                onClick={() => closePostRunOffer()}
              >
                <div
                  className="sc-join-offer-card sc-run-options-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="sc-join-offer-x"
                    aria-label="Close"
                    title="Close"
                    onClick={() => closePostRunOffer()}
                  >
                    ×
                  </button>
                  <h2 id="sc-run-options-title" className="sc-join-offer-title">
                    {liveTakeActive
                      ? (() => {
                          const n = stripFocusRunLocal
                          if (instantReplayEngaged) {
                            return n != null
                              ? `Instant Replay of Run ${n}`
                              : 'Instant Replay'
                          }
                          if (running) {
                            return n != null ? `You're running Run ${n}` : "You're running"
                          }
                          return n != null ? `Run ${n} is paused` : 'Paused'
                        })()
                      : selectedIsCompleted
                        ? inRunReview || boardActivity === 'watching' || tapeReviewActive
                          ? storyModalTitle
                          : 'Session Complete'
                        : boardActivity === 'editing'
                          ? 'Ready to publish?'
                          : storyModalTitle}
                  </h2>
                  {(() => {
                    /** Actively in Instant Replay of a finished run — not merely “runs exist”. */
                    const activelyReviewing =
                      !liveTakeActive &&
                      (inRunReview ||
                        boardActivity === 'watching' ||
                        tapeReviewActive)
                    /** Operator Previewing with finished runs — offer pick / start. */
                    const previewWithRuns =
                      !liveTakeActive && !activelyReviewing && reviewRunChips.length > 0
                    const runN = stripFocusRunLocal ?? takeNumber
                    // Spectator copy: name the activity, not a person (no host/trainer/owner).
                    const body = liveTakeActive
                      ? !canOperateSession
                        ? instantReplayEngaged
                          ? runN > 0
                            ? `Instant Replay of Run ${runN} is on. Follow along on the map and time bar.`
                            : 'Instant Replay is on. Follow along on the map and time bar.'
                          : running && !instantReplayEngaged
                            ? runN > 0
                              ? `Run ${runN} is in progress. Follow along on the map and time bar.`
                              : 'A run is in progress. Follow along on the map and time bar.'
                            : runN > 0
                              ? `Run ${runN} is paused. Follow along on the map and time bar.`
                              : 'The run is paused. Follow along on the map and time bar.'
                        : instantReplayEngaged
                          ? 'Use the time bar for Instant Replay, or Resume to continue from where you paused.'
                          : running && !instantReplayEngaged
                            ? 'Pause anytime for Instant Replay, or Stop when this run is finished.'
                            : 'Resume to continue the run, or Stop when you are done.'
                      : selectedIsCompleted
                        ? activelyReviewing
                          ? 'Play from the time bar, or pick another run.'
                          : reviewRunChips.length > 0
                            ? 'Pick a run to review, or view the final board.'
                            : 'Session complete. View the final board from the time bar.'
                        : activelyReviewing
                          ? !canOperateSession
                            ? reviewModalRunN != null
                              ? `Run ${reviewModalRunN} is being reviewed. Follow along on the map and time bar.`
                              : 'A finished run is being reviewed. Follow along on the map and time bar.'
                            : 'Pick a run to open, or start a new run when you are ready.'
                          : previewWithRuns
                            ? canOperateSession
                              ? 'Pick a run to open, or start a new run when you are ready.'
                              : reviewRunChips.length > 1
                                ? 'Finished runs are available. Follow along on the map and time bar.'
                                : 'A finished run is available. Follow along on the map and time bar.'
                            : !canOperateSession
                              ? 'Follow along on the map and time bar.'
                              : selectedIsPlanned
                                ? sessionIsPublishedNow
                                  ? 'Edit and preview the story anytime. Go Live when you are ready to run it.'
                                  : 'Edit and preview the story anytime. Publish when you are ready for others to find it.'
                                : 'Play the story from the time bar, or edit the board anytime.'
                    return body ? <p className="sc-ready-golive-body">{body}</p> : null
                  })()}
                  {!liveTakeActive &&
                  (hasReviewableRun || reviewRunChips.length > 0 || inRunReview) ? (
                    <>
                      <div className="sc-run-options-runs">
                        <div
                          className="sc-run-options-runs-label is-sr-only"
                          id="sc-run-options-runs-label"
                        >
                          Finished runs
                        </div>
                        <ReviewRunChipRow
                          chips={reviewRunChipItems}
                          activeRunId={reviewChipActiveRunId}
                          canPick={canPickReviewRunChip}
                          onPick={onPickReviewRunChip}
                        />
                      </div>
                      {selectedIsCompleted ? (
                        <div className="sc-join-offer-actions">
                          {inRunReview ||
                          boardActivity === 'watching' ||
                          tapeReviewActive ? (
                            <button
                              type="button"
                              className="sc-btn"
                              onClick={() => exitRunReviewToBoard()}
                            >
                              View final board
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="sc-btn"
                              autoFocus
                              onClick={() => closePostRunOffer()}
                            >
                              Close
                            </button>
                          )}
                        </div>
                      ) : canOperateSession ? (
                        <div className="sc-join-offer-actions">
                          {showExerciseRunChrome ? (
                            <button
                              type="button"
                              className="sc-btn"
                              onClick={() => requestStartRun()}
                            >
                              {startRunButtonLabel}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="sc-btn"
                            onClick={() => exitRunReviewToBoard()}
                          >
                            I&apos;m done reviewing
                          </button>
                        </div>
                      ) : (
                        <div className="sc-join-offer-actions">
                          <button
                            type="button"
                            className="sc-btn"
                            autoFocus
                            onClick={() => closePostRunOffer()}
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                  <div className="sc-join-offer-actions">
                    {liveTakeActive ? (
                      canOperateSession ? (
                        <>
                          <button
                            type="button"
                            className={
                              // Match IR / Run-paused gold (banner + strip) when resumable.
                              instantReplayEngaged || !running
                                ? 'sc-btn is-resume-hint'
                                : 'sc-btn rec armed'
                            }
                            autoFocus
                            onClick={() => {
                              closePostRunOffer()
                              if (instantReplayEngaged || !running) {
                                resumeLiveRunFromPause()
                              } else {
                                stripPlayBtnRef.current?.click()
                              }
                            }}
                          >
                            {running && !instantReplayEngaged ? 'Pause' : 'Resume'}
                          </button>
                          <button
                            type="button"
                            className="sc-btn"
                            onClick={() => {
                              closePostRunOffer()
                              setStopRunConfirmOpen(true)
                            }}
                          >
                            Stop
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="sc-btn"
                          autoFocus
                          onClick={() => closePostRunOffer()}
                        >
                          Close
                        </button>
                      )
                    ) : !mayOperateSession ? (
                      <button
                        type="button"
                        className="sc-btn"
                        autoFocus
                        onClick={() => closePostRunOffer()}
                      >
                        Close
                      </button>
                    ) : selectedIsPlanned ? (
                      <>
                        {mayGoLive ? (
                          <button
                            type="button"
                            className="sc-btn rec armed"
                            title={hubActionsLocked ? hubLockTitle : undefined}
                            disabled={!canGoLive}
                            onClick={() => {
                              closePostRunOffer()
                              setGoLiveConfirmOpen(true)
                            }}
                          >
                            Go Live
                          </button>
                        ) : mayPublish ? (
                          <button
                            type="button"
                            className="sc-btn go armed-green"
                            title={
                              hubActionsLocked
                                ? hubLockTitle
                                : shareBlockers.length
                                  ? shareBlockers.join(' · ')
                                  : shareHints.length
                                    ? shareHints.join(' · ')
                                    : windowLabels.publishTitle
                            }
                            disabled={!canPublish}
                            onClick={() => {
                              closePostRunOffer()
                              setSharePanelMode('publish')
                              setSharePanelOpen(true)
                            }}
                          >
                            {windowLabels.publish}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="sc-btn"
                          autoFocus
                          onClick={() => closePostRunOffer()}
                        >
                          Close
                        </button>
                      </>
                    ) : (
                      <>
                        {showExerciseRunChrome ? (
                          <button
                            type="button"
                            className="sc-btn rec armed"
                            autoFocus
                            onClick={() => requestStartRun()}
                          >
                            {startRunButtonLabel}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="sc-btn"
                          onClick={() => closePostRunOffer()}
                        >
                          Close
                        </button>
                      </>
                    )}
                  </div>
                  )}
                </div>
              </div>
            ) : null}
            {versionDupUi ? (
              <div
                className="sc-join-offer"
                role="dialog"
                aria-labelledby="sc-version-dup-title"
                onClick={() => setVersionDupUi(null)}
              >
                <div
                  className="sc-join-offer-card sc-run-options-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="sc-join-offer-x"
                    aria-label="Close"
                    title="Close"
                    onClick={() => setVersionDupUi(null)}
                  >
                    ×
                  </button>
                  <h2 id="sc-version-dup-title" className="sc-join-offer-title">
                    {versionDupUi.mode === 'done'
                      ? 'New Version Ready'
                      : `Copy ${versionQuoted}?`}
                  </h2>
                  <p className="sc-ready-golive-body">
                    {versionDupUi.mode === 'done'
                      ? `${versionQuoted} is selected. Edit freely without changing the original.`
                      : 'Creates a copy you can edit. The original stays unchanged.'}
                  </p>
                  {versionDupUi.mode === 'name' ? (
                    <VersionDupNameForm
                      key={versionDupUi.draft}
                      initial={versionDupUi.draft}
                      onDuplicate={(name) => confirmDuplicateVersionInline(name)}
                      onCancel={() => setVersionDupUi(null)}
                    />
                  ) : (
                    <div className="sc-join-offer-actions">
                      <button
                        type="button"
                        className="sc-btn rec armed"
                        autoFocus
                        onClick={() => setVersionDupUi(null)}
                      >
                        OK
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            {showJoinOfferCard ? (
              <div
                className="sc-join-offer"
                role="dialog"
                aria-labelledby="sc-join-offer-title"
                onClick={() => setInviteJoinPeekId(instanceId)}
              >
                <div className="sc-join-offer-card" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="sc-join-offer-x"
                    aria-label="Dismiss for now"
                    title="Dismiss for now"
                    onClick={() => setInviteJoinPeekId(instanceId)}
                  >
                    ×
                  </button>
                  <h2 id="sc-join-offer-title" className="sc-join-offer-title">
                    Join this session?
                  </h2>
                  {joinOfferHost ? (
                    <div
                      className="sc-join-offer-host"
                      style={
                        {
                          '--sc-presence-bg': joinOfferHost.tone.bg,
                          '--sc-presence-fg': joinOfferHost.tone.fg,
                          '--sc-presence-ring': joinOfferHost.tone.ring,
                        } as CSSProperties
                      }
                    >
                      <span className="sc-join-offer-avatar" aria-hidden="true">
                        {joinOfferHost.initials}
                      </span>
                      <span className="sc-join-offer-host-name">{joinOfferHost.name}</span>
                      <span className="sc-join-offer-host-tag">{joinOfferHost.hostTag}</span>
                    </div>
                  ) : null}
                  <div className="sc-join-offer-actions">
                    <button
                      type="button"
                      className="sc-btn go armed-green"
                      onClick={() => respondToInvite(instanceId, 'joined')}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className="sc-btn"
                      onClick={() => respondToInvite(instanceId, 'declined')}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {showJoinOfferChip ? (
              <div className="sc-join-chip" role="status">
                <span>You're invited.</span>
                <button type="button" onClick={() => setInviteJoinPeekId(null)}>
                  Join?
                </button>
              </div>
            ) : null}
            {showDeclineThanks ? (
              <div className="sc-invite-toast" role="status">
                <span>Thanks for responding. See you some other time!</span>
                <button type="button" onClick={() => setDeclineThanksOpen(false)}>
                  OK
                </button>
              </div>
            ) : null}
            {showJoinToastStack ? (
              <div className="sc-join-toasts" role="status">
                {joinToasts.map((item) => (
                  <div
                    key={item.id}
                    className={`sc-join-toast${item.debug ? ' debug' : ''}${item.sticky ? ' is-sticky' : ''}`}
                  >
                    <span>{item.text}</span>
                    {item.sticky ? (
                      <button
                        type="button"
                        className="sc-join-toast-x"
                        aria-label="Dismiss"
                        title="Dismiss"
                        onClick={() =>
                          setJoinToasts((list) => list.filter((row) => row.id !== item.id))
                        }
                      >
                        <CloseIcon />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {keyToast ? (
              <div className="sc-key-toast" role="status">
                <kbd className="sc-key-toast-kbd">{keyToast.token}</kbd>
                <span>{keyToast.label}</span>
              </div>
            ) : null}
            {keysOpen ? (
              <div className="sc-key-sheet" role="dialog" aria-label="Keyboard shortcuts">
                <button
                  type="button"
                  className="sc-key-sheet-x"
                  aria-label="Close shortcuts"
                  title="Close"
                  onClick={() => setKeysOpen(false)}
                >
                  ×
                </button>
                <div className="sc-key-sheet-row">
                  <kbd>?</kbd>
                  <span>Shortcuts</span>
                </div>
                {sessionOffersStoryClock && accountBoardMode !== 'following' ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Play or pause the story clock"
                    onClick={() => toggleClockRef.current()}
                  >
                    <kbd className="sc-key-space" aria-label="Spacebar" title="Spacebar">
                      <span className="sc-key-space-glyph">␣</span>
                    </kbd>
                    <span>Play / Pause</span>
                  </button>
                ) : null}
                {sessionOffersStoryClock ? (
                  <div
                    className={`sc-key-sheet-row${canSeekTimeBar ? '' : ' is-disabled'}`}
                    title={
                      canSeekTimeBar
                        ? 'Jump to the previous or next story card'
                        : 'The host controls the story clock'
                    }
                  >
                    <span className="sc-key-pair" aria-label="Left and right arrow keys">
                      <button
                        type="button"
                        className="sc-key-mini"
                        disabled={!canSeekTimeBar}
                        aria-label="Previous card"
                        onClick={() => skipBeatRef.current(-1)}
                      >
                        <kbd>←</kbd>
                      </button>
                      <button
                        type="button"
                        className="sc-key-mini"
                        disabled={!canSeekTimeBar}
                        aria-label="Next card"
                        onClick={() => skipBeatRef.current(1)}
                      >
                        <kbd>→</kbd>
                      </button>
                    </span>
                    <span>Prev / next card</span>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="sc-key-sheet-row sc-key-sheet-action"
                  title="Hide the palette, sidebar, and top bar"
                  onClick={() =>
                    setFocusMode((prev) => {
                      const next = !prev
                      showKeyToast('F', next ? 'Focus on' : 'Focus off')
                      return next
                    })
                  }
                >
                  <kbd>F</kbd>
                  <span>Focus</span>
                  <em>{focusMode ? 'On' : 'Off'}</em>
                </button>
                <button
                  type="button"
                  className="sc-key-sheet-row sc-key-sheet-action"
                  title="Kermit mode: show or hide personal chalk marks"
                  onClick={() => scribbleHotkeyRef.current()}
                >
                  <kbd>K</kbd>
                  <span>Scribble</span>
                  <em>{scribbleMode ? 'Shown' : 'Hidden'}</em>
                </button>
                {scribbleMode ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Erase all personal scribbles"
                    onClick={() => hotkeyActionsRef.current.eraseScribble()}
                  >
                    <kbd>E</kbd>
                    <span>Erase scribbles</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="sc-key-sheet-row sc-key-sheet-action"
                  title="Show or hide the left palette / map symbols rail"
                  onClick={() => hotkeyActionsRef.current.togglePalette()}
                >
                  <kbd>P</kbd>
                  <span>Palette</span>
                  <em>{showPalette ? 'Shown' : 'Hidden'}</em>
                </button>
                {canLeadWatchRef.current ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Share or stop sharing your pointer"
                    onClick={() => hotkeyActionsRef.current.toggleShare()}
                  >
                    <kbd>S</kbd>
                    <span>Share pointer</span>
                    <em>{watchOn ? 'On' : 'Off'}</em>
                  </button>
                ) : presenterEmail && watchFrame ? (
                  <div className="sc-key-sheet-row">
                    <kbd />
                    <span>Following Presenter</span>
                    <em>On</em>
                  </div>
                ) : null}
                {accountBoardMode !== 'following' && ghostsOfferedRef.current ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Hold to light objects this view is hiding at this moment on the clock"
                    onMouseDown={() => {
                      setGhostFlash(true)
                      showKeyToast('G', 'Show ghosts', true)
                    }}
                    onMouseUp={() => {
                      setGhostFlash(false)
                      clearKeyToast()
                    }}
                    onMouseLeave={() => {
                      setGhostFlash(false)
                      clearKeyToast()
                    }}
                  >
                    <kbd>G</kbd>
                    <span>Show ghosts</span>
                    <em>hold to reveal</em>
                  </button>
                ) : null}
                {accountBoardMode !== 'following' ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Hold to reveal all objects on the board"
                    onMouseDown={() => {
                      setZoomFlash(true)
                      showKeyToast('Z', 'Zoom out', true)
                    }}
                    onMouseUp={() => {
                      setZoomFlash(false)
                      clearKeyToast()
                    }}
                    onMouseLeave={() => {
                      setZoomFlash(false)
                      clearKeyToast()
                    }}
                  >
                    <kbd>Z</kbd>
                    <span>Zoom out</span>
                    <em>hold to reveal</em>
                  </button>
                ) : null}
                {videoExportStatus === 'recording' ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Stop tab screen capture"
                    onClick={() => void stopVideoCapture()}
                  >
                    <kbd>Esc</kbd>
                    <span>Stop capture</span>
                  </button>
                ) : null}
                <div className="sc-key-sheet-rule" />
                <button
                  type="button"
                  className="sc-key-sheet-row sc-key-sheet-action"
                  title="Follow the story camera"
                  onClick={() =>
                    setCameraFollowOn((prev) => {
                      const next = !prev
                      if (next) {
                        setCamEditHoldAtMs(null)
                        setCameraFollowApplyEpoch((n) => n + 1)
                      }
                      showKeyToast('T', next ? 'Auto-track on' : 'Auto-track off')
                      return next
                    })
                  }
                >
                  <kbd>T</kbd>
                  <span>Auto-track</span>
                  <em>{cameraFollowOn ? 'On' : 'Off'}</em>
                </button>
                <button
                  type="button"
                  className="sc-key-sheet-row sc-key-sheet-action"
                  title="Show or hide wind plumes on your view"
                  onClick={() => hotkeyActionsRef.current.togglePlumes()}
                >
                  <kbd>W</kbd>
                  <span>Wind plumes</span>
                  <em>{surfacePlumes ? 'On' : 'Off'}</em>
                </button>
                {accountBoardMode !== 'following' ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Cycle what this view shows"
                    onClick={() =>
                      setBoardRevealMode((prev) => {
                        const ids = BOARD_REVEAL_OPTIONS.map((o) => o.id)
                        const next = ids[(ids.indexOf(prev) + 1) % ids.length] ?? ids[0]!
                        const label = BOARD_REVEAL_OPTIONS.find((o) => o.id === next)?.label ?? 'board'
                        showKeyToast('V', `Viewing ${label}`)
                        return next
                      })
                    }
                  >
                    <kbd>V</kbd>
                    <span>Viewing</span>
                    <em>{BOARD_REVEAL_OPTIONS.find((o) => o.id === boardRevealMode)?.label}</em>
                  </button>
                ) : null}
                {canCaptureScreen ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action"
                    title="Start or stop tab screen capture. Esc also stops while recording."
                    onClick={() => hotkeyActionsRef.current.toggleCapture()}
                  >
                    <kbd>C</kbd>
                    <span>Capture screen</span>
                    <em>{videoExportStatus === 'recording' ? 'Recording' : 'Off'}</em>
                  </button>
                ) : null}
                <div
                  className="sc-key-sheet-row"
                  title="Shift-click the map to copy latitude and longitude to the clipboard"
                >
                  <span className="sc-key-pair" aria-label="Shift-click">
                    <kbd>⇧</kbd>
                    <kbd className="sc-key-mouse" aria-hidden="true">
                      <NavMdiIcon path={mdiCursorDefault} sizePx={12} />
                    </kbd>
                  </span>
                  <span>Copy lat, long</span>
                </div>
                {canViewDebug ? (
                  <button
                    type="button"
                    className="sc-key-sheet-row sc-key-sheet-action is-dev"
                    title="Developer only — hidden for normal users"
                    onClick={() =>
                      setDebugPanelsOn((prev) => {
                        const next = !prev
                        showKeyToast('D', next ? 'Developer panels on' : 'Developer panels off')
                        return next
                      })
                    }
                  >
                    <kbd>D</kbd>
                    <span>Developer panels</span>
                    <em>{debugPanelsOn ? 'Shown' : 'Hidden'}</em>
                  </button>
                ) : null}
                {liveTakeActive ? (
                  <>
                    <div className="sc-key-sheet-rule" />
                    <div className="sc-key-sheet-hd">This run</div>
                    {canOperateSession ? (
                      <button
                        type="button"
                        className="sc-key-sheet-row sc-key-sheet-action"
                        title="Show or hide wind plumes for everyone in this run"
                        onClick={() => hotkeyActionsRef.current.toggleRunRoomPlumes()}
                      >
                        <kbd>R</kbd>
                        <span>Show wind plumes</span>
                        <em>{session.runRoomPlumes === true ? 'On' : 'Off'}</em>
                      </button>
                    ) : (
                      <div
                        className="sc-key-sheet-row"
                        title="A trainer sets this for the room"
                      >
                        <kbd className="is-muted">R</kbd>
                        <span>Show wind plumes</span>
                        <em>{session.runRoomPlumes === true ? 'On' : 'Off'}</em>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>

          {showTimeBar ? (
            <div
              key={timeBarEnterKey}
              className={`sc-time-bar${timeBarEnterKey > 0 ? ' is-enter' : ''}${
                running && !instantReplayEngaged ? ' is-live-run' : ''
              }${instantReplayEngaged ? ' is-replay' : ''}${
                replayPlaying ? ' is-replay-playing' : ''
              }${studioLinkDown ? ' is-link-down' : ''}${
                versionSwitching ? ' is-switching' : ''
              }`}
              role="group"
              aria-label="Story time"
            >
              <div className="sc-time-bar-meta">
                {showStoryFollowEye ? (
                  <span
                    className="sc-time-bar-follow"
                    aria-label={followEyeLabel}
                    onMouseEnter={(e) => {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                      setTimeBarMetaTip({
                        text: followEyeLabel,
                        left: rect.left + rect.width / 2,
                        top: rect.top - 6,
                      })
                    }}
                    onMouseLeave={() => setTimeBarMetaTip(null)}
                  >
                    <svg viewBox="0 0 14 12" width="13" height="11" aria-hidden="true">
                      <path
                        d="M7 1.5C3.8 1.5 1.35 3.6.5 6c.85 2.4 3.3 4.5 6.5 4.5s5.65-2.1 6.5-4.5C12.65 3.6 10.2 1.5 7 1.5zm0 7.2A2.7 2.7 0 1 1 7 3.3a2.7 2.7 0 0 1 0 5.4z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={`sc-time-bar-play${
                      instantReplayEngaged
                        ? replayPlaying
                          ? ' is-playing'
                          : ''
                        : running
                          ? ' is-playing'
                          : ''
                    }`}
                    title={
                      instantReplayEngaged
                        ? replayPlaying
                          ? 'Pause Instant Replay'
                          : 'Play Instant Replay'
                        : officialTakeRunning
                          ? 'Pause this run'
                          : liveTakePausedForReplay
                            ? 'Resume this run'
                            : tapeReviewActive
                              ? running
                                ? 'Pause Instant Replay'
                                : 'Play Instant Replay'
                              : inRunReview
                                ? running
                                  ? 'Pause Instant Replay'
                                  : 'Review this run'
                                : inLiveRun && !liveTakeActive
                                  ? running
                                    ? 'Pause story preview'
                                    : 'Preview story'
                                  : viewingTemplate
                                    ? running
                                      ? 'Pause template preview'
                                      : 'Preview template'
                                    : running
                                      ? 'Pause story preview'
                                      : 'Preview story'
                    }
                    aria-label={
                      instantReplayEngaged
                        ? replayPlaying
                          ? 'Pause Instant Replay'
                          : 'Play Instant Replay'
                        : officialTakeRunning
                          ? 'Pause this run'
                          : liveTakePausedForReplay
                            ? 'Resume this run'
                            : tapeReviewActive
                              ? running
                                ? 'Pause Instant Replay'
                                : 'Play Instant Replay'
                              : inRunReview
                                ? running
                                  ? 'Pause Instant Replay'
                                  : 'Review this run'
                                : inLiveRun && !liveTakeActive
                                  ? running
                                    ? 'Pause story preview'
                                    : 'Preview story'
                                  : viewingTemplate
                                    ? running
                                      ? 'Pause template preview'
                                      : 'Preview template'
                                    : running
                                      ? 'Pause story preview'
                                      : 'Preview story'
                    }
                    disabled={
                      studioLinkDown ||
                      versionSwitching ||
                      (selectedIsPlanned && !canDrivePreview && !inLiveRun)
                    }
                    onClick={() => {
                      if (studioLinkDown || versionSwitching) return
                      // Finished-run Reviewing: same path as Space (hub Resume/Pause).
                      if (tapeReviewActive) {
                        toggleClock()
                        return
                      }
                      if (instantReplayEngaged) {
                        toggleReplayPlaying()
                        return
                      }
                      // Live-idle / between takes: always preview the live board.
                      // Instant Replay of a finished run is via run chips, not Play.
                      if (inLiveRun && !liveTakeActive) {
                        toggleClock()
                        return
                      }
                      if (stripPlayBtnRef.current) {
                        stripPlayBtnRef.current.click()
                        return
                      }
                      if (isClockPreview || selectedIsCompleted) toggleClock()
                    }}
                  >
                    {(tapeReviewActive
                      ? running
                      : instantReplayEngaged
                        ? replayPlaying
                        : running) ? (
                      <svg viewBox="0 0 12 14" width="11" height="12" aria-hidden="true">
                        <rect x="1" y="1" width="3.5" height="12" rx="0.6" fill="currentColor" />
                        <rect x="7.5" y="1" width="3.5" height="12" rx="0.6" fill="currentColor" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 12 14" width="11" height="12" aria-hidden="true">
                        <path d="M2 1.2v11.6L11 7 2 1.2z" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                )}
                <span
                  className="sc-time-bar-clock"
                  aria-label="Story time"
                  onMouseEnter={(e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                    setTimeBarMetaTip({
                      text: 'Story time',
                      left: rect.left + rect.width / 2,
                      top: rect.top - 6,
                    })
                  }}
                  onMouseLeave={() => setTimeBarMetaTip(null)}
                >
                  {formatRunningClock(mapPlayheadIso, authorClock)}
                </span>
              </div>
              {timeBarMetaTip && typeof document !== 'undefined'
                ? createPortal(
                    <div
                      className="sc-story-scrub-tip is-portal"
                      style={{ left: timeBarMetaTip.left, top: timeBarMetaTip.top }}
                      role="tooltip"
                    >
                      {timeBarMetaTip.text}
                    </div>,
                    document.body
                  )
                : null}
              <StoryScrubber
                variant="player"
                /* Full story span always; IR / tape review black out past the freeze. */
                durationMs={displayDur}
                availableUntilMs={
                  (tapeReviewActive || exerciseRunStopped) &&
                  tapeAvailableUntilMs != null
                    ? tapeAvailableUntilMs
                    : instantReplayEngaged && instantReplayPauseMs != null
                      ? instantReplayPauseMs
                      : null
                }
                availableUntilLabel={
                  (tapeReviewActive || exerciseRunStopped) &&
                  tapeAvailableUntilMs != null
                    ? 'Stop point'
                    : instantReplayEngaged && instantReplayPauseMs != null
                      ? 'Pause point'
                      : null
                }
                elapsedMs={Math.min(
                  mapElapsedMs,
                  (tapeReviewActive || exerciseRunStopped) &&
                    tapeAvailableUntilMs != null
                    ? tapeAvailableUntilMs
                    : instantReplayEngaged && instantReplayPauseMs != null
                      ? instantReplayPauseMs
                      : displayDur
                )}
                marks={storyScrubMarks}
                disabled={!canSeekTimeBar}
                ariaLabel="Story position"
                formatHoverMs={(ms) =>
                  formatRunningClock(addStoryMs(viewSession.storyStartTime, ms), authorClock)
                }
                onScrubBegin={() => {
                  if (!canSeekTimeBar) return
                  if (seriesPreviewActive) {
                    stopPathPreview()
                    stopVisPreview()
                    stopWindPreview()
                    stopRadiusPreview()
                  }
                  setInspectPreviewMs(null)
                  timeBarScrubbingRef.current = true
                  clearScrubSeekTimer()
                  deckScrubSeekGenRef.current++
                  // Non-urgent run-options (Paused / Reviewing / Start) yield to the time bar.
                  if (postRunOfferOpen) closePostRunOffer()
                  if (inRunReview) noteBoardReviewActivity()
                  if (inLiveRun && liveTakeActive) {
                    // pauseTape Seek so mid-drag Clocks cannot re-arm playing.
                    pauseReplayTapeForScrub()
                    if (!instantReplayEngagedRef.current) {
                      paintInstantReplayLocal()
                      // Stamp engage for the room on mousedown — local-only paint left P on
                      // "Run N paused" until a debounced scrub Seek landed (or was storm-dropped).
                      const freeze = instantReplayPauseMsRef.current
                      const ms = elapsedRef.current
                      if (freeze != null && Math.abs(ms - freeze) > 80) {
                        seekInstantReplay(ms, { engageReplay: true })
                      } else {
                        persistInstantReplayFields({ engaged: true, playing: false })
                      }
                    }
                    return
                  }
                  // Drop coalesced mid-drag Seeks from a prior gesture so Pause-only
                  // begins this drag (end↔start used to leave Seek(end) in the pump).
                  if (studioClockDesireRef.current?.seekMs != null) {
                    studioClockDesireRef.current = null
                  }
                  // Live Reviewing / Planned: hub Pause immediately. Local-only pause left
                  // the room Running while the thumb dragged end↔start (frozen bar after).
                  pauseForDeckScrub()
                  hubSeekOwnsHeadRef.current = true
                }}
                onScrubPreview={(ms) => {
                  if (!canSeekTimeBar) return
                  timeBarScrubbingRef.current = true
                  pauseReplayTapeForScrub()
                  const irCap =
                    (instantReplayEngagedRef.current || liveTakeActive) &&
                    instantReplayPauseMsRef.current != null
                      ? instantReplayPauseMsRef.current
                      : null
                  const tapeCap =
                    tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
                      ? tapeAvailableUntilMsRef.current
                      : null
                  const headCap =
                    irCap != null ? irCap : tapeCap != null ? tapeCap : null
                  const clamped = headCap != null ? Math.min(ms, headCap) : ms
                  elapsedRef.current = clamped
                  setElapsedMs(clamped)
                  setHoverElapsedMs(null)
                  // Planned / Reviewing / live-idle: coalesced forceAdmit Seeks (not jumpTo/
                  // pump — that raced commit and wedged Play after extreme thrash).
                  const deckScrubHub =
                    !(inLiveRun && liveTakeActive) &&
                    Boolean(instanceId) &&
                    !previewTemplateId &&
                    (canDrivePreview || canOperateSession) &&
                    (selectedIsPlanned ||
                      tapeReviewActive ||
                      (inLiveRun && !liveTakeActive))
                  if (deckScrubHub) {
                    scheduleDebouncedDeckScrubSeek(clamped)
                    return
                  }
                  scheduleDebouncedSeek(clamped)
                }}
                onScrubCommit={(ms) => {
                  if (!canSeekTimeBar) return
                  clearScrubSeekTimer()
                  pauseReplayTapeForScrub()
                  // End of gesture — allow tape/story-end effects again (running is false).
                  timeBarScrubbingRef.current = false
                  if (inLiveRun && liveTakeActive) {
                    seekInstantReplay(ms, { engageReplay: true, forceAdmit: true })
                    return
                  }
                  // Planned + Live Reviewing: forceAdmit Seek on release (mid-drag already
                  // coalesced via scheduleDebouncedDeckScrubSeek).
                  if (
                    Boolean(instanceId) &&
                    !previewTemplateId &&
                    (canDrivePreview || canOperateSession) &&
                    (selectedIsPlanned ||
                      tapeReviewActive ||
                      (inLiveRun && !liveTakeActive))
                  ) {
                    const tapeCap =
                      tapeReviewRef.current && tapeAvailableUntilMsRef.current != null
                        ? tapeAvailableUntilMsRef.current
                        : null
                    const cap =
                      tapeCap != null
                        ? tapeCap
                        : displayDur > 0
                          ? displayDur
                          : ms
                    const next = Math.min(Math.max(0, ms), cap)
                    elapsedRef.current = next
                    setElapsedMs(next)
                    setHoverElapsedMs(null)
                    runningRef.current = false
                    setRunning(false)
                    localPreviewDriveUntilRef.current = performance.now() + 400
                    // Invalidate in-flight mid-drag Seeks so Seek(end) cannot stamp after
                    // this Seek(0) on extreme thrash.
                    deckScrubSeekGenRef.current++
                    const scrubGen = deckScrubSeekGenRef.current
                    const id = instanceId
                    const start = viewSession.storyStartTime
                    const iso = addStoryMs(start, next)
                    hubSeekOwnsHeadRef.current = true
                    if (transportPauseHoldIdRef.current === id) {
                      transportPauseHoldIdRef.current = null
                    }
                    // Pin clocksRef now so a late mid-drag Seek Clock cannot store end
                    // while the thumb is at 0 (release then snapped the bar).
                    const prevClock = clocksRef.current.get(id)
                    if (prevClock) {
                      const pinned: StudioClock = {
                        ...prevClock,
                        clockStatus: 'paused',
                        anchorStoryTimeIso: iso,
                        anchorActualTime: null,
                      }
                      clocksRef.current.set(id, pinned)
                      serverClockRef.current = pinned
                      setServerClock(pinned)
                    }
                    // Room still Running (scrub-begin Pause in flight): Pause+Seek via pump.
                    // Do NOT pre-stamp lastSent/hubWire — pump.skip-seek-dup would no-op.
                    if (hubAdmittedRunningRef.current) {
                      enqueueStudioClock({ running: false, seekMs: next })
                      return
                    }
                    // Room already paused: drop leftover pauseWithoutPin desire and Seek.
                    studioClockDesireRef.current = null
                    void hubSeek(id, iso, false, false, true)
                      .then((admitted) => {
                        if (!admitted || scrubGen !== deckScrubSeekGenRef.current) return
                        lastSentHubSeekMsRef.current = next
                        hubWirePlayheadMsRef.current = next
                        setHubWirePlayheadMs(next)
                      })
                      .catch(() => {})
                      .finally(() => {
                        window.setTimeout(() => {
                          // Scrub-to-start then Play within ~800ms used to clear owns-head
                          // here and snap the plane back while PreviewPlay was in flight.
                          releaseHubSeekOwnsHeadUnlessPlayPending()
                        }, 800)
                      })
                    return
                  }
                  jumpTo(ms)
                }}
              />
              <span
                className="sc-time-bar-clock"
                title={
                  tapeReviewActive && tapeAvailableUntilMs != null
                    ? 'Stop point'
                    : instantReplayEngaged && instantReplayPauseMs != null
                    ? 'Pause point'
                    : 'Story length'
                }
              >
                {formatElapsed(
                  instantReplayEngaged && instantReplayPauseMs != null
                    ? instantReplayPauseMs
                    : displayDur
                )}
                {!instantReplayEngaged && elapsedMs > displayDur + 250 ? '+' : ''}
              </span>
              <PlaybackSpeedMenu
                menuPlacement="up"
                rate={previewSpeedRate}
                rates={speedMenuRates}
                fullRuntimeMs={fullRuntimeMs}
                disabled={!canEditTimeBarSpeed || playbackMaxRate <= PLAYBACK_RATE_MIN + 0.01}
                title={
                  officialTakeRunning
                    ? 'Speed unlocks when the take is paused'
                    : instantReplayEngaged
                      ? 'Tape speed only — leave Instant Replay, then set run speed'
                      : undefined
                }
                onPickRate={(rate) => applyPlaybackRate(rate)}
              />
            </div>
          ) : null}
        </div>

        <DockPane>
        {(dock, setDock) => {
        const showSessionsInviteBadge =
          (dock !== 'scenarios' || libraryMode) && inviteBadgeAvailable
        return (
        <aside className={`sc-right${dock === 'account' ? ' is-account' : ''}`}>
          {showBoardChrome && dock !== 'account' ? (
            <div className="sc-umbrella">
              {!(
                focusMode &&
                accountBoardMode === 'following' &&
                !passiveFocusChromeForTabs.showSessions
              ) ? (
                <div
                  className={`sc-pane-modes${dock !== 'scenarios' ? ' is-away' : ''}`}
                  role="tablist"
                  aria-label="Sessions or templates"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!libraryMode}
                    className={`sc-pane-mode is-primary${!libraryMode ? ' on' : ''}`}
                    title={
                      showSessionsInviteBadge
                        ? `${sessionsInviteBadgeCount} invitation${
                            sessionsInviteBadgeCount === 1 ? '' : 's'
                          } waiting in Sessions`
                        : dock !== 'scenarios' && !libraryMode
                          ? 'Show sessions'
                          : undefined
                    }
                    onClick={() => {
                      setDock('scenarios')
                      if (libraryMode) exitLibraryToSessions()
                    }}
                  >
                    {dock !== 'scenarios' && !libraryMode ? (
                      <span
                        className="sc-pane-mode-back"
                        role="img"
                        aria-hidden="true"
                        title="Back to sessions list"
                      >
                        <svg
                          className="sc-pane-mode-back-ico"
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          aria-hidden="true"
                        >
                          <path
                            d="M20 12H7.8M12.2 6.5 7 12l5.2 5.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    ) : null}
                    <span className="sc-pane-mode-label">Sessions</span>
                    {showSessionsInviteBadge ? (
                      <span
                        className={`sc-pane-mode-count${
                          sessionsInviteBadgeLive ? ' is-live' : ''
                        }`}
                        aria-hidden="true"
                      >
                        {sessionsInviteBadgeCount > 9 ? '9+' : sessionsInviteBadgeCount}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={libraryMode}
                    className={`sc-pane-mode is-secondary${libraryMode ? ' on' : ''}`}
                    title={
                      dock !== 'scenarios' && libraryMode
                        ? 'Show templates'
                        : 'Browse templates'
                    }
                    onClick={() => {
                      setDock('scenarios')
                      // Already in Templates with a selection — keep it (no deselect).
                      if (libraryMode && previewTemplateId) return
                      if (libraryMode) return
                      enterTemplatesHub()
                    }}
                  >
                    {dock !== 'scenarios' && libraryMode ? (
                      <span
                        className="sc-pane-mode-back"
                        role="img"
                        aria-hidden="true"
                        title="Back to templates list"
                      >
                        <svg
                          className="sc-pane-mode-back-ico"
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          aria-hidden="true"
                        >
                          <path
                            d="M20 12H7.8M12.2 6.5 7 12l5.2 5.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    ) : null}
                    <span className="sc-pane-mode-label">Templates</span>
                  </button>
                </div>
              ) : null}
              {hubTitlePlaceholder ? (
                <div className="sc-board-title-row">
                  <h2 className="is-placeholder">{hubTitlePlaceholder}</h2>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="sc-right-lower">
            <div className="sc-rbody">
              {dock === 'scenarios' ? (
                <>
                  {serverBooting ? (
                    <div className="sc-boot" role="status" aria-live="polite">
                      <span className="sc-boot-spin" aria-hidden="true" />
                      Loading
                    </div>
                  ) : bootError ? (
                    <p className="sc-hint sc-library-empty" role="status">
                      {isStudioUnreachableNotice(bootError)
                        ? STUDIO_UNREACHABLE_RAIL
                        : bootError}
                    </p>
                  ) : (
                    <>
                      {!libraryMode && accessPerms.has('scenarios.create') ? (
                        <div className="sc-rail-new-frame">
                          <button
                            type="button"
                            className="sc-btn sc-rail-new-btn"
                            title={hubActionsLocked ? hubLockTitle : 'Start a new session'}
                            onClick={() => openGetStartedWizard('menu')}
                            disabled={hubActionsLocked}
                          >
                            New session
                          </button>
                        </div>
                      ) : null}
                      <div
                        className={`sc-rscroll${cloningTemplateId ? ' is-cloning' : ''}`}
                        ref={scenarioRailRef}
                      >
                      {serverNotice ? (
                        <p
                          className={`sc-hint${/edition/i.test(serverNotice) ? ' sc-edition-note' : ' sc-library-empty'}`}
                          role="status"
                        >
                          {isStudioUnreachableNotice(serverNotice)
                            ? STUDIO_UNREACHABLE_RAIL
                            : serverNotice}
                        </p>
                      ) : null}
                      <>
                        {paneRunning.length ? (
                          <div className="sc-inst-group sc-inst-fold">
                            <button
                              type="button"
                              className="sc-inst-group-label sc-fold-btn"
                              aria-expanded={liveOpen}
                              onClick={(e) => toggleLiveFold(e.currentTarget)}
                            >
                              Live ({paneRunning.length})
                              <span
                                ref={liveFoldChevronRef}
                                className={`sc-chevron${liveOpen ? ' open' : ''}`}
                                aria-hidden="true"
                              >
                                ▾
                              </span>
                            </button>
                            <div
                              ref={liveFoldBodyRef}
                              className="sc-fold-body open"
                              hidden={!liveOpen}
                            >
                              <div className="sc-fold-inner">
                            {paneRunning.map((inst) => {
                              const on = !viewingTemplate && inst.id === instanceId
                              const board = on ? session : inst.session
                              const roster = on ? people : inst.people
                              const clock = inst.studioClock ?? clocksRef.current.get(inst.id)
                              // Same shape as Done: Go Live wall time · room-up duration (not story length).
                              // Per-card only — never prefer global actualStartTime (leaks across boards).
                              const goLiveIso =
                                inst.actualStartTime ?? clock?.actualStartTime ?? null
                              const liveSub = liveSessionCardSubtitle(inst, board, goLiveIso)
                              const byline = sessionCardByline(board, roster, user.userId)
                              // Re-render each livePulse tick so wall duration advances while Live.
                              void livePulse
                              return (
                                <div
                                  key={inst.id}
                                  data-inst-id={inst.id}
                                  className={`sc-inst${sessionRailToneClass(inst)}${on ? ' on' : ''}`}
                                >
                                  <div className="sc-inst-top">
                                    <button
                                      type="button"
                                      className="sc-inst-hit"
                                      onClick={() => selectInstance(inst.id)}
                                    >
                                      <b title={board.title}>
                                        <span className="sc-inst-title-text">{board.title}</span>
                                      </b>
                                      <span
                                        className={`sc-inst-byline${byline ? '' : ' is-empty'}`}
                                        title={byline ?? undefined}
                                      >
                                        {byline || '\u00a0'}
                                      </span>
                                      <span className="sc-inst-meta">
                                        <span
                                          className="sc-inst-meta-text sc-inst-sub"
                                          title={liveSub}
                                        >
                                          {liveSub}
                                        </span>
                                        <span
                                          className="sc-inst-live-pulse"
                                          aria-hidden="true"
                                          title="Live"
                                        />
                                      </span>
                                    </button>
                                    <div className="sc-inst-tools">
                                      {accessPerms.has('scenarios.edit_board') ? (
                                        <button
                                          type="button"
                                          className="sc-icon-btn"
                                          aria-label="Session settings"
                                          title="Session settings"
                                          onClick={() => openScenarioSettings(inst.id)}
                                        >
                                          <PencilIcon />
                                        </button>
                                      ) : null}
                                      {showPdfExports ? (
                                      <button
                                        type="button"
                                        className="sc-icon-btn"
                                        aria-label="Export run summary"
                                        title="Export run summary"
                                        onClick={() =>
                                          void downloadSessionSummaryPdf(
                                            instanceRun(
                                              on
                                                ? {
                                                    ...inst,
                                                    session,
                                                    people: roster,
                                                  }
                                                : inst,
                                              roster
                                            ),
                                            roster,
                                            inst.id
                                          )
                                        }
                                      >
                                        <PdfIcon />
                                      </button>
                                      ) : null}
                                      {sessionCloneTool(inst.id, board.title)}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {(
                          [
                            { label: 'Invited', items: paneInvited },
                            { label: 'Joined', items: paneJoined },
                            { label: 'Draft', items: paneDraft },
                            { label: 'Published', items: paneReady },
                          ] as const
                        ).map((group) =>
                          group.items.length ? (
                            <div key={group.label} className="sc-inst-group">
                              <div className="sc-inst-group-label">{group.label}</div>
                              {group.items.map((inst) => {
                                const on = !viewingTemplate && inst.id === instanceId
                                const board = on ? session : inst.session
                                const roster = on ? people : inst.people
                                const invitedLive =
                                  group.label === 'Invited' && inst.lifecycle === 'running'
                                const clock = invitedLive
                                  ? (inst.studioClock ?? clocksRef.current.get(inst.id))
                                  : undefined
                                const goLiveIso = invitedLive
                                  ? (inst.actualStartTime ?? clock?.actualStartTime ?? null)
                                  : null
                                if (invitedLive) void livePulse
                                const byline = sessionCardByline(board, roster, user.userId)
                                const meta = invitedLive
                                  ? liveSessionCardSubtitle(inst, board, goLiveIso)
                                  : group.label === 'Joined'
                                    ? joinedSessionCardSubtitle(
                                        board,
                                        roster,
                                        user.userId,
                                        inst.createdAtIso
                                      )
                                    : plannedSessionCardSubtitle(board, inst.createdAtIso)
                                return (
                                  <div
                                    key={inst.id}
                                    data-inst-id={inst.id}
                                    className={`sc-inst${sessionRailToneClass(inst)}${
                                      on ? ' on' : ''
                                    }`}
                                  >
                                    <div className="sc-inst-top">
                                      <button
                                        type="button"
                                        className="sc-inst-hit"
                                        onClick={() => selectInstance(inst.id)}
                                      >
                                        <b title={board.title}>
                                          <span className="sc-inst-title-text">{board.title}</span>
                                        </b>
                                        <span
                                          className={`sc-inst-byline${byline ? '' : ' is-empty'}`}
                                          title={byline ?? undefined}
                                        >
                                          {byline || '\u00a0'}
                                        </span>
                                        {invitedLive ? (
                                          <span className="sc-inst-meta">
                                            <span
                                              className="sc-inst-meta-text sc-inst-sub"
                                              title={meta}
                                            >
                                              {meta}
                                            </span>
                                            <span
                                              className="sc-inst-live-pulse"
                                              aria-hidden="true"
                                              title="Live"
                                            />
                                          </span>
                                        ) : (
                                          <span className="sc-inst-sub" title={meta}>
                                            {meta}
                                          </span>
                                        )}
                                      </button>
                                      <div className="sc-inst-tools">
                                        {accessPerms.has('scenarios.edit_board') ? (
                                          <button
                                            type="button"
                                            className="sc-icon-btn"
                                            aria-label="Session settings"
                                            title="Session settings"
                                            onClick={() => openScenarioSettings(inst.id)}
                                          >
                                            <PencilIcon />
                                          </button>
                                        ) : null}
                                        {showPdfExports ? (
                                        <button
                                          type="button"
                                          className="sc-icon-btn"
                                          aria-label={
                                            invitedLive
                                              ? 'Export run summary'
                                              : 'Export plan for this session'
                                          }
                                          title={
                                            invitedLive
                                              ? 'Export run summary'
                                              : 'Export plan for this session'
                                          }
                                          onClick={() =>
                                            invitedLive
                                              ? void downloadSessionSummaryPdf(
                                                  instanceRun(
                                                    on
                                                      ? {
                                                          ...inst,
                                                          session,
                                                          people: roster,
                                                        }
                                                      : inst,
                                                    roster
                                                  ),
                                                  roster,
                                                  inst.id
                                                )
                                              : void downloadSessionPlanPdf(
                                                  on ? session : inst.session,
                                                  roster,
                                                  {
                                                    instanceId: inst.id,
                                                  }
                                                )
                                          }
                                        >
                                          <PdfIcon />
                                        </button>
                                        ) : null}
                                        {sessionCloneTool(inst.id, board.title)}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : null
                        )}

                        {paneInterest.length ? (
                          <div className="sc-inst-group">
                            <div className="sc-inst-group-label">You may be interested in</div>
                            {paneInterest.map((inst) => {
                              const on = !viewingTemplate && inst.id === instanceId
                              const board = on ? session : inst.session
                              const roster = on ? people : inst.people
                              return (
                                <div
                                  key={inst.id}
                                  data-inst-id={inst.id}
                                  className={`sc-inst${sessionRailToneClass(inst)}${on ? ' on' : ''}`}
                                >
                                  <div className="sc-inst-top">
                                    <button
                                      type="button"
                                      className="sc-inst-hit"
                                      onClick={() => selectInstance(inst.id)}
                                    >
                                      <b title={board.title}>
                                        <span className="sc-inst-title-text">{board.title}</span>
                                      </b>
                                      {(() => {
                                        const byline = sessionCardByline(
                                          board,
                                          roster,
                                          user.userId
                                        )
                                        const meta = [
                                          'Public',
                                          lifecycleStatusLabel(inst.lifecycle, inst.session),
                                          board.scheduledStartTime
                                            ? formatWall(board.scheduledStartTime)
                                            : inst.createdAtIso
                                              ? formatWall(inst.createdAtIso)
                                              : null,
                                          scheduleDurationLabel(board) || null,
                                        ]
                                          .filter(Boolean)
                                          .join(' · ')
                                        return (
                                          <>
                                            <span
                                              className={`sc-inst-byline${
                                                byline ? '' : ' is-empty'
                                              }`}
                                              title={byline ?? undefined}
                                            >
                                              {byline || '\u00a0'}
                                            </span>
                                            <span className="sc-inst-sub" title={meta}>
                                              {meta}
                                            </span>
                                          </>
                                        )
                                      })()}
                                    </button>
                                    <div className="sc-inst-tools">
                                      {accessPerms.has('scenarios.edit_board') ? (
                                        <button
                                          type="button"
                                          className="sc-icon-btn"
                                          aria-label="Session settings"
                                          title="Session settings"
                                          onClick={() => openScenarioSettings(inst.id)}
                                        >
                                          <PencilIcon />
                                        </button>
                                      ) : null}
                                      {showPdfExports ? (
                                      <button
                                        type="button"
                                        className="sc-icon-btn"
                                        aria-label={
                                          inst.lifecycle === 'running'
                                            ? 'Export run summary'
                                            : 'Export plan for this session'
                                        }
                                        title={
                                          inst.lifecycle === 'running'
                                            ? 'Export run summary'
                                            : 'Export plan for this session'
                                        }
                                        onClick={() => {
                                          if (inst.lifecycle === 'running') {
                                            void downloadSessionSummaryPdf(
                                              instanceRun(
                                                on
                                                  ? {
                                                      ...inst,
                                                      session,
                                                      people: roster,
                                                    }
                                                  : inst,
                                                roster
                                              ),
                                              roster,
                                              inst.id
                                            )
                                            return
                                          }
                                          void downloadSessionPlanPdf(
                                            on ? session : inst.session,
                                            roster,
                                            {
                                              instanceId: inst.id,
                                            }
                                          )
                                        }}
                                      >
                                        <PdfIcon />
                                      </button>
                                      ) : null}
                                      {sessionCloneTool(inst.id, board.title)}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : null}

                        {panePinnedCompleted.length || paneListedCompleted.length ? (
                          <div className="sc-inst-group sc-inst-fold">
                            <button
                              type="button"
                              className="sc-inst-group-label sc-fold-btn"
                              aria-expanded={completedFoldOpen}
                              onClick={(e) => toggleCompletedFold(e.currentTarget)}
                            >
                              Done ({panePinnedCompleted.length + paneListedCompleted.length})
                              <span
                                ref={completedFoldChevronRef}
                                className={`sc-chevron${completedFoldOpen ? ' open' : ''}`}
                                aria-hidden="true"
                              >
                                ▾
                              </span>
                            </button>
                            <div
                              ref={completedFoldBodyRef}
                              className="sc-fold-body open"
                              hidden={!completedFoldOpen}
                            >
                              <div className="sc-fold-inner">
                                {(() => {
                                  const doneRows = [
                                    ...panePinnedCompleted,
                                    ...paneListedCompleted,
                                  ]
                                  const doneHidden = Math.max(
                                    0,
                                    doneRows.length - RAIL_FULL_CARDS,
                                  )
                                  return (
                                    <>
                                {doneRows.map((inst, doneIndex) => {
                                  const on = !viewingTemplate && inst.id === instanceId
                                  const justDone = inst.id === justCompletedId
                                  const board = on ? session : inst.session
                                  const roster = on ? people : inst.people
                                  // Newest N full; older are spines (hidden until “More”), unless open.
                                  const isOlder = doneIndex >= RAIL_FULL_CARDS
                                  if (isOlder && !on && !justDone && !doneShowMore) return null
                                  const isSpine =
                                    isOlder &&
                                    !justDone &&
                                    (!on || railForcedSpineIds.has(inst.id))
                                  const fullSub = doneSessionCardSubtitle(inst, board)
                                  const spineSub = inst.actualStartTime
                                    ? formatWall(inst.actualStartTime)
                                    : 'Done'
                                  const byline = sessionCardByline(board, roster, user.userId)
                                  const spineTip = [spineSub, byline].filter(Boolean).join(' · ')
                                  return (
                                    <div
                                      key={inst.id}
                                      data-inst-id={inst.id}
                                      className={`sc-inst${sessionRailToneClass(inst)}${on ? ' on' : ''}${
                                        justDone ? ' just-done' : ''
                                      }${isSpine ? ' is-spine' : ''}`}
                                    >
                                      <div className="sc-inst-top">
                                        <button
                                          type="button"
                                          className="sc-inst-hit"
                                          onClick={() => toggleRailOlderCard(inst.id, isOlder)}
                                        >
                                          <b title={board.title}>
                                            <span className="sc-inst-title-text">{board.title}</span>
                                          </b>
                                          <span
                                            className={`sc-inst-byline${byline ? '' : ' is-empty'}`}
                                            title={byline ?? undefined}
                                          >
                                            {byline || '\u00a0'}
                                          </span>
                                          <span className="sc-inst-meta">
                                            {justDone ? (
                                              <span
                                                className="sc-inst-done-badge"
                                                aria-label="Just finished"
                                              >
                                                ✓
                                              </span>
                                            ) : null}
                                            <span
                                              className="sc-inst-meta-text sc-inst-sub"
                                              title={isSpine ? spineTip : fullSub}
                                            >
                                              {isSpine ? spineSub : fullSub}
                                            </span>
                                          </span>
                                        </button>
                                        {!isSpine ? (
                                          <div className="sc-inst-tools">
                                            {accessPerms.has('scenarios.edit_board') ? (
                                              <button
                                                type="button"
                                                className="sc-icon-btn"
                                                aria-label="Session settings"
                                                title="Session settings"
                                                onClick={() => openScenarioSettings(inst.id)}
                                              >
                                                <PencilIcon />
                                              </button>
                                            ) : null}
                                            {showPdfExports ? (
                                            <button
                                              type="button"
                                              className="sc-icon-btn"
                                              aria-label="Export After Action Report"
                                              title="Export After Action Report"
                                              onClick={() =>
                                                void downloadSessionSummaryPdf(
                                                  instanceRun(inst, roster),
                                                  roster,
                                                  inst.id,
                                                  { aar: true }
                                                )
                                              }
                                            >
                                              <PdfIcon />
                                            </button>
                                            ) : null}
                                            {sessionCloneTool(inst.id, board.title)}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  )
                                })}
                                {doneHidden > 0 ? (
                                  <button
                                    type="button"
                                    className="sc-inst-more"
                                    aria-expanded={doneShowMore}
                                    onClick={() => setDoneShowMore((v) => !v)}
                                  >
                                    {doneShowMore
                                      ? 'Show fewer'
                                      : `${doneHidden} more`}
                                  </button>
                                ) : null}
                                    </>
                                  )
                                })()}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {paneOversight.length ? (
                          <div className="sc-inst-group sc-inst-fold">
                            <button
                              type="button"
                              className="sc-inst-group-label sc-fold-btn"
                              aria-expanded={oversightOpen}
                              title="Sessions you can open with View all — not already in your other bins"
                              onClick={(e) => toggleOversightFold(e.currentTarget)}
                            >
                              Admin Oversight ({paneOversight.length})
                              <span
                                ref={oversightFoldChevronRef}
                                className={`sc-chevron${oversightOpen ? ' open' : ''}`}
                                aria-hidden="true"
                              >
                                ▾
                              </span>
                            </button>
                            <div
                              ref={oversightFoldBodyRef}
                              className="sc-fold-body open"
                              hidden={!oversightOpen}
                            >
                              <div className="sc-fold-inner">
                            {paneOversight.map((inst, oversightIndex) => {
                              const on = !viewingTemplate && inst.id === instanceId
                              const board = on ? session : inst.session
                              const roster = on ? people : inst.people
                              const byline = sessionCardByline(board, roster, user.userId)
                              const status = lifecycleStatusLabel(inst.lifecycle, inst.session)
                              const when =
                                inst.lifecycle === 'running'
                                  ? liveSessionCardSubtitle(
                                      inst,
                                      board,
                                      inst.actualStartTime ??
                                        inst.studioClock?.actualStartTime ??
                                        null
                                    )
                                  : inst.lifecycle === 'completed'
                                    ? doneSessionCardSubtitle(inst, board)
                                    : plannedSessionCardSubtitle(board, inst.createdAtIso)
                              const meta =
                                inst.lifecycle === 'running' || inst.lifecycle === 'completed'
                                  ? when
                                  : [status, when].filter(Boolean).join(' · ')
                              if (inst.lifecycle === 'running') void livePulse
                              const isOlder = oversightIndex >= RAIL_FULL_CARDS
                              if (isOlder && !on && !oversightShowMore) return null
                              const isSpine =
                                isOlder && (!on || railForcedSpineIds.has(inst.id))
                              const spineWhen =
                                inst.lifecycle === 'running'
                                  ? inst.actualStartTime
                                    ? formatWall(inst.actualStartTime)
                                    : 'Live'
                                  : inst.lifecycle === 'completed'
                                    ? inst.actualStartTime
                                      ? formatWall(inst.actualStartTime)
                                      : 'Done'
                                    : inst.createdAtIso
                                      ? formatWall(inst.createdAtIso)
                                      : status
                              const spineTip = [spineWhen, byline].filter(Boolean).join(' · ')
                              return (
                                <div
                                  key={inst.id}
                                  data-inst-id={inst.id}
                                  className={`sc-inst${sessionRailToneClass(inst)}${on ? ' on' : ''}${
                                    isSpine ? ' is-spine' : ''
                                  }`}
                                >
                                  <div className="sc-inst-top">
                                    <button
                                      type="button"
                                      className="sc-inst-hit"
                                      onClick={() => toggleRailOlderCard(inst.id, isOlder)}
                                    >
                                      <b title={board.title}>
                                        <span className="sc-inst-title-text">{board.title}</span>
                                      </b>
                                      <span
                                        className={`sc-inst-byline${byline ? '' : ' is-empty'}`}
                                        title={byline ?? undefined}
                                      >
                                        {byline || '\u00a0'}
                                      </span>
                                      {isSpine ? (
                                        <span className="sc-inst-meta">
                                          <span
                                            className="sc-inst-meta-text sc-inst-sub"
                                            title={spineTip}
                                          >
                                            {spineWhen}
                                          </span>
                                          {inst.lifecycle === 'running' ? (
                                            <span
                                              className="sc-inst-live-pulse"
                                              aria-hidden="true"
                                              title="Live"
                                            />
                                          ) : null}
                                        </span>
                                      ) : (
                                        <span className="sc-inst-meta">
                                          <span
                                            className="sc-inst-meta-text sc-inst-sub"
                                            title={meta}
                                          >
                                            {meta}
                                          </span>
                                          {inst.lifecycle === 'running' ? (
                                            <span
                                              className="sc-inst-live-pulse"
                                              aria-hidden="true"
                                              title="Live"
                                            />
                                          ) : null}
                                        </span>
                                      )}
                                    </button>
                                    {!isSpine ? (
                                      <div className="sc-inst-tools">
                                        {accessPerms.has('scenarios.edit_board') ? (
                                          <button
                                            type="button"
                                            className="sc-icon-btn"
                                            aria-label="Session settings"
                                            title="Session settings"
                                            onClick={() => openScenarioSettings(inst.id)}
                                          >
                                            <PencilIcon />
                                          </button>
                                        ) : null}
                                        {sessionCloneTool(inst.id, board.title)}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              )
                            })}
                            {paneOversight.length > RAIL_FULL_CARDS ? (
                              <button
                                type="button"
                                className="sc-inst-more"
                                aria-expanded={oversightShowMore}
                                onClick={() => setOversightShowMore((v) => !v)}
                              >
                                {oversightShowMore
                                  ? 'Show fewer'
                                  : `${paneOversight.length - RAIL_FULL_CARDS} more`}
                              </button>
                            ) : null}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {libraryMode && paneTemplates.length === 0 ? (
                          <p className="sc-hint sc-library-empty">
                            No templates are available yet.
                          </p>
                        ) : null}
                        {!libraryMode && paneIsEmpty && !serverNotice ? (
                          <p className="sc-hint sc-library-empty">
                            No sessions yet. Use New session, or browse Templates for a starting
                            board.
                          </p>
                        ) : null}
                        {libraryMode && previewTemplateId && !hasOwnSession ? (
                          <p className="sc-hint sc-library-empty">
                            Use the{' '}
                            <span className="sc-hint-inline-ico" aria-hidden="true">
                              <NavMdiIcon path={mdiContentCopy} sizePx={13} />
                            </span>{' '}
                            to create a session from this template.
                          </p>
                        ) : null}
                        {libraryMode
                          ? templateGroups.map((group) => (
                              <div key={group.key} className="sc-inst-group">
                                {group.label ? (
                                  <div className="sc-inst-group-label">{group.label}</div>
                                ) : null}
                                <div className="sc-canned">
                                  {group.items.map((t) => {
                                    const on = previewTemplateId === t.id
                                    const versionCount = t.session.boardVersions?.length ?? 0
                                    const cloning = cloningTemplateId === t.id
                                    return (
                                      <div
                                        key={t.id}
                                        className={`sc-inst sc-inst-template${on ? ' on' : ''}${
                                          cloning ? ' is-cloning' : ''
                                        }`}
                                      >
                                        {cloning ? (
                                          <div
                                            className="sc-inst-clone-veil"
                                            role="status"
                                            aria-live="polite"
                                          >
                                            <span className="sc-boot-spin" aria-hidden="true" />
                                            Creating session…
                                          </div>
                                        ) : null}
                                        <div className="sc-inst-top">
                                          <button
                                            type="button"
                                            className="sc-inst-hit"
                                            disabled={Boolean(cloningTemplateId)}
                                            onClick={() =>
                                              selectTemplate(t.id, undefined, libraryMode)
                                            }
                                          >
                                            <b title={t.title}>
                                              <span className="sc-inst-title-text">{t.title}</span>
                                              {showVersionChrome && versionCount > 1 ? (
                                                <span
                                                  className="sc-inst-ver-count"
                                                  title={`${versionCount} versions in this template`}
                                                >
                                                  {versionCount}
                                                </span>
                                              ) : null}
                                            </b>
                                            <span
                                              className="sc-inst-sub"
                                              title={sessionCardSubtitle(
                                                t.durationMs > 0
                                                  ? formatDuration(t.durationMs)
                                                  : '',
                                                t.session.sessionType,
                                                yuccaElmCardLead(t.id) ??
                                                  yuccaElmCardLead(t.session.id)
                                              )}
                                            >
                                              {sessionCardSubtitle(
                                                t.durationMs > 0
                                                  ? formatDuration(t.durationMs)
                                                  : '',
                                                t.session.sessionType,
                                                yuccaElmCardLead(t.id) ??
                                                  yuccaElmCardLead(t.session.id)
                                              )}
                                            </span>
                                          </button>
                                          <div className="sc-inst-tools">
                                            {showPdfExports ? (
                                            <button
                                              type="button"
                                              className="sc-icon-btn"
                                              aria-label={`Export plan for ${t.title}`}
                                              title="Export plan for this template"
                                              onClick={() =>
                                                void downloadSessionPlanPdf(
                                                  cloneSession(t.session),
                                                  [],
                                                  {
                                                    fromTemplate: true,
                                                  }
                                                )
                                              }
                                            >
                                              <PdfIcon />
                                            </button>
                                            ) : null}
                                            {accessPerms.has('scenarios.create') ? (
                                              <button
                                                type="button"
                                                className={`sc-icon-btn${cloning ? ' is-busy' : ''}`}
                                                aria-label={`Copy ${t.title}`}
                                                title={
                                                  hubActionsLocked
                                                    ? hubLockTitle
                                                    : cloning
                                                      ? 'Creating your session…'
                                                      : 'Create a session from this template'
                                                }
                                                aria-busy={cloning || undefined}
                                                disabled={
                                                  hubActionsLocked || Boolean(cloningTemplateId)
                                                }
                                                onClick={() => void useTemplate(t.id)}
                                              >
                                                <NavMdiIcon path={mdiContentCopy} sizePx={14} />
                                              </button>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            ))
                          : null}
                      </>
                    </div>
                    </>
                  )}
                </>
              ) : null}

              {dock === 'timeline' ? (
                <>
                  <div className="sc-rhead">
                    <PaneEyebrow
                      label="Timeline"
                      onEggClick={canDemoRoleOverride ? () => setEggsOn((on) => !on) : undefined}
                      trailing={
                        canEditBoard && showBoardChrome && showTimelineEdit ? (
                          <button
                            type="button"
                            className={`sc-tl-edit-toggle${timelineEditMode ? ' is-on' : ''}`}
                            aria-pressed={timelineEditMode}
                            title={
                              timelineEditMode
                                ? 'Finish editing story cards'
                                : 'Edit story cards — add narration between cards'
                            }
                            onClick={() => {
                              setTimelineEditMode((on) => {
                                if (on) setNarrationDraftId(null)
                                return !on
                              })
                            }}
                          >
                            {timelineEditMode ? 'Done' : 'Edit'}
                          </button>
                        ) : null
                      }
                    />
                  </div>
                  {serverBooting ? (
                    <div className="sc-boot" role="status" aria-live="polite">
                      <span className="sc-boot-spin" aria-hidden="true" />
                      Loading
                    </div>
                  ) : bootError ? (
                    <p className="sc-hint sc-library-empty" role="status">
                      {isStudioUnreachableNotice(bootError)
                        ? STUDIO_UNREACHABLE_RAIL
                        : bootError}
                    </p>
                  ) : (
                  <div
                    className="sc-rscroll"
                    ref={timelineScrollRef}
                    onScroll={() => {
                      setCamHoverId(null)
                      setCamMenuSuppressId(null)
                    }}
                  >
                    {(() => {
                      const overviewRaw = (
                        selectedVersion?.summary?.trim() ||
                        viewSession.summary ||
                        ''
                      ).trim()
                      const blankOverview = /^Blank board\./i.test(overviewRaw)
                      // Collapse canned blank summary with the empty-timeline hint.
                      if (overviewRaw && !(blankOverview && displayTimeline.length === 0)) {
                        return <p className="sc-overview">{overviewRaw}</p>
                      }
                      return null
                    })()}
                    {(() => {
                      const canShowAdd =
                        timelineEditMode &&
                        canEditBoard &&
                        !displayTimeline.some(isPristineNarrationBeat)
                      const beatAddSlot = (afterIndex: number, label: string) => (
                        <div key={`tl-add-${afterIndex}-${label}`} className="sc-tl-add-slot">
                          <button
                            type="button"
                            className="sc-tl-add-beat"
                            onClick={() => insertNarrationBeat(afterIndex)}
                          >
                            <span className="sc-tl-add-plus" aria-hidden="true">
                              +
                            </span>
                            <span>{label}</span>
                          </button>
                        </div>
                      )
                      const insertSlot = (afterIndex: number) =>
                        beatAddSlot(afterIndex, 'Insert story card')
                      const addSlot = (afterIndex: number) =>
                        beatAddSlot(afterIndex, 'Add story card')
                      if (displayTimeline.length === 0) {
                        return (
                          <div className="sc-tl-empty">
                            <p className="sc-hint">
                              Place objects on the map with times to build the story spine.
                            </p>
                            {canShowAdd ? (
                              <>
                                <p className="sc-hint">
                                  Add a story card for narrative glue that isn't tied to an
                                  object.
                                </p>
                                {addSlot(-1)}
                              </>
                            ) : null}
                          </div>
                        )
                      }
                      return (
                      <div
                        className={`sc-tl${timelineEditMode ? ' is-editing' : ''}`}
                        onMouseLeave={() => {
                          setHoverIds([])
                          setHoverElapsedMs(null)
                        }}
                      >
                        {(() => {
                          const clusters: (typeof displayTimeline)[] = []
                          for (const ev of displayTimeline) {
                            const last = clusters[clusters.length - 1]
                            if (last && off(last[0]!.at) === off(ev.at)) last.push(ev)
                            else clusters.push([ev])
                          }
                          let flatIdx = 0
                          const clusterBlocks = clusters.map((cluster) => {
                            const groupCurrent =
                              currentAtMs != null &&
                              cluster.length > 1 &&
                              off(cluster[0]!.at) === currentAtMs
                            const rows = cluster.map((ev) => {
                              const idx = flatIdx++
                              const fired = viewElapsedMs >= off(ev.at)
                              const isCurrent =
                                !groupCurrent && currentAtMs != null && off(ev.at) === currentAtMs
                              const isScrollTarget = timelineScrollId === ev.id
                              const hideTitle =
                                accountBoardMode === 'following' && ev.hiddenFromParticipants && !fired
                              const relatedIds = eventObjectIds(ev.actions)
                              const inspectId = relatedIds.find((id) =>
                                viewSession.objects.some((o) => o.id === id)
                              )
                              const picked = selectedId != null && relatedIds.includes(selectedId)
                              const ico = ev.iconType ?? 'label'
                              const showTime =
                                idx === 0 || off(displayTimeline[idx - 1]!.at) !== off(ev.at)
                              const titleText = hideTitle
                                ? 'Scheduled inject'
                                : ev.id === 'ev-start'
                                  ? 'Scenario start'
                                  : ev.title
                              const isNarration = isNarrationTimelineEvent(ev)
                              const syncObjectId = isSyncedTimelineEvent(ev)
                                ? ev.actions.find((a) => a.kind === 'reveal')?.objectId
                                : undefined
                              const beatEditable =
                                timelineEditMode &&
                                canEditBoard &&
                                (isNarration || Boolean(syncObjectId))
                              // Sync: object type icon. Narration: muted story glyph (fills rail;
                              // same weight as ⓘ — not a bright type color).
                              const displayIco = isNarration ? 'story_beat' : ico
                              const syncObj = syncObjectId
                                ? viewSession.objects.find((o) => o.id === syncObjectId)
                                : undefined
                              const rail = (
                                <span className="sc-tl-rail">
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    className={`sc-tl-ico${isNarration ? ' is-story' : ''}`}
                                    style={
                                      isNarration
                                        ? undefined
                                        : { color: paletteColor(displayIco) }
                                    }
                                    title={paletteLabel(displayIco)}
                                    aria-label={paletteLabel(displayIco)}
                                  >
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: iconSvg(displayIco),
                                      }}
                                    />
                                  </button>
                                  {/* Narration beats own multi-target effects — no single-object ⓘ. */}
                                  {inspectId && !isNarration ? (
                                    <button
                                      type="button"
                                      className="sc-tl-inspect"
                                      title={`Inspect ${titleText}`}
                                      aria-label={`Inspect ${titleText}`}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        inspectFromTimeline(ev.id, relatedIds)
                                        if (canScrub) {
                                          jumpTo(off(ev.at), { keepTimelineFocus: true })
                                        }
                                      }}
                                    >
                                      <svg
                                        viewBox="0 0 16 16"
                                        width="14"
                                        height="14"
                                        aria-hidden="true"
                                      >
                                        <circle
                                          cx="8"
                                          cy="8"
                                          r="6.25"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                        />
                                        <circle cx="8" cy="5.25" r="1" fill="currentColor" />
                                        <path
                                          d="M8 7.25v4"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </button>
                                  ) : (
                                    <span className="sc-tl-inspect-spacer" aria-hidden="true" />
                                  )}
                                  {/* Camera chrome stays out of edit mode — layer shots after the spine. */}
                                  {!timelineEditMode
                                    ? (() => {
                                        const camBeat = cameraBeatNear(
                                          viewSession.cameraBeats ?? [],
                                          ev.at
                                        )
                                        const camOn =
                                          camBeat != null &&
                                          (camBeat.eventId
                                            ? camBeat.eventId === ev.id
                                            : !isSyncedTimelineEvent(ev))
                                        if (!canEditBoard && !camOn) {
                                          return (
                                            <span
                                              className="sc-tl-inspect-spacer"
                                              aria-hidden="true"
                                            />
                                          )
                                        }
                                        const menuOpen =
                                          camHoverId === ev.id && camMenuSuppressId !== ev.id
                                        return (
                                          <span
                                            className={`sc-tl-cam-wrap${camOn ? ' set' : ''}${
                                              menuOpen ? ' open' : ''
                                            }`}
                                            onMouseEnter={() => openCamMenu(ev.id)}
                                            onMouseLeave={() => scheduleCloseCamMenu(ev.id)}
                                          >
                                            <button
                                              type="button"
                                              className={`sc-tl-cam${camOn ? ' on' : ''}`}
                                              aria-label={`Camera options for ${titleText}`}
                                              aria-expanded={menuOpen}
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                openCamMenu(ev.id)
                                              }}
                                            >
                                              <svg
                                                viewBox="0 0 16 16"
                                                width="14"
                                                height="14"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  d="M2.5 5.5h2l1-1.5h5l1 1.5h2A1.5 1.5 0 0115 7v5.5A1.5 1.5 0 0113.5 14h-11A1.5 1.5 0 011 12.5V7A1.5 1.5 0 012.5 5.5z"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="1.4"
                                                  strokeLinejoin="round"
                                                />
                                                <circle
                                                  cx="8"
                                                  cy="9.5"
                                                  r="2.35"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="1.4"
                                                />
                                              </svg>
                                            </button>
                                            {menuOpen ? (
                                              <span
                                                className="sc-tl-cam-menu"
                                                role="menu"
                                                onMouseEnter={() => openCamMenu(ev.id)}
                                                onMouseLeave={() => scheduleCloseCamMenu(ev.id)}
                                              >
                                                {canEditBoard ? (
                                                  <button
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={(e) => {
                                                      e.stopPropagation()
                                                      stampCameraAt(off(ev.at), ev.id)
                                                    }}
                                                  >
                                                    Save camera
                                                  </button>
                                                ) : null}
                                                {camOn && camBeat ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      role="menuitem"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        previewCameraBeat(camBeat, ev.id)
                                                      }}
                                                    >
                                                      Preview camera
                                                    </button>
                                                    {canEditBoard ? (
                                                      <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="danger"
                                                        onClick={(e) => {
                                                          e.stopPropagation()
                                                          removeCameraAt(off(ev.at), ev.id)
                                                        }}
                                                      >
                                                        Remove
                                                      </button>
                                                    ) : null}
                                                  </>
                                                ) : null}
                                              </span>
                                            ) : null}
                                          </span>
                                        )
                                      })()
                                    : null}
                                </span>
                              )
                              return (
                                <Fragment key={ev.id}>
                                  <div
                                    ref={isScrollTarget ? currentRowRef : undefined}
                                    className={`sc-tl-row${fired ? ' fired' : ''}${isCurrent ? ' current' : ''}${
                                      timelineFocusId === ev.id ? ' hot' : ''
                                    }${picked ? ' picked' : ''}${
                                      beatEditable ? ' is-narration-edit' : ''
                                    }${narrationDraftId === ev.id ? ' is-draft' : ''}`}
                                    onMouseDown={() => {
                                      if (beatEditable) {
                                        focusTimelineBeat(ev.id, ev.at, relatedIds)
                                      }
                                    }}
                                    onMouseEnter={() => {
                                      if (beatEditable) return
                                      // Map hover-preview works while scrubbing or paused; during
                                      // live Preview the clock owns map highlight instead.
                                      if (running) return
                                      setHoverIds(relatedIds)
                                      if (canScrub) {
                                        // Drop Save-camera soft-hold so hover can show this card's beat.
                                        setCamEditHoldAtMs(null)
                                        setHoverElapsedMs(off(ev.at))
                                        setCameraFollowApplyEpoch((n) => n + 1)
                                      }
                                    }}
                                  >
                                    {isNarration && beatEditable ? (
                                      <button
                                        type="button"
                                        className="sc-tl-beat-x-float"
                                        title="Delete story card"
                                        aria-label={`Delete ${titleText}`}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          removeNarrationBeat(ev.id)
                                        }}
                                      >
                                        ×
                                      </button>
                                    ) : null}
                                    {beatEditable ? (
                                      <div className="sc-tl-main sc-tl-narration-form">
                                        <span className="sc-tl-body">
                                          <div className="sc-when-zone sc-tl-narration-when">
                                            <StoryWhenInput
                                              id={`sc-tl-beat-${ev.id}`}
                                              iso={ev.at}
                                              dateSet={
                                                viewSession.storyStartDateSet !== false ||
                                                Boolean(viewSession.storyEndTime)
                                              }
                                              timeZone={viewSession.timezone}
                                              withSeconds={
                                                (viewSession.timelineTimeFormat ?? 'hms') === 'hms'
                                              }
                                              flash={fieldFlashKey === `tl-when:${ev.id}`}
                                              onCommit={(value) => {
                                                if (!value) return 'Time is required'
                                                if (isNarration) {
                                                  updateNarrationBeat(ev.id, { at: value.iso })
                                                } else if (syncObjectId) {
                                                  updateSyncedBeatObject(syncObjectId, {
                                                    at: value.iso,
                                                  })
                                                }
                                                flashField(`tl-when:${ev.id}`)
                                                return null
                                              }}
                                            />
                                          </div>
                                          <NarrationBeatCopyFields
                                            eventId={ev.id}
                                            title={
                                              syncObj
                                                ? syncObj.label.trim() || ev.title
                                                : ev.title
                                            }
                                            summary={
                                              syncObj
                                                ? syncObj.description?.trim() ||
                                                  objectTypeMeta(syncObj.type).descriptionTemplate ||
                                                  ''
                                                : ev.summary
                                            }
                                            summaryPlaceholder="Description"
                                            autoFocus={narrationDraftId === ev.id}
                                            onCommitTitle={(title) => {
                                              if (isNarration) {
                                                updateNarrationBeat(ev.id, { title })
                                              } else if (syncObjectId) {
                                                updateSyncedBeatObject(syncObjectId, { title })
                                              }
                                            }}
                                            onCommitSummary={(summary) => {
                                              if (isNarration) {
                                                updateNarrationBeat(ev.id, { summary })
                                              } else if (syncObjectId) {
                                                updateSyncedBeatObject(syncObjectId, { summary })
                                              }
                                            }}
                                            onFocus={() =>
                                              focusTimelineBeat(ev.id, ev.at, relatedIds)
                                            }
                                          />
                                          {isNarration ? (
                                            <BeatEffectsEditor
                                              eventId={ev.id}
                                              actions={ev.actions ?? []}
                                              objects={viewSession.objects}
                                              preferredObjectId={selectedId}
                                              displayUnits={viewSession.displayUnits ?? 'imperial'}
                                              disabled={!canEditBoard}
                                              onCommitActions={(nextActions) =>
                                                updateNarrationBeat(ev.id, {
                                                  actions: nextActions,
                                                })
                                              }
                                              onFocus={() =>
                                                focusTimelineBeat(ev.id, ev.at, relatedIds)
                                              }
                                            />
                                          ) : null}
                                        </span>
                                        {rail}
                                      </div>
                                    ) : (
                                      <div
                                        className="sc-tl-main"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => {
                                          if (running) {
                                            setTimelineFocusId(null)
                                            if (canScrub) jumpTo(off(ev.at))
                                            return
                                          }
                                          jumpFromTimeline(ev.id, relatedIds)
                                          if (canScrub) {
                                            jumpTo(off(ev.at), { keepTimelineFocus: true })
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key !== 'Enter' && e.key !== ' ') return
                                          e.preventDefault()
                                          jumpFromTimeline(ev.id, relatedIds)
                                          if (canScrub) {
                                            jumpTo(off(ev.at), { keepTimelineFocus: true })
                                          }
                                        }}
                                      >
                                        <span
                                          className={`sc-tl-time${showTime ? '' : ' sc-tl-time-blank'}`}
                                          aria-hidden={!showTime}
                                        >
                                          {formatTimelineStamp(
                                            ev.at,
                                            viewSession.timezone,
                                            (viewSession.timelineTimeFormat ?? 'hms') === 'hms',
                                            false
                                          )}
                                        </span>
                                        <span className="sc-tl-body">
                                          <span className="sc-tl-title">
                                            <span className="sc-tl-title-text">{titleText}</span>
                                          </span>
                                          {!hideTitle ? (
                                            (() => {
                                              const blurb =
                                                ev.id === 'ev-start'
                                                  ? (session.intro ?? ev.summary)
                                                  : ev.summary
                                              const text = blurb?.trim()
                                              return text ? <small>{text}</small> : null
                                            })()
                                          ) : (
                                            <small>Watch the clock</small>
                                          )}
                                        </span>
                                        {rail}
                                      </div>
                                    )}
                                  </div>
                                  {canShowAdd && idx < displayTimeline.length - 1
                                    ? insertSlot(idx)
                                    : null}
                                  {canShowAdd && idx === displayTimeline.length - 1
                                    ? addSlot(idx)
                                    : null}
                                </Fragment>
                              )
                            })
                            if (groupCurrent) {
                              return (
                                <div key={`tl-g-${cluster[0]!.id}`} className="sc-tl-group current">
                                  {rows}
                                </div>
                              )
                            }
                            return <Fragment key={cluster[0]!.id}>{rows}</Fragment>
                          })
                          return (
                            <>
                              {canShowAdd ? insertSlot(-1) : null}
                              {clusterBlocks}
                            </>
                          )
                        })()}
                      </div>
                      )
                    })()}
                  </div>
                  )}
                </>
              ) : null}

              {dock === 'inspector' ? (
                <>
                  <div className={`sc-rhead${inspectTitle === 'Inspect' ? '' : ' sc-rhead-split'}`}>
                    <PaneEyebrow label="Inspect" />
                    {inspectTitle === 'Inspect' ? null : (
                      <h2
                        className={selectedSource ? 'sc-insp-pane-title' : undefined}
                        style={
                          selectedSource ? { color: paletteColor(selectedSource.type) } : undefined
                        }
                      >
                        {selectedSource ? (
                          <>
                            <span
                              className="sc-type-ico"
                              aria-hidden="true"
                              dangerouslySetInnerHTML={{
                                __html: iconSvg(
                                  selectedSource.type === 'sketch' &&
                                    selectedSource.params.strokeKind === 'line'
                                    ? 'pen'
                                    : selectedSource.type
                                ),
                              }}
                            />
                            {titleEditing && editing ? (
                              <input
                                className={`sc-insp-title-input${fieldFlashKey === `label:${selectedSource.id}` ? ' sc-field-flash' : ''}`}
                                value={selectedSource.label}
                                autoFocus
                                aria-label="Object title"
                                onChange={(e) => updateSelectedLabel(e.target.value)}
                                onBlur={(e) => {
                                  setTitleEditing(false)
                                  if (e.target.value !== labelFocusValueRef.current) {
                                    flashField(`label:${selectedSource.id}`)
                                  }
                                }}
                                onFocus={(e) => {
                                  labelFocusValueRef.current = e.target.value
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    ;(e.target as HTMLInputElement).blur()
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault()
                                    updateSelectedLabel(labelFocusValueRef.current)
                                    setTitleEditing(false)
                                  }
                                }}
                              />
                            ) : (
                              <>
                                <span
                                  className={`sc-insp-title-text${fieldFlashKey === `label:${selectedSource.id}` ? ' sc-title-flash' : ''}`}
                                >
                                  {inspectTitle}
                                </span>
                                {editing ? (
                                  <button
                                    type="button"
                                    className="sc-insp-title-edit"
                                    aria-label="Edit title"
                                    title="Edit title"
                                    disabled={!canMutateSelected}
                                    onClick={() => setTitleEditing(true)}
                                  >
                                    <PencilIcon />
                                  </button>
                                ) : null}
                              </>
                            )}
                          </>
                        ) : (
                          inspectTitle
                        )}
                      </h2>
                    )}
                  </div>
                  <div className="sc-rscroll">
                    {placeType === 'type' && canEditBoard ? (
                      <>
                        <div
                          className="sc-type-preview"
                          style={{
                            color: labelStyle.color,
                            fontSize: TYPE_SIZE_PX[labelStyle.size],
                          }}
                        >
                          {labelStyle.text.trim() ? labelStyle.text : 'Preview text'}
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Preview text</span>
                          <textarea
                            className="sc-fin"
                            rows={3}
                            value={labelStyle.text}
                            onChange={(e) =>
                              setLabelStyle((s) => ({
                                ...s,
                                text: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Color</span>
                          <SwatchRow
                            colors={TYPE_COLORS}
                            value={labelStyle.color}
                            onPick={(color) => setLabelStyle((s) => ({ ...s, color }))}
                          />
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Size</span>
                          <div className="sc-seg">
                            {(['sm', 'md', 'lg', 'xl'] as const).map((sz) => (
                              <button
                                key={sz}
                                type="button"
                                className={labelStyle.size === sz ? 'on' : ''}
                                onClick={() => setLabelStyle((s) => ({ ...s, size: sz }))}
                              >
                                {TYPE_SIZE_LABEL[sz]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="sc-hint">Click the map to place this label.</p>
                      </>
                    ) : (placeType === 'freehand' || placeType === 'pen') && canEditBoard ? (
                      <>
                        <StrokeFields
                          labelInputId="sc-stroke-label"
                          label={sketchStyle.label}
                          color={sketchStyle.color}
                          width={sketchStyle.width}
                          opacity={sketchStyle.opacity}
                          onLabel={(label) => setSketchStyle((s) => ({ ...s, label }))}
                          onColor={(color) => setSketchStyle((s) => ({ ...s, color }))}
                          onWidth={(width) => setSketchStyle((s) => ({ ...s, width }))}
                          onOpacity={(opacity) => setSketchStyle((s) => ({ ...s, opacity }))}
                        />
                        <p className="sc-hint">
                          {placeType === 'pen'
                            ? 'Set the look, then click to add vertices. Double-click to finish. Select the line afterward to change it.'
                            : 'Set the look, then hold and drag. Lift to commit. Select the sketch afterward to change it.'}
                        </p>
                        {placeType === 'freehand' &&
                        viewSession.objects.some(
                          (o) => o.type === 'sketch' && o.params.strokeKind !== 'line'
                        ) ? (
                          <button
                            type="button"
                            className="sc-btn rec"
                            onClick={() =>
                              commitSession((prev) => ({
                                ...prev,
                                objects: prev.objects.filter(
                                  (o) => o.type !== 'sketch' || o.params.strokeKind === 'line'
                                ),
                              }))
                            }
                          >
                            Clear all sketches
                          </button>
                        ) : null}
                      </>
                    ) : placeType === 'ruler' && canEditBoard ? (
                      <>
                        <div className="sc-ruler-stats">
                          <div className="sc-pipe-row">
                            <span>Points</span>
                            <b>{rulerStats?.points ?? 0}</b>
                          </div>
                          <div className="sc-pipe-row">
                            <span>Total distance</span>
                            <b>
                              {rulerStats && rulerStats.points > 1
                                ? formatDistanceM(rulerStats.totalM, viewSession.displayUnits)
                                : '—'}
                            </b>
                          </div>
                          <div className="sc-pipe-row">
                            <span>Last segment</span>
                            <b>
                              {rulerStats && rulerStats.points > 1
                                ? formatDistanceM(rulerStats.lastM, viewSession.displayUnits)
                                : '—'}
                            </b>
                          </div>
                        </div>
                        <p className="sc-hint">
                          Click the map to measure. Esc clears. Not saved to the board.
                        </p>
                        <button
                          type="button"
                          className="sc-btn"
                          disabled={!rulerStats?.points}
                          onClick={() => {
                            setRulerStats(null)
                            setPlaceType(null)
                            window.setTimeout(() => setPlaceType('ruler'), 0)
                          }}
                        >
                          Clear rule
                        </button>
                      </>
                    ) : selectedSource ? (
                      <div className={`sc-insp-object${minimalInspect ? ' is-minimal' : ''}`}>
                        {selectedSource.scope === 'global' ? (
                          <p className="sc-hint sc-insp-scope-hint">Applies to the whole board.</p>
                        ) : null}
                        <p className="sc-overview">{objectTypeMeta(selectedSource.type).blurb}</p>
                        {historicRunLocked ? (
                          <p className="sc-hint sc-insp-scope-hint">
                            This run is read-only. Exit review to edit the board for the next take.
                          </p>
                        ) : null}
                        {selectedSource.type === 'sketch' ? (
                          <StrokeFields
                            labelInputId="sc-stroke-edit-label"
                            label={selectedSource.label}
                            color={String(selectedSource.params.color ?? '#E24B4A')}
                            width={Number(selectedSource.params.width) || 4}
                            opacity={Number(selectedSource.params.opacity) || 0.9}
                            disabled={!canEditBoard}
                            onLabel={(label) => patchSelectedObject((o) => ({ ...o, label }))}
                            onColor={(color) =>
                              patchSelectedObject((o) => ({
                                ...o,
                                params: { ...o.params, color },
                              }))
                            }
                            onWidth={(width) =>
                              patchSelectedObject((o) => ({
                                ...o,
                                params: { ...o.params, width },
                              }))
                            }
                            onOpacity={(opacity) =>
                              patchSelectedObject((o) => ({
                                ...o,
                                params: { ...o.params, opacity },
                              }))
                            }
                          />
                        ) : null}
                        {selectedSource.type !== 'label' &&
                        selectedSource.type !== 'sketch' &&
                        selectedSource.type !== 'wind' ? (
                          <div className="sc-field">
                            <span className="sc-flbl">Map label</span>
                            {editing ? (
                              <input
                                className={`sc-fin${fieldFlashKey === `mapLabel:${selectedSource.id}` ? ' sc-field-flash' : ''}`}
                                value={selectedSource.mapLabel ?? ''}
                                placeholder="Optional caption under icon"
                                maxLength={MAP_LABEL_MAX_CHARS}
                                disabled={!canEditBoard}
                                onFocus={(e) => {
                                  clearPathEditFocus()
                                  mapLabelFocusValueRef.current = e.target.value
                                }}
                                onChange={(e) => {
                                  const next = e.target.value.slice(0, MAP_LABEL_MAX_CHARS)
                                  patchSelectedObject((o) => {
                                    const copy = { ...o }
                                    if (!next.trim()) delete copy.mapLabel
                                    else copy.mapLabel = next
                                    return copy
                                  })
                                }}
                                onBlur={(e) => {
                                  if (e.target.value !== mapLabelFocusValueRef.current) {
                                    flashField(`mapLabel:${selectedSource.id}`)
                                  }
                                }}
                              />
                            ) : (
                              <div className="sc-fval">
                                {selectedSource.mapLabel?.trim() || 'None'}
                              </div>
                            )}
                          </div>
                        ) : null}
                        <div className="sc-field sc-keep">
                          <div className="sc-title-row">
                            <span className="sc-flbl">Description</span>
                          </div>
                          {editing ? (
                            <textarea
                              className={`sc-fin${fieldFlashKey === `desc:${selectedSource.id}` ? ' sc-field-flash' : ''}`}
                              value={descDraft}
                              disabled={!canMutateSelected}
                              onChange={(e) => setDescDraft(e.target.value)}
                              onFocus={() => clearPathEditFocus()}
                              onBlur={() => {
                                saveDescription()
                              }}
                              placeholder={
                                isBrwnPlumeType(selectedSource.type)
                                  ? defaultObjectDescription(selectedSource, {
                                      firstName:
                                        profileFieldsFromBlob(readRegistrationProfileJson())
                                          .first ||
                                        (user.name ?? '').split(/\s+/)[0] ||
                                        '',
                                      displayUnits: viewSession.displayUnits,
                                    })
                                  : objectTypeMeta(selectedSource.type).descriptionTemplate ||
                                    'Description'
                              }
                            />
                          ) : (
                            <div className="sc-fval">
                              {selectedSource && isBrwnPlumeType(selectedSource.type)
                                ? defaultObjectDescription(selectedSource, {
                                    firstName:
                                      profileFieldsFromBlob(readRegistrationProfileJson()).first ||
                                      (user.name ?? '').split(/\s+/)[0] ||
                                      '',
                                    displayUnits: viewSession.displayUnits,
                                  })
                                : selectedSource.description?.trim() ||
                                  defaultObjectDescription(selectedSource, {
                                    firstName:
                                      profileFieldsFromBlob(readRegistrationProfileJson()).first ||
                                      (user.name ?? '').split(/\s+/)[0] ||
                                      '',
                                    displayUnits: viewSession.displayUnits,
                                  })}
                            </div>
                          )}
                        </div>
                        <label
                          className="sc-check sc-timeline-toggle sc-insp-timeline-toggle"
                          title="When off, keep the object for later without deleting it — it will not appear in play."
                        >
                          <input
                            type="checkbox"
                            checked={!selectedSource.ignored}
                            disabled={!canEditBoard}
                            onChange={(e) =>
                              patchSelectedObject((o) => {
                                const next = { ...o }
                                if (e.target.checked) delete next.ignored
                                else next.ignored = true
                                return next
                              })
                            }
                          />
                          Active
                          {selectedSource.ignored ? (
                            <span className="sc-insp-active-note">Hidden until turned on.</span>
                          ) : null}
                        </label>
                        {sessionOffersStoryClock &&
                        showTimelineAuthoring &&
                        !selectedSource.ignored ? (
                          <label className="sc-check sc-timeline-toggle sc-insp-timeline-toggle">
                            <input
                              type="checkbox"
                              checked={objectShowsOnTimeline(
                                selectedSource.type,
                                selectedSource.showOnTimeline
                              )}
                              disabled={!canEditBoard}
                              onChange={(e) =>
                                patchSelectedObject((o) => ({
                                  ...o,
                                  showOnTimeline: e.target.checked,
                                }))
                              }
                            />
                            Include as story card on timeline
                          </label>
                        ) : null}
                        <hr className="sc-insp-identity-rule" />
                        {selectedSource.type === 'label' ? (
                          <div className="sc-field">
                            <span className="sc-flbl">Map text</span>
                            {editing ? (
                              <textarea
                                className="sc-fin"
                                rows={3}
                                disabled={!canMutateSelected}
                                value={String(selectedSource.params.text ?? selectedSource.label)}
                                onChange={(e) => updateMapText(e.target.value)}
                              />
                            ) : (
                              <div className="sc-fval" style={{ whiteSpace: 'pre-line' }}>
                                {String(selectedSource.params.text ?? selectedSource.label)}
                              </div>
                            )}
                            <div
                              className="sc-type-preview"
                              style={{
                                color: String(selectedSource.params.color ?? '#ffffff'),
                                fontSize: TYPE_SIZE_PX[selectedSource.size] ?? 17,
                              }}
                            >
                              {String(selectedSource.params.text ?? selectedSource.label)}
                            </div>
                            <span className="sc-flbl">Color</span>
                            <SwatchRow
                              colors={TYPE_COLORS}
                              value={String(selectedSource.params.color ?? '#ffffff')}
                              disabled={!canMutateSelected}
                              onPick={(color) =>
                                patchSelectedObject((o) => ({
                                  ...o,
                                  params: { ...o.params, color },
                                }))
                              }
                            />
                            <span className="sc-flbl">Size</span>
                            <div className="sc-seg">
                              {(['sm', 'md', 'lg', 'xl'] as const).map((sz) => (
                                <button
                                  key={sz}
                                  type="button"
                                  className={selectedSource.size === sz ? 'on' : ''}
                                  disabled={!canMutateSelected}
                                  onClick={() =>
                                    patchSelectedObject((o) => ({
                                      ...o,
                                      size: sz,
                                    }))
                                  }
                                >
                                  {TYPE_SIZE_LABEL[sz]}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {selectedSource.pose && !sessionOffersStoryClock ? (
                          <div className="sc-field">
                            <div className="sc-title-row">
                              <span className="sc-flbl">Coordinates</span>
                            </div>
                            <div className="sc-coord">
                              <label>
                                Lat
                                <CoordInput
                                  value={selectedSource.pose.lat}
                                  ariaLabel="Lat"
                                  flash={fieldFlashKey === `pose-lat:${selectedSource.id}`}
                                  disabled={!canEditBoard}
                                  onFocus={clearPathEditFocus}
                                  onCommit={(lat) => {
                                    if (!selectedId || lat === selectedSource.pose!.lat) return
                                    moveObject(selectedId, selectedSource.pose!.lng, lat, 'pose')
                                    flashField(`pose-lat:${selectedSource.id}`)
                                  }}
                                />
                              </label>
                              <label>
                                Long
                                <CoordInput
                                  value={selectedSource.pose.lng}
                                  ariaLabel="Long"
                                  flash={fieldFlashKey === `pose-lng:${selectedSource.id}`}
                                  disabled={!canEditBoard}
                                  onFocus={clearPathEditFocus}
                                  onCommit={(lng) => {
                                    if (!selectedId || lng === selectedSource.pose!.lng) return
                                    moveObject(selectedId, lng, selectedSource.pose!.lat, 'pose')
                                    flashField(`pose-lng:${selectedSource.id}`)
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        ) : null}
                        {sessionOffersStoryClock &&
                        showOnBoardFields &&
                        !isLocalStorm(selectedSource.type) &&
                        hasVisibilityWindow(selectedSource.type) &&
                        !selectedSource.visibilityKeyframes?.length ? (
                          <div className="sc-field sc-keep">
                            <span className="sc-flbl">Show on board</span>
                            {editing ? (
                              <div className="sc-show-hide-row">
                                <div className="sc-show-hide-col">
                                  <span className="sc-flbl sc-flbl-sub">From</span>
                                  <ElapsedInput
                                    valueIso={selectedSource.startsAt ?? null}
                                    allowEmpty
                                    disabled={!canMutateSelected}
                                    flash={fieldFlashKey === `appears:${selectedSource.id}`}
                                    clock={authorClock}
                                    onFocusRow={() => clearPathEditFocus()}
                                    onCommit={(ms) => {
                                      patchSelectedObject((o) => {
                                        const next = { ...o }
                                        if (ms == null) delete next.startsAt
                                        else next.startsAt = ms
                                        return next
                                      })
                                      flashField(`appears:${selectedSource.id}`)
                                    }}
                                  />
                                </div>
                                <div className="sc-show-hide-col">
                                  <span className="sc-flbl sc-flbl-sub">Until</span>
                                  <ElapsedInput
                                    valueIso={selectedSource.endsAt ?? null}
                                    allowEmpty
                                    disabled={!canMutateSelected}
                                    flash={fieldFlashKey === `hides:${selectedSource.id}`}
                                    clock={authorClock}
                                    onFocusRow={() => clearPathEditFocus()}
                                    onCommit={(ms) => {
                                      patchSelectedObject((o) => {
                                        const next = { ...o }
                                        if (ms == null) delete next.endsAt
                                        else next.endsAt = ms
                                        return next
                                      })
                                      flashField(`hides:${selectedSource.id}`)
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="sc-fval">
                                {(() => {
                                  const show = selectedSource.startsAt
                                  const hide = selectedSource.endsAt
                                  if (show == null && hide == null) return 'Always'
                                  const showLbl =
                                    show != null ? formatAuthorTime(show, authorClock) : 'start'
                                  const hideLbl =
                                    hide != null ? formatAuthorTime(hide, authorClock) : null
                                  return hideLbl ? `${showLbl} → ${hideLbl}` : `From ${showLbl}`
                                })()}
                              </div>
                            )}
                          </div>
                        ) : null}
                        {selected?.type === 'truck' ? (
                          <div className="sc-rel">
                            {selected.effective.blocked ? (
                              <>
                                <b>Held at the work zone</b>
                                <br />
                                Related road is closed, so this truck stays at its hold point
                                instead of following waypoints.
                              </>
                            ) : (
                              <>
                                <b>On route</b>
                                <br />
                                Moves along waypoints until a related road closes.
                              </>
                            )}
                          </div>
                        ) : null}
                        {showFindAssets &&
                        selectedSource.type !== 'wind' &&
                        (nearbyCandidates.length > 0 ||
                          linkedAssetsSorted.length > 0 ||
                          (editing && Boolean(selectedSource.pose)) ||
                          Boolean(nearbyHint)) ? (
                          <div className="sc-field sc-insp-block-gap">
                            <span className="sc-flbl">Linked Map Assets</span>
                            {nearbyCandidates.length ? (
                              <div className="sc-nearby">
                                <div className="sc-nearby-table">
                                  <div className="sc-nearby-head">
                                    <button
                                      type="button"
                                      className={`sc-nearby-th sc-nearby-th-check${nearbySort.col === 'checked' ? ' sorted' : ''}`}
                                      title="Sort by checked"
                                      aria-label="Sort by checked"
                                      onClick={() => toggleNearbySort('checked')}
                                    >
                                      {nearbySort.col === 'checked' ? (
                                        <span className="sc-nearby-caret" aria-hidden="true">
                                          {nearbySort.dir === 'asc' ? '▲' : '▼'}
                                        </span>
                                      ) : (
                                        <span className="sc-nearby-caret muted" aria-hidden="true">
                                          ▲
                                        </span>
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      className={`sc-nearby-th sc-nearby-th-kind${nearbySort.col === 'type' ? ' sorted' : ''}`}
                                      title="Sort by type"
                                      onClick={() => toggleNearbySort('type')}
                                    >
                                      {nearbySort.col === 'type' ? (
                                        <span className="sc-nearby-caret" aria-hidden="true">
                                          {nearbySort.dir === 'asc' ? '▲' : '▼'}
                                        </span>
                                      ) : (
                                        <span className="sc-nearby-caret muted" aria-hidden="true">
                                          ▲
                                        </span>
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      className={`sc-nearby-th${nearbySort.col === 'name' ? ' sorted' : ''}`}
                                      onClick={() => toggleNearbySort('name')}
                                    >
                                      <span>Name</span>
                                      <span className="sc-nearby-caret-slot" aria-hidden="true">
                                        {nearbySort.col === 'name'
                                          ? nearbySort.dir === 'asc'
                                            ? '▲'
                                            : '▼'
                                          : ''}
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      className={`sc-nearby-th sc-nearby-th-dist${nearbySort.col === 'dist' ? ' sorted' : ''}`}
                                      onClick={() => toggleNearbySort('dist')}
                                    >
                                      <span>Distance</span>
                                      <span className="sc-nearby-caret-slot" aria-hidden="true">
                                        {nearbySort.col === 'dist'
                                          ? nearbySort.dir === 'asc'
                                            ? '▲'
                                            : '▼'
                                          : ''}
                                      </span>
                                    </button>
                                  </div>
                                  <div
                                    className="sc-nearby-body"
                                    onMouseLeave={() => setNearbyHoverKey(null)}
                                  >
                                    {sortedNearbyCandidates.map((c) => {
                                      const key = assetKey(c)
                                      const on = nearbyPicks.has(key)
                                      const hot = nearbyHoverKey === key
                                      const dist =
                                        c.distanceM != null && Number.isFinite(c.distanceM)
                                          ? formatDistanceCompact(
                                              c.distanceM,
                                              viewSession.displayUnits
                                            )
                                          : '—'
                                      return (
                                        <label
                                          key={key}
                                          className={`sc-nearby-row${hot ? ' hot' : ''}${on ? ' picked' : ''}`}
                                          onMouseEnter={() => {
                                            setLinkedHoverKey(null)
                                            setNearbyHoverKey(key)
                                          }}
                                          onMouseDown={(e) => {
                                            if (e.shiftKey || e.metaKey || e.ctrlKey)
                                              e.preventDefault()
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault()
                                            onNearbyRowToggle(key, e)
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={on}
                                            readOnly
                                            tabIndex={-1}
                                          />
                                          <button
                                            type="button"
                                            className={`sc-nearby-kind${assetDebugKey === key ? ' on' : ''}`}
                                            style={
                                              {
                                                '--sc-kind-color': mapAssetKindColor(c.kind),
                                              } as CSSProperties
                                            }
                                            title={
                                              canViewDebug
                                                ? assetDebugKey === key
                                                  ? 'Hide Mapbox properties'
                                                  : `${c.kind === 'pipeline' ? 'Pipe' : 'Road'} — show Mapbox properties`
                                                : c.kind === 'pipeline'
                                                  ? 'Pipeline'
                                                  : 'Road'
                                            }
                                            onClick={(e) => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                              toggleAssetDebug(key, e)
                                            }}
                                          >
                                            <NavMdiIcon
                                              path={c.kind === 'pipeline' ? mdiPipe : mdiRoad}
                                              sizePx={14}
                                              className="sc-nearby-kind-ico"
                                            />
                                          </button>
                                          <span className="sc-nearby-name" title={c.name}>
                                            {c.name}
                                          </span>
                                          <span className="sc-nearby-dist">{dist}</span>
                                        </label>
                                      )
                                    })}
                                  </div>
                                </div>
                                <div className="sc-series-actions">
                                  <button
                                    type="button"
                                    className="sc-btn primary"
                                    onClick={commitNearbyAttaches}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="sc-btn"
                                    onClick={() => {
                                      setNearbyCandidates([])
                                      setNearbyPicks(new Set())
                                      nearbySelectAnchorKeyRef.current = null
                                      setNearbyHoverKey(null)
                                      setAssetDebugKey(null)
                                      setNearbyHint(null)
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {linkedAssetsSorted.length ? (
                                  <div className="sc-nearby-table sc-attach-table">
                                    <div className="sc-nearby-head sc-attach-head">
                                      <button
                                        type="button"
                                        className={`sc-nearby-th sc-nearby-th-kind${linkedSort.col === 'type' ? ' sorted' : ''}`}
                                        title="Sort by type"
                                        onClick={() => toggleLinkedSort('type')}
                                      >
                                        {linkedSort.col === 'type' ? (
                                          <span className="sc-nearby-caret" aria-hidden="true">
                                            {linkedSort.dir === 'asc' ? '▲' : '▼'}
                                          </span>
                                        ) : (
                                          <span
                                            className="sc-nearby-caret muted"
                                            aria-hidden="true"
                                          >
                                            ▲
                                          </span>
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        className={`sc-nearby-th${linkedSort.col === 'name' ? ' sorted' : ''}`}
                                        onClick={() => toggleLinkedSort('name')}
                                      >
                                        <span>Name</span>
                                        <span className="sc-nearby-caret-slot" aria-hidden="true">
                                          {linkedSort.col === 'name'
                                            ? linkedSort.dir === 'asc'
                                              ? '▲'
                                              : '▼'
                                            : ''}
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        className={`sc-nearby-th sc-nearby-th-dist${linkedSort.col === 'dist' ? ' sorted' : ''}`}
                                        onClick={() => toggleLinkedSort('dist')}
                                      >
                                        <span>Distance</span>
                                        <span className="sc-nearby-caret-slot" aria-hidden="true">
                                          {linkedSort.col === 'dist'
                                            ? linkedSort.dir === 'asc'
                                              ? '▲'
                                              : '▼'
                                            : ''}
                                        </span>
                                      </button>
                                      <span className="sc-attach-head-x" aria-hidden="true" />
                                    </div>
                                    <div
                                      className="sc-attach-list"
                                      onMouseLeave={() => setLinkedHoverKey(null)}
                                    >
                                      {linkedAssetsSorted.map((asset) => {
                                        const key = assetKey(asset)
                                        const hot = linkedHoverKey === key
                                        const distM = selectedSource.pose
                                          ? assetDistanceM(selectedSource.pose, asset)
                                          : null
                                        return (
                                          <div
                                            key={key}
                                            className={`sc-attach-row${hot ? ' hot' : ''}`}
                                            onMouseEnter={() => {
                                              setNearbyHoverKey(null)
                                              setLinkedHoverKey(key)
                                            }}
                                          >
                                            <button
                                              type="button"
                                              className={`sc-attach-kind${assetDebugKey === key ? ' on' : ''}`}
                                              style={
                                                {
                                                  '--sc-kind-color': mapAssetKindColor(asset.kind),
                                                } as CSSProperties
                                              }
                                              title={
                                                canViewDebug
                                                  ? assetDebugKey === key
                                                    ? 'Hide Mapbox properties'
                                                    : `${asset.kind === 'pipeline' ? 'Pipe' : 'Road'} — show Mapbox properties`
                                                  : asset.kind === 'pipeline'
                                                    ? 'Pipeline'
                                                    : 'Road'
                                              }
                                              onClick={(e) => {
                                                e.preventDefault()
                                                toggleAssetDebug(key, e)
                                              }}
                                            >
                                              <NavMdiIcon
                                                path={asset.kind === 'pipeline' ? mdiPipe : mdiRoad}
                                                sizePx={14}
                                                className="sc-nearby-kind-ico"
                                              />
                                            </button>
                                            <span className="sc-attach-name" title={asset.name}>
                                              {asset.name}
                                            </span>
                                            <span className="sc-attach-dist">
                                              {distM != null
                                                ? formatDistanceCompact(
                                                    distM,
                                                    viewSession.displayUnits
                                                  )
                                                : '—'}
                                            </span>
                                            {editing ? (
                                              <button
                                                type="button"
                                                className="sc-x"
                                                aria-label={`Unlink ${asset.name}`}
                                                disabled={!canMutateSelected}
                                                onClick={() => removeAttachedAsset(key)}
                                              >
                                                <CloseIcon />
                                              </button>
                                            ) : (
                                              <span
                                                className="sc-attach-spacer"
                                                aria-hidden="true"
                                              />
                                            )}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                ) : null}
                                {editing && selectedSource.pose ? (
                                  <div className="sc-series-actions">
                                    <button
                                      type="button"
                                      className="sc-btn"
                                      disabled={!canMutateSelected}
                                      onClick={findNearbyAssets}
                                    >
                                      {linkedAssetsSorted.length ? 'Edit links' : 'Find assets'}
                                    </button>
                                  </div>
                                ) : null}
                                {nearbyHint ? (
                                  <p className="sc-hint sc-nearby-hint">{nearbyHint}</p>
                                ) : null}
                              </>
                            )}
                          </div>
                        ) : null}
                        {hasIconSize(selectedSource.type) ? (
                          <div className="sc-field sc-keep">
                            <span className="sc-flbl">Size</span>
                            <div className="sc-seg">
                              {(['sm', 'md', 'lg', 'xl'] as const).map((sz) => (
                                <button
                                  key={sz}
                                  type="button"
                                  className={selectedSource.size === sz ? 'on' : ''}
                                  disabled={!canMutateSelected}
                                  onClick={() => applyObjectSize(sz)}
                                >
                                  {sz === 'md' ? 'Med' : TYPE_SIZE_LABEL[sz]}
                                </button>
                              ))}
                            </div>
                            {SIZE_RADIUS_M[selectedSource.type] ? (
                              <p className="sc-hint">
                                Sets icon size
                                {selectedSource.radiusKeyframes?.length
                                  ? ` and the keep-out ring at ${formatElapsed(elapsedMs)}`
                                  : ' and keep-out ring size'}
                                .
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        {isLocalStorm(selectedSource.type) &&
                        canEditBoardUnlocked &&
                        sessionOffersStoryClock ? (
                          <div className="sc-insp-widget sc-tornado-setup">
                            <div className="sc-insp-widget-title">
                              {selectedSource.type === 'hurricane' ? 'Hurricane' : 'Tornado type'}
                            </div>
                            <p className="sc-hint sc-insp-widget-hint">
                              {selectedSource.type === 'hurricane'
                                ? 'Sets the track and sustained wind. Track is the direction the center travels, and the wind follows that heading. Width below is the wind field. Details below override this.'
                                : 'Sets the path, wind, and width. Details below override it.'}
                            </p>
                            <div className="sc-wind-presets">
                              <div className="sc-wind-presets-row">
                                <select
                                  className={`sc-fin sc-wind-presets-select${
                                    tornadoPresetId ? '' : ' is-placeholder'
                                  }`}
                                  value={tornadoPresetId}
                                  aria-label={
                                    selectedSource.type === 'hurricane' ? 'Hurricane' : 'Tornado type'
                                  }
                                  disabled={windPreviewActive || !canMutateSelected}
                                  onChange={(e) => {
                                    const next = e.target.value
                                    if (!next) return
                                    setTornadoPresetId(next)
                                    setTornadoPresetSpanMs(
                                      selectedSource.type === 'hurricane'
                                        ? hurricanePresetById(next as HurricanePresetId).defaultSpanMs
                                        : tornadoPresetById(next as TornadoPresetId).defaultSpanMs,
                                    )
                                  }}
                                >
                                  <option value="" disabled>
                                    {selectedSource.type === 'hurricane'
                                      ? 'Select a storm'
                                      : 'Select a type'}
                                  </option>
                                  {(selectedSource.type === 'hurricane'
                                    ? HURRICANE_PRESETS
                                    : TORNADO_PRESETS
                                  ).map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.label} ({formatSpeedMph(p.peakMph, viewSession.displayUnits)})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="sc-wind-presets-row sc-wind-presets-config">
                                <label className="sc-wind-presets-field">
                                  <span className="sc-flbl">Onset</span>
                                  <ElapsedInput
                                    valueIso={atOff(tornadoPresetOnsetMs)}
                                    clock={authorClock}
                                    disabled={
                                      !tornadoPresetId || windPreviewActive || !canMutateSelected
                                    }
                                    onCommit={(iso) => {
                                      if (iso == null) return
                                      const onset = Math.max(0, off(iso))
                                      setTornadoPresetOnsetMs(onset)
                                      patchSelectedObject((o) => ({
                                        ...o,
                                        params: { ...o.params, presetOnsetAt: iso },
                                      }))
                                    }}
                                  />
                                </label>
                                <label className="sc-wind-presets-field">
                                  <span className="sc-flbl">Duration</span>
                                  <select
                                    className="sc-fin"
                                    value={tornadoPresetSpanMs}
                                    disabled={
                                      !tornadoPresetId || windPreviewActive || !canMutateSelected
                                    }
                                    aria-label={
                                      selectedSource.type === 'hurricane'
                                        ? 'Hurricane duration'
                                        : 'Tornado type duration'
                                    }
                                    onChange={(e) =>
                                      setTornadoPresetSpanMs(Number(e.target.value))
                                    }
                                  >
                                    {(selectedSource.type === 'hurricane'
                                      ? HURRICANE_PRESET_SPAN_OPTIONS
                                      : TORNADO_PRESET_SPAN_OPTIONS
                                    ).map((opt) => (
                                      <option key={opt.ms} value={opt.ms}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                {selectedSource.type === 'hurricane' ? (
                                  <label className="sc-wind-presets-field sc-hurricane-track">
                                    <span className="sc-flbl">Track</span>
                                    <select
                                      className="sc-fin"
                                      value={hurricaneTrackHeadingDeg}
                                      disabled={
                                        !tornadoPresetId || windPreviewActive || !canMutateSelected
                                      }
                                      aria-label="Hurricane track direction"
                                      onChange={(e) => setHurricaneTrackHeadingDeg(Number(e.target.value))}
                                    >
                                      {HURRICANE_TRACK_HEADINGS.map((heading) => (
                                        <option key={heading.deg} value={heading.deg}>
                                          {heading.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                ) : null}
                                <button
                                  type="button"
                                  className="sc-btn sc-tornado-apply"
                                  disabled={
                                    !tornadoPresetId || windPreviewActive || !canMutateSelected
                                  }
                                  title={
                                    tornadoPresetId
                                      ? selectedSource.type === 'hurricane'
                                        ? 'Replace the track, wind, and width with this storm'
                                        : 'Replace the path, wind, and width with this type'
                                      : selectedSource.type === 'hurricane'
                                        ? 'Select a storm first'
                                        : 'Select a type first'
                                  }
                                  onClick={applyTornadoPreset}
                                >
                                  Apply
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {isLocalStorm(selectedSource.type) && zoneCfgFor(selectedSource.type) ? (
                          <div className="sc-insp-widget sc-tornado-width">
                            <div className="sc-insp-widget-title">Width</div>
                            {editing ? (
                              <>
                                <div className="sc-unit-field">
                                  <input
                                    className="sc-fin"
                                    type="number"
                                    inputMode="decimal"
                                    aria-label={
                                      selectedSource.type === 'hurricane'
                                        ? 'Hurricane width'
                                        : 'Tornado width'
                                    }
                                    value={
                                      radiusInputDraft ??
                                      String(
                                        widthInputNumber(
                                          inspectRadiusM(selected ?? selectedSource) * 2,
                                          viewSession.displayUnits,
                                          selectedSource.type === 'hurricane',
                                        ),
                                      )
                                    }
                                    onFocus={() => {
                                      clearPathEditFocus()
                                      setRadiusInputDraft(
                                        String(
                                          widthInputNumber(
                                            inspectRadiusM(selected ?? selectedSource) * 2,
                                            viewSession.displayUnits,
                                            selectedSource.type === 'hurricane',
                                          ),
                                        ),
                                      )
                                    }}
                                    onChange={(e) => setRadiusInputDraft(e.target.value)}
                                    onBlur={() => {
                                      const raw = radiusInputDraft
                                      setRadiusInputDraft(null)
                                      if (raw == null || raw.trim() === '') return
                                      const displayed = Number(raw)
                                      if (!Number.isFinite(displayed)) return
                                      applyRadiusMeters(
                                        widthInputToMeters(
                                          displayed,
                                          viewSession.displayUnits,
                                          selectedSource.type === 'hurricane',
                                        ) / 2,
                                      )
                                    }}
                                    disabled={!canMutateSelected}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                                    }}
                                  />
                                  <span className="sc-unit-affix" aria-hidden="true">
                                    {widthInputSuffix(
                                      viewSession.displayUnits,
                                      selectedSource.type === 'hurricane',
                                    )}
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={zoneCfgFor(selectedSource.type)!.min * 2}
                                  max={zoneCfgFor(selectedSource.type)!.max * 2}
                                  step={zoneCfgFor(selectedSource.type)!.step * 2}
                                  value={inspectRadiusM(selected ?? selectedSource) * 2}
                                  disabled={!canMutateSelected}
                                  aria-label={
                                    selectedSource.type === 'hurricane'
                                      ? 'Hurricane width'
                                      : 'Tornado width'
                                  }
                                  onChange={(e) => applyRadiusMeters(Number(e.target.value) / 2)}
                                />
                                <div className="sc-presets">
                                  {zoneCfgFor(selectedSource.type)!.presets.map((m) => (
                                    <button
                                      key={m}
                                      type="button"
                                      className="sc-btn tiny"
                                      disabled={!canMutateSelected}
                                      onClick={() => applyRadiusMeters(m)}
                                    >
                                      {formatDistanceM(m * 2, viewSession.displayUnits)}
                                    </button>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="sc-fval">
                                {formatDistanceM(
                                  inspectRadiusM(selected ?? selectedSource) * 2,
                                  viewSession.displayUnits,
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                        {windAuthoring && windShow ? (
                          <div
                            className={`sc-insp-widget sc-wind-widget${
                              isLocalStorm(windAuthoring.type) ? ' is-tornado' : ''
                            }`}
                          >
                            <div className="sc-insp-widget-title">
                              {isLocalStorm(windAuthoring.type)
                                ? `Wind at ${formatRunningClock(atOff(storyBarElapsedMs), authorClock)}`
                                : 'Surface wind'}
                            </div>
                            <div className="sc-wind-panel">
                              <div className="sc-wind-author">
                                <div
                                  className={`sc-wind-author-top${
                                    isLocalStorm(windAuthoring.type) ? ' is-tornado' : ''
                                  }`}
                                >
                                  <WindDegControl
                                    fromDeg={windShow.fromDeg}
                                    cardinal={windCardinal(windShow.fromDeg)}
                                    editing={canEditBoardUnlocked}
                                    disabled={!canEditBoard || windPreviewActive}
                                    onChange={(fromDeg) => {
                                      windUserEditRef.current = true
                                      setWindHoverIndex(null)
                                      stageWindParams({
                                        fromDeg,
                                        speedMph: windDraft?.speedMph ?? windShow.speedMph,
                                        plumeDistanceM:
                                          windDraft?.plumeDistanceM ?? windShow.plumeDistanceM,
                                        plumeSpreadDeg:
                                          windDraft?.plumeSpreadDeg ?? windShow.plumeSpreadDeg,
                                      })
                                    }}
                                  />
                                  {isLocalStorm(windAuthoring.type) ? (
                                    <WindCompass
                                      fromDeg={windShow.fromDeg}
                                      disabled={!canEditBoard || windPreviewActive || running}
                                      onChange={(fromDeg) => {
                                        windUserEditRef.current = true
                                        setWindHoverIndex(null)
                                        stageWindParams({
                                          fromDeg,
                                          speedMph: windDraft?.speedMph ?? windShow.speedMph,
                                          plumeDistanceM:
                                            windDraft?.plumeDistanceM ?? windShow.plumeDistanceM,
                                          plumeSpreadDeg:
                                            windDraft?.plumeSpreadDeg ?? windShow.plumeSpreadDeg,
                                        })
                                      }}
                                    />
                                  ) : sessionOffersStoryClock ? (
                                    <div className="sc-wind-author-time">
                                      <ElapsedInput
                                        valueIso={atOff(windAuthorMs)}
                                        clock={authorClock}
                                        disabled={!canEditBoard || windPreviewActive || running}
                                        onFocusRow={() => {
                                          windTimeFocusedRef.current = true
                                        }}
                                        onBlurRow={() => {
                                          windTimeFocusedRef.current = false
                                        }}
                                        onCommit={(iso) => {
                                          if (iso == null || running) return
                                          parkWindInspectPlayhead(off(iso))
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                                {isLocalStorm(windAuthoring.type) ? null : (
                                <div className="sc-wind-dials">
                                  <WindCompass
                                    fromDeg={windShow.fromDeg}
                                    disabled={!canEditBoard || windPreviewActive || running}
                                    onChange={(fromDeg) => {
                                      windUserEditRef.current = true
                                      setWindHoverIndex(null)
                                      stageWindParams({
                                        fromDeg,
                                        speedMph: windDraft?.speedMph ?? windShow.speedMph,
                                        plumeDistanceM:
                                          windDraft?.plumeDistanceM ?? windShow.plumeDistanceM,
                                        plumeSpreadDeg:
                                          windDraft?.plumeSpreadDeg ?? windShow.plumeSpreadDeg,
                                      })
                                    }}
                                  />
                                  {sessionOffersStoryClock ? (
                                    <WindAnalogClock
                                      playheadIso={atOff(windAuthorMs)}
                                      timeZone={viewSession.timezone}
                                    />
                                  ) : null}
                                </div>
                                )}
                              </div>
                              <div className="sc-wind-param">
                                <label className="sc-flbl">
                                  Wind speed:{' '}
                                  {formatSpeedMph(windShow.speedMph, viewSession.displayUnits)}
                                </label>
                                <input
                                  type="range"
                                  min={0}
                                  max={
                                    windAuthoring.type === 'hurricane'
                                      ? HURRICANE_WIND_SPEED_MAX_MPH
                                      : WIND_SPEED_SLIDER_MAX_MPH
                                  }
                                  step={5}
                                  tabIndex={-1}
                                  value={Math.min(
                                    windAuthoring.type === 'hurricane'
                                      ? HURRICANE_WIND_SPEED_MAX_MPH
                                      : WIND_SPEED_SLIDER_MAX_MPH,
                                    Math.round(windShow.speedMph)
                                  )}
                                  disabled={!canEditBoard || windPreviewActive || running}
                                  onPointerDown={() => {
                                    windUserEditRef.current = true
                                  }}
                                  onPointerUp={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onPointerCancel={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onChange={(e) => {
                                    const speedMph = Number(e.target.value)
                                    setWindHoverIndex(null)
                                    stageWindParams({
                                      fromDeg: windDraft?.fromDeg ?? windShow.fromDeg,
                                      speedMph,
                                      plumeDistanceM:
                                        windDraft?.plumeDistanceM ?? windShow.plumeDistanceM,
                                      plumeSpreadDeg:
                                        windDraft?.plumeSpreadDeg ?? windShow.plumeSpreadDeg,
                                    })
                                  }}
                                />
                                <div className="sc-scale" aria-hidden="true">
                                  {(['Calm', 'Moderate', 'Strong', 'Extreme'] as const).map(
                                    (label) => (
                                      <span
                                        key={label}
                                        className={
                                          windSpeedBand(windShow.speedMph) === label
                                            ? 'on'
                                            : undefined
                                        }
                                      >
                                        {label}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                              {isLocalStorm(windAuthoring.type) ? null : (
                              <>
                              <div className="sc-wind-param">
                                <label className="sc-flbl">
                                  Plume distance:{' '}
                                  {formatDistanceM(
                                    windShow.plumeDistanceM,
                                    viewSession.displayUnits
                                  )}
                                </label>
                                <input
                                  type="range"
                                  min={10}
                                  max={WIND_PLUME_DISTANCE_SLIDER_MAX_M}
                                  step={10}
                                  tabIndex={-1}
                                  value={Math.min(
                                    WIND_PLUME_DISTANCE_SLIDER_MAX_M,
                                    Math.round(windShow.plumeDistanceM)
                                  )}
                                  disabled={!canEditBoard || windPreviewActive || running}
                                  onPointerDown={() => {
                                    windUserEditRef.current = true
                                  }}
                                  onPointerUp={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onPointerCancel={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onChange={(e) => {
                                    const plumeDistanceM = Number(e.target.value)
                                    setWindHoverIndex(null)
                                    stageWindParams({
                                      fromDeg: windDraft?.fromDeg ?? windShow.fromDeg,
                                      speedMph: windDraft?.speedMph ?? windShow.speedMph,
                                      plumeDistanceM,
                                      plumeSpreadDeg:
                                        windDraft?.plumeSpreadDeg ?? windShow.plumeSpreadDeg,
                                    })
                                  }}
                                />
                              </div>
                              <div className="sc-wind-param sc-wind-param-last">
                                <label className="sc-flbl">
                                  Spread angle: {Math.round(windShow.plumeSpreadDeg)}°
                                </label>
                                <input
                                  type="range"
                                  min={5}
                                  max={60}
                                  step={5}
                                  tabIndex={-1}
                                  value={Math.round(windShow.plumeSpreadDeg)}
                                  disabled={!canEditBoard || windPreviewActive || running}
                                  onPointerDown={() => {
                                    windUserEditRef.current = true
                                  }}
                                  onPointerUp={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onPointerCancel={() => {
                                    windUserEditRef.current = false
                                  }}
                                  onChange={(e) => {
                                    const plumeSpreadDeg = Number(e.target.value)
                                    setWindHoverIndex(null)
                                    stageWindParams({
                                      fromDeg: windDraft?.fromDeg ?? windShow.fromDeg,
                                      speedMph: windDraft?.speedMph ?? windShow.speedMph,
                                      plumeDistanceM:
                                        windDraft?.plumeDistanceM ?? windShow.plumeDistanceM,
                                      plumeSpreadDeg,
                                    })
                                  }}
                                />
                                <div className="sc-scale" aria-hidden="true">
                                  {(['Narrow', 'Wide'] as const).map((label) => (
                                    <span
                                      key={label}
                                      className={
                                        plumeSpreadBand(windShow.plumeSpreadDeg) === label
                                          ? 'on'
                                          : undefined
                                      }
                                    >
                                      {label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              </>
                              )}
                              {sessionOffersStoryClock &&
                              isLocalStorm(windAuthoring.type) &&
                              displayDur > 0 ? (
                                <WindStoryMirror
                                  durationMs={displayDur}
                                  elapsedMs={storyBarElapsedMs}
                                  startLabel={formatRunningClock(viewSession.storyStartTime, authorClock)}
                                  endLabel={formatRunningClock(
                                    addStoryMs(viewSession.storyStartTime, displayDur),
                                    authorClock,
                                  )}
                                  frames={(() => {
                                    const marks = windFrames.map((frame) => ({
                                      atMs: off(frame.at),
                                      title: `${formatRunningClock(frame.at, authorClock)} · ${Math.round(frame.fromDeg)}° ${windCardinal(frame.fromDeg)}`,
                                    }))
                                    if (marks.some((mark) => mark.atMs <= WIND_MARK_MATCH_MS)) return marks
                                    const held = windFrames.length
                                      ? sampleWindAt(windFrames, viewSession.storyStartTime)
                                      : null
                                    const fromDeg = held?.fromDeg ?? windShow.fromDeg
                                    return [
                                      {
                                        atMs: 0,
                                        title: `${formatRunningClock(viewSession.storyStartTime, authorClock)} · ${Math.round(fromDeg)}° ${windCardinal(fromDeg)}`,
                                      },
                                      ...marks,
                                    ]
                                  })()}
                                  onSeekFrame={(ms) => {
                                    setWindHoverIndex(null)
                                    setWindDraft(null)
                                    setWindSelectIndex(null)
                                    seekStoryFromTornadoTick(ms)
                                  }}
                                />
                              ) : null}
                              {sessionOffersStoryClock ? (
                              <>
                              <div className="sc-series-actions">
                                {canEditBoardUnlocked ? (
                                  <button
                                    type="button"
                                    className="sc-btn primary"
                                    disabled={
                                      !canEditBoard ||
                                      windPreviewActive ||
                                      running ||
                                      tornadoPreviewFocus
                                    }
                                    title={
                                      windPreviewActive || running || tornadoPreviewFocus
                                        ? 'Stop Preview to set or update a wind point'
                                        : undefined
                                    }
                                    onClick={() => {
                                      stopWindPreview()
                                      const draft = windDraft ?? windShow
                                      const hit = nearestWindFrameIndex(windFrames, windClockMs, (iso) =>
                                        off(iso),
                                      )
                                      const at = hit >= 0 ? windFrames[hit]!.at : atOff(windClockMs)
                                      const frame: WindKeyframe = {
                                        at,
                                        fromDeg: Math.round(draft.fromDeg),
                                        speedMph: Math.round(draft.speedMph),
                                        plumeDistanceM: Math.round(draft.plumeDistanceM),
                                        plumeSpreadDeg: Math.round(draft.plumeSpreadDeg),
                                      }
                                      upsertWindKeyframe(frame)
                                      const next = sortWindFrames([
                                        ...windFrames.filter((f) => f.at !== at),
                                        frame,
                                      ])
                                      setWindSelectIndex(next.findIndex((f) => f.at === at))
                                      setWindHoverIndex(null)
                                      setWindDraft(draft)
                                    }}
                                  >
                                    {isLocalStorm(windAuthoring.type)
                                      ? windMarkIndexLive >= 0
                                        ? 'Update'
                                        : 'Set'
                                      : windAtExactMark && !running
                                        ? 'Update'
                                        : 'Set'}
                                  </button>
                                ) : null}
                                {isLocalStorm(windAuthoring.type) ? null : (
                                <button
                                  type="button"
                                  className={`sc-btn${windPreviewActive ? ' on' : ''}`}
                                  disabled={windFrames.length < 2}
                                  title={
                                    windFrames.length < 2
                                      ? 'Need at least two wind points to preview'
                                      : windPreviewActive
                                        ? 'Pause preview at the current time'
                                        : 'Play from the current time through the end of the wind timespan'
                                  }
                                  onClick={() => {
                                    if (windPreviewActive) stopWindPreview()
                                    else previewSelectedWind()
                                  }}
                                >
                                  {windPreviewActive ? 'Stop' : 'Preview'}
                                </button>
                                )}
                                {isLocalStorm(windAuthoring.type) && canEditBoardUnlocked ? (
                                  windMarkIndexLive >= 0 ? (
                                    <button
                                      type="button"
                                      className="sc-btn"
                                      title="Remove the wind point at this time"
                                      disabled={
                                        !canEditBoard ||
                                        windPreviewActive ||
                                        running ||
                                        tornadoPreviewFocus
                                      }
                                      onClick={() => {
                                        stopWindPreview()
                                        const idx = nearestWindFrameIndex(
                                          windFrames,
                                          windClockMs,
                                          (iso) => off(iso),
                                        )
                                        if (idx < 0) return
                                        removeWindKeyframeAt(idx)
                                        setWindSelectIndex(null)
                                        setWindHoverIndex(null)
                                        setWindDraft(null)
                                      }}
                                    >
                                      Remove
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="sc-btn"
                                      title="Remove every wind point"
                                      disabled={
                                        !canEditBoard ||
                                        windPreviewActive ||
                                        running ||
                                        tornadoPreviewFocus ||
                                        !windFrames.length
                                      }
                                      onClick={() => {
                                        stopWindPreview()
                                        setWindFrames(undefined)
                                        setWindSelectIndex(null)
                                        setWindHoverIndex(null)
                                        setWindDraft(null)
                                      }}
                                    >
                                      Clear all
                                    </button>
                                  )
                                ) : canEditBoardUnlocked && windAtExactMark && !running ? (
                                  <button
                                    type="button"
                                    className="sc-btn"
                                    title="Remove the wind point at this time"
                                    disabled={!canEditBoard || windPreviewActive}
                                    onClick={() => {
                                      stopWindPreview()
                                      const idx = nearestWindFrameIndex(windFrames, windAuthorMs, (iso) =>
                                        off(iso),
                                      )
                                      if (idx < 0) return
                                      removeWindKeyframeAt(idx)
                                      setWindSelectIndex(null)
                                      setWindHoverIndex(null)
                                      setWindDraft(null)
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : canEditBoardUnlocked && windFrames.length && !windPreviewActive ? (
                                  <button
                                    type="button"
                                    className="sc-btn"
                                    title="Remove every wind point"
                                    disabled={!canEditBoard}
                                    onClick={() => {
                                      stopWindPreview()
                                      setWindFrames(undefined)
                                      setWindSelectIndex(null)
                                      setWindHoverIndex(null)
                                      setWindDraft(null)
                                    }}
                                  >
                                    Clear all
                                  </button>
                                ) : null}
                                {isLocalStorm(windAuthoring.type) ? (
                                  <button
                                    type="button"
                                    className={`sc-btn sc-tornado-preview-btn${
                                      tornadoPreviewFocus ? ' on' : ''
                                    }`}
                                    disabled={displayDur <= 0}
                                    title={
                                      tornadoPreviewFocus
                                        ? 'Stop. The scene stays at this moment.'
                                        : `Play the story from the beginning. The map dims around this ${windAuthoring.type === 'hurricane' ? 'hurricane' : 'tornado'}.`
                                    }
                                    onClick={() => {
                                      if (tornadoPreviewFocus) stopTornadoStoryPreview()
                                      else startTornadoStoryPreview()
                                    }}
                                  >
                                    {tornadoPreviewFocus ? 'Stop' : 'Preview'}
                                  </button>
                                ) : null}
                              </div>
                              {isLocalStorm(windAuthoring.type) ? null : (
                              <WindKeyframeScrubber
                                frames={windFrames}
                                playheadIso={mapPlayheadIso}
                                clock={authorClock}
                                disabled={windPreviewActive}
                                onSeek={(iso) => {
                                  setWindHoverIndex(null)
                                  setWindDraft(null)
                                  setWindSelectIndex(null)
                                  parkWindInspectPlayhead(off(iso))
                                }}
                              />
                              )}
                              {canEditBoardUnlocked && windAuthoring.type === 'wind' ? (
                                <div className="sc-wind-presets-fold">
                                  <button
                                    type="button"
                                    className="sc-insp-fold-toggle"
                                    aria-expanded={windPresetsOpen}
                                    disabled={windPreviewActive}
                                    onClick={() => setWindPresetsOpen((open) => !open)}
                                  >
                                    <span>Weather presets</span>
                                    <span
                                      className={`sc-chevron${windPresetsOpen ? ' open' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ▾
                                    </span>
                                  </button>
                                  <div className={`sc-fold-body${windPresetsOpen ? ' open' : ''}`}>
                                    <div className="sc-fold-inner">
                                      <div className="sc-wind-presets">
                                        <div className="sc-wind-presets-row">
                                          <select
                                            className="sc-fin sc-wind-presets-select"
                                            value={windPresetId}
                                            aria-label="Weather preset"
                                            disabled={windPreviewActive}
                                            onChange={(e) =>
                                              setWindPresetId(e.target.value as WindPresetId)
                                            }
                                          >
                                            {WIND_PRESETS.map((p) => (
                                              <option key={p.id} value={p.id}>
                                                {p.label}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            type="button"
                                            className="sc-btn"
                                            disabled={windPreviewActive}
                                            title="Replace wind points with this preset (clamped to the session clock)"
                                            onClick={applyWindPreset}
                                          >
                                            Apply
                                          </button>
                                        </div>
                                        <div className="sc-wind-presets-row sc-wind-presets-config">
                                          <label className="sc-wind-presets-field">
                                            <span className="sc-flbl">Onset</span>
                                            <ElapsedInput
                                              valueIso={atOff(windPresetOnsetMs)}
                                              clock={authorClock}
                                              disabled={windPreviewActive}
                                              onCommit={(iso) => {
                                                if (iso == null) return
                                                setWindPresetOnsetMs(Math.max(0, off(iso)))
                                              }}
                                            />
                                          </label>
                                          <label className="sc-wind-presets-field">
                                            <span className="sc-flbl">Duration</span>
                                            <select
                                              className="sc-fin"
                                              value={windPresetSpanMs}
                                              disabled={windPreviewActive}
                                              aria-label="Preset duration"
                                              onChange={(e) =>
                                                setWindPresetSpanMs(Number(e.target.value))
                                              }
                                            >
                                              {WIND_PRESET_SPAN_OPTIONS.map((opt) => (
                                                <option key={opt.ms} value={opt.ms}>
                                                  {opt.label}
                                                </option>
                                              ))}
                                            </select>
                                          </label>
                                        </div>
                                        <p className="sc-hint">
                                          {formatWindPresetBlurb(windPresetId, {
                                            displayUnits: viewSession.displayUnits,
                                            clock: authorClock,
                                            onsetMs: windPresetOnsetMs,
                                            spanMs: windPresetSpanMs,
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              </>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                        {!isLocalStorm(selectedSource.type) &&
                        typeof selectedSource.params.radiusM === 'number' &&
                        selectedSource.type !== 'wind' ? (
                          <div
                            className={`sc-field${selectedSource.radiusKeyframes?.length ? ' sc-overridden' : ''}`}
                          >
                            <span className="sc-flbl">{zoneLabel(selectedSource.type)}</span>
                            {editing ? (
                              <>
                                <div className="sc-unit-field">
                                  <input
                                    className="sc-fin"
                                    type="number"
                                    inputMode="decimal"
                                    value={
                                      radiusInputDraft ??
                                      String(
                                        metersToDisplay(
                                          inspectRadiusM(selected ?? selectedSource),
                                          viewSession.displayUnits
                                        )
                                      )
                                    }
                                    onFocus={() => {
                                      clearPathEditFocus()
                                      setRadiusInputDraft(
                                        String(
                                          metersToDisplay(
                                            inspectRadiusM(selected ?? selectedSource),
                                            viewSession.displayUnits
                                          )
                                        )
                                      )
                                    }}
                                    onChange={(e) => {
                                      // Allow empty / partial typing — clamp only on blur.
                                      setRadiusInputDraft(e.target.value)
                                    }}
                                    onBlur={() => {
                                      const raw = radiusInputDraft
                                      setRadiusInputDraft(null)
                                      if (raw == null || raw.trim() === '') return
                                      const displayed = Number(raw)
                                      if (!Number.isFinite(displayed)) return
                                      applyRadiusMeters(
                                        displayToMeters(displayed, viewSession.displayUnits)
                                      )
                                    }}
                                    disabled={!canMutateSelected}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                                    }}
                                  />
                                  <span className="sc-unit-affix" aria-hidden="true">
                                    {distanceUnitShort(viewSession.displayUnits)}
                                  </span>
                                </div>
                                {zoneCfgFor(selectedSource.type) ? (
                                  <>
                                    <input
                                      type="range"
                                      min={zoneCfgFor(selectedSource.type)!.min}
                                      max={zoneCfgFor(selectedSource.type)!.max}
                                      step={zoneCfgFor(selectedSource.type)!.step}
                                      value={inspectRadiusM(selected ?? selectedSource)}
                                      disabled={!canMutateSelected}
                                      onChange={(e) => applyRadiusMeters(Number(e.target.value))}
                                    />
                                    <div className="sc-presets">
                                      {zoneCfgFor(selectedSource.type)!.presets.map((m) => (
                                        <button
                                          key={m}
                                          type="button"
                                          className="sc-btn tiny"
                                          disabled={!canMutateSelected}
                                          onClick={() => applyRadiusMeters(m)}
                                        >
                                          {presetLabel(m, viewSession.displayUnits)}
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <div className="sc-fval">
                                {formatDistanceM(
                                  inspectRadiusM(selected ?? selectedSource),
                                  viewSession.displayUnits
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                        {supportsKeyframe(selectedSource.type, 'status') &&
                        !selectedSource.statusKeyframes?.length ? (
                          <div className="sc-field">
                            <span className="sc-flbl">Status</span>
                            <div className="sc-seg">
                              {(['open', 'slow', 'closed'] as const).map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  className={
                                    String(
                                      selected?.effective.status ??
                                        selectedSource.params.status ??
                                        'open'
                                    ) === status
                                      ? 'on'
                                      : ''
                                  }
                                  disabled={!canMutateSelected}
                                  onClick={() => applyRoadStatus(status)}
                                >
                                  {status === 'open'
                                    ? 'Open'
                                    : status === 'slow'
                                      ? 'Slow'
                                      : 'Closed'}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {(() => {
                          const typeId = selectedSource.type
                          // Story-clock packages only — Whiteboard has no playhead for timed frames.
                          if (!sessionOffersStoryClock || !showTimelineAuthoring) return null
                          if (
                            sessionTypeProfile?.visibilityKeyframes === false &&
                            sessionTypeProfile?.waypoints === false
                          ) {
                            return null
                          }
                          const showPoseKf =
                            showWaypoints &&
                            supportsKeyframe(typeId, 'waypoints') &&
                            Boolean(
                              selectedSource.keyframes?.length || (editing && selectedSource.pose)
                            )
                          const showVisKf =
                            showVisibility &&
                            supportsKeyframe(typeId, 'visibility') &&
                            (Boolean(selectedSource.visibilityKeyframes?.length) || editing)
                          const showStatusKf =
                            supportsKeyframe(typeId, 'status') &&
                            (Boolean(selectedSource.statusKeyframes?.length) || editing)
                          // Radius-over-time: legacy boards with frames, or types that opt into `radius`.
                          // Wind has no ring. Tornado width is one circle, not a keyframed series.
                          const showRadiusKf =
                            !isLocalStorm(typeId) &&
                            typeId !== 'wind' &&
                            (Boolean(selectedSource.radiusKeyframes?.length) ||
                              (editing && supportsKeyframe(typeId, 'radius')))
                          if (!showPoseKf && !showVisKf && !showStatusKf && !showRadiusKf)
                            return null
                          return (
                            <div className="sc-kf-block">
                              <div className="sc-panel-title">Keyframing</div>
                              <p className="sc-hint">
                                {typeId === 'hurricane'
                                  ? 'Schedule the hurricane’s track and holds.'
                                  : typeId === 'tornado'
                                    ? 'Schedule the tornado’s path and holds.'
                                    : 'Vary how this object looks and moves as time advances.'}
                              </p>
                              {showPoseKf ? (
                                <div className="sc-insp-fold sc-kf-pose">
                                  <button
                                    type="button"
                                    className="sc-insp-fold-toggle"
                                    aria-expanded={waypointsOpen}
                                    onClick={() => {
                                      setWaypointsOpen((open) => {
                                        if (open) {
                                          setWaypointFocusIndex(null)
                                          stopPathPreview()
                                        }
                                        return !open
                                      })
                                    }}
                                  >
                                    <span>
                                      {poseFoldLabel(typeId)}
                                      {(selectedSource.keyframes?.length ?? 0) > 0
                                        ? ` (${selectedSource.keyframes!.length})`
                                        : ''}
                                    </span>
                                    <span
                                      className={`sc-chevron${waypointsOpen ? ' open' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ▾
                                    </span>
                                  </button>
                                  <div className={`sc-fold-body${waypointsOpen ? ' open' : ''}`}>
                                    <div className="sc-fold-inner">
                                      <div className="sc-insp-fold-pane">
                                        <PoseSeriesEditor
                                          frames={
                                            selectedSource.keyframes?.length
                                              ? sortPoseFrames(selectedSource.keyframes)
                                              : []
                                          }
                                          editing={editing}
                                          focusIndex={waypointFocusIndex}
                                          flashIndex={waypointFlashIndex}
                                          previewIndex={
                                            pathPreviewActive && pathPreviewWaypointIndex != null
                                              ? pathPreviewWaypointIndex
                                              : null
                                          }
                                          previewHolding={pathPreviewActive && pathPreviewHolding}
                                          pathPreviewActive={
                                            isLocalStorm(selectedSource.type)
                                              ? false
                                              : pathPreviewActive
                                          }
                                          onTogglePathPreview={
                                            isLocalStorm(selectedSource.type)
                                              ? undefined
                                              : () => {
                                                  if (pathPreviewActive) stopPathPreview()
                                                  else previewSelectedPath()
                                                }
                                          }
                                          showRetime={!isLocalStorm(selectedSource.type)}
                                          anchorPose={selectedSource.pose}
                                          appearAt={selectedSource.startsAt ?? null}
                                          playheadIso={playheadIso}
                                          clock={authorClock}
                                          onFocusIndex={focusPoseKeyframe}
                                          onFlashIndex={flashWaypointRow}
                                          onChange={setPoseFrames}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {showVisKf ? (
                                <div className="sc-insp-fold sc-kf-vis">
                                  <button
                                    type="button"
                                    className="sc-insp-fold-toggle"
                                    aria-expanded={visibilityOpen}
                                    onClick={() => {
                                      setVisibilityOpen((open) => {
                                        if (open) {
                                          stopVisPreview()
                                          setVisibilityFocusIndex(null)
                                        }
                                        return !open
                                      })
                                    }}
                                  >
                                    <span>
                                      Visibility
                                      {(selectedSource.visibilityKeyframes?.length ?? 0) > 0
                                        ? ` (${selectedSource.visibilityKeyframes!.length})`
                                        : ''}
                                    </span>
                                    <span
                                      className={`sc-chevron${visibilityOpen ? ' open' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ▾
                                    </span>
                                  </button>
                                  <div className={`sc-fold-body${visibilityOpen ? ' open' : ''}`}>
                                    <div className="sc-fold-inner">
                                      <div className="sc-insp-fold-pane">
                                        <VisibilitySeriesEditor
                                          frames={
                                            selectedSource.visibilityKeyframes?.length
                                              ? sortVisFrames(selectedSource.visibilityKeyframes)
                                              : []
                                          }
                                          editing={editing}
                                          clock={authorClock}
                                          appearAt={selectedSource.startsAt ?? null}
                                          playheadIso={playheadIso}
                                          focusIndex={visibilityFocusIndex}
                                          previewIndex={
                                            visPreviewActive
                                              ? visibilityPreviewIndexAtElapsed(
                                                  selectedSource.visibilityKeyframes ?? [],
                                                  playheadIso
                                                )
                                              : null
                                          }
                                          visPreviewActive={visPreviewActive}
                                          onToggleVisPreview={() => {
                                            if (visPreviewActive) stopVisPreview()
                                            else previewSelectedVisibility()
                                          }}
                                          onFocusIndex={setVisibilityFocusIndex}
                                          onChange={setVisibilityFrames}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {showStatusKf ? (
                                <div className="sc-insp-fold sc-kf-status">
                                  <button
                                    type="button"
                                    className="sc-insp-fold-toggle"
                                    aria-expanded={statusOpen}
                                    onClick={() => {
                                      setStatusOpen((open) => {
                                        if (open) setStatusFocusIndex(null)
                                        return !open
                                      })
                                    }}
                                  >
                                    <span>
                                      Status
                                      {(selectedSource.statusKeyframes?.length ?? 0) > 0
                                        ? ` (${selectedSource.statusKeyframes!.length})`
                                        : ''}
                                    </span>
                                    <span
                                      className={`sc-chevron${statusOpen ? ' open' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ▾
                                    </span>
                                  </button>
                                  <div className={`sc-fold-body${statusOpen ? ' open' : ''}`}>
                                    <div className="sc-fold-inner">
                                      <div className="sc-insp-fold-pane">
                                        <StatusSeriesEditor
                                          frames={
                                            selectedSource.statusKeyframes?.length
                                              ? sortStatusFrames(selectedSource.statusKeyframes)
                                              : []
                                          }
                                          editing={editing}
                                          clock={authorClock}
                                          appearAt={selectedSource.startsAt ?? null}
                                          playheadIso={playheadIso}
                                          defaultStatus={
                                            String(
                                              selectedSource.params.status ?? 'open'
                                            ) as RoadStatus
                                          }
                                          focusIndex={statusFocusIndex}
                                          onFocusIndex={(index) => {
                                            setStatusFocusIndex(index)
                                            if (index == null) return
                                            const frames = selectedSource.statusKeyframes?.length
                                              ? sortStatusFrames(selectedSource.statusKeyframes)
                                              : []
                                            const frame = frames[index]
                                            if (!frame) return
                                            setRunning(false)
                                            setElapsedMs(off(frame.at))
                                          }}
                                          onChange={setStatusFrames}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {showRadiusKf ? (
                                <div className="sc-insp-fold sc-kf-radius">
                                  <button
                                    type="button"
                                    className="sc-insp-fold-toggle"
                                    aria-expanded={radiusOpen}
                                    onClick={() => {
                                      setRadiusOpen((open) => {
                                        if (open) {
                                          stopRadiusPreview()
                                          setRadiusFocusIndex(null)
                                        }
                                        return !open
                                      })
                                    }}
                                  >
                                    <span>
                                      {zoneLabel(selectedSource.type)}
                                      {(selectedSource.radiusKeyframes?.length ?? 0) > 0
                                        ? ` (${selectedSource.radiusKeyframes!.length})`
                                        : ''}
                                    </span>
                                    <span
                                      className={`sc-chevron${radiusOpen ? ' open' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ▾
                                    </span>
                                  </button>
                                  <div className={`sc-fold-body${radiusOpen ? ' open' : ''}`}>
                                    <div className="sc-fold-inner">
                                      <div className="sc-insp-fold-pane">
                                        <NumberSeriesEditor
                                          frames={
                                            selectedSource.radiusKeyframes?.length
                                              ? selectedSource.radiusKeyframes
                                              : []
                                          }
                                          displayUnits={viewSession.displayUnits}
                                          editing={editing}
                                          clock={authorClock}
                                          appearAt={selectedSource.startsAt ?? null}
                                          playheadIso={playheadIso}
                                          focusIndex={radiusFocusIndex}
                                          previewIndex={
                                            radiusPreviewActive
                                              ? numberPreviewIndexAtElapsed(
                                                  selectedSource.radiusKeyframes ?? [],
                                                  playheadIso
                                                )
                                              : null
                                          }
                                          numberPreviewActive={radiusPreviewActive}
                                          onToggleNumberPreview={() => {
                                            if (radiusPreviewActive) stopRadiusPreview()
                                            else previewSelectedRadius(radiusFocusIndex)
                                          }}
                                          onFocusIndex={(index) => {
                                            setRadiusFocusIndex(index)
                                            if (index == null) return
                                            const frame = selectedSource.radiusKeyframes?.[index]
                                            if (!frame) return
                                            // Seek only — Preview starts from the Preview button, not row focus.
                                            if (radiusPreviewActive) stopRadiusPreview()
                                            setRunning(false)
                                            setElapsedMs(off(frame.at))
                                          }}
                                          defaultValue={Number(selectedSource.params.radiusM) || 0}
                                          onChange={(frames) => {
                                            if (!frames?.length) {
                                              const last =
                                                selectedSource.radiusKeyframes?.[
                                                  selectedSource.radiusKeyframes.length - 1
                                                ]
                                              patchSelectedObject((o) => {
                                                const next = { ...o }
                                                if (last)
                                                  next.params = {
                                                    ...o.params,
                                                    radiusM: last.value,
                                                  }
                                                delete next.radiusKeyframes
                                                return next
                                              })
                                              setRadiusFocusIndex(null)
                                              return
                                            }
                                            setRadiusFrames(frames)
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )
                        })()}
                        {editing ? (
                          <div className="sc-insp-more">
                            <button
                              type="button"
                              className="sc-btn sc-btn-danger-secondary"
                              disabled={!canMutateSelected}
                              onClick={() => {
                                if (selectedSource.pristine) {
                                  deleteObject(selectedSource.id)
                                  return
                                }
                                setPendingDeleteObjectId(selectedSource.id)
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <p className="sc-hint">Select an item on the map to inspect it.</p>
                    )}
                  </div>
                </>
              ) : null}

              {dock === 'notes' ? (
                <>
                  <div className="sc-rhead">
                    <PaneEyebrow label="Chat" />
                  </div>
                  <div
                    className={`sc-rscroll sc-chat-pane${chatSplit ? ' is-split' : ''}`}
                  >
                    {chatSplit ? (
                      <div className="sc-chat-split">
                        <section className="sc-thread-section is-during-run">
                          {isExerciseType ? (
                            <h3 className="sc-thread-section-label">During this run</h3>
                          ) : null}
                          <ChatThreadScroll
                            className="sc-thread sc-thread-scroll"
                            deps={chatSections.duringRun}
                            scrollRef={chatStoryScrollRef}
                          >
                            {chatSections.duringRun.length === 0 ? (
                              <p className="sc-hint sc-comments-empty">
                                {chatSections.layout === 'room'
                                  ? 'Nothing in the story here yet. Move the time bar forward to see more.'
                                  : 'No story messages in this run yet.'}
                              </p>
                            ) : (
                              renderChatThread(chatSections.duringRun, 'story')
                            )}
                          </ChatThreadScroll>
                        </section>
                        <div className="sc-chat-split-wall" aria-hidden="true" />
                        <section className="sc-thread-section is-session">
                          {isExerciseType ? (
                            <h3 className="sc-thread-section-label">During this session</h3>
                          ) : null}
                          <ChatThreadScroll
                            className="sc-thread sc-thread-scroll"
                            deps={chatSections.room}
                            scrollRef={chatSessionScrollRef}
                          >
                            {chatSections.room.length === 0 ? (
                              <p className="sc-hint sc-comments-empty">
                                No messages yet.
                              </p>
                            ) : (
                              renderChatThread(chatSections.room, 'wall')
                            )}
                          </ChatThreadScroll>
                          {canComposeChat ? (
                            <RoomChatCompose
                              onSend={addComment}
                              onFocus={() =>
                                scrollChatThreadToBottom(chatSessionScrollRef.current)
                              }
                            />
                          ) : null}
                        </section>
                      </div>
                    ) : chatShowStoryLane ? (
                      <>
                        <section className="sc-thread-section is-during-run is-solo">
                          <h3 className="sc-thread-section-label">During this run</h3>
                          <ChatThreadScroll
                            className="sc-thread sc-thread-scroll"
                            deps={chatSections.duringRun}
                            scrollRef={chatStoryScrollRef}
                          >
                            {chatSections.duringRun.length === 0 ? (
                              <p className="sc-hint sc-comments-empty">
                                {canComposeChat
                                  ? 'No messages here yet. Move the time bar forward, or send a message.'
                                  : 'No messages here yet.'}
                              </p>
                            ) : (
                              renderChatThread(chatSections.duringRun, 'story')
                            )}
                          </ChatThreadScroll>
                        </section>
                        {canComposeChat ? (
                          <RoomChatCompose
                            onSend={addComment}
                            onFocus={() =>
                              scrollChatThreadToBottom(chatStoryScrollRef.current)
                            }
                          />
                        ) : null}
                      </>
                    ) : (
                      <>
                        <section className="sc-thread-section is-session is-solo">
                          {isExerciseType ? (
                            <h3 className="sc-thread-section-label">During this session</h3>
                          ) : null}
                          <ChatThreadScroll
                            className="sc-thread sc-thread-scroll"
                            deps={chatSections.room}
                            scrollRef={chatSessionScrollRef}
                          >
                            {chatSections.room.length === 0 ? (
                              <p className="sc-hint sc-comments-empty">No messages yet.</p>
                            ) : (
                              renderChatThread(chatSections.room, 'wall')
                            )}
                          </ChatThreadScroll>
                        </section>
                        {canComposeChat ? (
                          <RoomChatCompose
                            onSend={addComment}
                            onFocus={() =>
                              scrollChatThreadToBottom(chatSessionScrollRef.current)
                            }
                          />
                        ) : null}
                      </>
                    )}
                    {!canComposeChat && !canComment ? (
                      <p className="sc-hint sc-chat-readonly-hint">
                        {viewingTemplate
                          ? 'Template preview is view-only. Press + to make a copy you can chat on.'
                          : 'Chat is closed for this session.'}
                      </p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {dock === 'people' ? (
                <>
                  <div className="sc-rhead">
                    <PaneEyebrow label="People" />
                  </div>
                  <div
                    className="sc-rscroll sc-people-roster"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) setSelectedPeopleIds([])
                    }}
                  >
                    {serverBooting ? (
                      <div className="sc-boot" role="status" aria-live="polite">
                        <span className="sc-boot-spin" aria-hidden="true" />
                        Loading
                      </div>
                    ) : bootError ? (
                      <p className="sc-hint" role="status">
                        {isStudioUnreachableNotice(bootError)
                          ? STUDIO_UNREACHABLE_RAIL
                          : bootError}
                      </p>
                    ) : viewingTemplate ? (
                      <p className="sc-hint">
                        The people list belongs to your session copy. Press + on the template to
                        create a Planned run before you invite anyone. Buxus accounts already exist
                        — this is who is on this session.
                      </p>
                    ) : (
                      <>
                        {canManagePeople ? (
                          <p className="sc-hint sc-people-roster-tip">
                            {selectedIsCompleted
                              ? 'Session ended. You can still add, invite, or remove people to fix the roster or send report links.'
                              : 'Add people to the roster, then invite them from here.'}
                          </p>
                        ) : null}
                        {canSelectPeople && peopleTabList.length > 1 ? (
                          <div className="sc-people-selbar">
                            <div className="sc-people-selbar-row">
                              <button
                                type="button"
                                className="sc-link-btn"
                                aria-pressed={peopleSelectMode}
                                onClick={togglePeopleSelectMode}
                              >
                                {peopleSelectMode ? 'Done' : 'Select'}
                              </button>
                              {peopleSelectMode ? (
                                <>
                                  <button
                                    type="button"
                                    className="sc-link-btn"
                                    onClick={selectAllPeople}
                                  >
                                    Select all
                                  </button>
                                  {selectedPeopleIds.length ? (
                                    <button
                                      type="button"
                                      className="sc-link-btn"
                                      onClick={() => setPeopleSelection([])}
                                    >
                                      Clear
                                    </button>
                                  ) : null}
                                  {selectedPeopleIds.length ? (
                                    <span className="sc-people-selbar-count">
                                      {selectedPeopleIds.length} selected
                                    </span>
                                  ) : null}
                                </>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <div
                          className={`sc-people-list${canSelectPeople && peopleSelectMode ? ' selecting' : ''}`}
                          onClick={(e) => {
                            if (e.target === e.currentTarget) setSelectedPeopleIds([])
                          }}
                        >
                          {peopleTabList.length === 0 && presenceGuests.length === 0 ? (
                            <p className="sc-hint">
                              {canManagePeople
                                ? selectedIsPlanned
                                  ? 'No one on this list yet. Type a name or email below, then invite them to this Planned run.'
                                  : 'No one on this list yet. Type a name or email below to add enabled Buxus accounts.'
                                : 'No one has joined this session yet.'}
                            </p>
                          ) : null}
                          {peopleTabList.map((p) => {
                            const on = selectedPeopleIds.includes(p.id)
                            const badge = personPrivilegeLabel(p)
                            return (
                              <div
                                key={p.id}
                                className={`sc-person${on ? ' on' : ''}`}
                                role="button"
                                tabIndex={0}
                                onMouseDown={(e) => {
                                  if (!(e.shiftKey || e.metaKey || e.ctrlKey)) return
                                  e.preventDefault()
                                  onPersonCardActivate(p.id, e)
                                }}
                                onClick={(e) => {
                                  if (e.shiftKey || e.metaKey || e.ctrlKey) return
                                  onPersonCardActivate(p.id, e)
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  onPersonCardActivate(p.id)
                                }}
                              >
                                <div className="sc-person-main">
                                  {canSelectPeople && peopleSelectMode ? (
                                    <span
                                      className={`sc-person-check${on ? ' on' : ''}`}
                                      aria-hidden="true"
                                    />
                                  ) : null}
                                  <div className="sc-person-name">
                                    <b>{rosterDisplayName(p)}</b>
                                    {isDesignatedOwner(
                                      p,
                                      viewSession.primaryOwnerUserId,
                                      activePeople,
                                    ) ? (
                                      <span
                                        className="sc-person-owner-star"
                                        title="Designated session owner"
                                        aria-label="Designated session owner"
                                      >
                                        <NavMdiIcon path={mdiStar} sizePx={12} />
                                      </span>
                                    ) : null}
                                    <PersonPresencePip
                                      person={p}
                                      isSelf={
                                        (p.email ?? '').toLowerCase() ===
                                        user.email.toLowerCase()
                                      }
                                      live={presentEmails.has(
                                        (p.email ?? '').trim().toLowerCase(),
                                      )}
                                    />
                                  </div>
                                  {badge ? (
                                    <span className="sc-person-badge">{badge}</span>
                                  ) : null}
                                </div>
                                {/* Copy icon stops propagation; rest of the card selects. */}
                                <EmailLine email={p.email} link={false} />
                              </div>
                            )
                          })}
                          {presenceGuests.map((g) => {
                            const on = selectedPeopleIds.includes(g.id)
                            const isSelf = g.email === user.email.trim().toLowerCase()
                            return (
                              <div
                                key={g.id}
                                className={`sc-person is-guest${on ? ' on' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => onPersonCardActivate(g.id)}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return
                                  e.preventDefault()
                                  onPersonCardActivate(g.id)
                                }}
                              >
                                <div className="sc-person-main">
                                  <div className="sc-person-name">
                                    <b>{g.name}</b>
                                    <PersonPresencePip
                                      person={{
                                        userId: g.id,
                                        id: g.id,
                                        name: g.name,
                                        email: g.email,
                                        status: 'joined',
                                        roles: [],
                                      }}
                                      isSelf={isSelf}
                                      live
                                    />
                                  </div>
                                  <span className="sc-person-badge is-guest">Guest</span>
                                </div>
                                <EmailLine email={g.email} link={false} />
                              </div>
                            )
                          })}

                        </div>
                      </>
                    )}
                  </div>
                  {canManagePeople &&
                  !viewingTemplate &&
                  !serverBooting &&
                  !bootError &&
                  !studioLinkDown ? (
                    <div className="sc-people-add-dock">
                      <RosterAddField
                        homeTenantId={homeTenantId}
                        activePeople={activePeople}
                        onPick={onRosterPick}
                      />
                    </div>
                  ) : null}
                  {!viewingTemplate &&
                  (selectedPeople.length > 0 ||
                    selectedPresenceGuests.length === 1 ||
                    Boolean(peopleInviteFlash) ||
                    (activePeople.length > 0 && canSelectPeople && peopleSelectMode) ||
                    showDebugPanels) ? (
                    <div className="sc-people-actions">
                      {selectedPeople.length === 0 && selectedPresenceGuests.length === 1 ? (
                        <div className="sc-action-box sc-people-detail">
                          {(() => {
                            const g = selectedPresenceGuests[0]!
                            return (
                              <>
                                <div className="sc-people-id">
                                  <div className="sc-people-detail-head">
                                    <div className="sc-people-detail-title">{g.name}</div>
                                    <span className="sc-person-badge is-guest">Guest</span>
                                  </div>
                                  <EmailLine email={g.email} link={false} />
                                  <p className="sc-hint">
                                    On the board via the share link — not on the official roster.
                                  </p>
                                </div>
                                {canManagePeople ? (
                                  <div className="sc-invite-send-row is-single">
                                    <div className="sc-invite-actions-line">
                                      <button
                                        type="button"
                                        className="sc-btn primary"
                                        title="Add to the roster so you can invite them formally"
                                        onClick={() => invitePresenceGuest(g.email, g.name)}
                                      >
                                        Add to roster
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                              </>
                            )
                          })()}
                        </div>
                      ) : null}
                      {selectedPeople.length ? (
                        <div
                          className={`sc-action-box sc-people-detail is-selected${
                            canSendInvites &&
                            (selectedPeople.length > 1 ||
                              selectedPeople.every((p) => rosterPendingInviteIds.has(p.id)))
                              ? ' is-invite-focus'
                              : ''
                          }`}
                        >
                          {selectedPeople.length === 1
                            ? (() => {
                                const p = selectedPeople[0]!
                                const personIsOwner = personHasRole(p, 'owner')
                                const isLastOwner =
                                  personIsOwner && activeOwners(activePeople).length <= 1
                                const canRemoveP =
                                  canManagePeople &&
                                  !isLastOwner &&
                                  p.email.trim().toLowerCase() !== user.email.trim().toLowerCase()
                                // Roles stay available even while Send is armed — invite-first
                                // must not hide the only way to clear a stuck Editor/Trainer.
                                const showInvite = canSendInvites
                                const showRemove = canRemoveP
                                // Invite/manage UI: skip name/email (list already shows them).
                                // Non-privileged viewers: full identity card + read-only Roles.
                                const showIdentityCard = !peopleTabAdmin
                                const badge = personPrivilegeLabel(p)
                                const designated = isDesignatedOwner(
                                  p,
                                  viewSession.primaryOwnerUserId,
                                  activePeople,
                                )
                                // Owner checkbox only once the viewer is an Owner (or ScenarioAdmin /
                                // ViewAll). Keeps Owner from looking like a normal entitlement.
                                type RoleOpt = { role: PersonRole; editable: boolean }
                                const ceiling = ceilingForEmail(p.email)
                                // Owner is a session grant, but it is still a role. Hide the row
                                // until this account's Studio permissions are known, unless an
                                // elevated role is already on and needs to be cleared.
                                const ceilingKnown = ceiling != null
                                const showRoles =
                                  ceilingKnown ||
                                  personHasRole(p, 'owner') ||
                                  personHasRole(p, 'editor') ||
                                  personHasRole(p, 'trainer')
                                const roleOpts: RoleOpt[] = [
                                  ...(canAssignOwner
                                    ? [
                                        {
                                          role: 'owner' as PersonRole,
                                          editable:
                                            canManagePeople &&
                                            canAssignOwner &&
                                            (ceilingKnown || personHasRole(p, 'owner')),
                                        },
                                      ]
                                    : []),
                                  ...CARD_EDIT_ROLES.filter((r) => r !== 'participant').map(
                                    (r) => ({
                                      role: r,
                                      editable:
                                        canManagePeople &&
                                        rosterRoleCanBeOffered(
                                          r,
                                          ceilingForEmail(p.email),
                                          personHasRole(p, r),
                                        ),
                                    }),
                                  ),
                                ]
                                const docsMeta = inviteDocsForTargets([p])
                                const job = p.jobTitle?.trim()
                                const phone = p.phone?.trim()
                                return (
                                  <>
                                    {showIdentityCard ? (
                                      <div className="sc-people-id">
                                        <div className="sc-people-detail-head">
                                          <div className="sc-people-detail-title">
                                            <span className="sc-person-name">
                                              <b>{rosterDisplayName(p)}</b>
                                              {designated ? (
                                                <span
                                                  className="sc-person-owner-star"
                                                  title="Designated session owner"
                                                  aria-label="Designated session owner"
                                                >
                                                  <NavMdiIcon path={mdiStar} sizePx={12} />
                                                </span>
                                              ) : null}
                                            </span>
                                          </div>
                                          {badge ? (
                                            <span className="sc-person-badge">{badge}</span>
                                          ) : null}
                                        </div>
                                        <EmailLine email={p.email} link={false} />
                                        {job ? (
                                          <p className="sc-people-detail-meta">{job}</p>
                                        ) : null}
                                        {phone ? (
                                          <p className="sc-people-detail-meta">{phone}</p>
                                        ) : null}
                                      </div>
                                    ) : job || phone ? (
                                      <div className="sc-people-id">
                                        {job ? (
                                          <p className="sc-people-detail-meta">{job}</p>
                                        ) : null}
                                        {phone ? (
                                          <p className="sc-people-detail-meta">{phone}</p>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {showInvite ? (
                                      <div className="sc-invite-send-row is-focus">
                                        <div className="sc-invite-actions-line">
                                          {(() => {
                                            const act = inviteAction([p], {
                                              review: selectedIsCompleted,
                                            })
                                            return (
                                              <span className="sc-invite-action">
                                                <button
                                                  type="button"
                                                  className={`sc-btn primary armed-blue${
                                                    act.inviteAnyway ? ' sc-invite-anyway' : ''
                                                  }`}
                                                  title={act.title}
                                                  onClick={() => invitePersonNow(p.id)}
                                                >
                                                  {act.label}
                                                </button>
                                                {peopleInviteFlash ? (
                                                  <span className="sc-action-flash" role="status">
                                                    {peopleInviteFlash}
                                                  </span>
                                                ) : null}
                                              </span>
                                            )
                                          })()}
                                        </div>
                                        {inviteExtras(docsMeta)}
                                        {peopleInviteHint ? (
                                          <p className="sc-hint">{peopleInviteHint}</p>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {showRoles ? (
                                    <div className="sc-field">
                                      <span className="sc-flbl">Roles</span>
                                      <div className="sc-roles">
                                        {roleOpts.map(({ role: r, editable }) => {
                                          const isOwnerOpt = r === 'owner'
                                          const checked = personHasRole(p, r)
                                          const locked =
                                            !editable || (isOwnerOpt && isLastOwner && checked)
                                          return (
                                            <label
                                              key={r}
                                              className="sc-role-opt"
                                              title={
                                                isOwnerOpt && isLastOwner && checked
                                                  ? 'Keep at least one owner'
                                                  : !ceilingKnown && !editable
                                                    ? 'Roles can be set once this account’s Studio permissions are known.'
                                                    : !editable && isOwnerOpt
                                                      ? 'Only owners can change Owner'
                                                      : !editable && ceilingKnown
                                                        ? `${ROLE_LABEL[r]} is not included in this account's Studio permissions`
                                                        : undefined
                                              }
                                            >
                                              <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={locked}
                                                onChange={() => {
                                                  if (locked) return
                                                  togglePersonRole(p.id, r)
                                                }}
                                              />
                                              {ROLE_LABEL[r]}
                                            </label>
                                          )
                                        })}
                                      </div>
                                    </div>
                                    ) : null}
                                    {canManagePeople && rolePlayOn && rolePlaySlots.length ? (
                                      <div className="sc-field">
                                        <span className="sc-flbl">Roleplay position</span>
                                        <select
                                          className="sc-fin"
                                          value={
                                            rolePlaySlots.find(
                                              (s) => slotAssigneeId(viewSession, s) === p.id
                                            )?.id ?? ''
                                          }
                                          onChange={(e) => {
                                            assignPersonRolePlaySeat(
                                              p.id,
                                              e.target.value || null
                                            )
                                          }}
                                        >
                                          <option value="">Unassigned</option>
                                          {rolePlaySlots.map((s) => {
                                            const takenBy = slotAssigneeId(viewSession, s)
                                            const takenName =
                                              takenBy && takenBy !== p.id
                                                ? people.find((x) => x.id === takenBy)?.name
                                                : null
                                            return (
                                              <option key={s.id} value={s.id}>
                                                {rolePlayLabelUi(s.label)}
                                                {takenName ? ` (${takenName})` : ''}
                                              </option>
                                            )
                                          })}
                                        </select>
                                      </div>
                                    ) : null}
                                    {showRemove ? (
                                      <div className="sc-insp-more">
                                        <button
                                          type="button"
                                          className="sc-btn sc-btn-danger-secondary"
                                          title={
                                            personIsOwner
                                              ? 'Remove this owner from the roster'
                                              : 'Remove this person from the roster'
                                          }
                                          onClick={() => requestRemovePeople([p.id])}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ) : null}
                                  </>
                                )
                              })()
                            : (() => {
                                // Multi-select: invite only. Roles / remove are one-at-a-time on a single card.
                                const docsMeta = inviteDocsForTargets(selectedPeople)
                                if (!canSendInvites) {
                                  return peopleInviteHint ? (
                                    <p className="sc-hint">{peopleInviteHint}</p>
                                  ) : null
                                }
                                const act = inviteAction(selectedPeople, {
                                  review: selectedIsCompleted,
                                  prepare: selectedIsPlanned && !sessionIsPublishedNow,
                                })
                                const label = act.label
                                return (
                                  <>
                                    <div className="sc-invite-send-row is-focus">
                                      <div className="sc-invite-actions-line">
                                        <span className="sc-invite-action">
                                          <button
                                            type="button"
                                            className={`sc-btn primary armed-blue${
                                              act.inviteAnyway ? ' sc-invite-anyway' : ''
                                            }`}
                                            title={act.title}
                                            onClick={sendInvites}
                                          >
                                            {label}
                                          </button>
                                          {peopleInviteFlash ? (
                                            <span className="sc-action-flash" role="status">
                                              {peopleInviteFlash}
                                            </span>
                                          ) : null}
                                        </span>
                                      </div>
                                      {inviteExtras(docsMeta)}
                                    </div>
                                    {peopleInviteHint ? (
                                      <p className="sc-hint">{peopleInviteHint}</p>
                                    ) : null}
                                  </>
                                )
                              })()}
                        </div>
                      ) : peopleInviteFlash ? (
                        <p className="sc-people-invite-flash" role="status">
                          {peopleInviteFlash}
                        </p>
                      ) : activePeople.length && canSelectPeople && peopleSelectMode ? (
                        <p className="sc-hint sc-people-detail-empty">
                          Select roster members for invites or access changes.
                        </p>
                      ) : null}

                      {showDebugPanels ? (
                        <div className="sc-action-box sc-people-action-box sc-debug">
                          <div className="sc-panel-title">Developer</div>
                          <div className="sc-series-actions">
                            <button
                              type="button"
                              className={`sc-btn${jsonDebug?.kind === 'people' ? ' primary' : ''}`}
                              onClick={openPeopleJsonDebug}
                            >
                              View people JSON
                            </button>
                            {selectedPeople.length === 1 &&
                            (() => {
                              const p = selectedPeople[0]!
                              const self =
                                p.email.trim().toLowerCase() === user.email.trim().toLowerCase()
                              return (
                                platformProfileDebugForEmail(p.email).matched ||
                                (self && readRegistrationProfileJson() != null)
                              )
                            })() ? (
                              <button
                                type="button"
                                className={`sc-btn${jsonDebug?.kind === 'profile' ? ' primary' : ''}`}
                                onClick={openSelectedProfileJsonDebug}
                              >
                                View profile JSON
                              </button>
                            ) : null}
                            {canManagePeople && selectedPeople.length === 1
                              ? (() => {
                                  const p = selectedPeople[0]!
                                  return (
                                    <>
                                      {p.status === 'pending' ? (
                                        <button
                                          type="button"
                                          className="sc-btn"
                                          title="Flip to joined so Remind appears"
                                          onClick={() => setPersonRosterStatus(p.id, 'joined')}
                                        >
                                          Simulate joined
                                        </button>
                                      ) : null}
                                      {p.status === 'joined' && !personHasRole(p, 'owner') ? (
                                        <button
                                          type="button"
                                          className="sc-btn"
                                          title="Flip to declined so Invite again appears"
                                          onClick={() => setPersonRosterStatus(p.id, 'declined')}
                                        >
                                          Simulate declined
                                        </button>
                                      ) : null}
                                    </>
                                  )
                                })()
                              : null}
                          </div>
                          {jsonDebug?.kind === 'people' || jsonDebug?.kind === 'profile' ? (
                            <div className="sc-json-inline">
                              <span className="sc-flbl">{jsonDebug.title}</span>
                              <textarea
                                className="sc-json-textarea"
                                readOnly
                                rows={14}
                                value={jsonDebug.body}
                                aria-label={jsonDebug.title}
                              />
                              <div className="sc-json-copy">
                                <button
                                  type="button"
                                  className="sc-btn"
                                  onClick={() => {
                                    void (async () => {
                                      try {
                                        await navigator.clipboard.writeText(jsonDebug.body)
                                        flashJsonCopyNote('Copied')
                                      } catch {
                                        flashJsonCopyNote('Copy failed')
                                      }
                                    })()
                                  }}
                                >
                                  <CopyIcon />
                                  Copy
                                </button>
                                <button
                                  type="button"
                                  className="sc-btn"
                                  onClick={() => {
                                    downloadBlob(
                                      new Blob([jsonDebug.body], {
                                        type: 'application/json',
                                      }),
                                      jsonDebug.kind === 'profile'
                                        ? 'platform-profile.json'
                                        : 'scenario-people.json'
                                    )
                                  }}
                                >
                                  Download
                                </button>
                                {jsonCopyNote ? (
                                  <span className="sc-series-io-note" role="status">
                                    {jsonCopyNote}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : null}

              {dock === 'account' ? (
                <div className="sc-account-pane">
                  <div className="sc-account-pane-hd">
                    <h2 className="sc-account-pane-title">Profile</h2>
                    <button
                      type="button"
                      className="sc-btn sc-account-pane-x"
                      aria-label="Close profile"
                      onClick={() => setDock('scenarios')}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  <div className="sc-rscroll sc-settings sc-account-pane-body">
                    <div className="sc-account-identity">
                      <div
                        className="sc-account-who-inline"
                        style={
                          {
                            '--sc-presence-bg': myPresenceTone.bg,
                            '--sc-presence-fg': myPresenceTone.fg,
                            '--sc-presence-ring': myPresenceTone.ring,
                          } as CSSProperties
                        }
                      >
                        <span className="sc-account-avatar" aria-hidden="true">
                          {myPresenceInitials}
                        </span>
                        <div className="sc-account-who-meta">
                          <div className="sc-account-name">{user.name?.trim() || 'Signed in'}</div>
                          {user.email ? <div className="sc-account-email">{user.email}</div> : null}
                        </div>
                        <button
                          type="button"
                          className="sc-btn sc-account-logout"
                          disabled={loggingOut}
                          onClick={logOutOfBuxus}
                        >
                          {loggingOut ? 'Logging out…' : 'Log out'}
                        </button>
                      </div>
                    </div>

                    <section className="sc-account-block" aria-labelledby="sc-account-my-settings">
                      <h3 id="sc-account-my-settings" className="sc-account-block-hd">
                        My Settings
                      </h3>
                      <div className="sc-account-block-body">
                        <div className="sc-field">
                          <span className="sc-flbl">Text size</span>
                          <div className="sc-seg sc-ui-scale" role="group" aria-label="Text size">
                            {(
                              [
                                ['sm', 'Small'],
                                ['md', 'Default'],
                                ['lg', 'Large'],
                                ['xl', 'Extra large'],
                              ] as const
                            ).map(([id, label]) => (
                              <button
                                key={id}
                                type="button"
                                className={uiScale === id ? 'on' : ''}
                                disabled={hubActionsLocked}
                                title={hubActionsLocked ? hubLockTitle : undefined}
                                onClick={() => chooseUiScale(id)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <p className="sc-hint">
                            Changes text in the sidebar, timeline, menus, and tooltips.
                          </p>
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Theme</span>
                          <div className="sc-seg sc-ui-scale" role="group" aria-label="Theme">
                            {(
                              [
                                ['soft-blue', 'Soft Blue'],
                                ['steel', 'Steel'],
                                ['amber', 'Amber'],
                              ] as const
                            ).map(([id, label]) => (
                              <button
                                key={id}
                                type="button"
                                className={studioTheme === id ? 'on' : ''}
                                disabled={hubActionsLocked}
                                title={hubActionsLocked ? hubLockTitle : undefined}
                                onClick={() => chooseStudioTheme(id)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <p className="sc-hint">
                            Color accent for the timeline and some other areas.
                          </p>
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Show plumes</span>
                          <label
                            className={`sc-top-cam${surfacePlumes ? ' on' : ''}`}
                            title={
                              surfacePlumes
                                ? 'Plumes on. Hover a plume to see surface wind or other winds.'
                                : 'Plumes off.'
                            }
                          >
                            <input
                              type="checkbox"
                              checked={surfacePlumes}
                              disabled={hubActionsLocked}
                              onChange={(e) => {
                                const on = e.target.checked
                                setSurfacePlumes(on)
                                try {
                                  localStorage.setItem('buxus.sc.surfacePlumes', on ? '1' : '0')
                                } catch {
                                  /* private mode */
                                }
                              }}
                            />
                            <span className="sc-top-cam-ui" aria-hidden="true">
                              <span className="sc-top-cam-knob" />
                            </span>
                            <span className="sc-top-cam-lbl">{surfacePlumes ? 'On' : 'Off'}</span>
                          </label>
                          <p className="sc-hint">
                            Show fire, vapor, and wind footprints on the map.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Last in Profile; pinned to pane bottom. Respect Developer panels toggle;
                        still show when returning from impersonation so they can exit. */}
                    {showDebugPanels || hasSavedImpersonationSession() ? (
                      <ScenarioUserSwitcher />
                    ) : null}
                  </div>
                </div>
              ) : null}

              {dock === 'settings' ? (
                <>
                  <div className="sc-rhead">
                    <PaneEyebrow label="Settings" />
                  </div>
                  <div className="sc-rscroll sc-settings">
                    {viewingTemplate ? (
                      <>
                        <div className="sc-field">
                          <span className="sc-flbl">Title</span>
                          <p className="sc-settings-read">{viewSession.title}</p>
                        </div>
                        <div className="sc-field">
                          <span className="sc-flbl">Description</span>
                          <p className="sc-settings-read">{viewSession.summary}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="sc-field">
                          <label className="sc-flbl" htmlFor="sc-session-type">
                            Session type
                          </label>
                          <select
                            id="sc-session-type"
                            className={`sc-fin${sessionTypeById(viewSession.sessionType) ? '' : ' is-placeholder'}`}
                            value={sessionTypeById(viewSession.sessionType)?.id ?? ''}
                            disabled={
                              !canEditSettings ||
                              sessionIsPublishedNow ||
                              inLiveRun ||
                              selectedIsCompleted
                            }
                            onChange={(e) => {
                              if (sessionIsPublishedNow || inLiveRun || selectedIsCompleted) return
                              const profile = sessionTypeById(e.target.value)
                              if (!profile) {
                                commitSessionFields((prev) => {
                                  const next = {
                                    ...prev,
                                    boardComplexity: 'everything' as const,
                                  }
                                  delete next.sessionType
                                  return next
                                })
                                setInstances((prev) =>
                                  prev.map((i) => {
                                    if (i.id !== instanceId) return i
                                    const session = {
                                      ...i.session,
                                      boardComplexity: 'everything' as const,
                                    }
                                    delete session.sessionType
                                    return { ...i, session }
                                  })
                                )
                                persistSessionField({
                                  sessionType: '',
                                  boardComplexity: 'everything',
                                })
                                return
                              }
                              const renameUntitled =
                                isDefaultUntitledTitle(titleDraft) ||
                                isDefaultUntitledTitle(viewSession.title)
                              const nextTitle = renameUntitled
                                ? nextCopyTitle(
                                    defaultUntitledTitle(profile.label),
                                    instances.filter((i) => i.id !== instanceId)
                                  )
                                : null
                              commitSessionFields((prev) => ({
                                ...prev,
                                sessionType: profile.id,
                                boardComplexity: profile.palette,
                                ...(nextTitle ? { title: nextTitle } : {}),
                              }))
                              if (nextTitle) setTitleDraft(nextTitle)
                              setInstances((prev) =>
                                prev.map((i) =>
                                  i.id === instanceId
                                    ? {
                                        ...i,
                                        session: {
                                          ...i.session,
                                          sessionType: profile.id,
                                          boardComplexity: profile.palette,
                                          ...(nextTitle ? { title: nextTitle } : {}),
                                        },
                                      }
                                    : i
                                )
                              )
                              persistSessionField({
                                sessionType: profile.id,
                                boardComplexity: profile.palette,
                                ...(nextTitle ? { title: nextTitle } : {}),
                              })
                            }}
                          >
                            <option value="">Select one</option>
                            {offeredSessionTypes(viewSession.sessionType).map((row) => (
                              <option key={row.id} value={row.id}>
                                {row.label}
                              </option>
                            ))}
                          </select>
                          {sessionTypeById(viewSession.sessionType) ? (
                            <p className="sc-hint">
                              {sessionTypeById(viewSession.sessionType)!.blurb}
                            </p>
                          ) : null}
                        </div>
                        <div className="sc-field">
                          <label className="sc-flbl" htmlFor="sc-title">
                            Title
                          </label>
                          <input
                            id="sc-title"
                            className={`sc-fin${fieldFlashKey === 'title' ? ' sc-field-flash' : ''}`}
                            value={titleDraft}
                            disabled={!canEditSettings}
                            onChange={(e) => {
                              setTitleDraft(e.target.value)
                              if (titleError) setTitleError(null)
                            }}
                            onBlur={() => {
                              if (!canEditSettings) return
                              saveScenarioMeta('title')
                            }}
                          />
                          {titleError ? <p className="sc-title-err">{titleError}</p> : null}
                        </div>
                        <div className="sc-field">
                          <label className="sc-flbl" htmlFor="sc-summary">
                            Description
                          </label>
                          <textarea
                            id="sc-summary"
                            className={`sc-fin sc-settings-desc${fieldFlashKey === 'summary' ? ' sc-field-flash' : ''}`}
                            value={summaryDraft}
                            disabled={!canEditSettings}
                            onChange={(e) => setSummaryDraft(e.target.value)}
                            onBlur={() => {
                              if (!canEditSettings) return
                              saveScenarioMeta('summary')
                            }}
                          />
                        </div>
                      </>
                    )}
                    {minimalInspect ? null : (
                      <>
                        <div className="sc-field">
                          <label className="sc-flbl" htmlFor="sc-map">
                            Background Map
                          </label>
                          <select
                            id="sc-map"
                            className={`sc-fin sc-map-pick${viewingTemplate ? ' sc-locked' : ''}${fieldFlashKey === 'activeMapId' ? ' sc-field-flash' : ''}`}
                            value={viewSession.activeMapId}
                            disabled={viewingTemplate ? false : mapLocked || !canEditSettings}
                            onChange={(e) => {
                              if (viewingTemplate) return
                              const next = e.target.value
                              if (!next || next.startsWith('__')) return
                              changeFoundationMap(next)
                            }}
                          >
                            {(() => {
                              const { board, operators } = mapsForPicker(
                                viewSession.maps,
                                downloadedOperatorMaps,
                              )
                              return (
                                <>
                                  {board.map((m) => (
                                    <option key={m.id} value={m.id} disabled={viewingTemplate}>
                                      {truncateMapPickerLabel(m.label)}
                                    </option>
                                  ))}
                                  {showOperatorMapStyles && operators.length ? (
                                    <>
                                      <option value="__ops_rule__" disabled>
                                        ━━━━━━━━━━━━━━━━━━━━
                                      </option>
                                      <option value="__ops_lbl__" disabled>
                                        Operator Maps
                                      </option>
                                      {operators.map((m) => (
                                        <option key={m.id} value={m.id} disabled={viewingTemplate}>
                                          {truncateMapPickerLabel(m.label)}
                                        </option>
                                      ))}
                                    </>
                                  ) : null}
                                </>
                              )
                            })()}
                          </select>
                          {viewingTemplate ? (
                            <p className="sc-hint">
                              Press + in Sessions to choose a map on your template copy.
                            </p>
                          ) : null}
                        </div>
                        {!viewingTemplate && !boardLocationLocked ? (
                          <div
                            className={`sc-field${fieldFlashKey === 'boardLocation' ? ' sc-field-flash' : ''}`}
                          >
                            <label className="sc-flbl" htmlFor="sc-board-location">
                              Board location
                            </label>
                            <button
                              type="button"
                              id="sc-board-location"
                              className="sc-btn"
                              title={
                                hubActionsLocked
                                  ? hubLockTitle
                                  : boardHomeLocked
                                    ? 'Board location stays fixed while the session is live'
                                    : undefined
                              }
                              disabled={
                                hubActionsLocked || boardHomeLocked || !canEditSettings
                              }
                              onClick={openBoardLocationSettings}
                            >
                              Set Location…
                            </button>
                            <p className="sc-hint">
                              {boardHomeLocked
                                ? 'Board location stays fixed while the session is live.'
                                : 'Default pan and zoom setting for the session.'}
                            </p>
                          </div>
                        ) : null}
                        {!viewingTemplate ? (
                          <div className="sc-settings-time-block">
                            <div className="sc-panel-title">Schedule</div>
                            <p className="sc-hint">
                              When the session is planned to meet. Leave blank if none.
                            </p>
                            <div className="sc-field">
                              <label className="sc-flbl" htmlFor="sc-schedule-start">
                                Start
                              </label>
                              <div className="sc-when-zone">
                                <StoryWhenInput
                                  id="sc-schedule-start"
                                  iso={viewSession.scheduledStartTime}
                                  dateSet={Boolean(viewSession.scheduledStartTime)}
                                  timeZone={viewSession.timezone}
                                  withSeconds={false}
                                  allowEmpty
                                  disabled={!canEditSettings}
                                  flash={fieldFlashKey === 'schedule-start'}
                                  onCommit={(value) => {
                                    if (!canEditSettings) return null
                                    if (
                                      value &&
                                      viewSession.scheduledEndTime &&
                                      storyMs(value.iso) >= storyMs(viewSession.scheduledEndTime)
                                    ) {
                                      return 'Start must be before the end'
                                    }
                                    commitSessionFields((prev) => {
                                      const copy = { ...prev }
                                      if (!value) delete copy.scheduledStartTime
                                      else copy.scheduledStartTime = value.iso
                                      return copy
                                    })
                                    persistSessionField({
                                      scheduledStartTime: value?.iso ?? null,
                                    })
                                    flashField('schedule-start')
                                    return null
                                  }}
                                />
                                {viewSession.showTimeZone && viewSession.scheduledStartTime ? (
                                  <span className="sc-zone-code">
                                    {timeZoneAbbrev(
                                      viewSession.scheduledStartTime,
                                      viewSession.timezone
                                    )}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="sc-field">
                              <label className="sc-flbl" htmlFor="sc-schedule-end">
                                End
                              </label>
                              <div className="sc-when-zone">
                                <StoryWhenInput
                                  id="sc-schedule-end"
                                  iso={viewSession.scheduledEndTime}
                                  dateSet={Boolean(viewSession.scheduledEndTime)}
                                  timeZone={viewSession.timezone}
                                  withSeconds={false}
                                  allowEmpty
                                  afterIso={viewSession.scheduledStartTime}
                                  disabled={!canEditSettings}
                                  flash={fieldFlashKey === 'schedule-end'}
                                  onCommit={(value) => {
                                    if (!canEditSettings) return null
                                    if (
                                      value &&
                                      viewSession.scheduledStartTime &&
                                      storyMs(value.iso) <= storyMs(viewSession.scheduledStartTime)
                                    ) {
                                      return 'End is before the start'
                                    }
                                    commitSessionFields((prev) => {
                                      const copy = { ...prev }
                                      if (!value) delete copy.scheduledEndTime
                                      else copy.scheduledEndTime = value.iso
                                      return copy
                                    })
                                    persistSessionField({
                                      scheduledEndTime: value?.iso ?? null,
                                    })
                                    flashField('schedule-end')
                                    return null
                                  }}
                                />
                                {viewSession.showTimeZone && viewSession.scheduledEndTime ? (
                                  <span className="sc-zone-code">
                                    {timeZoneAbbrev(
                                      viewSession.scheduledEndTime,
                                      viewSession.timezone
                                    )}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="sc-field">
                              <label className="sc-flbl" htmlFor="sc-timezone">
                                Timezone
                              </label>
                              <select
                                id="sc-timezone"
                                className={`sc-fin${fieldFlashKey === 'timezone' ? ' sc-field-flash' : ''}`}
                                value={viewSession.timezone}
                                disabled={!canEditSettings}
                                onChange={(e) => {
                                  const timezone = e.target.value
                                  if (!timezone || timezone === viewSession.timezone) return
                                  commitSessionFields((prev) => ({
                                    ...prev,
                                    timezone,
                                  }))
                                  persistSessionField({ timezone })
                                  flashField('timezone')
                                }}
                              >
                                {(STUDIO_TIME_ZONES.includes(
                                  viewSession.timezone as (typeof STUDIO_TIME_ZONES)[number]
                                )
                                  ? [...STUDIO_TIME_ZONES]
                                  : [viewSession.timezone, ...STUDIO_TIME_ZONES]
                                ).map((zone) => (
                                  <option key={zone} value={zone}>
                                    {formatTimeZoneLabel(zone)}
                                  </option>
                                ))}
                              </select>
                              <label
                                className="sc-check"
                                title="Show the short zone name on clocks, schedule, and PDFs."
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(viewSession.showTimeZone)}
                                  disabled={!canEditSettings}
                                  onChange={(e) => {
                                    const showTimeZone = e.target.checked
                                    if (showTimeZone === Boolean(viewSession.showTimeZone)) return
                                    commitSessionFields((prev) => ({
                                      ...prev,
                                      showTimeZone,
                                    }))
                                    persistSessionField({ showTimeZone })
                                    flashField('timezone')
                                  }}
                                />
                                Show timezone
                              </label>
                              <p className="sc-hint">Shows the short name, such as CST or CDT.</p>
                            </div>
                          </div>
                        ) : null}
                        {sessionOffersStoryClock && showTimelineAuthoring ? (
                          <div className="sc-settings-time-block">
                            <div className="sc-panel-title">Scenario Story and Timeline</div>
                            <p className="sc-hint">
                              Fictional or reenacted time on the board. Leave end blank if
                              open-ended.
                            </p>
                            <div className="sc-field">
                              <label className="sc-flbl" htmlFor="sc-clock-offset">
                                Start
                              </label>
                              <div className="sc-when-zone">
                                <StoryWhenInput
                                  id="sc-clock-offset"
                                  iso={viewSession.storyStartTime}
                                  dateSet={
                                    viewSession.storyStartDateSet !== false ||
                                    (viewSession.storyEndTime
                                      ? storyDaysDiffer(
                                          viewSession.storyStartTime,
                                          viewSession.storyEndTime,
                                          viewSession.timezone
                                        )
                                      : false)
                                  }
                                  timeZone={viewSession.timezone}
                                  withSeconds={(viewSession.timelineTimeFormat ?? 'hms') === 'hms'}
                                  disabled={viewingTemplate || !canEditSettings}
                                  flash={fieldFlashKey === 'clock-offset'}
                                  onCommit={(value) => {
                                    if (viewingTemplate || !canEditSettings) return null
                                    if (!value) return 'Start time is required'
                                    const applyLocalRebase = (prev: ScenarioSession) => {
                                      const next = rebaseStorySession(
                                        prev,
                                        storyDeltaMs(prev.storyStartTime, value.iso)
                                      )
                                      if (value.dateSet) {
                                        const copy = { ...next }
                                        delete copy.storyStartDateSet
                                        return copy
                                      }
                                      return {
                                        ...next,
                                        storyStartDateSet: false,
                                      }
                                    }
                                    // Server owns rebase + session.rebase tape row + Snapshot.
                                    // Adopt local shifts as the persisted baseline so heal/save
                                    // paths cannot Upsert-storm every shifted object.
                                    commitSession(applyLocalRebase, {
                                      persistObjects: false,
                                      adoptAsPersisted: true,
                                    })
                                    if (instanceId && !previewTemplateId) {
                                      void hubRebaseStory(
                                        instanceId,
                                        value.iso,
                                        value.dateSet ? true : false,
                                      ).catch((err) => {
                                        setServerNotice(
                                          studioActionMessage(err, 'Could not rebase story start.'),
                                        )
                                      })
                                    }
                                    flashField('clock-offset')
                                    return null
                                  }}
                                />
                                {viewSession.showTimeZone ? (
                                  <span className="sc-zone-code">
                                    {timeZoneAbbrev(
                                      viewSession.storyStartTime,
                                      viewSession.timezone
                                    )}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="sc-field">
                              <label className="sc-flbl" htmlFor="sc-clock-end">
                                End
                              </label>
                              <div className="sc-when-zone">
                                <StoryWhenInput
                                  id="sc-clock-end"
                                  iso={viewSession.storyEndTime}
                                  dateSet={
                                    viewSession.storyEndDateSet !== false ||
                                    storyDaysDiffer(
                                      viewSession.storyStartTime,
                                      viewSession.storyEndTime ?? viewSession.storyStartTime,
                                      viewSession.timezone
                                    )
                                  }
                                  timeZone={viewSession.timezone}
                                  withSeconds={(viewSession.timelineTimeFormat ?? 'hms') === 'hms'}
                                  allowEmpty
                                  afterIso={viewSession.storyStartTime}
                                  disabled={viewingTemplate || !canEditSettings}
                                  flash={fieldFlashKey === 'clock-end'}
                                  onCommit={(value) => {
                                    if (viewingTemplate || !canEditSettings) return null
                                    if (!viewSession.storyStartTime) return 'Set a start time first'
                                    if (
                                      value &&
                                      storyMs(value.iso) <= storyMs(viewSession.storyStartTime)
                                    ) {
                                      return 'End is before the start'
                                    }
                                    commitSessionFields((prev) => {
                                      const copy = { ...prev }
                                      if (!value) {
                                        delete copy.storyEndTime
                                        delete copy.storyEndDateSet
                                        return copy
                                      }
                                      copy.storyEndTime = value.iso
                                      if (value.dateSet) delete copy.storyEndDateSet
                                      else copy.storyEndDateSet = false
                                      return copy
                                    })
                                    persistSessionField(
                                      value
                                        ? {
                                            storyEndTime: value.iso,
                                            storyEndDateSet: value.dateSet,
                                          }
                                        : { storyEndTime: null, storyEndDateSet: null },
                                    )
                                    flashField('clock-end')
                                    return null
                                  }}
                                />
                                {viewSession.showTimeZone && viewSession.storyEndTime ? (
                                  <span className="sc-zone-code">
                                    {timeZoneAbbrev(viewSession.storyEndTime, viewSession.timezone)}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="sc-field">
                              <span className="sc-flbl">Time Format</span>
                              <div className="sc-time-format-row">
                                <div
                                  className={`sc-seg${fieldFlashKey === 'timelineTimeFormat' ? ' sc-field-flash' : ''}`}
                                >
                                  <button
                                    type="button"
                                    className={
                                      (viewSession.timelineTimeFormat ?? 'hms') === 'hm' ? 'on' : ''
                                    }
                                    disabled={viewingTemplate || !canEditSettings}
                                    onClick={() => {
                                      if ((viewSession.timelineTimeFormat ?? 'hms') === 'hm') return
                                      commitSessionFields((prev) => ({
                                        ...prev,
                                        timelineTimeFormat: 'hm',
                                      }))
                                      persistSessionField({
                                        timelineTimeFormat: 'hm',
                                      })
                                      flashField('timelineTimeFormat')
                                    }}
                                  >
                                    h:mm a
                                  </button>
                                  <button
                                    type="button"
                                    className={
                                      (viewSession.timelineTimeFormat ?? 'hms') === 'hms'
                                        ? 'on'
                                        : ''
                                    }
                                    disabled={viewingTemplate || !canEditSettings}
                                    onClick={() => {
                                      if ((viewSession.timelineTimeFormat ?? 'hms') === 'hms')
                                        return
                                      commitSessionFields((prev) => ({
                                        ...prev,
                                        timelineTimeFormat: 'hms',
                                      }))
                                      persistSessionField({
                                        timelineTimeFormat: 'hms',
                                      })
                                      flashField('timelineTimeFormat')
                                    }}
                                  >
                                    h:mm:ss a
                                  </button>
                                </div>
                                <span className="sc-time-format-preview">
                                  {(viewSession.timelineTimeFormat ?? 'hms') === 'hms'
                                    ? TIME_FORMAT_EXAMPLE_HMS
                                    : TIME_FORMAT_EXAMPLE_HM}
                                  {viewSession.showTimeZone
                                    ? ` ${timeZoneAbbrev(viewSession.storyStartTime, viewSession.timezone)}`
                                    : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {!viewingTemplate ? (
                          <>
                            {showPublicAudience || showAnyoneWithLink ? (
                            <div className="sc-field sc-settings-break">
                              <span className="sc-flbl">Visibility</span>
                              {selectedIsCompleted || !showPublicAudience ? null : (
                                <>
                                  <label
                                    className="sc-check"
                                    title="Show this session in others' lists while planned or live."
                                  >
                                    <input
                                      type="checkbox"
                                      className={
                                        fieldFlashKey === 'audienceType'
                                          ? 'sc-field-flash'
                                          : undefined
                                      }
                                      checked={currentInstance?.audienceType === 'public'}
                                      disabled={!canEditVisibility}
                                      onChange={(e) => {
                                        const next = e.target.checked ? 'public' : 'private'
                                        if (
                                          next ===
                                          (currentInstance?.audienceType === 'public'
                                            ? 'public'
                                            : 'private')
                                        )
                                          return
                                        if (next === 'private') {
                                          setPrivateConfirmOpen(true)
                                          return
                                        }
                                        persistAudienceType(next)
                                        flashField('audienceType')
                                      }}
                                    />
                                    Public
                                  </label>
                                  <p className="sc-hint">
                                    {sessionIsPublishedNow || !selectedIsPlanned
                                      ? 'Others in your organization can find this while planned or live.'
                                      : 'Others can find this once Published.'}
                                  </p>
                                </>
                              )}
                              {showAnyoneWithLink ? (
                                <>
                                  <label
                                    className="sc-check"
                                    title={
                                      sessionIsPublishedNow || !selectedIsPlanned
                                        ? 'Anyone in your organization with the share link can open this board.'
                                        : 'Share link opens for guests once Published. Invited people can open while Draft.'
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      className={
                                        fieldFlashKey === 'allowLinkGuests'
                                          ? 'sc-field-flash'
                                          : undefined
                                      }
                                      checked={Boolean(currentInstance?.allowLinkGuests)}
                                      disabled={!canEditVisibility}
                                      onChange={(e) => {
                                        const next = e.target.checked
                                        if (next === Boolean(currentInstance?.allowLinkGuests))
                                          return
                                        persistAllowLinkGuests(next)
                                        flashField('allowLinkGuests')
                                      }}
                                    />
                                    Anyone with the link
                                  </label>
                                  <p className="sc-hint">
                                    {sessionIsPublishedNow || !selectedIsPlanned
                                      ? 'Share link opens the board without an invite.'
                                      : 'Share link works once Published.'}
                                  </p>
                                </>
                              ) : null}
                            </div>
                            ) : null}
                            {showRolePlay ? (
                              <div className="sc-field sc-settings-break">
                                <span className="sc-flbl">Roleplay</span>
                                <label
                                  className="sc-check"
                                  title="Map people to roles represented on the board."
                                >
                                  <input
                                    type="checkbox"
                                    className={
                                      fieldFlashKey === 'rolePlayEnabled'
                                        ? 'sc-field-flash'
                                        : undefined
                                    }
                                    checked={isRolePlayEnabled(viewSession)}
                                    disabled={!canEditSettings}
                                    onChange={(e) => {
                                      const on = e.target.checked
                                      if (on === isRolePlayEnabled(session)) return
                                      commitSessionFields((prev) => ({
                                        ...prev,
                                        rolePlayEnabled: on,
                                      }))
                                      persistSessionField({
                                        rolePlayEnabled: on,
                                      })
                                      flashField('rolePlayEnabled')
                                    }}
                                  />
                                  Use roleplay positions
                                </label>
                                {selectedIsCompleted ? null : (
                                  <p className="sc-hint">
                                    Map people to roles represented on the board.
                                  </p>
                                )}
                              </div>
                            ) : null}
                            <div className="sc-field">
                              <span className="sc-flbl">Exports</span>
                              <label
                                className="sc-check"
                                title="Hide email and phone from Exercise Plan and Run Summary PDFs."
                              >
                                <input
                                  type="checkbox"
                                  className={
                                    fieldFlashKey === 'omitContacts' ? 'sc-field-flash' : undefined
                                  }
                                  checked={Boolean(viewSession.omitContactDetailsFromExports)}
                                  disabled={!canEditExportOmit}
                                  onChange={(e) => {
                                    const omit = e.target.checked
                                    if (omit === Boolean(session.omitContactDetailsFromExports))
                                      return
                                    commitSessionFields((prev) => ({
                                      ...prev,
                                      omitContactDetailsFromExports: omit,
                                    }))
                                    persistSessionField({
                                      omitContactDetailsFromExports: omit,
                                    })
                                    flashField('omitContacts')
                                  }}
                                />
                                Omit contact details from exports
                              </label>
                              {selectedIsCompleted ? null : (
                                <p className="sc-hint">Hide email and phone from PDFs.</p>
                              )}
                            </div>
                            <div className="sc-settings-section">
                              {settingsChrome === 'expanded' ? (
                                <div className="sc-field">
                                  <label className="sc-flbl" htmlFor="sc-board-complexity">
                                    Palette Options
                                  </label>
                                  <select
                                    id="sc-board-complexity"
                                    className={`sc-fin${fieldFlashKey === 'boardComplexity' ? ' sc-field-flash' : ''}`}
                                    value={
                                      viewSession.boardComplexity === 'easy' ||
                                      viewSession.boardComplexity === 'default'
                                        ? viewSession.boardComplexity
                                        : 'everything'
                                    }
                                    disabled={!canEditSettings}
                                    onChange={(e) => {
                                      const raw = e.target.value
                                      const boardComplexity =
                                        raw === 'easy' || raw === 'default' ? raw : 'everything'
                                      const current =
                                        session.boardComplexity === 'easy' ||
                                        session.boardComplexity === 'default'
                                          ? session.boardComplexity
                                          : 'everything'
                                      if (boardComplexity === current) return
                                      commitSessionFields((prev) => ({
                                        ...prev,
                                        boardComplexity,
                                      }))
                                      persistSessionField({ boardComplexity })
                                      flashField('boardComplexity')
                                    }}
                                  >
                                    <option value="everything">All options</option>
                                    <option value="default">Standard options</option>
                                    <option value="easy">Simplified options</option>
                                  </select>
                                </div>
                              ) : null}
                              <div className="sc-field">
                                <span className="sc-flbl">Display Units</span>
                                <div className="sc-time-format-row">
                                  <div
                                    className={`sc-seg${fieldFlashKey === 'displayUnits' ? ' sc-field-flash' : ''}`}
                                  >
                                    <button
                                      type="button"
                                      className={
                                        viewSession.displayUnits === 'imperial' ? 'on' : ''
                                      }
                                      disabled={!canEditSettings}
                                      onClick={() => {
                                        if (session.displayUnits === 'imperial') return
                                        commitSession((s) => ({
                                          ...s,
                                          displayUnits: 'imperial',
                                          objects: s.objects.map((o) =>
                                            isBrwnPlumeType(o.type) &&
                                            isGeneratedBrwnPlumeDescription(o.description ?? '')
                                              ? { ...o, description: '' }
                                              : o
                                          ),
                                        }))
                                        persistSessionField({
                                          displayUnits: 'imperial',
                                        })
                                        flashField('displayUnits')
                                      }}
                                    >
                                      Imperial (ft)
                                    </button>
                                    <button
                                      type="button"
                                      className={viewSession.displayUnits === 'metric' ? 'on' : ''}
                                      disabled={!canEditSettings}
                                      onClick={() => {
                                        if (session.displayUnits === 'metric') return
                                        commitSession((s) => ({
                                          ...s,
                                          displayUnits: 'metric',
                                          objects: s.objects.map((o) =>
                                            isBrwnPlumeType(o.type) &&
                                            isGeneratedBrwnPlumeDescription(o.description ?? '')
                                              ? { ...o, description: '' }
                                              : o
                                          ),
                                        }))
                                        persistSessionField({
                                          displayUnits: 'metric',
                                        })
                                        flashField('displayUnits')
                                      }}
                                    >
                                      Metric (m)
                                    </button>
                                  </div>
                                  <span className="sc-time-format-preview">
                                    {formatDistanceCompact(
                                      DISPLAY_UNITS_EXAMPLE_M,
                                      viewSession.displayUnits
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : null}
                      </>
                    )}
                    {showSessionDeleteAction ? (
                      <div className="sc-settings-danger">
                        <button
                          type="button"
                          className="sc-btn sc-btn-danger-secondary"
                          title={hubActionsLocked ? hubLockTitle : undefined}
                          disabled={hubActionsLocked}
                          onClick={() => setPendingDeleteId(instanceId)}
                        >
                          {sessionDeleteIsDiscard ? 'Discard draft' : 'Delete'}
                        </button>
                      </div>
                    ) : null}
                    {showDebugPanels ? (
                      <div className="sc-field sc-debug sc-settings-section">
                        <span className="sc-flbl">Developer</span>
                        <div className="sc-series-actions">
                          <button
                            type="button"
                            className="sc-btn"
                            disabled={!accessPerms.has('scenarios.create')}
                            title={
                              accessPerms.has('scenarios.create')
                                ? 'Open Get started wizard (manual entry; does not require first-login)'
                                : 'Requires scenarios.create'
                            }
                            onClick={() => openGetStartedWizard()}
                          >
                            Get started wizard
                          </button>
                          <button
                            type="button"
                            className="sc-btn"
                            title="Clear sc-get-started-seen so the wizard auto-prompts on next reload"
                            onClick={() => {
                              try {
                                localStorage.removeItem(GET_STARTED_SEEN_KEY)
                              } catch {
                                /* private mode */
                              }
                              getStartedAutoPromptedRef.current = false
                              emptyHubWizardPromptedRef.current = false
                              showKeyToast('D', 'Wizard seen cleared — reload for auto prompt')
                            }}
                          >
                            Reset wizard seen
                          </button>
                          <button
                            type="button"
                            className="sc-btn"
                            disabled={
                              !instanceId ||
                              Boolean(viewingTemplate) ||
                              !(viewSession.roomChat?.length)
                            }
                            title={
                              !instanceId || viewingTemplate
                                ? 'Open a session board to clear room chat'
                                : !(viewSession.roomChat?.length)
                                  ? 'No room chat lines to clear'
                                  : 'Clear session.roomChat for everyone in the room'
                            }
                            onClick={() => clearRoomChatHistory()}
                          >
                            Clear chat history
                          </button>
                          <button
                            type="button"
                            className={`sc-btn${jsonDebug?.kind === 'scenario' ? ' primary' : ''}`}
                            onClick={openScenarioJsonDebug}
                          >
                            View scenario JSON
                          </button>
                          <button
                            type="button"
                            className={`sc-btn${jsonDebug?.kind === 'actions' ? ' primary' : ''}`}
                            disabled={!instanceId || Boolean(viewingTemplate)}
                            title={
                              !instanceId || viewingTemplate
                                ? 'Open a session board to load ActionsJson'
                                : 'Fetch append-only room tape from the server'
                            }
                            onClick={openActionsJsonDebug}
                          >
                            View actions JSON
                          </button>
                          <button
                            type="button"
                            className={`sc-btn${jsonDebug?.kind === 'store' ? ' primary' : ''}`}
                            onClick={openStoreJsonDebug}
                          >
                            View storage JSON
                          </button>
                        </div>
                        {jsonDebug ? (
                          <div className="sc-json-inline">
                            <span className="sc-flbl">{jsonDebug.title}</span>
                            <textarea
                              className="sc-json-textarea"
                              readOnly
                              rows={14}
                              value={jsonDebug.body}
                              aria-label={jsonDebug.title}
                            />
                            <div className="sc-json-copy">
                              <button
                                type="button"
                                className="sc-btn"
                                onClick={() => {
                                  void (async () => {
                                    try {
                                      await navigator.clipboard.writeText(jsonDebug.body)
                                      flashJsonCopyNote('Copied')
                                    } catch {
                                      flashJsonCopyNote('Copy failed')
                                    }
                                  })()
                                }}
                              >
                                <CopyIcon />
                                Copy
                              </button>
                              <button
                                type="button"
                                className="sc-btn"
                                onClick={() => {
                                  const name =
                                    jsonDebug.kind === 'store'
                                      ? 'studio-snapshot.json'
                                      : jsonDebug.kind === 'people'
                                        ? 'scenario-people.json'
                                        : jsonDebug.kind === 'profile'
                                          ? 'platform-profile.json'
                                          : jsonDebug.kind === 'actions'
                                            ? 'scenario-actions.json'
                                            : 'scenario-instance.json'
                                  downloadBlob(
                                    new Blob([jsonDebug.body], {
                                      type: 'application/json',
                                    }),
                                    name
                                  )
                                }}
                              >
                                Download
                              </button>
                              {jsonCopyNote ? (
                                <span className="sc-series-io-note" role="status">
                                  {jsonCopyNote}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
            {dock !== 'account' && showBoardTools ? (
              <nav className="sc-rtabs" aria-label="Side panels">
                {/* Always reserved so the first tab’s top edge stays put when the pane eyebrow appears. */}
                <div className="sc-rtab-eyebrow-clear" aria-hidden="true" />
                {TABS.filter((t) => {
                  if (t.id === 'timeline' && (!sessionOffersStoryClock || !showTimelineAuthoring))
                    return false
                  if (t.id === 'notes' && !showComments) return false
                  if (t.id === 'inspector' && !templateInspectOffered) return false
                  // Templates (and hubs with nothing selected) have no roster — Roster tab is session-only.
                  if (t.id === 'people' && (viewingTemplate || !instanceId)) return false
                  if (focusMode && accountBoardMode === 'following') {
                    const chrome = passiveFocusChromeForTabs
                    if (chrome.hideRightRail) return false
                    if (t.id === 'inspector' && !chrome.showInspect) return false
                    if (t.id === 'notes' && !chrome.showComments) return false
                    if (t.id === 'settings' && !chrome.showSettings) return false
                    if (t.id === 'people' && !chrome.showPeople) return false
                    if (t.id === 'timeline' && !chrome.showTimeline) return false
                  }
                  return true
                }).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`sc-rtab${t.iconPath ? ' is-icon' : ''}${dock === t.id ? ' on' : ''}`}
                    data-dock={t.id}
                    aria-label={t.label}
                    title={t.label}
                    onClick={(e) => {
                      // Badge click: clear unread without changing the dock.
                      if (
                        t.id === 'notes' &&
                        chatUnreadCount > 0 &&
                        (e.target as HTMLElement).closest('.sc-rtab-badge.is-on')
                      ) {
                        if (markChatRead(instanceId, chatRoomLines, chatReaderId)) {
                          setChatReadEpoch((n) => n + 1)
                        }
                        return
                      }
                      // Re-click a content tab → List; List itself stays selected.
                      if (dock === t.id) {
                        if (t.id !== 'scenarios') startTransition(() => setDock('scenarios'))
                        return
                      }
                      if (t.id === 'inspector') {
                        openInspectorDock()
                        return
                      }
                      startTransition(() => setDock(t.id))
                    }}
                  >
                    {t.iconPath ? (
                      <NavMdiIcon path={t.iconPath} sizePx={18} className="sc-rtab-ico" />
                    ) : (
                      <span className="sc-rtab-label">{t.label}</span>
                    )}
                    {t.id === 'notes' ? (
                      <span
                        className={`sc-rtab-badge${chatUnreadCount > 0 ? ' is-on' : ''}`}
                        aria-hidden={chatUnreadCount <= 0}
                        title={
                          chatUnreadCount > 0
                            ? `${chatUnreadCount} unread — click to clear`
                            : undefined
                        }
                        aria-label={
                          chatUnreadCount > 0
                            ? `${chatUnreadCount} unread, click to clear`
                            : undefined
                        }
                      >
                        {chatUnreadCount > 0
                          ? chatUnreadCount > 99
                            ? '99+'
                            : String(chatUnreadCount)
                          : '\u00a0'}
                      </span>
                    ) : null}
                  </button>
                ))}
              </nav>
            ) : null}
          </div>
          {mapAssetDetailsOpen && mapAssetPick ? (
            <MapAssetPanel
              hits={mapAssetPick.hits}
              index={mapAssetPick.index}
              operators={mapAssetOperators}
              onIndex={(index) =>
                setMapAssetPick((cur) => (cur ? { ...cur, index } : cur))
              }
              onClose={closeMapAssetPanel}
            />
          ) : null}
          {helpOpen ? (
            <div className="sc-help-pane" role="dialog" aria-label="Help">
              <div className="sc-account-pane-hd">
                <h2 className="sc-account-pane-title">Help</h2>
                <button
                  type="button"
                  className="sc-btn sc-account-pane-x"
                  aria-label="Close help"
                  onClick={() => setHelpOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="sc-rscroll sc-settings sc-account-pane-body">
                <section className="sc-account-block" aria-labelledby="sc-help-guides">
                  <h3 id="sc-help-guides" className="sc-account-block-hd">
                    Guides
                  </h3>
                  <div className="sc-account-block-body">
                    <div className="sc-series-actions sc-account-docs">
                      <button
                        type="button"
                        className="sc-btn"
                        disabled={hubActionsLocked}
                        title={hubActionsLocked ? hubLockTitle : undefined}
                        onClick={() => void downloadServerDocPdf('cheatsheet')}
                      >
                        Product Cheatsheet
                      </button>
                      <button
                        type="button"
                        className="sc-btn"
                        disabled={hubActionsLocked}
                        title={hubActionsLocked ? hubLockTitle : undefined}
                        onClick={() => void downloadServerDocPdf('trainer')}
                      >
                        Trainer Guide
                      </button>
                    </div>
                    <button
                      type="button"
                      className="sc-account-keys-tip"
                      onClick={() => {
                        setHelpOpen(false)
                        setKeysOpen(true)
                      }}
                    >
                      <kbd aria-hidden="true">?</kbd>
                      <span>Keyboard shortcuts</span>
                    </button>
                  </div>
                </section>
                <section className="sc-account-block" aria-labelledby="sc-help-contact">
                  <h3 id="sc-help-contact" className="sc-account-block-hd">
                    Contact
                  </h3>
                  <p className="sc-hint sc-help-contact">
                    If you have questions or need assistance, please contact us at{' '}
                    <a href="mailto:help@buxus.io">help@buxus.io</a> or{' '}
                    <a href="tel:8448883318">844-888-3318</a>.
                  </p>
                </section>
              </div>
            </div>
          ) : null}
        </aside>
        )
        }}
        </DockPane>
      </div>
      {privateConfirmOpen ? (
        <div className="sc-modal-back" onClick={() => setPrivateConfirmOpen(false)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-private-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPrivateConfirmOpen(false)
            }}
          >
            <h3 id="sc-private-title">Are you sure you want to make this private?</h3>
            <p>
              {currentInstance && !seeAllInstances && !onMyRoster(currentInstance)
                ? 'It will leave your list, because you are not on the roster.'
                : 'It leaves the list for anyone who is not on the roster.'}
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                autoFocus
                onClick={() => setPrivateConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn rec"
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked}
                onClick={() => {
                  setPrivateConfirmOpen(false)
                  persistAudienceType('private')
                  flashField('audienceType')
                  if (audienceHidesFromMe('private')) setDock('scenarios')
                }}
              >
                Make private
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {sharePanelOpen && instanceId && !viewingTemplate ? (
        <ShareSessionPanel
          open={sharePanelOpen}
          mode={sharePanelMode}
          phase={
            selectedIsCompleted
              ? 'review'
              : selectedStatus === 'live'
                ? 'live'
                : selectedStatus === 'draft'
                  ? 'draft'
                  : 'published'
          }
          eventLabel={sessionTypeProfile?.label ?? viewSession.title}
          instanceId={instanceId}
          sessionTitle={viewSession.title}
          onPublish={() => {
            setSessionPublished(true)
          }}
          onOpenPeople={() => {
            setSharePanelOpen(false)
            setDock('people')
          }}
          onClose={() => setSharePanelOpen(false)}
          canInvitePeople={canSendInvites && !hubActionsLocked}
          hadPrePublishInvites={people.some(
            (p) => !p.isDeleted && p.hasBeenInvited === true,
          )}
          publishDisabled={hubActionsLocked}
          publishDisabledTitle={hubLockTitle}
          showQr={showQr}
        />
      ) : null}
      {sessionUnavailableOpen ? (
        <div className="sc-modal-back" onClick={() => setSessionUnavailableOpen(false)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-session-missing-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="sc-session-missing-title">Session not available</h3>
            <p className="sc-hint">
              You no longer have access — it may have ended, been closed, or your invite was
              removed.
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                onClick={() => setSessionUnavailableOpen(false)}
              >
                {accessPerms.has('scenarios.create') ? 'Not now' : 'OK'}
              </button>
              {accessPerms.has('scenarios.create') ? (
                <button
                  type="button"
                  className="sc-btn go armed-green"
                  onClick={() => {
                    setSessionUnavailableOpen(false)
                    openGetStartedWizard()
                  }}
                >
                  Get started
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <GettingStartedWizard
        open={getStartedOpen}
        phase={getStartedPhase}
        resultType={getStartedType}
        doneMeta={getStartedDoneMeta}
        userKey={user.email}
        busy={getStartedBusy}
        canCreate={accessPerms.has('scenarios.create')}
        forceArriveOnDismiss={getStartedForceArrive}
        dismissLabel={getStartedDismissLabel}
        onStart={(result) => {
          void startFromWizard(result)
        }}
        onClose={() => {
          markGetStartedSeen()
          setGetStartedOpen(false)
        }}
        onGoToSession={finishWizardGoToSession}
        onDismissDone={finishWizardDismiss}
      />
      {boardLocationEditor ? (
        <div className="sc-modal-back" onClick={cancelBoardLocationEditor}>
          <div
            className="sc-modal sc-wizard sc-wizard-place"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-board-loc-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sc-wizard-top">
              <h2 id="sc-board-loc-title">Set Location</h2>
              <p className="sc-wizard-sub">
                Sessions always live at a specific place on the map. Where should this one live?
              </p>
            </div>
            <div className="sc-wizard-place-body">
              <BoardLocationPicker
                key={`loc-${boardLocationEditor.mode}-${boardLocationEditor.templateId ?? boardLocationEditor.instanceId}`}
                initialCamera={boardLocationEditor.camera}
                mapId={boardLocationEditor.mapId}
                seedAsPicked={boardLocationEditor.mode === 'settings'}
                showReset={boardLocationEditor.mode === 'settings'}
                busy={boardLocationBusy}
                confirmLabel={
                  boardLocationEditor.mode === 'template-pre' ? 'Create session' : 'Save'
                }
                onConfirm={(camera) => {
                  void confirmBoardLocationEditor(camera)
                }}
                onCancel={cancelBoardLocationEditor}
              />
            </div>
          </div>
        </div>
      ) : null}
      {goLiveConfirmOpen ? (
        <div className="sc-modal-back" onClick={() => setGoLiveConfirmOpen(false)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-golive-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setGoLiveConfirmOpen(false)
            }}
          >
            <h3 id="sc-golive-title">Go Live?</h3>
            <p>
              This session is Published. Others can find it and join. Go Live starts the live
              session.
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                autoFocus
                onClick={() => setGoLiveConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn"
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked}
                onClick={() => {
                  setGoLiveConfirmOpen(false)
                  setSessionPublished(false)
                }}
              >
                Back to draft
              </button>
              <button
                type="button"
                className="sc-btn rec armed"
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked || !canGoLive}
                onClick={() => {
                  setGoLiveConfirmOpen(false)
                  startScenario()
                }}
              >
                Go Live
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {stopRunConfirmOpen ? (
        <div className="sc-modal-back" onClick={() => setStopRunConfirmOpen(false)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-stop-run-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setStopRunConfirmOpen(false)
            }}
          >
            <h3 id="sc-stop-run-title">
              Stop{stripFocusRunLocal != null ? ` Run ${stripFocusRunLocal}` : ' run'}?
            </h3>
            <p>
              Ends this timed run. You can start another. The session stays Live.
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                autoFocus
                onClick={() => setStopRunConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn rec"
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked}
                onClick={() => {
                  setStopRunConfirmOpen(false)
                  stopLiveRun()
                }}
              >
                Stop run
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {stopConfirmOpen ? (
        <div className="sc-modal-back" onClick={() => setStopConfirmOpen(false)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-stop-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setStopConfirmOpen(false)
            }}
          >
            <h3 id="sc-stop-title">
              {exerciseRunActive
                ? 'End session including your timed exercise?'
                : typeOffersSharedRun(viewSession.sessionType)
                  ? 'End this Live session?'
                  : 'End this session?'}
            </h3>
            <p>
              {exerciseRunActive
                ? "This will end both the timed exercise and your overall session. You won't be able to Go Live again."
                : !typeOffersSharedRun(viewSession.sessionType) && running
                  ? "This will end the session for everyone and stop the story clock. You won't be able to Go Live again."
                  : "This will end the session for everyone. You won't be able to Go Live again."}
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                autoFocus
                onClick={() => setStopConfirmOpen(false)}
              >
                Keep live
              </button>
              <button
                type="button"
                className="sc-btn rec"
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked}
                onClick={() => {
                  setStopConfirmOpen(false)
                  stopScenario()
                }}
              >
                {exerciseRunActive ? 'Yes, I understand' : 'End session'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {pendingDeleteId ? (
        <div className="sc-modal-back" onClick={() => setPendingDeleteId(null)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-del-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPendingDeleteId(null)
            }}
          >
            <h3 id="sc-del-title">
              {sessionDeleteIsDiscard && pendingDeleteId === instanceId
                ? 'Discard this draft?'
                : 'Delete this session?'}
            </h3>
            <p>
              {sessionDeleteIsDiscard && pendingDeleteId === instanceId ? 'Discard' : 'Delete'}{' '}
              <strong>
                {instances.find((i) => i.id === pendingDeleteId)?.session.title ?? 'this session'}
              </strong>
              ? This cannot be undone.
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                onClick={() => setPendingDeleteId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn rec"
                autoFocus
                title={hubActionsLocked ? hubLockTitle : undefined}
                disabled={hubActionsLocked}
                onClick={() => {
                  if (pendingDeleteId) deleteInstance(pendingDeleteId)
                }}
              >
                {sessionDeleteIsDiscard && pendingDeleteId === instanceId
                  ? 'Discard draft'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {pendingDeleteObjectId ? (
        <div className="sc-modal-back" onClick={() => setPendingDeleteObjectId(null)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-del-obj-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPendingDeleteObjectId(null)
            }}
          >
            <h3 id="sc-del-obj-title">Delete?</h3>
            <p>
              Delete{' '}
              <strong>
                {viewSession.objects.find((o) => o.id === pendingDeleteObjectId)?.label.trim() ||
                  paletteLabel(
                    viewSession.objects.find((o) => o.id === pendingDeleteObjectId)?.type ?? ''
                  ) ||
                  'this item'}
              </strong>{' '}
              from the board? This cannot be undone.
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                onClick={() => setPendingDeleteObjectId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn rec"
                autoFocus
                onClick={() => {
                  if (pendingDeleteObjectId) deleteObject(pendingDeleteObjectId)
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {pendingRemovePeopleIds?.length ? (
        <div className="sc-modal-back" onClick={() => setPendingRemovePeopleIds(null)}>
          <div
            className="sc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-rm-people-title"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPendingRemovePeopleIds(null)
            }}
          >
            <h3 id="sc-rm-people-title">Remove from roster?</h3>
            <p>
              {pendingRemovePeopleIds.length === 1 ? (
                <>
                  Remove{' '}
                  <strong>
                    {activePeople.find((x) => x.id === pendingRemovePeopleIds[0])?.name.trim() ||
                      'this person'}
                  </strong>{' '}
                  from this session’s roster? They lose board access until invited again.
                </>
              ) : (
                <>
                  Remove <strong>{pendingRemovePeopleIds.length}</strong> people from this
                  session’s roster? They lose board access until invited again.
                </>
              )}
            </p>
            <div className="sc-modal-actions">
              <button
                type="button"
                className="sc-btn"
                autoFocus
                onClick={() => setPendingRemovePeopleIds(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn rec"
                onClick={() => {
                  const ids = pendingRemovePeopleIds
                  setPendingRemovePeopleIds(null)
                  if (ids?.length) softDeletePeople(ids)
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </StudioDockProvider>
  )
}
